export type ProtobufBasicType =
    | 'int32'
    | 'string'
    | 'bool'
    | 'float'
    | 'double'
    | 'uint32'
    | 'sint32'
    | 'int64'
    | 'uint64'
    | 'sint64'
    | 'fixed32'
    | 'sfixed32'
    | 'fixed64'
    | 'sfixed64'
    | 'bytes';

export type CodecWrapper<Data = unknown, WrappedData = unknown> = {
    wrap(data: Data): WrappedData;
    unwrap(wrappedData: WrappedData): Data;
};

export interface CodecContext {
    basicTypes: ProtobufBasicType[];
    wireTypes: Map<number, number>;
    wrappers: Map<number, CodecWrapper>;
    shiftMask: {
        fieldShift: number;
        fieldMask: number;
        typeShift: number;
        typeMask: number;
        repeatedShift: number;
        repeatedMask: number;
    };
    typeTable: number[];
    fieldTable: string[];
    mapTypes: number[];
}
export const enum UltrabufEntries {
    CommandData = 16,
    Revision = 8237,
    CTSlide = 8239,
    MapOp = 8525,
    EGChunkData = 8528,
    MutationTransformRequest = 8536,
    MutationTransformResponse = 8541,
    TransformRequest = 8544,
    TransformResponse = 8551,
}

export interface UltrabufTypeMap {
    [UltrabufEntries.CommandData]: ICommandData;
    [UltrabufEntries.Revision]: IRevision;
    [UltrabufEntries.CTSlide]: ICTSlide;
    [UltrabufEntries.MapOp]: IMapOp;
    [UltrabufEntries.EGChunkData]: IEGChunkData;
    [UltrabufEntries.MutationTransformRequest]: IMutationTransformRequest;
    [UltrabufEntries.MutationTransformResponse]: IMutationTransformResponse;
    [UltrabufEntries.TransformRequest]: ITransformRequest;
    [UltrabufEntries.TransformResponse]: ITransformResponse;
}

export interface ICommandData {
    /** Command的类型 */
    type?: CommandType;
    /** Command操作来源 */
    operateType?: OperateType;
    /** Command是否做过冲突处理 */
    hasTransformed?: boolean;
    /** 该Command版本（来源于mutations中的最高版本） */
    version?: string;
    mutations?: IMutationData[];
}

export interface IRevision {
    commands?: ICommandData[];
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_Slide name="CT_Slide">
 * ```
 */
export interface ICTSlide {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CommonSlideData maxOccurs="1" minOccurs="1" name="cSld" type="CT_CommonSlideData">
     * ```
     */
    cSld?: ICTCommonSlideData;
    /**
     * ```xml
     * <xsd:group typeRedirected=pml:EG_ChildSlide ref=pml:EG_ChildSlide maxOccurs="1" minOccurs="0" name="EG_ChildSlide" ref="EG_ChildSlide">
     * ref pml:EG_ChildSlide
     * ```
     */
    egChildSlide?: IEGChildSlide;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_SlideTransition maxOccurs="1" minOccurs="0" name="transition" type="CT_SlideTransition">
     * ```
     */
    transition?: ICTSlideTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_SlideTiming maxOccurs="1" minOccurs="0" name="timing" type="CT_SlideTiming">
     * ```
     */
    timing?: ICTSlideTiming;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModify;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="showMasterSp" type="xsd:boolean" use="optional">
     * ```
     */
    showMasterSp?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="showMasterPhAnim" type="xsd:boolean" use="optional">
     * ```
     */
    showMasterPhAnim?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="show" type="xsd:boolean" use="optional">
     * ```
     */
    show?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_Slide name="CT_Slide">
 * ```
 */
export interface ICTSlideDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CommonSlideData maxOccurs="1" minOccurs="1" name="cSld" type="CT_CommonSlideData">
     * ```
     */
    cSld?: ICTCommonSlideDataDelta | null;
    /**
     * ```xml
     * <xsd:group typeRedirected=pml:EG_ChildSlide ref=pml:EG_ChildSlide maxOccurs="1" minOccurs="0" name="EG_ChildSlide" ref="EG_ChildSlide">
     * ref pml:EG_ChildSlide
     * ```
     */
    egChildSlide?: IEGChildSlideDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_SlideTransition maxOccurs="1" minOccurs="0" name="transition" type="CT_SlideTransition">
     * ```
     */
    transition?: ICTSlideTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_SlideTiming maxOccurs="1" minOccurs="0" name="timing" type="CT_SlideTiming">
     * ```
     */
    timing?: ICTSlideTimingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModifyDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="showMasterSp" type="xsd:boolean" use="optional">
     * ```
     */
    showMasterSp?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="showMasterPhAnim" type="xsd:boolean" use="optional">
     * ```
     */
    showMasterPhAnim?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="show" type="xsd:boolean" use="optional">
     * ```
     */
    show?: boolean | null;
}

export interface IMapOp {
    opType?: MapOpType;
    opKey?: string;
}

export interface IEGChunkData {
    slideChunkData?: ISlideChunkData;
}

export interface IMutationTransformRequest {
    requestId?: string;
    /** The base mutation data. */
    baseMutationData?: IMutationData;
    /** The mutation data to be transformed. */
    incomingMutationData?: IMutationData;
    /** Whether the incoming mutation is from server. */
    isIncomingMutationFromClient: boolean;
}

export interface IMutationTransformResponse {
    requestId?: string;
    /** The transformed mutation data. */
    transformedMutationsData?: IMutationData[];
}

export interface ITransformRequest {
    /** 冲突处理主调方，在command级别上冲突处理不会产生裂变或湮灭 */
    commandsA?: ITransformCommand[];
    /** 冲突处理被调方，在command级别上冲突处理不会产生裂变或湮灭 */
    commandsB?: ITransformCommand[];
    /**
     * is_a_after_b: 表示commands_a和commands_b在实际调用场景中的先后顺序。例如：
     * 1.在前端基于远端落盘数据转换本地未提交数据，a是本地未提交数据，b是远端落盘数据，is_a_after_b应为true。
     * 2.在前端基于本地未提交数据转换远端落盘数据，a是远端落盘数据，b是本地未提交数据，is_a_after_b应为false。
     */
    isAAfterB: boolean;
}

/** 返回双边结果，成功则返回被转换后的commands_a和commands_b，否则返回错误信息 */
export interface ITransformResponse {
    code?: TransformStatusCode;
    transformedCommandsA?: ITransformCommand[];
    transformedCommandsB?: ITransformCommand[];
    errMsg?: string;
}

export enum CommandType {
    Empty = 0,
    /** 特殊操作 */
    Keyframe = 1,
    /** presentation */
    SetSlideSize = 4,
    /** 在已有的presentation中添加另一个presentation数据 */
    AddPresentation = 5,
    /** 更新presentation属性 */
    UpdatePresentationProperties = 6,
    /** 缩放幻灯片到固定比例 */
    SetSizeType = 7,
    /** 更新图片元信息/更新视频元信息/更新音频元信息/更新字体信息 */
    UpdateResourceMap = 8,
    UpdateSlideGuideList = 9,
    /** TODO(xiaoyewang): 不知道干啥的 */
    ReplacePresentation = 10,
    /** 修改页面尺寸，不缩放元素 */
    UpdateCustomSlideSizeWithNoScale = 11,
    /** TODO(xiaoyewang): 不知道干啥的 */
    UpdateCustomSlideSizeWithEnlargeOrReduce = 12,
    /** 修改页面尺寸，缩放元素 */
    UpdateCustomSlideSizeWithScale = 13,
    /** page(16) */
    AddSlide = 100,
    /** 在节内添加幻灯片 */
    AddSlideWithSection = 101,
    /** 添加模版 */
    AddLayout = 102,
    /** 添加母版 */
    AddSlideMaster = 103,
    /** 添加notes master */
    AddNotesMaster = 104,
    /** 添加备注 */
    AddNotes = 105,
    /** 删除幻灯片 */
    RemovePage = 106,
    /** 移动幻灯片 */
    MovePages = 112,
    /** 复制页 */
    DuplicatePages = 114,
    /** 更新页名 */
    UpdatePageName = 116,
    /** 更新页属性 */
    UpdatePageProps = 117,
    /** 版式切换函数入口, TODO(xiaoyewang): 有一个changeLayoutQuickSetting，需要确认是否需要实现 */
    ChangeLayout = 118,
    /** 设置幻灯片是否隐藏 */
    UpdateSlideVisible = 119,
    /** TODO(xiaoyewang): 不知道干啥的 */
    UpdateShowMasterSp = 120,
    /** 解除 slide 占位符和 layout 某一占位符的关联关系 */
    UnlinkMatchedShapesOfPlaceholder = 121,
    /** 建立 slide 占位符和 layout 某一占位符的关联关系 */
    LinkMatchedShapesOfPlaceholder = 122,
    /** 添加版式的占位符 */
    AddLayoutPlaceholder = 123,
    /** 添加版式的唯一占位符 */
    AddUniquePlaceholder = 124,
    /** 更新Header Footer */
    UpdateHeaderFooter = 126,
    /** TODO(xiaoyewang): 不知道干啥的 */
    UpdateLayoutSlideGuideList = 127,
    /** 更新母版名称 */
    UpdateSlideMasterName = 128,
    /** 添加母版的占位符 */
    AddSlideMasterPlaceholder = 129,
    /** 提供的全量更新 textStyles 的接口 */
    UpdateSlideMasterTextStyles = 130,
    /** 解除 layout 占位符和 master 某一占位符的关联关系 */
    UnlinkMatchedPlaceholderOfSlideMaster = 131,
    /** 提供的全量替换更新headerFooter的接口 */
    UpdateSlideMasterHeaderFooter = 132,
    /** 母版使用主题字体：仅sdk 内部使用 */
    UseThemeFontInTextStyle = 133,
    /** TODO(xiaoyewang): 不知道干啥的 */
    UpdateSlideMasterSlideGuideList = 134,
    /** section */
    AddSection = 200,
    /** 删除一个节。本属于该被删除节的幻灯片会被归至其上一个节中 */
    RemoveSections = 201,
    /** 移动一整节到目标位置，页会跟随同时移动 */
    MoveSection = 202,
    /** 更新section属性 */
    UpdateSectionProps = 203,
    /** theme */
    UpdateThemeFontFamily = 300,
    /** 添加主题 */
    AddTheme = 301,
    /** 移除 Theme */
    RemoveTheme = 302,
    /** 设置主题 */
    SetTheme = 303,
    /** shape */
    RemoveShapes = 400,
    /** 组合元素 */
    Group = 401,
    /** 解组组合 */
    Ungroup = 402,
    /** 调整形状层级，相对于各形状的自身位置进行调整。 */
    ReorderShapes = 403,
    /** 调整形状层级，统一调整到同一指定位置。 */
    MoveShapes = 404,
    /** 添加shape */
    AddShapes = 405,
    /** 更新形状的位置、大小、旋转等属性 */
    UpdateTransform = 406,
    /** 更新形状属性 */
    UpdateShapeProps = 407,
    /** 更新图片属性 */
    UpdatePictureProps = 408,
    /** 更新smartart属性 */
    UpdateSmartArtProps = 409,
    /** 更新连接线属性 */
    UpdateConnectorProps = 410,
    /** 更新组合属性 */
    UpdateGroupProps = 411,
    /** text */
    UpdateParagraphList = 500,
    /** 更新text body属性 */
    UpdateTextBodyProperties = 501,
    /** 更新text styles */
    UpdateTextStyles = 502,
    /** table */
    InsertRowsOrColumns = 600,
    /** 删除行列 */
    DeleteRowsOrColumns = 601,
    /** 合并单元格 */
    MergeCell = 602,
    /** 取消合并单元格 */
    CancelMergedCell = 603,
    /** 修改表格行高/列宽 */
    UpdateTableWidthOrHeight = 604,
    /** 设置单元格行高 */
    SetTableRowHeights = 605,
    /** 设置单元格列宽 */
    SetTableColWidths = 606,
    /** 更新某个范围内单元格属性 */
    UpdateTableCellProperties = 607,
    /** 更新table属性 */
    UpdateTableProps = 608,
    /** chart */
    UpdateChartProps = 700,
    /** 更新子表属性 */
    UpdateSubChartProps = 701,
    /** 添加子表 */
    AddSubChart = 702,
    /** 移除子表 */
    RemoveSubChart = 703,
    /** 多请求 */
    MultipleRequest = 801,
    /** animation */
    AddAnimation = 901,
}

export enum OperateType {
    /** 上行的三种类型 */
    Local = 0,
    /** 撤销 */
    Undo = 1,
    /** 重做 */
    Redo = 2,
    /** 下行的两种类型 */
    History = 3,
    /** 协同，任何上行消息类型，同步给协作者，这里都会改成Collab */
    Collab = 4,
}

export interface IMutationData {
    type?: MutationType;
    /** 更新幻灯片属性 */
    setPresentationProperties?: ISetPresentationPropertiesMutationData;
    /** 更新页面大小 */
    setSlideSize?: ISetSlideSizeMutationData;
    /** 添加页面 */
    addPage?: IAddPageMutationData;
    /** 删除页面 */
    removePage?: IRemovePageMutationData;
    /** 移动页面 */
    movePage?: IMovePageMutationData;
    /** 更新页属性 */
    setPageProperties?: ISetPagePropertiesMutationData;
    /** 添加形状 */
    addShape?: IAddShapeMutationData;
    /** 删除形状 */
    removeShape?: IRemoveShapeMutationData;
    /** 更新形状的位置、大小、翻转 */
    transformShape?: ITransformShapeMutationData;
    /** 更新形状层级 */
    moveShape?: IMoveShapeMutationData;
    /** 组合形状 */
    group?: IGroupMutationData;
    /** 解除形状组合 */
    ungroup?: IUngroupMutationData;
    /** 更新形状属性 */
    setShapeProperties?: ISetShapePropertiesMutationData;
    /** 添加页面章节 */
    addSection?: IAddSectionMutationData;
    /** 删除页面章节 */
    removeSection?: IRemoveSectionMutationData;
    /** 更新页面章节 */
    setSectionProperties?: ISetSectionPropertiesMutationData;
    /** 绑定节到幻灯片 */
    bindSectionToSlide?: IBindSectionToSlideMutationData;
    /** 插入表格行 */
    insertTableRow?: IInsertTableRowMutationData;
    /** 插入表格列 */
    insertTableCol?: IInsertTableColMutationData;
    /** 删除表格行 */
    deleteTableRow?: IDeleteTableRowMutationData;
    /** 删除表格列 */
    deleteTableCol?: IDeleteTableColMutationData;
    /** 合并表格单元格 */
    mergeTableCells?: IMergeTableCellsMutationData;
    /** 解除表格合并单元格 */
    unmergeTableCells?: IUnmergeTableCellsMutationData;
    /** 更新表格列宽度 */
    setTableColWidths?: ISetTableColWidthsMutationData;
    /** 更新表格行高度 */
    setTableRowHeights?: ISetTableRowHeightsMutationData;
    /** 更新表格单元格属性 */
    setTableCellProperties?: ISetTableCellPropertiesMutationData;
    /** 添加动画 */
    addAnimation?: IAddAnimationMutationData;
    /** 删除动画 */
    removeAnimation?: IRemoveAnimationMutationData;
    /** 更新动画 */
    setAnimationProperties?: ISetAnimationPropertiesMutationData;
    /** 调整动画顺序 */
    moveAnimation?: IMoveAnimationMutationData;
    /** 添加主题 */
    addTheme?: IAddThemeMutationData;
    /** 删除主题 */
    removeTheme?: IRemoveThemeMutationData;
    /** 修改主题属性 */
    setTheme?: ISetThemeMutationData;
    /** 插入文本 */
    insertText?: IInsertTextMutationData;
    /** 删除文本 */
    deleteText?: IDeleteTextMutationData;
    /** 设置文本属性 */
    setTextProperties?: ISetTextPropertiesMutationData;
    /** ** 注意，SetTextDataMutationData 为覆盖操作，与前面三个文本 mutation 不共存！ */
    setTextData?: ISetTextDataMutationData;
    /** 设置外层ChartSpace图表属性 */
    setChartSpaceProperties?: ISetChartSpacePropertiesMutationData;
    /** 添加一个子图表 */
    addSubChart?: IAddSubChartMutationData;
    /** 删除子表 */
    removeSubChart?: IRemoveSubChartMutationData;
    /** 移动子表 */
    moveSubChart?: IMoveSubChartMutationData;
    /** 修改子表属性 */
    setSubChartProperties?: ISetSubChartPropertiesMutationData;
    /** 修改 notes 的显示尺寸 */
    setNotesSize?: ISetNotesSizeMutationData;
    /** 设置文本框属性 */
    setTextBodyProperties?: ISetTextBodyPropertiesMutationData;
    /** 更新资源映射表 */
    updateResourceMap?: IUpdateResourceMapMutationData;
    /** 动态计算 group 的位置、大小 */
    computeGroupTransform?: IComputeGroupTransformMutationData;
    /** 设置图表数据 */
    setDiagramData?: ISetDiagramDataMutationData;
    /** 设置图表绘图 */
    setDiagramDrawing?: ISetDiagramDrawingMutationData;
    /** 设置图表 */
    setDiagrams?: ISetDiagramsMutationData;
    /** 添加批注 */
    addComment?: IAddCommentMutationData;
    /** 更新批注 */
    setCommentProperties?: ISetCommentPropertiesMutationData;
    /** 删除批注 */
    removeComment?: IRemoveCommentMutationData;
    /** 添加扩展数据 */
    setDataExtension?: ISetDataExtensionMutationData;
    /** 删除扩展数据 */
    removeDataExtension?: IRemoveDataExtensionMutationData;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_CommonSlideData name="CT_CommonSlideData">
 * ```
 */
export interface ICTCommonSlideData {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_BackgroundPml maxOccurs="1" minOccurs="0" name="bg" type="CT_BackgroundPml">
     * ```
     */
    bg?: ICTBackgroundPml;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_GroupShapePml maxOccurs="1" minOccurs="1" name="spTree" type="CT_GroupShapePml">
     * ```
     */
    spTree?: ICTGroupShapePml;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CustomerDataList maxOccurs="1" minOccurs="0" name="custDataLst" type="CT_CustomerDataList">
     * ```
     */
    custDataLst?: ICTCustomerDataList;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ControlList maxOccurs="1" minOccurs="0" name="controls" type="CT_ControlList">
     * ```
     */
    controls?: ICTControlList;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_CommonSlideData name="CT_CommonSlideData">
 * ```
 */
export interface ICTCommonSlideDataDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_BackgroundPml maxOccurs="1" minOccurs="0" name="bg" type="CT_BackgroundPml">
     * ```
     */
    bg?: ICTBackgroundPmlDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_GroupShapePml maxOccurs="1" minOccurs="1" name="spTree" type="CT_GroupShapePml">
     * ```
     */
    spTree?: ICTGroupShapePmlDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CustomerDataList maxOccurs="1" minOccurs="0" name="custDataLst" type="CT_CustomerDataList">
     * ```
     */
    custDataLst?: ICTCustomerDataListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ControlList maxOccurs="1" minOccurs="0" name="controls" type="CT_ControlList">
     * ```
     */
    controls?: ICTControlListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string | null;
}

/**
 * ```xml
 * <xsd:group name=pml:EG_ChildSlide name="EG_ChildSlide">
 * ```
 */
export interface IEGChildSlide {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorMappingOverride maxOccurs="1" minOccurs="0" name="clrMapOvr" type="a:CT_ColorMappingOverride">
     * ```
     */
    clrMapOvr?: ICTColorMappingOverride;
}

/**
 * ```xml
 * <xsd:group name=pml:EG_ChildSlide name="EG_ChildSlide">
 * ```
 */
export interface IEGChildSlideDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorMappingOverride maxOccurs="1" minOccurs="0" name="clrMapOvr" type="a:CT_ColorMappingOverride">
     * ```
     */
    clrMapOvr?: ICTColorMappingOverrideDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_SlideTransition name="CT_SlideTransition">
 * ```
 */
export interface ICTSlideTransition {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OrientationTransition name="blinds" type="CT_OrientationTransition">
     * ```
     */
    blinds?: ICTOrientationTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OrientationTransition name="checker" type="CT_OrientationTransition">
     * ```
     */
    checker?: ICTOrientationTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="circle" type="CT_EmptyPml">
     * ```
     */
    circle?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="dissolve" type="CT_EmptyPml">
     * ```
     */
    dissolve?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OrientationTransition name="comb" type="CT_OrientationTransition">
     * ```
     */
    comb?: ICTOrientationTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EightDirectionTransition name="cover" type="CT_EightDirectionTransition">
     * ```
     */
    cover?: ICTEightDirectionTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OptionalBlackTransition name="cut" type="CT_OptionalBlackTransition">
     * ```
     */
    cut?: ICTOptionalBlackTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="diamond" type="CT_EmptyPml">
     * ```
     */
    diamond?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OptionalBlackTransition name="fade" type="CT_OptionalBlackTransition">
     * ```
     */
    fade?: ICTOptionalBlackTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="newsflash" type="CT_EmptyPml">
     * ```
     */
    newsflash?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="plus" type="CT_EmptyPml">
     * ```
     */
    plus?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EightDirectionTransition name="pull" type="CT_EightDirectionTransition">
     * ```
     */
    pull?: ICTEightDirectionTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_SideDirectionTransition name="push" type="CT_SideDirectionTransition">
     * ```
     */
    push?: ICTSideDirectionTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="random" type="CT_EmptyPml">
     * ```
     */
    random?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OrientationTransition name="randomBar" type="CT_OrientationTransition">
     * ```
     */
    randomBar?: ICTOrientationTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_SplitTransition name="split" type="CT_SplitTransition">
     * ```
     */
    split?: ICTSplitTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CornerDirectionTransition name="strips" type="CT_CornerDirectionTransition">
     * ```
     */
    strips?: ICTCornerDirectionTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="wedge" type="CT_EmptyPml">
     * ```
     */
    wedge?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_WheelTransition name="wheel" type="CT_WheelTransition">
     * ```
     */
    wheel?: ICTWheelTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_SideDirectionTransition name="wipe" type="CT_SideDirectionTransition">
     * ```
     */
    wipe?: ICTSideDirectionTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_InOutTransition name="zoom" type="CT_InOutTransition">
     * ```
     */
    zoom?: ICTInOutTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TransitionSoundAction maxOccurs="1" minOccurs="0" name="sndAc" type="CT_TransitionSoundAction">
     * ```
     */
    sndAc?: ICTTransitionSoundAction;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModify;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionSpeed default="fast" name="spd" type="ST_TransitionSpeed" use="optional">
     * ```
     */
    spd?: STTransitionSpeed;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="advClick" type="xsd:boolean" use="optional">
     * ```
     */
    advClick?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="advTm" type="xsd:unsignedInt" use="optional">
     * ```
     */
    advTm?: number;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml ref=msppt2010:honeycomb convType="td:ST_EmptyCTBoolean" name="honeycomb" ref="p14:honeycomb" type="p:CT_EmptyPml">
     * ```
     */
    honeycomb?: ICTEmptyPml;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_SideDirectionTransition ref=msppt2010:vortex name="vortex" ref="p14:vortex" type="p:CT_SideDirectionTransition">
     * ```
     */
    vortex?: ICTSideDirectionTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_ShredTransition ref=msppt2010:shred name="shred" ref="p14:shred" type="CT_ShredTransition">
     * ```
     */
    shred?: ICTShredTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_RippleTransition ref=msppt2010:ripple name="ripple" ref="p14:ripple" type="CT_RippleTransition">
     * ```
     */
    ripple?: ICTRippleTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_LeftRightDirectionTransition ref=msppt2010:switch aliasName=switchKeyword aliasName="switchKeyword" name="switch" ref="p14:switch" type="CT_LeftRightDirectionTransition">
     * ```
     */
    switchKeyword?: ICTLeftRightDirectionTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_LeftRightDirectionTransition ref=msppt2010:flip name="flip" ref="p14:flip" type="CT_LeftRightDirectionTransition">
     * ```
     */
    flip?: ICTLeftRightDirectionTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_LeftRightDirectionTransition ref=msppt2010:gallery name="gallery" ref="p14:gallery" type="CT_LeftRightDirectionTransition">
     * ```
     */
    gallery?: ICTLeftRightDirectionTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_PrismTransition ref=msppt2010:prism name="prism" ref="p14:prism" type="CT_PrismTransition">
     * ```
     */
    prism?: ICTPrismTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OrientationTransition ref=msppt2010:doors name="doors" ref="p14:doors" type="p:CT_OrientationTransition">
     * ```
     */
    doors?: ICTOrientationTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_InOutTransition ref=msppt2010:warp name="warp" ref="p14:warp" type="p:CT_InOutTransition">
     * ```
     */
    warp?: ICTInOutTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_SideDirectionTransition ref=msppt2010:pan name="pan" ref="p14:pan" type="p:CT_SideDirectionTransition">
     * ```
     */
    pan?: ICTSideDirectionTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_LeftRightDirectionTransition ref=msppt2010:ferris name="ferris" ref="p14:ferris" type="CT_LeftRightDirectionTransition">
     * ```
     */
    ferris?: ICTLeftRightDirectionTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_LeftRightDirectionTransition ref=msppt2010:conveyor name="conveyor" ref="p14:conveyor" type="CT_LeftRightDirectionTransition">
     * ```
     */
    conveyor?: ICTLeftRightDirectionTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OrientationTransition ref=msppt2010:window name="window" ref="p14:window" type="p:CT_OrientationTransition">
     * ```
     */
    window?: ICTOrientationTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_FlyThroughTransition ref=msppt2010:flythrough name="flythrough" ref="p14:flythrough" type="CT_FlyThroughTransition">
     * ```
     */
    flythrough?: ICTFlyThroughTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_GlitterTransition ref=msppt2010:glitter name="glitter" ref="p14:glitter" type="CT_GlitterTransition">
     * ```
     */
    glitter?: ICTGlitterTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml ref=msppt2010:flash convType="td:ST_EmptyCTBoolean" name="flash" ref="p14:flash" type="p:CT_EmptyPml">
     * ```
     */
    flash?: ICTEmptyPml;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_RevealTransition ref=msppt2010:reveal name="reveal" ref="p14:reveal" type="CT_RevealTransition">
     * ```
     */
    reveal?: ICTRevealTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt201509:CT_MorphTransition ref=msppt201509:morph name="morph" ref="p159:morph" type="CT_MorphTransition">
     * ```
     */
    morph?: ICTMorphTransition;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2012:CT_PresetTransition ref=msppt2012:prstTrans name="prstTrans" p15="http://schemas.microsoft.com/office/powerpoint/2012/main" ref="p15:prstTrans" type="CT_PresetTransition">
     * ```
     */
    prstTrans?: ICTPresetTransition;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=msppt2010:dur name="dur" ref="p14:dur" type="p14:ST_UniversalTimeOffset" use="optional">
     * ```
     */
    dur?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_SlideTransition name="CT_SlideTransition">
 * ```
 */
export interface ICTSlideTransitionDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OrientationTransition name="blinds" type="CT_OrientationTransition">
     * ```
     */
    blinds?: ICTOrientationTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OrientationTransition name="checker" type="CT_OrientationTransition">
     * ```
     */
    checker?: ICTOrientationTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="circle" type="CT_EmptyPml">
     * ```
     */
    circle?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="dissolve" type="CT_EmptyPml">
     * ```
     */
    dissolve?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OrientationTransition name="comb" type="CT_OrientationTransition">
     * ```
     */
    comb?: ICTOrientationTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EightDirectionTransition name="cover" type="CT_EightDirectionTransition">
     * ```
     */
    cover?: ICTEightDirectionTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OptionalBlackTransition name="cut" type="CT_OptionalBlackTransition">
     * ```
     */
    cut?: ICTOptionalBlackTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="diamond" type="CT_EmptyPml">
     * ```
     */
    diamond?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OptionalBlackTransition name="fade" type="CT_OptionalBlackTransition">
     * ```
     */
    fade?: ICTOptionalBlackTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="newsflash" type="CT_EmptyPml">
     * ```
     */
    newsflash?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="plus" type="CT_EmptyPml">
     * ```
     */
    plus?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EightDirectionTransition name="pull" type="CT_EightDirectionTransition">
     * ```
     */
    pull?: ICTEightDirectionTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_SideDirectionTransition name="push" type="CT_SideDirectionTransition">
     * ```
     */
    push?: ICTSideDirectionTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="random" type="CT_EmptyPml">
     * ```
     */
    random?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OrientationTransition name="randomBar" type="CT_OrientationTransition">
     * ```
     */
    randomBar?: ICTOrientationTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_SplitTransition name="split" type="CT_SplitTransition">
     * ```
     */
    split?: ICTSplitTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CornerDirectionTransition name="strips" type="CT_CornerDirectionTransition">
     * ```
     */
    strips?: ICTCornerDirectionTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="wedge" type="CT_EmptyPml">
     * ```
     */
    wedge?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_WheelTransition name="wheel" type="CT_WheelTransition">
     * ```
     */
    wheel?: ICTWheelTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_SideDirectionTransition name="wipe" type="CT_SideDirectionTransition">
     * ```
     */
    wipe?: ICTSideDirectionTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_InOutTransition name="zoom" type="CT_InOutTransition">
     * ```
     */
    zoom?: ICTInOutTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TransitionSoundAction maxOccurs="1" minOccurs="0" name="sndAc" type="CT_TransitionSoundAction">
     * ```
     */
    sndAc?: ICTTransitionSoundActionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModifyDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionSpeed default="fast" name="spd" type="ST_TransitionSpeed" use="optional">
     * ```
     */
    spd?: STTransitionSpeed | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="advClick" type="xsd:boolean" use="optional">
     * ```
     */
    advClick?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="advTm" type="xsd:unsignedInt" use="optional">
     * ```
     */
    advTm?: number | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml ref=msppt2010:honeycomb convType="td:ST_EmptyCTBoolean" name="honeycomb" ref="p14:honeycomb" type="p:CT_EmptyPml">
     * ```
     */
    honeycomb?: ICTEmptyPml | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_SideDirectionTransition ref=msppt2010:vortex name="vortex" ref="p14:vortex" type="p:CT_SideDirectionTransition">
     * ```
     */
    vortex?: ICTSideDirectionTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_ShredTransition ref=msppt2010:shred name="shred" ref="p14:shred" type="CT_ShredTransition">
     * ```
     */
    shred?: ICTShredTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_RippleTransition ref=msppt2010:ripple name="ripple" ref="p14:ripple" type="CT_RippleTransition">
     * ```
     */
    ripple?: ICTRippleTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_LeftRightDirectionTransition ref=msppt2010:switch aliasName=switchKeyword aliasName="switchKeyword" name="switch" ref="p14:switch" type="CT_LeftRightDirectionTransition">
     * ```
     */
    switchKeyword?: ICTLeftRightDirectionTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_LeftRightDirectionTransition ref=msppt2010:flip name="flip" ref="p14:flip" type="CT_LeftRightDirectionTransition">
     * ```
     */
    flip?: ICTLeftRightDirectionTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_LeftRightDirectionTransition ref=msppt2010:gallery name="gallery" ref="p14:gallery" type="CT_LeftRightDirectionTransition">
     * ```
     */
    gallery?: ICTLeftRightDirectionTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_PrismTransition ref=msppt2010:prism name="prism" ref="p14:prism" type="CT_PrismTransition">
     * ```
     */
    prism?: ICTPrismTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OrientationTransition ref=msppt2010:doors name="doors" ref="p14:doors" type="p:CT_OrientationTransition">
     * ```
     */
    doors?: ICTOrientationTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_InOutTransition ref=msppt2010:warp name="warp" ref="p14:warp" type="p:CT_InOutTransition">
     * ```
     */
    warp?: ICTInOutTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_SideDirectionTransition ref=msppt2010:pan name="pan" ref="p14:pan" type="p:CT_SideDirectionTransition">
     * ```
     */
    pan?: ICTSideDirectionTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_LeftRightDirectionTransition ref=msppt2010:ferris name="ferris" ref="p14:ferris" type="CT_LeftRightDirectionTransition">
     * ```
     */
    ferris?: ICTLeftRightDirectionTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_LeftRightDirectionTransition ref=msppt2010:conveyor name="conveyor" ref="p14:conveyor" type="CT_LeftRightDirectionTransition">
     * ```
     */
    conveyor?: ICTLeftRightDirectionTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OrientationTransition ref=msppt2010:window name="window" ref="p14:window" type="p:CT_OrientationTransition">
     * ```
     */
    window?: ICTOrientationTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_FlyThroughTransition ref=msppt2010:flythrough name="flythrough" ref="p14:flythrough" type="CT_FlyThroughTransition">
     * ```
     */
    flythrough?: ICTFlyThroughTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_GlitterTransition ref=msppt2010:glitter name="glitter" ref="p14:glitter" type="CT_GlitterTransition">
     * ```
     */
    glitter?: ICTGlitterTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml ref=msppt2010:flash convType="td:ST_EmptyCTBoolean" name="flash" ref="p14:flash" type="p:CT_EmptyPml">
     * ```
     */
    flash?: ICTEmptyPml | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_RevealTransition ref=msppt2010:reveal name="reveal" ref="p14:reveal" type="CT_RevealTransition">
     * ```
     */
    reveal?: ICTRevealTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt201509:CT_MorphTransition ref=msppt201509:morph name="morph" ref="p159:morph" type="CT_MorphTransition">
     * ```
     */
    morph?: ICTMorphTransitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2012:CT_PresetTransition ref=msppt2012:prstTrans name="prstTrans" p15="http://schemas.microsoft.com/office/powerpoint/2012/main" ref="p15:prstTrans" type="CT_PresetTransition">
     * ```
     */
    prstTrans?: ICTPresetTransitionDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=msppt2010:dur name="dur" ref="p14:dur" type="p14:ST_UniversalTimeOffset" use="optional">
     * ```
     */
    dur?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_SlideTiming name="CT_SlideTiming">
 * ```
 */
export interface ICTSlideTiming {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TimeNodeList maxOccurs="1" minOccurs="0" name="tnLst" type="CT_TimeNodeList">
     * ```
     */
    tnLst?: ICTTimeNodeList;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_BuildList maxOccurs="1" minOccurs="0" name="bldLst" type="CT_BuildList">
     * ```
     */
    bldLst?: ICTBuildList;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModify;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_SlideTiming name="CT_SlideTiming">
 * ```
 */
export interface ICTSlideTimingDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TimeNodeList maxOccurs="1" minOccurs="0" name="tnLst" type="CT_TimeNodeList">
     * ```
     */
    tnLst?: ICTTimeNodeListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_BuildList maxOccurs="1" minOccurs="0" name="bldLst" type="CT_BuildList">
     * ```
     */
    bldLst?: ICTBuildListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModifyDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ExtensionListModify name="CT_ExtensionListModify">
 * ```
 */
export interface ICTExtensionListModify {
    /**
     * ```xml
     * <xsd:group typeRedirected=pml:EG_ExtensionListPml ref=pml:EG_ExtensionListPml maxOccurs="1" minOccurs="0" name="EG_ExtensionListPml" ref="EG_ExtensionListPml">
     * ref pml:EG_ExtensionListPml
     * ```
     */
    egExtensionListPml?: IEGExtensionListPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="mod" type="xsd:boolean" use="optional">
     * ```
     */
    mod?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ExtensionListModify name="CT_ExtensionListModify">
 * ```
 */
export interface ICTExtensionListModifyDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=pml:EG_ExtensionListPml ref=pml:EG_ExtensionListPml maxOccurs="1" minOccurs="0" name="EG_ExtensionListPml" ref="EG_ExtensionListPml">
     * ref pml:EG_ExtensionListPml
     * ```
     */
    egExtensionListPml?: IEGExtensionListPmlDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="mod" type="xsd:boolean" use="optional">
     * ```
     */
    mod?: boolean | null;
}

export enum MapOpType {
    Unknown = 0,
    Update = 1,
    Set = 2,
    Delete = 3,
}

/** SlideChunkData slide文档完整的序列化数据 */
export interface ISlideChunkData {
    /** 是presentation的关键帧数据 */
    presentation: Uint8Array;
    /** Ultrabuf 咱不支持 map（支持 map 的不支持去掉默认值，这里兼容下） */
    pages?: IPageEntry[];
}

export interface ITransformCommand {
    mutations?: IMutationData[];
    seq?: string;
}

export enum TransformStatusCode {
    /** 成功 */
    Success = 0,
    /** 失败 */
    Failure = 1,
}

export enum MutationType {
    Empty = 0,
    /** 更新幻灯片属性 */
    SetPresentationProperties = 1,
    /** 更新页面大小 */
    SetSlideSize = 2,
    /** 添加页面 */
    AddPage = 3,
    /** 删除页面 */
    RemovePage = 4,
    /** 移动页面 */
    MovePage = 5,
    /** 更新页属性 */
    SetPageProperties = 6,
    /** 添加形状 */
    AddShape = 7,
    /** 删除形状 */
    RemoveShape = 8,
    /** 更新形状的位置、大小、翻转 */
    TransformShape = 9,
    /** 更新形状层级 */
    MoveShape = 10,
    /** 组合形状 */
    Group = 11,
    /** 解除形状组合 */
    Ungroup = 12,
    /** 更新形状属性 */
    SetShapeProperties = 13,
    /** 添加页面章节 */
    AddSection = 14,
    /** 删除页面章节 */
    RemoveSection = 15,
    /** 更新页面章节 */
    SetSectionProperties = 16,
    /** 绑定节到幻灯片 */
    BindSectionToSlide = 17,
    /** 插入表格行 */
    InsertTableRow = 18,
    /** 插入表格列 */
    InsertTableCol = 19,
    /** 删除表格行 */
    DeleteTableRow = 20,
    /** 删除表格列 */
    DeleteTableCol = 21,
    /** 合并表格单元格 */
    MergeTableCells = 22,
    /** 解除表格合并单元格 */
    UnmergeTableCells = 23,
    /** 更新表格列宽度 */
    SetTableColWidths = 24,
    /** 更新表格行高度 */
    SetTableRowHeights = 25,
    /** 更新表格单元格属性 */
    SetTableCellProperties = 26,
    /** 添加动画 */
    AddAnimation = 27,
    /** 删除动画 */
    RemoveAnimation = 28,
    /** 更新动画 */
    SetAnimationProperties = 29,
    /** 调整动画顺序 */
    MoveAnimation = 30,
    /** 添加主题 */
    AddTheme = 31,
    /** 删除主题 */
    RemoveTheme = 32,
    /** 修改主题属性 */
    SetTheme = 33,
    /** 插入文本 */
    InsertText = 34,
    /** 删除文本 */
    DeleteText = 35,
    /** 更新文本属性 */
    SetTextProperties = 36,
    /** 设置文本数据 */
    SetTextData = 37,
    /** 修改图表属性，不包含子表 */
    SetChartSpaceProperties = 38,
    /** 添加子表 */
    AddSubChart = 39,
    /** 修改子表属性 */
    SetSubChartProperties = 40,
    /** 删除子表 */
    RemoveSubChart = 41,
    /** 移动子表 */
    MoveSubChart = 42,
    /** 组合形状为SmartArt */
    GroupAsSmartArt = 43,
    /** 修改 notes 的显示尺寸 */
    SetNotesSize = 44,
    /** 设置文本框属性 */
    SetTextBodyProperties = 45,
    /** 更新资源映射表 */
    UpdateResourceMap = 46,
    /** 更新资源映射表 */
    ComputeGroupTransform = 47,
    /** SmartArt - 设置 data 数据 */
    SetDiagramData = 48,
    /** SmartArt - 设置 drawing 数据 */
    SetDiagramDrawing = 49,
    /** SmartArt - 设置 Layout、Colors、Styles */
    SetDiagrams = 50,
    /** 添加批注 */
    AddComment = 51,
    /** 更新批注 */
    SetCommentProperties = 52,
    /** 删除批注 */
    RemoveComment = 53,
    /** 添加扩展数据 */
    SetDataExtension = 54,
    /** 删除扩展数据 */
    RemoveDataExtension = 55,
}

export interface ISetPresentationPropertiesMutationData {
    type?: MutationType;
    version?: string;
    presentationPr?: ICTPresentationPropertiesDelta;
    defaultTextStyle?: ICTTextListStyleDelta | null;
    /** 预设的表格样式列表 */
    tableStyleList?: ICTTableStyleListDelta;
    /** 预设的视图属性 */
    viewPr?: ICTViewPropertiesDelta;
    /** 是否在标题幻灯片上显示特殊的占位符 */
    showSpecialPlsOnTitleSld?: boolean | null;
    /** 设置页码的起始值 */
    firstSlideNum?: number | null;
    embeddedFontList?: ICTEmbeddedFontListDelta;
    /** 参考线，走全量覆盖 */
    slideGuideList?: ICTExtendedGuideListDelta;
    /** 编辑密码 */
    modifyVerifier?: ICTModifyVerifierDelta | null;
}

export interface ISetSlideSizeMutationData {
    type?: MutationType;
    version?: string;
    size?: ICTSlideSizeDelta;
}

export interface IAddPageMutationData {
    type?: MutationType;
    version?: string;
    pageId?: string;
    index?: number;
    pageType?: STPageType;
    /** shape_tree的id(全局唯一) */
    shapeTreeId?: string;
    slideOptions?: ISlideOptions;
    slideLayoutOptions?: ISlideLayoutOptions;
    slideMasterOptions?: ISlideMasterOptions;
    notesOptions?: INotesOptions;
    notesMasterOptions?: INotesMasterOptions;
}

export interface IRemovePageMutationData {
    type?: MutationType;
    version?: string;
    pageId?: string;
    pageType?: STPageType;
    /** layout_id、master_id、theme_id 用于校验 */
    parentId?: string;
    index?: number;
}

export interface IMovePageMutationData {
    type?: MutationType;
    version?: string;
    pageId?: string;
    pageType?: STPageType;
    /** Layout 为当前所属的 Master 的 ID，其他留空 */
    srcParentPageId?: string;
    /** 相对当前 Master 的 layoutList 数组索引 */
    srcIndex?: number;
    /** Layout 为移动目标的 Master 的 ID，其他留空 */
    destParentPageId?: string;
    /** 相对目标 Master 的 layoutList 数组索引 */
    destIndex?: number;
}

export interface ISetPagePropertiesMutationData {
    type?: MutationType;
    version?: string;
    pageId?: string;
    pageType?: STPageType;
    pageProperties?: IPageProperties;
    slideProperties?: ISlideProperties;
    layoutProperties?: ISlideLayoutProperties;
    masterProperties?: ISlideMasterProperties;
    notesProperties?: INotesProperties;
    notesMasterProperties?: INotesMasterProperties;
}

/** 插入形状到末尾 */
export interface IAddShapeMutationData {
    type?: MutationType;
    version?: string;
    /** Shape 所属的页的 ID */
    pageId?: string;
    /** Shape 本身的 ID */
    shapeId?: string;
    /** Shape 的类型，包括普通形状、组合、连接线、图片、表格等 */
    shapeType?: ShapeType;
    /** TODO(donnieyang):删除因为导入导出那里现在用了，导入导出改了再删除 */
    shapeData?: IShapeDelta;
    /** 添加table需要的参数 */
    tableOptions?: IAddTableOptions;
    /** 父节点的 ID */
    parentId?: string;
    /** 相对父节点的索引位置 */
    index?: number;
}

export interface IRemoveShapeMutationData {
    type?: MutationType;
    version?: string;
    /** Shape 所属的页的 ID */
    pageId?: string;
    /** 待删除的 Shape 的 ID */
    shapeId?: string;
    /** 父节点的 ID */
    parentId?: string;
    /** 相对父节点的索引位置 */
    index?: number;
}

export interface ITransformShapeMutationData {
    type?: MutationType;
    version?: string;
    /** Shape 所属的页的 ID */
    pageId?: string;
    /** 待变换的 Shape 的 ID */
    shapeId?: string;
    /** 待变换的 Shape 的类型 */
    shapeType?: ShapeType;
    xfrmDelta?: ICTTransform2DDelta;
    /** 仅应用于 GroupShape，导入导出与 xfrm.ch_off 和 xfrm.ch_ext 关联 */
    chXfrmDelta?: ICTGroupChildTransform;
    /** 删除自身的 xfrm（分情况，走继承或者恢复默认值），与 xfrm_delta 互斥 */
    clearXfrm?: boolean;
    /** 待变换的 Shape 的父节点 ID，仅冲突处理使用 */
    parentId?: string;
}

export interface IMoveShapeMutationData {
    type?: MutationType;
    version?: string;
    /** Shape 所属的页的 ID */
    pageId?: string;
    /** 待调整的 Shape 的 ID */
    shapeId?: string;
    /** 移动操作类型，包括上移、下移、移至最前、移至最后 @deprecated */
    opType?: ReorderOpType;
    /** 移动到目标点的 Shape ID，冲突处理需要 @deprecated */
    destShapeId?: string;
    /** 移动起始父节点的 ID */
    srcParentId?: string;
    /** 移动目标父节点的 ID */
    destParentId?: string;
    /** 移动起始位置，相对父节点的索引位置 */
    srcIndex?: number;
    /** 移动目标位置，相对父节点的索引位置 */
    destIndex?: number;
}

export interface IGroupMutationData {
    type?: MutationType;
    version?: string;
    /** 待组合 Shape 所属页的 ID */
    pageId?: string;
    /** 组合后，组合形状 ID */
    groupShapeId?: string;
    /** 待组合的 Shape ID 列表 */
    shapeIds?: string[];
    /** 是否是smart_art, smart_shape降级为group后, 需要保留类型 */
    isSmartArt?: boolean;
    /** 父节点的 ID */
    parentId?: string;
    /** 相对父节点的索引位置 */
    index?: number;
}

export interface IUngroupMutationData {
    type?: MutationType;
    version?: string;
    /** 组合所属页的 ID */
    pageId?: string;
    /** 待解组的组合形状 ID */
    groupShapeId?: string;
    /** 原始组合的 Shape ID 列表 */
    shapeIds?: string[];
    /** 父节点的 ID */
    parentId?: string;
    /** 相对父节点的索引位置 */
    index?: number;
}

export interface ISetShapePropertiesMutationData {
    type?: MutationType;
    version?: string;
    /** 形状所属的 Page ID */
    pageId?: string;
    /** 待更新的 Shape ID */
    shapeId?: string;
    /** 形状变更详情，统一描述不同类型形状属性的更新或移除 */
    shapeDelta?: IShapeDelta;
}

export interface IAddSectionMutationData {
    type?: MutationType;
    version?: string;
    sectionId?: string;
    name?: string;
}

export interface IRemoveSectionMutationData {
    type?: MutationType;
    version?: string;
    sectionId?: string;
}

export interface ISetSectionPropertiesMutationData {
    type?: MutationType;
    version?: string;
    sectionId?: string;
    name?: string;
}

export interface IBindSectionToSlideMutationData {
    type?: MutationType;
    version?: string;
    sectionId?: string;
    /** 以当前 Section 分割，后续第一个 Slide 的索引位置，如果后续无 Slide，则取 slide 数组的长度 */
    firstSlideIndex?: number;
}

export interface IInsertTableRowMutationData {
    type?: MutationType;
    version?: string;
    pageId?: string;
    shapeId?: string;
    index?: number;
    count?: number;
}

export interface IInsertTableColMutationData {
    type?: MutationType;
    version?: string;
    pageId?: string;
    shapeId?: string;
    index?: number;
    count?: number;
}

export interface IDeleteTableRowMutationData {
    type?: MutationType;
    version?: string;
    pageId?: string;
    shapeId?: string;
    index?: number;
    count?: number;
}

export interface IDeleteTableColMutationData {
    type?: MutationType;
    version?: string;
    pageId?: string;
    shapeId?: string;
    index?: number;
    count?: number;
}

export interface IMergeTableCellsMutationData {
    type?: MutationType;
    version?: string;
    pageId?: string;
    shapeId?: string;
    range?: ITableRangeData;
}

export interface IUnmergeTableCellsMutationData {
    type?: MutationType;
    version?: string;
    pageId?: string;
    shapeId?: string;
    range?: ITableRangeData;
}

export interface ISetTableColWidthsMutationData {
    type?: MutationType;
    version?: string;
    pageId?: string;
    shapeId?: string;
    widths?: IWidthInfo[];
}

export interface ISetTableRowHeightsMutationData {
    type?: MutationType;
    version?: string;
    pageId?: string;
    shapeId?: string;
    heights?: IHeightInfo[];
}

/** 更新单元格属性 */
export interface ISetTableCellPropertiesMutationData {
    type?: MutationType;
    version?: string;
    pageId?: string;
    shapeId?: string;
    range?: ITableRangeData;
    tableCellProperties?: ICTTableCellPropertiesDelta | null;
}

export interface IAddAnimationMutationData {
    type?: MutationType;
    version?: string;
    /** 操作的 page id，用于做冲突处理 */
    pageId?: string;
    /** 添加动画的 shape id，用于做冲突处理 */
    shapeId?: string;
    /** 触发这个动画的元素 id */
    triggerId?: string;
    /** 队列类型 */
    seqType?: STTLTimeNodeType;
    /** 插入动画 index */
    index?: number;
}

export interface IRemoveAnimationMutationData {
    type?: MutationType;
    version?: string;
    /** 操作的 page id，用于做冲突处理 */
    pageId?: string;
    /** 添加动画的 shape id，用于做冲突处理 */
    shapeId?: string;
    /** 触发这个动画的元素 id */
    triggerId?: string;
    /** 队列类型 */
    seqType?: STTLTimeNodeType;
    /** 插入动画 index */
    index?: number;
}

export interface ISetAnimationPropertiesMutationData {
    type?: MutationType;
    version?: string;
    /** 操作的 page id，用于做冲突处理 */
    pageId?: string;
    /** 添加动画的 shape id，用于做冲突处理 */
    shapeId?: string;
    /** 触发这个动画的元素 id */
    triggerId?: string;
    /** 队列类型 */
    seqType?: STTLTimeNodeType;
    /** 插入动画 index */
    index?: number;
    /** 动画值。如果动画类型是音/视频，会额外携带音/视频的相关数据,存储在audio/vedio字段中 */
    timeNode?: IEGTimeNodeChoiceDelta;
}

export interface IMoveAnimationMutationData {
    type?: MutationType;
    version?: string;
    /** 操作的 page id，用于做冲突处理 */
    pageId?: string;
    /** 添加动画的 shape id，用于做冲突处理 */
    shapeId?: string;
    fromTriggerId?: string;
    /** 队列触发器 id */
    triggerId?: string;
    /** 移动前的队列类型 */
    fromSeqType?: STTLTimeNodeType;
    /** 队列类型 */
    seqType?: STTLTimeNodeType;
    /** 移动前的动画 index */
    fromIndex?: number;
    /** 移动到动画 index */
    index?: number;
}

export interface IAddThemeMutationData {
    type?: MutationType;
    version?: string;
    themeId?: string;
    themeName?: string;
    themeElements?: ICTBaseStylesDelta;
}

export interface IRemoveThemeMutationData {
    type?: MutationType;
    version?: string;
    themeId?: string;
}

export interface ISetThemeMutationData {
    type?: MutationType;
    version?: string;
    themeId?: string;
    themeName?: string;
    themeElements?: ICTBaseStylesDelta;
}

/** 文本属性变更 */
export interface IInsertTextMutationData {
    type?: MutationType;
    version?: string;
    /** Shape 所属的页的 ID */
    pageId?: string;
    /** 文本所属的形状的 ID */
    shapeId?: string;
    /** 插入位点的索引 */
    index?: number;
    /** 待插入的文本 */
    text?: string;
    /** 行号，用于table中的cell */
    row?: number;
    /** 列号, 用于table中的cell */
    col?: number;
}

/** 删除文本 */
export interface IDeleteTextMutationData {
    type?: MutationType;
    version?: string;
    /** Shape 所属的页的 ID */
    pageId?: string;
    /** 文本所属的形状的 ID */
    shapeId?: string;
    /** 删除位点的索引 */
    index?: number;
    /** 待删除的文本长度 */
    count?: number;
    /** 行号，用于table中的cell */
    row?: number;
    /** 列号, 用于table中的cell */
    col?: number;
}

/** 文本属性变更 */
export interface ISetTextPropertiesMutationData {
    type?: MutationType;
    version?: string;
    /** Shape 所属的页的 ID */
    pageId?: string;
    /** 文本所属的形状的 ID */
    shapeId?: string;
    /** 文本属性变更的起始位点索引 */
    index?: number;
    /** 文本属性变更的文本长度 */
    count?: number;
    /** paragraph属性 */
    paragraphProperties?: ICTTextParagraphPropertiesDelta;
    /** run属性 */
    runProperties?: ICTTextCharacterPropertiesDelta;
    /** field属性 */
    fieldProperties?: ICTTextFieldDmlADelta;
    /** 文本属性类型 */
    textPropertyType?: TextPropertyType;
    /** 行号，用于table中的cell */
    row?: number;
    /** 列号, 用于table中的cell */
    col?: number;
}

/** 设置文本数据，包括文本和文本属性（为全量覆盖） */
export interface ISetTextDataMutationData {
    type?: MutationType;
    version?: string;
    /** Shape 所属的页的 ID */
    pageId?: string;
    /** 文本所属的形状的 ID */
    shapeId?: string;
    /** 行号，用于table中的cell */
    row?: number;
    /** 列号, 用于table中的cell */
    col?: number;
    /** 变更的文本 */
    text?: string;
    textProperties?: ITextPropertyData[];
}

/** 设置最外层的图表数据。路径为graphic.graphicData.chartSpace */
export interface ISetChartSpacePropertiesMutationData {
    type?: MutationType;
    version?: string;
    /** Shape 所属的页的 ID */
    pageId?: string;
    /** Shape 本身的 ID */
    shapeId?: string;
    chartSpacePr?: IChartSpaceProperties;
}

/** 添加一个子图表 */
export interface IAddSubChartMutationData {
    type?: MutationType;
    version?: string;
    /** Shape 所属的页的 ID */
    pageId?: string;
    /** Shape 本身的 ID */
    shapeId?: string;
    /** SubChart的索引 */
    index?: number;
    subChartType?: SubChartType;
}

/** 移除子图表 */
export interface IRemoveSubChartMutationData {
    type?: MutationType;
    version?: string;
    /** Shape 所属的页的 ID */
    pageId?: string;
    /** Shape 本身的 ID */
    shapeId?: string;
    /** SubChart的索引 */
    index?: number;
}

/** 移动子图表 */
export interface IMoveSubChartMutationData {
    type?: MutationType;
    version?: string;
    /** Shape 所属的页的 ID */
    pageId?: string;
    /** Shape 本身的 ID */
    shapeId?: string;
    /** SubChart的当前索引 */
    srcIndex?: number;
    /** SubChart的目标索引 */
    destIndex?: number;
}

/** 设置子图表的属性 */
export interface ISetSubChartPropertiesMutationData {
    type?: MutationType;
    version?: string;
    /** Shape 所属的页的 ID */
    pageId?: string;
    /** Shape 本身的 ID */
    shapeId?: string;
    /** SubChart的索引 */
    index?: number;
    /** 包含各种类型的子表的属性 */
    plot?: IEGPlotDelta;
    axesPr?: IChartAxesProperties | null;
}

export interface ISetNotesSizeMutationData {
    type?: MutationType;
    version?: string;
    size?: ICTPositiveSize2DDelta;
}

/** 设置文本样式 */
export interface ISetTextBodyPropertiesMutationData {
    type?: MutationType;
    version?: string;
    /** Shape 所属的页的 ID */
    pageId?: string;
    /** 文本所属的形状的 ID */
    shapeId?: string;
    /** 行号，用于table中的cell */
    row?: number;
    /** 列号, 用于table中的cell */
    col?: number;
    /** shape 中的文本属性，包含文本方向等信息 */
    textBodyProperties?: ICTTextBodyPropertiesDelta | null;
    /** 文本的列表样式，包含bullet size， bullet color 等信息 */
    textListStyle?: ICTTextListStyleDelta | null;
}

/** 更新资源映射表 */
export interface IUpdateResourceMapMutationData {
    type?: MutationType;
    version?: string;
    /** 资源文档内唯一的 ID，用于标识资源。 */
    id?: string;
    /** 资源类型 */
    resourceType?: ResourceType;
    /** 图片资源描述 */
    imageDescriptor?: IImageResourceDescriptor;
    /** 视频资源描述 */
    videoDescriptor?: IVideoResourceDescriptor;
    /** 音频资源描述 */
    audioDescriptor?: IAudioResourceDescriptor;
    /** 3D模型资源描述 */
    model_3dDescriptor?: IOOXMLPartResourceDescriptor;
    /** OLE资源描述 */
    oleDescriptor?: IOOXMLPartResourceDescriptor;
    /** 内嵌字体资源描述 */
    fontDescriptor?: IOOXMLPartResourceDescriptor;
}

export interface IComputeGroupTransformMutationData {
    type?: MutationType;
    version?: string;
    /** Group 所属的页的 ID */
    pageId?: string;
    /** 待变换的 Group 的 ID */
    groupId?: string;
    /** 组合里的 Shape ID 列表 @deprecated */
    shapeIds?: string[];
    /** 待变换的 Group 的父节点 ID，仅冲突处理使用 */
    parentId?: string;
}

/** 设置 SmartArt data */
export interface ISetDiagramDataMutationData {
    type?: MutationType;
    version?: string;
    /** Shape 所属的页的 ID */
    pageId?: string;
    /** Shape 本身的 ID */
    shapeId?: string;
    dataModel?: ICTDataModelDelta;
}

export interface ISetDiagramDrawingMutationData {
    type?: MutationType;
    version?: string;
    pageId?: string;
    shapeId?: string;
    drawing?: ICTDrawingMsDspDelta;
}

export interface ISetDiagramsMutationData {
    type?: MutationType;
    version?: string;
    pageId?: string;
    shapeId?: string;
    layoutDef?: ICTDiagramDefinitionDelta | null;
    styleDef?: ICTStyleDefinitionDelta | null;
    colorDef?: ICTColorTransformDelta | null;
}

export interface IAddCommentMutationData {
    type?: MutationType;
    version?: string;
    /** required 批注所属页的id */
    pageId?: string;
    /** required 批注的类型 */
    commentType?: CommentType;
    /** required 批注组的id */
    groupId?: string;
    /** optional 文本所属的形状的 ID。当comment_type为Comment_type_slide时，shape_id为空。 */
    shapeId?: string;
    /** optional 批注在页面中的角标显示位置。当该值不存在时，默认在对应锚点的左上角。 */
    anchorPosition?: ICTPoint2DDelta;
    /** optional 文字批注的锚点位置。当comment_type不为Comment_type_text时，text_anchor为空。 */
    textAnchor?: ITextAnchor;
    /** optional 批注组的创建时间戳 */
    createdTimeStamp?: number;
}

export interface ISetCommentPropertiesMutationData {
    type?: MutationType;
    version?: string;
    /** required 批注所属页的id */
    pageId?: string;
    /** required 批注的类型 */
    commentType?: CommentType;
    /** required 批注的id */
    groupId?: string;
    /** optional 文本所属的形状的 ID。当comment_type为Comment_type_slide时，shape_id为空。 */
    shapeId?: string;
    /** optional 批注在页面中的角标显示位置。当该值不存在时，默认在对应锚点的左上角。 */
    anchorPosition?: ICTPoint2DDelta;
    /** optional 文字批注的锚点位置。当comment_type不为Comment_type_text时，text_anchor为空。 */
    textAnchor?: ITextAnchor;
}

export interface IRemoveCommentMutationData {
    type?: MutationType;
    version?: string;
    /** required 批注所属页的id */
    pageId?: string;
    /** required 批注的类型 */
    commentType?: CommentType;
    /** required 批注的id */
    groupId?: string;
    /** optional 文本所属的形状的 ID。当comment_type为Comment_type_slide时，shape_id为空。 */
    shapeId?: string;
}

export interface ISetDataExtensionMutationData {
    type?: MutationType;
    version?: string;
    /** 扩展数据挂载对象类型 */
    targetType?: TargetType;
    /** 扩展数据挂载对象所属页的 ID，用于冲突处理，targetType=presentation 时为空 */
    pageId?: string;
    /** 扩展数据挂载对象 ID */
    targetId?: string;
    /** 扩展数据的 Key */
    key?: string;
    /** 扩展数据的 Value */
    value?: string;
}

export interface IRemoveDataExtensionMutationData {
    type?: MutationType;
    version?: string;
    /** 扩展数据挂载对象类型 */
    targetType?: TargetType;
    /** 扩展数据挂载对象所属页的 ID，用于冲突处理，targetType=presentation 时为空 */
    pageId?: string;
    /** 扩展数据挂载对象 ID */
    targetId?: string;
    /** 扩展数据的 Key，空表示清空所有的 Record */
    key?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_BackgroundPml name="CT_BackgroundPml">
 * ```
 */
export interface ICTBackgroundPml {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_BackgroundProperties name="bgPr" type="CT_BackgroundProperties">
     * ```
     */
    bgPr?: ICTBackgroundProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StyleMatrixReference name="bgRef" type="a:CT_StyleMatrixReference">
     * ```
     */
    bgRef?: ICTStyleMatrixReference;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_BlackWhiteMode default="white" name="bwMode" type="a:ST_BlackWhiteMode" use="optional">
     * ```
     */
    bwMode?: STBlackWhiteMode;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_BackgroundPml name="CT_BackgroundPml">
 * ```
 */
export interface ICTBackgroundPmlDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_BackgroundProperties name="bgPr" type="CT_BackgroundProperties">
     * ```
     */
    bgPr?: ICTBackgroundPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StyleMatrixReference name="bgRef" type="a:CT_StyleMatrixReference">
     * ```
     */
    bgRef?: ICTStyleMatrixReferenceDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_BlackWhiteMode default="white" name="bwMode" type="a:ST_BlackWhiteMode" use="optional">
     * ```
     */
    bwMode?: STBlackWhiteMode | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_GroupShapePml name="CT_GroupShapePml">
 * ```
 */
export interface ICTGroupShapePml {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_GroupShapeNonVisualPml maxOccurs="1" minOccurs="1" name="nvGrpSpPr" type="CT_GroupShapeNonVisualPml">
     * ```
     */
    nvGrpSpPr?: ICTGroupShapeNonVisualPml;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupShapeProperties maxOccurs="1" minOccurs="1" name="grpSpPr" type="a:CT_GroupShapeProperties">
     * ```
     */
    grpSpPr?: ICTGroupShapeProperties;
    /**
     * ```xml
     * <xsd:group typeRedirected=pml:EG_GroupShapePml ref=pml:EG_GroupShapePml maxOccurs="unbounded" minOccurs="0" name="EG_GroupShapePml" ref="EG_GroupShapePml">
     * ref pml:EG_GroupShapePml
     * ```
     */
    egGroupShapePml?: IEGGroupShapePml[];
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModify;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_GroupShapePml name="CT_GroupShapePml">
 * ```
 */
export interface ICTGroupShapePmlDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_GroupShapeNonVisualPml maxOccurs="1" minOccurs="1" name="nvGrpSpPr" type="CT_GroupShapeNonVisualPml">
     * ```
     */
    nvGrpSpPr?: ICTGroupShapeNonVisualPmlDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupShapeProperties maxOccurs="1" minOccurs="1" name="grpSpPr" type="a:CT_GroupShapeProperties">
     * ```
     */
    grpSpPr?: ICTGroupShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:group typeRedirected=pml:EG_GroupShapePml ref=pml:EG_GroupShapePml maxOccurs="unbounded" minOccurs="0" name="EG_GroupShapePml" ref="EG_GroupShapePml">
     * ref pml:EG_GroupShapePml
     * ```
     */
    egGroupShapePml?: IEGGroupShapePmlDelta[];
    egGroupShapePmlOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModifyDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_CustomerDataList name="CT_CustomerDataList">
 * ```
 */
export interface ICTCustomerDataList {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CustomerData maxOccurs="unbounded" minOccurs="0" name="custData" type="CT_CustomerData">
     * ```
     */
    custData?: ICTCustomerData[];
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TagsData maxOccurs="1" minOccurs="0" name="tags" type="CT_TagsData">
     * ```
     */
    tags?: ICTTagsData;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_CustomerDataList name="CT_CustomerDataList">
 * ```
 */
export interface ICTCustomerDataListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CustomerData maxOccurs="unbounded" minOccurs="0" name="custData" type="CT_CustomerData">
     * ```
     */
    custData?: ICTCustomerDataDelta[];
    custDataOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TagsData maxOccurs="1" minOccurs="0" name="tags" type="CT_TagsData">
     * ```
     */
    tags?: ICTTagsDataDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ControlList name="CT_ControlList">
 * ```
 */
export interface ICTControlList {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ControlPml maxOccurs="unbounded" minOccurs="0" name="control" type="CT_ControlPml">
     * ```
     */
    control?: ICTControlPml[];
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ControlList name="CT_ControlList">
 * ```
 */
export interface ICTControlListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ControlPml maxOccurs="unbounded" minOccurs="0" name="control" type="CT_ControlPml">
     * ```
     */
    control?: ICTControlPmlDelta[];
    controlOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ExtensionListPml name="CT_ExtensionListPml">
 * ```
 */
export interface ICTExtensionListPml {
    /**
     * ```xml
     * <xsd:group typeRedirected=pml:EG_ExtensionListPml ref=pml:EG_ExtensionListPml maxOccurs="1" minOccurs="0" name="EG_ExtensionListPml" ref="EG_ExtensionListPml">
     * ref pml:EG_ExtensionListPml
     * ```
     */
    egExtensionListPml?: IEGExtensionListPml;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ExtensionListPml name="CT_ExtensionListPml">
 * ```
 */
export interface ICTExtensionListPmlDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=pml:EG_ExtensionListPml ref=pml:EG_ExtensionListPml maxOccurs="1" minOccurs="0" name="EG_ExtensionListPml" ref="EG_ExtensionListPml">
     * ref pml:EG_ExtensionListPml
     * ```
     */
    egExtensionListPml?: IEGExtensionListPmlDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ColorMappingOverride name="CT_ColorMappingOverride">
 * ```
 */
export interface ICTColorMappingOverride {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EmptyElement convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="masterClrMapping" type="CT_EmptyElement">
     * ```
     */
    masterClrMapping?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorMapping name="overrideClrMapping" type="CT_ColorMapping">
     * ```
     */
    overrideClrMapping?: ICTColorMapping;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ColorMappingOverride name="CT_ColorMappingOverride">
 * ```
 */
export interface ICTColorMappingOverrideDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EmptyElement convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="masterClrMapping" type="CT_EmptyElement">
     * ```
     */
    masterClrMapping?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorMapping name="overrideClrMapping" type="CT_ColorMapping">
     * ```
     */
    overrideClrMapping?: ICTColorMappingDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_OrientationTransition name="CT_OrientationTransition">
 * ```
 */
export interface ICTOrientationTransition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_DirectionPml default="horz" name="dir" type="ST_DirectionPml" use="optional">
     * ```
     */
    dir?: STDirectionPml;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_OrientationTransition name="CT_OrientationTransition">
 * ```
 */
export interface ICTOrientationTransitionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_DirectionPml default="horz" name="dir" type="ST_DirectionPml" use="optional">
     * ```
     */
    dir?: STDirectionPml | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_EightDirectionTransition name="CT_EightDirectionTransition">
 * ```
 */
export interface ICTEightDirectionTransition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionEightDirectionType default="l" name="dir" type="ST_TransitionEightDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionEightDirectionType;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_EightDirectionTransition name="CT_EightDirectionTransition">
 * ```
 */
export interface ICTEightDirectionTransitionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionEightDirectionType default="l" name="dir" type="ST_TransitionEightDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionEightDirectionType | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_OptionalBlackTransition name="CT_OptionalBlackTransition">
 * ```
 */
export interface ICTOptionalBlackTransition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="thruBlk" type="xsd:boolean" use="optional">
     * ```
     */
    thruBlk?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_OptionalBlackTransition name="CT_OptionalBlackTransition">
 * ```
 */
export interface ICTOptionalBlackTransitionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="thruBlk" type="xsd:boolean" use="optional">
     * ```
     */
    thruBlk?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_SideDirectionTransition name="CT_SideDirectionTransition">
 * ```
 */
export interface ICTSideDirectionTransition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionSideDirectionType default="l" name="dir" type="ST_TransitionSideDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionSideDirectionType;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_SideDirectionTransition name="CT_SideDirectionTransition">
 * ```
 */
export interface ICTSideDirectionTransitionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionSideDirectionType default="l" name="dir" type="ST_TransitionSideDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionSideDirectionType | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_SplitTransition name="CT_SplitTransition">
 * ```
 */
export interface ICTSplitTransition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_DirectionPml default="horz" name="orient" type="ST_DirectionPml" use="optional">
     * ```
     */
    orient?: STDirectionPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionInOutDirectionType default="out" name="dir" type="ST_TransitionInOutDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionInOutDirectionType;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_SplitTransition name="CT_SplitTransition">
 * ```
 */
export interface ICTSplitTransitionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_DirectionPml default="horz" name="orient" type="ST_DirectionPml" use="optional">
     * ```
     */
    orient?: STDirectionPml | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionInOutDirectionType default="out" name="dir" type="ST_TransitionInOutDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionInOutDirectionType | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_CornerDirectionTransition name="CT_CornerDirectionTransition">
 * ```
 */
export interface ICTCornerDirectionTransition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionCornerDirectionType default="lu" name="dir" type="ST_TransitionCornerDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionCornerDirectionType;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_CornerDirectionTransition name="CT_CornerDirectionTransition">
 * ```
 */
export interface ICTCornerDirectionTransitionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionCornerDirectionType default="lu" name="dir" type="ST_TransitionCornerDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionCornerDirectionType | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_WheelTransition name="CT_WheelTransition">
 * ```
 */
export interface ICTWheelTransition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="4" name="spokes" type="xsd:unsignedInt" use="optional">
     * ```
     */
    spokes?: number;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_WheelTransition name="CT_WheelTransition">
 * ```
 */
export interface ICTWheelTransitionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="4" name="spokes" type="xsd:unsignedInt" use="optional">
     * ```
     */
    spokes?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_InOutTransition name="CT_InOutTransition">
 * ```
 */
export interface ICTInOutTransition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionInOutDirectionType default="out" name="dir" type="ST_TransitionInOutDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionInOutDirectionType;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_InOutTransition name="CT_InOutTransition">
 * ```
 */
export interface ICTInOutTransitionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionInOutDirectionType default="out" name="dir" type="ST_TransitionInOutDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionInOutDirectionType | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TransitionSoundAction name="CT_TransitionSoundAction">
 * ```
 */
export interface ICTTransitionSoundAction {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TransitionStartSoundAction name="stSnd" type="CT_TransitionStartSoundAction">
     * ```
     */
    stSnd?: ICTTransitionStartSoundAction;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="endSnd" type="CT_EmptyPml">
     * ```
     */
    endSnd?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TransitionSoundAction name="CT_TransitionSoundAction">
 * ```
 */
export interface ICTTransitionSoundActionDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TransitionStartSoundAction name="stSnd" type="CT_TransitionStartSoundAction">
     * ```
     */
    stSnd?: ICTTransitionStartSoundActionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="endSnd" type="CT_EmptyPml">
     * ```
     */
    endSnd?: boolean | null;
}

export enum STTransitionSpeed {
    PBDEFAULT = 0,
    slow = 1,
    med = 2,
    fast = 3,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_EmptyPml name="CT_EmptyPml">
 * ```
 */
export interface ICTEmptyPml {}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_ShredTransition name="CT_ShredTransition">
 * ```
 */
export interface ICTShredTransition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=msppt2010:ST_TransitionShredPattern default="strip" name="pattern" type="ST_TransitionShredPattern" use="optional">
     * ```
     */
    pattern?: STTransitionShredPattern;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionInOutDirectionType default="in" name="dir" type="p:ST_TransitionInOutDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionInOutDirectionType;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_ShredTransition name="CT_ShredTransition">
 * ```
 */
export interface ICTShredTransitionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=msppt2010:ST_TransitionShredPattern default="strip" name="pattern" type="ST_TransitionShredPattern" use="optional">
     * ```
     */
    pattern?: STTransitionShredPattern | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionInOutDirectionType default="in" name="dir" type="p:ST_TransitionInOutDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionInOutDirectionType | null;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_RippleTransition name="CT_RippleTransition">
 * ```
 */
export interface ICTRippleTransition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=msppt2010:ST_TransitionCornerAndCenterDirectionType default="center" name="dir" type="ST_TransitionCornerAndCenterDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionCornerAndCenterDirectionType;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_RippleTransition name="CT_RippleTransition">
 * ```
 */
export interface ICTRippleTransitionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=msppt2010:ST_TransitionCornerAndCenterDirectionType default="center" name="dir" type="ST_TransitionCornerAndCenterDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionCornerAndCenterDirectionType | null;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_LeftRightDirectionTransition name="CT_LeftRightDirectionTransition">
 * ```
 */
export interface ICTLeftRightDirectionTransition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=msppt2010:ST_TransitionLeftRightDirectionType name="dir" type="ST_TransitionLeftRightDirectionType">
     * ```
     */
    dir?: STTransitionLeftRightDirectionType;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_LeftRightDirectionTransition name="CT_LeftRightDirectionTransition">
 * ```
 */
export interface ICTLeftRightDirectionTransitionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=msppt2010:ST_TransitionLeftRightDirectionType name="dir" type="ST_TransitionLeftRightDirectionType">
     * ```
     */
    dir?: STTransitionLeftRightDirectionType | null;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_PrismTransition name="CT_PrismTransition">
 * ```
 */
export interface ICTPrismTransition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionSideDirectionType default="l" name="dir" type="p:ST_TransitionSideDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionSideDirectionType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="isContent" type="xsd:boolean" use="optional">
     * ```
     */
    isContent?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="isInverted" type="xsd:boolean" use="optional">
     * ```
     */
    isInverted?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_PrismTransition name="CT_PrismTransition">
 * ```
 */
export interface ICTPrismTransitionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionSideDirectionType default="l" name="dir" type="p:ST_TransitionSideDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionSideDirectionType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="isContent" type="xsd:boolean" use="optional">
     * ```
     */
    isContent?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="isInverted" type="xsd:boolean" use="optional">
     * ```
     */
    isInverted?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_FlyThroughTransition name="CT_FlyThroughTransition">
 * ```
 */
export interface ICTFlyThroughTransition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionInOutDirectionType default="in" name="dir" type="p:ST_TransitionInOutDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionInOutDirectionType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="hasBounce" type="xsd:boolean" use="optional">
     * ```
     */
    hasBounce?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_FlyThroughTransition name="CT_FlyThroughTransition">
 * ```
 */
export interface ICTFlyThroughTransitionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionInOutDirectionType default="in" name="dir" type="p:ST_TransitionInOutDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionInOutDirectionType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="hasBounce" type="xsd:boolean" use="optional">
     * ```
     */
    hasBounce?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_GlitterTransition name="CT_GlitterTransition">
 * ```
 */
export interface ICTGlitterTransition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionSideDirectionType default="l" name="dir" type="p:ST_TransitionSideDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionSideDirectionType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=msppt2010:ST_TransitionPattern default="diamond" name="pattern" type="ST_TransitionPattern" use="optional">
     * ```
     */
    pattern?: STTransitionPattern;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_GlitterTransition name="CT_GlitterTransition">
 * ```
 */
export interface ICTGlitterTransitionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TransitionSideDirectionType default="l" name="dir" type="p:ST_TransitionSideDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionSideDirectionType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=msppt2010:ST_TransitionPattern default="diamond" name="pattern" type="ST_TransitionPattern" use="optional">
     * ```
     */
    pattern?: STTransitionPattern | null;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_RevealTransition name="CT_RevealTransition">
 * ```
 */
export interface ICTRevealTransition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="thruBlk" type="xsd:boolean" use="optional">
     * ```
     */
    thruBlk?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=msppt2010:ST_TransitionLeftRightDirectionType default="l" name="dir" type="ST_TransitionLeftRightDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionLeftRightDirectionType;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_RevealTransition name="CT_RevealTransition">
 * ```
 */
export interface ICTRevealTransitionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="thruBlk" type="xsd:boolean" use="optional">
     * ```
     */
    thruBlk?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=msppt2010:ST_TransitionLeftRightDirectionType default="l" name="dir" type="ST_TransitionLeftRightDirectionType" use="optional">
     * ```
     */
    dir?: STTransitionLeftRightDirectionType | null;
}

/**
 * ```xml
 * <xsd:complexType name=msppt201509:CT_MorphTransition name="CT_MorphTransition">
 * ```
 */
export interface ICTMorphTransition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=msppt201509:ST_TransitionMorphOption name="option" type="ST_TransitionMorphOption" use="required">
     * ```
     */
    option?: STTransitionMorphOption;
}

/**
 * ```xml
 * <xsd:complexType name=msppt201509:CT_MorphTransition name="CT_MorphTransition">
 * ```
 */
export interface ICTMorphTransitionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=msppt201509:ST_TransitionMorphOption name="option" type="ST_TransitionMorphOption" use="required">
     * ```
     */
    option?: STTransitionMorphOption | null;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2012:CT_PresetTransition name="CT_PresetTransition" p15="http://schemas.microsoft.com/office/powerpoint/2012/main">
 * ```
 */
export interface ICTPresetTransition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="prst" type="xsd:string">
     * ```
     */
    prst?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="invX" type="xsd:boolean" use="optional">
     * ```
     */
    invX?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="invY" type="xsd:boolean" use="optional">
     * ```
     */
    invY?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2012:CT_PresetTransition name="CT_PresetTransition" p15="http://schemas.microsoft.com/office/powerpoint/2012/main">
 * ```
 */
export interface ICTPresetTransitionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="prst" type="xsd:string">
     * ```
     */
    prst?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="invX" type="xsd:boolean" use="optional">
     * ```
     */
    invX?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="invY" type="xsd:boolean" use="optional">
     * ```
     */
    invY?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TimeNodeList name="CT_TimeNodeList">
 * ```
 */
export interface ICTTimeNodeList {
    /**
     * ```xml
     * <xsd:group typeRedirected=pml:EG_TimeNodeChoice ref=pml:EG_TimeNodeChoice maxOccurs="unbounded" minOccurs="1" name="EG_TimeNodeChoice" ref="EG_TimeNodeChoice">
     * ref pml:EG_TimeNodeChoice
     * ```
     */
    egTimeNodeChoice?: IEGTimeNodeChoice[];
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TimeNodeList name="CT_TimeNodeList">
 * ```
 */
export interface ICTTimeNodeListDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=pml:EG_TimeNodeChoice ref=pml:EG_TimeNodeChoice maxOccurs="unbounded" minOccurs="1" name="EG_TimeNodeChoice" ref="EG_TimeNodeChoice">
     * ref pml:EG_TimeNodeChoice
     * ```
     */
    egTimeNodeChoice?: IEGTimeNodeChoiceDelta[];
    egTimeNodeChoiceOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_BuildList name="CT_BuildList">
 * ```
 */
export interface ICTBuildList {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLBuildParagraph name="bldP" type="CT_TLBuildParagraph">
     * ```
     */
    bldP?: ICTTLBuildParagraph[];
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLBuildDiagram name="bldDgm" type="CT_TLBuildDiagram">
     * ```
     */
    bldDgm?: ICTTLBuildDiagram[];
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLOleBuildChart name="bldOleChart" type="CT_TLOleBuildChart">
     * ```
     */
    bldOleChart?: ICTTLOleBuildChart[];
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLGraphicalObjectBuild name="bldGraphic" type="CT_TLGraphicalObjectBuild">
     * ```
     */
    bldGraphic?: ICTTLGraphicalObjectBuild[];
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_BuildList name="CT_BuildList">
 * ```
 */
export interface ICTBuildListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLBuildParagraph name="bldP" type="CT_TLBuildParagraph">
     * ```
     */
    bldP?: ICTTLBuildParagraphDelta[];
    bldPOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLBuildDiagram name="bldDgm" type="CT_TLBuildDiagram">
     * ```
     */
    bldDgm?: ICTTLBuildDiagramDelta[];
    bldDgmOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLOleBuildChart name="bldOleChart" type="CT_TLOleBuildChart">
     * ```
     */
    bldOleChart?: ICTTLOleBuildChartDelta[];
    bldOleChartOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLGraphicalObjectBuild name="bldGraphic" type="CT_TLGraphicalObjectBuild">
     * ```
     */
    bldGraphic?: ICTTLGraphicalObjectBuildDelta[];
    bldGraphicOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:group name=pml:EG_ExtensionListPml name="EG_ExtensionListPml">
 * ```
 */
export interface IEGExtensionListPml {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionPml maxOccurs="unbounded" minOccurs="0" name="ext" type="CT_ExtensionPml">
     * ```
     */
    ext?: ICTExtensionPml[];
}

/**
 * ```xml
 * <xsd:group name=pml:EG_ExtensionListPml name="EG_ExtensionListPml">
 * ```
 */
export interface IEGExtensionListPmlDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionPml maxOccurs="unbounded" minOccurs="0" name="ext" type="CT_ExtensionPml">
     * ```
     */
    ext?: ICTExtensionPmlDelta[];
    extOp?: IArrayOp;
}

export interface IPageEntry {
    id: string;
    data: Uint8Array;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_PresentationProperties name="CT_PresentationProperties">
 * ```
 */
export interface ICTPresentationProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_HtmlPublishProperties maxOccurs="1" minOccurs="0" name="htmlPubPr" type="CT_HtmlPublishProperties">
     * ```
     */
    htmlPubPr?: ICTHtmlPublishProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_WebProperties maxOccurs="1" minOccurs="0" name="webPr" type="CT_WebProperties">
     * ```
     */
    webPr?: ICTWebProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_PrintProperties maxOccurs="1" minOccurs="0" name="prnPr" type="CT_PrintProperties">
     * ```
     */
    prnPr?: ICTPrintProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ShowProperties maxOccurs="1" minOccurs="0" name="showPr" type="CT_ShowProperties">
     * ```
     */
    showPr?: ICTShowProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorMRUDmlA maxOccurs="1" minOccurs="0" name="clrMru" type="a:CT_ColorMRUDmlA">
     * ```
     */
    clrMru?: ICTColorMRUDmlA;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_PresentationProperties name="CT_PresentationProperties">
 * ```
 */
export interface ICTPresentationPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_HtmlPublishProperties maxOccurs="1" minOccurs="0" name="htmlPubPr" type="CT_HtmlPublishProperties">
     * ```
     */
    htmlPubPr?: ICTHtmlPublishPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_WebProperties maxOccurs="1" minOccurs="0" name="webPr" type="CT_WebProperties">
     * ```
     */
    webPr?: ICTWebPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_PrintProperties maxOccurs="1" minOccurs="0" name="prnPr" type="CT_PrintProperties">
     * ```
     */
    prnPr?: ICTPrintPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ShowProperties maxOccurs="1" minOccurs="0" name="showPr" type="CT_ShowProperties">
     * ```
     */
    showPr?: ICTShowPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorMRUDmlA maxOccurs="1" minOccurs="0" name="clrMru" type="a:CT_ColorMRUDmlA">
     * ```
     */
    clrMru?: ICTColorMRUDmlADelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextListStyle name="CT_TextListStyle">
 * ```
 */
export interface ICTTextListStyle {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="defPPr" type="CT_TextParagraphProperties">
     * ```
     */
    defPPr?: ICTTextParagraphProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="lvl1pPr" type="CT_TextParagraphProperties">
     * ```
     */
    lvl1pPr?: ICTTextParagraphProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="lvl2pPr" type="CT_TextParagraphProperties">
     * ```
     */
    lvl2pPr?: ICTTextParagraphProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="lvl3pPr" type="CT_TextParagraphProperties">
     * ```
     */
    lvl3pPr?: ICTTextParagraphProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="lvl4pPr" type="CT_TextParagraphProperties">
     * ```
     */
    lvl4pPr?: ICTTextParagraphProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="lvl5pPr" type="CT_TextParagraphProperties">
     * ```
     */
    lvl5pPr?: ICTTextParagraphProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="lvl6pPr" type="CT_TextParagraphProperties">
     * ```
     */
    lvl6pPr?: ICTTextParagraphProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="lvl7pPr" type="CT_TextParagraphProperties">
     * ```
     */
    lvl7pPr?: ICTTextParagraphProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="lvl8pPr" type="CT_TextParagraphProperties">
     * ```
     */
    lvl8pPr?: ICTTextParagraphProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="lvl9pPr" type="CT_TextParagraphProperties">
     * ```
     */
    lvl9pPr?: ICTTextParagraphProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextListStyle name="CT_TextListStyle">
 * ```
 */
export interface ICTTextListStyleDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="defPPr" type="CT_TextParagraphProperties">
     * ```
     */
    defPPr?: ICTTextParagraphPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="lvl1pPr" type="CT_TextParagraphProperties">
     * ```
     */
    lvl1pPr?: ICTTextParagraphPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="lvl2pPr" type="CT_TextParagraphProperties">
     * ```
     */
    lvl2pPr?: ICTTextParagraphPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="lvl3pPr" type="CT_TextParagraphProperties">
     * ```
     */
    lvl3pPr?: ICTTextParagraphPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="lvl4pPr" type="CT_TextParagraphProperties">
     * ```
     */
    lvl4pPr?: ICTTextParagraphPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="lvl5pPr" type="CT_TextParagraphProperties">
     * ```
     */
    lvl5pPr?: ICTTextParagraphPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="lvl6pPr" type="CT_TextParagraphProperties">
     * ```
     */
    lvl6pPr?: ICTTextParagraphPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="lvl7pPr" type="CT_TextParagraphProperties">
     * ```
     */
    lvl7pPr?: ICTTextParagraphPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="lvl8pPr" type="CT_TextParagraphProperties">
     * ```
     */
    lvl8pPr?: ICTTextParagraphPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="lvl9pPr" type="CT_TextParagraphProperties">
     * ```
     */
    lvl9pPr?: ICTTextParagraphPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableStyleList name="CT_TableStyleList">
 * ```
 */
export interface ICTTableStyleList {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableStyleDmlA maxOccurs="unbounded" minOccurs="0" name="tblStyle" type="CT_TableStyleDmlA">
     * ```
     */
    tblStyle?: ICTTableStyleDmlA[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="def" type="s:ST_Guid" use="required">
     * ```
     */
    def?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableStyleList name="CT_TableStyleList">
 * ```
 */
export interface ICTTableStyleListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableStyleDmlA maxOccurs="unbounded" minOccurs="0" name="tblStyle" type="CT_TableStyleDmlA">
     * ```
     */
    tblStyle?: ICTTableStyleDmlADelta[];
    tblStyleOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="def" type="s:ST_Guid" use="required">
     * ```
     */
    def?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ViewProperties name="CT_ViewProperties">
 * ```
 */
export interface ICTViewProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_NormalViewProperties maxOccurs="1" minOccurs="0" name="normalViewPr" type="CT_NormalViewProperties">
     * ```
     */
    normalViewPr?: ICTNormalViewProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_SlideViewProperties maxOccurs="1" minOccurs="0" name="slideViewPr" type="CT_SlideViewProperties">
     * ```
     */
    slideViewPr?: ICTSlideViewProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OutlineViewProperties maxOccurs="1" minOccurs="0" name="outlineViewPr" type="CT_OutlineViewProperties">
     * ```
     */
    outlineViewPr?: ICTOutlineViewProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_NotesTextViewProperties maxOccurs="1" minOccurs="0" name="notesTextViewPr" type="CT_NotesTextViewProperties">
     * ```
     */
    notesTextViewPr?: ICTNotesTextViewProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_SlideSorterViewProperties maxOccurs="1" minOccurs="0" name="sorterViewPr" type="CT_SlideSorterViewProperties">
     * ```
     */
    sorterViewPr?: ICTSlideSorterViewProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_NotesViewProperties maxOccurs="1" minOccurs="0" name="notesViewPr" type="CT_NotesViewProperties">
     * ```
     */
    notesViewPr?: ICTNotesViewProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositiveSize2D maxOccurs="1" minOccurs="0" name="gridSpacing" type="a:CT_PositiveSize2D">
     * ```
     */
    gridSpacing?: ICTPositiveSize2D;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_ViewType default="sldView" name="lastView" type="ST_ViewType" use="optional">
     * ```
     */
    lastView?: STViewType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="showComments" type="xsd:boolean" use="optional">
     * ```
     */
    showComments?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ViewProperties name="CT_ViewProperties">
 * ```
 */
export interface ICTViewPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_NormalViewProperties maxOccurs="1" minOccurs="0" name="normalViewPr" type="CT_NormalViewProperties">
     * ```
     */
    normalViewPr?: ICTNormalViewPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_SlideViewProperties maxOccurs="1" minOccurs="0" name="slideViewPr" type="CT_SlideViewProperties">
     * ```
     */
    slideViewPr?: ICTSlideViewPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OutlineViewProperties maxOccurs="1" minOccurs="0" name="outlineViewPr" type="CT_OutlineViewProperties">
     * ```
     */
    outlineViewPr?: ICTOutlineViewPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_NotesTextViewProperties maxOccurs="1" minOccurs="0" name="notesTextViewPr" type="CT_NotesTextViewProperties">
     * ```
     */
    notesTextViewPr?: ICTNotesTextViewPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_SlideSorterViewProperties maxOccurs="1" minOccurs="0" name="sorterViewPr" type="CT_SlideSorterViewProperties">
     * ```
     */
    sorterViewPr?: ICTSlideSorterViewPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_NotesViewProperties maxOccurs="1" minOccurs="0" name="notesViewPr" type="CT_NotesViewProperties">
     * ```
     */
    notesViewPr?: ICTNotesViewPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositiveSize2D maxOccurs="1" minOccurs="0" name="gridSpacing" type="a:CT_PositiveSize2D">
     * ```
     */
    gridSpacing?: ICTPositiveSize2DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_ViewType default="sldView" name="lastView" type="ST_ViewType" use="optional">
     * ```
     */
    lastView?: STViewType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="showComments" type="xsd:boolean" use="optional">
     * ```
     */
    showComments?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_EmbeddedFontList name="CT_EmbeddedFontList">
 * ```
 */
export interface ICTEmbeddedFontList {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmbeddedFontListEntry maxOccurs="unbounded" minOccurs="0" name="embeddedFont" type="CT_EmbeddedFontListEntry">
     * ```
     */
    embeddedFont?: ICTEmbeddedFontListEntry[];
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_EmbeddedFontList name="CT_EmbeddedFontList">
 * ```
 */
export interface ICTEmbeddedFontListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmbeddedFontListEntry maxOccurs="unbounded" minOccurs="0" name="embeddedFont" type="CT_EmbeddedFontListEntry">
     * ```
     */
    embeddedFont?: ICTEmbeddedFontListEntryDelta[];
    embeddedFontOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2012:CT_ExtendedGuideList name="CT_ExtendedGuideList">
 * ```
 */
export interface ICTExtendedGuideList {
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2012:CT_ExtendedGuide maxOccurs="unbounded" minOccurs="0" name="guide" type="CT_ExtendedGuide">
     * ```
     */
    guide?: ICTExtendedGuide[];
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="p:CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2012:CT_ExtendedGuideList name="CT_ExtendedGuideList">
 * ```
 */
export interface ICTExtendedGuideListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2012:CT_ExtendedGuide maxOccurs="unbounded" minOccurs="0" name="guide" type="CT_ExtendedGuide">
     * ```
     */
    guide?: ICTExtendedGuideDelta[];
    guideOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="p:CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ModifyVerifier name="CT_ModifyVerifier">
 * ```
 */
export interface ICTModifyVerifier {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="algorithmName" type="xsd:string" use="optional">
     * ```
     */
    algorithmName?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="hashValue" type="xsd:base64Binary" use="optional">
     * ```
     */
    hashValue?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="saltValue" type="xsd:base64Binary" use="optional">
     * ```
     */
    saltValue?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="spinValue" type="xsd:unsignedInt" use="optional">
     * ```
     */
    spinValue?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=sharedcommonsimpletypes:ST_CryptProv name="cryptProviderType" type="s:ST_CryptProv" use="optional">
     * ```
     */
    cryptProviderType?: STCryptProv;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=sharedcommonsimpletypes:ST_AlgClass name="cryptAlgorithmClass" type="s:ST_AlgClass" use="optional">
     * ```
     */
    cryptAlgorithmClass?: STAlgClass;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=sharedcommonsimpletypes:ST_AlgType name="cryptAlgorithmType" type="s:ST_AlgType" use="optional">
     * ```
     */
    cryptAlgorithmType?: STAlgType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="cryptAlgorithmSid" type="xsd:unsignedInt" use="optional">
     * ```
     */
    cryptAlgorithmSid?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="spinCount" type="xsd:unsignedInt" use="optional">
     * ```
     */
    spinCount?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="saltData" type="xsd:base64Binary" use="optional">
     * ```
     */
    saltData?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="hashData" type="xsd:base64Binary" use="optional">
     * ```
     */
    hashData?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="cryptProvider" type="xsd:string" use="optional">
     * ```
     */
    cryptProvider?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="algIdExt" type="xsd:unsignedInt" use="optional">
     * ```
     */
    algIdExt?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="algIdExtSource" type="xsd:string" use="optional">
     * ```
     */
    algIdExtSource?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="cryptProviderTypeExt" type="xsd:unsignedInt" use="optional">
     * ```
     */
    cryptProviderTypeExt?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="cryptProviderTypeExtSource" type="xsd:string" use="optional">
     * ```
     */
    cryptProviderTypeExtSource?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ModifyVerifier name="CT_ModifyVerifier">
 * ```
 */
export interface ICTModifyVerifierDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="algorithmName" type="xsd:string" use="optional">
     * ```
     */
    algorithmName?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="hashValue" type="xsd:base64Binary" use="optional">
     * ```
     */
    hashValue?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="saltValue" type="xsd:base64Binary" use="optional">
     * ```
     */
    saltValue?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="spinValue" type="xsd:unsignedInt" use="optional">
     * ```
     */
    spinValue?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=sharedcommonsimpletypes:ST_CryptProv name="cryptProviderType" type="s:ST_CryptProv" use="optional">
     * ```
     */
    cryptProviderType?: STCryptProv | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=sharedcommonsimpletypes:ST_AlgClass name="cryptAlgorithmClass" type="s:ST_AlgClass" use="optional">
     * ```
     */
    cryptAlgorithmClass?: STAlgClass | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=sharedcommonsimpletypes:ST_AlgType name="cryptAlgorithmType" type="s:ST_AlgType" use="optional">
     * ```
     */
    cryptAlgorithmType?: STAlgType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="cryptAlgorithmSid" type="xsd:unsignedInt" use="optional">
     * ```
     */
    cryptAlgorithmSid?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="spinCount" type="xsd:unsignedInt" use="optional">
     * ```
     */
    spinCount?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="saltData" type="xsd:base64Binary" use="optional">
     * ```
     */
    saltData?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="hashData" type="xsd:base64Binary" use="optional">
     * ```
     */
    hashData?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="cryptProvider" type="xsd:string" use="optional">
     * ```
     */
    cryptProvider?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="algIdExt" type="xsd:unsignedInt" use="optional">
     * ```
     */
    algIdExt?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="algIdExtSource" type="xsd:string" use="optional">
     * ```
     */
    algIdExtSource?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="cryptProviderTypeExt" type="xsd:unsignedInt" use="optional">
     * ```
     */
    cryptProviderTypeExt?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="cryptProviderTypeExtSource" type="xsd:string" use="optional">
     * ```
     */
    cryptProviderTypeExtSource?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_SlideSize name="CT_SlideSize">
 * ```
 */
export interface ICTSlideSize {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="cx" type="ST_SlideSizeCoordinate" use="required">
     * ```
     */
    cx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="cy" type="ST_SlideSizeCoordinate" use="required">
     * ```
     */
    cy?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_SlideSizeType default="custom" name="type" type="ST_SlideSizeType" use="optional">
     * ```
     */
    type?: STSlideSizeType;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_SlideSize name="CT_SlideSize">
 * ```
 */
export interface ICTSlideSizeDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="cx" type="ST_SlideSizeCoordinate" use="required">
     * ```
     */
    cx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="cy" type="ST_SlideSizeCoordinate" use="required">
     * ```
     */
    cy?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_SlideSizeType default="custom" name="type" type="ST_SlideSizeType" use="optional">
     * ```
     */
    type?: STSlideSizeType | null;
}

export enum STPageType {
    PBDEFAULT = 0,
    slide = 1,
    slideMaster = 2,
    slideLayout = 3,
    notes = 4,
    notesMaster = 5,
}

export interface ISlideOptions {
    /** slide关联的layout */
    layoutId?: string;
}

export interface ISlideLayoutOptions {
    /** layout所属的master */
    masterId?: string;
}

export interface ISlideMasterOptions {
    /** master关联的theme */
    themeId?: string;
}

export interface INotesOptions {
    /** notes关联的slide */
    slideId?: string;
}

export interface INotesMasterOptions {
    /** notes master的theme，需要与slides master的区分开 */
    themeId?: string;
}

export interface IPageProperties {
    name?: string | null;
    bg?: ICTBackgroundPmlDelta | null;
    transition?: ICTSlideTransitionDelta | null;
}

export interface ISlideProperties {
    layoutId?: string;
    show?: boolean | null;
    showMasterSp?: boolean | null;
    showMasterPhAnim?: boolean | null;
    clrMapOvr?: ICTColorMappingOverrideDelta | null;
}

export interface ISlideLayoutProperties {
    slideMasterId?: string;
    layoutType?: STSlideLayoutType;
    hf?: ICTHeaderFooterPmlDelta | null;
    showMasterSp?: boolean | null;
    showMasterPhAnim?: boolean | null;
    clrMapOvr?: ICTColorMappingOverrideDelta | null;
    matchingName?: string | null;
    /** 参考线，走全量覆盖 */
    slideGuideList?: ICTExtendedGuideListDelta;
}

export interface ISlideMasterProperties {
    themeId?: string;
    hf?: ICTHeaderFooterPmlDelta | null;
    clrMap?: ICTColorMappingDelta | null;
    txStyles?: ICTSlideMasterTextStylesDelta | null;
    /** 参考线，走全量覆盖 */
    slideGuideList?: ICTExtendedGuideListDelta;
}

export interface INotesProperties {}

export interface INotesMasterProperties {
    notesStyle?: ICTTextListStyleDelta;
    hf?: ICTHeaderFooterPmlDelta | null;
    clrMap?: ICTColorMappingDelta | null;
}

export enum ShapeType {
    /** 未知 */
    Unknown = 0,
    /** 普通形状 */
    Shape = 1,
    /** 组合 */
    GroupShape = 2,
    /** 连接线 */
    Connector = 3,
    /** 图片 */
    Picture = 4,
    /** 表格（继承自 GraphicObjectFrame） */
    GraphicalObjectFrameTable = 5,
    /** 图表（继承自 GraphicObjectFrame） */
    GraphicalObjectFrameChart = 6,
    /** 未细分的 GraphicObjectFrame，导入时 Keep 用 */
    GraphicalObjectFrameUnknown = 7,
    Ink = 8,
    Equation = 9,
    /** 旧的 SmartArt，导入导出按照组合操作 */
    GraphicalObjectFrameSmartart = 10,
    GraphicalObjectFrameOleObject = 11,
    /** 3D 模型 */
    GraphicalObjectFrameThreeDimentionalModel = 12,
    /** 新的 SmartArt */
    GraphicalObjectFrameSmartartNew = 13,
}

export interface IShapeDelta {
    /** 形状可见属性（不包含 xfrm） */
    spPr?: ICTShapePropertiesDelta | null;
    /** 不可见属性，表达形状之间的关系、业务约束等 */
    nvPr?: INonVisualPropertiesDelta | null;
    /** 主题样式关联 */
    style?: ICTShapeStyleDelta | null;
    /** Shape 专属属性 */
    shape?: IShapePropertiesDelta | null;
    /** GraphicalObjectFrame 专属属性 */
    graphicalObjectFrame?: IGraphicalObjectFrameDelta | null;
    /** Picture 专属属性 */
    picture?: IPicturePropertiesDelta | null;
    /** table 属性 */
    table?: ICTTablePropertiesDelta | null;
    /** ink 属性 */
    ink?: IInkPropertiesDelta | null;
    /** smart_art属性 */
    smartArt?: ISmartArtPropertiesDelta | null;
    /** ole_object属性 */
    oleObject?: IOleObjectPropertiesDelta | null;
    /** 3D 模型属性 */
    model_3d?: IModel3DPropertiesDelta | null;
}

export interface IAddTableOptions {
    rowCount?: number;
    colCount?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Transform2D name="CT_Transform2D">
 * ```
 */
export interface ICTTransform2D {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point2D maxOccurs="1" minOccurs="0" name="off" type="CT_Point2D">
     * ```
     */
    off?: ICTPoint2D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositiveSize2D maxOccurs="1" minOccurs="0" name="ext" type="CT_PositiveSize2D">
     * ```
     */
    ext?: ICTPositiveSize2D;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="rot" type="ST_AngleDmlA" use="optional">
     * ```
     */
    rot?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="flipH" type="xsd:boolean" use="optional">
     * ```
     */
    flipH?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="flipV" type="xsd:boolean" use="optional">
     * ```
     */
    flipV?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Transform2D name="CT_Transform2D">
 * ```
 */
export interface ICTTransform2DDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point2D maxOccurs="1" minOccurs="0" name="off" type="CT_Point2D">
     * ```
     */
    off?: ICTPoint2DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositiveSize2D maxOccurs="1" minOccurs="0" name="ext" type="CT_PositiveSize2D">
     * ```
     */
    ext?: ICTPositiveSize2DDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="rot" type="ST_AngleDmlA" use="optional">
     * ```
     */
    rot?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="flipH" type="xsd:boolean" use="optional">
     * ```
     */
    flipH?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="flipV" type="xsd:boolean" use="optional">
     * ```
     */
    flipV?: boolean | null;
}

export interface ICTGroupChildTransform {
    chOff?: ICTPoint2DDelta | null;
    chExt?: ICTPositiveSize2DDelta | null;
}

export enum ReorderOpType {
    /** 置于底层 */
    Back = 0,
    /** 后移一层 */
    Backward = 1,
    /** 前移一层 */
    Forward = 2,
    /** 置于顶层 */
    Front = 3,
}

export interface ITableRangeData {
    startRowIndex?: number;
    startColIndex?: number;
    rowSpan?: number;
    colSpan?: number;
}

export interface IWidthInfo {
    index?: number;
    width?: number;
}

export interface IHeightInfo {
    index?: number;
    height?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableCellProperties name="CT_TableCellProperties">
 * ```
 */
export interface ICTTableCellProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="lnL" type="CT_LineProperties">
     * ```
     */
    lnL?: ICTLineProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="lnR" type="CT_LineProperties">
     * ```
     */
    lnR?: ICTLineProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="lnT" type="CT_LineProperties">
     * ```
     */
    lnT?: ICTLineProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="lnB" type="CT_LineProperties">
     * ```
     */
    lnB?: ICTLineProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="lnTlToBr" type="CT_LineProperties">
     * ```
     */
    lnTlToBr?: ICTLineProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="lnBlToTr" type="CT_LineProperties">
     * ```
     */
    lnBlToTr?: ICTLineProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Cell3D maxOccurs="1" minOccurs="0" name="cell3D" type="CT_Cell3D">
     * ```
     */
    cell3D?: ICTCell3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HeadersDmlA minOccurs="0" name="headers" type="CT_HeadersDmlA">
     * ```
     */
    headers?: ICTHeadersDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="91440" name="marL" type="ST_Coordinate32" use="optional">
     * ```
     */
    marL?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="91440" name="marR" type="ST_Coordinate32" use="optional">
     * ```
     */
    marR?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="45720" name="marT" type="ST_Coordinate32" use="optional">
     * ```
     */
    marT?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="45720" name="marB" type="ST_Coordinate32" use="optional">
     * ```
     */
    marB?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextVerticalType default="horz" name="vert" type="ST_TextVerticalType" use="optional">
     * ```
     */
    vert?: STTextVerticalType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextAnchoringType default="t" name="anchor" type="ST_TextAnchoringType" use="optional">
     * ```
     */
    anchor?: STTextAnchoringType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="anchorCtr" type="xsd:boolean" use="optional">
     * ```
     */
    anchorCtr?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextHorzOverflowType default="clip" name="horzOverflow" type="ST_TextHorzOverflowType" use="optional">
     * ```
     */
    horzOverflow?: STTextHorzOverflowType;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableCellProperties name="CT_TableCellProperties">
 * ```
 */
export interface ICTTableCellPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="lnL" type="CT_LineProperties">
     * ```
     */
    lnL?: ICTLinePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="lnR" type="CT_LineProperties">
     * ```
     */
    lnR?: ICTLinePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="lnT" type="CT_LineProperties">
     * ```
     */
    lnT?: ICTLinePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="lnB" type="CT_LineProperties">
     * ```
     */
    lnB?: ICTLinePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="lnTlToBr" type="CT_LineProperties">
     * ```
     */
    lnTlToBr?: ICTLinePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="lnBlToTr" type="CT_LineProperties">
     * ```
     */
    lnBlToTr?: ICTLinePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Cell3D maxOccurs="1" minOccurs="0" name="cell3D" type="CT_Cell3D">
     * ```
     */
    cell3D?: ICTCell3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HeadersDmlA minOccurs="0" name="headers" type="CT_HeadersDmlA">
     * ```
     */
    headers?: ICTHeadersDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="91440" name="marL" type="ST_Coordinate32" use="optional">
     * ```
     */
    marL?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="91440" name="marR" type="ST_Coordinate32" use="optional">
     * ```
     */
    marR?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="45720" name="marT" type="ST_Coordinate32" use="optional">
     * ```
     */
    marT?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="45720" name="marB" type="ST_Coordinate32" use="optional">
     * ```
     */
    marB?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextVerticalType default="horz" name="vert" type="ST_TextVerticalType" use="optional">
     * ```
     */
    vert?: STTextVerticalType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextAnchoringType default="t" name="anchor" type="ST_TextAnchoringType" use="optional">
     * ```
     */
    anchor?: STTextAnchoringType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="anchorCtr" type="xsd:boolean" use="optional">
     * ```
     */
    anchorCtr?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextHorzOverflowType default="clip" name="horzOverflow" type="ST_TextHorzOverflowType" use="optional">
     * ```
     */
    horzOverflow?: STTextHorzOverflowType | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean | null;
}

export enum STTLTimeNodeType {
    PBDEFAULT = 0,
    clickEffect = 1,
    withEffect = 2,
    afterEffect = 3,
    mainSeq = 4,
    interactiveSeq = 5,
    clickPar = 6,
    withGroup = 7,
    afterGroup = 8,
    tmRoot = 9,
}

/**
 * ```xml
 * <xsd:group name=pml:EG_TimeNodeChoice name="EG_TimeNodeChoice">
 * ```
 */
export interface IEGTimeNodeChoice {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeNodeParallel name="par" type="CT_TLTimeNodeParallel">
     * ```
     */
    par?: ICTTLTimeNodeParallel;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeNodeSequence name="seq" type="CT_TLTimeNodeSequence">
     * ```
     */
    seq?: ICTTLTimeNodeSequence;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeNodeExclusive name="excl" type="CT_TLTimeNodeExclusive">
     * ```
     */
    excl?: ICTTLTimeNodeExclusive;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimateBehavior name="anim" type="CT_TLAnimateBehavior">
     * ```
     */
    anim?: ICTTLAnimateBehavior;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimateColorBehavior name="animClr" type="CT_TLAnimateColorBehavior">
     * ```
     */
    animClr?: ICTTLAnimateColorBehavior;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimateEffectBehavior name="animEffect" type="CT_TLAnimateEffectBehavior">
     * ```
     */
    animEffect?: ICTTLAnimateEffectBehavior;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimateMotionBehavior name="animMotion" type="CT_TLAnimateMotionBehavior">
     * ```
     */
    animMotion?: ICTTLAnimateMotionBehavior;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimateRotationBehavior name="animRot" type="CT_TLAnimateRotationBehavior">
     * ```
     */
    animRot?: ICTTLAnimateRotationBehavior;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimateScaleBehavior name="animScale" type="CT_TLAnimateScaleBehavior">
     * ```
     */
    animScale?: ICTTLAnimateScaleBehavior;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommandBehavior name="cmd" type="CT_TLCommandBehavior">
     * ```
     */
    cmd?: ICTTLCommandBehavior;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLSetBehavior name="set" type="CT_TLSetBehavior">
     * ```
     */
    set?: ICTTLSetBehavior;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLMediaNodeAudio name="audio" type="CT_TLMediaNodeAudio">
     * ```
     */
    audio?: ICTTLMediaNodeAudio;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLMediaNodeVideo name="video" type="CT_TLMediaNodeVideo">
     * ```
     */
    video?: ICTTLMediaNodeVideo;
}

/**
 * ```xml
 * <xsd:group name=pml:EG_TimeNodeChoice name="EG_TimeNodeChoice">
 * ```
 */
export interface IEGTimeNodeChoiceDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeNodeParallel name="par" type="CT_TLTimeNodeParallel">
     * ```
     */
    par?: ICTTLTimeNodeParallelDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeNodeSequence name="seq" type="CT_TLTimeNodeSequence">
     * ```
     */
    seq?: ICTTLTimeNodeSequenceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeNodeExclusive name="excl" type="CT_TLTimeNodeExclusive">
     * ```
     */
    excl?: ICTTLTimeNodeExclusiveDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimateBehavior name="anim" type="CT_TLAnimateBehavior">
     * ```
     */
    anim?: ICTTLAnimateBehaviorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimateColorBehavior name="animClr" type="CT_TLAnimateColorBehavior">
     * ```
     */
    animClr?: ICTTLAnimateColorBehaviorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimateEffectBehavior name="animEffect" type="CT_TLAnimateEffectBehavior">
     * ```
     */
    animEffect?: ICTTLAnimateEffectBehaviorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimateMotionBehavior name="animMotion" type="CT_TLAnimateMotionBehavior">
     * ```
     */
    animMotion?: ICTTLAnimateMotionBehaviorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimateRotationBehavior name="animRot" type="CT_TLAnimateRotationBehavior">
     * ```
     */
    animRot?: ICTTLAnimateRotationBehaviorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimateScaleBehavior name="animScale" type="CT_TLAnimateScaleBehavior">
     * ```
     */
    animScale?: ICTTLAnimateScaleBehaviorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommandBehavior name="cmd" type="CT_TLCommandBehavior">
     * ```
     */
    cmd?: ICTTLCommandBehaviorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLSetBehavior name="set" type="CT_TLSetBehavior">
     * ```
     */
    set?: ICTTLSetBehaviorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLMediaNodeAudio name="audio" type="CT_TLMediaNodeAudio">
     * ```
     */
    audio?: ICTTLMediaNodeAudioDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLMediaNodeVideo name="video" type="CT_TLMediaNodeVideo">
     * ```
     */
    video?: ICTTLMediaNodeVideoDelta | null;
}

/** A factory function to create a clear delta object of IEGTimeNodeChoiceDelta. */
export function createIEGTimeNodeChoiceClearDelta(): IEGTimeNodeChoiceDelta {
    return {
        par: null,
        seq: null,
        excl: null,
        anim: null,
        animClr: null,
        animEffect: null,
        animMotion: null,
        animRot: null,
        animScale: null,
        cmd: null,
        set: null,
        audio: null,
        video: null,
    };
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_BaseStyles name="CT_BaseStyles">
 * ```
 */
export interface ICTBaseStyles {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorScheme maxOccurs="1" minOccurs="1" name="clrScheme" type="CT_ColorScheme">
     * ```
     */
    clrScheme?: ICTColorScheme;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FontSchemeDmlA maxOccurs="1" minOccurs="1" name="fontScheme" type="CT_FontSchemeDmlA">
     * ```
     */
    fontScheme?: ICTFontSchemeDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StyleMatrix maxOccurs="1" minOccurs="1" name="fmtScheme" type="CT_StyleMatrix">
     * ```
     */
    fmtScheme?: ICTStyleMatrix;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_BaseStyles name="CT_BaseStyles">
 * ```
 */
export interface ICTBaseStylesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorScheme maxOccurs="1" minOccurs="1" name="clrScheme" type="CT_ColorScheme">
     * ```
     */
    clrScheme?: ICTColorSchemeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FontSchemeDmlA maxOccurs="1" minOccurs="1" name="fontScheme" type="CT_FontSchemeDmlA">
     * ```
     */
    fontScheme?: ICTFontSchemeDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StyleMatrix maxOccurs="1" minOccurs="1" name="fmtScheme" type="CT_StyleMatrix">
     * ```
     */
    fmtScheme?: ICTStyleMatrixDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextParagraphProperties name="CT_TextParagraphProperties">
 * ```
 */
export interface ICTTextParagraphProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextSpacing maxOccurs="1" minOccurs="0" name="lnSpc" type="CT_TextSpacing">
     * ```
     */
    lnSpc?: ICTTextSpacing;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextSpacing maxOccurs="1" minOccurs="0" name="spcBef" type="CT_TextSpacing">
     * ```
     */
    spcBef?: ICTTextSpacing;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextSpacing maxOccurs="1" minOccurs="0" name="spcAft" type="CT_TextSpacing">
     * ```
     */
    spcAft?: ICTTextSpacing;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="buClr" type="CT_ColorDmlA">
     * ```
     */
    buClr?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBulletSizePercent name="buSzPct" type="CT_TextBulletSizePercent">
     * ```
     */
    buSzPct?: ICTTextBulletSizePercent;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBulletSizePoint name="buSzPts" type="CT_TextBulletSizePoint">
     * ```
     */
    buSzPts?: ICTTextBulletSizePoint;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFont name="buFont" type="CT_TextFont">
     * ```
     */
    buFont?: ICTTextFont;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextAutonumberBullet name="buAutoNum" type="CT_TextAutonumberBullet">
     * ```
     */
    buAutoNum?: ICTTextAutonumberBullet;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextCharBullet name="buChar" type="CT_TextCharBullet">
     * ```
     */
    buChar?: ICTTextCharBullet;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBlipBullet name="buBlip" type="CT_TextBlipBullet">
     * ```
     */
    buBlip?: ICTTextBlipBullet;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextTabStopList maxOccurs="1" minOccurs="0" name="tabLst" type="CT_TextTabStopList">
     * ```
     */
    tabLst?: ICTTextTabStopList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextCharacterProperties maxOccurs="1" minOccurs="0" name="defRPr" type="CT_TextCharacterProperties">
     * ```
     */
    defRPr?: ICTTextCharacterProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="marL" type="ST_TextMargin" use="optional">
     * ```
     */
    marL?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="marR" type="ST_TextMargin" use="optional">
     * ```
     */
    marR?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="lvl" type="ST_TextIndentLevelType" use="optional">
     * ```
     */
    lvl?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="indent" type="ST_TextIndent" use="optional">
     * ```
     */
    indent?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextAlignType name="algn" type="ST_TextAlignType" use="optional">
     * ```
     */
    algn?: STTextAlignType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="defTabSz" type="ST_Coordinate32" use="optional">
     * ```
     */
    defTabSz?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="rtl" type="xsd:boolean" use="optional">
     * ```
     */
    rtl?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="eaLnBrk" type="xsd:boolean" use="optional">
     * ```
     */
    eaLnBrk?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextFontAlignType name="fontAlgn" type="ST_TextFontAlignType" use="optional">
     * ```
     */
    fontAlgn?: STTextFontAlignType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="latinLnBrk" type="xsd:boolean" use="optional">
     * ```
     */
    latinLnBrk?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="hangingPunct" type="xsd:boolean" use="optional">
     * ```
     */
    hangingPunct?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBulletColorFollowText convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="buClrTx" type="CT_TextBulletColorFollowText">
     * ```
     */
    buClrTx?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBulletSizeFollowText convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="buSzTx" type="CT_TextBulletSizeFollowText">
     * ```
     */
    buSzTx?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBulletTypefaceFollowText convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="buFontTx" type="CT_TextBulletTypefaceFollowText">
     * ```
     */
    buFontTx?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextNoBullet convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="buNone" type="CT_TextNoBullet">
     * ```
     */
    buNone?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextParagraphProperties name="CT_TextParagraphProperties">
 * ```
 */
export interface ICTTextParagraphPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextSpacing maxOccurs="1" minOccurs="0" name="lnSpc" type="CT_TextSpacing">
     * ```
     */
    lnSpc?: ICTTextSpacingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextSpacing maxOccurs="1" minOccurs="0" name="spcBef" type="CT_TextSpacing">
     * ```
     */
    spcBef?: ICTTextSpacingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextSpacing maxOccurs="1" minOccurs="0" name="spcAft" type="CT_TextSpacing">
     * ```
     */
    spcAft?: ICTTextSpacingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="buClr" type="CT_ColorDmlA">
     * ```
     */
    buClr?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBulletSizePercent name="buSzPct" type="CT_TextBulletSizePercent">
     * ```
     */
    buSzPct?: ICTTextBulletSizePercentDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBulletSizePoint name="buSzPts" type="CT_TextBulletSizePoint">
     * ```
     */
    buSzPts?: ICTTextBulletSizePointDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFont name="buFont" type="CT_TextFont">
     * ```
     */
    buFont?: ICTTextFontDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextAutonumberBullet name="buAutoNum" type="CT_TextAutonumberBullet">
     * ```
     */
    buAutoNum?: ICTTextAutonumberBulletDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextCharBullet name="buChar" type="CT_TextCharBullet">
     * ```
     */
    buChar?: ICTTextCharBulletDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBlipBullet name="buBlip" type="CT_TextBlipBullet">
     * ```
     */
    buBlip?: ICTTextBlipBulletDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextTabStopList maxOccurs="1" minOccurs="0" name="tabLst" type="CT_TextTabStopList">
     * ```
     */
    tabLst?: ICTTextTabStopListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextCharacterProperties maxOccurs="1" minOccurs="0" name="defRPr" type="CT_TextCharacterProperties">
     * ```
     */
    defRPr?: ICTTextCharacterPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="marL" type="ST_TextMargin" use="optional">
     * ```
     */
    marL?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="marR" type="ST_TextMargin" use="optional">
     * ```
     */
    marR?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="lvl" type="ST_TextIndentLevelType" use="optional">
     * ```
     */
    lvl?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="indent" type="ST_TextIndent" use="optional">
     * ```
     */
    indent?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextAlignType name="algn" type="ST_TextAlignType" use="optional">
     * ```
     */
    algn?: STTextAlignType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="defTabSz" type="ST_Coordinate32" use="optional">
     * ```
     */
    defTabSz?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="rtl" type="xsd:boolean" use="optional">
     * ```
     */
    rtl?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="eaLnBrk" type="xsd:boolean" use="optional">
     * ```
     */
    eaLnBrk?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextFontAlignType name="fontAlgn" type="ST_TextFontAlignType" use="optional">
     * ```
     */
    fontAlgn?: STTextFontAlignType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="latinLnBrk" type="xsd:boolean" use="optional">
     * ```
     */
    latinLnBrk?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="hangingPunct" type="xsd:boolean" use="optional">
     * ```
     */
    hangingPunct?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBulletColorFollowText convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="buClrTx" type="CT_TextBulletColorFollowText">
     * ```
     */
    buClrTx?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBulletSizeFollowText convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="buSzTx" type="CT_TextBulletSizeFollowText">
     * ```
     */
    buSzTx?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBulletTypefaceFollowText convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="buFontTx" type="CT_TextBulletTypefaceFollowText">
     * ```
     */
    buFontTx?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextNoBullet convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="buNone" type="CT_TextNoBullet">
     * ```
     */
    buNone?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextCharacterProperties name="CT_TextCharacterProperties">
 * ```
 */
export interface ICTTextCharacterProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="ln" type="CT_LineProperties">
     * ```
     */
    ln?: ICTLineProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectList maxOccurs="1" minOccurs="1" name="effectLst" type="CT_EffectList">
     * ```
     */
    effectLst?: ICTEffectList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="effectDag" type="CT_EffectContainer">
     * ```
     */
    effectDag?: ICTEffectContainer;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="0" name="highlight" type="CT_ColorDmlA">
     * ```
     */
    highlight?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="uLn" type="CT_LineProperties">
     * ```
     */
    uLn?: ICTLineProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextUnderlineFillGroupWrapper name="uFill" type="CT_TextUnderlineFillGroupWrapper">
     * ```
     */
    uFill?: ICTTextUnderlineFillGroupWrapper;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFont maxOccurs="1" minOccurs="0" name="latin" type="CT_TextFont">
     * ```
     */
    latin?: ICTTextFont;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFont maxOccurs="1" minOccurs="0" name="ea" type="CT_TextFont">
     * ```
     */
    ea?: ICTTextFont;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFont maxOccurs="1" minOccurs="0" name="cs" type="CT_TextFont">
     * ```
     */
    cs?: ICTTextFont;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFont maxOccurs="1" minOccurs="0" name="sym" type="CT_TextFont">
     * ```
     */
    sym?: ICTTextFont;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HyperlinkDmlA maxOccurs="1" minOccurs="0" name="hlinkClick" type="CT_HyperlinkDmlA">
     * ```
     */
    hlinkClick?: ICTHyperlinkDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HyperlinkDmlA maxOccurs="1" minOccurs="0" name="hlinkMouseOver" type="CT_HyperlinkDmlA">
     * ```
     */
    hlinkMouseOver?: ICTHyperlinkDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BooleanDmlA minOccurs="0" name="rtl" type="CT_BooleanDmlA">
     * ```
     */
    rtl?: ICTBooleanDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="kumimoji" type="xsd:boolean" use="optional">
     * ```
     */
    kumimoji?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="lang" type="s:ST_Lang" use="optional">
     * ```
     */
    lang?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="altLang" type="s:ST_Lang" use="optional">
     * ```
     */
    altLang?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="sz" type="ST_TextFontSize" use="optional">
     * ```
     */
    sz?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="b" type="xsd:boolean" use="optional">
     * ```
     */
    b?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="i" type="xsd:boolean" use="optional">
     * ```
     */
    i?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextUnderlineType name="u" type="ST_TextUnderlineType" use="optional">
     * ```
     */
    u?: STTextUnderlineType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextStrikeType name="strike" type="ST_TextStrikeType" use="optional">
     * ```
     */
    strike?: STTextStrikeType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="kern" type="ST_TextNonNegativePoint" use="optional">
     * ```
     */
    kern?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextCapsType default="none" name="cap" type="ST_TextCapsType" use="optional">
     * ```
     */
    cap?: STTextCapsType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_TwipsMeasurePrecise" name="spc" type="ST_TextPoint" use="optional">
     * ```
     */
    spc?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="normalizeH" type="xsd:boolean" use="optional">
     * ```
     */
    normalizeH?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" name="baseline" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    baseline?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="noProof" type="xsd:boolean" use="optional">
     * ```
     */
    noProof?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="dirty" type="xsd:boolean" use="optional">
     * ```
     */
    dirty?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="err" type="xsd:boolean" use="optional">
     * ```
     */
    err?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="smtClean" type="xsd:boolean" use="optional">
     * ```
     */
    smtClean?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="0" name="smtId" type="xsd:unsignedInt" use="optional">
     * ```
     */
    smtId?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="bmk" type="xsd:string" use="optional">
     * ```
     */
    bmk?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextUnderlineLineFollowText convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="uLnTx" type="CT_TextUnderlineLineFollowText">
     * ```
     */
    uLnTx?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextUnderlineFillFollowText convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="uFillTx" type="CT_TextUnderlineFillFollowText">
     * ```
     */
    uFillTx?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="isPlaceholder" skipIO="true" type="xsd:boolean">
     * ```
     */
    isPlaceholder?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextCharacterProperties name="CT_TextCharacterProperties">
 * ```
 */
export interface ICTTextCharacterPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="ln" type="CT_LineProperties">
     * ```
     */
    ln?: ICTLinePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectList maxOccurs="1" minOccurs="1" name="effectLst" type="CT_EffectList">
     * ```
     */
    effectLst?: ICTEffectListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="effectDag" type="CT_EffectContainer">
     * ```
     */
    effectDag?: ICTEffectContainerDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="0" name="highlight" type="CT_ColorDmlA">
     * ```
     */
    highlight?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="uLn" type="CT_LineProperties">
     * ```
     */
    uLn?: ICTLinePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextUnderlineFillGroupWrapper name="uFill" type="CT_TextUnderlineFillGroupWrapper">
     * ```
     */
    uFill?: ICTTextUnderlineFillGroupWrapperDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFont maxOccurs="1" minOccurs="0" name="latin" type="CT_TextFont">
     * ```
     */
    latin?: ICTTextFontDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFont maxOccurs="1" minOccurs="0" name="ea" type="CT_TextFont">
     * ```
     */
    ea?: ICTTextFontDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFont maxOccurs="1" minOccurs="0" name="cs" type="CT_TextFont">
     * ```
     */
    cs?: ICTTextFontDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFont maxOccurs="1" minOccurs="0" name="sym" type="CT_TextFont">
     * ```
     */
    sym?: ICTTextFontDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HyperlinkDmlA maxOccurs="1" minOccurs="0" name="hlinkClick" type="CT_HyperlinkDmlA">
     * ```
     */
    hlinkClick?: ICTHyperlinkDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HyperlinkDmlA maxOccurs="1" minOccurs="0" name="hlinkMouseOver" type="CT_HyperlinkDmlA">
     * ```
     */
    hlinkMouseOver?: ICTHyperlinkDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BooleanDmlA minOccurs="0" name="rtl" type="CT_BooleanDmlA">
     * ```
     */
    rtl?: ICTBooleanDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="kumimoji" type="xsd:boolean" use="optional">
     * ```
     */
    kumimoji?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="lang" type="s:ST_Lang" use="optional">
     * ```
     */
    lang?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="altLang" type="s:ST_Lang" use="optional">
     * ```
     */
    altLang?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="sz" type="ST_TextFontSize" use="optional">
     * ```
     */
    sz?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="b" type="xsd:boolean" use="optional">
     * ```
     */
    b?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="i" type="xsd:boolean" use="optional">
     * ```
     */
    i?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextUnderlineType name="u" type="ST_TextUnderlineType" use="optional">
     * ```
     */
    u?: STTextUnderlineType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextStrikeType name="strike" type="ST_TextStrikeType" use="optional">
     * ```
     */
    strike?: STTextStrikeType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="kern" type="ST_TextNonNegativePoint" use="optional">
     * ```
     */
    kern?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextCapsType default="none" name="cap" type="ST_TextCapsType" use="optional">
     * ```
     */
    cap?: STTextCapsType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_TwipsMeasurePrecise" name="spc" type="ST_TextPoint" use="optional">
     * ```
     */
    spc?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="normalizeH" type="xsd:boolean" use="optional">
     * ```
     */
    normalizeH?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" name="baseline" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    baseline?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="noProof" type="xsd:boolean" use="optional">
     * ```
     */
    noProof?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="dirty" type="xsd:boolean" use="optional">
     * ```
     */
    dirty?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="err" type="xsd:boolean" use="optional">
     * ```
     */
    err?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="smtClean" type="xsd:boolean" use="optional">
     * ```
     */
    smtClean?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="0" name="smtId" type="xsd:unsignedInt" use="optional">
     * ```
     */
    smtId?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="bmk" type="xsd:string" use="optional">
     * ```
     */
    bmk?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextUnderlineLineFollowText convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="uLnTx" type="CT_TextUnderlineLineFollowText">
     * ```
     */
    uLnTx?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextUnderlineFillFollowText convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="uFillTx" type="CT_TextUnderlineFillFollowText">
     * ```
     */
    uFillTx?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="isPlaceholder" skipIO="true" type="xsd:boolean">
     * ```
     */
    isPlaceholder?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextFieldDmlA name="CT_TextFieldDmlA">
 * ```
 */
export interface ICTTextFieldDmlA {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextCharacterProperties maxOccurs="1" minOccurs="0" name="rPr" type="CT_TextCharacterProperties">
     * ```
     */
    rPr?: ICTTextCharacterProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="pPr" type="CT_TextParagraphProperties">
     * ```
     */
    pPr?: ICTTextParagraphProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="t" type="xsd:string">
     * ```
     */
    t?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="s:ST_Guid" use="required">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="type" type="xsd:string" use="optional">
     * ```
     */
    type?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=tencentdocumentdml:ST_FldType name="fldType" type="tddml:ST_FldType" use="required">
     * ```
     */
    fldType?: STFldType;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextFieldDmlA name="CT_TextFieldDmlA">
 * ```
 */
export interface ICTTextFieldDmlADelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextCharacterProperties maxOccurs="1" minOccurs="0" name="rPr" type="CT_TextCharacterProperties">
     * ```
     */
    rPr?: ICTTextCharacterPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="pPr" type="CT_TextParagraphProperties">
     * ```
     */
    pPr?: ICTTextParagraphPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="t" type="xsd:string">
     * ```
     */
    t?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="s:ST_Guid" use="required">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="type" type="xsd:string" use="optional">
     * ```
     */
    type?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=tencentdocumentdml:ST_FldType name="fldType" type="tddml:ST_FldType" use="required">
     * ```
     */
    fldType?: STFldType | null;
}

export enum TextPropertyType {
    Unknown = 0,
    Run = 1,
    Paragraph = 2,
    Field = 3,
}

/** 文本属性 */
export interface ITextPropertyData {
    /** 文本属性的起始位点索引 */
    index?: number;
    /** 改文本属性覆盖文本的长度 */
    count?: number;
    /** 文本属性类型，一个类型仅填一种对应的 properties 数据，如为 */
    textPropertyType?: TextPropertyType;
    /** TextPropertyType_RUN 时下方属性仅填入 run_properties */
    paragraphProperties?: ICTTextParagraphPropertiesDelta;
    /** run属性 */
    runProperties?: ICTTextCharacterPropertiesDelta;
    /** field属性 */
    fieldProperties?: ICTTextFieldDmlADelta;
}

export interface IChartSpaceProperties {
    /** 图表的核心数据 */
    chart?: IChartProperties | null;
    spPr?: ICTShapePropertiesDelta | null;
    txPr?: ICTTextBodyDelta | null;
    date1904?: ICTBooleanDmlCDelta | null;
    lang?: ICTTextLanguageIDDelta | null;
    roundedCorners?: ICTBooleanDmlCDelta | null;
    c14Style?: ICTStyleMsC2007Delta | null;
    clrMapOvr?: ICTColorMappingDelta | null;
    pivotSource?: ICTPivotSourceDelta | null;
    protection?: ICTProtectionDelta | null;
    externalData?: ICTExternalDataDelta | null;
    printSettings?: ICTPrintSettingsDelta | null;
    extLst?: ICTExtensionListDmlCDelta | null;
    style?: ICTStyleDmlCDelta | null;
    colorStyle?: ICTColorStyleDelta | null;
    chartStyle?: ICTChartStyleDelta | null;
}

export enum SubChartType {
    /** 未知 */
    Unknown = 0,
    AreaChart = 1,
    Area3DChart = 2,
    LineChart = 3,
    Line3DChart = 4,
    StockChart = 5,
    RadarChart = 6,
    ScatterChart = 7,
    PieChart = 8,
    Pie3DChart = 9,
    DoughnutChart = 10,
    BarChart = 11,
    Bar3DChart = 12,
    OfPieChart = 13,
    SurfaceChart = 14,
    Surface3DChart = 15,
    BubbleChart = 16,
}

/**
 * ```xml
 * <xsd:group name=dmlchart:EG_Plot name="EG_Plot">
 * ```
 */
export interface IEGPlot {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AreaChart maxOccurs="1" minOccurs="1" name="areaChart" type="CT_AreaChart">
     * ```
     */
    areaChart?: ICTAreaChart;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Area3DChart maxOccurs="1" minOccurs="1" name="area3DChart" type="CT_Area3DChart">
     * ```
     */
    area3DChart?: ICTArea3DChart;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LineChart maxOccurs="1" minOccurs="1" name="lineChart" type="CT_LineChart">
     * ```
     */
    lineChart?: ICTLineChart;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Line3DChart maxOccurs="1" minOccurs="1" name="line3DChart" type="CT_Line3DChart">
     * ```
     */
    line3DChart?: ICTLine3DChart;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StockChart maxOccurs="1" minOccurs="1" name="stockChart" type="CT_StockChart">
     * ```
     */
    stockChart?: ICTStockChart;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_RadarChart maxOccurs="1" minOccurs="1" name="radarChart" type="CT_RadarChart">
     * ```
     */
    radarChart?: ICTRadarChart;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ScatterChart maxOccurs="1" minOccurs="1" name="scatterChart" type="CT_ScatterChart">
     * ```
     */
    scatterChart?: ICTScatterChart;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PieChart maxOccurs="1" minOccurs="1" name="pieChart" type="CT_PieChart">
     * ```
     */
    pieChart?: ICTPieChart;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Pie3DChart maxOccurs="1" minOccurs="1" name="pie3DChart" type="CT_Pie3DChart">
     * ```
     */
    pie3DChart?: ICTPie3DChart;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DoughnutChart maxOccurs="1" minOccurs="1" name="doughnutChart" type="CT_DoughnutChart">
     * ```
     */
    doughnutChart?: ICTDoughnutChart;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BarChart maxOccurs="1" minOccurs="1" name="barChart" type="CT_BarChart">
     * ```
     */
    barChart?: ICTBarChart;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Bar3DChart maxOccurs="1" minOccurs="1" name="bar3DChart" type="CT_Bar3DChart">
     * ```
     */
    bar3DChart?: ICTBar3DChart;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_OfPieChart maxOccurs="1" minOccurs="1" name="ofPieChart" type="CT_OfPieChart">
     * ```
     */
    ofPieChart?: ICTOfPieChart;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SurfaceChart maxOccurs="1" minOccurs="1" name="surfaceChart" type="CT_SurfaceChart">
     * ```
     */
    surfaceChart?: ICTSurfaceChart;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Surface3DChart maxOccurs="1" minOccurs="1" name="surface3DChart" type="CT_Surface3DChart">
     * ```
     */
    surface3DChart?: ICTSurface3DChart;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BubbleChart maxOccurs="1" minOccurs="1" name="bubbleChart" type="CT_BubbleChart">
     * ```
     */
    bubbleChart?: ICTBubbleChart;
    /**
     * ```xml
     * <xsd:element typeRedirected=tencentdocumentcommon:ST_ChartType maxOccurs="1" minOccurs="1" name="chartType" type="tdc:ST_ChartType">
     * ```
     */
    chartType?: STChartType;
}

/**
 * ```xml
 * <xsd:group name=dmlchart:EG_Plot name="EG_Plot">
 * ```
 */
export interface IEGPlotDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AreaChart maxOccurs="1" minOccurs="1" name="areaChart" type="CT_AreaChart">
     * ```
     */
    areaChart?: ICTAreaChartDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Area3DChart maxOccurs="1" minOccurs="1" name="area3DChart" type="CT_Area3DChart">
     * ```
     */
    area3DChart?: ICTArea3DChartDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LineChart maxOccurs="1" minOccurs="1" name="lineChart" type="CT_LineChart">
     * ```
     */
    lineChart?: ICTLineChartDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Line3DChart maxOccurs="1" minOccurs="1" name="line3DChart" type="CT_Line3DChart">
     * ```
     */
    line3DChart?: ICTLine3DChartDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StockChart maxOccurs="1" minOccurs="1" name="stockChart" type="CT_StockChart">
     * ```
     */
    stockChart?: ICTStockChartDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_RadarChart maxOccurs="1" minOccurs="1" name="radarChart" type="CT_RadarChart">
     * ```
     */
    radarChart?: ICTRadarChartDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ScatterChart maxOccurs="1" minOccurs="1" name="scatterChart" type="CT_ScatterChart">
     * ```
     */
    scatterChart?: ICTScatterChartDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PieChart maxOccurs="1" minOccurs="1" name="pieChart" type="CT_PieChart">
     * ```
     */
    pieChart?: ICTPieChartDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Pie3DChart maxOccurs="1" minOccurs="1" name="pie3DChart" type="CT_Pie3DChart">
     * ```
     */
    pie3DChart?: ICTPie3DChartDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DoughnutChart maxOccurs="1" minOccurs="1" name="doughnutChart" type="CT_DoughnutChart">
     * ```
     */
    doughnutChart?: ICTDoughnutChartDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BarChart maxOccurs="1" minOccurs="1" name="barChart" type="CT_BarChart">
     * ```
     */
    barChart?: ICTBarChartDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Bar3DChart maxOccurs="1" minOccurs="1" name="bar3DChart" type="CT_Bar3DChart">
     * ```
     */
    bar3DChart?: ICTBar3DChartDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_OfPieChart maxOccurs="1" minOccurs="1" name="ofPieChart" type="CT_OfPieChart">
     * ```
     */
    ofPieChart?: ICTOfPieChartDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SurfaceChart maxOccurs="1" minOccurs="1" name="surfaceChart" type="CT_SurfaceChart">
     * ```
     */
    surfaceChart?: ICTSurfaceChartDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Surface3DChart maxOccurs="1" minOccurs="1" name="surface3DChart" type="CT_Surface3DChart">
     * ```
     */
    surface3DChart?: ICTSurface3DChartDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BubbleChart maxOccurs="1" minOccurs="1" name="bubbleChart" type="CT_BubbleChart">
     * ```
     */
    bubbleChart?: ICTBubbleChartDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=tencentdocumentcommon:ST_ChartType maxOccurs="1" minOccurs="1" name="chartType" type="tdc:ST_ChartType">
     * ```
     */
    chartType?: STChartType | null;
}

export interface IChartAxesProperties {
    /** 子表的坐标轴数组 */
    axes?: IEGAxisDelta[];
    /**
     * axes_add_time 定义为：”子表坐标轴的添加时间“,
     * 相关设计原因：
     * 坐标轴和子表在ooxml里是多对多的关系，按这种结构，在冲突处理中存在无法解决的问题：
     * 在协同进行 ”增/删坐标轴” 时，无论是增量还是全量修改坐标轴集合，均会有数据丢失/异常的case
     * 故将坐标轴按映射关系舔加到各个子图表中，以保证冲突处理的正确性，同时便于数据的维护。
     * 但是，在不增加额外字段的情况，无法直接根据子图还原出ooxml中”坐标轴的有序集合“（该顺序在office中关联到主/次坐标轴的判定）
     * 解决办法为：在增加子表时，需要额外设置对应坐标轴的index，来描述坐标轴在外层的先后顺序。在导出时，在plotArea下按顺序还原坐标轴集合
     */
    index?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PositiveSize2D name="CT_PositiveSize2D">
 * ```
 */
export interface ICTPositiveSize2D {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="cx" type="ST_PositiveCoordinate" use="required">
     * ```
     */
    cx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="cy" type="ST_PositiveCoordinate" use="required">
     * ```
     */
    cy?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PositiveSize2D name="CT_PositiveSize2D">
 * ```
 */
export interface ICTPositiveSize2DDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="cx" type="ST_PositiveCoordinate" use="required">
     * ```
     */
    cx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="cy" type="ST_PositiveCoordinate" use="required">
     * ```
     */
    cy?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextBodyProperties name="CT_TextBodyProperties">
 * ```
 */
export interface ICTTextBodyProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PresetTextShape maxOccurs="1" minOccurs="0" name="prstTxWarp" type="CT_PresetTextShape">
     * ```
     */
    prstTxWarp?: ICTPresetTextShape;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextNormalAutofit name="normAutofit" type="CT_TextNormalAutofit">
     * ```
     */
    normAutofit?: ICTTextNormalAutofit;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Scene3D maxOccurs="1" minOccurs="0" name="scene3d" type="CT_Scene3D">
     * ```
     */
    scene3d?: ICTScene3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Shape3D maxOccurs="1" minOccurs="1" name="sp3d" type="CT_Shape3D">
     * ```
     */
    sp3d?: ICTShape3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FlatText maxOccurs="1" minOccurs="1" name="flatTx" type="CT_FlatText">
     * ```
     */
    flatTx?: ICTFlatText;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="rot" type="ST_AngleDmlA" use="optional">
     * ```
     */
    rot?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="spcFirstLastPara" type="xsd:boolean" use="optional">
     * ```
     */
    spcFirstLastPara?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextVertOverflowType name="vertOverflow" type="ST_TextVertOverflowType" use="optional">
     * ```
     */
    vertOverflow?: STTextVertOverflowType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextHorzOverflowType name="horzOverflow" type="ST_TextHorzOverflowType" use="optional">
     * ```
     */
    horzOverflow?: STTextHorzOverflowType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextVerticalType name="vert" type="ST_TextVerticalType" use="optional">
     * ```
     */
    vert?: STTextVerticalType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextWrappingType name="wrap" type="ST_TextWrappingType" use="optional">
     * ```
     */
    wrap?: STTextWrappingType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="lIns" type="ST_Coordinate32" use="optional">
     * ```
     */
    lIns?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="tIns" type="ST_Coordinate32" use="optional">
     * ```
     */
    tIns?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="rIns" type="ST_Coordinate32" use="optional">
     * ```
     */
    rIns?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="bIns" type="ST_Coordinate32" use="optional">
     * ```
     */
    bIns?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="numCol" type="ST_TextColumnCount" use="optional">
     * ```
     */
    numCol?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="spcCol" type="ST_PositiveCoordinate32" use="optional">
     * ```
     */
    spcCol?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="rtlCol" type="xsd:boolean" use="optional">
     * ```
     */
    rtlCol?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="fromWordArt" type="xsd:boolean" use="optional">
     * ```
     */
    fromWordArt?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextAnchoringType name="anchor" type="ST_TextAnchoringType" use="optional">
     * ```
     */
    anchor?: STTextAnchoringType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="anchorCtr" type="xsd:boolean" use="optional">
     * ```
     */
    anchorCtr?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="forceAA" type="xsd:boolean" use="optional">
     * ```
     */
    forceAa?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="upright" type="xsd:boolean" use="optional">
     * ```
     */
    upright?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="compatLnSpc" type="xsd:boolean" use="optional">
     * ```
     */
    compatLnSpc?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextNoAutofit convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="noAutofit" type="CT_TextNoAutofit">
     * ```
     */
    noAutofit?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextShapeAutofit convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="spAutoFit" type="CT_TextShapeAutofit">
     * ```
     */
    spAutoFit?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextBodyProperties name="CT_TextBodyProperties">
 * ```
 */
export interface ICTTextBodyPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PresetTextShape maxOccurs="1" minOccurs="0" name="prstTxWarp" type="CT_PresetTextShape">
     * ```
     */
    prstTxWarp?: ICTPresetTextShapeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextNormalAutofit name="normAutofit" type="CT_TextNormalAutofit">
     * ```
     */
    normAutofit?: ICTTextNormalAutofitDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Scene3D maxOccurs="1" minOccurs="0" name="scene3d" type="CT_Scene3D">
     * ```
     */
    scene3d?: ICTScene3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Shape3D maxOccurs="1" minOccurs="1" name="sp3d" type="CT_Shape3D">
     * ```
     */
    sp3d?: ICTShape3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FlatText maxOccurs="1" minOccurs="1" name="flatTx" type="CT_FlatText">
     * ```
     */
    flatTx?: ICTFlatTextDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="rot" type="ST_AngleDmlA" use="optional">
     * ```
     */
    rot?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="spcFirstLastPara" type="xsd:boolean" use="optional">
     * ```
     */
    spcFirstLastPara?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextVertOverflowType name="vertOverflow" type="ST_TextVertOverflowType" use="optional">
     * ```
     */
    vertOverflow?: STTextVertOverflowType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextHorzOverflowType name="horzOverflow" type="ST_TextHorzOverflowType" use="optional">
     * ```
     */
    horzOverflow?: STTextHorzOverflowType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextVerticalType name="vert" type="ST_TextVerticalType" use="optional">
     * ```
     */
    vert?: STTextVerticalType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextWrappingType name="wrap" type="ST_TextWrappingType" use="optional">
     * ```
     */
    wrap?: STTextWrappingType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="lIns" type="ST_Coordinate32" use="optional">
     * ```
     */
    lIns?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="tIns" type="ST_Coordinate32" use="optional">
     * ```
     */
    tIns?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="rIns" type="ST_Coordinate32" use="optional">
     * ```
     */
    rIns?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="bIns" type="ST_Coordinate32" use="optional">
     * ```
     */
    bIns?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="numCol" type="ST_TextColumnCount" use="optional">
     * ```
     */
    numCol?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="spcCol" type="ST_PositiveCoordinate32" use="optional">
     * ```
     */
    spcCol?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="rtlCol" type="xsd:boolean" use="optional">
     * ```
     */
    rtlCol?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="fromWordArt" type="xsd:boolean" use="optional">
     * ```
     */
    fromWordArt?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextAnchoringType name="anchor" type="ST_TextAnchoringType" use="optional">
     * ```
     */
    anchor?: STTextAnchoringType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="anchorCtr" type="xsd:boolean" use="optional">
     * ```
     */
    anchorCtr?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="forceAA" type="xsd:boolean" use="optional">
     * ```
     */
    forceAa?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="upright" type="xsd:boolean" use="optional">
     * ```
     */
    upright?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="compatLnSpc" type="xsd:boolean" use="optional">
     * ```
     */
    compatLnSpc?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextNoAutofit convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="noAutofit" type="CT_TextNoAutofit">
     * ```
     */
    noAutofit?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextShapeAutofit convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="spAutoFit" type="CT_TextShapeAutofit">
     * ```
     */
    spAutoFit?: boolean | null;
}

export enum ResourceType {
    /** 未知类型 */
    Unknown = 0,
    /** 图片资源 */
    Image = 1,
    /** 视频资源 */
    Video = 2,
    /** 音频资源 */
    Audio = 3,
    /** 3D 模型 */
    Model3D = 4,
    /** OLE */
    Ole = 5,
    /** 内嵌字体 */
    Font = 6,
}

export interface IImageResourceDescriptor {
    /** 图片的 Src */
    src?: string;
    /** 图片的 MIME 类型 */
    type?: string;
    /** 图片的宽度。单位为像素（px） */
    width?: number;
    /** 图片的高度。单位为像素（px） */
    height?: number;
    /** 图片的大小。**单位为 Bytes，最大为4GB** */
    size?: number;
    /** 替换图片 Src，用于导出时 Keep WDP 原图等场景 */
    alternateSrc?: string;
    /** 图片的垂直分辨率 */
    verticalDpi?: number;
    /** 图片的水平分辨率 */
    horizontalDpi?: number;
    /** 图片EXIF记录的旋转信息 */
    orientation?: ExifOrientation;
}

export interface IVideoResourceDescriptor {
    /** 视频的 Src */
    src?: string;
    /** 视频的 MIME 类型 */
    type?: string;
    /** 视频的宽度。单位为像素（px） */
    width?: number;
    /** 视频的高度。单位为像素（px） */
    height?: number;
    /** 视频的大小。**单位为 Bytes，最大为4GB** */
    size?: number;
    /** 替换视频 Src */
    alternateSrc?: string;
    /** 视频的时长。单位为秒（s） */
    duration?: number;
    /** 是否为外部视频 */
    isExternal?: boolean;
    /** 音频的格式 */
    format?: string;
}

export interface IAudioResourceDescriptor {
    /** 音频的 Src */
    src?: string;
    /** 音频的 MIME 类型 */
    type?: string;
    /** 音频的大小。**单位为 Bytes，最大为4GB** */
    size?: number;
    /** 替换音频 Src */
    alternateSrc?: string;
    /** 音频的时长。单位为秒（s） */
    duration?: number;
    /** 是否为外部视频 */
    isExternal?: boolean;
    /** 音频的格式 */
    format?: string;
}

export interface IOOXMLPartResourceDescriptor {
    /** Part 的 Src */
    src?: string;
    /** Part 的 Relationship Type */
    type?: string;
    /** Part 的大小。**单位为 Bytes，最大为4GB** */
    size?: number;
    /** 是否为外部 Part */
    isExternal?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_DataModel name="CT_DataModel">
 * ```
 */
export interface ICTDataModel {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_PtList name="ptLst" type="CT_PtList">
     * ```
     */
    ptLst?: ICTPtList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_CxnList maxOccurs="1" minOccurs="0" name="cxnLst" type="CT_CxnList">
     * ```
     */
    cxnLst?: ICTCxnList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BackgroundFormatting minOccurs="0" name="bg" type="a:CT_BackgroundFormatting">
     * ```
     */
    bg?: ICTBackgroundFormatting;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_WholeE2oFormatting minOccurs="0" name="whole" type="a:CT_WholeE2oFormatting">
     * ```
     */
    whole?: ICTWholeE2oFormatting;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:diagramId name="diagramId" ref="td:diagramId" type="s:ST_String">
     * ```
     */
    diagramId?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_DataModel name="CT_DataModel">
 * ```
 */
export interface ICTDataModelDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_PtList name="ptLst" type="CT_PtList">
     * ```
     */
    ptLst?: ICTPtListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_CxnList maxOccurs="1" minOccurs="0" name="cxnLst" type="CT_CxnList">
     * ```
     */
    cxnLst?: ICTCxnListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BackgroundFormatting minOccurs="0" name="bg" type="a:CT_BackgroundFormatting">
     * ```
     */
    bg?: ICTBackgroundFormattingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_WholeE2oFormatting minOccurs="0" name="whole" type="a:CT_WholeE2oFormatting">
     * ```
     */
    whole?: ICTWholeE2oFormattingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:diagramId name="diagramId" ref="td:diagramId" type="s:ST_String">
     * ```
     */
    diagramId?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdsp2008:CT_DrawingMsDsp name="CT_DrawingMsDsp">
 * ```
 */
export interface ICTDrawingMsDsp {
    /**
     * ```xml
     * <xsd:element typeRedirected=msdsp2008:CT_GroupShapeMsDsp maxOccurs="1" minOccurs="1" name="spTree" type="CT_GroupShapeMsDsp">
     * ```
     */
    spTree?: ICTGroupShapeMsDsp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:diagramId name="diagramId" ref="td:diagramId" type="s:ST_String">
     * ```
     */
    diagramId?: string;
}

/**
 * ```xml
 * <xsd:complexType name=msdsp2008:CT_DrawingMsDsp name="CT_DrawingMsDsp">
 * ```
 */
export interface ICTDrawingMsDspDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msdsp2008:CT_GroupShapeMsDsp maxOccurs="1" minOccurs="1" name="spTree" type="CT_GroupShapeMsDsp">
     * ```
     */
    spTree?: ICTGroupShapeMsDspDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:diagramId name="diagramId" ref="td:diagramId" type="s:ST_String">
     * ```
     */
    diagramId?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_DiagramDefinition name="CT_DiagramDefinition">
 * ```
 */
export interface ICTDiagramDefinition {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Name maxOccurs="unbounded" minOccurs="0" name="title" type="CT_Name">
     * ```
     */
    title?: ICTName[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Description maxOccurs="unbounded" minOccurs="0" name="desc" type="CT_Description">
     * ```
     */
    desc?: ICTDescription[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Categories minOccurs="0" name="catLst" type="CT_Categories">
     * ```
     */
    catLst?: ICTCategories;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_SampleData minOccurs="0" name="sampData" type="CT_SampleData">
     * ```
     */
    sampData?: ICTSampleData;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_SampleData minOccurs="0" name="styleData" type="CT_SampleData">
     * ```
     */
    styleData?: ICTSampleData;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_SampleData minOccurs="0" name="clrData" type="CT_SampleData">
     * ```
     */
    clrData?: ICTSampleData;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_LayoutNode name="layoutNode" type="CT_LayoutNode">
     * ```
     */
    layoutNode?: ICTLayoutNode;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="uniqueId" type="xsd:string" use="optional">
     * ```
     */
    uniqueId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="minVer" type="xsd:string" use="optional">
     * ```
     */
    minVer?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="defStyle" type="xsd:string" use="optional">
     * ```
     */
    defStyle?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:diagramId name="diagramId" ref="td:diagramId" type="s:ST_String">
     * ```
     */
    diagramId?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_DiagramDefinition name="CT_DiagramDefinition">
 * ```
 */
export interface ICTDiagramDefinitionDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Name maxOccurs="unbounded" minOccurs="0" name="title" type="CT_Name">
     * ```
     */
    title?: ICTNameDelta[];
    titleOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Description maxOccurs="unbounded" minOccurs="0" name="desc" type="CT_Description">
     * ```
     */
    desc?: ICTDescriptionDelta[];
    descOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Categories minOccurs="0" name="catLst" type="CT_Categories">
     * ```
     */
    catLst?: ICTCategoriesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_SampleData minOccurs="0" name="sampData" type="CT_SampleData">
     * ```
     */
    sampData?: ICTSampleDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_SampleData minOccurs="0" name="styleData" type="CT_SampleData">
     * ```
     */
    styleData?: ICTSampleDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_SampleData minOccurs="0" name="clrData" type="CT_SampleData">
     * ```
     */
    clrData?: ICTSampleDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_LayoutNode name="layoutNode" type="CT_LayoutNode">
     * ```
     */
    layoutNode?: ICTLayoutNodeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="uniqueId" type="xsd:string" use="optional">
     * ```
     */
    uniqueId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="minVer" type="xsd:string" use="optional">
     * ```
     */
    minVer?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="defStyle" type="xsd:string" use="optional">
     * ```
     */
    defStyle?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:diagramId name="diagramId" ref="td:diagramId" type="s:ST_String">
     * ```
     */
    diagramId?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_StyleDefinition name="CT_StyleDefinition">
 * ```
 */
export interface ICTStyleDefinition {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_SDName maxOccurs="unbounded" minOccurs="0" name="title" type="CT_SDName">
     * ```
     */
    title?: ICTSDName[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_SDDescription maxOccurs="unbounded" minOccurs="0" name="desc" type="CT_SDDescription">
     * ```
     */
    desc?: ICTSDDescription[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_SDCategories minOccurs="0" name="catLst" type="CT_SDCategories">
     * ```
     */
    catLst?: ICTSDCategories;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Scene3D maxOccurs="1" minOccurs="0" name="scene3d" type="a:CT_Scene3D">
     * ```
     */
    scene3d?: ICTScene3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_StyleLabel maxOccurs="unbounded" minOccurs="1" name="styleLbl" type="CT_StyleLabel">
     * ```
     */
    styleLbl?: ICTStyleLabel[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="uniqueId" type="xsd:string" use="optional">
     * ```
     */
    uniqueId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="minVer" type="xsd:string" use="optional">
     * ```
     */
    minVer?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:diagramId name="diagramId" ref="td:diagramId" type="s:ST_String">
     * ```
     */
    diagramId?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_StyleDefinition name="CT_StyleDefinition">
 * ```
 */
export interface ICTStyleDefinitionDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_SDName maxOccurs="unbounded" minOccurs="0" name="title" type="CT_SDName">
     * ```
     */
    title?: ICTSDNameDelta[];
    titleOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_SDDescription maxOccurs="unbounded" minOccurs="0" name="desc" type="CT_SDDescription">
     * ```
     */
    desc?: ICTSDDescriptionDelta[];
    descOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_SDCategories minOccurs="0" name="catLst" type="CT_SDCategories">
     * ```
     */
    catLst?: ICTSDCategoriesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Scene3D maxOccurs="1" minOccurs="0" name="scene3d" type="a:CT_Scene3D">
     * ```
     */
    scene3d?: ICTScene3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_StyleLabel maxOccurs="unbounded" minOccurs="1" name="styleLbl" type="CT_StyleLabel">
     * ```
     */
    styleLbl?: ICTStyleLabelDelta[];
    styleLblOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="uniqueId" type="xsd:string" use="optional">
     * ```
     */
    uniqueId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="minVer" type="xsd:string" use="optional">
     * ```
     */
    minVer?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:diagramId name="diagramId" ref="td:diagramId" type="s:ST_String">
     * ```
     */
    diagramId?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_ColorTransform name="CT_ColorTransform">
 * ```
 */
export interface ICTColorTransform {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_CTName maxOccurs="unbounded" minOccurs="0" name="title" type="CT_CTName">
     * ```
     */
    title?: ICTCTName[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_CTDescription maxOccurs="unbounded" minOccurs="0" name="desc" type="CT_CTDescription">
     * ```
     */
    desc?: ICTCTDescription[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_CTCategories minOccurs="0" name="catLst" type="CT_CTCategories">
     * ```
     */
    catLst?: ICTCTCategories;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_CTStyleLabel maxOccurs="unbounded" minOccurs="0" name="styleLbl" type="CT_CTStyleLabel">
     * ```
     */
    styleLbl?: ICTCTStyleLabel[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="uniqueId" type="xsd:string" use="optional">
     * ```
     */
    uniqueId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="minVer" type="xsd:string" use="optional">
     * ```
     */
    minVer?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:diagramId name="diagramId" ref="td:diagramId" type="s:ST_String">
     * ```
     */
    diagramId?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_ColorTransform name="CT_ColorTransform">
 * ```
 */
export interface ICTColorTransformDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_CTName maxOccurs="unbounded" minOccurs="0" name="title" type="CT_CTName">
     * ```
     */
    title?: ICTCTNameDelta[];
    titleOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_CTDescription maxOccurs="unbounded" minOccurs="0" name="desc" type="CT_CTDescription">
     * ```
     */
    desc?: ICTCTDescriptionDelta[];
    descOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_CTCategories minOccurs="0" name="catLst" type="CT_CTCategories">
     * ```
     */
    catLst?: ICTCTCategoriesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_CTStyleLabel maxOccurs="unbounded" minOccurs="0" name="styleLbl" type="CT_CTStyleLabel">
     * ```
     */
    styleLbl?: ICTCTStyleLabelDelta[];
    styleLblOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="uniqueId" type="xsd:string" use="optional">
     * ```
     */
    uniqueId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="minVer" type="xsd:string" use="optional">
     * ```
     */
    minVer?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:diagramId name="diagramId" ref="td:diagramId" type="s:ST_String">
     * ```
     */
    diagramId?: string | null;
}

export enum CommentType {
    /** 缺省值 */
    PBDEFAULT = 0,
    /** 为当前页添加批注 */
    slide = 1,
    /** 为元素添加批注 */
    element = 2,
    /** 为文字添加批注 */
    text = 3,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Point2D name="CT_Point2D">
 * ```
 */
export interface ICTPoint2D {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="x" type="ST_Coordinate" use="required">
     * ```
     */
    x?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="y" type="ST_Coordinate" use="required">
     * ```
     */
    y?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Point2D name="CT_Point2D">
 * ```
 */
export interface ICTPoint2DDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="x" type="ST_Coordinate" use="required">
     * ```
     */
    x?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="y" type="ST_Coordinate" use="required">
     * ```
     */
    y?: number | null;
}

export interface ITextAnchor {
    /** 第一个字符的位置 */
    cp?: number;
    /** 批注选中的字符串长度 */
    len?: number;
    /** optional 表格批注的锚点位置。当非表格批注时，table_cell_position为空。 */
    commentTableRange?: ICommentTableRange;
}

export enum TargetType {
    /** 未知 */
    Unknown = 0,
    /** 文档自身 */
    Presentation = 1,
    /** 主题 */
    Theme = 2,
    /** 页 */
    Page = 3,
    /** 形状 */
    Shape = 4,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_BackgroundProperties name="CT_BackgroundProperties">
 * ```
 */
export interface ICTBackgroundProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectList maxOccurs="1" minOccurs="1" name="effectLst" type="CT_EffectList">
     * ```
     */
    effectLst?: ICTEffectList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="effectDag" type="CT_EffectContainer">
     * ```
     */
    effectDag?: ICTEffectContainer;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="shadeToTitle" type="xsd:boolean" use="optional">
     * ```
     */
    shadeToTitle?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_BackgroundProperties name="CT_BackgroundProperties">
 * ```
 */
export interface ICTBackgroundPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectList maxOccurs="1" minOccurs="1" name="effectLst" type="CT_EffectList">
     * ```
     */
    effectLst?: ICTEffectListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="effectDag" type="CT_EffectContainer">
     * ```
     */
    effectDag?: ICTEffectContainerDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="shadeToTitle" type="xsd:boolean" use="optional">
     * ```
     */
    shadeToTitle?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_StyleMatrixReference name="CT_StyleMatrixReference">
 * ```
 */
export interface ICTStyleMatrixReference {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoice;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="ST_StyleMatrixColumnIndex" use="required">
     * ```
     */
    idx?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_StyleMatrixReference name="CT_StyleMatrixReference">
 * ```
 */
export interface ICTStyleMatrixReferenceDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoiceDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="ST_StyleMatrixColumnIndex" use="required">
     * ```
     */
    idx?: number | null;
}

export enum STBlackWhiteMode {
    PBDEFAULT = 0,
    clr = 1,
    auto = 2,
    gray = 3,
    ltGray = 4,
    invGray = 5,
    grayWhite = 6,
    blackGray = 7,
    blackWhite = 8,
    black = 9,
    white = 10,
    hidden = 11,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_GroupShapeNonVisualPml name="CT_GroupShapeNonVisualPml">
 * ```
 */
export interface ICTGroupShapeNonVisualPml {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingProps maxOccurs="1" minOccurs="1" name="cNvPr" type="a:CT_NonVisualDrawingProps">
     * ```
     */
    cNvPr?: ICTNonVisualDrawingProps;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualGroupDrawingShapeProps maxOccurs="1" minOccurs="1" name="cNvGrpSpPr" type="a:CT_NonVisualGroupDrawingShapeProps">
     * ```
     */
    cNvGrpSpPr?: ICTNonVisualGroupDrawingShapeProps;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ApplicationNonVisualDrawingProps maxOccurs="1" minOccurs="1" name="nvPr" type="CT_ApplicationNonVisualDrawingProps">
     * ```
     */
    nvPr?: ICTApplicationNonVisualDrawingProps;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_GroupShapeNonVisualPml name="CT_GroupShapeNonVisualPml">
 * ```
 */
export interface ICTGroupShapeNonVisualPmlDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingProps maxOccurs="1" minOccurs="1" name="cNvPr" type="a:CT_NonVisualDrawingProps">
     * ```
     */
    cNvPr?: ICTNonVisualDrawingPropsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualGroupDrawingShapeProps maxOccurs="1" minOccurs="1" name="cNvGrpSpPr" type="a:CT_NonVisualGroupDrawingShapeProps">
     * ```
     */
    cNvGrpSpPr?: ICTNonVisualGroupDrawingShapePropsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ApplicationNonVisualDrawingProps maxOccurs="1" minOccurs="1" name="nvPr" type="CT_ApplicationNonVisualDrawingProps">
     * ```
     */
    nvPr?: ICTApplicationNonVisualDrawingPropsDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GroupShapeProperties name="CT_GroupShapeProperties">
 * ```
 */
export interface ICTGroupShapeProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupTransform2D maxOccurs="1" minOccurs="0" name="xfrm" type="CT_GroupTransform2D">
     * ```
     */
    xfrm?: ICTGroupTransform2D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectList maxOccurs="1" minOccurs="1" name="effectLst" type="CT_EffectList">
     * ```
     */
    effectLst?: ICTEffectList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="effectDag" type="CT_EffectContainer">
     * ```
     */
    effectDag?: ICTEffectContainer;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Scene3D maxOccurs="1" minOccurs="0" name="scene3d" type="CT_Scene3D">
     * ```
     */
    scene3d?: ICTScene3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_BlackWhiteMode name="bwMode" type="ST_BlackWhiteMode" use="optional">
     * ```
     */
    bwMode?: STBlackWhiteMode;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GroupShapeProperties name="CT_GroupShapeProperties">
 * ```
 */
export interface ICTGroupShapePropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupTransform2D maxOccurs="1" minOccurs="0" name="xfrm" type="CT_GroupTransform2D">
     * ```
     */
    xfrm?: ICTGroupTransform2DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectList maxOccurs="1" minOccurs="1" name="effectLst" type="CT_EffectList">
     * ```
     */
    effectLst?: ICTEffectListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="effectDag" type="CT_EffectContainer">
     * ```
     */
    effectDag?: ICTEffectContainerDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Scene3D maxOccurs="1" minOccurs="0" name="scene3d" type="CT_Scene3D">
     * ```
     */
    scene3d?: ICTScene3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_BlackWhiteMode name="bwMode" type="ST_BlackWhiteMode" use="optional">
     * ```
     */
    bwMode?: STBlackWhiteMode | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean | null;
}

/**
 * ```xml
 * <xsd:group name=pml:EG_GroupShapePml name="EG_GroupShapePml">
 * ```
 */
export interface IEGGroupShapePml {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ShapePml name="sp" type="CT_ShapePml">
     * ```
     */
    sp?: ICTShapePml;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_GroupShapePml name="grpSp" type="CT_GroupShapePml">
     * ```
     */
    grpSp?: ICTGroupShapePml;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_GraphicalObjectFramePml name="graphicFrame" type="CT_GraphicalObjectFramePml">
     * ```
     */
    graphicFrame?: ICTGraphicalObjectFramePml;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ConnectorPml name="cxnSp" type="CT_ConnectorPml">
     * ```
     */
    cxnSp?: ICTConnectorPml;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_PicturePml name="pic" type="CT_PicturePml">
     * ```
     */
    pic?: ICTPicturePml;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_RelPml name="contentPart" type="CT_RelPml">
     * ```
     */
    contentPart?: ICTRelPml;
}

/**
 * ```xml
 * <xsd:group name=pml:EG_GroupShapePml name="EG_GroupShapePml">
 * ```
 */
export interface IEGGroupShapePmlDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ShapePml name="sp" type="CT_ShapePml">
     * ```
     */
    sp?: ICTShapePmlDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_GroupShapePml name="grpSp" type="CT_GroupShapePml">
     * ```
     */
    grpSp?: ICTGroupShapePmlDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_GraphicalObjectFramePml name="graphicFrame" type="CT_GraphicalObjectFramePml">
     * ```
     */
    graphicFrame?: ICTGraphicalObjectFramePmlDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ConnectorPml name="cxnSp" type="CT_ConnectorPml">
     * ```
     */
    cxnSp?: ICTConnectorPmlDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_PicturePml name="pic" type="CT_PicturePml">
     * ```
     */
    pic?: ICTPicturePmlDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_RelPml name="contentPart" type="CT_RelPml">
     * ```
     */
    contentPart?: ICTRelPmlDelta | null;
}

export interface IArrayOp {
    opType?: ArrayOpType;
    opIndex?: number;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_CustomerData name="CT_CustomerData">
 * ```
 */
export interface ICTCustomerData {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_CustomerData name="CT_CustomerData">
 * ```
 */
export interface ICTCustomerDataDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TagsData name="CT_TagsData">
 * ```
 */
export interface ICTTagsData {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TagsData name="CT_TagsData">
 * ```
 */
export interface ICTTagsDataDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ControlPml name="CT_ControlPml">
 * ```
 */
export interface ICTControlPml {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_PicturePml maxOccurs="1" minOccurs="0" name="pic" type="CT_PicturePml">
     * ```
     */
    pic?: ICTPicturePml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="spid" type="a:ST_ShapeID" use="optional">
     * ```
     */
    spid?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="showAsIcon" type="xsd:boolean" use="optional">
     * ```
     */
    showAsIcon?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="optional">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="imgW" type="a:ST_PositiveCoordinate32" use="optional">
     * ```
     */
    imgW?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="imgH" type="a:ST_PositiveCoordinate32" use="optional">
     * ```
     */
    imgH?: number;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ControlPml name="CT_ControlPml">
 * ```
 */
export interface ICTControlPmlDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_PicturePml maxOccurs="1" minOccurs="0" name="pic" type="CT_PicturePml">
     * ```
     */
    pic?: ICTPicturePmlDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="spid" type="a:ST_ShapeID" use="optional">
     * ```
     */
    spid?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="showAsIcon" type="xsd:boolean" use="optional">
     * ```
     */
    showAsIcon?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="optional">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="imgW" type="a:ST_PositiveCoordinate32" use="optional">
     * ```
     */
    imgW?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="imgH" type="a:ST_PositiveCoordinate32" use="optional">
     * ```
     */
    imgH?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ColorMapping name="CT_ColorMapping">
 * ```
 */
export interface ICTColorMapping {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="bg1" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    bg1?: STColorSchemeIndex;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="tx1" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    tx1?: STColorSchemeIndex;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="bg2" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    bg2?: STColorSchemeIndex;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="tx2" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    tx2?: STColorSchemeIndex;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="accent1" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    accent1?: STColorSchemeIndex;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="accent2" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    accent2?: STColorSchemeIndex;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="accent3" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    accent3?: STColorSchemeIndex;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="accent4" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    accent4?: STColorSchemeIndex;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="accent5" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    accent5?: STColorSchemeIndex;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="accent6" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    accent6?: STColorSchemeIndex;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="hlink" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    hlink?: STColorSchemeIndex;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="folHlink" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    folHlink?: STColorSchemeIndex;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ColorMapping name="CT_ColorMapping">
 * ```
 */
export interface ICTColorMappingDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="bg1" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    bg1?: STColorSchemeIndex | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="tx1" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    tx1?: STColorSchemeIndex | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="bg2" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    bg2?: STColorSchemeIndex | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="tx2" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    tx2?: STColorSchemeIndex | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="accent1" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    accent1?: STColorSchemeIndex | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="accent2" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    accent2?: STColorSchemeIndex | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="accent3" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    accent3?: STColorSchemeIndex | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="accent4" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    accent4?: STColorSchemeIndex | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="accent5" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    accent5?: STColorSchemeIndex | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="accent6" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    accent6?: STColorSchemeIndex | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="hlink" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    hlink?: STColorSchemeIndex | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ColorSchemeIndex name="folHlink" type="ST_ColorSchemeIndex" use="required">
     * ```
     */
    folHlink?: STColorSchemeIndex | null;
}

export enum STDirectionPml {
    PBDEFAULT = 0,
    horz = 1,
    vert = 2,
}

export enum STTransitionEightDirectionType {
    PBDEFAULT = 0,
    l = 1,
    u = 2,
    r = 3,
    d = 4,
    lu = 5,
    ru = 6,
    ld = 7,
    rd = 8,
}

export enum STTransitionSideDirectionType {
    PBDEFAULT = 0,
    l = 1,
    u = 2,
    r = 3,
    d = 4,
}

export enum STTransitionInOutDirectionType {
    PBDEFAULT = 0,
    out = 1,
    in = 2,
}

export enum STTransitionCornerDirectionType {
    PBDEFAULT = 0,
    lu = 1,
    ru = 2,
    ld = 3,
    rd = 4,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TransitionStartSoundAction name="CT_TransitionStartSoundAction">
 * ```
 */
export interface ICTTransitionStartSoundAction {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EmbeddedWAVAudioFile maxOccurs="1" minOccurs="1" name="snd" type="a:CT_EmbeddedWAVAudioFile">
     * ```
     */
    snd?: ICTEmbeddedWAVAudioFile;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="loop" type="xsd:boolean" use="optional">
     * ```
     */
    loop?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TransitionStartSoundAction name="CT_TransitionStartSoundAction">
 * ```
 */
export interface ICTTransitionStartSoundActionDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EmbeddedWAVAudioFile maxOccurs="1" minOccurs="1" name="snd" type="a:CT_EmbeddedWAVAudioFile">
     * ```
     */
    snd?: ICTEmbeddedWAVAudioFileDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="loop" type="xsd:boolean" use="optional">
     * ```
     */
    loop?: boolean | null;
}

export enum STTransitionShredPattern {
    PBDEFAULT = 0,
    strip = 1,
    rectangle = 2,
}

export enum STTransitionCornerAndCenterDirectionType {
    PBDEFAULT = 0,
    lu = 1,
    ru = 2,
    ld = 3,
    rd = 4,
    center = 5,
}

export enum STTransitionLeftRightDirectionType {
    PBDEFAULT = 0,
    l = 1,
    r = 2,
}

export enum STTransitionPattern {
    PBDEFAULT = 0,
    diamond = 1,
    hexagon = 2,
}

export enum STTransitionMorphOption {
    PBDEFAULT = 0,
    byObject = 1,
    byWord = 2,
    byChar = 3,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLBuildParagraph name="CT_TLBuildParagraph">
 * ```
 */
export interface ICTTLBuildParagraph {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTemplateList maxOccurs="1" minOccurs="0" name="tmplLst" type="CT_TLTemplateList">
     * ```
     */
    tmplLst?: ICTTLTemplateList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="spid" type="a:ST_DrawingElementId" use="required">
     * ```
     */
    spid?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="grpId" type="xsd:unsignedInt" use="required">
     * ```
     */
    grpId?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="uiExpand" type="xsd:boolean" use="optional">
     * ```
     */
    uiExpand?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLParaBuildType default="whole" name="build" type="ST_TLParaBuildType" use="optional">
     * ```
     */
    build?: STTLParaBuildType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="1" name="bldLvl" type="xsd:unsignedInt" use="optional">
     * ```
     */
    bldLvl?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="animBg" type="xsd:boolean" use="optional">
     * ```
     */
    animBg?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="autoUpdateAnimBg" type="xsd:boolean" use="optional">
     * ```
     */
    autoUpdateAnimBg?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="rev" type="xsd:boolean" use="optional">
     * ```
     */
    rev?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="indefinite" name="advAuto" type="ST_TLTime" use="optional">
     * ```
     */
    advAuto?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLBuildParagraph name="CT_TLBuildParagraph">
 * ```
 */
export interface ICTTLBuildParagraphDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTemplateList maxOccurs="1" minOccurs="0" name="tmplLst" type="CT_TLTemplateList">
     * ```
     */
    tmplLst?: ICTTLTemplateListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="spid" type="a:ST_DrawingElementId" use="required">
     * ```
     */
    spid?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="grpId" type="xsd:unsignedInt" use="required">
     * ```
     */
    grpId?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="uiExpand" type="xsd:boolean" use="optional">
     * ```
     */
    uiExpand?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLParaBuildType default="whole" name="build" type="ST_TLParaBuildType" use="optional">
     * ```
     */
    build?: STTLParaBuildType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="1" name="bldLvl" type="xsd:unsignedInt" use="optional">
     * ```
     */
    bldLvl?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="animBg" type="xsd:boolean" use="optional">
     * ```
     */
    animBg?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="autoUpdateAnimBg" type="xsd:boolean" use="optional">
     * ```
     */
    autoUpdateAnimBg?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="rev" type="xsd:boolean" use="optional">
     * ```
     */
    rev?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="indefinite" name="advAuto" type="ST_TLTime" use="optional">
     * ```
     */
    advAuto?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLBuildDiagram name="CT_TLBuildDiagram">
 * ```
 */
export interface ICTTLBuildDiagram {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="spid" type="a:ST_DrawingElementId" use="required">
     * ```
     */
    spid?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="grpId" type="xsd:unsignedInt" use="required">
     * ```
     */
    grpId?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="uiExpand" type="xsd:boolean" use="optional">
     * ```
     */
    uiExpand?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLDiagramBuildType default="whole" name="bld" type="ST_TLDiagramBuildType" use="optional">
     * ```
     */
    bld?: STTLDiagramBuildType;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLBuildDiagram name="CT_TLBuildDiagram">
 * ```
 */
export interface ICTTLBuildDiagramDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="spid" type="a:ST_DrawingElementId" use="required">
     * ```
     */
    spid?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="grpId" type="xsd:unsignedInt" use="required">
     * ```
     */
    grpId?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="uiExpand" type="xsd:boolean" use="optional">
     * ```
     */
    uiExpand?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLDiagramBuildType default="whole" name="bld" type="ST_TLDiagramBuildType" use="optional">
     * ```
     */
    bld?: STTLDiagramBuildType | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLOleBuildChart name="CT_TLOleBuildChart">
 * ```
 */
export interface ICTTLOleBuildChart {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="spid" type="a:ST_DrawingElementId" use="required">
     * ```
     */
    spid?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="grpId" type="xsd:unsignedInt" use="required">
     * ```
     */
    grpId?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="uiExpand" type="xsd:boolean" use="optional">
     * ```
     */
    uiExpand?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLOleChartBuildType default="allAtOnce" name="bld" type="ST_TLOleChartBuildType" use="optional">
     * ```
     */
    bld?: STTLOleChartBuildType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="animBg" type="xsd:boolean" use="optional">
     * ```
     */
    animBg?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLOleBuildChart name="CT_TLOleBuildChart">
 * ```
 */
export interface ICTTLOleBuildChartDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="spid" type="a:ST_DrawingElementId" use="required">
     * ```
     */
    spid?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="grpId" type="xsd:unsignedInt" use="required">
     * ```
     */
    grpId?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="uiExpand" type="xsd:boolean" use="optional">
     * ```
     */
    uiExpand?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLOleChartBuildType default="allAtOnce" name="bld" type="ST_TLOleChartBuildType" use="optional">
     * ```
     */
    bld?: STTLOleChartBuildType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="animBg" type="xsd:boolean" use="optional">
     * ```
     */
    animBg?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLGraphicalObjectBuild name="CT_TLGraphicalObjectBuild">
 * ```
 */
export interface ICTTLGraphicalObjectBuild {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="bldAsOne" type="CT_EmptyPml">
     * ```
     */
    bldAsOne?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AnimationGraphicalObjectBuildProperties name="bldSub" type="a:CT_AnimationGraphicalObjectBuildProperties">
     * ```
     */
    bldSub?: ICTAnimationGraphicalObjectBuildProperties;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="spid" type="a:ST_DrawingElementId" use="required">
     * ```
     */
    spid?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="grpId" type="xsd:unsignedInt" use="required">
     * ```
     */
    grpId?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="uiExpand" type="xsd:boolean" use="optional">
     * ```
     */
    uiExpand?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLGraphicalObjectBuild name="CT_TLGraphicalObjectBuild">
 * ```
 */
export interface ICTTLGraphicalObjectBuildDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="bldAsOne" type="CT_EmptyPml">
     * ```
     */
    bldAsOne?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AnimationGraphicalObjectBuildProperties name="bldSub" type="a:CT_AnimationGraphicalObjectBuildProperties">
     * ```
     */
    bldSub?: ICTAnimationGraphicalObjectBuildPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="spid" type="a:ST_DrawingElementId" use="required">
     * ```
     */
    spid?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="grpId" type="xsd:unsignedInt" use="required">
     * ```
     */
    grpId?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="uiExpand" type="xsd:boolean" use="optional">
     * ```
     */
    uiExpand?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ExtensionPml name="CT_ExtensionPml">
 * ```
 */
export interface ICTExtensionPml {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="uri" type="xsd:token" use="required">
     * ```
     */
    uri?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_Media ref=msppt2010:media name="media" ref="p14:media" type="CT_Media">
     * ```
     */
    media?: ICTMedia[];
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_SectionList ref=msppt2010:sectionLst name="sectionLst" ref="p14:sectionLst" type="CT_SectionList">
     * ```
     */
    sectionLst?: ICTSectionList[];
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2012:CT_ExtendedGuideList ref=msppt2012:sldGuideLst name="sldGuideLst" ref="p15:sldGuideLst" type="CT_ExtendedGuideList">
     * ```
     */
    sldGuideLst?: ICTExtendedGuideList[];
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2012:CT_CommentThreading ref=msppt2012:threadingInfo name="threadingInfo" ref="p15:threadingInfo" type="CT_CommentThreading">
     * ```
     */
    threadingInfo?: ICTCommentThreading[];
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt201808:CT_CommentRelationship ref=msppt201808:commentRel name="commentRel" ref="p188:commentRel" type="CT_CommentRelationship">
     * ```
     */
    commentRel?: ICTCommentRelationship[];
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ExtensionPml name="CT_ExtensionPml">
 * ```
 */
export interface ICTExtensionPmlDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="uri" type="xsd:token" use="required">
     * ```
     */
    uri?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_Media ref=msppt2010:media name="media" ref="p14:media" type="CT_Media">
     * ```
     */
    media?: ICTMediaDelta[];
    mediaOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_SectionList ref=msppt2010:sectionLst name="sectionLst" ref="p14:sectionLst" type="CT_SectionList">
     * ```
     */
    sectionLst?: ICTSectionListDelta[];
    sectionLstOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2012:CT_ExtendedGuideList ref=msppt2012:sldGuideLst name="sldGuideLst" ref="p15:sldGuideLst" type="CT_ExtendedGuideList">
     * ```
     */
    sldGuideLst?: ICTExtendedGuideListDelta[];
    sldGuideLstOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2012:CT_CommentThreading ref=msppt2012:threadingInfo name="threadingInfo" ref="p15:threadingInfo" type="CT_CommentThreading">
     * ```
     */
    threadingInfo?: ICTCommentThreadingDelta[];
    threadingInfoOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt201808:CT_CommentRelationship ref=msppt201808:commentRel name="commentRel" ref="p188:commentRel" type="CT_CommentRelationship">
     * ```
     */
    commentRel?: ICTCommentRelationshipDelta[];
    commentRelOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_HtmlPublishProperties name="CT_HtmlPublishProperties">
 * ```
 */
export interface ICTHtmlPublishProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="sldAll" type="CT_EmptyPml">
     * ```
     */
    sldAll?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_IndexRange name="sldRg" type="CT_IndexRange">
     * ```
     */
    sldRg?: ICTIndexRange;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CustomShowId name="custShow" type="CT_CustomShowId">
     * ```
     */
    custShow?: ICTCustomShowId;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="showSpeakerNotes" type="xsd:boolean" use="optional">
     * ```
     */
    showSpeakerNotes?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="target" type="xsd:string" use="optional">
     * ```
     */
    target?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="title" type="xsd:string" use="optional">
     * ```
     */
    title?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_HtmlPublishProperties name="CT_HtmlPublishProperties">
 * ```
 */
export interface ICTHtmlPublishPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="sldAll" type="CT_EmptyPml">
     * ```
     */
    sldAll?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_IndexRange name="sldRg" type="CT_IndexRange">
     * ```
     */
    sldRg?: ICTIndexRangeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CustomShowId name="custShow" type="CT_CustomShowId">
     * ```
     */
    custShow?: ICTCustomShowIdDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="showSpeakerNotes" type="xsd:boolean" use="optional">
     * ```
     */
    showSpeakerNotes?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="target" type="xsd:string" use="optional">
     * ```
     */
    target?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="title" type="xsd:string" use="optional">
     * ```
     */
    title?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_WebProperties name="CT_WebProperties">
 * ```
 */
export interface ICTWebProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="showAnimation" type="xsd:boolean" use="optional">
     * ```
     */
    showAnimation?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="resizeGraphics" type="xsd:boolean" use="optional">
     * ```
     */
    resizeGraphics?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="allowPng" type="xsd:boolean" use="optional">
     * ```
     */
    allowPng?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="relyOnVml" type="xsd:boolean" use="optional">
     * ```
     */
    relyOnVml?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="organizeInFolders" type="xsd:boolean" use="optional">
     * ```
     */
    organizeInFolders?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="useLongFilenames" type="xsd:boolean" use="optional">
     * ```
     */
    useLongFilenames?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_WebScreenSize default="800x600" name="imgSz" type="ST_WebScreenSize" use="optional">
     * ```
     */
    imgSz?: STWebScreenSize;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="encoding" type="ST_WebEncoding" use="optional">
     * ```
     */
    encoding?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_WebColorType default="whiteTextOnBlack" name="clr" type="ST_WebColorType" use="optional">
     * ```
     */
    clr?: STWebColorType;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_WebProperties name="CT_WebProperties">
 * ```
 */
export interface ICTWebPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="showAnimation" type="xsd:boolean" use="optional">
     * ```
     */
    showAnimation?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="resizeGraphics" type="xsd:boolean" use="optional">
     * ```
     */
    resizeGraphics?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="allowPng" type="xsd:boolean" use="optional">
     * ```
     */
    allowPng?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="relyOnVml" type="xsd:boolean" use="optional">
     * ```
     */
    relyOnVml?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="organizeInFolders" type="xsd:boolean" use="optional">
     * ```
     */
    organizeInFolders?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="useLongFilenames" type="xsd:boolean" use="optional">
     * ```
     */
    useLongFilenames?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_WebScreenSize default="800x600" name="imgSz" type="ST_WebScreenSize" use="optional">
     * ```
     */
    imgSz?: STWebScreenSize | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="encoding" type="ST_WebEncoding" use="optional">
     * ```
     */
    encoding?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_WebColorType default="whiteTextOnBlack" name="clr" type="ST_WebColorType" use="optional">
     * ```
     */
    clr?: STWebColorType | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_PrintProperties name="CT_PrintProperties">
 * ```
 */
export interface ICTPrintProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_PrintWhat default="slides" name="prnWhat" type="ST_PrintWhat" use="optional">
     * ```
     */
    prnWhat?: STPrintWhat;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_PrintColorMode default="clr" name="clrMode" type="ST_PrintColorMode" use="optional">
     * ```
     */
    clrMode?: STPrintColorMode;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="hiddenSlides" type="xsd:boolean" use="optional">
     * ```
     */
    hiddenSlides?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="scaleToFitPaper" type="xsd:boolean" use="optional">
     * ```
     */
    scaleToFitPaper?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="frameSlides" type="xsd:boolean" use="optional">
     * ```
     */
    frameSlides?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_PrintProperties name="CT_PrintProperties">
 * ```
 */
export interface ICTPrintPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_PrintWhat default="slides" name="prnWhat" type="ST_PrintWhat" use="optional">
     * ```
     */
    prnWhat?: STPrintWhat | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_PrintColorMode default="clr" name="clrMode" type="ST_PrintColorMode" use="optional">
     * ```
     */
    clrMode?: STPrintColorMode | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="hiddenSlides" type="xsd:boolean" use="optional">
     * ```
     */
    hiddenSlides?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="scaleToFitPaper" type="xsd:boolean" use="optional">
     * ```
     */
    scaleToFitPaper?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="frameSlides" type="xsd:boolean" use="optional">
     * ```
     */
    frameSlides?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ShowProperties name="CT_ShowProperties">
 * ```
 */
export interface ICTShowProperties {
    /**
     * ```xml
     * <xsd:group typeRedirected=pml:EG_ShowType ref=pml:EG_ShowType maxOccurs="1" minOccurs="0" name="EG_ShowType" ref="EG_ShowType">
     * ref pml:EG_ShowType
     * ```
     */
    egShowType?: IEGShowType;
    /**
     * ```xml
     * <xsd:group typeRedirected=pml:EG_SlideListChoice ref=pml:EG_SlideListChoice maxOccurs="1" minOccurs="0" name="EG_SlideListChoice" ref="EG_SlideListChoice">
     * ref pml:EG_SlideListChoice
     * ```
     */
    egSlideListChoice?: IEGSlideListChoice;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="0" name="penClr" type="a:CT_ColorDmlA">
     * ```
     */
    penClr?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="loop" type="xsd:boolean" use="optional">
     * ```
     */
    loop?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="showNarration" type="xsd:boolean" use="optional">
     * ```
     */
    showNarration?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="showAnimation" type="xsd:boolean" use="optional">
     * ```
     */
    showAnimation?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="useTimings" type="xsd:boolean" use="optional">
     * ```
     */
    useTimings?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ShowProperties name="CT_ShowProperties">
 * ```
 */
export interface ICTShowPropertiesDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=pml:EG_ShowType ref=pml:EG_ShowType maxOccurs="1" minOccurs="0" name="EG_ShowType" ref="EG_ShowType">
     * ref pml:EG_ShowType
     * ```
     */
    egShowType?: IEGShowTypeDelta | null;
    /**
     * ```xml
     * <xsd:group typeRedirected=pml:EG_SlideListChoice ref=pml:EG_SlideListChoice maxOccurs="1" minOccurs="0" name="EG_SlideListChoice" ref="EG_SlideListChoice">
     * ref pml:EG_SlideListChoice
     * ```
     */
    egSlideListChoice?: IEGSlideListChoiceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="0" name="penClr" type="a:CT_ColorDmlA">
     * ```
     */
    penClr?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="loop" type="xsd:boolean" use="optional">
     * ```
     */
    loop?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="showNarration" type="xsd:boolean" use="optional">
     * ```
     */
    showNarration?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="showAnimation" type="xsd:boolean" use="optional">
     * ```
     */
    showAnimation?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="useTimings" type="xsd:boolean" use="optional">
     * ```
     */
    useTimings?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ColorMRUDmlA name="CT_ColorMRUDmlA">
 * ```
 */
export interface ICTColorMRUDmlA {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="unbounded" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoice[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ColorMRUDmlA name="CT_ColorMRUDmlA">
 * ```
 */
export interface ICTColorMRUDmlADelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="unbounded" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoiceDelta[];
    egColorChoiceOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_OfficeArtExtensionList name="CT_OfficeArtExtensionList">
 * ```
 */
export interface ICTOfficeArtExtensionList {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtension maxOccurs="unbounded" minOccurs="0" name="ext" type="CT_OfficeArtExtension">
     * ```
     */
    ext?: ICTOfficeArtExtension[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_OfficeArtExtensionList name="CT_OfficeArtExtensionList">
 * ```
 */
export interface ICTOfficeArtExtensionListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtension maxOccurs="unbounded" minOccurs="0" name="ext" type="CT_OfficeArtExtension">
     * ```
     */
    ext?: ICTOfficeArtExtensionDelta[];
    extOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableStyleDmlA name="CT_TableStyleDmlA">
 * ```
 */
export interface ICTTableStyleDmlA {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableBackgroundStyle maxOccurs="1" minOccurs="0" name="tblBg" type="CT_TableBackgroundStyle">
     * ```
     */
    tblBg?: ICTTableBackgroundStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="wholeTbl" type="CT_TablePartStyle">
     * ```
     */
    wholeTbl?: ICTTablePartStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="band1H" type="CT_TablePartStyle">
     * ```
     */
    band1H?: ICTTablePartStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="band2H" type="CT_TablePartStyle">
     * ```
     */
    band2H?: ICTTablePartStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="band1V" type="CT_TablePartStyle">
     * ```
     */
    band1V?: ICTTablePartStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="band2V" type="CT_TablePartStyle">
     * ```
     */
    band2V?: ICTTablePartStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="lastCol" type="CT_TablePartStyle">
     * ```
     */
    lastCol?: ICTTablePartStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="firstCol" type="CT_TablePartStyle">
     * ```
     */
    firstCol?: ICTTablePartStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="lastRow" type="CT_TablePartStyle">
     * ```
     */
    lastRow?: ICTTablePartStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="seCell" type="CT_TablePartStyle">
     * ```
     */
    seCell?: ICTTablePartStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="swCell" type="CT_TablePartStyle">
     * ```
     */
    swCell?: ICTTablePartStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="firstRow" type="CT_TablePartStyle">
     * ```
     */
    firstRow?: ICTTablePartStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="neCell" type="CT_TablePartStyle">
     * ```
     */
    neCell?: ICTTablePartStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="nwCell" type="CT_TablePartStyle">
     * ```
     */
    nwCell?: ICTTablePartStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="styleId" type="s:ST_Guid" use="required">
     * ```
     */
    styleId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="styleName" type="xsd:string" use="required">
     * ```
     */
    styleName?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableStyleDmlA name="CT_TableStyleDmlA">
 * ```
 */
export interface ICTTableStyleDmlADelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableBackgroundStyle maxOccurs="1" minOccurs="0" name="tblBg" type="CT_TableBackgroundStyle">
     * ```
     */
    tblBg?: ICTTableBackgroundStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="wholeTbl" type="CT_TablePartStyle">
     * ```
     */
    wholeTbl?: ICTTablePartStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="band1H" type="CT_TablePartStyle">
     * ```
     */
    band1H?: ICTTablePartStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="band2H" type="CT_TablePartStyle">
     * ```
     */
    band2H?: ICTTablePartStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="band1V" type="CT_TablePartStyle">
     * ```
     */
    band1V?: ICTTablePartStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="band2V" type="CT_TablePartStyle">
     * ```
     */
    band2V?: ICTTablePartStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="lastCol" type="CT_TablePartStyle">
     * ```
     */
    lastCol?: ICTTablePartStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="firstCol" type="CT_TablePartStyle">
     * ```
     */
    firstCol?: ICTTablePartStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="lastRow" type="CT_TablePartStyle">
     * ```
     */
    lastRow?: ICTTablePartStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="seCell" type="CT_TablePartStyle">
     * ```
     */
    seCell?: ICTTablePartStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="swCell" type="CT_TablePartStyle">
     * ```
     */
    swCell?: ICTTablePartStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="firstRow" type="CT_TablePartStyle">
     * ```
     */
    firstRow?: ICTTablePartStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="neCell" type="CT_TablePartStyle">
     * ```
     */
    neCell?: ICTTablePartStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TablePartStyle maxOccurs="1" minOccurs="0" name="nwCell" type="CT_TablePartStyle">
     * ```
     */
    nwCell?: ICTTablePartStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="styleId" type="s:ST_Guid" use="required">
     * ```
     */
    styleId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="styleName" type="xsd:string" use="required">
     * ```
     */
    styleName?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_NormalViewProperties name="CT_NormalViewProperties">
 * ```
 */
export interface ICTNormalViewProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_NormalViewPortion maxOccurs="1" minOccurs="1" name="restoredLeft" type="CT_NormalViewPortion">
     * ```
     */
    restoredLeft?: ICTNormalViewPortion;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_NormalViewPortion maxOccurs="1" minOccurs="1" name="restoredTop" type="CT_NormalViewPortion">
     * ```
     */
    restoredTop?: ICTNormalViewPortion;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="showOutlineIcons" type="xsd:boolean" use="optional">
     * ```
     */
    showOutlineIcons?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="snapVertSplitter" type="xsd:boolean" use="optional">
     * ```
     */
    snapVertSplitter?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_SplitterBarState default="restored" name="vertBarState" type="ST_SplitterBarState" use="optional">
     * ```
     */
    vertBarState?: STSplitterBarState;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_SplitterBarState default="restored" name="horzBarState" type="ST_SplitterBarState" use="optional">
     * ```
     */
    horzBarState?: STSplitterBarState;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="preferSingleView" type="xsd:boolean" use="optional">
     * ```
     */
    preferSingleView?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_NormalViewProperties name="CT_NormalViewProperties">
 * ```
 */
export interface ICTNormalViewPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_NormalViewPortion maxOccurs="1" minOccurs="1" name="restoredLeft" type="CT_NormalViewPortion">
     * ```
     */
    restoredLeft?: ICTNormalViewPortionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_NormalViewPortion maxOccurs="1" minOccurs="1" name="restoredTop" type="CT_NormalViewPortion">
     * ```
     */
    restoredTop?: ICTNormalViewPortionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="showOutlineIcons" type="xsd:boolean" use="optional">
     * ```
     */
    showOutlineIcons?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="snapVertSplitter" type="xsd:boolean" use="optional">
     * ```
     */
    snapVertSplitter?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_SplitterBarState default="restored" name="vertBarState" type="ST_SplitterBarState" use="optional">
     * ```
     */
    vertBarState?: STSplitterBarState | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_SplitterBarState default="restored" name="horzBarState" type="ST_SplitterBarState" use="optional">
     * ```
     */
    horzBarState?: STSplitterBarState | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="preferSingleView" type="xsd:boolean" use="optional">
     * ```
     */
    preferSingleView?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_SlideViewProperties name="CT_SlideViewProperties">
 * ```
 */
export interface ICTSlideViewProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CommonSlideViewProperties maxOccurs="1" minOccurs="1" name="cSldViewPr" type="CT_CommonSlideViewProperties">
     * ```
     */
    cSldViewPr?: ICTCommonSlideViewProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_SlideViewProperties name="CT_SlideViewProperties">
 * ```
 */
export interface ICTSlideViewPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CommonSlideViewProperties maxOccurs="1" minOccurs="1" name="cSldViewPr" type="CT_CommonSlideViewProperties">
     * ```
     */
    cSldViewPr?: ICTCommonSlideViewPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_OutlineViewProperties name="CT_OutlineViewProperties">
 * ```
 */
export interface ICTOutlineViewProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CommonViewProperties maxOccurs="1" minOccurs="1" name="cViewPr" type="CT_CommonViewProperties">
     * ```
     */
    cViewPr?: ICTCommonViewProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OutlineViewSlideList maxOccurs="1" minOccurs="0" name="sldLst" type="CT_OutlineViewSlideList">
     * ```
     */
    sldLst?: ICTOutlineViewSlideList;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_OutlineViewProperties name="CT_OutlineViewProperties">
 * ```
 */
export interface ICTOutlineViewPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CommonViewProperties maxOccurs="1" minOccurs="1" name="cViewPr" type="CT_CommonViewProperties">
     * ```
     */
    cViewPr?: ICTCommonViewPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OutlineViewSlideList maxOccurs="1" minOccurs="0" name="sldLst" type="CT_OutlineViewSlideList">
     * ```
     */
    sldLst?: ICTOutlineViewSlideListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_NotesTextViewProperties name="CT_NotesTextViewProperties">
 * ```
 */
export interface ICTNotesTextViewProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CommonViewProperties maxOccurs="1" minOccurs="1" name="cViewPr" type="CT_CommonViewProperties">
     * ```
     */
    cViewPr?: ICTCommonViewProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_NotesTextViewProperties name="CT_NotesTextViewProperties">
 * ```
 */
export interface ICTNotesTextViewPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CommonViewProperties maxOccurs="1" minOccurs="1" name="cViewPr" type="CT_CommonViewProperties">
     * ```
     */
    cViewPr?: ICTCommonViewPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_SlideSorterViewProperties name="CT_SlideSorterViewProperties">
 * ```
 */
export interface ICTSlideSorterViewProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CommonViewProperties maxOccurs="1" minOccurs="1" name="cViewPr" type="CT_CommonViewProperties">
     * ```
     */
    cViewPr?: ICTCommonViewProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="showFormatting" type="xsd:boolean" use="optional">
     * ```
     */
    showFormatting?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_SlideSorterViewProperties name="CT_SlideSorterViewProperties">
 * ```
 */
export interface ICTSlideSorterViewPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CommonViewProperties maxOccurs="1" minOccurs="1" name="cViewPr" type="CT_CommonViewProperties">
     * ```
     */
    cViewPr?: ICTCommonViewPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="showFormatting" type="xsd:boolean" use="optional">
     * ```
     */
    showFormatting?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_NotesViewProperties name="CT_NotesViewProperties">
 * ```
 */
export interface ICTNotesViewProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CommonSlideViewProperties maxOccurs="1" minOccurs="1" name="cSldViewPr" type="CT_CommonSlideViewProperties">
     * ```
     */
    cSldViewPr?: ICTCommonSlideViewProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_NotesViewProperties name="CT_NotesViewProperties">
 * ```
 */
export interface ICTNotesViewPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CommonSlideViewProperties maxOccurs="1" minOccurs="1" name="cSldViewPr" type="CT_CommonSlideViewProperties">
     * ```
     */
    cSldViewPr?: ICTCommonSlideViewPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
}

export enum STViewType {
    PBDEFAULT = 0,
    sldView = 1,
    sldMasterView = 2,
    notesView = 3,
    handoutView = 4,
    notesMasterView = 5,
    outlineView = 6,
    sldSorterView = 7,
    sldThumbnailView = 8,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_EmbeddedFontListEntry name="CT_EmbeddedFontListEntry">
 * ```
 */
export interface ICTEmbeddedFontListEntry {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFont maxOccurs="1" minOccurs="1" name="font" type="a:CT_TextFont">
     * ```
     */
    font?: ICTTextFont;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmbeddedFontDataId maxOccurs="1" minOccurs="0" name="regular" type="CT_EmbeddedFontDataId">
     * ```
     */
    regular?: ICTEmbeddedFontDataId;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmbeddedFontDataId maxOccurs="1" minOccurs="0" name="bold" type="CT_EmbeddedFontDataId">
     * ```
     */
    bold?: ICTEmbeddedFontDataId;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmbeddedFontDataId maxOccurs="1" minOccurs="0" name="italic" type="CT_EmbeddedFontDataId">
     * ```
     */
    italic?: ICTEmbeddedFontDataId;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmbeddedFontDataId maxOccurs="1" minOccurs="0" name="boldItalic" type="CT_EmbeddedFontDataId">
     * ```
     */
    boldItalic?: ICTEmbeddedFontDataId;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_EmbeddedFontListEntry name="CT_EmbeddedFontListEntry">
 * ```
 */
export interface ICTEmbeddedFontListEntryDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFont maxOccurs="1" minOccurs="1" name="font" type="a:CT_TextFont">
     * ```
     */
    font?: ICTTextFontDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmbeddedFontDataId maxOccurs="1" minOccurs="0" name="regular" type="CT_EmbeddedFontDataId">
     * ```
     */
    regular?: ICTEmbeddedFontDataIdDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmbeddedFontDataId maxOccurs="1" minOccurs="0" name="bold" type="CT_EmbeddedFontDataId">
     * ```
     */
    bold?: ICTEmbeddedFontDataIdDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmbeddedFontDataId maxOccurs="1" minOccurs="0" name="italic" type="CT_EmbeddedFontDataId">
     * ```
     */
    italic?: ICTEmbeddedFontDataIdDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmbeddedFontDataId maxOccurs="1" minOccurs="0" name="boldItalic" type="CT_EmbeddedFontDataId">
     * ```
     */
    boldItalic?: ICTEmbeddedFontDataIdDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2012:CT_ExtendedGuide name="CT_ExtendedGuide">
 * ```
 */
export interface ICTExtendedGuide {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="clr" type="a:CT_ColorDmlA">
     * ```
     */
    clr?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="p:CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="xsd:unsignedInt" use="required">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_DirectionPml default="vert" name="orient" type="p:ST_DirectionPml" use="optional">
     * ```
     */
    orient?: STDirectionPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="0" name="pos" type="a:ST_Coordinate32" use="optional">
     * ```
     */
    pos?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="userDrawn" type="xsd:boolean" use="optional">
     * ```
     */
    userDrawn?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2012:CT_ExtendedGuide name="CT_ExtendedGuide">
 * ```
 */
export interface ICTExtendedGuideDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="clr" type="a:CT_ColorDmlA">
     * ```
     */
    clr?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="p:CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="xsd:unsignedInt" use="required">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_DirectionPml default="vert" name="orient" type="p:ST_DirectionPml" use="optional">
     * ```
     */
    orient?: STDirectionPml | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="0" name="pos" type="a:ST_Coordinate32" use="optional">
     * ```
     */
    pos?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="userDrawn" type="xsd:boolean" use="optional">
     * ```
     */
    userDrawn?: boolean | null;
}

export enum STCryptProv {
    PBDEFAULT = 0,
    rsaAES = 1,
    rsaFull = 2,
    custom = 3,
}

export enum STAlgClass {
    PBDEFAULT = 0,
    hash = 1,
    custom = 2,
}

export enum STAlgType {
    PBDEFAULT = 0,
    typeAny = 1,
    custom = 2,
}

export enum STSlideSizeType {
    PBDEFAULT = 0,
    screen4x3 = 1,
    letter = 2,
    A4 = 3,
    '35mm' = 4,
    overhead = 5,
    banner = 6,
    custom = 7,
    ledger = 8,
    A3 = 9,
    B4ISO = 10,
    B5ISO = 11,
    B4JIS = 12,
    B5JIS = 13,
    hagakiCard = 14,
    screen16x9 = 15,
    screen16x10 = 16,
}

export enum STSlideLayoutType {
    PBDEFAULT = 0,
    title = 1,
    tx = 2,
    twoColTx = 3,
    tbl = 4,
    txAndChart = 5,
    chartAndTx = 6,
    dgm = 7,
    chart = 8,
    txAndClipArt = 9,
    clipArtAndTx = 10,
    titleOnly = 11,
    blank = 12,
    txAndObj = 13,
    objAndTx = 14,
    objOnly = 15,
    obj = 16,
    txAndMedia = 17,
    mediaAndTx = 18,
    objOverTx = 19,
    txOverObj = 20,
    txAndTwoObj = 21,
    twoObjAndTx = 22,
    twoObjOverTx = 23,
    fourObj = 24,
    vertTx = 25,
    clipArtAndVertTx = 26,
    vertTitleAndTx = 27,
    vertTitleAndTxOverChart = 28,
    twoObj = 29,
    objAndTwoObj = 30,
    twoObjAndObj = 31,
    cust = 32,
    secHead = 33,
    twoTxTwoObj = 34,
    objTx = 35,
    picTx = 36,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_HeaderFooterPml name="CT_HeaderFooterPml">
 * ```
 */
export interface ICTHeaderFooterPml {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModify;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="sldNum" type="xsd:boolean" use="optional">
     * ```
     */
    sldNum?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="hdr" type="xsd:boolean" use="optional">
     * ```
     */
    hdr?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="ftr" type="xsd:boolean" use="optional">
     * ```
     */
    ftr?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="dt" type="xsd:boolean" use="optional">
     * ```
     */
    dt?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_HeaderFooterPml name="CT_HeaderFooterPml">
 * ```
 */
export interface ICTHeaderFooterPmlDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModifyDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="sldNum" type="xsd:boolean" use="optional">
     * ```
     */
    sldNum?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="hdr" type="xsd:boolean" use="optional">
     * ```
     */
    hdr?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="ftr" type="xsd:boolean" use="optional">
     * ```
     */
    ftr?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="dt" type="xsd:boolean" use="optional">
     * ```
     */
    dt?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_SlideMasterTextStyles name="CT_SlideMasterTextStyles">
 * ```
 */
export interface ICTSlideMasterTextStyles {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextListStyle maxOccurs="1" minOccurs="0" name="titleStyle" type="a:CT_TextListStyle">
     * ```
     */
    titleStyle?: ICTTextListStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextListStyle maxOccurs="1" minOccurs="0" name="bodyStyle" type="a:CT_TextListStyle">
     * ```
     */
    bodyStyle?: ICTTextListStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextListStyle maxOccurs="1" minOccurs="0" name="otherStyle" type="a:CT_TextListStyle">
     * ```
     */
    otherStyle?: ICTTextListStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_SlideMasterTextStyles name="CT_SlideMasterTextStyles">
 * ```
 */
export interface ICTSlideMasterTextStylesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextListStyle maxOccurs="1" minOccurs="0" name="titleStyle" type="a:CT_TextListStyle">
     * ```
     */
    titleStyle?: ICTTextListStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextListStyle maxOccurs="1" minOccurs="0" name="bodyStyle" type="a:CT_TextListStyle">
     * ```
     */
    bodyStyle?: ICTTextListStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextListStyle maxOccurs="1" minOccurs="0" name="otherStyle" type="a:CT_TextListStyle">
     * ```
     */
    otherStyle?: ICTTextListStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ShapeProperties name="CT_ShapeProperties">
 * ```
 */
export interface ICTShapeProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Transform2D maxOccurs="1" minOccurs="0" name="xfrm" type="CT_Transform2D">
     * ```
     */
    xfrm?: ICTTransform2D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_CustomGeometry2D maxOccurs="1" minOccurs="1" name="custGeom" type="CT_CustomGeometry2D">
     * ```
     */
    custGeom?: ICTCustomGeometry2D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PresetGeometry2D maxOccurs="1" minOccurs="1" name="prstGeom" type="CT_PresetGeometry2D">
     * ```
     */
    prstGeom?: ICTPresetGeometry2D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="ln" type="CT_LineProperties">
     * ```
     */
    ln?: ICTLineProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectList maxOccurs="1" minOccurs="1" name="effectLst" type="CT_EffectList">
     * ```
     */
    effectLst?: ICTEffectList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="effectDag" type="CT_EffectContainer">
     * ```
     */
    effectDag?: ICTEffectContainer;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Scene3D maxOccurs="1" minOccurs="0" name="scene3d" type="CT_Scene3D">
     * ```
     */
    scene3d?: ICTScene3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Shape3D maxOccurs="1" minOccurs="0" name="sp3d" type="CT_Shape3D">
     * ```
     */
    sp3d?: ICTShape3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_BlackWhiteMode name="bwMode" type="ST_BlackWhiteMode" use="optional">
     * ```
     */
    bwMode?: STBlackWhiteMode;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ShapeProperties name="CT_ShapeProperties">
 * ```
 */
export interface ICTShapePropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Transform2D maxOccurs="1" minOccurs="0" name="xfrm" type="CT_Transform2D">
     * ```
     */
    xfrm?: ICTTransform2DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_CustomGeometry2D maxOccurs="1" minOccurs="1" name="custGeom" type="CT_CustomGeometry2D">
     * ```
     */
    custGeom?: ICTCustomGeometry2DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PresetGeometry2D maxOccurs="1" minOccurs="1" name="prstGeom" type="CT_PresetGeometry2D">
     * ```
     */
    prstGeom?: ICTPresetGeometry2DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="ln" type="CT_LineProperties">
     * ```
     */
    ln?: ICTLinePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectList maxOccurs="1" minOccurs="1" name="effectLst" type="CT_EffectList">
     * ```
     */
    effectLst?: ICTEffectListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="effectDag" type="CT_EffectContainer">
     * ```
     */
    effectDag?: ICTEffectContainerDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Scene3D maxOccurs="1" minOccurs="0" name="scene3d" type="CT_Scene3D">
     * ```
     */
    scene3d?: ICTScene3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Shape3D maxOccurs="1" minOccurs="0" name="sp3d" type="CT_Shape3D">
     * ```
     */
    sp3d?: ICTShape3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_BlackWhiteMode name="bwMode" type="ST_BlackWhiteMode" use="optional">
     * ```
     */
    bwMode?: STBlackWhiteMode | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean | null;
}

export interface INonVisualPropertiesDelta {
    /** 占位符 */
    ph?: ICTPlaceholderPmlDelta | null;
    egMedia?: IEGMediaDelta | null;
    custDataLst?: ICTCustomerDataListDelta | null;
    isPhoto?: boolean;
    userDrawn?: boolean;
    /** 超链接 */
    hlinkClick?: ICTHyperlinkDmlADelta | null;
    hlinkHover?: ICTHyperlinkDmlADelta | null;
    name?: string | null;
    descr?: string | null;
    /** 是否隐藏 */
    hidden?: boolean | null;
    title?: string | null;
    /** 各种锁 */
    locking?: ILockingDelta | null;
    /** Shape 专属不可见属性 */
    shape?: IShapeNonVisualPropertiesDelta | null;
    /** Connector 专属不可见属性 */
    connector?: IConnectorNonVisualPropertiesDelta | null;
    /** Picture 专属不可见属性 */
    picture?: IPictureNonVisualPropertiesDelta | null;
    /** 附件 */
    attachment?: IAttachmentInfo | null;
    /** 插件 */
    addon?: IAddon | null;
    media?: ICTMediaDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ShapeStyle name="CT_ShapeStyle">
 * ```
 */
export interface ICTShapeStyle {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StyleMatrixReference maxOccurs="1" minOccurs="1" name="lnRef" type="CT_StyleMatrixReference">
     * ```
     */
    lnRef?: ICTStyleMatrixReference;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StyleMatrixReference maxOccurs="1" minOccurs="1" name="fillRef" type="CT_StyleMatrixReference">
     * ```
     */
    fillRef?: ICTStyleMatrixReference;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StyleMatrixReference maxOccurs="1" minOccurs="1" name="effectRef" type="CT_StyleMatrixReference">
     * ```
     */
    effectRef?: ICTStyleMatrixReference;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FontReferenceDmlA maxOccurs="1" minOccurs="1" name="fontRef" type="CT_FontReferenceDmlA">
     * ```
     */
    fontRef?: ICTFontReferenceDmlA;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ShapeStyle name="CT_ShapeStyle">
 * ```
 */
export interface ICTShapeStyleDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StyleMatrixReference maxOccurs="1" minOccurs="1" name="lnRef" type="CT_StyleMatrixReference">
     * ```
     */
    lnRef?: ICTStyleMatrixReferenceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StyleMatrixReference maxOccurs="1" minOccurs="1" name="fillRef" type="CT_StyleMatrixReference">
     * ```
     */
    fillRef?: ICTStyleMatrixReferenceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StyleMatrixReference maxOccurs="1" minOccurs="1" name="effectRef" type="CT_StyleMatrixReference">
     * ```
     */
    effectRef?: ICTStyleMatrixReferenceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FontReferenceDmlA maxOccurs="1" minOccurs="1" name="fontRef" type="CT_FontReferenceDmlA">
     * ```
     */
    fontRef?: ICTFontReferenceDmlADelta | null;
}

export interface IShapePropertiesDelta {
    txBody?: ICTTextBodyDelta | null;
    useBgFill?: boolean;
}

export interface IGraphicalObjectFrameDelta {
    graphic?: ICTGraphicalObjectDelta | null;
    bwMode?: STBlackWhiteMode | null;
}

export interface IPicturePropertiesDelta {
    blipFill?: ICTBlipFillPropertiesDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableProperties name="CT_TableProperties">
 * ```
 */
export interface ICTTableProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectList maxOccurs="1" minOccurs="1" name="effectLst" type="CT_EffectList">
     * ```
     */
    effectLst?: ICTEffectList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="effectDag" type="CT_EffectContainer">
     * ```
     */
    effectDag?: ICTEffectContainer;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableStyleDmlA name="tableStyle" type="CT_TableStyleDmlA">
     * ```
     */
    tableStyle?: ICTTableStyleDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string name="tableStyleId" type="s:ST_Guid">
     * ```
     */
    tableStyleId?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="rtl" type="xsd:boolean" use="optional">
     * ```
     */
    rtl?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="firstRow" type="xsd:boolean" use="optional">
     * ```
     */
    firstRow?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="firstCol" type="xsd:boolean" use="optional">
     * ```
     */
    firstCol?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="lastRow" type="xsd:boolean" use="optional">
     * ```
     */
    lastRow?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="lastCol" type="xsd:boolean" use="optional">
     * ```
     */
    lastCol?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="bandRow" type="xsd:boolean" use="optional">
     * ```
     */
    bandRow?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="bandCol" type="xsd:boolean" use="optional">
     * ```
     */
    bandCol?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableProperties name="CT_TableProperties">
 * ```
 */
export interface ICTTablePropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectList maxOccurs="1" minOccurs="1" name="effectLst" type="CT_EffectList">
     * ```
     */
    effectLst?: ICTEffectListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="effectDag" type="CT_EffectContainer">
     * ```
     */
    effectDag?: ICTEffectContainerDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableStyleDmlA name="tableStyle" type="CT_TableStyleDmlA">
     * ```
     */
    tableStyle?: ICTTableStyleDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string name="tableStyleId" type="s:ST_Guid">
     * ```
     */
    tableStyleId?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="rtl" type="xsd:boolean" use="optional">
     * ```
     */
    rtl?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="firstRow" type="xsd:boolean" use="optional">
     * ```
     */
    firstRow?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="firstCol" type="xsd:boolean" use="optional">
     * ```
     */
    firstCol?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="lastRow" type="xsd:boolean" use="optional">
     * ```
     */
    lastRow?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="lastCol" type="xsd:boolean" use="optional">
     * ```
     */
    lastCol?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="bandRow" type="xsd:boolean" use="optional">
     * ```
     */
    bandRow?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="bandCol" type="xsd:boolean" use="optional">
     * ```
     */
    bandCol?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean | null;
}

export interface IInkPropertiesDelta {
    /** keep数据 */
    inkData?: ICTInkInkMLDelta | null;
}

export interface ISmartArtPropertiesDelta {
    data?: ICTDataModelDelta | null;
    colorDef?: ICTColorTransformDelta | null;
    layoutDef?: ICTDiagramDefinitionDelta | null;
    styleDef?: ICTStyleDefinitionDelta | null;
    drawing?: ICTDrawingMsDspDelta | null;
}

export interface IOleObjectPropertiesDelta {
    object?: ICTOleObjectPmlDelta | null;
}

export interface IModel3DPropertiesDelta {
    modelData?: ICTModel3DDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_LineProperties name="CT_LineProperties">
 * ```
 */
export interface ICTLineProperties {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_LineFillProperties ref=dmlmain:EG_LineFillProperties maxOccurs="1" minOccurs="0" name="EG_LineFillProperties" ref="EG_LineFillProperties">
     * ref dmlmain:EG_LineFillProperties
     * ```
     */
    egLineFillProperties?: IEGLineFillProperties;
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_LineDashProperties ref=dmlmain:EG_LineDashProperties maxOccurs="1" minOccurs="0" name="EG_LineDashProperties" ref="EG_LineDashProperties">
     * ref dmlmain:EG_LineDashProperties
     * ```
     */
    egLineDashProperties?: IEGLineDashProperties;
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_LineJoinProperties ref=dmlmain:EG_LineJoinProperties maxOccurs="1" minOccurs="0" name="EG_LineJoinProperties" ref="EG_LineJoinProperties">
     * ref dmlmain:EG_LineJoinProperties
     * ```
     */
    egLineJoinProperties?: IEGLineJoinProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineEndProperties maxOccurs="1" minOccurs="0" name="headEnd" type="CT_LineEndProperties">
     * ```
     */
    headEnd?: ICTLineEndProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineEndProperties maxOccurs="1" minOccurs="0" name="tailEnd" type="CT_LineEndProperties">
     * ```
     */
    tailEnd?: ICTLineEndProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="w" type="ST_LineWidth" use="optional">
     * ```
     */
    w?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_LineCap name="cap" type="ST_LineCap" use="optional">
     * ```
     */
    cap?: STLineCap;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_CompoundLine name="cmpd" type="ST_CompoundLine" use="optional">
     * ```
     */
    cmpd?: STCompoundLine;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PenAlignment name="algn" type="ST_PenAlignment" use="optional">
     * ```
     */
    algn?: STPenAlignment;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_LineProperties name="CT_LineProperties">
 * ```
 */
export interface ICTLinePropertiesDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_LineFillProperties ref=dmlmain:EG_LineFillProperties maxOccurs="1" minOccurs="0" name="EG_LineFillProperties" ref="EG_LineFillProperties">
     * ref dmlmain:EG_LineFillProperties
     * ```
     */
    egLineFillProperties?: IEGLineFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_LineDashProperties ref=dmlmain:EG_LineDashProperties maxOccurs="1" minOccurs="0" name="EG_LineDashProperties" ref="EG_LineDashProperties">
     * ref dmlmain:EG_LineDashProperties
     * ```
     */
    egLineDashProperties?: IEGLineDashPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_LineJoinProperties ref=dmlmain:EG_LineJoinProperties maxOccurs="1" minOccurs="0" name="EG_LineJoinProperties" ref="EG_LineJoinProperties">
     * ref dmlmain:EG_LineJoinProperties
     * ```
     */
    egLineJoinProperties?: IEGLineJoinPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineEndProperties maxOccurs="1" minOccurs="0" name="headEnd" type="CT_LineEndProperties">
     * ```
     */
    headEnd?: ICTLineEndPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineEndProperties maxOccurs="1" minOccurs="0" name="tailEnd" type="CT_LineEndProperties">
     * ```
     */
    tailEnd?: ICTLineEndPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="w" type="ST_LineWidth" use="optional">
     * ```
     */
    w?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_LineCap name="cap" type="ST_LineCap" use="optional">
     * ```
     */
    cap?: STLineCap | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_CompoundLine name="cmpd" type="ST_CompoundLine" use="optional">
     * ```
     */
    cmpd?: STCompoundLine | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PenAlignment name="algn" type="ST_PenAlignment" use="optional">
     * ```
     */
    algn?: STPenAlignment | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Cell3D name="CT_Cell3D">
 * ```
 */
export interface ICTCell3D {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Bevel maxOccurs="1" minOccurs="1" name="bevel" type="CT_Bevel">
     * ```
     */
    bevel?: ICTBevel;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LightRig maxOccurs="1" minOccurs="0" name="lightRig" type="CT_LightRig">
     * ```
     */
    lightRig?: ICTLightRig;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PresetMaterialType default="plastic" name="prstMaterial" type="ST_PresetMaterialType" use="optional">
     * ```
     */
    prstMaterial?: STPresetMaterialType;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Cell3D name="CT_Cell3D">
 * ```
 */
export interface ICTCell3DDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Bevel maxOccurs="1" minOccurs="1" name="bevel" type="CT_Bevel">
     * ```
     */
    bevel?: ICTBevelDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LightRig maxOccurs="1" minOccurs="0" name="lightRig" type="CT_LightRig">
     * ```
     */
    lightRig?: ICTLightRigDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PresetMaterialType default="plastic" name="prstMaterial" type="ST_PresetMaterialType" use="optional">
     * ```
     */
    prstMaterial?: STPresetMaterialType | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_SolidColorFillProperties name="CT_SolidColorFillProperties">
 * ```
 */
export interface ICTSolidColorFillProperties {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoice;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_SolidColorFillProperties name="CT_SolidColorFillProperties">
 * ```
 */
export interface ICTSolidColorFillPropertiesDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoiceDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GradientFillProperties name="CT_GradientFillProperties">
 * ```
 */
export interface ICTGradientFillProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientStopList maxOccurs="1" minOccurs="0" name="gsLst" type="CT_GradientStopList">
     * ```
     */
    gsLst?: ICTGradientStopList;
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ShadeProperties ref=dmlmain:EG_ShadeProperties maxOccurs="1" minOccurs="0" name="EG_ShadeProperties" ref="EG_ShadeProperties">
     * ref dmlmain:EG_ShadeProperties
     * ```
     */
    egShadeProperties?: IEGShadeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_RelativeRect maxOccurs="1" minOccurs="0" name="tileRect" type="CT_RelativeRect">
     * ```
     */
    tileRect?: ICTRelativeRect;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TileFlipMode default="none" name="flip" type="ST_TileFlipMode" use="optional">
     * ```
     */
    flip?: STTileFlipMode;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="rotWithShape" type="xsd:boolean" use="optional">
     * ```
     */
    rotWithShape?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GradientFillProperties name="CT_GradientFillProperties">
 * ```
 */
export interface ICTGradientFillPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientStopList maxOccurs="1" minOccurs="0" name="gsLst" type="CT_GradientStopList">
     * ```
     */
    gsLst?: ICTGradientStopListDelta | null;
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ShadeProperties ref=dmlmain:EG_ShadeProperties maxOccurs="1" minOccurs="0" name="EG_ShadeProperties" ref="EG_ShadeProperties">
     * ref dmlmain:EG_ShadeProperties
     * ```
     */
    egShadeProperties?: IEGShadePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_RelativeRect maxOccurs="1" minOccurs="0" name="tileRect" type="CT_RelativeRect">
     * ```
     */
    tileRect?: ICTRelativeRectDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TileFlipMode default="none" name="flip" type="ST_TileFlipMode" use="optional">
     * ```
     */
    flip?: STTileFlipMode | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="rotWithShape" type="xsd:boolean" use="optional">
     * ```
     */
    rotWithShape?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_BlipFillProperties name="CT_BlipFillProperties">
 * ```
 */
export interface ICTBlipFillProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Blip maxOccurs="1" minOccurs="0" name="blip" type="CT_Blip">
     * ```
     */
    blip?: ICTBlip;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_RelativeRect maxOccurs="1" minOccurs="0" name="srcRect" type="CT_RelativeRect">
     * ```
     */
    srcRect?: ICTRelativeRect;
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_FillModeProperties ref=dmlmain:EG_FillModeProperties maxOccurs="1" minOccurs="0" name="EG_FillModeProperties" ref="EG_FillModeProperties">
     * ref dmlmain:EG_FillModeProperties
     * ```
     */
    egFillModeProperties?: IEGFillModeProperties;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="dpi" type="xsd:unsignedInt" use="optional">
     * ```
     */
    dpi?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="rotWithShape" type="xsd:boolean" use="optional">
     * ```
     */
    rotWithShape?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_BlipFillProperties name="CT_BlipFillProperties">
 * ```
 */
export interface ICTBlipFillPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Blip maxOccurs="1" minOccurs="0" name="blip" type="CT_Blip">
     * ```
     */
    blip?: ICTBlipDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_RelativeRect maxOccurs="1" minOccurs="0" name="srcRect" type="CT_RelativeRect">
     * ```
     */
    srcRect?: ICTRelativeRectDelta | null;
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_FillModeProperties ref=dmlmain:EG_FillModeProperties maxOccurs="1" minOccurs="0" name="EG_FillModeProperties" ref="EG_FillModeProperties">
     * ref dmlmain:EG_FillModeProperties
     * ```
     */
    egFillModeProperties?: IEGFillModePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="dpi" type="xsd:unsignedInt" use="optional">
     * ```
     */
    dpi?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="rotWithShape" type="xsd:boolean" use="optional">
     * ```
     */
    rotWithShape?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PatternFillProperties name="CT_PatternFillProperties">
 * ```
 */
export interface ICTPatternFillProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="0" name="fgClr" type="CT_ColorDmlA">
     * ```
     */
    fgClr?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="0" name="bgClr" type="CT_ColorDmlA">
     * ```
     */
    bgClr?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PresetPatternVal name="prst" type="ST_PresetPatternVal" use="optional">
     * ```
     */
    prst?: STPresetPatternVal;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PatternFillProperties name="CT_PatternFillProperties">
 * ```
 */
export interface ICTPatternFillPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="0" name="fgClr" type="CT_ColorDmlA">
     * ```
     */
    fgClr?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="0" name="bgClr" type="CT_ColorDmlA">
     * ```
     */
    bgClr?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PresetPatternVal name="prst" type="ST_PresetPatternVal" use="optional">
     * ```
     */
    prst?: STPresetPatternVal | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_HeadersDmlA name="CT_HeadersDmlA">
 * ```
 */
export interface ICTHeadersDmlA {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string name="header" type="xsd:string">
     * ```
     */
    header?: string[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_HeadersDmlA name="CT_HeadersDmlA">
 * ```
 */
export interface ICTHeadersDmlADelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string name="header" type="xsd:string">
     * ```
     */
    header?: string[];
    headerOp?: IArrayOp;
}

export enum STTextVerticalType {
    PBDEFAULT = 0,
    horz = 1,
    vert = 2,
    vert270 = 3,
    wordArtVert = 4,
    eaVert = 5,
    mongolianVert = 6,
    wordArtVertRtl = 7,
}

export enum STTextAnchoringType {
    PBDEFAULT = 0,
    t = 1,
    ctr = 2,
    b = 3,
    just = 4,
    dist = 5,
}

export enum STTextHorzOverflowType {
    PBDEFAULT = 0,
    overflow = 1,
    clip = 2,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTimeNodeParallel name="CT_TLTimeNodeParallel">
 * ```
 */
export interface ICTTLTimeNodeParallel {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonTimeNodeData maxOccurs="1" minOccurs="1" name="cTn" type="CT_TLCommonTimeNodeData">
     * ```
     */
    cTn?: ICTTLCommonTimeNodeData;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTimeNodeParallel name="CT_TLTimeNodeParallel">
 * ```
 */
export interface ICTTLTimeNodeParallelDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonTimeNodeData maxOccurs="1" minOccurs="1" name="cTn" type="CT_TLCommonTimeNodeData">
     * ```
     */
    cTn?: ICTTLCommonTimeNodeDataDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTimeNodeSequence name="CT_TLTimeNodeSequence">
 * ```
 */
export interface ICTTLTimeNodeSequence {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonTimeNodeData maxOccurs="1" minOccurs="1" name="cTn" type="CT_TLCommonTimeNodeData">
     * ```
     */
    cTn?: ICTTLCommonTimeNodeData;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeConditionList maxOccurs="1" minOccurs="0" name="prevCondLst" type="CT_TLTimeConditionList">
     * ```
     */
    prevCondLst?: ICTTLTimeConditionList;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeConditionList maxOccurs="1" minOccurs="0" name="nextCondLst" type="CT_TLTimeConditionList">
     * ```
     */
    nextCondLst?: ICTTLTimeConditionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="concurrent" type="xsd:boolean" use="optional">
     * ```
     */
    concurrent?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLPreviousActionType name="prevAc" type="ST_TLPreviousActionType" use="optional">
     * ```
     */
    prevAc?: STTLPreviousActionType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLNextActionType name="nextAc" type="ST_TLNextActionType" use="optional">
     * ```
     */
    nextAc?: STTLNextActionType;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTimeNodeSequence name="CT_TLTimeNodeSequence">
 * ```
 */
export interface ICTTLTimeNodeSequenceDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonTimeNodeData maxOccurs="1" minOccurs="1" name="cTn" type="CT_TLCommonTimeNodeData">
     * ```
     */
    cTn?: ICTTLCommonTimeNodeDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeConditionList maxOccurs="1" minOccurs="0" name="prevCondLst" type="CT_TLTimeConditionList">
     * ```
     */
    prevCondLst?: ICTTLTimeConditionListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeConditionList maxOccurs="1" minOccurs="0" name="nextCondLst" type="CT_TLTimeConditionList">
     * ```
     */
    nextCondLst?: ICTTLTimeConditionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="concurrent" type="xsd:boolean" use="optional">
     * ```
     */
    concurrent?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLPreviousActionType name="prevAc" type="ST_TLPreviousActionType" use="optional">
     * ```
     */
    prevAc?: STTLPreviousActionType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLNextActionType name="nextAc" type="ST_TLNextActionType" use="optional">
     * ```
     */
    nextAc?: STTLNextActionType | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTimeNodeExclusive name="CT_TLTimeNodeExclusive">
 * ```
 */
export interface ICTTLTimeNodeExclusive {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonTimeNodeData maxOccurs="1" minOccurs="1" name="cTn" type="CT_TLCommonTimeNodeData">
     * ```
     */
    cTn?: ICTTLCommonTimeNodeData;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTimeNodeExclusive name="CT_TLTimeNodeExclusive">
 * ```
 */
export interface ICTTLTimeNodeExclusiveDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonTimeNodeData maxOccurs="1" minOccurs="1" name="cTn" type="CT_TLCommonTimeNodeData">
     * ```
     */
    cTn?: ICTTLCommonTimeNodeDataDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimateBehavior name="CT_TLAnimateBehavior">
 * ```
 */
export interface ICTTLAnimateBehavior {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonBehaviorData maxOccurs="1" minOccurs="1" name="cBhvr" type="CT_TLCommonBehaviorData">
     * ```
     */
    cBhvr?: ICTTLCommonBehaviorData;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeAnimateValueList maxOccurs="1" minOccurs="0" name="tavLst" type="CT_TLTimeAnimateValueList">
     * ```
     */
    tavLst?: ICTTLTimeAnimateValueList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="by" type="xsd:string" use="optional">
     * ```
     */
    by?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="from" type="xsd:string" use="optional">
     * ```
     */
    from?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="to" type="xsd:string" use="optional">
     * ```
     */
    to?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLAnimateBehaviorCalcMode name="calcmode" type="ST_TLAnimateBehaviorCalcMode" use="optional">
     * ```
     */
    calcmode?: STTLAnimateBehaviorCalcMode;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLAnimateBehaviorValueType name="valueType" type="ST_TLAnimateBehaviorValueType" use="optional">
     * ```
     */
    valueType?: STTLAnimateBehaviorValueType;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimateBehavior name="CT_TLAnimateBehavior">
 * ```
 */
export interface ICTTLAnimateBehaviorDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonBehaviorData maxOccurs="1" minOccurs="1" name="cBhvr" type="CT_TLCommonBehaviorData">
     * ```
     */
    cBhvr?: ICTTLCommonBehaviorDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeAnimateValueList maxOccurs="1" minOccurs="0" name="tavLst" type="CT_TLTimeAnimateValueList">
     * ```
     */
    tavLst?: ICTTLTimeAnimateValueListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="by" type="xsd:string" use="optional">
     * ```
     */
    by?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="from" type="xsd:string" use="optional">
     * ```
     */
    from?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="to" type="xsd:string" use="optional">
     * ```
     */
    to?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLAnimateBehaviorCalcMode name="calcmode" type="ST_TLAnimateBehaviorCalcMode" use="optional">
     * ```
     */
    calcmode?: STTLAnimateBehaviorCalcMode | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLAnimateBehaviorValueType name="valueType" type="ST_TLAnimateBehaviorValueType" use="optional">
     * ```
     */
    valueType?: STTLAnimateBehaviorValueType | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimateColorBehavior name="CT_TLAnimateColorBehavior">
 * ```
 */
export interface ICTTLAnimateColorBehavior {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonBehaviorData maxOccurs="1" minOccurs="1" name="cBhvr" type="CT_TLCommonBehaviorData">
     * ```
     */
    cBhvr?: ICTTLCommonBehaviorData;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLByAnimateColorTransform maxOccurs="1" minOccurs="0" name="by" type="CT_TLByAnimateColorTransform">
     * ```
     */
    by?: ICTTLByAnimateColorTransform;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="0" name="from" type="a:CT_ColorDmlA">
     * ```
     */
    from?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="0" name="to" type="a:CT_ColorDmlA">
     * ```
     */
    to?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLAnimateColorSpace name="clrSpc" type="ST_TLAnimateColorSpace" use="optional">
     * ```
     */
    clrSpc?: STTLAnimateColorSpace;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLAnimateColorDirection name="dir" type="ST_TLAnimateColorDirection" use="optional">
     * ```
     */
    dir?: STTLAnimateColorDirection;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimateColorBehavior name="CT_TLAnimateColorBehavior">
 * ```
 */
export interface ICTTLAnimateColorBehaviorDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonBehaviorData maxOccurs="1" minOccurs="1" name="cBhvr" type="CT_TLCommonBehaviorData">
     * ```
     */
    cBhvr?: ICTTLCommonBehaviorDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLByAnimateColorTransform maxOccurs="1" minOccurs="0" name="by" type="CT_TLByAnimateColorTransform">
     * ```
     */
    by?: ICTTLByAnimateColorTransformDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="0" name="from" type="a:CT_ColorDmlA">
     * ```
     */
    from?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="0" name="to" type="a:CT_ColorDmlA">
     * ```
     */
    to?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLAnimateColorSpace name="clrSpc" type="ST_TLAnimateColorSpace" use="optional">
     * ```
     */
    clrSpc?: STTLAnimateColorSpace | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLAnimateColorDirection name="dir" type="ST_TLAnimateColorDirection" use="optional">
     * ```
     */
    dir?: STTLAnimateColorDirection | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimateEffectBehavior name="CT_TLAnimateEffectBehavior">
 * ```
 */
export interface ICTTLAnimateEffectBehavior {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonBehaviorData maxOccurs="1" minOccurs="1" name="cBhvr" type="CT_TLCommonBehaviorData">
     * ```
     */
    cBhvr?: ICTTLCommonBehaviorData;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimVariant maxOccurs="1" minOccurs="0" name="progress" type="CT_TLAnimVariant">
     * ```
     */
    progress?: ICTTLAnimVariant;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLAnimateEffectTransition default="in" name="transition" type="ST_TLAnimateEffectTransition" use="optional">
     * ```
     */
    transition?: STTLAnimateEffectTransition;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="filter" type="xsd:string" use="optional">
     * ```
     */
    filter?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="prLst" type="xsd:string" use="optional">
     * ```
     */
    prLst?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimateEffectBehavior name="CT_TLAnimateEffectBehavior">
 * ```
 */
export interface ICTTLAnimateEffectBehaviorDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonBehaviorData maxOccurs="1" minOccurs="1" name="cBhvr" type="CT_TLCommonBehaviorData">
     * ```
     */
    cBhvr?: ICTTLCommonBehaviorDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimVariant maxOccurs="1" minOccurs="0" name="progress" type="CT_TLAnimVariant">
     * ```
     */
    progress?: ICTTLAnimVariantDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLAnimateEffectTransition default="in" name="transition" type="ST_TLAnimateEffectTransition" use="optional">
     * ```
     */
    transition?: STTLAnimateEffectTransition | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="filter" type="xsd:string" use="optional">
     * ```
     */
    filter?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="prLst" type="xsd:string" use="optional">
     * ```
     */
    prLst?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimateMotionBehavior name="CT_TLAnimateMotionBehavior">
 * ```
 */
export interface ICTTLAnimateMotionBehavior {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonBehaviorData maxOccurs="1" minOccurs="1" name="cBhvr" type="CT_TLCommonBehaviorData">
     * ```
     */
    cBhvr?: ICTTLCommonBehaviorData;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLPoint maxOccurs="1" minOccurs="0" name="by" type="CT_TLPoint">
     * ```
     */
    by?: ICTTLPoint;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLPoint maxOccurs="1" minOccurs="0" name="from" type="CT_TLPoint">
     * ```
     */
    from?: ICTTLPoint;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLPoint maxOccurs="1" minOccurs="0" name="to" type="CT_TLPoint">
     * ```
     */
    to?: ICTTLPoint;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLPoint maxOccurs="1" minOccurs="0" name="rCtr" type="CT_TLPoint">
     * ```
     */
    rCtr?: ICTTLPoint;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLAnimateMotionBehaviorOrigin name="origin" type="ST_TLAnimateMotionBehaviorOrigin" use="optional">
     * ```
     */
    origin?: STTLAnimateMotionBehaviorOrigin;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="path" type="xsd:string" use="optional">
     * ```
     */
    path?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLAnimateMotionPathEditMode name="pathEditMode" type="ST_TLAnimateMotionPathEditMode" use="optional">
     * ```
     */
    pathEditMode?: STTLAnimateMotionPathEditMode;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="rAng" type="a:ST_AngleDmlA" use="optional">
     * ```
     */
    rAng?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="ptsTypes" type="xsd:string" use="optional">
     * ```
     */
    ptsTypes?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimateMotionBehavior name="CT_TLAnimateMotionBehavior">
 * ```
 */
export interface ICTTLAnimateMotionBehaviorDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonBehaviorData maxOccurs="1" minOccurs="1" name="cBhvr" type="CT_TLCommonBehaviorData">
     * ```
     */
    cBhvr?: ICTTLCommonBehaviorDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLPoint maxOccurs="1" minOccurs="0" name="by" type="CT_TLPoint">
     * ```
     */
    by?: ICTTLPointDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLPoint maxOccurs="1" minOccurs="0" name="from" type="CT_TLPoint">
     * ```
     */
    from?: ICTTLPointDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLPoint maxOccurs="1" minOccurs="0" name="to" type="CT_TLPoint">
     * ```
     */
    to?: ICTTLPointDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLPoint maxOccurs="1" minOccurs="0" name="rCtr" type="CT_TLPoint">
     * ```
     */
    rCtr?: ICTTLPointDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLAnimateMotionBehaviorOrigin name="origin" type="ST_TLAnimateMotionBehaviorOrigin" use="optional">
     * ```
     */
    origin?: STTLAnimateMotionBehaviorOrigin | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="path" type="xsd:string" use="optional">
     * ```
     */
    path?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLAnimateMotionPathEditMode name="pathEditMode" type="ST_TLAnimateMotionPathEditMode" use="optional">
     * ```
     */
    pathEditMode?: STTLAnimateMotionPathEditMode | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="rAng" type="a:ST_AngleDmlA" use="optional">
     * ```
     */
    rAng?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="ptsTypes" type="xsd:string" use="optional">
     * ```
     */
    ptsTypes?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimateRotationBehavior name="CT_TLAnimateRotationBehavior">
 * ```
 */
export interface ICTTLAnimateRotationBehavior {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonBehaviorData maxOccurs="1" minOccurs="1" name="cBhvr" type="CT_TLCommonBehaviorData">
     * ```
     */
    cBhvr?: ICTTLCommonBehaviorData;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="by" type="a:ST_AngleDmlA" use="optional">
     * ```
     */
    by?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="from" type="a:ST_AngleDmlA" use="optional">
     * ```
     */
    from?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="to" type="a:ST_AngleDmlA" use="optional">
     * ```
     */
    to?: number;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimateRotationBehavior name="CT_TLAnimateRotationBehavior">
 * ```
 */
export interface ICTTLAnimateRotationBehaviorDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonBehaviorData maxOccurs="1" minOccurs="1" name="cBhvr" type="CT_TLCommonBehaviorData">
     * ```
     */
    cBhvr?: ICTTLCommonBehaviorDataDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="by" type="a:ST_AngleDmlA" use="optional">
     * ```
     */
    by?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="from" type="a:ST_AngleDmlA" use="optional">
     * ```
     */
    from?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="to" type="a:ST_AngleDmlA" use="optional">
     * ```
     */
    to?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimateScaleBehavior name="CT_TLAnimateScaleBehavior">
 * ```
 */
export interface ICTTLAnimateScaleBehavior {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonBehaviorData maxOccurs="1" minOccurs="1" name="cBhvr" type="CT_TLCommonBehaviorData">
     * ```
     */
    cBhvr?: ICTTLCommonBehaviorData;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLPoint maxOccurs="1" minOccurs="0" name="by" type="CT_TLPoint">
     * ```
     */
    by?: ICTTLPoint;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLPoint maxOccurs="1" minOccurs="0" name="from" type="CT_TLPoint">
     * ```
     */
    from?: ICTTLPoint;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLPoint maxOccurs="1" minOccurs="0" name="to" type="CT_TLPoint">
     * ```
     */
    to?: ICTTLPoint;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="zoomContents" type="xsd:boolean" use="optional">
     * ```
     */
    zoomContents?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimateScaleBehavior name="CT_TLAnimateScaleBehavior">
 * ```
 */
export interface ICTTLAnimateScaleBehaviorDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonBehaviorData maxOccurs="1" minOccurs="1" name="cBhvr" type="CT_TLCommonBehaviorData">
     * ```
     */
    cBhvr?: ICTTLCommonBehaviorDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLPoint maxOccurs="1" minOccurs="0" name="by" type="CT_TLPoint">
     * ```
     */
    by?: ICTTLPointDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLPoint maxOccurs="1" minOccurs="0" name="from" type="CT_TLPoint">
     * ```
     */
    from?: ICTTLPointDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLPoint maxOccurs="1" minOccurs="0" name="to" type="CT_TLPoint">
     * ```
     */
    to?: ICTTLPointDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="zoomContents" type="xsd:boolean" use="optional">
     * ```
     */
    zoomContents?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLCommandBehavior name="CT_TLCommandBehavior">
 * ```
 */
export interface ICTTLCommandBehavior {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonBehaviorData maxOccurs="1" minOccurs="1" name="cBhvr" type="CT_TLCommonBehaviorData">
     * ```
     */
    cBhvr?: ICTTLCommonBehaviorData;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLCommandType name="type" type="ST_TLCommandType" use="optional">
     * ```
     */
    type?: STTLCommandType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="cmd" type="xsd:string" use="optional">
     * ```
     */
    cmd?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLCommandBehavior name="CT_TLCommandBehavior">
 * ```
 */
export interface ICTTLCommandBehaviorDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonBehaviorData maxOccurs="1" minOccurs="1" name="cBhvr" type="CT_TLCommonBehaviorData">
     * ```
     */
    cBhvr?: ICTTLCommonBehaviorDataDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLCommandType name="type" type="ST_TLCommandType" use="optional">
     * ```
     */
    type?: STTLCommandType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="cmd" type="xsd:string" use="optional">
     * ```
     */
    cmd?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLSetBehavior name="CT_TLSetBehavior">
 * ```
 */
export interface ICTTLSetBehavior {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonBehaviorData maxOccurs="1" minOccurs="1" name="cBhvr" type="CT_TLCommonBehaviorData">
     * ```
     */
    cBhvr?: ICTTLCommonBehaviorData;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimVariant maxOccurs="1" minOccurs="0" name="to" type="CT_TLAnimVariant">
     * ```
     */
    to?: ICTTLAnimVariant;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLSetBehavior name="CT_TLSetBehavior">
 * ```
 */
export interface ICTTLSetBehaviorDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonBehaviorData maxOccurs="1" minOccurs="1" name="cBhvr" type="CT_TLCommonBehaviorData">
     * ```
     */
    cBhvr?: ICTTLCommonBehaviorDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimVariant maxOccurs="1" minOccurs="0" name="to" type="CT_TLAnimVariant">
     * ```
     */
    to?: ICTTLAnimVariantDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLMediaNodeAudio name="CT_TLMediaNodeAudio">
 * ```
 */
export interface ICTTLMediaNodeAudio {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonMediaNodeData maxOccurs="1" minOccurs="1" name="cMediaNode" type="CT_TLCommonMediaNodeData">
     * ```
     */
    cMediaNode?: ICTTLCommonMediaNodeData;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="isNarration" type="xsd:boolean" use="optional">
     * ```
     */
    isNarration?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLMediaNodeAudio name="CT_TLMediaNodeAudio">
 * ```
 */
export interface ICTTLMediaNodeAudioDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonMediaNodeData maxOccurs="1" minOccurs="1" name="cMediaNode" type="CT_TLCommonMediaNodeData">
     * ```
     */
    cMediaNode?: ICTTLCommonMediaNodeDataDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="isNarration" type="xsd:boolean" use="optional">
     * ```
     */
    isNarration?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLMediaNodeVideo name="CT_TLMediaNodeVideo">
 * ```
 */
export interface ICTTLMediaNodeVideo {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonMediaNodeData maxOccurs="1" minOccurs="1" name="cMediaNode" type="CT_TLCommonMediaNodeData">
     * ```
     */
    cMediaNode?: ICTTLCommonMediaNodeData;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="fullScrn" type="xsd:boolean" use="optional">
     * ```
     */
    fullScrn?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLMediaNodeVideo name="CT_TLMediaNodeVideo">
 * ```
 */
export interface ICTTLMediaNodeVideoDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonMediaNodeData maxOccurs="1" minOccurs="1" name="cMediaNode" type="CT_TLCommonMediaNodeData">
     * ```
     */
    cMediaNode?: ICTTLCommonMediaNodeDataDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="fullScrn" type="xsd:boolean" use="optional">
     * ```
     */
    fullScrn?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ColorScheme name="CT_ColorScheme">
 * ```
 */
export interface ICTColorScheme {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="dk1" type="CT_ColorDmlA">
     * ```
     */
    dk1?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="lt1" type="CT_ColorDmlA">
     * ```
     */
    lt1?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="dk2" type="CT_ColorDmlA">
     * ```
     */
    dk2?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="lt2" type="CT_ColorDmlA">
     * ```
     */
    lt2?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="accent1" type="CT_ColorDmlA">
     * ```
     */
    accent1?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="accent2" type="CT_ColorDmlA">
     * ```
     */
    accent2?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="accent3" type="CT_ColorDmlA">
     * ```
     */
    accent3?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="accent4" type="CT_ColorDmlA">
     * ```
     */
    accent4?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="accent5" type="CT_ColorDmlA">
     * ```
     */
    accent5?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="accent6" type="CT_ColorDmlA">
     * ```
     */
    accent6?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="hlink" type="CT_ColorDmlA">
     * ```
     */
    hlink?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="folHlink" type="CT_ColorDmlA">
     * ```
     */
    folHlink?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string" use="required">
     * ```
     */
    name?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ColorScheme name="CT_ColorScheme">
 * ```
 */
export interface ICTColorSchemeDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="dk1" type="CT_ColorDmlA">
     * ```
     */
    dk1?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="lt1" type="CT_ColorDmlA">
     * ```
     */
    lt1?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="dk2" type="CT_ColorDmlA">
     * ```
     */
    dk2?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="lt2" type="CT_ColorDmlA">
     * ```
     */
    lt2?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="accent1" type="CT_ColorDmlA">
     * ```
     */
    accent1?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="accent2" type="CT_ColorDmlA">
     * ```
     */
    accent2?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="accent3" type="CT_ColorDmlA">
     * ```
     */
    accent3?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="accent4" type="CT_ColorDmlA">
     * ```
     */
    accent4?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="accent5" type="CT_ColorDmlA">
     * ```
     */
    accent5?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="accent6" type="CT_ColorDmlA">
     * ```
     */
    accent6?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="hlink" type="CT_ColorDmlA">
     * ```
     */
    hlink?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="folHlink" type="CT_ColorDmlA">
     * ```
     */
    folHlink?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string" use="required">
     * ```
     */
    name?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_FontSchemeDmlA name="CT_FontSchemeDmlA">
 * ```
 */
export interface ICTFontSchemeDmlA {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FontCollection maxOccurs="1" minOccurs="1" name="majorFont" type="CT_FontCollection">
     * ```
     */
    majorFont?: ICTFontCollection;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FontCollection maxOccurs="1" minOccurs="1" name="minorFont" type="CT_FontCollection">
     * ```
     */
    minorFont?: ICTFontCollection;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string" use="required">
     * ```
     */
    name?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_FontSchemeDmlA name="CT_FontSchemeDmlA">
 * ```
 */
export interface ICTFontSchemeDmlADelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FontCollection maxOccurs="1" minOccurs="1" name="majorFont" type="CT_FontCollection">
     * ```
     */
    majorFont?: ICTFontCollectionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FontCollection maxOccurs="1" minOccurs="1" name="minorFont" type="CT_FontCollection">
     * ```
     */
    minorFont?: ICTFontCollectionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string" use="required">
     * ```
     */
    name?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_StyleMatrix name="CT_StyleMatrix">
 * ```
 */
export interface ICTStyleMatrix {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FillStyleList maxOccurs="1" minOccurs="1" name="fillStyleLst" type="CT_FillStyleList">
     * ```
     */
    fillStyleLst?: ICTFillStyleList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineStyleList maxOccurs="1" minOccurs="1" name="lnStyleLst" type="CT_LineStyleList">
     * ```
     */
    lnStyleLst?: ICTLineStyleList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectStyleList maxOccurs="1" minOccurs="1" name="effectStyleLst" type="CT_EffectStyleList">
     * ```
     */
    effectStyleLst?: ICTEffectStyleList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BackgroundFillStyleList maxOccurs="1" minOccurs="1" name="bgFillStyleLst" type="CT_BackgroundFillStyleList">
     * ```
     */
    bgFillStyleLst?: ICTBackgroundFillStyleList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_StyleMatrix name="CT_StyleMatrix">
 * ```
 */
export interface ICTStyleMatrixDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FillStyleList maxOccurs="1" minOccurs="1" name="fillStyleLst" type="CT_FillStyleList">
     * ```
     */
    fillStyleLst?: ICTFillStyleListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineStyleList maxOccurs="1" minOccurs="1" name="lnStyleLst" type="CT_LineStyleList">
     * ```
     */
    lnStyleLst?: ICTLineStyleListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectStyleList maxOccurs="1" minOccurs="1" name="effectStyleLst" type="CT_EffectStyleList">
     * ```
     */
    effectStyleLst?: ICTEffectStyleListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BackgroundFillStyleList maxOccurs="1" minOccurs="1" name="bgFillStyleLst" type="CT_BackgroundFillStyleList">
     * ```
     */
    bgFillStyleLst?: ICTBackgroundFillStyleListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextSpacing name="CT_TextSpacing">
 * ```
 */
export interface ICTTextSpacing {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextSpacingPercent name="spcPct" type="CT_TextSpacingPercent">
     * ```
     */
    spcPct?: ICTTextSpacingPercent;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextSpacingPoint name="spcPts" type="CT_TextSpacingPoint">
     * ```
     */
    spcPts?: ICTTextSpacingPoint;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextSpacing name="CT_TextSpacing">
 * ```
 */
export interface ICTTextSpacingDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextSpacingPercent name="spcPct" type="CT_TextSpacingPercent">
     * ```
     */
    spcPct?: ICTTextSpacingPercentDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextSpacingPoint name="spcPts" type="CT_TextSpacingPoint">
     * ```
     */
    spcPts?: ICTTextSpacingPointDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ColorDmlA name="CT_ColorDmlA">
 * ```
 */
export interface ICTColorDmlA {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ScRgbColor maxOccurs="1" minOccurs="1" name="scrgbClr" type="CT_ScRgbColor">
     * ```
     */
    scrgbClr?: ICTScRgbColor;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SRgbColor maxOccurs="1" minOccurs="1" name="srgbClr" type="CT_SRgbColor">
     * ```
     */
    srgbClr?: ICTSRgbColor;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HslColor maxOccurs="1" minOccurs="1" name="hslClr" type="CT_HslColor">
     * ```
     */
    hslClr?: ICTHslColor;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SystemColor maxOccurs="1" minOccurs="1" name="sysClr" type="CT_SystemColor">
     * ```
     */
    sysClr?: ICTSystemColor;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SchemeColor maxOccurs="1" minOccurs="1" name="schemeClr" type="CT_SchemeColor">
     * ```
     */
    schemeClr?: ICTSchemeColor;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PresetColor maxOccurs="1" minOccurs="1" name="prstClr" type="CT_PresetColor">
     * ```
     */
    prstClr?: ICTPresetColor;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ColorDmlA name="CT_ColorDmlA">
 * ```
 */
export interface ICTColorDmlADelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ScRgbColor maxOccurs="1" minOccurs="1" name="scrgbClr" type="CT_ScRgbColor">
     * ```
     */
    scrgbClr?: ICTScRgbColorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SRgbColor maxOccurs="1" minOccurs="1" name="srgbClr" type="CT_SRgbColor">
     * ```
     */
    srgbClr?: ICTSRgbColorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HslColor maxOccurs="1" minOccurs="1" name="hslClr" type="CT_HslColor">
     * ```
     */
    hslClr?: ICTHslColorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SystemColor maxOccurs="1" minOccurs="1" name="sysClr" type="CT_SystemColor">
     * ```
     */
    sysClr?: ICTSystemColorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SchemeColor maxOccurs="1" minOccurs="1" name="schemeClr" type="CT_SchemeColor">
     * ```
     */
    schemeClr?: ICTSchemeColorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PresetColor maxOccurs="1" minOccurs="1" name="prstClr" type="CT_PresetColor">
     * ```
     */
    prstClr?: ICTPresetColorDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextBulletSizePercent name="CT_TextBulletSizePercent">
 * ```
 */
export interface ICTTextBulletSizePercent {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="ST_TextBulletSizePercent" use="required">
     * ```
     */
    val?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextBulletSizePercent name="CT_TextBulletSizePercent">
 * ```
 */
export interface ICTTextBulletSizePercentDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="ST_TextBulletSizePercent" use="required">
     * ```
     */
    val?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextBulletSizePoint name="CT_TextBulletSizePoint">
 * ```
 */
export interface ICTTextBulletSizePoint {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="val" type="ST_TextFontSize" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextBulletSizePoint name="CT_TextBulletSizePoint">
 * ```
 */
export interface ICTTextBulletSizePointDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="val" type="ST_TextFontSize" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextFont name="CT_TextFont">
 * ```
 */
export interface ICTTextFont {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="typeface" type="ST_TextTypeface" use="required">
     * ```
     */
    typeface?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="panose" type="s:ST_Panose" use="optional">
     * ```
     */
    panose?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="pitchFamily" type="ST_PitchFamily" use="optional">
     * ```
     */
    pitchFamily?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="1" name="charset" type="xsd:byte" use="optional">
     * ```
     */
    charset?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextFont name="CT_TextFont">
 * ```
 */
export interface ICTTextFontDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="typeface" type="ST_TextTypeface" use="required">
     * ```
     */
    typeface?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="panose" type="s:ST_Panose" use="optional">
     * ```
     */
    panose?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="pitchFamily" type="ST_PitchFamily" use="optional">
     * ```
     */
    pitchFamily?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="1" name="charset" type="xsd:byte" use="optional">
     * ```
     */
    charset?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextAutonumberBullet name="CT_TextAutonumberBullet">
 * ```
 */
export interface ICTTextAutonumberBullet {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextAutonumberScheme name="type" type="ST_TextAutonumberScheme" use="required">
     * ```
     */
    type?: STTextAutonumberScheme;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="1" name="startAt" type="ST_TextBulletStartAtNum" use="optional">
     * ```
     */
    startAt?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextAutonumberBullet name="CT_TextAutonumberBullet">
 * ```
 */
export interface ICTTextAutonumberBulletDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextAutonumberScheme name="type" type="ST_TextAutonumberScheme" use="required">
     * ```
     */
    type?: STTextAutonumberScheme | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="1" name="startAt" type="ST_TextBulletStartAtNum" use="optional">
     * ```
     */
    startAt?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextCharBullet name="CT_TextCharBullet">
 * ```
 */
export interface ICTTextCharBullet {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string aliasName=charKeyword aliasName="charKeyword" name="char" type="xsd:string" use="required">
     * ```
     */
    charKeyword?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextCharBullet name="CT_TextCharBullet">
 * ```
 */
export interface ICTTextCharBulletDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string aliasName=charKeyword aliasName="charKeyword" name="char" type="xsd:string" use="required">
     * ```
     */
    charKeyword?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextBlipBullet name="CT_TextBlipBullet">
 * ```
 */
export interface ICTTextBlipBullet {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Blip maxOccurs="1" minOccurs="1" name="blip" type="CT_Blip">
     * ```
     */
    blip?: ICTBlip;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextBlipBullet name="CT_TextBlipBullet">
 * ```
 */
export interface ICTTextBlipBulletDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Blip maxOccurs="1" minOccurs="1" name="blip" type="CT_Blip">
     * ```
     */
    blip?: ICTBlipDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextTabStopList name="CT_TextTabStopList">
 * ```
 */
export interface ICTTextTabStopList {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextTabStop maxOccurs="32" minOccurs="0" name="tab" type="CT_TextTabStop">
     * ```
     */
    tab?: ICTTextTabStop[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextTabStopList name="CT_TextTabStopList">
 * ```
 */
export interface ICTTextTabStopListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextTabStop maxOccurs="32" minOccurs="0" name="tab" type="CT_TextTabStop">
     * ```
     */
    tab?: ICTTextTabStopDelta[];
    tabOp?: IArrayOp;
}

export enum STTextAlignType {
    PBDEFAULT = 0,
    l = 1,
    ctr = 2,
    r = 3,
    just = 4,
    justLow = 5,
    dist = 6,
    thaiDist = 7,
}

export enum STTextFontAlignType {
    PBDEFAULT = 0,
    auto = 1,
    t = 2,
    ctr = 3,
    base = 4,
    b = 5,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_EffectList name="CT_EffectList">
 * ```
 */
export interface ICTEffectList {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlurEffect maxOccurs="1" minOccurs="0" name="blur" type="CT_BlurEffect">
     * ```
     */
    blur?: ICTBlurEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FillOverlayEffect maxOccurs="1" minOccurs="0" name="fillOverlay" type="CT_FillOverlayEffect">
     * ```
     */
    fillOverlay?: ICTFillOverlayEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GlowEffect maxOccurs="1" minOccurs="0" name="glow" type="CT_GlowEffect">
     * ```
     */
    glow?: ICTGlowEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_InnerShadowEffect maxOccurs="1" minOccurs="0" name="innerShdw" type="CT_InnerShadowEffect">
     * ```
     */
    innerShdw?: ICTInnerShadowEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OuterShadowEffect maxOccurs="1" minOccurs="0" name="outerShdw" type="CT_OuterShadowEffect">
     * ```
     */
    outerShdw?: ICTOuterShadowEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PresetShadowEffect maxOccurs="1" minOccurs="0" name="prstShdw" type="CT_PresetShadowEffect">
     * ```
     */
    prstShdw?: ICTPresetShadowEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ReflectionEffect maxOccurs="1" minOccurs="0" name="reflection" type="CT_ReflectionEffect">
     * ```
     */
    reflection?: ICTReflectionEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SoftEdgesEffect maxOccurs="1" minOccurs="0" name="softEdge" type="CT_SoftEdgesEffect">
     * ```
     */
    softEdge?: ICTSoftEdgesEffect;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_EffectList name="CT_EffectList">
 * ```
 */
export interface ICTEffectListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlurEffect maxOccurs="1" minOccurs="0" name="blur" type="CT_BlurEffect">
     * ```
     */
    blur?: ICTBlurEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FillOverlayEffect maxOccurs="1" minOccurs="0" name="fillOverlay" type="CT_FillOverlayEffect">
     * ```
     */
    fillOverlay?: ICTFillOverlayEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GlowEffect maxOccurs="1" minOccurs="0" name="glow" type="CT_GlowEffect">
     * ```
     */
    glow?: ICTGlowEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_InnerShadowEffect maxOccurs="1" minOccurs="0" name="innerShdw" type="CT_InnerShadowEffect">
     * ```
     */
    innerShdw?: ICTInnerShadowEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OuterShadowEffect maxOccurs="1" minOccurs="0" name="outerShdw" type="CT_OuterShadowEffect">
     * ```
     */
    outerShdw?: ICTOuterShadowEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PresetShadowEffect maxOccurs="1" minOccurs="0" name="prstShdw" type="CT_PresetShadowEffect">
     * ```
     */
    prstShdw?: ICTPresetShadowEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ReflectionEffect maxOccurs="1" minOccurs="0" name="reflection" type="CT_ReflectionEffect">
     * ```
     */
    reflection?: ICTReflectionEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SoftEdgesEffect maxOccurs="1" minOccurs="0" name="softEdge" type="CT_SoftEdgesEffect">
     * ```
     */
    softEdge?: ICTSoftEdgesEffectDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_EffectContainer name="CT_EffectContainer">
 * ```
 */
export interface ICTEffectContainer {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_Effect ref=dmlmain:EG_Effect maxOccurs="unbounded" minOccurs="0" name="EG_Effect" ref="EG_Effect">
     * ref dmlmain:EG_Effect
     * ```
     */
    egEffect?: IEGEffect[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_EffectContainerType default="sib" name="type" type="ST_EffectContainerType" use="optional">
     * ```
     */
    type?: STEffectContainerType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:token" use="optional">
     * ```
     */
    name?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_EffectContainer name="CT_EffectContainer">
 * ```
 */
export interface ICTEffectContainerDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_Effect ref=dmlmain:EG_Effect maxOccurs="unbounded" minOccurs="0" name="EG_Effect" ref="EG_Effect">
     * ref dmlmain:EG_Effect
     * ```
     */
    egEffect?: IEGEffectDelta[];
    egEffectOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_EffectContainerType default="sib" name="type" type="ST_EffectContainerType" use="optional">
     * ```
     */
    type?: STEffectContainerType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:token" use="optional">
     * ```
     */
    name?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextUnderlineFillGroupWrapper name="CT_TextUnderlineFillGroupWrapper">
 * ```
 */
export interface ICTTextUnderlineFillGroupWrapper {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextUnderlineFillGroupWrapper name="CT_TextUnderlineFillGroupWrapper">
 * ```
 */
export interface ICTTextUnderlineFillGroupWrapperDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_HyperlinkDmlA name="CT_HyperlinkDmlA">
 * ```
 */
export interface ICTHyperlinkDmlA {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EmbeddedWAVAudioFile maxOccurs="1" minOccurs="0" name="snd" type="CT_EmbeddedWAVAudioFile">
     * ```
     */
    snd?: ICTEmbeddedWAVAudioFile;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="optional">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="invalidUrl" type="xsd:string" use="optional">
     * ```
     */
    invalidUrl?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="action" type="xsd:string" use="optional">
     * ```
     */
    action?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="tgtFrame" type="xsd:string" use="optional">
     * ```
     */
    tgtFrame?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="tooltip" type="xsd:string" use="optional">
     * ```
     */
    tooltip?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="history" type="xsd:boolean" use="optional">
     * ```
     */
    history?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="highlightClick" type="xsd:boolean" use="optional">
     * ```
     */
    highlightClick?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="endSnd" type="xsd:boolean" use="optional">
     * ```
     */
    endSnd?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="mimeType" skipIO="true" type="xsd:string" use="optional">
     * ```
     */
    mimeType?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="iconMode" skipIO="true" type="xsd:boolean" use="optional">
     * ```
     */
    iconMode?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_HyperlinkDmlA name="CT_HyperlinkDmlA">
 * ```
 */
export interface ICTHyperlinkDmlADelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EmbeddedWAVAudioFile maxOccurs="1" minOccurs="0" name="snd" type="CT_EmbeddedWAVAudioFile">
     * ```
     */
    snd?: ICTEmbeddedWAVAudioFileDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="optional">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="invalidUrl" type="xsd:string" use="optional">
     * ```
     */
    invalidUrl?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="action" type="xsd:string" use="optional">
     * ```
     */
    action?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="tgtFrame" type="xsd:string" use="optional">
     * ```
     */
    tgtFrame?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="tooltip" type="xsd:string" use="optional">
     * ```
     */
    tooltip?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="history" type="xsd:boolean" use="optional">
     * ```
     */
    history?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="highlightClick" type="xsd:boolean" use="optional">
     * ```
     */
    highlightClick?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="endSnd" type="xsd:boolean" use="optional">
     * ```
     */
    endSnd?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="mimeType" skipIO="true" type="xsd:string" use="optional">
     * ```
     */
    mimeType?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="iconMode" skipIO="true" type="xsd:boolean" use="optional">
     * ```
     */
    iconMode?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_BooleanDmlA name="CT_BooleanDmlA">
 * ```
 */
export interface ICTBooleanDmlA {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="0" name="val" type="s:ST_OnOff">
     * ```
     */
    val?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_BooleanDmlA name="CT_BooleanDmlA">
 * ```
 */
export interface ICTBooleanDmlADelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="0" name="val" type="s:ST_OnOff">
     * ```
     */
    val?: boolean | null;
}

export enum STTextUnderlineType {
    PBDEFAULT = 0,
    none = 1,
    words = 2,
    sng = 3,
    dbl = 4,
    heavy = 5,
    dotted = 6,
    dottedHeavy = 7,
    dash = 8,
    dashHeavy = 9,
    dashLong = 10,
    dashLongHeavy = 11,
    dotDash = 12,
    dotDashHeavy = 13,
    dotDotDash = 14,
    dotDotDashHeavy = 15,
    wavy = 16,
    wavyHeavy = 17,
    wavyDbl = 18,
}

export enum STTextStrikeType {
    PBDEFAULT = 0,
    noStrike = 1,
    sngStrike = 2,
    dblStrike = 3,
}

export enum STTextCapsType {
    PBDEFAULT = 0,
    none = 1,
    small = 2,
    all = 3,
}

export enum STFldType {
    PBDEFAULT = 0,
    begin = 1,
    separate = 2,
    end = 3,
}

export interface IChartProperties {
    /** 绘图区，不包含子表属性，子表单独Add */
    plotArea?: IChartPlotAreaProperties | null;
    /** 标题 */
    title?: ICTTitleDelta | null;
    /** 图例 */
    legend?: ICTLegendDelta | null;
    /** 图表标题 */
    autoTitleDeleted?: ICTBooleanDmlCDelta | null;
    pivotFmts?: ICTPivotFmtsDelta | null;
    view_3d?: ICTView3DDelta | null;
    floor?: ICTSurfaceDelta | null;
    sideWall?: ICTSurfaceDelta | null;
    backWall?: ICTSurfaceDelta | null;
    plotVisOnly?: ICTBooleanDmlCDelta | null;
    dispBlanksAs?: ICTDispBlanksAsDelta | null;
    showDlblsOverMax?: ICTBooleanDmlCDelta | null;
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextBody name="CT_TextBody">
 * ```
 */
export interface ICTTextBody {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBodyProperties maxOccurs="1" minOccurs="1" name="bodyPr" type="CT_TextBodyProperties">
     * ```
     */
    bodyPr?: ICTTextBodyProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextListStyle maxOccurs="1" minOccurs="0" name="lstStyle" type="CT_TextListStyle">
     * ```
     */
    lstStyle?: ICTTextListStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraph maxOccurs="unbounded" minOccurs="1" name="p" type="CT_TextParagraph">
     * ```
     */
    p?: ICTTextParagraph[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextBody name="CT_TextBody">
 * ```
 */
export interface ICTTextBodyDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBodyProperties maxOccurs="1" minOccurs="1" name="bodyPr" type="CT_TextBodyProperties">
     * ```
     */
    bodyPr?: ICTTextBodyPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextListStyle maxOccurs="1" minOccurs="0" name="lstStyle" type="CT_TextListStyle">
     * ```
     */
    lstStyle?: ICTTextListStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraph maxOccurs="unbounded" minOccurs="1" name="p" type="CT_TextParagraph">
     * ```
     */
    p?: ICTTextParagraphDelta[];
    pOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BooleanDmlC name="CT_BooleanDmlC">
 * ```
 */
export interface ICTBooleanDmlC {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" defaultValue="true" name="val" type="xsd:boolean" use="optional">
     * ```
     */
    val?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BooleanDmlC name="CT_BooleanDmlC">
 * ```
 */
export interface ICTBooleanDmlCDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" defaultValue="true" name="val" type="xsd:boolean" use="optional">
     * ```
     */
    val?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_TextLanguageID name="CT_TextLanguageID">
 * ```
 */
export interface ICTTextLanguageID {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="s:ST_Lang" use="required">
     * ```
     */
    val?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_TextLanguageID name="CT_TextLanguageID">
 * ```
 */
export interface ICTTextLanguageIDDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="s:ST_Lang" use="required">
     * ```
     */
    val?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=msc2007:CT_StyleMsC2007 name="CT_StyleMsC2007">
 * ```
 */
export interface ICTStyleMsC2007 {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="val" type="ST_StyleMsC2007" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msc2007:CT_StyleMsC2007 name="CT_StyleMsC2007">
 * ```
 */
export interface ICTStyleMsC2007Delta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="val" type="ST_StyleMsC2007" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PivotSource name="CT_PivotSource">
 * ```
 */
export interface ICTPivotSource {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="name" type="s:ST_Xstring">
     * ```
     */
    name?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="fmtId" type="CT_UnsignedInt">
     * ```
     */
    fmtId?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="unbounded" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PivotSource name="CT_PivotSource">
 * ```
 */
export interface ICTPivotSourceDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="name" type="s:ST_Xstring">
     * ```
     */
    name?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="fmtId" type="CT_UnsignedInt">
     * ```
     */
    fmtId?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="unbounded" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta[];
    extLstOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Protection name="CT_Protection">
 * ```
 */
export interface ICTProtection {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="chartObject" type="CT_BooleanDmlC">
     * ```
     */
    chartObject?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="data" type="CT_BooleanDmlC">
     * ```
     */
    data?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="formatting" type="CT_BooleanDmlC">
     * ```
     */
    formatting?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="selection" type="CT_BooleanDmlC">
     * ```
     */
    selection?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="userInterface" type="CT_BooleanDmlC">
     * ```
     */
    userInterface?: ICTBooleanDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Protection name="CT_Protection">
 * ```
 */
export interface ICTProtectionDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="chartObject" type="CT_BooleanDmlC">
     * ```
     */
    chartObject?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="data" type="CT_BooleanDmlC">
     * ```
     */
    data?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="formatting" type="CT_BooleanDmlC">
     * ```
     */
    formatting?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="selection" type="CT_BooleanDmlC">
     * ```
     */
    selection?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="userInterface" type="CT_BooleanDmlC">
     * ```
     */
    userInterface?: ICTBooleanDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ExternalData name="CT_ExternalData">
 * ```
 */
export interface ICTExternalData {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="autoUpdate" type="CT_BooleanDmlC">
     * ```
     */
    autoUpdate?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ExternalData name="CT_ExternalData">
 * ```
 */
export interface ICTExternalDataDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="autoUpdate" type="CT_BooleanDmlC">
     * ```
     */
    autoUpdate?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PrintSettings name="CT_PrintSettings">
 * ```
 */
export interface ICTPrintSettings {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_HeaderFooterDmlC maxOccurs="1" minOccurs="0" name="headerFooter" type="CT_HeaderFooterDmlC">
     * ```
     */
    headerFooter?: ICTHeaderFooterDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PageMarginsDmlC maxOccurs="1" minOccurs="0" name="pageMargins" type="CT_PageMarginsDmlC">
     * ```
     */
    pageMargins?: ICTPageMarginsDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PageSetupDmlC maxOccurs="1" minOccurs="0" name="pageSetup" type="CT_PageSetupDmlC">
     * ```
     */
    pageSetup?: ICTPageSetupDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_RelId maxOccurs="1" minOccurs="0" name="legacyDrawingHF" type="CT_RelId">
     * ```
     */
    legacyDrawingHf?: ICTRelId;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PrintSettings name="CT_PrintSettings">
 * ```
 */
export interface ICTPrintSettingsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_HeaderFooterDmlC maxOccurs="1" minOccurs="0" name="headerFooter" type="CT_HeaderFooterDmlC">
     * ```
     */
    headerFooter?: ICTHeaderFooterDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PageMarginsDmlC maxOccurs="1" minOccurs="0" name="pageMargins" type="CT_PageMarginsDmlC">
     * ```
     */
    pageMargins?: ICTPageMarginsDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PageSetupDmlC maxOccurs="1" minOccurs="0" name="pageSetup" type="CT_PageSetupDmlC">
     * ```
     */
    pageSetup?: ICTPageSetupDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_RelId maxOccurs="1" minOccurs="0" name="legacyDrawingHF" type="CT_RelId">
     * ```
     */
    legacyDrawingHf?: ICTRelIdDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ExtensionListDmlC name="CT_ExtensionListDmlC">
 * ```
 */
export interface ICTExtensionListDmlC {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionDmlC maxOccurs="unbounded" minOccurs="0" name="ext" type="CT_ExtensionDmlC">
     * ```
     */
    ext?: ICTExtensionDmlC[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ExtensionListDmlC name="CT_ExtensionListDmlC">
 * ```
 */
export interface ICTExtensionListDmlCDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionDmlC maxOccurs="unbounded" minOccurs="0" name="ext" type="CT_ExtensionDmlC">
     * ```
     */
    ext?: ICTExtensionDmlCDelta[];
    extOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_StyleDmlC name="CT_StyleDmlC">
 * ```
 */
export interface ICTStyleDmlC {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="val" type="ST_StyleDmlC" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_StyleDmlC name="CT_StyleDmlC">
 * ```
 */
export interface ICTStyleDmlCDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="val" type="ST_StyleDmlC" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=mscs2012:CT_ColorStyle name="CT_ColorStyle">
 * ```
 */
export interface ICTColorStyle {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="unbounded" minOccurs="1" name="EG_ColorChoice" ref="a:EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoice[];
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_ColorStyleVariation maxOccurs="unbounded" minOccurs="0" name="variation" type="CT_ColorStyleVariation">
     * ```
     */
    variation?: ICTColorStyleVariation[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="meth" type="ST_ColorStyleMethod" use="required">
     * ```
     */
    meth?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="xsd:unsignedInt" use="optional">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:chartId name="chartId" ref="td:chartId" type="s:ST_String">
     * ```
     */
    chartId?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mscs2012:CT_ColorStyle name="CT_ColorStyle">
 * ```
 */
export interface ICTColorStyleDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="unbounded" minOccurs="1" name="EG_ColorChoice" ref="a:EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoiceDelta[];
    egColorChoiceOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_ColorStyleVariation maxOccurs="unbounded" minOccurs="0" name="variation" type="CT_ColorStyleVariation">
     * ```
     */
    variation?: ICTColorStyleVariationDelta[];
    variationOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="meth" type="ST_ColorStyleMethod" use="required">
     * ```
     */
    meth?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="xsd:unsignedInt" use="optional">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:chartId name="chartId" ref="td:chartId" type="s:ST_String">
     * ```
     */
    chartId?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=mscs2012:CT_ChartStyle name="CT_ChartStyle">
 * ```
 */
export interface ICTChartStyle {
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="axisTitle" type="CT_StyleEntry">
     * ```
     */
    axisTitle?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="categoryAxis" type="CT_StyleEntry">
     * ```
     */
    categoryAxis?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="chartArea" type="CT_StyleEntry">
     * ```
     */
    chartArea?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="dataLabel" type="CT_StyleEntry">
     * ```
     */
    dataLabel?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="0" name="dataLabelCallout" type="CT_StyleEntry">
     * ```
     */
    dataLabelCallout?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="dataPoint" type="CT_StyleEntry">
     * ```
     */
    dataPoint?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="dataPoint3D" type="CT_StyleEntry">
     * ```
     */
    dataPoint3D?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="dataPointLine" type="CT_StyleEntry">
     * ```
     */
    dataPointLine?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="dataPointMarker" type="CT_StyleEntry">
     * ```
     */
    dataPointMarker?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_MarkerLayout maxOccurs="1" minOccurs="0" name="dataPointMarkerLayout" type="CT_MarkerLayout">
     * ```
     */
    dataPointMarkerLayout?: ICTMarkerLayout;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="dataPointWireframe" type="CT_StyleEntry">
     * ```
     */
    dataPointWireframe?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="dataTable" type="CT_StyleEntry">
     * ```
     */
    dataTable?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="downBar" type="CT_StyleEntry">
     * ```
     */
    downBar?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="dropLine" type="CT_StyleEntry">
     * ```
     */
    dropLine?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="errorBar" type="CT_StyleEntry">
     * ```
     */
    errorBar?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="floor" type="CT_StyleEntry">
     * ```
     */
    floor?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="gridlineMajor" type="CT_StyleEntry">
     * ```
     */
    gridlineMajor?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="gridlineMinor" type="CT_StyleEntry">
     * ```
     */
    gridlineMinor?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="hiLoLine" type="CT_StyleEntry">
     * ```
     */
    hiLoLine?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="leaderLine" type="CT_StyleEntry">
     * ```
     */
    leaderLine?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="legend" type="CT_StyleEntry">
     * ```
     */
    legend?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="plotArea" type="CT_StyleEntry">
     * ```
     */
    plotArea?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="plotArea3D" type="CT_StyleEntry">
     * ```
     */
    plotArea3D?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="seriesAxis" type="CT_StyleEntry">
     * ```
     */
    seriesAxis?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="seriesLine" type="CT_StyleEntry">
     * ```
     */
    seriesLine?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="title" type="CT_StyleEntry">
     * ```
     */
    title?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="trendline" type="CT_StyleEntry">
     * ```
     */
    trendline?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="trendlineLabel" type="CT_StyleEntry">
     * ```
     */
    trendlineLabel?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="upBar" type="CT_StyleEntry">
     * ```
     */
    upBar?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="valueAxis" type="CT_StyleEntry">
     * ```
     */
    valueAxis?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="wall" type="CT_StyleEntry">
     * ```
     */
    wall?: ICTStyleEntry;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="xsd:unsignedInt" use="optional">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:chartId name="chartId" ref="td:chartId" type="s:ST_String">
     * ```
     */
    chartId?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mscs2012:CT_ChartStyle name="CT_ChartStyle">
 * ```
 */
export interface ICTChartStyleDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="axisTitle" type="CT_StyleEntry">
     * ```
     */
    axisTitle?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="categoryAxis" type="CT_StyleEntry">
     * ```
     */
    categoryAxis?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="chartArea" type="CT_StyleEntry">
     * ```
     */
    chartArea?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="dataLabel" type="CT_StyleEntry">
     * ```
     */
    dataLabel?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="0" name="dataLabelCallout" type="CT_StyleEntry">
     * ```
     */
    dataLabelCallout?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="dataPoint" type="CT_StyleEntry">
     * ```
     */
    dataPoint?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="dataPoint3D" type="CT_StyleEntry">
     * ```
     */
    dataPoint3D?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="dataPointLine" type="CT_StyleEntry">
     * ```
     */
    dataPointLine?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="dataPointMarker" type="CT_StyleEntry">
     * ```
     */
    dataPointMarker?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_MarkerLayout maxOccurs="1" minOccurs="0" name="dataPointMarkerLayout" type="CT_MarkerLayout">
     * ```
     */
    dataPointMarkerLayout?: ICTMarkerLayoutDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="dataPointWireframe" type="CT_StyleEntry">
     * ```
     */
    dataPointWireframe?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="dataTable" type="CT_StyleEntry">
     * ```
     */
    dataTable?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="downBar" type="CT_StyleEntry">
     * ```
     */
    downBar?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="dropLine" type="CT_StyleEntry">
     * ```
     */
    dropLine?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="errorBar" type="CT_StyleEntry">
     * ```
     */
    errorBar?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="floor" type="CT_StyleEntry">
     * ```
     */
    floor?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="gridlineMajor" type="CT_StyleEntry">
     * ```
     */
    gridlineMajor?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="gridlineMinor" type="CT_StyleEntry">
     * ```
     */
    gridlineMinor?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="hiLoLine" type="CT_StyleEntry">
     * ```
     */
    hiLoLine?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="leaderLine" type="CT_StyleEntry">
     * ```
     */
    leaderLine?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="legend" type="CT_StyleEntry">
     * ```
     */
    legend?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="plotArea" type="CT_StyleEntry">
     * ```
     */
    plotArea?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="plotArea3D" type="CT_StyleEntry">
     * ```
     */
    plotArea3D?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="seriesAxis" type="CT_StyleEntry">
     * ```
     */
    seriesAxis?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="seriesLine" type="CT_StyleEntry">
     * ```
     */
    seriesLine?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="title" type="CT_StyleEntry">
     * ```
     */
    title?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="trendline" type="CT_StyleEntry">
     * ```
     */
    trendline?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="trendlineLabel" type="CT_StyleEntry">
     * ```
     */
    trendlineLabel?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="upBar" type="CT_StyleEntry">
     * ```
     */
    upBar?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="valueAxis" type="CT_StyleEntry">
     * ```
     */
    valueAxis?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleEntry maxOccurs="1" minOccurs="1" name="wall" type="CT_StyleEntry">
     * ```
     */
    wall?: ICTStyleEntryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="xsd:unsignedInt" use="optional">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:chartId name="chartId" ref="td:chartId" type="s:ST_String">
     * ```
     */
    chartId?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_AreaChart name="CT_AreaChart">
 * ```
 */
export interface ICTAreaChart {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Grouping maxOccurs="1" minOccurs="0" name="grouping" type="CT_Grouping">
     * ```
     */
    grouping?: ICTGrouping;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AreaSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_AreaSer">
     * ```
     */
    ser?: ICTAreaSer[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="dropLines" type="CT_ChartLines">
     * ```
     */
    dropLines?: ICTChartLines;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="2" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedInt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_AreaChart name="CT_AreaChart">
 * ```
 */
export interface ICTAreaChartDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Grouping maxOccurs="1" minOccurs="0" name="grouping" type="CT_Grouping">
     * ```
     */
    grouping?: ICTGroupingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AreaSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_AreaSer">
     * ```
     */
    ser?: ICTAreaSerDelta[];
    serOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="dropLines" type="CT_ChartLines">
     * ```
     */
    dropLines?: ICTChartLinesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="2" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedIntDelta[];
    axIdOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Area3DChart name="CT_Area3DChart">
 * ```
 */
export interface ICTArea3DChart {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Grouping maxOccurs="1" minOccurs="0" name="grouping" type="CT_Grouping">
     * ```
     */
    grouping?: ICTGrouping;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AreaSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_AreaSer">
     * ```
     */
    ser?: ICTAreaSer[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="dropLines" type="CT_ChartLines">
     * ```
     */
    dropLines?: ICTChartLines;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_GapAmount maxOccurs="1" minOccurs="0" name="gapDepth" type="CT_GapAmount">
     * ```
     */
    gapDepth?: ICTGapAmount;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="3" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedInt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Area3DChart name="CT_Area3DChart">
 * ```
 */
export interface ICTArea3DChartDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Grouping maxOccurs="1" minOccurs="0" name="grouping" type="CT_Grouping">
     * ```
     */
    grouping?: ICTGroupingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AreaSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_AreaSer">
     * ```
     */
    ser?: ICTAreaSerDelta[];
    serOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="dropLines" type="CT_ChartLines">
     * ```
     */
    dropLines?: ICTChartLinesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_GapAmount maxOccurs="1" minOccurs="0" name="gapDepth" type="CT_GapAmount">
     * ```
     */
    gapDepth?: ICTGapAmountDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="3" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedIntDelta[];
    axIdOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LineChart name="CT_LineChart">
 * ```
 */
export interface ICTLineChart {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Grouping maxOccurs="1" minOccurs="1" name="grouping" type="CT_Grouping">
     * ```
     */
    grouping?: ICTGrouping;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LineSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_LineSer">
     * ```
     */
    ser?: ICTLineSer[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="dropLines" type="CT_ChartLines">
     * ```
     */
    dropLines?: ICTChartLines;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="hiLowLines" type="CT_ChartLines">
     * ```
     */
    hiLowLines?: ICTChartLines;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UpDownBars maxOccurs="1" minOccurs="0" name="upDownBars" type="CT_UpDownBars">
     * ```
     */
    upDownBars?: ICTUpDownBars;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="marker" type="CT_BooleanDmlC">
     * ```
     */
    marker?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="smooth" type="CT_BooleanDmlC">
     * ```
     */
    smooth?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="2" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedInt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LineChart name="CT_LineChart">
 * ```
 */
export interface ICTLineChartDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Grouping maxOccurs="1" minOccurs="1" name="grouping" type="CT_Grouping">
     * ```
     */
    grouping?: ICTGroupingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LineSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_LineSer">
     * ```
     */
    ser?: ICTLineSerDelta[];
    serOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="dropLines" type="CT_ChartLines">
     * ```
     */
    dropLines?: ICTChartLinesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="hiLowLines" type="CT_ChartLines">
     * ```
     */
    hiLowLines?: ICTChartLinesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UpDownBars maxOccurs="1" minOccurs="0" name="upDownBars" type="CT_UpDownBars">
     * ```
     */
    upDownBars?: ICTUpDownBarsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="marker" type="CT_BooleanDmlC">
     * ```
     */
    marker?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="smooth" type="CT_BooleanDmlC">
     * ```
     */
    smooth?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="2" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedIntDelta[];
    axIdOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Line3DChart name="CT_Line3DChart">
 * ```
 */
export interface ICTLine3DChart {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Grouping maxOccurs="1" minOccurs="1" name="grouping" type="CT_Grouping">
     * ```
     */
    grouping?: ICTGrouping;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LineSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_LineSer">
     * ```
     */
    ser?: ICTLineSer[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="dropLines" type="CT_ChartLines">
     * ```
     */
    dropLines?: ICTChartLines;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_GapAmount maxOccurs="1" minOccurs="0" name="gapDepth" type="CT_GapAmount">
     * ```
     */
    gapDepth?: ICTGapAmount;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="3" minOccurs="3" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedInt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Line3DChart name="CT_Line3DChart">
 * ```
 */
export interface ICTLine3DChartDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Grouping maxOccurs="1" minOccurs="1" name="grouping" type="CT_Grouping">
     * ```
     */
    grouping?: ICTGroupingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LineSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_LineSer">
     * ```
     */
    ser?: ICTLineSerDelta[];
    serOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="dropLines" type="CT_ChartLines">
     * ```
     */
    dropLines?: ICTChartLinesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_GapAmount maxOccurs="1" minOccurs="0" name="gapDepth" type="CT_GapAmount">
     * ```
     */
    gapDepth?: ICTGapAmountDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="3" minOccurs="3" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedIntDelta[];
    axIdOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_StockChart name="CT_StockChart">
 * ```
 */
export interface ICTStockChart {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LineSer maxOccurs="4" minOccurs="3" name="ser" type="CT_LineSer">
     * ```
     */
    ser?: ICTLineSer[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="dropLines" type="CT_ChartLines">
     * ```
     */
    dropLines?: ICTChartLines;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="hiLowLines" type="CT_ChartLines">
     * ```
     */
    hiLowLines?: ICTChartLines;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UpDownBars maxOccurs="1" minOccurs="0" name="upDownBars" type="CT_UpDownBars">
     * ```
     */
    upDownBars?: ICTUpDownBars;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="2" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedInt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_StockChart name="CT_StockChart">
 * ```
 */
export interface ICTStockChartDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LineSer maxOccurs="4" minOccurs="3" name="ser" type="CT_LineSer">
     * ```
     */
    ser?: ICTLineSerDelta[];
    serOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="dropLines" type="CT_ChartLines">
     * ```
     */
    dropLines?: ICTChartLinesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="hiLowLines" type="CT_ChartLines">
     * ```
     */
    hiLowLines?: ICTChartLinesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UpDownBars maxOccurs="1" minOccurs="0" name="upDownBars" type="CT_UpDownBars">
     * ```
     */
    upDownBars?: ICTUpDownBarsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="2" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedIntDelta[];
    axIdOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_RadarChart name="CT_RadarChart">
 * ```
 */
export interface ICTRadarChart {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_RadarStyle maxOccurs="1" minOccurs="1" name="radarStyle" type="CT_RadarStyle">
     * ```
     */
    radarStyle?: ICTRadarStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_RadarSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_RadarSer">
     * ```
     */
    ser?: ICTRadarSer[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="2" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedInt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_RadarChart name="CT_RadarChart">
 * ```
 */
export interface ICTRadarChartDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_RadarStyle maxOccurs="1" minOccurs="1" name="radarStyle" type="CT_RadarStyle">
     * ```
     */
    radarStyle?: ICTRadarStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_RadarSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_RadarSer">
     * ```
     */
    ser?: ICTRadarSerDelta[];
    serOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="2" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedIntDelta[];
    axIdOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ScatterChart name="CT_ScatterChart">
 * ```
 */
export interface ICTScatterChart {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ScatterStyle maxOccurs="1" minOccurs="1" name="scatterStyle" type="CT_ScatterStyle">
     * ```
     */
    scatterStyle?: ICTScatterStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ScatterSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_ScatterSer">
     * ```
     */
    ser?: ICTScatterSer[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="2" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedInt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ScatterChart name="CT_ScatterChart">
 * ```
 */
export interface ICTScatterChartDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ScatterStyle maxOccurs="1" minOccurs="1" name="scatterStyle" type="CT_ScatterStyle">
     * ```
     */
    scatterStyle?: ICTScatterStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ScatterSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_ScatterSer">
     * ```
     */
    ser?: ICTScatterSerDelta[];
    serOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="2" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedIntDelta[];
    axIdOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PieChart name="CT_PieChart">
 * ```
 */
export interface ICTPieChart {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PieSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_PieSer">
     * ```
     */
    ser?: ICTPieSer[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_FirstSliceAng maxOccurs="1" minOccurs="0" name="firstSliceAng" type="CT_FirstSliceAng">
     * ```
     */
    firstSliceAng?: ICTFirstSliceAng;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PieChart name="CT_PieChart">
 * ```
 */
export interface ICTPieChartDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PieSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_PieSer">
     * ```
     */
    ser?: ICTPieSerDelta[];
    serOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_FirstSliceAng maxOccurs="1" minOccurs="0" name="firstSliceAng" type="CT_FirstSliceAng">
     * ```
     */
    firstSliceAng?: ICTFirstSliceAngDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Pie3DChart name="CT_Pie3DChart">
 * ```
 */
export interface ICTPie3DChart {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PieSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_PieSer">
     * ```
     */
    ser?: ICTPieSer[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Pie3DChart name="CT_Pie3DChart">
 * ```
 */
export interface ICTPie3DChartDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PieSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_PieSer">
     * ```
     */
    ser?: ICTPieSerDelta[];
    serOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DoughnutChart name="CT_DoughnutChart">
 * ```
 */
export interface ICTDoughnutChart {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PieSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_PieSer">
     * ```
     */
    ser?: ICTPieSer[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_FirstSliceAng maxOccurs="1" minOccurs="0" name="firstSliceAng" type="CT_FirstSliceAng">
     * ```
     */
    firstSliceAng?: ICTFirstSliceAng;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_HoleSize maxOccurs="1" minOccurs="0" name="holeSize" type="CT_HoleSize">
     * ```
     */
    holeSize?: ICTHoleSize;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DoughnutChart name="CT_DoughnutChart">
 * ```
 */
export interface ICTDoughnutChartDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PieSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_PieSer">
     * ```
     */
    ser?: ICTPieSerDelta[];
    serOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_FirstSliceAng maxOccurs="1" minOccurs="0" name="firstSliceAng" type="CT_FirstSliceAng">
     * ```
     */
    firstSliceAng?: ICTFirstSliceAngDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_HoleSize maxOccurs="1" minOccurs="0" name="holeSize" type="CT_HoleSize">
     * ```
     */
    holeSize?: ICTHoleSizeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BarChart name="CT_BarChart">
 * ```
 */
export interface ICTBarChart {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BarDir maxOccurs="1" minOccurs="1" name="barDir" type="CT_BarDir">
     * ```
     */
    barDir?: ICTBarDir;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BarGrouping maxOccurs="1" minOccurs="0" name="grouping" type="CT_BarGrouping">
     * ```
     */
    grouping?: ICTBarGrouping;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BarSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_BarSer">
     * ```
     */
    ser?: ICTBarSer[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_GapAmount maxOccurs="1" minOccurs="0" name="gapWidth" type="CT_GapAmount">
     * ```
     */
    gapWidth?: ICTGapAmount;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Overlap maxOccurs="1" minOccurs="0" name="overlap" type="CT_Overlap">
     * ```
     */
    overlap?: ICTOverlap;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="unbounded" minOccurs="0" name="serLines" type="CT_ChartLines">
     * ```
     */
    serLines?: ICTChartLines[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="2" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedInt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BarChart name="CT_BarChart">
 * ```
 */
export interface ICTBarChartDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BarDir maxOccurs="1" minOccurs="1" name="barDir" type="CT_BarDir">
     * ```
     */
    barDir?: ICTBarDirDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BarGrouping maxOccurs="1" minOccurs="0" name="grouping" type="CT_BarGrouping">
     * ```
     */
    grouping?: ICTBarGroupingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BarSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_BarSer">
     * ```
     */
    ser?: ICTBarSerDelta[];
    serOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_GapAmount maxOccurs="1" minOccurs="0" name="gapWidth" type="CT_GapAmount">
     * ```
     */
    gapWidth?: ICTGapAmountDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Overlap maxOccurs="1" minOccurs="0" name="overlap" type="CT_Overlap">
     * ```
     */
    overlap?: ICTOverlapDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="unbounded" minOccurs="0" name="serLines" type="CT_ChartLines">
     * ```
     */
    serLines?: ICTChartLinesDelta[];
    serLinesOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="2" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedIntDelta[];
    axIdOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Bar3DChart name="CT_Bar3DChart">
 * ```
 */
export interface ICTBar3DChart {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BarDir maxOccurs="1" minOccurs="1" name="barDir" type="CT_BarDir">
     * ```
     */
    barDir?: ICTBarDir;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BarGrouping maxOccurs="1" minOccurs="0" name="grouping" type="CT_BarGrouping">
     * ```
     */
    grouping?: ICTBarGrouping;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BarSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_BarSer">
     * ```
     */
    ser?: ICTBarSer[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_GapAmount maxOccurs="1" minOccurs="0" name="gapWidth" type="CT_GapAmount">
     * ```
     */
    gapWidth?: ICTGapAmount;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_GapAmount maxOccurs="1" minOccurs="0" name="gapDepth" type="CT_GapAmount">
     * ```
     */
    gapDepth?: ICTGapAmount;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ShapeDmlC maxOccurs="1" minOccurs="0" name="shape" type="CT_ShapeDmlC">
     * ```
     */
    shape?: ICTShapeDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="3" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedInt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Bar3DChart name="CT_Bar3DChart">
 * ```
 */
export interface ICTBar3DChartDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BarDir maxOccurs="1" minOccurs="1" name="barDir" type="CT_BarDir">
     * ```
     */
    barDir?: ICTBarDirDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BarGrouping maxOccurs="1" minOccurs="0" name="grouping" type="CT_BarGrouping">
     * ```
     */
    grouping?: ICTBarGroupingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BarSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_BarSer">
     * ```
     */
    ser?: ICTBarSerDelta[];
    serOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_GapAmount maxOccurs="1" minOccurs="0" name="gapWidth" type="CT_GapAmount">
     * ```
     */
    gapWidth?: ICTGapAmountDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_GapAmount maxOccurs="1" minOccurs="0" name="gapDepth" type="CT_GapAmount">
     * ```
     */
    gapDepth?: ICTGapAmountDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ShapeDmlC maxOccurs="1" minOccurs="0" name="shape" type="CT_ShapeDmlC">
     * ```
     */
    shape?: ICTShapeDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="3" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedIntDelta[];
    axIdOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_OfPieChart name="CT_OfPieChart">
 * ```
 */
export interface ICTOfPieChart {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_OfPieType maxOccurs="1" minOccurs="1" name="ofPieType" type="CT_OfPieType">
     * ```
     */
    ofPieType?: ICTOfPieType;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PieSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_PieSer">
     * ```
     */
    ser?: ICTPieSer[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_GapAmount maxOccurs="1" minOccurs="0" name="gapWidth" type="CT_GapAmount">
     * ```
     */
    gapWidth?: ICTGapAmount;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SplitType maxOccurs="1" minOccurs="0" name="splitType" type="CT_SplitType">
     * ```
     */
    splitType?: ICTSplitType;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="splitPos" type="CT_Double">
     * ```
     */
    splitPos?: ICTDouble;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_CustSplit maxOccurs="1" minOccurs="0" name="custSplit" type="CT_CustSplit">
     * ```
     */
    custSplit?: ICTCustSplit;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SecondPieSize maxOccurs="1" minOccurs="0" name="secondPieSize" type="CT_SecondPieSize">
     * ```
     */
    secondPieSize?: ICTSecondPieSize;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="unbounded" minOccurs="0" name="serLines" type="CT_ChartLines">
     * ```
     */
    serLines?: ICTChartLines[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_OfPieChart name="CT_OfPieChart">
 * ```
 */
export interface ICTOfPieChartDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_OfPieType maxOccurs="1" minOccurs="1" name="ofPieType" type="CT_OfPieType">
     * ```
     */
    ofPieType?: ICTOfPieTypeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PieSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_PieSer">
     * ```
     */
    ser?: ICTPieSerDelta[];
    serOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_GapAmount maxOccurs="1" minOccurs="0" name="gapWidth" type="CT_GapAmount">
     * ```
     */
    gapWidth?: ICTGapAmountDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SplitType maxOccurs="1" minOccurs="0" name="splitType" type="CT_SplitType">
     * ```
     */
    splitType?: ICTSplitTypeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="splitPos" type="CT_Double">
     * ```
     */
    splitPos?: ICTDoubleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_CustSplit maxOccurs="1" minOccurs="0" name="custSplit" type="CT_CustSplit">
     * ```
     */
    custSplit?: ICTCustSplitDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SecondPieSize maxOccurs="1" minOccurs="0" name="secondPieSize" type="CT_SecondPieSize">
     * ```
     */
    secondPieSize?: ICTSecondPieSizeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="unbounded" minOccurs="0" name="serLines" type="CT_ChartLines">
     * ```
     */
    serLines?: ICTChartLinesDelta[];
    serLinesOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_SurfaceChart name="CT_SurfaceChart">
 * ```
 */
export interface ICTSurfaceChart {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="wireframe" type="CT_BooleanDmlC">
     * ```
     */
    wireframe?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SurfaceSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_SurfaceSer">
     * ```
     */
    ser?: ICTSurfaceSer[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BandFmts maxOccurs="1" minOccurs="0" name="bandFmts" type="CT_BandFmts">
     * ```
     */
    bandFmts?: ICTBandFmts;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="3" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedInt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_SurfaceChart name="CT_SurfaceChart">
 * ```
 */
export interface ICTSurfaceChartDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="wireframe" type="CT_BooleanDmlC">
     * ```
     */
    wireframe?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SurfaceSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_SurfaceSer">
     * ```
     */
    ser?: ICTSurfaceSerDelta[];
    serOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BandFmts maxOccurs="1" minOccurs="0" name="bandFmts" type="CT_BandFmts">
     * ```
     */
    bandFmts?: ICTBandFmtsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="3" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedIntDelta[];
    axIdOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Surface3DChart name="CT_Surface3DChart">
 * ```
 */
export interface ICTSurface3DChart {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="wireframe" type="CT_BooleanDmlC">
     * ```
     */
    wireframe?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SurfaceSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_SurfaceSer">
     * ```
     */
    ser?: ICTSurfaceSer[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BandFmts maxOccurs="1" minOccurs="0" name="bandFmts" type="CT_BandFmts">
     * ```
     */
    bandFmts?: ICTBandFmts;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="3" minOccurs="3" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedInt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Surface3DChart name="CT_Surface3DChart">
 * ```
 */
export interface ICTSurface3DChartDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="wireframe" type="CT_BooleanDmlC">
     * ```
     */
    wireframe?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SurfaceSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_SurfaceSer">
     * ```
     */
    ser?: ICTSurfaceSerDelta[];
    serOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BandFmts maxOccurs="1" minOccurs="0" name="bandFmts" type="CT_BandFmts">
     * ```
     */
    bandFmts?: ICTBandFmtsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="3" minOccurs="3" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedIntDelta[];
    axIdOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BubbleChart name="CT_BubbleChart">
 * ```
 */
export interface ICTBubbleChart {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BubbleSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_BubbleSer">
     * ```
     */
    ser?: ICTBubbleSer[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="bubble3D" type="CT_BooleanDmlC">
     * ```
     */
    bubble3D?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BubbleScale maxOccurs="1" minOccurs="0" name="bubbleScale" type="CT_BubbleScale">
     * ```
     */
    bubbleScale?: ICTBubbleScale;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showNegBubbles" type="CT_BooleanDmlC">
     * ```
     */
    showNegBubbles?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SizeRepresents maxOccurs="1" minOccurs="0" name="sizeRepresents" type="CT_SizeRepresents">
     * ```
     */
    sizeRepresents?: ICTSizeRepresents;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="2" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedInt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BubbleChart name="CT_BubbleChart">
 * ```
 */
export interface ICTBubbleChartDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="varyColors" type="CT_BooleanDmlC">
     * ```
     */
    varyColors?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BubbleSer maxOccurs="unbounded" minOccurs="0" name="ser" type="CT_BubbleSer">
     * ```
     */
    ser?: ICTBubbleSerDelta[];
    serOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="bubble3D" type="CT_BooleanDmlC">
     * ```
     */
    bubble3D?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BubbleScale maxOccurs="1" minOccurs="0" name="bubbleScale" type="CT_BubbleScale">
     * ```
     */
    bubbleScale?: ICTBubbleScaleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showNegBubbles" type="CT_BooleanDmlC">
     * ```
     */
    showNegBubbles?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SizeRepresents maxOccurs="1" minOccurs="0" name="sizeRepresents" type="CT_SizeRepresents">
     * ```
     */
    sizeRepresents?: ICTSizeRepresentsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="2" minOccurs="2" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedIntDelta[];
    axIdOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

export enum STChartType {
    PBDEFAULT = 0,
    area = 1,
    area3D = 2,
    line = 3,
    line3D = 4,
    highLowCloseStock = 5,
    radar = 6,
    scatter = 7,
    pie = 8,
    pie3D = 9,
    doughnut = 10,
    clusteredBar = 11,
    clusteredBar3D = 12,
    clusteredColumn = 13,
    clusteredColumn3D = 14,
    pieOfPie = 15,
    surface = 16,
    surface3D = 17,
    bubble = 18,
    customCombo = 19,
    boxWhisker = 20,
    column3D = 21,
    funnel = 22,
    paretoLine = 23,
    regionMap = 24,
    sunburst = 25,
    treemap = 26,
    waterfall = 27,
    wordCloud = 28,
    stackedBar = 29,
    percentStackedBar = 30,
    stackedBar3D = 31,
    percentStackedBar3D = 32,
    stackedColumn = 33,
    percentStackedColumn = 34,
    stackedColumn3D = 35,
    percentStackedColumn3D = 36,
    stackedLine = 37,
    percentStackedLine = 38,
    markerLine = 39,
    stackedMarkerLine = 40,
    percentStackedMarkerLine = 41,
    stackedArea = 42,
    percentStackedArea = 43,
    stackedArea3D = 44,
    percentStackedArea3D = 45,
    barOfPie = 46,
    histogram = 47,
    smoothLineAndMarkerScatter = 48,
    smoothLineScatter = 49,
    straightLineAndMarkerScatter = 50,
    straightLineScatter = 51,
    bubble3D = 52,
    openHighLowCloseStock = 53,
    volumeHighLowCloseStock = 54,
    volumeOpenHighLowCloseStock = 55,
    wireframeSurface = 56,
    wireframeSurface3D = 57,
    markerRadar = 58,
    filledRadar = 59,
    clusteredColumnAndLineCombo = 60,
    clusteredColumnAndLineOnSecondaryAxisCombo = 61,
    stackedAreaAndClusteredColumnCombo = 62,
}

/**
 * ```xml
 * <xsd:group name=dmlchart:EG_Axis name="EG_Axis">
 * ```
 */
export interface IEGAxis {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ValAx maxOccurs="1" minOccurs="1" name="valAx" type="CT_ValAx">
     * ```
     */
    valAx?: ICTValAx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_CatAx maxOccurs="1" minOccurs="1" name="catAx" type="CT_CatAx">
     * ```
     */
    catAx?: ICTCatAx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DateAx maxOccurs="1" minOccurs="1" name="dateAx" type="CT_DateAx">
     * ```
     */
    dateAx?: ICTDateAx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SerAx maxOccurs="1" minOccurs="1" name="serAx" type="CT_SerAx">
     * ```
     */
    serAx?: ICTSerAx;
}

/**
 * ```xml
 * <xsd:group name=dmlchart:EG_Axis name="EG_Axis">
 * ```
 */
export interface IEGAxisDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ValAx maxOccurs="1" minOccurs="1" name="valAx" type="CT_ValAx">
     * ```
     */
    valAx?: ICTValAxDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_CatAx maxOccurs="1" minOccurs="1" name="catAx" type="CT_CatAx">
     * ```
     */
    catAx?: ICTCatAxDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DateAx maxOccurs="1" minOccurs="1" name="dateAx" type="CT_DateAx">
     * ```
     */
    dateAx?: ICTDateAxDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SerAx maxOccurs="1" minOccurs="1" name="serAx" type="CT_SerAx">
     * ```
     */
    serAx?: ICTSerAxDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PresetTextShape name="CT_PresetTextShape">
 * ```
 */
export interface ICTPresetTextShape {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GeomGuideList maxOccurs="1" minOccurs="0" name="avLst" type="CT_GeomGuideList">
     * ```
     */
    avLst?: ICTGeomGuideList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextShapeType name="prst" type="ST_TextShapeType" use="required">
     * ```
     */
    prst?: STTextShapeType;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PresetTextShape name="CT_PresetTextShape">
 * ```
 */
export interface ICTPresetTextShapeDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GeomGuideList maxOccurs="1" minOccurs="0" name="avLst" type="CT_GeomGuideList">
     * ```
     */
    avLst?: ICTGeomGuideListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextShapeType name="prst" type="ST_TextShapeType" use="required">
     * ```
     */
    prst?: STTextShapeType | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextNormalAutofit name="CT_TextNormalAutofit">
 * ```
 */
export interface ICTTextNormalAutofit {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_TextFontScalePercent" default="100000" name="fontScale" type="ST_TextFontScalePercentOrPercentString" use="optional">
     * ```
     */
    fontScale?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_TextSpacingPercent" default="0" name="lnSpcReduction" type="ST_TextSpacingPercentOrPercentString" use="optional">
     * ```
     */
    lnSpcReduction?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextNormalAutofit name="CT_TextNormalAutofit">
 * ```
 */
export interface ICTTextNormalAutofitDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_TextFontScalePercent" default="100000" name="fontScale" type="ST_TextFontScalePercentOrPercentString" use="optional">
     * ```
     */
    fontScale?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_TextSpacingPercent" default="0" name="lnSpcReduction" type="ST_TextSpacingPercentOrPercentString" use="optional">
     * ```
     */
    lnSpcReduction?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Scene3D name="CT_Scene3D">
 * ```
 */
export interface ICTScene3D {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Camera maxOccurs="1" minOccurs="1" name="camera" type="CT_Camera">
     * ```
     */
    camera?: ICTCamera;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LightRig maxOccurs="1" minOccurs="1" name="lightRig" type="CT_LightRig">
     * ```
     */
    lightRig?: ICTLightRig;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Backdrop maxOccurs="1" minOccurs="0" name="backdrop" type="CT_Backdrop">
     * ```
     */
    backdrop?: ICTBackdrop;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Scene3D name="CT_Scene3D">
 * ```
 */
export interface ICTScene3DDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Camera maxOccurs="1" minOccurs="1" name="camera" type="CT_Camera">
     * ```
     */
    camera?: ICTCameraDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LightRig maxOccurs="1" minOccurs="1" name="lightRig" type="CT_LightRig">
     * ```
     */
    lightRig?: ICTLightRigDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Backdrop maxOccurs="1" minOccurs="0" name="backdrop" type="CT_Backdrop">
     * ```
     */
    backdrop?: ICTBackdropDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Shape3D name="CT_Shape3D">
 * ```
 */
export interface ICTShape3D {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Bevel maxOccurs="1" minOccurs="0" name="bevelT" type="CT_Bevel">
     * ```
     */
    bevelT?: ICTBevel;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Bevel maxOccurs="1" minOccurs="0" name="bevelB" type="CT_Bevel">
     * ```
     */
    bevelB?: ICTBevel;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="0" name="extrusionClr" type="CT_ColorDmlA">
     * ```
     */
    extrusionClr?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="0" name="contourClr" type="CT_ColorDmlA">
     * ```
     */
    contourClr?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="0" name="z" type="ST_Coordinate" use="optional">
     * ```
     */
    z?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="extrusionH" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    extrusionH?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="contourW" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    contourW?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PresetMaterialType default="warmMatte" name="prstMaterial" type="ST_PresetMaterialType" use="optional">
     * ```
     */
    prstMaterial?: STPresetMaterialType;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Shape3D name="CT_Shape3D">
 * ```
 */
export interface ICTShape3DDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Bevel maxOccurs="1" minOccurs="0" name="bevelT" type="CT_Bevel">
     * ```
     */
    bevelT?: ICTBevelDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Bevel maxOccurs="1" minOccurs="0" name="bevelB" type="CT_Bevel">
     * ```
     */
    bevelB?: ICTBevelDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="0" name="extrusionClr" type="CT_ColorDmlA">
     * ```
     */
    extrusionClr?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="0" name="contourClr" type="CT_ColorDmlA">
     * ```
     */
    contourClr?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="0" name="z" type="ST_Coordinate" use="optional">
     * ```
     */
    z?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="extrusionH" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    extrusionH?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="contourW" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    contourW?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PresetMaterialType default="warmMatte" name="prstMaterial" type="ST_PresetMaterialType" use="optional">
     * ```
     */
    prstMaterial?: STPresetMaterialType | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_FlatText name="CT_FlatText">
 * ```
 */
export interface ICTFlatText {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="0" name="z" type="ST_Coordinate" use="optional">
     * ```
     */
    z?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_FlatText name="CT_FlatText">
 * ```
 */
export interface ICTFlatTextDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="0" name="z" type="ST_Coordinate" use="optional">
     * ```
     */
    z?: number | null;
}

export enum STTextVertOverflowType {
    PBDEFAULT = 0,
    overflow = 1,
    ellipsis = 2,
    clip = 3,
}

export enum STTextWrappingType {
    PBDEFAULT = 0,
    none = 1,
    square = 2,
}

export enum ExifOrientation {
    /** Unspecified orientation */
    OrientationOrientationUnspecified = 0,
    /** Normal orientation (top left) */
    OrientationTopLeft = 1,
    /** Flipped horizontally (top right) */
    OrientationTopRight = 2,
    /** Upside down (bottom right) */
    OrientationBottomRight = 3,
    /** Flipped vertically (bottom left) */
    OrientationBottomLeft = 4,
    /** Flipped horizontally and rotated 90 degrees clockwise (left top) */
    OrientationLeftTop = 5,
    /** Rotated 90 degrees clockwise (right top) */
    OrientationRightTop = 6,
    /** Flipped horizontally and rotated 90 degrees counter-clockwise (right bottom) */
    OrientationRightBottom = 7,
    /** Rotated 90 degrees counter-clockwise (left bottom) */
    OrientationLeftBottom = 8,
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_PtList name="CT_PtList">
 * ```
 */
export interface ICTPtList {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Pt maxOccurs="unbounded" minOccurs="0" name="pt" type="CT_Pt">
     * ```
     */
    pt?: ICTPt[];
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_PtList name="CT_PtList">
 * ```
 */
export interface ICTPtListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Pt maxOccurs="unbounded" minOccurs="0" name="pt" type="CT_Pt">
     * ```
     */
    pt?: ICTPtDelta[];
    ptOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_CxnList name="CT_CxnList">
 * ```
 */
export interface ICTCxnList {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Cxn maxOccurs="unbounded" minOccurs="0" name="cxn" type="CT_Cxn">
     * ```
     */
    cxn?: ICTCxn[];
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_CxnList name="CT_CxnList">
 * ```
 */
export interface ICTCxnListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Cxn maxOccurs="unbounded" minOccurs="0" name="cxn" type="CT_Cxn">
     * ```
     */
    cxn?: ICTCxnDelta[];
    cxnOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_BackgroundFormatting name="CT_BackgroundFormatting">
 * ```
 */
export interface ICTBackgroundFormatting {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectList maxOccurs="1" minOccurs="1" name="effectLst" type="CT_EffectList">
     * ```
     */
    effectLst?: ICTEffectList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="effectDag" type="CT_EffectContainer">
     * ```
     */
    effectDag?: ICTEffectContainer;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_BackgroundFormatting name="CT_BackgroundFormatting">
 * ```
 */
export interface ICTBackgroundFormattingDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectList maxOccurs="1" minOccurs="1" name="effectLst" type="CT_EffectList">
     * ```
     */
    effectLst?: ICTEffectListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="effectDag" type="CT_EffectContainer">
     * ```
     */
    effectDag?: ICTEffectContainerDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_WholeE2oFormatting name="CT_WholeE2oFormatting">
 * ```
 */
export interface ICTWholeE2oFormatting {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="ln" type="CT_LineProperties">
     * ```
     */
    ln?: ICTLineProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectList maxOccurs="1" minOccurs="1" name="effectLst" type="CT_EffectList">
     * ```
     */
    effectLst?: ICTEffectList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="effectDag" type="CT_EffectContainer">
     * ```
     */
    effectDag?: ICTEffectContainer;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_WholeE2oFormatting name="CT_WholeE2oFormatting">
 * ```
 */
export interface ICTWholeE2oFormattingDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="0" name="ln" type="CT_LineProperties">
     * ```
     */
    ln?: ICTLinePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectList maxOccurs="1" minOccurs="1" name="effectLst" type="CT_EffectList">
     * ```
     */
    effectLst?: ICTEffectListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="effectDag" type="CT_EffectContainer">
     * ```
     */
    effectDag?: ICTEffectContainerDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdsp2008:CT_GroupShapeMsDsp name="CT_GroupShapeMsDsp">
 * ```
 */
export interface ICTGroupShapeMsDsp {
    /**
     * ```xml
     * <xsd:element typeRedirected=msdsp2008:CT_GroupShapeNonVisualMsDsp maxOccurs="1" minOccurs="1" name="nvGrpSpPr" type="CT_GroupShapeNonVisualMsDsp">
     * ```
     */
    nvGrpSpPr?: ICTGroupShapeNonVisualMsDsp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupShapeProperties maxOccurs="1" minOccurs="1" name="grpSpPr" type="a:CT_GroupShapeProperties">
     * ```
     */
    grpSpPr?: ICTGroupShapeProperties;
    /**
     * ```xml
     * <xsd:group typeRedirected=msdsp2008:EG_GroupShapeMsDsp ref=msdsp2008:EG_GroupShapeMsDsp maxOccurs="unbounded" minOccurs="0" name="EG_GroupShapeMsDsp" ref="EG_GroupShapeMsDsp">
     * ref msdsp2008:EG_GroupShapeMsDsp
     * ```
     */
    egGroupShapeMsDsp?: IEGGroupShapeMsDsp[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
}

/**
 * ```xml
 * <xsd:complexType name=msdsp2008:CT_GroupShapeMsDsp name="CT_GroupShapeMsDsp">
 * ```
 */
export interface ICTGroupShapeMsDspDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msdsp2008:CT_GroupShapeNonVisualMsDsp maxOccurs="1" minOccurs="1" name="nvGrpSpPr" type="CT_GroupShapeNonVisualMsDsp">
     * ```
     */
    nvGrpSpPr?: ICTGroupShapeNonVisualMsDspDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupShapeProperties maxOccurs="1" minOccurs="1" name="grpSpPr" type="a:CT_GroupShapeProperties">
     * ```
     */
    grpSpPr?: ICTGroupShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:group typeRedirected=msdsp2008:EG_GroupShapeMsDsp ref=msdsp2008:EG_GroupShapeMsDsp maxOccurs="unbounded" minOccurs="0" name="EG_GroupShapeMsDsp" ref="EG_GroupShapeMsDsp">
     * ref msdsp2008:EG_GroupShapeMsDsp
     * ```
     */
    egGroupShapeMsDsp?: IEGGroupShapeMsDspDelta[];
    egGroupShapeMsDspOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Name name="CT_Name">
 * ```
 */
export interface ICTName {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="lang" type="xsd:string" use="optional">
     * ```
     */
    lang?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="xsd:string" use="required">
     * ```
     */
    val?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Name name="CT_Name">
 * ```
 */
export interface ICTNameDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="lang" type="xsd:string" use="optional">
     * ```
     */
    lang?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="xsd:string" use="required">
     * ```
     */
    val?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Description name="CT_Description">
 * ```
 */
export interface ICTDescription {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="lang" type="xsd:string" use="optional">
     * ```
     */
    lang?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="xsd:string" use="required">
     * ```
     */
    val?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Description name="CT_Description">
 * ```
 */
export interface ICTDescriptionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="lang" type="xsd:string" use="optional">
     * ```
     */
    lang?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="xsd:string" use="required">
     * ```
     */
    val?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Categories name="CT_Categories">
 * ```
 */
export interface ICTCategories {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Category maxOccurs="unbounded" minOccurs="0" name="cat" type="CT_Category">
     * ```
     */
    cat?: ICTCategory[];
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Categories name="CT_Categories">
 * ```
 */
export interface ICTCategoriesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Category maxOccurs="unbounded" minOccurs="0" name="cat" type="CT_Category">
     * ```
     */
    cat?: ICTCategoryDelta[];
    catOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_SampleData name="CT_SampleData">
 * ```
 */
export interface ICTSampleData {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_DataModel minOccurs="0" name="dataModel" type="CT_DataModel">
     * ```
     */
    dataModel?: ICTDataModel;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="useDef" type="xsd:boolean" use="optional">
     * ```
     */
    useDef?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_SampleData name="CT_SampleData">
 * ```
 */
export interface ICTSampleDataDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_DataModel minOccurs="0" name="dataModel" type="CT_DataModel">
     * ```
     */
    dataModel?: ICTDataModelDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="useDef" type="xsd:boolean" use="optional">
     * ```
     */
    useDef?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_LayoutNode name="CT_LayoutNode">
 * ```
 */
export interface ICTLayoutNode {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Algorithm maxOccurs="1" minOccurs="0" name="alg" type="CT_Algorithm">
     * ```
     */
    alg?: ICTAlgorithm[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ShapeDmlDgm maxOccurs="1" minOccurs="0" name="shape" type="CT_ShapeDmlDgm">
     * ```
     */
    shape?: ICTShapeDmlDgm[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_PresentationOf maxOccurs="1" minOccurs="0" name="presOf" type="CT_PresentationOf">
     * ```
     */
    presOf?: ICTPresentationOf[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Constraints maxOccurs="1" minOccurs="0" name="constrLst" type="CT_Constraints">
     * ```
     */
    constrLst?: ICTConstraints[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_RulesDmlDgm maxOccurs="1" minOccurs="0" name="ruleLst" type="CT_RulesDmlDgm">
     * ```
     */
    ruleLst?: ICTRulesDmlDgm[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_LayoutVariablePropertySet maxOccurs="1" minOccurs="0" name="varLst" type="CT_LayoutVariablePropertySet">
     * ```
     */
    varLst?: ICTLayoutVariablePropertySet[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ForEach name="forEach" type="CT_ForEach">
     * ```
     */
    forEach?: ICTForEach[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_LayoutNode name="layoutNode" type="CT_LayoutNode">
     * ```
     */
    layoutNode?: ICTLayoutNode[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Choose name="choose" type="CT_Choose">
     * ```
     */
    choose?: ICTChoose[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="styleLbl" type="xsd:string" use="optional">
     * ```
     */
    styleLbl?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ChildOrderType default="b" name="chOrder" type="ST_ChildOrderType" use="optional">
     * ```
     */
    chOrder?: STChildOrderType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="moveWith" type="xsd:string" use="optional">
     * ```
     */
    moveWith?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_LayoutNode name="CT_LayoutNode">
 * ```
 */
export interface ICTLayoutNodeDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Algorithm maxOccurs="1" minOccurs="0" name="alg" type="CT_Algorithm">
     * ```
     */
    alg?: ICTAlgorithmDelta[];
    algOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ShapeDmlDgm maxOccurs="1" minOccurs="0" name="shape" type="CT_ShapeDmlDgm">
     * ```
     */
    shape?: ICTShapeDmlDgmDelta[];
    shapeOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_PresentationOf maxOccurs="1" minOccurs="0" name="presOf" type="CT_PresentationOf">
     * ```
     */
    presOf?: ICTPresentationOfDelta[];
    presOfOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Constraints maxOccurs="1" minOccurs="0" name="constrLst" type="CT_Constraints">
     * ```
     */
    constrLst?: ICTConstraintsDelta[];
    constrLstOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_RulesDmlDgm maxOccurs="1" minOccurs="0" name="ruleLst" type="CT_RulesDmlDgm">
     * ```
     */
    ruleLst?: ICTRulesDmlDgmDelta[];
    ruleLstOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_LayoutVariablePropertySet maxOccurs="1" minOccurs="0" name="varLst" type="CT_LayoutVariablePropertySet">
     * ```
     */
    varLst?: ICTLayoutVariablePropertySetDelta[];
    varLstOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ForEach name="forEach" type="CT_ForEach">
     * ```
     */
    forEach?: ICTForEachDelta[];
    forEachOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_LayoutNode name="layoutNode" type="CT_LayoutNode">
     * ```
     */
    layoutNode?: ICTLayoutNodeDelta[];
    layoutNodeOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Choose name="choose" type="CT_Choose">
     * ```
     */
    choose?: ICTChooseDelta[];
    chooseOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta[];
    extLstOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="styleLbl" type="xsd:string" use="optional">
     * ```
     */
    styleLbl?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ChildOrderType default="b" name="chOrder" type="ST_ChildOrderType" use="optional">
     * ```
     */
    chOrder?: STChildOrderType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="moveWith" type="xsd:string" use="optional">
     * ```
     */
    moveWith?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_SDName name="CT_SDName">
 * ```
 */
export interface ICTSDName {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="lang" type="xsd:string" use="optional">
     * ```
     */
    lang?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="xsd:string" use="required">
     * ```
     */
    val?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_SDName name="CT_SDName">
 * ```
 */
export interface ICTSDNameDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="lang" type="xsd:string" use="optional">
     * ```
     */
    lang?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="xsd:string" use="required">
     * ```
     */
    val?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_SDDescription name="CT_SDDescription">
 * ```
 */
export interface ICTSDDescription {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="lang" type="xsd:string" use="optional">
     * ```
     */
    lang?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="xsd:string" use="required">
     * ```
     */
    val?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_SDDescription name="CT_SDDescription">
 * ```
 */
export interface ICTSDDescriptionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="lang" type="xsd:string" use="optional">
     * ```
     */
    lang?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="xsd:string" use="required">
     * ```
     */
    val?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_SDCategories name="CT_SDCategories">
 * ```
 */
export interface ICTSDCategories {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_SDCategory maxOccurs="unbounded" minOccurs="0" name="cat" type="CT_SDCategory">
     * ```
     */
    cat?: ICTSDCategory[];
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_SDCategories name="CT_SDCategories">
 * ```
 */
export interface ICTSDCategoriesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_SDCategory maxOccurs="unbounded" minOccurs="0" name="cat" type="CT_SDCategory">
     * ```
     */
    cat?: ICTSDCategoryDelta[];
    catOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_StyleLabel name="CT_StyleLabel">
 * ```
 */
export interface ICTStyleLabel {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Scene3D maxOccurs="1" minOccurs="0" name="scene3d" type="a:CT_Scene3D">
     * ```
     */
    scene3d?: ICTScene3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Shape3D maxOccurs="1" minOccurs="0" name="sp3d" type="a:CT_Shape3D">
     * ```
     */
    sp3d?: ICTShape3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_TextProps maxOccurs="1" minOccurs="0" name="txPr" type="CT_TextProps">
     * ```
     */
    txPr?: ICTTextProps;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeStyle maxOccurs="1" minOccurs="0" name="style" type="a:CT_ShapeStyle">
     * ```
     */
    style?: ICTShapeStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string" use="required">
     * ```
     */
    name?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_StyleLabel name="CT_StyleLabel">
 * ```
 */
export interface ICTStyleLabelDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Scene3D maxOccurs="1" minOccurs="0" name="scene3d" type="a:CT_Scene3D">
     * ```
     */
    scene3d?: ICTScene3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Shape3D maxOccurs="1" minOccurs="0" name="sp3d" type="a:CT_Shape3D">
     * ```
     */
    sp3d?: ICTShape3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_TextProps maxOccurs="1" minOccurs="0" name="txPr" type="CT_TextProps">
     * ```
     */
    txPr?: ICTTextPropsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeStyle maxOccurs="1" minOccurs="0" name="style" type="a:CT_ShapeStyle">
     * ```
     */
    style?: ICTShapeStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string" use="required">
     * ```
     */
    name?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_CTName name="CT_CTName">
 * ```
 */
export interface ICTCTName {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="lang" type="xsd:string" use="optional">
     * ```
     */
    lang?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="xsd:string" use="required">
     * ```
     */
    val?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_CTName name="CT_CTName">
 * ```
 */
export interface ICTCTNameDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="lang" type="xsd:string" use="optional">
     * ```
     */
    lang?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="xsd:string" use="required">
     * ```
     */
    val?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_CTDescription name="CT_CTDescription">
 * ```
 */
export interface ICTCTDescription {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="lang" type="xsd:string" use="optional">
     * ```
     */
    lang?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="xsd:string" use="required">
     * ```
     */
    val?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_CTDescription name="CT_CTDescription">
 * ```
 */
export interface ICTCTDescriptionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="lang" type="xsd:string" use="optional">
     * ```
     */
    lang?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="xsd:string" use="required">
     * ```
     */
    val?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_CTCategories name="CT_CTCategories">
 * ```
 */
export interface ICTCTCategories {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_CTCategory maxOccurs="unbounded" minOccurs="0" name="cat" type="CT_CTCategory">
     * ```
     */
    cat?: ICTCTCategory[];
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_CTCategories name="CT_CTCategories">
 * ```
 */
export interface ICTCTCategoriesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_CTCategory maxOccurs="unbounded" minOccurs="0" name="cat" type="CT_CTCategory">
     * ```
     */
    cat?: ICTCTCategoryDelta[];
    catOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_CTStyleLabel name="CT_CTStyleLabel">
 * ```
 */
export interface ICTCTStyleLabel {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ColorsDmlDgm maxOccurs="1" minOccurs="0" name="fillClrLst" type="CT_ColorsDmlDgm">
     * ```
     */
    fillClrLst?: ICTColorsDmlDgm;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ColorsDmlDgm maxOccurs="1" minOccurs="0" name="linClrLst" type="CT_ColorsDmlDgm">
     * ```
     */
    linClrLst?: ICTColorsDmlDgm;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ColorsDmlDgm maxOccurs="1" minOccurs="0" name="effectClrLst" type="CT_ColorsDmlDgm">
     * ```
     */
    effectClrLst?: ICTColorsDmlDgm;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ColorsDmlDgm maxOccurs="1" minOccurs="0" name="txLinClrLst" type="CT_ColorsDmlDgm">
     * ```
     */
    txLinClrLst?: ICTColorsDmlDgm;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ColorsDmlDgm maxOccurs="1" minOccurs="0" name="txFillClrLst" type="CT_ColorsDmlDgm">
     * ```
     */
    txFillClrLst?: ICTColorsDmlDgm;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ColorsDmlDgm maxOccurs="1" minOccurs="0" name="txEffectClrLst" type="CT_ColorsDmlDgm">
     * ```
     */
    txEffectClrLst?: ICTColorsDmlDgm;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string" use="required">
     * ```
     */
    name?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_CTStyleLabel name="CT_CTStyleLabel">
 * ```
 */
export interface ICTCTStyleLabelDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ColorsDmlDgm maxOccurs="1" minOccurs="0" name="fillClrLst" type="CT_ColorsDmlDgm">
     * ```
     */
    fillClrLst?: ICTColorsDmlDgmDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ColorsDmlDgm maxOccurs="1" minOccurs="0" name="linClrLst" type="CT_ColorsDmlDgm">
     * ```
     */
    linClrLst?: ICTColorsDmlDgmDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ColorsDmlDgm maxOccurs="1" minOccurs="0" name="effectClrLst" type="CT_ColorsDmlDgm">
     * ```
     */
    effectClrLst?: ICTColorsDmlDgmDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ColorsDmlDgm maxOccurs="1" minOccurs="0" name="txLinClrLst" type="CT_ColorsDmlDgm">
     * ```
     */
    txLinClrLst?: ICTColorsDmlDgmDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ColorsDmlDgm maxOccurs="1" minOccurs="0" name="txFillClrLst" type="CT_ColorsDmlDgm">
     * ```
     */
    txFillClrLst?: ICTColorsDmlDgmDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ColorsDmlDgm maxOccurs="1" minOccurs="0" name="txEffectClrLst" type="CT_ColorsDmlDgm">
     * ```
     */
    txEffectClrLst?: ICTColorsDmlDgmDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string" use="required">
     * ```
     */
    name?: string | null;
}

export interface ICommentTableRange {
    startRowIndex?: number;
    startColIndex?: number;
    rowSpan?: number;
    colSpan?: number;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_ColorChoice name="EG_ColorChoice">
 * ```
 */
export interface IEGColorChoice {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ScRgbColor maxOccurs="1" minOccurs="1" name="scrgbClr" type="CT_ScRgbColor">
     * ```
     */
    scrgbClr?: ICTScRgbColor;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SRgbColor maxOccurs="1" minOccurs="1" name="srgbClr" type="CT_SRgbColor">
     * ```
     */
    srgbClr?: ICTSRgbColor;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HslColor maxOccurs="1" minOccurs="1" name="hslClr" type="CT_HslColor">
     * ```
     */
    hslClr?: ICTHslColor;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SystemColor maxOccurs="1" minOccurs="1" name="sysClr" type="CT_SystemColor">
     * ```
     */
    sysClr?: ICTSystemColor;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SchemeColor maxOccurs="1" minOccurs="1" name="schemeClr" type="CT_SchemeColor">
     * ```
     */
    schemeClr?: ICTSchemeColor;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PresetColor maxOccurs="1" minOccurs="1" name="prstClr" type="CT_PresetColor">
     * ```
     */
    prstClr?: ICTPresetColor;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_ColorChoice name="EG_ColorChoice">
 * ```
 */
export interface IEGColorChoiceDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ScRgbColor maxOccurs="1" minOccurs="1" name="scrgbClr" type="CT_ScRgbColor">
     * ```
     */
    scrgbClr?: ICTScRgbColorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SRgbColor maxOccurs="1" minOccurs="1" name="srgbClr" type="CT_SRgbColor">
     * ```
     */
    srgbClr?: ICTSRgbColorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HslColor maxOccurs="1" minOccurs="1" name="hslClr" type="CT_HslColor">
     * ```
     */
    hslClr?: ICTHslColorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SystemColor maxOccurs="1" minOccurs="1" name="sysClr" type="CT_SystemColor">
     * ```
     */
    sysClr?: ICTSystemColorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SchemeColor maxOccurs="1" minOccurs="1" name="schemeClr" type="CT_SchemeColor">
     * ```
     */
    schemeClr?: ICTSchemeColorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PresetColor maxOccurs="1" minOccurs="1" name="prstClr" type="CT_PresetColor">
     * ```
     */
    prstClr?: ICTPresetColorDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_NonVisualDrawingProps name="CT_NonVisualDrawingProps">
 * ```
 */
export interface ICTNonVisualDrawingProps {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HyperlinkDmlA maxOccurs="1" minOccurs="0" name="hlinkClick" type="CT_HyperlinkDmlA">
     * ```
     */
    hlinkClick?: ICTHyperlinkDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HyperlinkDmlA maxOccurs="1" minOccurs="0" name="hlinkHover" type="CT_HyperlinkDmlA">
     * ```
     */
    hlinkHover?: ICTHyperlinkDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="ST_DrawingElementId" use="required">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string" use="required">
     * ```
     */
    name?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="descr" type="xsd:string" use="optional">
     * ```
     */
    descr?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="hidden" type="xsd:boolean" use="optional">
     * ```
     */
    hidden?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="title" type="xsd:string" use="optional">
     * ```
     */
    title?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="originId" skipIO="true" type="ST_DrawingElementId" use="required">
     * ```
     */
    originId?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_NonVisualDrawingProps name="CT_NonVisualDrawingProps">
 * ```
 */
export interface ICTNonVisualDrawingPropsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HyperlinkDmlA maxOccurs="1" minOccurs="0" name="hlinkClick" type="CT_HyperlinkDmlA">
     * ```
     */
    hlinkClick?: ICTHyperlinkDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HyperlinkDmlA maxOccurs="1" minOccurs="0" name="hlinkHover" type="CT_HyperlinkDmlA">
     * ```
     */
    hlinkHover?: ICTHyperlinkDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="ST_DrawingElementId" use="required">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string" use="required">
     * ```
     */
    name?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="descr" type="xsd:string" use="optional">
     * ```
     */
    descr?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="hidden" type="xsd:boolean" use="optional">
     * ```
     */
    hidden?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="title" type="xsd:string" use="optional">
     * ```
     */
    title?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="originId" skipIO="true" type="ST_DrawingElementId" use="required">
     * ```
     */
    originId?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_NonVisualGroupDrawingShapeProps name="CT_NonVisualGroupDrawingShapeProps">
 * ```
 */
export interface ICTNonVisualGroupDrawingShapeProps {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupLocking maxOccurs="1" minOccurs="0" name="grpSpLocks" type="CT_GroupLocking">
     * ```
     */
    grpSpLocks?: ICTGroupLocking;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_NonVisualGroupDrawingShapeProps name="CT_NonVisualGroupDrawingShapeProps">
 * ```
 */
export interface ICTNonVisualGroupDrawingShapePropsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupLocking maxOccurs="1" minOccurs="0" name="grpSpLocks" type="CT_GroupLocking">
     * ```
     */
    grpSpLocks?: ICTGroupLockingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ApplicationNonVisualDrawingProps name="CT_ApplicationNonVisualDrawingProps">
 * ```
 */
export interface ICTApplicationNonVisualDrawingProps {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_PlaceholderPml maxOccurs="1" minOccurs="0" name="ph" type="CT_PlaceholderPml">
     * ```
     */
    ph?: ICTPlaceholderPml;
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_Media ref=dmlmain:EG_Media maxOccurs="1" minOccurs="0" name="EG_Media" ref="a:EG_Media">
     * ref dmlmain:EG_Media
     * ```
     */
    egMedia?: IEGMedia;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CustomerDataList maxOccurs="1" minOccurs="0" name="custDataLst" type="CT_CustomerDataList">
     * ```
     */
    custDataLst?: ICTCustomerDataList;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="isPhoto" type="xsd:boolean" use="optional">
     * ```
     */
    isPhoto?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="userDrawn" type="xsd:boolean" use="optional">
     * ```
     */
    userDrawn?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ApplicationNonVisualDrawingProps name="CT_ApplicationNonVisualDrawingProps">
 * ```
 */
export interface ICTApplicationNonVisualDrawingPropsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_PlaceholderPml maxOccurs="1" minOccurs="0" name="ph" type="CT_PlaceholderPml">
     * ```
     */
    ph?: ICTPlaceholderPmlDelta | null;
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_Media ref=dmlmain:EG_Media maxOccurs="1" minOccurs="0" name="EG_Media" ref="a:EG_Media">
     * ref dmlmain:EG_Media
     * ```
     */
    egMedia?: IEGMediaDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CustomerDataList maxOccurs="1" minOccurs="0" name="custDataLst" type="CT_CustomerDataList">
     * ```
     */
    custDataLst?: ICTCustomerDataListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="isPhoto" type="xsd:boolean" use="optional">
     * ```
     */
    isPhoto?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="userDrawn" type="xsd:boolean" use="optional">
     * ```
     */
    userDrawn?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GroupTransform2D name="CT_GroupTransform2D">
 * ```
 */
export interface ICTGroupTransform2D {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point2D maxOccurs="1" minOccurs="0" name="off" type="CT_Point2D">
     * ```
     */
    off?: ICTPoint2D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositiveSize2D maxOccurs="1" minOccurs="0" name="ext" type="CT_PositiveSize2D">
     * ```
     */
    ext?: ICTPositiveSize2D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point2D maxOccurs="1" minOccurs="0" name="chOff" type="CT_Point2D">
     * ```
     */
    chOff?: ICTPoint2D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositiveSize2D maxOccurs="1" minOccurs="0" name="chExt" type="CT_PositiveSize2D">
     * ```
     */
    chExt?: ICTPositiveSize2D;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="rot" type="ST_AngleDmlA" use="optional">
     * ```
     */
    rot?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="flipH" type="xsd:boolean" use="optional">
     * ```
     */
    flipH?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="flipV" type="xsd:boolean" use="optional">
     * ```
     */
    flipV?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GroupTransform2D name="CT_GroupTransform2D">
 * ```
 */
export interface ICTGroupTransform2DDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point2D maxOccurs="1" minOccurs="0" name="off" type="CT_Point2D">
     * ```
     */
    off?: ICTPoint2DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositiveSize2D maxOccurs="1" minOccurs="0" name="ext" type="CT_PositiveSize2D">
     * ```
     */
    ext?: ICTPositiveSize2DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point2D maxOccurs="1" minOccurs="0" name="chOff" type="CT_Point2D">
     * ```
     */
    chOff?: ICTPoint2DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositiveSize2D maxOccurs="1" minOccurs="0" name="chExt" type="CT_PositiveSize2D">
     * ```
     */
    chExt?: ICTPositiveSize2DDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="rot" type="ST_AngleDmlA" use="optional">
     * ```
     */
    rot?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="flipH" type="xsd:boolean" use="optional">
     * ```
     */
    flipH?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="flipV" type="xsd:boolean" use="optional">
     * ```
     */
    flipV?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ShapePml name="CT_ShapePml">
 * ```
 */
export interface ICTShapePml {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ShapeNonVisualPml maxOccurs="1" minOccurs="1" name="nvSpPr" type="CT_ShapeNonVisualPml">
     * ```
     */
    nvSpPr?: ICTShapeNonVisualPml;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="1" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeStyle maxOccurs="1" minOccurs="0" name="style" type="a:CT_ShapeStyle">
     * ```
     */
    style?: ICTShapeStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txBody" type="a:CT_TextBody">
     * ```
     */
    txBody?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModify;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="useBgFill" type="xsd:boolean" use="optional">
     * ```
     */
    useBgFill?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ShapePml name="CT_ShapePml">
 * ```
 */
export interface ICTShapePmlDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ShapeNonVisualPml maxOccurs="1" minOccurs="1" name="nvSpPr" type="CT_ShapeNonVisualPml">
     * ```
     */
    nvSpPr?: ICTShapeNonVisualPmlDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="1" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeStyle maxOccurs="1" minOccurs="0" name="style" type="a:CT_ShapeStyle">
     * ```
     */
    style?: ICTShapeStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txBody" type="a:CT_TextBody">
     * ```
     */
    txBody?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModifyDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="useBgFill" type="xsd:boolean" use="optional">
     * ```
     */
    useBgFill?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_GraphicalObjectFramePml name="CT_GraphicalObjectFramePml">
 * ```
 */
export interface ICTGraphicalObjectFramePml {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_GraphicalObjectFrameNonVisualPml maxOccurs="1" minOccurs="1" name="nvGraphicFramePr" type="CT_GraphicalObjectFrameNonVisualPml">
     * ```
     */
    nvGraphicFramePr?: ICTGraphicalObjectFrameNonVisualPml;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Transform2D maxOccurs="1" minOccurs="1" name="xfrm" type="a:CT_Transform2D">
     * ```
     */
    xfrm?: ICTTransform2D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GraphicalObject ref=dmlmain:graphic maxOccurs="1" minOccurs="1" name="graphic" ref="a:graphic" type="CT_GraphicalObject">
     * ```
     */
    graphic?: ICTGraphicalObject;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModify;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_BlackWhiteMode name="bwMode" type="a:ST_BlackWhiteMode" use="optional">
     * ```
     */
    bwMode?: STBlackWhiteMode;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_GraphicalObjectFramePml name="CT_GraphicalObjectFramePml">
 * ```
 */
export interface ICTGraphicalObjectFramePmlDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_GraphicalObjectFrameNonVisualPml maxOccurs="1" minOccurs="1" name="nvGraphicFramePr" type="CT_GraphicalObjectFrameNonVisualPml">
     * ```
     */
    nvGraphicFramePr?: ICTGraphicalObjectFrameNonVisualPmlDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Transform2D maxOccurs="1" minOccurs="1" name="xfrm" type="a:CT_Transform2D">
     * ```
     */
    xfrm?: ICTTransform2DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GraphicalObject ref=dmlmain:graphic maxOccurs="1" minOccurs="1" name="graphic" ref="a:graphic" type="CT_GraphicalObject">
     * ```
     */
    graphic?: ICTGraphicalObjectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModifyDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_BlackWhiteMode name="bwMode" type="a:ST_BlackWhiteMode" use="optional">
     * ```
     */
    bwMode?: STBlackWhiteMode | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ConnectorPml name="CT_ConnectorPml">
 * ```
 */
export interface ICTConnectorPml {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ConnectorNonVisualPml maxOccurs="1" minOccurs="1" name="nvCxnSpPr" type="CT_ConnectorNonVisualPml">
     * ```
     */
    nvCxnSpPr?: ICTConnectorNonVisualPml;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="1" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeStyle maxOccurs="1" minOccurs="0" name="style" type="a:CT_ShapeStyle">
     * ```
     */
    style?: ICTShapeStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModify;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ConnectorPml name="CT_ConnectorPml">
 * ```
 */
export interface ICTConnectorPmlDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ConnectorNonVisualPml maxOccurs="1" minOccurs="1" name="nvCxnSpPr" type="CT_ConnectorNonVisualPml">
     * ```
     */
    nvCxnSpPr?: ICTConnectorNonVisualPmlDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="1" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeStyle maxOccurs="1" minOccurs="0" name="style" type="a:CT_ShapeStyle">
     * ```
     */
    style?: ICTShapeStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModifyDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_PicturePml name="CT_PicturePml">
 * ```
 */
export interface ICTPicturePml {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_PictureNonVisualPml maxOccurs="1" minOccurs="1" name="nvPicPr" type="CT_PictureNonVisualPml">
     * ```
     */
    nvPicPr?: ICTPictureNonVisualPml;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="a:CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="1" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeStyle maxOccurs="1" minOccurs="0" name="style" type="a:CT_ShapeStyle">
     * ```
     */
    style?: ICTShapeStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModify;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_PicturePml name="CT_PicturePml">
 * ```
 */
export interface ICTPicturePmlDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_PictureNonVisualPml maxOccurs="1" minOccurs="1" name="nvPicPr" type="CT_PictureNonVisualPml">
     * ```
     */
    nvPicPr?: ICTPictureNonVisualPmlDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="a:CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="1" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeStyle maxOccurs="1" minOccurs="0" name="style" type="a:CT_ShapeStyle">
     * ```
     */
    style?: ICTShapeStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModifyDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_RelPml name="CT_RelPml">
 * ```
 */
export interface ICTRelPml {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_RelPml name="CT_RelPml">
 * ```
 */
export interface ICTRelPmlDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string | null;
}

export enum ArrayOpType {
    Append = 0,
    Set = 1,
    Modify = 2,
    Insert = 3,
    Delete = 4,
}

export enum STColorSchemeIndex {
    PBDEFAULT = 0,
    dk1 = 1,
    lt1 = 2,
    dk2 = 3,
    lt2 = 4,
    accent1 = 5,
    accent2 = 6,
    accent3 = 7,
    accent4 = 8,
    accent5 = 9,
    accent6 = 10,
    hlink = 11,
    folHlink = 12,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_EmbeddedWAVAudioFile name="CT_EmbeddedWAVAudioFile">
 * ```
 */
export interface ICTEmbeddedWAVAudioFile {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:embed name="embed" ref="r:embed" type="ST_RelationshipId" use="required">
     * ```
     */
    embed?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_EmbeddedWAVAudioFile name="CT_EmbeddedWAVAudioFile">
 * ```
 */
export interface ICTEmbeddedWAVAudioFileDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:embed name="embed" ref="r:embed" type="ST_RelationshipId" use="required">
     * ```
     */
    embed?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTemplateList name="CT_TLTemplateList">
 * ```
 */
export interface ICTTLTemplateList {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTemplate maxOccurs="9" minOccurs="0" name="tmpl" type="CT_TLTemplate">
     * ```
     */
    tmpl?: ICTTLTemplate[];
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTemplateList name="CT_TLTemplateList">
 * ```
 */
export interface ICTTLTemplateListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTemplate maxOccurs="9" minOccurs="0" name="tmpl" type="CT_TLTemplate">
     * ```
     */
    tmpl?: ICTTLTemplateDelta[];
    tmplOp?: IArrayOp;
}

export enum STTLParaBuildType {
    PBDEFAULT = 0,
    allAtOnce = 1,
    p = 2,
    cust = 3,
    whole = 4,
}

export enum STTLDiagramBuildType {
    PBDEFAULT = 0,
    whole = 1,
    depthByNode = 2,
    depthByBranch = 3,
    breadthByNode = 4,
    breadthByLvl = 5,
    cw = 6,
    cwIn = 7,
    cwOut = 8,
    ccw = 9,
    ccwIn = 10,
    ccwOut = 11,
    inByRing = 12,
    outByRing = 13,
    up = 14,
    down = 15,
    allAtOnce = 16,
    cust = 17,
}

export enum STTLOleChartBuildType {
    PBDEFAULT = 0,
    allAtOnce = 1,
    series = 2,
    category = 3,
    seriesEl = 4,
    categoryEl = 5,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AnimationGraphicalObjectBuildProperties name="CT_AnimationGraphicalObjectBuildProperties">
 * ```
 */
export interface ICTAnimationGraphicalObjectBuildProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AnimationDgmBuildProperties name="bldDgm" type="CT_AnimationDgmBuildProperties">
     * ```
     */
    bldDgm?: ICTAnimationDgmBuildProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AnimationChartBuildProperties name="bldChart" type="CT_AnimationChartBuildProperties">
     * ```
     */
    bldChart?: ICTAnimationChartBuildProperties;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AnimationGraphicalObjectBuildProperties name="CT_AnimationGraphicalObjectBuildProperties">
 * ```
 */
export interface ICTAnimationGraphicalObjectBuildPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AnimationDgmBuildProperties name="bldDgm" type="CT_AnimationDgmBuildProperties">
     * ```
     */
    bldDgm?: ICTAnimationDgmBuildPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AnimationChartBuildProperties name="bldChart" type="CT_AnimationChartBuildProperties">
     * ```
     */
    bldChart?: ICTAnimationChartBuildPropertiesDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_Media name="CT_Media">
 * ```
 */
export interface ICTMedia {
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_MediaTrim maxOccurs="1" minOccurs="0" name="trim" type="CT_MediaTrim">
     * ```
     */
    trim?: ICTMediaTrim;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_MediaFade maxOccurs="1" minOccurs="0" name="fade" type="CT_MediaFade">
     * ```
     */
    fade?: ICTMediaFade;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_MediaBookmarkList maxOccurs="1" minOccurs="0" name="bmkLst" type="CT_MediaBookmarkList">
     * ```
     */
    bmkLst?: ICTMediaBookmarkList;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="p:CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:embed default="" name="embed" ref="r:embed" type="ST_RelationshipId" use="optional">
     * ```
     */
    embed?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:link default="" name="link" ref="r:link" type="ST_RelationshipId" use="optional">
     * ```
     */
    link?: string;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_Media name="CT_Media">
 * ```
 */
export interface ICTMediaDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_MediaTrim maxOccurs="1" minOccurs="0" name="trim" type="CT_MediaTrim">
     * ```
     */
    trim?: ICTMediaTrimDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_MediaFade maxOccurs="1" minOccurs="0" name="fade" type="CT_MediaFade">
     * ```
     */
    fade?: ICTMediaFadeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_MediaBookmarkList maxOccurs="1" minOccurs="0" name="bmkLst" type="CT_MediaBookmarkList">
     * ```
     */
    bmkLst?: ICTMediaBookmarkListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="p:CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:embed default="" name="embed" ref="r:embed" type="ST_RelationshipId" use="optional">
     * ```
     */
    embed?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:link default="" name="link" ref="r:link" type="ST_RelationshipId" use="optional">
     * ```
     */
    link?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_SectionList name="CT_SectionList">
 * ```
 */
export interface ICTSectionList {
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_Section maxOccurs="unbounded" minOccurs="1" name="section" type="CT_Section">
     * ```
     */
    section?: ICTSection[];
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_SectionList name="CT_SectionList">
 * ```
 */
export interface ICTSectionListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_Section maxOccurs="unbounded" minOccurs="1" name="section" type="CT_Section">
     * ```
     */
    section?: ICTSectionDelta[];
    sectionOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2012:CT_CommentThreading name="CT_CommentThreading">
 * ```
 */
export interface ICTCommentThreading {
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2012:CT_ParentCommentIdentifier maxOccurs="1" minOccurs="0" name="parentCm" type="CT_ParentCommentIdentifier">
     * ```
     */
    parentCm?: ICTParentCommentIdentifier;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="timeZoneBias" type="xsd:int">
     * ```
     */
    timeZoneBias?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2012:CT_CommentThreading name="CT_CommentThreading">
 * ```
 */
export interface ICTCommentThreadingDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2012:CT_ParentCommentIdentifier maxOccurs="1" minOccurs="0" name="parentCm" type="CT_ParentCommentIdentifier">
     * ```
     */
    parentCm?: ICTParentCommentIdentifierDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="timeZoneBias" type="xsd:int">
     * ```
     */
    timeZoneBias?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msppt201808:CT_CommentRelationship name="CT_CommentRelationship">
 * ```
 */
export interface ICTCommentRelationship {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string;
}

/**
 * ```xml
 * <xsd:complexType name=msppt201808:CT_CommentRelationship name="CT_CommentRelationship">
 * ```
 */
export interface ICTCommentRelationshipDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_IndexRange name="CT_IndexRange">
 * ```
 */
export interface ICTIndexRange {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="st" type="ST_Index" use="required">
     * ```
     */
    st?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="end" type="ST_Index" use="required">
     * ```
     */
    end?: number;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_IndexRange name="CT_IndexRange">
 * ```
 */
export interface ICTIndexRangeDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="st" type="ST_Index" use="required">
     * ```
     */
    st?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="end" type="ST_Index" use="required">
     * ```
     */
    end?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_CustomShowId name="CT_CustomShowId">
 * ```
 */
export interface ICTCustomShowId {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="xsd:unsignedInt" use="required">
     * ```
     */
    id?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_CustomShowId name="CT_CustomShowId">
 * ```
 */
export interface ICTCustomShowIdDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="xsd:unsignedInt" use="required">
     * ```
     */
    id?: string | null;
}

export enum STWebScreenSize {
    PBDEFAULT = 0,
    '544x376' = 1,
    '640x480' = 2,
    '720x512' = 3,
    '800x600' = 4,
    '1024x768' = 5,
    '1152x882' = 6,
    '1152x900' = 7,
    '1280x1024' = 8,
    '1600x1200' = 9,
    '1800x1400' = 10,
    '1920x1200' = 11,
}

export enum STWebColorType {
    PBDEFAULT = 0,
    none = 1,
    browser = 2,
    presentationText = 3,
    presentationAccent = 4,
    whiteTextOnBlack = 5,
    blackTextOnWhite = 6,
}

export enum STPrintWhat {
    PBDEFAULT = 0,
    slides = 1,
    handouts1 = 2,
    handouts2 = 3,
    handouts3 = 4,
    handouts4 = 5,
    handouts6 = 6,
    handouts9 = 7,
    notes = 8,
    outline = 9,
}

export enum STPrintColorMode {
    PBDEFAULT = 0,
    bw = 1,
    gray = 2,
    clr = 3,
}

/**
 * ```xml
 * <xsd:group name=pml:EG_ShowType name="EG_ShowType">
 * ```
 */
export interface IEGShowType {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="present" type="CT_EmptyPml">
     * ```
     */
    present?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ShowInfoBrowse name="browse" type="CT_ShowInfoBrowse">
     * ```
     */
    browse?: ICTShowInfoBrowse;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ShowInfoKiosk name="kiosk" type="CT_ShowInfoKiosk">
     * ```
     */
    kiosk?: ICTShowInfoKiosk;
}

/**
 * ```xml
 * <xsd:group name=pml:EG_ShowType name="EG_ShowType">
 * ```
 */
export interface IEGShowTypeDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="present" type="CT_EmptyPml">
     * ```
     */
    present?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ShowInfoBrowse name="browse" type="CT_ShowInfoBrowse">
     * ```
     */
    browse?: ICTShowInfoBrowseDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ShowInfoKiosk name="kiosk" type="CT_ShowInfoKiosk">
     * ```
     */
    kiosk?: ICTShowInfoKioskDelta | null;
}

/**
 * ```xml
 * <xsd:group name=pml:EG_SlideListChoice name="EG_SlideListChoice">
 * ```
 */
export interface IEGSlideListChoice {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="sldAll" type="CT_EmptyPml">
     * ```
     */
    sldAll?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_IndexRange name="sldRg" type="CT_IndexRange">
     * ```
     */
    sldRg?: ICTIndexRange;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CustomShowId name="custShow" type="CT_CustomShowId">
     * ```
     */
    custShow?: ICTCustomShowId;
}

/**
 * ```xml
 * <xsd:group name=pml:EG_SlideListChoice name="EG_SlideListChoice">
 * ```
 */
export interface IEGSlideListChoiceDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="sldAll" type="CT_EmptyPml">
     * ```
     */
    sldAll?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_IndexRange name="sldRg" type="CT_IndexRange">
     * ```
     */
    sldRg?: ICTIndexRangeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CustomShowId name="custShow" type="CT_CustomShowId">
     * ```
     */
    custShow?: ICTCustomShowIdDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_OfficeArtExtension name="CT_OfficeArtExtension">
 * ```
 */
export interface ICTOfficeArtExtension {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="uri" type="xsd:token" use="required">
     * ```
     */
    uri?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdsp2008:CT_DataModelExtBlock ref=msdsp2008:dataModelExt name="dataModelExt" ref="dsp:dataModelExt" type="CT_DataModelExtBlock">
     * ```
     */
    dataModelExt?: ICTDataModelExtBlock[];
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_Photo ref=msdm2010:imgProps minOccurs="0" name="imgProps" ref="msdm2010:imgProps" type="CT_Photo">
     * ```
     */
    imgProps?: ICTPhoto[];
    /**
     * ```xml
     * <xsd:element typeRedirected=msask2018:CT_LineSketchStyleProperties ref=msask2018:lineSketchStyleProps minOccurs="0" name="lineSketchStyleProps" ref="ask:lineSketchStyleProps" type="CT_LineSketchStyleProperties">
     * ```
     */
    lineSketchStyleProps?: ICTLineSketchStyleProperties[];
    /**
     * ```xml
     * <xsd:element typeRedirected=msasvg2016:CT_SVGBlip ref=msasvg2016:svgBlip minOccurs="0" name="svgBlip" ref="asvg:svgBlip" type="CT_SVGBlip">
     * ```
     */
    svgBlip?: ICTSVGBlip[];
    /**
     * ```xml
     * <xsd:element typeRedirected=msahyp2018:CT_HyperlinkColor ref=msahyp2018:hlinkClr minOccurs="0" name="hlinkClr" ref="ahyp:hlinkClr" type="CT_HyperlinkColor">
     * ```
     */
    hlinkClr?: ICTHyperlinkColor[];
    /**
     * ```xml
     * <xsd:element typeRedirected=msthm2012:CT_ThemeFamily ref=msthm2012:themeFamily minOccurs="0" name="themeFamily" ref="thm15:themeFamily" type="CT_ThemeFamily">
     * ```
     */
    themeFamily?: ICTThemeFamily[];
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2014:CT_Identifier ref=msdm2014:rowId minOccurs="0" name="rowId" ref="a16:rowId" type="CT_Identifier">
     * ```
     */
    rowId?: ICTIdentifier[];
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2014:CT_Identifier ref=msdm2014:colId minOccurs="0" name="colId" ref="a16:colId" type="CT_Identifier">
     * ```
     */
    colId?: ICTIdentifier[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_OfficeArtExtension name="CT_OfficeArtExtension">
 * ```
 */
export interface ICTOfficeArtExtensionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="uri" type="xsd:token" use="required">
     * ```
     */
    uri?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdsp2008:CT_DataModelExtBlock ref=msdsp2008:dataModelExt name="dataModelExt" ref="dsp:dataModelExt" type="CT_DataModelExtBlock">
     * ```
     */
    dataModelExt?: ICTDataModelExtBlockDelta[];
    dataModelExtOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_Photo ref=msdm2010:imgProps minOccurs="0" name="imgProps" ref="msdm2010:imgProps" type="CT_Photo">
     * ```
     */
    imgProps?: ICTPhotoDelta[];
    imgPropsOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=msask2018:CT_LineSketchStyleProperties ref=msask2018:lineSketchStyleProps minOccurs="0" name="lineSketchStyleProps" ref="ask:lineSketchStyleProps" type="CT_LineSketchStyleProperties">
     * ```
     */
    lineSketchStyleProps?: ICTLineSketchStylePropertiesDelta[];
    lineSketchStylePropsOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=msasvg2016:CT_SVGBlip ref=msasvg2016:svgBlip minOccurs="0" name="svgBlip" ref="asvg:svgBlip" type="CT_SVGBlip">
     * ```
     */
    svgBlip?: ICTSVGBlipDelta[];
    svgBlipOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=msahyp2018:CT_HyperlinkColor ref=msahyp2018:hlinkClr minOccurs="0" name="hlinkClr" ref="ahyp:hlinkClr" type="CT_HyperlinkColor">
     * ```
     */
    hlinkClr?: ICTHyperlinkColorDelta[];
    hlinkClrOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=msthm2012:CT_ThemeFamily ref=msthm2012:themeFamily minOccurs="0" name="themeFamily" ref="thm15:themeFamily" type="CT_ThemeFamily">
     * ```
     */
    themeFamily?: ICTThemeFamilyDelta[];
    themeFamilyOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2014:CT_Identifier ref=msdm2014:rowId minOccurs="0" name="rowId" ref="a16:rowId" type="CT_Identifier">
     * ```
     */
    rowId?: ICTIdentifierDelta[];
    rowIdOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2014:CT_Identifier ref=msdm2014:colId minOccurs="0" name="colId" ref="a16:colId" type="CT_Identifier">
     * ```
     */
    colId?: ICTIdentifierDelta[];
    colIdOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableBackgroundStyle name="CT_TableBackgroundStyle">
 * ```
 */
export interface ICTTableBackgroundStyle {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FillProperties maxOccurs="1" minOccurs="1" name="fill" type="CT_FillProperties">
     * ```
     */
    fill?: ICTFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StyleMatrixReference maxOccurs="1" minOccurs="1" name="fillRef" type="CT_StyleMatrixReference">
     * ```
     */
    fillRef?: ICTStyleMatrixReference;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectProperties maxOccurs="1" minOccurs="1" name="effect" type="CT_EffectProperties">
     * ```
     */
    effect?: ICTEffectProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StyleMatrixReference maxOccurs="1" minOccurs="1" name="effectRef" type="CT_StyleMatrixReference">
     * ```
     */
    effectRef?: ICTStyleMatrixReference;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableBackgroundStyle name="CT_TableBackgroundStyle">
 * ```
 */
export interface ICTTableBackgroundStyleDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FillProperties maxOccurs="1" minOccurs="1" name="fill" type="CT_FillProperties">
     * ```
     */
    fill?: ICTFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StyleMatrixReference maxOccurs="1" minOccurs="1" name="fillRef" type="CT_StyleMatrixReference">
     * ```
     */
    fillRef?: ICTStyleMatrixReferenceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectProperties maxOccurs="1" minOccurs="1" name="effect" type="CT_EffectProperties">
     * ```
     */
    effect?: ICTEffectPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StyleMatrixReference maxOccurs="1" minOccurs="1" name="effectRef" type="CT_StyleMatrixReference">
     * ```
     */
    effectRef?: ICTStyleMatrixReferenceDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TablePartStyle name="CT_TablePartStyle">
 * ```
 */
export interface ICTTablePartStyle {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableStyleTextStyle maxOccurs="1" minOccurs="0" name="tcTxStyle" type="CT_TableStyleTextStyle">
     * ```
     */
    tcTxStyle?: ICTTableStyleTextStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableStyleCellStyle maxOccurs="1" minOccurs="0" name="tcStyle" type="CT_TableStyleCellStyle">
     * ```
     */
    tcStyle?: ICTTableStyleCellStyle;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TablePartStyle name="CT_TablePartStyle">
 * ```
 */
export interface ICTTablePartStyleDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableStyleTextStyle maxOccurs="1" minOccurs="0" name="tcTxStyle" type="CT_TableStyleTextStyle">
     * ```
     */
    tcTxStyle?: ICTTableStyleTextStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableStyleCellStyle maxOccurs="1" minOccurs="0" name="tcStyle" type="CT_TableStyleCellStyle">
     * ```
     */
    tcStyle?: ICTTableStyleCellStyleDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_NormalViewPortion name="CT_NormalViewPortion">
 * ```
 */
export interface ICTNormalViewPortion {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="sz" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    sz?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="autoAdjust" type="xsd:boolean" use="optional">
     * ```
     */
    autoAdjust?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_NormalViewPortion name="CT_NormalViewPortion">
 * ```
 */
export interface ICTNormalViewPortionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="sz" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    sz?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="autoAdjust" type="xsd:boolean" use="optional">
     * ```
     */
    autoAdjust?: boolean | null;
}

export enum STSplitterBarState {
    PBDEFAULT = 0,
    minimized = 1,
    restored = 2,
    maximized = 3,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_CommonSlideViewProperties name="CT_CommonSlideViewProperties">
 * ```
 */
export interface ICTCommonSlideViewProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CommonViewProperties maxOccurs="1" minOccurs="1" name="cViewPr" type="CT_CommonViewProperties">
     * ```
     */
    cViewPr?: ICTCommonViewProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_GuideList maxOccurs="1" minOccurs="0" name="guideLst" type="CT_GuideList">
     * ```
     */
    guideLst?: ICTGuideList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="snapToGrid" type="xsd:boolean" use="optional">
     * ```
     */
    snapToGrid?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="snapToObjects" type="xsd:boolean" use="optional">
     * ```
     */
    snapToObjects?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="showGuides" type="xsd:boolean" use="optional">
     * ```
     */
    showGuides?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_CommonSlideViewProperties name="CT_CommonSlideViewProperties">
 * ```
 */
export interface ICTCommonSlideViewPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_CommonViewProperties maxOccurs="1" minOccurs="1" name="cViewPr" type="CT_CommonViewProperties">
     * ```
     */
    cViewPr?: ICTCommonViewPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_GuideList maxOccurs="1" minOccurs="0" name="guideLst" type="CT_GuideList">
     * ```
     */
    guideLst?: ICTGuideListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="snapToGrid" type="xsd:boolean" use="optional">
     * ```
     */
    snapToGrid?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="snapToObjects" type="xsd:boolean" use="optional">
     * ```
     */
    snapToObjects?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="showGuides" type="xsd:boolean" use="optional">
     * ```
     */
    showGuides?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_CommonViewProperties name="CT_CommonViewProperties">
 * ```
 */
export interface ICTCommonViewProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Scale2D maxOccurs="1" minOccurs="1" name="scale" type="a:CT_Scale2D">
     * ```
     */
    scale?: ICTScale2D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point2D maxOccurs="1" minOccurs="1" name="origin" type="a:CT_Point2D">
     * ```
     */
    origin?: ICTPoint2D;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="varScale" type="xsd:boolean" use="optional">
     * ```
     */
    varScale?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_CommonViewProperties name="CT_CommonViewProperties">
 * ```
 */
export interface ICTCommonViewPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Scale2D maxOccurs="1" minOccurs="1" name="scale" type="a:CT_Scale2D">
     * ```
     */
    scale?: ICTScale2DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point2D maxOccurs="1" minOccurs="1" name="origin" type="a:CT_Point2D">
     * ```
     */
    origin?: ICTPoint2DDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="varScale" type="xsd:boolean" use="optional">
     * ```
     */
    varScale?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_OutlineViewSlideList name="CT_OutlineViewSlideList">
 * ```
 */
export interface ICTOutlineViewSlideList {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OutlineViewSlideEntry maxOccurs="unbounded" minOccurs="0" name="sld" type="CT_OutlineViewSlideEntry">
     * ```
     */
    sld?: ICTOutlineViewSlideEntry[];
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_OutlineViewSlideList name="CT_OutlineViewSlideList">
 * ```
 */
export interface ICTOutlineViewSlideListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OutlineViewSlideEntry maxOccurs="unbounded" minOccurs="0" name="sld" type="CT_OutlineViewSlideEntry">
     * ```
     */
    sld?: ICTOutlineViewSlideEntryDelta[];
    sldOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_EmbeddedFontDataId name="CT_EmbeddedFontDataId">
 * ```
 */
export interface ICTEmbeddedFontDataId {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_EmbeddedFontDataId name="CT_EmbeddedFontDataId">
 * ```
 */
export interface ICTEmbeddedFontDataIdDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_CustomGeometry2D name="CT_CustomGeometry2D">
 * ```
 */
export interface ICTCustomGeometry2D {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GeomGuideList maxOccurs="1" minOccurs="0" name="avLst" type="CT_GeomGuideList">
     * ```
     */
    avLst?: ICTGeomGuideList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GeomGuideList maxOccurs="1" minOccurs="0" name="gdLst" type="CT_GeomGuideList">
     * ```
     */
    gdLst?: ICTGeomGuideList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AdjustHandleList maxOccurs="1" minOccurs="0" name="ahLst" type="CT_AdjustHandleList">
     * ```
     */
    ahLst?: ICTAdjustHandleList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ConnectionSiteList maxOccurs="1" minOccurs="0" name="cxnLst" type="CT_ConnectionSiteList">
     * ```
     */
    cxnLst?: ICTConnectionSiteList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GeomRect maxOccurs="1" minOccurs="0" name="rect" type="CT_GeomRect">
     * ```
     */
    rect?: ICTGeomRect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Path2DList maxOccurs="1" minOccurs="1" name="pathLst" type="CT_Path2DList">
     * ```
     */
    pathLst?: ICTPath2DList;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_CustomGeometry2D name="CT_CustomGeometry2D">
 * ```
 */
export interface ICTCustomGeometry2DDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GeomGuideList maxOccurs="1" minOccurs="0" name="avLst" type="CT_GeomGuideList">
     * ```
     */
    avLst?: ICTGeomGuideListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GeomGuideList maxOccurs="1" minOccurs="0" name="gdLst" type="CT_GeomGuideList">
     * ```
     */
    gdLst?: ICTGeomGuideListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AdjustHandleList maxOccurs="1" minOccurs="0" name="ahLst" type="CT_AdjustHandleList">
     * ```
     */
    ahLst?: ICTAdjustHandleListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ConnectionSiteList maxOccurs="1" minOccurs="0" name="cxnLst" type="CT_ConnectionSiteList">
     * ```
     */
    cxnLst?: ICTConnectionSiteListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GeomRect maxOccurs="1" minOccurs="0" name="rect" type="CT_GeomRect">
     * ```
     */
    rect?: ICTGeomRectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Path2DList maxOccurs="1" minOccurs="1" name="pathLst" type="CT_Path2DList">
     * ```
     */
    pathLst?: ICTPath2DListDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PresetGeometry2D name="CT_PresetGeometry2D">
 * ```
 */
export interface ICTPresetGeometry2D {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GeomGuideList maxOccurs="1" minOccurs="0" name="avLst" type="CT_GeomGuideList">
     * ```
     */
    avLst?: ICTGeomGuideList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ShapeType name="prst" type="ST_ShapeType" use="required">
     * ```
     */
    prst?: STShapeType;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PresetGeometry2D name="CT_PresetGeometry2D">
 * ```
 */
export interface ICTPresetGeometry2DDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GeomGuideList maxOccurs="1" minOccurs="0" name="avLst" type="CT_GeomGuideList">
     * ```
     */
    avLst?: ICTGeomGuideListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ShapeType name="prst" type="ST_ShapeType" use="required">
     * ```
     */
    prst?: STShapeType | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_PlaceholderPml name="CT_PlaceholderPml">
 * ```
 */
export interface ICTPlaceholderPml {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModify;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_PlaceholderType default="obj" name="type" type="ST_PlaceholderType" use="optional">
     * ```
     */
    type?: STPlaceholderType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_DirectionPml default="horz" name="orient" type="ST_DirectionPml" use="optional">
     * ```
     */
    orient?: STDirectionPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_PlaceholderSize default="full" name="sz" type="ST_PlaceholderSize" use="optional">
     * ```
     */
    sz?: STPlaceholderSize;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="0" name="idx" type="xsd:unsignedInt" use="optional">
     * ```
     */
    idx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="hasCustomPrompt" type="xsd:boolean" use="optional">
     * ```
     */
    hasCustomPrompt?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_PlaceholderPml name="CT_PlaceholderPml">
 * ```
 */
export interface ICTPlaceholderPmlDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListModify maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListModify">
     * ```
     */
    extLst?: ICTExtensionListModifyDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_PlaceholderType default="obj" name="type" type="ST_PlaceholderType" use="optional">
     * ```
     */
    type?: STPlaceholderType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_DirectionPml default="horz" name="orient" type="ST_DirectionPml" use="optional">
     * ```
     */
    orient?: STDirectionPml | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_PlaceholderSize default="full" name="sz" type="ST_PlaceholderSize" use="optional">
     * ```
     */
    sz?: STPlaceholderSize | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="0" name="idx" type="xsd:unsignedInt" use="optional">
     * ```
     */
    idx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="hasCustomPrompt" type="xsd:boolean" use="optional">
     * ```
     */
    hasCustomPrompt?: boolean | null;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_Media name="EG_Media">
 * ```
 */
export interface IEGMedia {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AudioCD name="audioCd" type="CT_AudioCD">
     * ```
     */
    audioCd?: ICTAudioCD;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EmbeddedWAVAudioFile name="wavAudioFile" type="CT_EmbeddedWAVAudioFile">
     * ```
     */
    wavAudioFile?: ICTEmbeddedWAVAudioFile;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AudioFile name="audioFile" type="CT_AudioFile">
     * ```
     */
    audioFile?: ICTAudioFile;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_VideoFile name="videoFile" type="CT_VideoFile">
     * ```
     */
    videoFile?: ICTVideoFile;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_QuickTimeFile name="quickTimeFile" type="CT_QuickTimeFile">
     * ```
     */
    quickTimeFile?: ICTQuickTimeFile;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_Media name="EG_Media">
 * ```
 */
export interface IEGMediaDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AudioCD name="audioCd" type="CT_AudioCD">
     * ```
     */
    audioCd?: ICTAudioCDDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EmbeddedWAVAudioFile name="wavAudioFile" type="CT_EmbeddedWAVAudioFile">
     * ```
     */
    wavAudioFile?: ICTEmbeddedWAVAudioFileDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AudioFile name="audioFile" type="CT_AudioFile">
     * ```
     */
    audioFile?: ICTAudioFileDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_VideoFile name="videoFile" type="CT_VideoFile">
     * ```
     */
    videoFile?: ICTVideoFileDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_QuickTimeFile name="quickTimeFile" type="CT_QuickTimeFile">
     * ```
     */
    quickTimeFile?: ICTQuickTimeFileDelta | null;
}

export interface ILockingDelta {
    noAdjustHandles?: boolean;
    noChangeArrowheads?: boolean;
    noChangeAspect?: boolean;
    noChangeShapeType?: boolean;
    noCrop?: boolean;
    noDrilldown?: boolean;
    noEditPoints?: boolean;
    noGrp?: boolean;
    noMove?: boolean;
    noResize?: boolean;
    noRot?: boolean;
    noSelect?: boolean;
    noTextEdit?: boolean;
    noUngrp?: boolean;
}

export interface IShapeNonVisualPropertiesDelta {
    txBox?: boolean;
    isEquationShape?: boolean | null;
}

export interface IConnectorNonVisualPropertiesDelta {
    stCxn?: ICTConnectionDmlADelta | null;
    endCxn?: ICTConnectionDmlADelta | null;
}

export interface IPictureNonVisualPropertiesDelta {
    preferRelativeResize?: boolean;
}

/** 附件信息 */
export interface IAttachmentInfo {
    /** 附件实例 ID */
    id?: string;
    /** 附件上传cos后的唯一key */
    objKey?: string;
    /** 附件的名字 */
    name?: string;
    /** 附件大小 */
    size?: number;
    /** 以文件后缀名区分的附件类型（docx、pptx等） */
    type?: string;
    /** 附件类型（video、audio、doc等） */
    classify?: STAttachmentClassify;
    /** 附件展示形态 */
    showType?: STAttachmentShowType;
    /** 附件封面信息 */
    posterInfo?: IPosterInfo;
}

/** 插件 */
export interface IAddon {
    /** 插件展示形态 */
    displayMode?: STAddonMode;
    /** 插件视频地址 */
    embedUrl?: string;
    /** 插件悬浮菜单的 ID */
    iframeId?: string;
    /** 视频封面 */
    originUrl?: string;
    /** 插件标题（可能存在） */
    title?: string;
    /** 公式插件 ID */
    aid?: string;
    /** 类型，用于区分公式插件 */
    type?: string;
    /** 自定义数据 */
    extraData?: string;
    /** 插件详细信息：插件组件需要的信息 */
    docElement?: IAddonDocElement;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_FontReferenceDmlA name="CT_FontReferenceDmlA">
 * ```
 */
export interface ICTFontReferenceDmlA {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoice;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_FontCollectionIndex name="idx" type="ST_FontCollectionIndex" use="required">
     * ```
     */
    idx?: STFontCollectionIndex;
    scrgbClr?: ICTScRgbColor;
    srgbClr?: ICTSRgbColor;
    hslClr?: ICTHslColor;
    sysClr?: ICTSystemColor;
    schemeClr?: ICTSchemeColor;
    prstClr?: ICTPresetColor;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_FontReferenceDmlA name="CT_FontReferenceDmlA">
 * ```
 */
export interface ICTFontReferenceDmlADelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoiceDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_FontCollectionIndex name="idx" type="ST_FontCollectionIndex" use="required">
     * ```
     */
    idx?: STFontCollectionIndex | null;
    scrgbClr?: ICTScRgbColorDelta | null;
    srgbClr?: ICTSRgbColorDelta | null;
    hslClr?: ICTHslColorDelta | null;
    sysClr?: ICTSystemColorDelta | null;
    schemeClr?: ICTSchemeColorDelta | null;
    prstClr?: ICTPresetColorDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GraphicalObject name="CT_GraphicalObject">
 * ```
 */
export interface ICTGraphicalObject {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GraphicalObjectData name="graphicData" type="CT_GraphicalObjectData">
     * ```
     */
    graphicData?: ICTGraphicalObjectData;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GraphicalObject name="CT_GraphicalObject">
 * ```
 */
export interface ICTGraphicalObjectDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GraphicalObjectData name="graphicData" type="CT_GraphicalObjectData">
     * ```
     */
    graphicData?: ICTGraphicalObjectDataDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_InkInkML name="CT_InkInkML">
 * ```
 */
export interface ICTInkInkML {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Definitions maxOccurs="unbounded" minOccurs="0" name="definitions" type="CT_Definitions">
     * ```
     */
    definitions?: ICTDefinitions[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Context maxOccurs="unbounded" minOccurs="0" name="context" type="CT_Context">
     * ```
     */
    context?: ICTContext[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Trace maxOccurs="unbounded" minOccurs="1" name="trace" type="CT_Trace">
     * ```
     */
    trace?: ICTTrace[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceGroup maxOccurs="unbounded" minOccurs="0" name="traceGroup" type="CT_TraceGroup">
     * ```
     */
    traceGroup?: ICTTraceGroup[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceView maxOccurs="unbounded" minOccurs="0" name="traceView" type="CT_TraceView">
     * ```
     */
    traceView?: ICTTraceView[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Annotation maxOccurs="unbounded" minOccurs="0" name="annotation" type="CT_Annotation">
     * ```
     */
    annotation?: ICTAnnotation[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_AnnotationXML maxOccurs="unbounded" minOccurs="0" name="annotationXML" type="CT_AnnotationXML">
     * ```
     */
    annotationXml?: ICTAnnotationXML[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="documentID" type="xsd:anyURI">
     * ```
     */
    documentId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:inkMLId name="inkMLId" ref="td:inkMLId" type="s:ST_String">
     * ```
     */
    inkMlId?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_InkInkML name="CT_InkInkML">
 * ```
 */
export interface ICTInkInkMLDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Definitions maxOccurs="unbounded" minOccurs="0" name="definitions" type="CT_Definitions">
     * ```
     */
    definitions?: ICTDefinitionsDelta[];
    definitionsOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Context maxOccurs="unbounded" minOccurs="0" name="context" type="CT_Context">
     * ```
     */
    context?: ICTContextDelta[];
    contextOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Trace maxOccurs="unbounded" minOccurs="1" name="trace" type="CT_Trace">
     * ```
     */
    trace?: ICTTraceDelta[];
    traceOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceGroup maxOccurs="unbounded" minOccurs="0" name="traceGroup" type="CT_TraceGroup">
     * ```
     */
    traceGroup?: ICTTraceGroupDelta[];
    traceGroupOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceView maxOccurs="unbounded" minOccurs="0" name="traceView" type="CT_TraceView">
     * ```
     */
    traceView?: ICTTraceViewDelta[];
    traceViewOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Annotation maxOccurs="unbounded" minOccurs="0" name="annotation" type="CT_Annotation">
     * ```
     */
    annotation?: ICTAnnotationDelta[];
    annotationOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_AnnotationXML maxOccurs="unbounded" minOccurs="0" name="annotationXML" type="CT_AnnotationXML">
     * ```
     */
    annotationXml?: ICTAnnotationXMLDelta[];
    annotationXmlOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="documentID" type="xsd:anyURI">
     * ```
     */
    documentId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:inkMLId name="inkMLId" ref="td:inkMLId" type="s:ST_String">
     * ```
     */
    inkMlId?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_OleObjectPml name="CT_OleObjectPml">
 * ```
 */
export interface ICTOleObjectPml {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OleObjectEmbed name="embed" type="CT_OleObjectEmbed">
     * ```
     */
    embed?: ICTOleObjectEmbed;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OleObjectLink name="link" type="CT_OleObjectLink">
     * ```
     */
    link?: ICTOleObjectLink;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_PicturePml maxOccurs="1" minOccurs="0" name="pic" type="CT_PicturePml">
     * ```
     */
    pic?: ICTPicturePml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="spid" type="a:ST_ShapeID" use="optional">
     * ```
     */
    spid?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="showAsIcon" type="xsd:boolean" use="optional">
     * ```
     */
    showAsIcon?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="optional">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="imgW" type="a:ST_PositiveCoordinate32" use="optional">
     * ```
     */
    imgW?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="imgH" type="a:ST_PositiveCoordinate32" use="optional">
     * ```
     */
    imgH?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="progId" type="xsd:string" use="optional">
     * ```
     */
    progId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="oleObjId" skipIO="true" type="xsd:string" use="optional">
     * ```
     */
    oleObjId?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_OleObjectPml name="CT_OleObjectPml">
 * ```
 */
export interface ICTOleObjectPmlDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OleObjectEmbed name="embed" type="CT_OleObjectEmbed">
     * ```
     */
    embed?: ICTOleObjectEmbedDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OleObjectLink name="link" type="CT_OleObjectLink">
     * ```
     */
    link?: ICTOleObjectLinkDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_PicturePml maxOccurs="1" minOccurs="0" name="pic" type="CT_PicturePml">
     * ```
     */
    pic?: ICTPicturePmlDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="spid" type="a:ST_ShapeID" use="optional">
     * ```
     */
    spid?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="showAsIcon" type="xsd:boolean" use="optional">
     * ```
     */
    showAsIcon?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="optional">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="imgW" type="a:ST_PositiveCoordinate32" use="optional">
     * ```
     */
    imgW?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="imgH" type="a:ST_PositiveCoordinate32" use="optional">
     * ```
     */
    imgH?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="progId" type="xsd:string" use="optional">
     * ```
     */
    progId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="oleObjId" skipIO="true" type="xsd:string" use="optional">
     * ```
     */
    oleObjId?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_Model3D name="CT_Model3D">
 * ```
 */
export interface ICTModel3D {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="1" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_Model3DCamera maxOccurs="1" minOccurs="1" name="camera" type="CT_Model3DCamera">
     * ```
     */
    camera?: ICTModel3DCamera;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_Model3DTransform maxOccurs="1" minOccurs="1" name="trans" type="CT_Model3DTransform">
     * ```
     */
    trans?: ICTModel3DTransform;
    /**
     * ```xml
     * <xsd:element typeRedirected=msa1611:CT_PictureAttributionSourceURL maxOccurs="1" minOccurs="0" name="attrSrcUrl" type="a1611:CT_PictureAttributionSourceURL">
     * ```
     */
    attrSrcUrl?: ICTPictureAttributionSourceURL;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_Model3DRaster maxOccurs="1" minOccurs="0" name="raster" type="CT_Model3DRaster">
     * ```
     */
    raster?: ICTModel3DRaster;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_ObjectViewport name="objViewport" type="CT_ObjectViewport">
     * ```
     */
    objViewport?: ICTObjectViewport;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_WindowViewport name="winViewport" type="CT_WindowViewport">
     * ```
     */
    winViewport?: ICTWindowViewport;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_AmbientLight maxOccurs="1" minOccurs="0" name="ambientLight" type="CT_AmbientLight">
     * ```
     */
    ambientLight?: ICTAmbientLight;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_PointLight name="ptLight" type="CT_PointLight">
     * ```
     */
    ptLight?: ICTPointLight[];
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_SpotLight name="spotLight" type="CT_SpotLight">
     * ```
     */
    spotLight?: ICTSpotLight[];
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_DirectionalLight name="dirLight" type="CT_DirectionalLight">
     * ```
     */
    dirLight?: ICTDirectionalLight[];
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_UnknownLight name="unkLight" type="CT_UnknownLight">
     * ```
     */
    unkLight?: ICTUnknownLight[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:embed default="" name="embed" ref="r:embed" type="ST_RelationshipId" use="optional">
     * ```
     */
    embed?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:link default="" name="link" ref="r:link" type="ST_RelationshipId" use="optional">
     * ```
     */
    link?: string;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_Model3D name="CT_Model3D">
 * ```
 */
export interface ICTModel3DDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="1" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_Model3DCamera maxOccurs="1" minOccurs="1" name="camera" type="CT_Model3DCamera">
     * ```
     */
    camera?: ICTModel3DCameraDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_Model3DTransform maxOccurs="1" minOccurs="1" name="trans" type="CT_Model3DTransform">
     * ```
     */
    trans?: ICTModel3DTransformDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msa1611:CT_PictureAttributionSourceURL maxOccurs="1" minOccurs="0" name="attrSrcUrl" type="a1611:CT_PictureAttributionSourceURL">
     * ```
     */
    attrSrcUrl?: ICTPictureAttributionSourceURLDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_Model3DRaster maxOccurs="1" minOccurs="0" name="raster" type="CT_Model3DRaster">
     * ```
     */
    raster?: ICTModel3DRasterDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_ObjectViewport name="objViewport" type="CT_ObjectViewport">
     * ```
     */
    objViewport?: ICTObjectViewportDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_WindowViewport name="winViewport" type="CT_WindowViewport">
     * ```
     */
    winViewport?: ICTWindowViewportDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_AmbientLight maxOccurs="1" minOccurs="0" name="ambientLight" type="CT_AmbientLight">
     * ```
     */
    ambientLight?: ICTAmbientLightDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_PointLight name="ptLight" type="CT_PointLight">
     * ```
     */
    ptLight?: ICTPointLightDelta[];
    ptLightOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_SpotLight name="spotLight" type="CT_SpotLight">
     * ```
     */
    spotLight?: ICTSpotLightDelta[];
    spotLightOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_DirectionalLight name="dirLight" type="CT_DirectionalLight">
     * ```
     */
    dirLight?: ICTDirectionalLightDelta[];
    dirLightOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_UnknownLight name="unkLight" type="CT_UnknownLight">
     * ```
     */
    unkLight?: ICTUnknownLight[];
    unkLightOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:embed default="" name="embed" ref="r:embed" type="ST_RelationshipId" use="optional">
     * ```
     */
    embed?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:link default="" name="link" ref="r:link" type="ST_RelationshipId" use="optional">
     * ```
     */
    link?: string | null;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_LineFillProperties name="EG_LineFillProperties">
 * ```
 */
export interface IEGLineFillProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_LineFillProperties name="EG_LineFillProperties">
 * ```
 */
export interface IEGLineFillPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean | null;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_LineDashProperties name="EG_LineDashProperties">
 * ```
 */
export interface IEGLineDashProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PresetLineDashProperties maxOccurs="1" minOccurs="1" name="prstDash" type="CT_PresetLineDashProperties">
     * ```
     */
    prstDash?: ICTPresetLineDashProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_DashStopList maxOccurs="1" minOccurs="1" name="custDash" type="CT_DashStopList">
     * ```
     */
    custDash?: ICTDashStopList;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_LineDashProperties name="EG_LineDashProperties">
 * ```
 */
export interface IEGLineDashPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PresetLineDashProperties maxOccurs="1" minOccurs="1" name="prstDash" type="CT_PresetLineDashProperties">
     * ```
     */
    prstDash?: ICTPresetLineDashPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_DashStopList maxOccurs="1" minOccurs="1" name="custDash" type="CT_DashStopList">
     * ```
     */
    custDash?: ICTDashStopListDelta | null;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_LineJoinProperties name="EG_LineJoinProperties">
 * ```
 */
export interface IEGLineJoinProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineJoinMiterProperties maxOccurs="1" minOccurs="1" name="miter" type="CT_LineJoinMiterProperties">
     * ```
     */
    miter?: ICTLineJoinMiterProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineJoinRound convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="round" type="CT_LineJoinRound">
     * ```
     */
    round?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineJoinBevel convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="bevel" type="CT_LineJoinBevel">
     * ```
     */
    bevel?: boolean;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_LineJoinProperties name="EG_LineJoinProperties">
 * ```
 */
export interface IEGLineJoinPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineJoinMiterProperties maxOccurs="1" minOccurs="1" name="miter" type="CT_LineJoinMiterProperties">
     * ```
     */
    miter?: ICTLineJoinMiterPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineJoinRound convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="round" type="CT_LineJoinRound">
     * ```
     */
    round?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineJoinBevel convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="bevel" type="CT_LineJoinBevel">
     * ```
     */
    bevel?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_LineEndProperties name="CT_LineEndProperties">
 * ```
 */
export interface ICTLineEndProperties {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_LineEndType default="none" name="type" type="ST_LineEndType" use="optional">
     * ```
     */
    type?: STLineEndType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_LineEndWidth name="w" type="ST_LineEndWidth" use="optional">
     * ```
     */
    w?: STLineEndWidth;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_LineEndLength name="len" type="ST_LineEndLength" use="optional">
     * ```
     */
    len?: STLineEndLength;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_LineEndProperties name="CT_LineEndProperties">
 * ```
 */
export interface ICTLineEndPropertiesDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_LineEndType default="none" name="type" type="ST_LineEndType" use="optional">
     * ```
     */
    type?: STLineEndType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_LineEndWidth name="w" type="ST_LineEndWidth" use="optional">
     * ```
     */
    w?: STLineEndWidth | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_LineEndLength name="len" type="ST_LineEndLength" use="optional">
     * ```
     */
    len?: STLineEndLength | null;
}

export enum STLineCap {
    PBDEFAULT = 0,
    rnd = 1,
    sq = 2,
    flat = 3,
}

export enum STCompoundLine {
    PBDEFAULT = 0,
    sng = 1,
    dbl = 2,
    thickThin = 3,
    thinThick = 4,
    tri = 5,
}

export enum STPenAlignment {
    PBDEFAULT = 0,
    ctr = 1,
    in = 2,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Bevel name="CT_Bevel">
 * ```
 */
export interface ICTBevel {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="76200" name="w" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    w?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="76200" name="h" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    h?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_BevelPresetType default="circle" name="prst" type="ST_BevelPresetType" use="optional">
     * ```
     */
    prst?: STBevelPresetType;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Bevel name="CT_Bevel">
 * ```
 */
export interface ICTBevelDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="76200" name="w" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    w?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="76200" name="h" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    h?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_BevelPresetType default="circle" name="prst" type="ST_BevelPresetType" use="optional">
     * ```
     */
    prst?: STBevelPresetType | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_LightRig name="CT_LightRig">
 * ```
 */
export interface ICTLightRig {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SphereCoords maxOccurs="1" minOccurs="0" name="rot" type="CT_SphereCoords">
     * ```
     */
    rot?: ICTSphereCoords;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_LightRigType name="rig" type="ST_LightRigType" use="required">
     * ```
     */
    rig?: STLightRigType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_LightRigDirection name="dir" type="ST_LightRigDirection" use="required">
     * ```
     */
    dir?: STLightRigDirection;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_LightRig name="CT_LightRig">
 * ```
 */
export interface ICTLightRigDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SphereCoords maxOccurs="1" minOccurs="0" name="rot" type="CT_SphereCoords">
     * ```
     */
    rot?: ICTSphereCoordsDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_LightRigType name="rig" type="ST_LightRigType" use="required">
     * ```
     */
    rig?: STLightRigType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_LightRigDirection name="dir" type="ST_LightRigDirection" use="required">
     * ```
     */
    dir?: STLightRigDirection | null;
}

export enum STPresetMaterialType {
    PBDEFAULT = 0,
    legacyMatte = 1,
    legacyPlastic = 2,
    legacyMetal = 3,
    legacyWireframe = 4,
    matte = 5,
    plastic = 6,
    metal = 7,
    warmMatte = 8,
    translucentPowder = 9,
    powder = 10,
    dkEdge = 11,
    softEdge = 12,
    clear = 13,
    flat = 14,
    softmetal = 15,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GradientStopList name="CT_GradientStopList">
 * ```
 */
export interface ICTGradientStopList {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientStopDmlA maxOccurs="unbounded" minOccurs="2" name="gs" type="CT_GradientStopDmlA">
     * ```
     */
    gs?: ICTGradientStopDmlA[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GradientStopList name="CT_GradientStopList">
 * ```
 */
export interface ICTGradientStopListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientStopDmlA maxOccurs="unbounded" minOccurs="2" name="gs" type="CT_GradientStopDmlA">
     * ```
     */
    gs?: ICTGradientStopDmlADelta[];
    gsOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_ShadeProperties name="EG_ShadeProperties">
 * ```
 */
export interface IEGShadeProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LinearShadeProperties maxOccurs="1" minOccurs="1" name="lin" type="CT_LinearShadeProperties">
     * ```
     */
    lin?: ICTLinearShadeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PathShadeProperties maxOccurs="1" minOccurs="1" name="path" type="CT_PathShadeProperties">
     * ```
     */
    path?: ICTPathShadeProperties;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_ShadeProperties name="EG_ShadeProperties">
 * ```
 */
export interface IEGShadePropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LinearShadeProperties maxOccurs="1" minOccurs="1" name="lin" type="CT_LinearShadeProperties">
     * ```
     */
    lin?: ICTLinearShadePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PathShadeProperties maxOccurs="1" minOccurs="1" name="path" type="CT_PathShadeProperties">
     * ```
     */
    path?: ICTPathShadePropertiesDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_RelativeRect name="CT_RelativeRect">
 * ```
 */
export interface ICTRelativeRect {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="0%" name="l" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    l?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="0%" name="t" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    t?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="0%" name="r" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    r?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="0%" name="b" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    b?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_RelativeRect name="CT_RelativeRect">
 * ```
 */
export interface ICTRelativeRectDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="0%" name="l" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    l?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="0%" name="t" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    t?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="0%" name="r" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    r?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="0%" name="b" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    b?: number | null;
}

export enum STTileFlipMode {
    PBDEFAULT = 0,
    none = 1,
    x = 2,
    y = 3,
    xy = 4,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Blip name="CT_Blip">
 * ```
 */
export interface ICTBlip {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaBiLevelEffect maxOccurs="1" minOccurs="1" name="alphaBiLevel" type="CT_AlphaBiLevelEffect">
     * ```
     */
    alphaBiLevel?: ICTAlphaBiLevelEffect[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaInverseEffect maxOccurs="1" minOccurs="1" name="alphaInv" type="CT_AlphaInverseEffect">
     * ```
     */
    alphaInv?: ICTAlphaInverseEffect[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaModulateEffect maxOccurs="1" minOccurs="1" name="alphaMod" type="CT_AlphaModulateEffect">
     * ```
     */
    alphaMod?: ICTAlphaModulateEffect[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaModulateFixedEffect maxOccurs="1" minOccurs="1" name="alphaModFix" type="CT_AlphaModulateFixedEffect">
     * ```
     */
    alphaModFix?: ICTAlphaModulateFixedEffect[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaReplaceEffect maxOccurs="1" minOccurs="1" name="alphaRepl" type="CT_AlphaReplaceEffect">
     * ```
     */
    alphaRepl?: ICTAlphaReplaceEffect[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BiLevelEffect maxOccurs="1" minOccurs="1" name="biLevel" type="CT_BiLevelEffect">
     * ```
     */
    biLevel?: ICTBiLevelEffect[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlurEffect maxOccurs="1" minOccurs="1" name="blur" type="CT_BlurEffect">
     * ```
     */
    blur?: ICTBlurEffect[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorChangeEffect maxOccurs="1" minOccurs="1" name="clrChange" type="CT_ColorChangeEffect">
     * ```
     */
    clrChange?: ICTColorChangeEffect[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorReplaceEffect maxOccurs="1" minOccurs="1" name="clrRepl" type="CT_ColorReplaceEffect">
     * ```
     */
    clrRepl?: ICTColorReplaceEffect[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_DuotoneEffect maxOccurs="1" minOccurs="1" name="duotone" type="CT_DuotoneEffect">
     * ```
     */
    duotone?: ICTDuotoneEffect[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FillOverlayEffect maxOccurs="1" minOccurs="1" name="fillOverlay" type="CT_FillOverlayEffect">
     * ```
     */
    fillOverlay?: ICTFillOverlayEffect[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HSLEffect maxOccurs="1" minOccurs="1" name="hsl" type="CT_HSLEffect">
     * ```
     */
    hsl?: ICTHSLEffect[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LuminanceEffect maxOccurs="1" minOccurs="1" name="lum" type="CT_LuminanceEffect">
     * ```
     */
    lum?: ICTLuminanceEffect[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TintEffect maxOccurs="1" minOccurs="1" name="tint" type="CT_TintEffect">
     * ```
     */
    tint?: ICTTintEffect[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:embed default="" name="embed" ref="r:embed" type="ST_RelationshipId" use="optional">
     * ```
     */
    embed?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:link default="" name="link" ref="r:link" type="ST_RelationshipId" use="optional">
     * ```
     */
    link?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_BlipCompression default="none" name="cstate" type="ST_BlipCompression" use="optional">
     * ```
     */
    cstate?: STBlipCompression;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaFloorEffect convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="alphaFloor" type="CT_AlphaFloorEffect">
     * ```
     */
    alphaFloor?: boolean[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaCeilingEffect convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="alphaCeiling" type="CT_AlphaCeilingEffect">
     * ```
     */
    alphaCeiling?: boolean[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GrayscaleEffect convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grayscl" type="CT_GrayscaleEffect">
     * ```
     */
    grayscl?: boolean[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Blip name="CT_Blip">
 * ```
 */
export interface ICTBlipDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaBiLevelEffect maxOccurs="1" minOccurs="1" name="alphaBiLevel" type="CT_AlphaBiLevelEffect">
     * ```
     */
    alphaBiLevel?: ICTAlphaBiLevelEffectDelta[];
    alphaBiLevelOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaInverseEffect maxOccurs="1" minOccurs="1" name="alphaInv" type="CT_AlphaInverseEffect">
     * ```
     */
    alphaInv?: ICTAlphaInverseEffectDelta[];
    alphaInvOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaModulateEffect maxOccurs="1" minOccurs="1" name="alphaMod" type="CT_AlphaModulateEffect">
     * ```
     */
    alphaMod?: ICTAlphaModulateEffectDelta[];
    alphaModOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaModulateFixedEffect maxOccurs="1" minOccurs="1" name="alphaModFix" type="CT_AlphaModulateFixedEffect">
     * ```
     */
    alphaModFix?: ICTAlphaModulateFixedEffectDelta[];
    alphaModFixOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaReplaceEffect maxOccurs="1" minOccurs="1" name="alphaRepl" type="CT_AlphaReplaceEffect">
     * ```
     */
    alphaRepl?: ICTAlphaReplaceEffectDelta[];
    alphaReplOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BiLevelEffect maxOccurs="1" minOccurs="1" name="biLevel" type="CT_BiLevelEffect">
     * ```
     */
    biLevel?: ICTBiLevelEffectDelta[];
    biLevelOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlurEffect maxOccurs="1" minOccurs="1" name="blur" type="CT_BlurEffect">
     * ```
     */
    blur?: ICTBlurEffectDelta[];
    blurOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorChangeEffect maxOccurs="1" minOccurs="1" name="clrChange" type="CT_ColorChangeEffect">
     * ```
     */
    clrChange?: ICTColorChangeEffectDelta[];
    clrChangeOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorReplaceEffect maxOccurs="1" minOccurs="1" name="clrRepl" type="CT_ColorReplaceEffect">
     * ```
     */
    clrRepl?: ICTColorReplaceEffectDelta[];
    clrReplOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_DuotoneEffect maxOccurs="1" minOccurs="1" name="duotone" type="CT_DuotoneEffect">
     * ```
     */
    duotone?: ICTDuotoneEffectDelta[];
    duotoneOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FillOverlayEffect maxOccurs="1" minOccurs="1" name="fillOverlay" type="CT_FillOverlayEffect">
     * ```
     */
    fillOverlay?: ICTFillOverlayEffectDelta[];
    fillOverlayOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HSLEffect maxOccurs="1" minOccurs="1" name="hsl" type="CT_HSLEffect">
     * ```
     */
    hsl?: ICTHSLEffectDelta[];
    hslOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LuminanceEffect maxOccurs="1" minOccurs="1" name="lum" type="CT_LuminanceEffect">
     * ```
     */
    lum?: ICTLuminanceEffectDelta[];
    lumOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TintEffect maxOccurs="1" minOccurs="1" name="tint" type="CT_TintEffect">
     * ```
     */
    tint?: ICTTintEffectDelta[];
    tintOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:embed default="" name="embed" ref="r:embed" type="ST_RelationshipId" use="optional">
     * ```
     */
    embed?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:link default="" name="link" ref="r:link" type="ST_RelationshipId" use="optional">
     * ```
     */
    link?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_BlipCompression default="none" name="cstate" type="ST_BlipCompression" use="optional">
     * ```
     */
    cstate?: STBlipCompression | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaFloorEffect convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="alphaFloor" type="CT_AlphaFloorEffect">
     * ```
     */
    alphaFloor?: boolean[];
    alphaFloorOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaCeilingEffect convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="alphaCeiling" type="CT_AlphaCeilingEffect">
     * ```
     */
    alphaCeiling?: boolean[];
    alphaCeilingOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GrayscaleEffect convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grayscl" type="CT_GrayscaleEffect">
     * ```
     */
    grayscl?: boolean[];
    graysclOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_FillModeProperties name="EG_FillModeProperties">
 * ```
 */
export interface IEGFillModeProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TileInfoProperties maxOccurs="1" minOccurs="1" name="tile" type="CT_TileInfoProperties">
     * ```
     */
    tile?: ICTTileInfoProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StretchInfoProperties maxOccurs="1" minOccurs="1" name="stretch" type="CT_StretchInfoProperties">
     * ```
     */
    stretch?: ICTStretchInfoProperties;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_FillModeProperties name="EG_FillModeProperties">
 * ```
 */
export interface IEGFillModePropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TileInfoProperties maxOccurs="1" minOccurs="1" name="tile" type="CT_TileInfoProperties">
     * ```
     */
    tile?: ICTTileInfoPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StretchInfoProperties maxOccurs="1" minOccurs="1" name="stretch" type="CT_StretchInfoProperties">
     * ```
     */
    stretch?: ICTStretchInfoPropertiesDelta | null;
}

export enum STPresetPatternVal {
    PBDEFAULT = 0,
    pct5 = 1,
    pct10 = 2,
    pct20 = 3,
    pct25 = 4,
    pct30 = 5,
    pct40 = 6,
    pct50 = 7,
    pct60 = 8,
    pct70 = 9,
    pct75 = 10,
    pct80 = 11,
    pct90 = 12,
    horz = 13,
    vert = 14,
    ltHorz = 15,
    ltVert = 16,
    dkHorz = 17,
    dkVert = 18,
    narHorz = 19,
    narVert = 20,
    dashHorz = 21,
    dashVert = 22,
    cross = 23,
    dnDiag = 24,
    upDiag = 25,
    ltDnDiag = 26,
    ltUpDiag = 27,
    dkDnDiag = 28,
    dkUpDiag = 29,
    wdDnDiag = 30,
    wdUpDiag = 31,
    dashDnDiag = 32,
    dashUpDiag = 33,
    diagCross = 34,
    smCheck = 35,
    lgCheck = 36,
    smGrid = 37,
    lgGrid = 38,
    dotGrid = 39,
    smConfetti = 40,
    lgConfetti = 41,
    horzBrick = 42,
    diagBrick = 43,
    solidDmnd = 44,
    openDmnd = 45,
    dotDmnd = 46,
    plaid = 47,
    sphere = 48,
    weave = 49,
    divot = 50,
    shingle = 51,
    wave = 52,
    trellis = 53,
    zigZag = 54,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLCommonTimeNodeData name="CT_TLCommonTimeNodeData">
 * ```
 */
export interface ICTTLCommonTimeNodeData {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeConditionList maxOccurs="1" minOccurs="0" name="stCondLst" type="CT_TLTimeConditionList">
     * ```
     */
    stCondLst?: ICTTLTimeConditionList;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeConditionList maxOccurs="1" minOccurs="0" name="endCondLst" type="CT_TLTimeConditionList">
     * ```
     */
    endCondLst?: ICTTLTimeConditionList;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeCondition maxOccurs="1" minOccurs="0" name="endSync" type="CT_TLTimeCondition">
     * ```
     */
    endSync?: ICTTLTimeCondition;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLIterateData maxOccurs="1" minOccurs="0" name="iterate" type="CT_TLIterateData">
     * ```
     */
    iterate?: ICTTLIterateData;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TimeNodeList maxOccurs="1" minOccurs="0" name="childTnLst" type="CT_TimeNodeList">
     * ```
     */
    childTnLst?: ICTTimeNodeList;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TimeNodeList maxOccurs="1" minOccurs="0" name="subTnLst" type="CT_TimeNodeList">
     * ```
     */
    subTnLst?: ICTTimeNodeList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="ST_TLTimeNodeID" use="optional">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="presetID" type="xsd:int" use="optional">
     * ```
     */
    presetId?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLTimeNodePresetClassType name="presetClass" type="ST_TLTimeNodePresetClassType" use="optional">
     * ```
     */
    presetClass?: STTLTimeNodePresetClassType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="presetSubtype" type="xsd:int" use="optional">
     * ```
     */
    presetSubtype?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="dur" type="ST_TLTime" use="optional">
     * ```
     */
    dur?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="1000" name="repeatCount" type="ST_TLTime" use="optional">
     * ```
     */
    repeatCount?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="repeatDur" type="ST_TLTime" use="optional">
     * ```
     */
    repeatDur?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PercentageDecimal" default="100%" name="spd" type="a:ST_PercentageDmlA" use="optional">
     * ```
     */
    spd?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0%" name="accel" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    accel?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0%" name="decel" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    decel?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="autoRev" type="xsd:boolean" use="optional">
     * ```
     */
    autoRev?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLTimeNodeRestartType name="restart" type="ST_TLTimeNodeRestartType" use="optional">
     * ```
     */
    restart?: STTLTimeNodeRestartType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLTimeNodeFillType name="fill" type="ST_TLTimeNodeFillType" use="optional">
     * ```
     */
    fill?: STTLTimeNodeFillType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLTimeNodeSyncType name="syncBehavior" type="ST_TLTimeNodeSyncType" use="optional">
     * ```
     */
    syncBehavior?: STTLTimeNodeSyncType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="tmFilter" type="xsd:string" use="optional">
     * ```
     */
    tmFilter?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="evtFilter" type="xsd:string" use="optional">
     * ```
     */
    evtFilter?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="display" type="xsd:boolean" use="optional">
     * ```
     */
    display?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLTimeNodeMasterRelation name="masterRel" type="ST_TLTimeNodeMasterRelation" use="optional">
     * ```
     */
    masterRel?: STTLTimeNodeMasterRelation;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="bldLvl" type="xsd:int" use="optional">
     * ```
     */
    bldLvl?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="grpId" type="xsd:unsignedInt" use="optional">
     * ```
     */
    grpId?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="afterEffect" type="xsd:boolean" use="optional">
     * ```
     */
    afterEffect?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLTimeNodeType name="nodeType" type="ST_TLTimeNodeType" use="optional">
     * ```
     */
    nodeType?: STTLTimeNodeType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="nodePh" type="xsd:boolean" use="optional">
     * ```
     */
    nodePh?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLCommonTimeNodeData name="CT_TLCommonTimeNodeData">
 * ```
 */
export interface ICTTLCommonTimeNodeDataDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeConditionList maxOccurs="1" minOccurs="0" name="stCondLst" type="CT_TLTimeConditionList">
     * ```
     */
    stCondLst?: ICTTLTimeConditionListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeConditionList maxOccurs="1" minOccurs="0" name="endCondLst" type="CT_TLTimeConditionList">
     * ```
     */
    endCondLst?: ICTTLTimeConditionListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeCondition maxOccurs="1" minOccurs="0" name="endSync" type="CT_TLTimeCondition">
     * ```
     */
    endSync?: ICTTLTimeConditionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLIterateData maxOccurs="1" minOccurs="0" name="iterate" type="CT_TLIterateData">
     * ```
     */
    iterate?: ICTTLIterateDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TimeNodeList maxOccurs="1" minOccurs="0" name="childTnLst" type="CT_TimeNodeList">
     * ```
     */
    childTnLst?: ICTTimeNodeListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TimeNodeList maxOccurs="1" minOccurs="0" name="subTnLst" type="CT_TimeNodeList">
     * ```
     */
    subTnLst?: ICTTimeNodeListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="ST_TLTimeNodeID" use="optional">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="presetID" type="xsd:int" use="optional">
     * ```
     */
    presetId?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLTimeNodePresetClassType name="presetClass" type="ST_TLTimeNodePresetClassType" use="optional">
     * ```
     */
    presetClass?: STTLTimeNodePresetClassType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="presetSubtype" type="xsd:int" use="optional">
     * ```
     */
    presetSubtype?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="dur" type="ST_TLTime" use="optional">
     * ```
     */
    dur?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="1000" name="repeatCount" type="ST_TLTime" use="optional">
     * ```
     */
    repeatCount?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="repeatDur" type="ST_TLTime" use="optional">
     * ```
     */
    repeatDur?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PercentageDecimal" default="100%" name="spd" type="a:ST_PercentageDmlA" use="optional">
     * ```
     */
    spd?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0%" name="accel" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    accel?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0%" name="decel" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    decel?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="autoRev" type="xsd:boolean" use="optional">
     * ```
     */
    autoRev?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLTimeNodeRestartType name="restart" type="ST_TLTimeNodeRestartType" use="optional">
     * ```
     */
    restart?: STTLTimeNodeRestartType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLTimeNodeFillType name="fill" type="ST_TLTimeNodeFillType" use="optional">
     * ```
     */
    fill?: STTLTimeNodeFillType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLTimeNodeSyncType name="syncBehavior" type="ST_TLTimeNodeSyncType" use="optional">
     * ```
     */
    syncBehavior?: STTLTimeNodeSyncType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="tmFilter" type="xsd:string" use="optional">
     * ```
     */
    tmFilter?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="evtFilter" type="xsd:string" use="optional">
     * ```
     */
    evtFilter?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="display" type="xsd:boolean" use="optional">
     * ```
     */
    display?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLTimeNodeMasterRelation name="masterRel" type="ST_TLTimeNodeMasterRelation" use="optional">
     * ```
     */
    masterRel?: STTLTimeNodeMasterRelation | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="bldLvl" type="xsd:int" use="optional">
     * ```
     */
    bldLvl?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="grpId" type="xsd:unsignedInt" use="optional">
     * ```
     */
    grpId?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="afterEffect" type="xsd:boolean" use="optional">
     * ```
     */
    afterEffect?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLTimeNodeType name="nodeType" type="ST_TLTimeNodeType" use="optional">
     * ```
     */
    nodeType?: STTLTimeNodeType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="nodePh" type="xsd:boolean" use="optional">
     * ```
     */
    nodePh?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTimeConditionList name="CT_TLTimeConditionList">
 * ```
 */
export interface ICTTLTimeConditionList {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeCondition maxOccurs="unbounded" minOccurs="1" name="cond" type="CT_TLTimeCondition">
     * ```
     */
    cond?: ICTTLTimeCondition[];
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTimeConditionList name="CT_TLTimeConditionList">
 * ```
 */
export interface ICTTLTimeConditionListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeCondition maxOccurs="unbounded" minOccurs="1" name="cond" type="CT_TLTimeCondition">
     * ```
     */
    cond?: ICTTLTimeConditionDelta[];
    condOp?: IArrayOp;
}

export enum STTLPreviousActionType {
    PBDEFAULT = 0,
    none = 1,
    skipTimed = 2,
}

export enum STTLNextActionType {
    PBDEFAULT = 0,
    none = 1,
    seek = 2,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLCommonBehaviorData name="CT_TLCommonBehaviorData">
 * ```
 */
export interface ICTTLCommonBehaviorData {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonTimeNodeData maxOccurs="1" minOccurs="1" name="cTn" type="CT_TLCommonTimeNodeData">
     * ```
     */
    cTn?: ICTTLCommonTimeNodeData;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeTargetElement maxOccurs="1" minOccurs="1" name="tgtEl" type="CT_TLTimeTargetElement">
     * ```
     */
    tgtEl?: ICTTLTimeTargetElement;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLBehaviorAttributeNameList maxOccurs="1" minOccurs="0" name="attrNameLst" type="CT_TLBehaviorAttributeNameList">
     * ```
     */
    attrNameLst?: ICTTLBehaviorAttributeNameList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLBehaviorAdditiveType name="additive" type="ST_TLBehaviorAdditiveType" use="optional">
     * ```
     */
    additive?: STTLBehaviorAdditiveType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLBehaviorAccumulateType name="accumulate" type="ST_TLBehaviorAccumulateType" use="optional">
     * ```
     */
    accumulate?: STTLBehaviorAccumulateType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLBehaviorTransformType name="xfrmType" type="ST_TLBehaviorTransformType" use="optional">
     * ```
     */
    xfrmType?: STTLBehaviorTransformType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="from" type="xsd:string" use="optional">
     * ```
     */
    from?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="to" type="xsd:string" use="optional">
     * ```
     */
    to?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="by" type="xsd:string" use="optional">
     * ```
     */
    by?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="rctx" type="xsd:string" use="optional">
     * ```
     */
    rctx?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLBehaviorOverrideType name="override" type="ST_TLBehaviorOverrideType" use="optional">
     * ```
     */
    override?: STTLBehaviorOverrideType;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLCommonBehaviorData name="CT_TLCommonBehaviorData">
 * ```
 */
export interface ICTTLCommonBehaviorDataDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonTimeNodeData maxOccurs="1" minOccurs="1" name="cTn" type="CT_TLCommonTimeNodeData">
     * ```
     */
    cTn?: ICTTLCommonTimeNodeDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeTargetElement maxOccurs="1" minOccurs="1" name="tgtEl" type="CT_TLTimeTargetElement">
     * ```
     */
    tgtEl?: ICTTLTimeTargetElementDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLBehaviorAttributeNameList maxOccurs="1" minOccurs="0" name="attrNameLst" type="CT_TLBehaviorAttributeNameList">
     * ```
     */
    attrNameLst?: ICTTLBehaviorAttributeNameListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLBehaviorAdditiveType name="additive" type="ST_TLBehaviorAdditiveType" use="optional">
     * ```
     */
    additive?: STTLBehaviorAdditiveType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLBehaviorAccumulateType name="accumulate" type="ST_TLBehaviorAccumulateType" use="optional">
     * ```
     */
    accumulate?: STTLBehaviorAccumulateType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLBehaviorTransformType name="xfrmType" type="ST_TLBehaviorTransformType" use="optional">
     * ```
     */
    xfrmType?: STTLBehaviorTransformType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="from" type="xsd:string" use="optional">
     * ```
     */
    from?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="to" type="xsd:string" use="optional">
     * ```
     */
    to?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="by" type="xsd:string" use="optional">
     * ```
     */
    by?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="rctx" type="xsd:string" use="optional">
     * ```
     */
    rctx?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLBehaviorOverrideType name="override" type="ST_TLBehaviorOverrideType" use="optional">
     * ```
     */
    override?: STTLBehaviorOverrideType | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTimeAnimateValueList name="CT_TLTimeAnimateValueList">
 * ```
 */
export interface ICTTLTimeAnimateValueList {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeAnimateValue maxOccurs="unbounded" minOccurs="0" name="tav" type="CT_TLTimeAnimateValue">
     * ```
     */
    tav?: ICTTLTimeAnimateValue[];
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTimeAnimateValueList name="CT_TLTimeAnimateValueList">
 * ```
 */
export interface ICTTLTimeAnimateValueListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeAnimateValue maxOccurs="unbounded" minOccurs="0" name="tav" type="CT_TLTimeAnimateValue">
     * ```
     */
    tav?: ICTTLTimeAnimateValueDelta[];
    tavOp?: IArrayOp;
}

export enum STTLAnimateBehaviorCalcMode {
    PBDEFAULT = 0,
    discrete = 1,
    lin = 2,
    fmla = 3,
}

export enum STTLAnimateBehaviorValueType {
    PBDEFAULT = 0,
    str = 1,
    num = 2,
    clr = 3,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLByAnimateColorTransform name="CT_TLByAnimateColorTransform">
 * ```
 */
export interface ICTTLByAnimateColorTransform {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLByRgbColorTransform name="rgb" type="CT_TLByRgbColorTransform">
     * ```
     */
    rgb?: ICTTLByRgbColorTransform;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLByHslColorTransform name="hsl" type="CT_TLByHslColorTransform">
     * ```
     */
    hsl?: ICTTLByHslColorTransform;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLByAnimateColorTransform name="CT_TLByAnimateColorTransform">
 * ```
 */
export interface ICTTLByAnimateColorTransformDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLByRgbColorTransform name="rgb" type="CT_TLByRgbColorTransform">
     * ```
     */
    rgb?: ICTTLByRgbColorTransformDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLByHslColorTransform name="hsl" type="CT_TLByHslColorTransform">
     * ```
     */
    hsl?: ICTTLByHslColorTransformDelta | null;
}

export enum STTLAnimateColorSpace {
    PBDEFAULT = 0,
    rgb = 1,
    hsl = 2,
}

export enum STTLAnimateColorDirection {
    PBDEFAULT = 0,
    cw = 1,
    ccw = 2,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimVariant name="CT_TLAnimVariant">
 * ```
 */
export interface ICTTLAnimVariant {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimVariantBooleanVal name="boolVal" type="CT_TLAnimVariantBooleanVal">
     * ```
     */
    boolVal?: ICTTLAnimVariantBooleanVal;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimVariantIntegerVal name="intVal" type="CT_TLAnimVariantIntegerVal">
     * ```
     */
    intVal?: ICTTLAnimVariantIntegerVal;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimVariantFloatVal name="fltVal" type="CT_TLAnimVariantFloatVal">
     * ```
     */
    fltVal?: ICTTLAnimVariantFloatVal;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimVariantStringVal name="strVal" type="CT_TLAnimVariantStringVal">
     * ```
     */
    strVal?: ICTTLAnimVariantStringVal;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA name="clrVal" type="a:CT_ColorDmlA">
     * ```
     */
    clrVal?: ICTColorDmlA;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimVariant name="CT_TLAnimVariant">
 * ```
 */
export interface ICTTLAnimVariantDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimVariantBooleanVal name="boolVal" type="CT_TLAnimVariantBooleanVal">
     * ```
     */
    boolVal?: ICTTLAnimVariantBooleanValDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimVariantIntegerVal name="intVal" type="CT_TLAnimVariantIntegerVal">
     * ```
     */
    intVal?: ICTTLAnimVariantIntegerValDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimVariantFloatVal name="fltVal" type="CT_TLAnimVariantFloatVal">
     * ```
     */
    fltVal?: ICTTLAnimVariantFloatValDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimVariantStringVal name="strVal" type="CT_TLAnimVariantStringVal">
     * ```
     */
    strVal?: ICTTLAnimVariantStringValDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA name="clrVal" type="a:CT_ColorDmlA">
     * ```
     */
    clrVal?: ICTColorDmlADelta | null;
}

export enum STTLAnimateEffectTransition {
    PBDEFAULT = 0,
    in = 1,
    out = 2,
    none = 3,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLPoint name="CT_TLPoint">
 * ```
 */
export interface ICTTLPoint {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PercentageDecimal" name="x" type="a:ST_PercentageDmlA" use="required">
     * ```
     */
    x?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PercentageDecimal" name="y" type="a:ST_PercentageDmlA" use="required">
     * ```
     */
    y?: number;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLPoint name="CT_TLPoint">
 * ```
 */
export interface ICTTLPointDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PercentageDecimal" name="x" type="a:ST_PercentageDmlA" use="required">
     * ```
     */
    x?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PercentageDecimal" name="y" type="a:ST_PercentageDmlA" use="required">
     * ```
     */
    y?: number | null;
}

export enum STTLAnimateMotionBehaviorOrigin {
    PBDEFAULT = 0,
    parent = 1,
    layout = 2,
}

export enum STTLAnimateMotionPathEditMode {
    PBDEFAULT = 0,
    relative = 1,
    fixed = 2,
}

export enum STTLCommandType {
    PBDEFAULT = 0,
    evt = 1,
    call = 2,
    verb = 3,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLCommonMediaNodeData name="CT_TLCommonMediaNodeData">
 * ```
 */
export interface ICTTLCommonMediaNodeData {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonTimeNodeData maxOccurs="1" minOccurs="1" name="cTn" type="CT_TLCommonTimeNodeData">
     * ```
     */
    cTn?: ICTTLCommonTimeNodeData;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeTargetElement maxOccurs="1" minOccurs="1" name="tgtEl" type="CT_TLTimeTargetElement">
     * ```
     */
    tgtEl?: ICTTLTimeTargetElement;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="50%" name="vol" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    vol?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="mute" type="xsd:boolean" use="optional">
     * ```
     */
    mute?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="1" name="numSld" type="xsd:unsignedInt" use="optional">
     * ```
     */
    numSld?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="showWhenStopped" type="xsd:boolean" use="optional">
     * ```
     */
    showWhenStopped?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLCommonMediaNodeData name="CT_TLCommonMediaNodeData">
 * ```
 */
export interface ICTTLCommonMediaNodeDataDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLCommonTimeNodeData maxOccurs="1" minOccurs="1" name="cTn" type="CT_TLCommonTimeNodeData">
     * ```
     */
    cTn?: ICTTLCommonTimeNodeDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeTargetElement maxOccurs="1" minOccurs="1" name="tgtEl" type="CT_TLTimeTargetElement">
     * ```
     */
    tgtEl?: ICTTLTimeTargetElementDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="50%" name="vol" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    vol?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="mute" type="xsd:boolean" use="optional">
     * ```
     */
    mute?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="1" name="numSld" type="xsd:unsignedInt" use="optional">
     * ```
     */
    numSld?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="showWhenStopped" type="xsd:boolean" use="optional">
     * ```
     */
    showWhenStopped?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_FontCollection name="CT_FontCollection">
 * ```
 */
export interface ICTFontCollection {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFont maxOccurs="1" minOccurs="1" name="latin" type="CT_TextFont">
     * ```
     */
    latin?: ICTTextFont;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFont maxOccurs="1" minOccurs="1" name="ea" type="CT_TextFont">
     * ```
     */
    ea?: ICTTextFont;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFont maxOccurs="1" minOccurs="1" name="cs" type="CT_TextFont">
     * ```
     */
    cs?: ICTTextFont;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SupplementalFont maxOccurs="unbounded" minOccurs="0" name="font" type="CT_SupplementalFont">
     * ```
     */
    font?: ICTSupplementalFont[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_FontCollection name="CT_FontCollection">
 * ```
 */
export interface ICTFontCollectionDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFont maxOccurs="1" minOccurs="1" name="latin" type="CT_TextFont">
     * ```
     */
    latin?: ICTTextFontDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFont maxOccurs="1" minOccurs="1" name="ea" type="CT_TextFont">
     * ```
     */
    ea?: ICTTextFontDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFont maxOccurs="1" minOccurs="1" name="cs" type="CT_TextFont">
     * ```
     */
    cs?: ICTTextFontDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SupplementalFont maxOccurs="unbounded" minOccurs="0" name="font" type="CT_SupplementalFont">
     * ```
     */
    font?: ICTSupplementalFontDelta[];
    fontOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_FillStyleList name="CT_FillStyleList">
 * ```
 */
export interface ICTFillStyleList {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_FillProperties ref=dmlmain:EG_FillProperties maxOccurs="unbounded" minOccurs="3" name="EG_FillProperties" ref="EG_FillProperties">
     * ref dmlmain:EG_FillProperties
     * ```
     */
    egFillProperties?: IEGFillProperties[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_FillStyleList name="CT_FillStyleList">
 * ```
 */
export interface ICTFillStyleListDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_FillProperties ref=dmlmain:EG_FillProperties maxOccurs="unbounded" minOccurs="3" name="EG_FillProperties" ref="EG_FillProperties">
     * ref dmlmain:EG_FillProperties
     * ```
     */
    egFillProperties?: IEGFillPropertiesDelta[];
    egFillPropertiesOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_LineStyleList name="CT_LineStyleList">
 * ```
 */
export interface ICTLineStyleList {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="unbounded" minOccurs="3" name="ln" type="CT_LineProperties">
     * ```
     */
    ln?: ICTLineProperties[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_LineStyleList name="CT_LineStyleList">
 * ```
 */
export interface ICTLineStyleListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="unbounded" minOccurs="3" name="ln" type="CT_LineProperties">
     * ```
     */
    ln?: ICTLinePropertiesDelta[];
    lnOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_EffectStyleList name="CT_EffectStyleList">
 * ```
 */
export interface ICTEffectStyleList {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectStyleItem maxOccurs="unbounded" minOccurs="3" name="effectStyle" type="CT_EffectStyleItem">
     * ```
     */
    effectStyle?: ICTEffectStyleItem[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_EffectStyleList name="CT_EffectStyleList">
 * ```
 */
export interface ICTEffectStyleListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectStyleItem maxOccurs="unbounded" minOccurs="3" name="effectStyle" type="CT_EffectStyleItem">
     * ```
     */
    effectStyle?: ICTEffectStyleItemDelta[];
    effectStyleOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_BackgroundFillStyleList name="CT_BackgroundFillStyleList">
 * ```
 */
export interface ICTBackgroundFillStyleList {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_FillProperties ref=dmlmain:EG_FillProperties maxOccurs="unbounded" minOccurs="3" name="EG_FillProperties" ref="EG_FillProperties">
     * ref dmlmain:EG_FillProperties
     * ```
     */
    egFillProperties?: IEGFillProperties[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_BackgroundFillStyleList name="CT_BackgroundFillStyleList">
 * ```
 */
export interface ICTBackgroundFillStyleListDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_FillProperties ref=dmlmain:EG_FillProperties maxOccurs="unbounded" minOccurs="3" name="EG_FillProperties" ref="EG_FillProperties">
     * ref dmlmain:EG_FillProperties
     * ```
     */
    egFillProperties?: IEGFillPropertiesDelta[];
    egFillPropertiesOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextSpacingPercent name="CT_TextSpacingPercent">
 * ```
 */
export interface ICTTextSpacingPercent {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_TextSpacingPercent" name="val" type="ST_TextSpacingPercentOrPercentString" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextSpacingPercent name="CT_TextSpacingPercent">
 * ```
 */
export interface ICTTextSpacingPercentDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_TextSpacingPercent" name="val" type="ST_TextSpacingPercentOrPercentString" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextSpacingPoint name="CT_TextSpacingPoint">
 * ```
 */
export interface ICTTextSpacingPoint {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="val" type="ST_TextSpacingPoint" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextSpacingPoint name="CT_TextSpacingPoint">
 * ```
 */
export interface ICTTextSpacingPointDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="val" type="ST_TextSpacingPoint" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ScRgbColor name="CT_ScRgbColor">
 * ```
 */
export interface ICTScRgbColor {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorTransform ref=dmlmain:EG_ColorTransform maxOccurs="unbounded" minOccurs="0" name="EG_ColorTransform" ref="EG_ColorTransform">
     * ref dmlmain:EG_ColorTransform
     * ```
     */
    egColorTransform?: IEGColorTransform[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" name="r" type="ST_PercentageDmlA" use="required">
     * ```
     */
    r?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" name="g" type="ST_PercentageDmlA" use="required">
     * ```
     */
    g?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" name="b" type="ST_PercentageDmlA" use="required">
     * ```
     */
    b?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ScRgbColor name="CT_ScRgbColor">
 * ```
 */
export interface ICTScRgbColorDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorTransform ref=dmlmain:EG_ColorTransform maxOccurs="unbounded" minOccurs="0" name="EG_ColorTransform" ref="EG_ColorTransform">
     * ref dmlmain:EG_ColorTransform
     * ```
     */
    egColorTransform?: IEGColorTransformDelta[];
    egColorTransformOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" name="r" type="ST_PercentageDmlA" use="required">
     * ```
     */
    r?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" name="g" type="ST_PercentageDmlA" use="required">
     * ```
     */
    g?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" name="b" type="ST_PercentageDmlA" use="required">
     * ```
     */
    b?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_SRgbColor name="CT_SRgbColor">
 * ```
 */
export interface ICTSRgbColor {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorTransform ref=dmlmain:EG_ColorTransform maxOccurs="unbounded" minOccurs="0" name="EG_ColorTransform" ref="EG_ColorTransform">
     * ref dmlmain:EG_ColorTransform
     * ```
     */
    egColorTransform?: IEGColorTransform[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="s:ST_HexColorRGB" use="required">
     * ```
     */
    val?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_SRgbColor name="CT_SRgbColor">
 * ```
 */
export interface ICTSRgbColorDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorTransform ref=dmlmain:EG_ColorTransform maxOccurs="unbounded" minOccurs="0" name="EG_ColorTransform" ref="EG_ColorTransform">
     * ref dmlmain:EG_ColorTransform
     * ```
     */
    egColorTransform?: IEGColorTransformDelta[];
    egColorTransformOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="s:ST_HexColorRGB" use="required">
     * ```
     */
    val?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_HslColor name="CT_HslColor">
 * ```
 */
export interface ICTHslColor {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorTransform ref=dmlmain:EG_ColorTransform maxOccurs="unbounded" minOccurs="0" name="EG_ColorTransform" ref="EG_ColorTransform">
     * ref dmlmain:EG_ColorTransform
     * ```
     */
    egColorTransform?: IEGColorTransform[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="hue" type="ST_PositiveFixedAngle" use="required">
     * ```
     */
    hue?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" name="sat" type="ST_PercentageDmlA" use="required">
     * ```
     */
    sat?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" name="lum" type="ST_PercentageDmlA" use="required">
     * ```
     */
    lum?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_HslColor name="CT_HslColor">
 * ```
 */
export interface ICTHslColorDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorTransform ref=dmlmain:EG_ColorTransform maxOccurs="unbounded" minOccurs="0" name="EG_ColorTransform" ref="EG_ColorTransform">
     * ref dmlmain:EG_ColorTransform
     * ```
     */
    egColorTransform?: IEGColorTransformDelta[];
    egColorTransformOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="hue" type="ST_PositiveFixedAngle" use="required">
     * ```
     */
    hue?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" name="sat" type="ST_PercentageDmlA" use="required">
     * ```
     */
    sat?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" name="lum" type="ST_PercentageDmlA" use="required">
     * ```
     */
    lum?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_SystemColor name="CT_SystemColor">
 * ```
 */
export interface ICTSystemColor {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorTransform ref=dmlmain:EG_ColorTransform maxOccurs="unbounded" minOccurs="0" name="EG_ColorTransform" ref="EG_ColorTransform">
     * ref dmlmain:EG_ColorTransform
     * ```
     */
    egColorTransform?: IEGColorTransform[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_SystemColorVal name="val" type="ST_SystemColorVal" use="required">
     * ```
     */
    val?: STSystemColorVal;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="lastClr" type="s:ST_HexColorRGB" use="optional">
     * ```
     */
    lastClr?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_SystemColor name="CT_SystemColor">
 * ```
 */
export interface ICTSystemColorDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorTransform ref=dmlmain:EG_ColorTransform maxOccurs="unbounded" minOccurs="0" name="EG_ColorTransform" ref="EG_ColorTransform">
     * ref dmlmain:EG_ColorTransform
     * ```
     */
    egColorTransform?: IEGColorTransformDelta[];
    egColorTransformOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_SystemColorVal name="val" type="ST_SystemColorVal" use="required">
     * ```
     */
    val?: STSystemColorVal | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="lastClr" type="s:ST_HexColorRGB" use="optional">
     * ```
     */
    lastClr?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_SchemeColor name="CT_SchemeColor">
 * ```
 */
export interface ICTSchemeColor {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorTransform ref=dmlmain:EG_ColorTransform maxOccurs="unbounded" minOccurs="0" name="EG_ColorTransform" ref="EG_ColorTransform">
     * ref dmlmain:EG_ColorTransform
     * ```
     */
    egColorTransform?: IEGColorTransform[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_SchemeColorVal name="val" type="ST_SchemeColorVal" use="required">
     * ```
     */
    val?: STSchemeColorVal;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_SchemeColor name="CT_SchemeColor">
 * ```
 */
export interface ICTSchemeColorDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorTransform ref=dmlmain:EG_ColorTransform maxOccurs="unbounded" minOccurs="0" name="EG_ColorTransform" ref="EG_ColorTransform">
     * ref dmlmain:EG_ColorTransform
     * ```
     */
    egColorTransform?: IEGColorTransformDelta[];
    egColorTransformOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_SchemeColorVal name="val" type="ST_SchemeColorVal" use="required">
     * ```
     */
    val?: STSchemeColorVal | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PresetColor name="CT_PresetColor">
 * ```
 */
export interface ICTPresetColor {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorTransform ref=dmlmain:EG_ColorTransform maxOccurs="unbounded" minOccurs="0" name="EG_ColorTransform" ref="EG_ColorTransform">
     * ref dmlmain:EG_ColorTransform
     * ```
     */
    egColorTransform?: IEGColorTransform[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PresetColorVal name="val" type="ST_PresetColorVal" use="required">
     * ```
     */
    val?: STPresetColorVal;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PresetColor name="CT_PresetColor">
 * ```
 */
export interface ICTPresetColorDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorTransform ref=dmlmain:EG_ColorTransform maxOccurs="unbounded" minOccurs="0" name="EG_ColorTransform" ref="EG_ColorTransform">
     * ref dmlmain:EG_ColorTransform
     * ```
     */
    egColorTransform?: IEGColorTransformDelta[];
    egColorTransformOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PresetColorVal name="val" type="ST_PresetColorVal" use="required">
     * ```
     */
    val?: STPresetColorVal | null;
}

export enum STTextAutonumberScheme {
    PBDEFAULT = 0,
    alphaLcParenBoth = 1,
    alphaUcParenBoth = 2,
    alphaLcParenR = 3,
    alphaUcParenR = 4,
    alphaLcPeriod = 5,
    alphaUcPeriod = 6,
    arabicParenBoth = 7,
    arabicParenR = 8,
    arabicPeriod = 9,
    arabicPlain = 10,
    romanLcParenBoth = 11,
    romanUcParenBoth = 12,
    romanLcParenR = 13,
    romanUcParenR = 14,
    romanLcPeriod = 15,
    romanUcPeriod = 16,
    circleNumDbPlain = 17,
    circleNumWdBlackPlain = 18,
    circleNumWdWhitePlain = 19,
    arabicDbPeriod = 20,
    arabicDbPlain = 21,
    ea1ChsPeriod = 22,
    ea1ChsPlain = 23,
    ea1ChtPeriod = 24,
    ea1ChtPlain = 25,
    ea1JpnChsDbPeriod = 26,
    ea1JpnKorPlain = 27,
    ea1JpnKorPeriod = 28,
    arabic1Minus = 29,
    arabic2Minus = 30,
    hebrew2Minus = 31,
    thaiAlphaPeriod = 32,
    thaiAlphaParenR = 33,
    thaiAlphaParenBoth = 34,
    thaiNumPeriod = 35,
    thaiNumParenR = 36,
    thaiNumParenBoth = 37,
    hindiAlphaPeriod = 38,
    hindiNumPeriod = 39,
    hindiNumParenR = 40,
    hindiAlpha1Period = 41,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextTabStop name="CT_TextTabStop">
 * ```
 */
export interface ICTTextTabStop {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="pos" type="ST_Coordinate32" use="optional">
     * ```
     */
    pos?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextTabAlignType name="algn" type="ST_TextTabAlignType" use="optional">
     * ```
     */
    algn?: STTextTabAlignType;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextTabStop name="CT_TextTabStop">
 * ```
 */
export interface ICTTextTabStopDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="pos" type="ST_Coordinate32" use="optional">
     * ```
     */
    pos?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TextTabAlignType name="algn" type="ST_TextTabAlignType" use="optional">
     * ```
     */
    algn?: STTextTabAlignType | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_BlurEffect name="CT_BlurEffect">
 * ```
 */
export interface ICTBlurEffect {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="rad" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    rad?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="grow" type="xsd:boolean" use="optional">
     * ```
     */
    grow?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_BlurEffect name="CT_BlurEffect">
 * ```
 */
export interface ICTBlurEffectDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="rad" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    rad?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="grow" type="xsd:boolean" use="optional">
     * ```
     */
    grow?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_FillOverlayEffect name="CT_FillOverlayEffect">
 * ```
 */
export interface ICTFillOverlayEffect {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillProperties;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_BlendMode name="blend" type="ST_BlendMode" use="required">
     * ```
     */
    blend?: STBlendMode;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_FillOverlayEffect name="CT_FillOverlayEffect">
 * ```
 */
export interface ICTFillOverlayEffectDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_BlendMode name="blend" type="ST_BlendMode" use="required">
     * ```
     */
    blend?: STBlendMode | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GlowEffect name="CT_GlowEffect">
 * ```
 */
export interface ICTGlowEffect {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoice;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="rad" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    rad?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GlowEffect name="CT_GlowEffect">
 * ```
 */
export interface ICTGlowEffectDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoiceDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="rad" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    rad?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_InnerShadowEffect name="CT_InnerShadowEffect">
 * ```
 */
export interface ICTInnerShadowEffect {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoice;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="blurRad" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    blurRad?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="dist" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    dist?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="dir" type="ST_PositiveFixedAngle" use="optional">
     * ```
     */
    dir?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_InnerShadowEffect name="CT_InnerShadowEffect">
 * ```
 */
export interface ICTInnerShadowEffectDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoiceDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="blurRad" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    blurRad?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="dist" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    dist?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="dir" type="ST_PositiveFixedAngle" use="optional">
     * ```
     */
    dir?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_OuterShadowEffect name="CT_OuterShadowEffect">
 * ```
 */
export interface ICTOuterShadowEffect {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoice;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="blurRad" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    blurRad?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="dist" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    dist?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="dir" type="ST_PositiveFixedAngle" use="optional">
     * ```
     */
    dir?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="100" name="sx" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    sx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="100" name="sy" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    sy?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="kx" type="ST_FixedAngle" use="optional">
     * ```
     */
    kx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="ky" type="ST_FixedAngle" use="optional">
     * ```
     */
    ky?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_RectAlignment default="b" name="algn" type="ST_RectAlignment" use="optional">
     * ```
     */
    algn?: STRectAlignment;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="rotWithShape" type="xsd:boolean" use="optional">
     * ```
     */
    rotWithShape?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_OuterShadowEffect name="CT_OuterShadowEffect">
 * ```
 */
export interface ICTOuterShadowEffectDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoiceDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="blurRad" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    blurRad?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="dist" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    dist?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="dir" type="ST_PositiveFixedAngle" use="optional">
     * ```
     */
    dir?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="100" name="sx" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    sx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="100" name="sy" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    sy?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="kx" type="ST_FixedAngle" use="optional">
     * ```
     */
    kx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="ky" type="ST_FixedAngle" use="optional">
     * ```
     */
    ky?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_RectAlignment default="b" name="algn" type="ST_RectAlignment" use="optional">
     * ```
     */
    algn?: STRectAlignment | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="rotWithShape" type="xsd:boolean" use="optional">
     * ```
     */
    rotWithShape?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PresetShadowEffect name="CT_PresetShadowEffect">
 * ```
 */
export interface ICTPresetShadowEffect {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoice;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PresetShadowVal name="prst" type="ST_PresetShadowVal" use="required">
     * ```
     */
    prst?: STPresetShadowVal;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="dist" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    dist?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="dir" type="ST_PositiveFixedAngle" use="optional">
     * ```
     */
    dir?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PresetShadowEffect name="CT_PresetShadowEffect">
 * ```
 */
export interface ICTPresetShadowEffectDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoiceDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PresetShadowVal name="prst" type="ST_PresetShadowVal" use="required">
     * ```
     */
    prst?: STPresetShadowVal | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="dist" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    dist?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="dir" type="ST_PositiveFixedAngle" use="optional">
     * ```
     */
    dir?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ReflectionEffect name="CT_ReflectionEffect">
 * ```
 */
export interface ICTReflectionEffect {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="blurRad" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    blurRad?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositiveFixedPercentageDecimal" default="100000" name="stA" type="ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    stA?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositiveFixedPercentageDecimal" default="0" name="stPos" type="ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    stPos?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositiveFixedPercentageDecimal" default="0" name="endA" type="ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    endA?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositiveFixedPercentageDecimal" default="100000" name="endPos" type="ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    endPos?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="dist" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    dist?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="dir" type="ST_PositiveFixedAngle" use="optional">
     * ```
     */
    dir?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="5400000" name="fadeDir" type="ST_PositiveFixedAngle" use="optional">
     * ```
     */
    fadeDir?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="100" name="sx" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    sx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="100" name="sy" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    sy?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="kx" type="ST_FixedAngle" use="optional">
     * ```
     */
    kx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="ky" type="ST_FixedAngle" use="optional">
     * ```
     */
    ky?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_RectAlignment default="b" name="algn" type="ST_RectAlignment" use="optional">
     * ```
     */
    algn?: STRectAlignment;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="rotWithShape" type="xsd:boolean" use="optional">
     * ```
     */
    rotWithShape?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ReflectionEffect name="CT_ReflectionEffect">
 * ```
 */
export interface ICTReflectionEffectDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="blurRad" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    blurRad?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositiveFixedPercentageDecimal" default="100000" name="stA" type="ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    stA?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositiveFixedPercentageDecimal" default="0" name="stPos" type="ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    stPos?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositiveFixedPercentageDecimal" default="0" name="endA" type="ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    endA?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositiveFixedPercentageDecimal" default="100000" name="endPos" type="ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    endPos?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="dist" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    dist?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="dir" type="ST_PositiveFixedAngle" use="optional">
     * ```
     */
    dir?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="5400000" name="fadeDir" type="ST_PositiveFixedAngle" use="optional">
     * ```
     */
    fadeDir?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="100" name="sx" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    sx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="100" name="sy" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    sy?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="kx" type="ST_FixedAngle" use="optional">
     * ```
     */
    kx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="ky" type="ST_FixedAngle" use="optional">
     * ```
     */
    ky?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_RectAlignment default="b" name="algn" type="ST_RectAlignment" use="optional">
     * ```
     */
    algn?: STRectAlignment | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="rotWithShape" type="xsd:boolean" use="optional">
     * ```
     */
    rotWithShape?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_SoftEdgesEffect name="CT_SoftEdgesEffect">
 * ```
 */
export interface ICTSoftEdgesEffect {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="rad" type="ST_PositiveCoordinate" use="required">
     * ```
     */
    rad?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_SoftEdgesEffect name="CT_SoftEdgesEffect">
 * ```
 */
export interface ICTSoftEdgesEffectDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="rad" type="ST_PositiveCoordinate" use="required">
     * ```
     */
    rad?: number | null;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_Effect name="EG_Effect">
 * ```
 */
export interface IEGEffect {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="cont" type="CT_EffectContainer">
     * ```
     */
    cont?: ICTEffectContainer;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectReference maxOccurs="1" minOccurs="1" name="effect" type="CT_EffectReference">
     * ```
     */
    effect?: ICTEffectReference;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaBiLevelEffect maxOccurs="1" minOccurs="1" name="alphaBiLevel" type="CT_AlphaBiLevelEffect">
     * ```
     */
    alphaBiLevel?: ICTAlphaBiLevelEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaInverseEffect maxOccurs="1" minOccurs="1" name="alphaInv" type="CT_AlphaInverseEffect">
     * ```
     */
    alphaInv?: ICTAlphaInverseEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaModulateEffect maxOccurs="1" minOccurs="1" name="alphaMod" type="CT_AlphaModulateEffect">
     * ```
     */
    alphaMod?: ICTAlphaModulateEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaModulateFixedEffect maxOccurs="1" minOccurs="1" name="alphaModFix" type="CT_AlphaModulateFixedEffect">
     * ```
     */
    alphaModFix?: ICTAlphaModulateFixedEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaOutsetEffect maxOccurs="1" minOccurs="1" name="alphaOutset" type="CT_AlphaOutsetEffect">
     * ```
     */
    alphaOutset?: ICTAlphaOutsetEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaReplaceEffect maxOccurs="1" minOccurs="1" name="alphaRepl" type="CT_AlphaReplaceEffect">
     * ```
     */
    alphaRepl?: ICTAlphaReplaceEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BiLevelEffect maxOccurs="1" minOccurs="1" name="biLevel" type="CT_BiLevelEffect">
     * ```
     */
    biLevel?: ICTBiLevelEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlendEffect maxOccurs="1" minOccurs="1" name="blend" type="CT_BlendEffect">
     * ```
     */
    blend?: ICTBlendEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlurEffect maxOccurs="1" minOccurs="1" name="blur" type="CT_BlurEffect">
     * ```
     */
    blur?: ICTBlurEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorChangeEffect maxOccurs="1" minOccurs="1" name="clrChange" type="CT_ColorChangeEffect">
     * ```
     */
    clrChange?: ICTColorChangeEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorReplaceEffect maxOccurs="1" minOccurs="1" name="clrRepl" type="CT_ColorReplaceEffect">
     * ```
     */
    clrRepl?: ICTColorReplaceEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_DuotoneEffect maxOccurs="1" minOccurs="1" name="duotone" type="CT_DuotoneEffect">
     * ```
     */
    duotone?: ICTDuotoneEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FillEffect maxOccurs="1" minOccurs="1" name="fill" type="CT_FillEffect">
     * ```
     */
    fill?: ICTFillEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FillOverlayEffect maxOccurs="1" minOccurs="1" name="fillOverlay" type="CT_FillOverlayEffect">
     * ```
     */
    fillOverlay?: ICTFillOverlayEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GlowEffect maxOccurs="1" minOccurs="1" name="glow" type="CT_GlowEffect">
     * ```
     */
    glow?: ICTGlowEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HSLEffect maxOccurs="1" minOccurs="1" name="hsl" type="CT_HSLEffect">
     * ```
     */
    hsl?: ICTHSLEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_InnerShadowEffect maxOccurs="1" minOccurs="1" name="innerShdw" type="CT_InnerShadowEffect">
     * ```
     */
    innerShdw?: ICTInnerShadowEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LuminanceEffect maxOccurs="1" minOccurs="1" name="lum" type="CT_LuminanceEffect">
     * ```
     */
    lum?: ICTLuminanceEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OuterShadowEffect maxOccurs="1" minOccurs="1" name="outerShdw" type="CT_OuterShadowEffect">
     * ```
     */
    outerShdw?: ICTOuterShadowEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PresetShadowEffect maxOccurs="1" minOccurs="1" name="prstShdw" type="CT_PresetShadowEffect">
     * ```
     */
    prstShdw?: ICTPresetShadowEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ReflectionEffect maxOccurs="1" minOccurs="1" name="reflection" type="CT_ReflectionEffect">
     * ```
     */
    reflection?: ICTReflectionEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_RelativeOffsetEffect maxOccurs="1" minOccurs="1" name="relOff" type="CT_RelativeOffsetEffect">
     * ```
     */
    relOff?: ICTRelativeOffsetEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SoftEdgesEffect maxOccurs="1" minOccurs="1" name="softEdge" type="CT_SoftEdgesEffect">
     * ```
     */
    softEdge?: ICTSoftEdgesEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TintEffect maxOccurs="1" minOccurs="1" name="tint" type="CT_TintEffect">
     * ```
     */
    tint?: ICTTintEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TransformEffect maxOccurs="1" minOccurs="1" name="xfrm" type="CT_TransformEffect">
     * ```
     */
    xfrm?: ICTTransformEffect;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaCeilingEffect convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="alphaCeiling" type="CT_AlphaCeilingEffect">
     * ```
     */
    alphaCeiling?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GrayscaleEffect convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grayscl" type="CT_GrayscaleEffect">
     * ```
     */
    grayscl?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaFloorEffect convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="alphaFloor" type="CT_AlphaFloorEffect">
     * ```
     */
    alphaFloor?: boolean;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_Effect name="EG_Effect">
 * ```
 */
export interface IEGEffectDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="cont" type="CT_EffectContainer">
     * ```
     */
    cont?: ICTEffectContainerDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectReference maxOccurs="1" minOccurs="1" name="effect" type="CT_EffectReference">
     * ```
     */
    effect?: ICTEffectReferenceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaBiLevelEffect maxOccurs="1" minOccurs="1" name="alphaBiLevel" type="CT_AlphaBiLevelEffect">
     * ```
     */
    alphaBiLevel?: ICTAlphaBiLevelEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaInverseEffect maxOccurs="1" minOccurs="1" name="alphaInv" type="CT_AlphaInverseEffect">
     * ```
     */
    alphaInv?: ICTAlphaInverseEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaModulateEffect maxOccurs="1" minOccurs="1" name="alphaMod" type="CT_AlphaModulateEffect">
     * ```
     */
    alphaMod?: ICTAlphaModulateEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaModulateFixedEffect maxOccurs="1" minOccurs="1" name="alphaModFix" type="CT_AlphaModulateFixedEffect">
     * ```
     */
    alphaModFix?: ICTAlphaModulateFixedEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaOutsetEffect maxOccurs="1" minOccurs="1" name="alphaOutset" type="CT_AlphaOutsetEffect">
     * ```
     */
    alphaOutset?: ICTAlphaOutsetEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaReplaceEffect maxOccurs="1" minOccurs="1" name="alphaRepl" type="CT_AlphaReplaceEffect">
     * ```
     */
    alphaRepl?: ICTAlphaReplaceEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BiLevelEffect maxOccurs="1" minOccurs="1" name="biLevel" type="CT_BiLevelEffect">
     * ```
     */
    biLevel?: ICTBiLevelEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlendEffect maxOccurs="1" minOccurs="1" name="blend" type="CT_BlendEffect">
     * ```
     */
    blend?: ICTBlendEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlurEffect maxOccurs="1" minOccurs="1" name="blur" type="CT_BlurEffect">
     * ```
     */
    blur?: ICTBlurEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorChangeEffect maxOccurs="1" minOccurs="1" name="clrChange" type="CT_ColorChangeEffect">
     * ```
     */
    clrChange?: ICTColorChangeEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorReplaceEffect maxOccurs="1" minOccurs="1" name="clrRepl" type="CT_ColorReplaceEffect">
     * ```
     */
    clrRepl?: ICTColorReplaceEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_DuotoneEffect maxOccurs="1" minOccurs="1" name="duotone" type="CT_DuotoneEffect">
     * ```
     */
    duotone?: ICTDuotoneEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FillEffect maxOccurs="1" minOccurs="1" name="fill" type="CT_FillEffect">
     * ```
     */
    fill?: ICTFillEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FillOverlayEffect maxOccurs="1" minOccurs="1" name="fillOverlay" type="CT_FillOverlayEffect">
     * ```
     */
    fillOverlay?: ICTFillOverlayEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GlowEffect maxOccurs="1" minOccurs="1" name="glow" type="CT_GlowEffect">
     * ```
     */
    glow?: ICTGlowEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HSLEffect maxOccurs="1" minOccurs="1" name="hsl" type="CT_HSLEffect">
     * ```
     */
    hsl?: ICTHSLEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_InnerShadowEffect maxOccurs="1" minOccurs="1" name="innerShdw" type="CT_InnerShadowEffect">
     * ```
     */
    innerShdw?: ICTInnerShadowEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LuminanceEffect maxOccurs="1" minOccurs="1" name="lum" type="CT_LuminanceEffect">
     * ```
     */
    lum?: ICTLuminanceEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OuterShadowEffect maxOccurs="1" minOccurs="1" name="outerShdw" type="CT_OuterShadowEffect">
     * ```
     */
    outerShdw?: ICTOuterShadowEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PresetShadowEffect maxOccurs="1" minOccurs="1" name="prstShdw" type="CT_PresetShadowEffect">
     * ```
     */
    prstShdw?: ICTPresetShadowEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ReflectionEffect maxOccurs="1" minOccurs="1" name="reflection" type="CT_ReflectionEffect">
     * ```
     */
    reflection?: ICTReflectionEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_RelativeOffsetEffect maxOccurs="1" minOccurs="1" name="relOff" type="CT_RelativeOffsetEffect">
     * ```
     */
    relOff?: ICTRelativeOffsetEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SoftEdgesEffect maxOccurs="1" minOccurs="1" name="softEdge" type="CT_SoftEdgesEffect">
     * ```
     */
    softEdge?: ICTSoftEdgesEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TintEffect maxOccurs="1" minOccurs="1" name="tint" type="CT_TintEffect">
     * ```
     */
    tint?: ICTTintEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TransformEffect maxOccurs="1" minOccurs="1" name="xfrm" type="CT_TransformEffect">
     * ```
     */
    xfrm?: ICTTransformEffectDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaCeilingEffect convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="alphaCeiling" type="CT_AlphaCeilingEffect">
     * ```
     */
    alphaCeiling?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GrayscaleEffect convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grayscl" type="CT_GrayscaleEffect">
     * ```
     */
    grayscl?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AlphaFloorEffect convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="alphaFloor" type="CT_AlphaFloorEffect">
     * ```
     */
    alphaFloor?: boolean | null;
}

export enum STEffectContainerType {
    PBDEFAULT = 0,
    sib = 1,
    tree = 2,
}

export interface IChartPlotAreaProperties {
    layout?: ICTLayoutDelta | null;
    dTable?: ICTDTableDelta | null;
    spPr?: ICTShapePropertiesDelta | null;
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Title name="CT_Title">
 * ```
 */
export interface ICTTitle {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Tx maxOccurs="1" minOccurs="0" name="tx" type="CT_Tx">
     * ```
     */
    tx?: ICTTx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Layout maxOccurs="1" minOccurs="0" name="layout" type="CT_Layout">
     * ```
     */
    layout?: ICTLayout;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="overlay" type="CT_BooleanDmlC">
     * ```
     */
    overlay?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Title name="CT_Title">
 * ```
 */
export interface ICTTitleDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Tx maxOccurs="1" minOccurs="0" name="tx" type="CT_Tx">
     * ```
     */
    tx?: ICTTxDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Layout maxOccurs="1" minOccurs="0" name="layout" type="CT_Layout">
     * ```
     */
    layout?: ICTLayoutDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="overlay" type="CT_BooleanDmlC">
     * ```
     */
    overlay?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Legend name="CT_Legend">
 * ```
 */
export interface ICTLegend {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LegendPos maxOccurs="1" minOccurs="0" name="legendPos" type="CT_LegendPos">
     * ```
     */
    legendPos?: ICTLegendPos;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LegendEntry maxOccurs="unbounded" minOccurs="0" name="legendEntry" type="CT_LegendEntry">
     * ```
     */
    legendEntry?: ICTLegendEntry[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Layout maxOccurs="1" minOccurs="0" name="layout" type="CT_Layout">
     * ```
     */
    layout?: ICTLayout;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="overlay" type="CT_BooleanDmlC">
     * ```
     */
    overlay?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Legend name="CT_Legend">
 * ```
 */
export interface ICTLegendDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LegendPos maxOccurs="1" minOccurs="0" name="legendPos" type="CT_LegendPos">
     * ```
     */
    legendPos?: ICTLegendPosDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LegendEntry maxOccurs="unbounded" minOccurs="0" name="legendEntry" type="CT_LegendEntry">
     * ```
     */
    legendEntry?: ICTLegendEntryDelta[];
    legendEntryOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Layout maxOccurs="1" minOccurs="0" name="layout" type="CT_Layout">
     * ```
     */
    layout?: ICTLayoutDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="overlay" type="CT_BooleanDmlC">
     * ```
     */
    overlay?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PivotFmts name="CT_PivotFmts">
 * ```
 */
export interface ICTPivotFmts {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PivotFmt maxOccurs="unbounded" minOccurs="0" name="pivotFmt" type="CT_PivotFmt">
     * ```
     */
    pivotFmt?: ICTPivotFmt[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PivotFmts name="CT_PivotFmts">
 * ```
 */
export interface ICTPivotFmtsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PivotFmt maxOccurs="unbounded" minOccurs="0" name="pivotFmt" type="CT_PivotFmt">
     * ```
     */
    pivotFmt?: ICTPivotFmtDelta[];
    pivotFmtOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_View3D name="CT_View3D">
 * ```
 */
export interface ICTView3D {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_RotX maxOccurs="1" minOccurs="0" name="rotX" type="CT_RotX">
     * ```
     */
    rotX?: ICTRotX;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_HPercent maxOccurs="1" minOccurs="0" name="hPercent" type="CT_HPercent">
     * ```
     */
    hPercent?: ICTHPercent;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_RotY maxOccurs="1" minOccurs="0" name="rotY" type="CT_RotY">
     * ```
     */
    rotY?: ICTRotY;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DepthPercent maxOccurs="1" minOccurs="0" name="depthPercent" type="CT_DepthPercent">
     * ```
     */
    depthPercent?: ICTDepthPercent;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="rAngAx" type="CT_BooleanDmlC">
     * ```
     */
    rAngAx?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Perspective maxOccurs="1" minOccurs="0" name="perspective" type="CT_Perspective">
     * ```
     */
    perspective?: ICTPerspective;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_View3D name="CT_View3D">
 * ```
 */
export interface ICTView3DDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_RotX maxOccurs="1" minOccurs="0" name="rotX" type="CT_RotX">
     * ```
     */
    rotX?: ICTRotXDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_HPercent maxOccurs="1" minOccurs="0" name="hPercent" type="CT_HPercent">
     * ```
     */
    hPercent?: ICTHPercentDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_RotY maxOccurs="1" minOccurs="0" name="rotY" type="CT_RotY">
     * ```
     */
    rotY?: ICTRotYDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DepthPercent maxOccurs="1" minOccurs="0" name="depthPercent" type="CT_DepthPercent">
     * ```
     */
    depthPercent?: ICTDepthPercentDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="rAngAx" type="CT_BooleanDmlC">
     * ```
     */
    rAngAx?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Perspective maxOccurs="1" minOccurs="0" name="perspective" type="CT_Perspective">
     * ```
     */
    perspective?: ICTPerspectiveDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Surface name="CT_Surface">
 * ```
 */
export interface ICTSurface {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Thickness maxOccurs="1" minOccurs="0" name="thickness" type="CT_Thickness">
     * ```
     */
    thickness?: ICTThickness;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PictureOptions maxOccurs="1" minOccurs="0" name="pictureOptions" type="CT_PictureOptions">
     * ```
     */
    pictureOptions?: ICTPictureOptions;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Surface name="CT_Surface">
 * ```
 */
export interface ICTSurfaceDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Thickness maxOccurs="1" minOccurs="0" name="thickness" type="CT_Thickness">
     * ```
     */
    thickness?: ICTThicknessDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PictureOptions maxOccurs="1" minOccurs="0" name="pictureOptions" type="CT_PictureOptions">
     * ```
     */
    pictureOptions?: ICTPictureOptionsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DispBlanksAs name="CT_DispBlanksAs">
 * ```
 */
export interface ICTDispBlanksAs {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_DispBlanksAs default="zero" name="val" type="ST_DispBlanksAs">
     * ```
     */
    val?: STDispBlanksAs;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DispBlanksAs name="CT_DispBlanksAs">
 * ```
 */
export interface ICTDispBlanksAsDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_DispBlanksAs default="zero" name="val" type="ST_DispBlanksAs">
     * ```
     */
    val?: STDispBlanksAs | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextParagraph name="CT_TextParagraph">
 * ```
 */
export interface ICTTextParagraph {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="pPr" type="CT_TextParagraphProperties">
     * ```
     */
    pPr?: ICTTextParagraphProperties;
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_TextRun ref=dmlmain:EG_TextRun maxOccurs="unbounded" minOccurs="0" name="EG_TextRun" ref="EG_TextRun">
     * ref dmlmain:EG_TextRun
     * ```
     */
    egTextRun?: IEGTextRun[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextCharacterProperties maxOccurs="1" minOccurs="0" name="endParaRPr" type="CT_TextCharacterProperties">
     * ```
     */
    endParaRPr?: ICTTextCharacterProperties;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextParagraph name="CT_TextParagraph">
 * ```
 */
export interface ICTTextParagraphDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextParagraphProperties maxOccurs="1" minOccurs="0" name="pPr" type="CT_TextParagraphProperties">
     * ```
     */
    pPr?: ICTTextParagraphPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_TextRun ref=dmlmain:EG_TextRun maxOccurs="unbounded" minOccurs="0" name="EG_TextRun" ref="EG_TextRun">
     * ref dmlmain:EG_TextRun
     * ```
     */
    egTextRun?: IEGTextRunDelta[];
    egTextRunOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextCharacterProperties maxOccurs="1" minOccurs="0" name="endParaRPr" type="CT_TextCharacterProperties">
     * ```
     */
    endParaRPr?: ICTTextCharacterPropertiesDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_UnsignedInt name="CT_UnsignedInt">
 * ```
 */
export interface ICTUnsignedInt {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="val" type="xsd:unsignedInt" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_UnsignedInt name="CT_UnsignedInt">
 * ```
 */
export interface ICTUnsignedIntDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="val" type="xsd:unsignedInt" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_HeaderFooterDmlC name="CT_HeaderFooterDmlC">
 * ```
 */
export interface ICTHeaderFooterDmlC {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="oddHeader" type="s:ST_Xstring">
     * ```
     */
    oddHeader?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="oddFooter" type="s:ST_Xstring">
     * ```
     */
    oddFooter?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="evenHeader" type="s:ST_Xstring">
     * ```
     */
    evenHeader?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="evenFooter" type="s:ST_Xstring">
     * ```
     */
    evenFooter?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="firstHeader" type="s:ST_Xstring">
     * ```
     */
    firstHeader?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="firstFooter" type="s:ST_Xstring">
     * ```
     */
    firstFooter?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="alignWithMargins" type="xsd:boolean">
     * ```
     */
    alignWithMargins?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="differentOddEven" type="xsd:boolean">
     * ```
     */
    differentOddEven?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="differentFirst" type="xsd:boolean">
     * ```
     */
    differentFirst?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_HeaderFooterDmlC name="CT_HeaderFooterDmlC">
 * ```
 */
export interface ICTHeaderFooterDmlCDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="oddHeader" type="s:ST_Xstring">
     * ```
     */
    oddHeader?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="oddFooter" type="s:ST_Xstring">
     * ```
     */
    oddFooter?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="evenHeader" type="s:ST_Xstring">
     * ```
     */
    evenHeader?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="evenFooter" type="s:ST_Xstring">
     * ```
     */
    evenFooter?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="firstHeader" type="s:ST_Xstring">
     * ```
     */
    firstHeader?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="firstFooter" type="s:ST_Xstring">
     * ```
     */
    firstFooter?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="alignWithMargins" type="xsd:boolean">
     * ```
     */
    alignWithMargins?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="differentOddEven" type="xsd:boolean">
     * ```
     */
    differentOddEven?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="differentFirst" type="xsd:boolean">
     * ```
     */
    differentFirst?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PageMarginsDmlC name="CT_PageMarginsDmlC">
 * ```
 */
export interface ICTPageMarginsDmlC {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="l" type="xsd:double" use="required">
     * ```
     */
    l?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="r" type="xsd:double" use="required">
     * ```
     */
    r?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="t" type="xsd:double" use="required">
     * ```
     */
    t?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="b" type="xsd:double" use="required">
     * ```
     */
    b?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="header" type="xsd:double" use="required">
     * ```
     */
    header?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="footer" type="xsd:double" use="required">
     * ```
     */
    footer?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PageMarginsDmlC name="CT_PageMarginsDmlC">
 * ```
 */
export interface ICTPageMarginsDmlCDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="l" type="xsd:double" use="required">
     * ```
     */
    l?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="r" type="xsd:double" use="required">
     * ```
     */
    r?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="t" type="xsd:double" use="required">
     * ```
     */
    t?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="b" type="xsd:double" use="required">
     * ```
     */
    b?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="header" type="xsd:double" use="required">
     * ```
     */
    header?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="footer" type="xsd:double" use="required">
     * ```
     */
    footer?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PageSetupDmlC name="CT_PageSetupDmlC">
 * ```
 */
export interface ICTPageSetupDmlC {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="1" name="paperSize" type="xsd:unsignedInt" use="optional">
     * ```
     */
    paperSize?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="paperHeight" type="s:ST_PositiveUniversalMeasure" use="optional">
     * ```
     */
    paperHeight?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="paperWidth" type="s:ST_PositiveUniversalMeasure" use="optional">
     * ```
     */
    paperWidth?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="1" name="firstPageNumber" type="xsd:unsignedInt" use="optional">
     * ```
     */
    firstPageNumber?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_PageSetupOrientation default="default" name="orientation" type="ST_PageSetupOrientation" use="optional">
     * ```
     */
    orientation?: STPageSetupOrientation;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="blackAndWhite" type="xsd:boolean" use="optional">
     * ```
     */
    blackAndWhite?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="draft" type="xsd:boolean" use="optional">
     * ```
     */
    draft?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="useFirstPageNumber" type="xsd:boolean" use="optional">
     * ```
     */
    useFirstPageNumber?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="600" name="horizontalDpi" type="xsd:int" use="optional">
     * ```
     */
    horizontalDpi?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="600" name="verticalDpi" type="xsd:int" use="optional">
     * ```
     */
    verticalDpi?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="1" name="copies" type="xsd:unsignedInt" use="optional">
     * ```
     */
    copies?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PageSetupDmlC name="CT_PageSetupDmlC">
 * ```
 */
export interface ICTPageSetupDmlCDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="1" name="paperSize" type="xsd:unsignedInt" use="optional">
     * ```
     */
    paperSize?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="paperHeight" type="s:ST_PositiveUniversalMeasure" use="optional">
     * ```
     */
    paperHeight?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="paperWidth" type="s:ST_PositiveUniversalMeasure" use="optional">
     * ```
     */
    paperWidth?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="1" name="firstPageNumber" type="xsd:unsignedInt" use="optional">
     * ```
     */
    firstPageNumber?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_PageSetupOrientation default="default" name="orientation" type="ST_PageSetupOrientation" use="optional">
     * ```
     */
    orientation?: STPageSetupOrientation | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="blackAndWhite" type="xsd:boolean" use="optional">
     * ```
     */
    blackAndWhite?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="draft" type="xsd:boolean" use="optional">
     * ```
     */
    draft?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="useFirstPageNumber" type="xsd:boolean" use="optional">
     * ```
     */
    useFirstPageNumber?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="600" name="horizontalDpi" type="xsd:int" use="optional">
     * ```
     */
    horizontalDpi?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="600" name="verticalDpi" type="xsd:int" use="optional">
     * ```
     */
    verticalDpi?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="1" name="copies" type="xsd:unsignedInt" use="optional">
     * ```
     */
    copies?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_RelId name="CT_RelId">
 * ```
 */
export interface ICTRelId {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_RelId name="CT_RelId">
 * ```
 */
export interface ICTRelIdDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ExtensionDmlC name="CT_ExtensionDmlC">
 * ```
 */
export interface ICTExtensionDmlC {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="uri" type="xsd:token">
     * ```
     */
    uri?: string;
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlchart:EG_ExtensionDmlC ref=dmlchart:EG_ExtensionDmlC maxOccurs="unbounded" name="EG_ExtensionDmlC" ref="EG_ExtensionDmlC">
     * ref dmlchart:EG_ExtensionDmlC
     * ```
     */
    egExtensionDmlC?: IEGExtensionDmlC[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ExtensionDmlC name="CT_ExtensionDmlC">
 * ```
 */
export interface ICTExtensionDmlCDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="uri" type="xsd:token">
     * ```
     */
    uri?: string | null;
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlchart:EG_ExtensionDmlC ref=dmlchart:EG_ExtensionDmlC maxOccurs="unbounded" name="EG_ExtensionDmlC" ref="EG_ExtensionDmlC">
     * ref dmlchart:EG_ExtensionDmlC
     * ```
     */
    egExtensionDmlC?: IEGExtensionDmlCDelta[];
    egExtensionDmlCOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=mscs2012:CT_ColorStyleVariation name="CT_ColorStyleVariation">
 * ```
 */
export interface ICTColorStyleVariation {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorTransform ref=dmlmain:EG_ColorTransform maxOccurs="unbounded" minOccurs="0" name="EG_ColorTransform" ref="a:EG_ColorTransform">
     * ref dmlmain:EG_ColorTransform
     * ```
     */
    egColorTransform?: IEGColorTransform[];
}

/**
 * ```xml
 * <xsd:complexType name=mscs2012:CT_ColorStyleVariation name="CT_ColorStyleVariation">
 * ```
 */
export interface ICTColorStyleVariationDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorTransform ref=dmlmain:EG_ColorTransform maxOccurs="unbounded" minOccurs="0" name="EG_ColorTransform" ref="a:EG_ColorTransform">
     * ref dmlmain:EG_ColorTransform
     * ```
     */
    egColorTransform?: IEGColorTransformDelta[];
    egColorTransformOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=mscs2012:CT_StyleEntry name="CT_StyleEntry">
 * ```
 */
export interface ICTStyleEntry {
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleReference maxOccurs="1" minOccurs="1" name="lnRef" type="CT_StyleReference">
     * ```
     */
    lnRef?: ICTStyleReference;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:double default="1.0" maxOccurs="1" minOccurs="0" name="lineWidthScale" type="xsd:double">
     * ```
     */
    lineWidthScale?: number;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleReference maxOccurs="1" minOccurs="1" name="fillRef" type="CT_StyleReference">
     * ```
     */
    fillRef?: ICTStyleReference;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleReference maxOccurs="1" minOccurs="1" name="effectRef" type="CT_StyleReference">
     * ```
     */
    effectRef?: ICTStyleReference;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_FontReferenceMsCs maxOccurs="1" minOccurs="1" name="fontRef" type="CT_FontReferenceMsCs">
     * ```
     */
    fontRef?: ICTFontReferenceMsCs;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextCharacterProperties maxOccurs="1" minOccurs="0" name="defRPr" type="a:CT_TextCharacterProperties">
     * ```
     */
    defRPr?: ICTTextCharacterProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBodyProperties maxOccurs="1" minOccurs="0" name="bodyPr" type="a:CT_TextBodyProperties">
     * ```
     */
    bodyPr?: ICTTextBodyProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="mods" type="ST_StyleEntryModifierList" use="optional">
     * ```
     */
    mods?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mscs2012:CT_StyleEntry name="CT_StyleEntry">
 * ```
 */
export interface ICTStyleEntryDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleReference maxOccurs="1" minOccurs="1" name="lnRef" type="CT_StyleReference">
     * ```
     */
    lnRef?: ICTStyleReferenceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:double default="1.0" maxOccurs="1" minOccurs="0" name="lineWidthScale" type="xsd:double">
     * ```
     */
    lineWidthScale?: number | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleReference maxOccurs="1" minOccurs="1" name="fillRef" type="CT_StyleReference">
     * ```
     */
    fillRef?: ICTStyleReferenceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleReference maxOccurs="1" minOccurs="1" name="effectRef" type="CT_StyleReference">
     * ```
     */
    effectRef?: ICTStyleReferenceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_FontReferenceMsCs maxOccurs="1" minOccurs="1" name="fontRef" type="CT_FontReferenceMsCs">
     * ```
     */
    fontRef?: ICTFontReferenceMsCsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextCharacterProperties maxOccurs="1" minOccurs="0" name="defRPr" type="a:CT_TextCharacterProperties">
     * ```
     */
    defRPr?: ICTTextCharacterPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBodyProperties maxOccurs="1" minOccurs="0" name="bodyPr" type="a:CT_TextBodyProperties">
     * ```
     */
    bodyPr?: ICTTextBodyPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="mods" type="ST_StyleEntryModifierList" use="optional">
     * ```
     */
    mods?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=mscs2012:CT_MarkerLayout name="CT_MarkerLayout">
 * ```
 */
export interface ICTMarkerLayout {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mscs2012:ST_MarkerStyleMsCs name="symbol" type="ST_MarkerStyleMsCs" use="optional">
     * ```
     */
    symbol?: STMarkerStyleMsCs;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="size" type="ST_MarkerSizeMsCs" use="optional">
     * ```
     */
    size?: number;
}

/**
 * ```xml
 * <xsd:complexType name=mscs2012:CT_MarkerLayout name="CT_MarkerLayout">
 * ```
 */
export interface ICTMarkerLayoutDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mscs2012:ST_MarkerStyleMsCs name="symbol" type="ST_MarkerStyleMsCs" use="optional">
     * ```
     */
    symbol?: STMarkerStyleMsCs | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="size" type="ST_MarkerSizeMsCs" use="optional">
     * ```
     */
    size?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Grouping name="CT_Grouping">
 * ```
 */
export interface ICTGrouping {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_Grouping default="standard" name="val" type="ST_Grouping">
     * ```
     */
    val?: STGrouping;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Grouping name="CT_Grouping">
 * ```
 */
export interface ICTGroupingDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_Grouping default="standard" name="val" type="ST_Grouping">
     * ```
     */
    val?: STGrouping | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_AreaSer name="CT_AreaSer">
 * ```
 */
export interface ICTAreaSer {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="order" type="CT_UnsignedInt">
     * ```
     */
    order?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SerTx maxOccurs="1" minOccurs="0" name="tx" type="CT_SerTx">
     * ```
     */
    tx?: ICTSerTx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PictureOptions maxOccurs="1" minOccurs="0" name="pictureOptions" type="CT_PictureOptions">
     * ```
     */
    pictureOptions?: ICTPictureOptions;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DPt maxOccurs="unbounded" minOccurs="0" name="dPt" type="CT_DPt">
     * ```
     */
    dPt?: ICTDPt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Trendline maxOccurs="unbounded" minOccurs="0" name="trendline" type="CT_Trendline">
     * ```
     */
    trendline?: ICTTrendline[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ErrBars maxOccurs="2" minOccurs="0" name="errBars" type="CT_ErrBars">
     * ```
     */
    errBars?: ICTErrBars[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxDataSource maxOccurs="1" minOccurs="0" name="cat" type="CT_AxDataSource">
     * ```
     */
    cat?: ICTAxDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="val" type="CT_NumDataSource">
     * ```
     */
    val?: ICTNumDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="listIndex" skipIO="true" type="CT_UnsignedInt">
     * ```
     */
    listIndex?: ICTUnsignedInt;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_AreaSer name="CT_AreaSer">
 * ```
 */
export interface ICTAreaSerDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="order" type="CT_UnsignedInt">
     * ```
     */
    order?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SerTx maxOccurs="1" minOccurs="0" name="tx" type="CT_SerTx">
     * ```
     */
    tx?: ICTSerTxDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PictureOptions maxOccurs="1" minOccurs="0" name="pictureOptions" type="CT_PictureOptions">
     * ```
     */
    pictureOptions?: ICTPictureOptionsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DPt maxOccurs="unbounded" minOccurs="0" name="dPt" type="CT_DPt">
     * ```
     */
    dPt?: ICTDPtDelta[];
    dPtOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Trendline maxOccurs="unbounded" minOccurs="0" name="trendline" type="CT_Trendline">
     * ```
     */
    trendline?: ICTTrendlineDelta[];
    trendlineOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ErrBars maxOccurs="2" minOccurs="0" name="errBars" type="CT_ErrBars">
     * ```
     */
    errBars?: ICTErrBarsDelta[];
    errBarsOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxDataSource maxOccurs="1" minOccurs="0" name="cat" type="CT_AxDataSource">
     * ```
     */
    cat?: ICTAxDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="val" type="CT_NumDataSource">
     * ```
     */
    val?: ICTNumDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="listIndex" skipIO="true" type="CT_UnsignedInt">
     * ```
     */
    listIndex?: ICTUnsignedIntDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DLbls name="CT_DLbls">
 * ```
 */
export interface ICTDLbls {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbl maxOccurs="unbounded" minOccurs="0" name="dLbl" type="CT_DLbl">
     * ```
     */
    dLbl?: ICTDLbl[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumFmtDmlC maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumFmtDmlC">
     * ```
     */
    numFmt?: ICTNumFmtDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLblPos maxOccurs="1" minOccurs="0" name="dLblPos" type="CT_DLblPos">
     * ```
     */
    dLblPos?: ICTDLblPos;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showLegendKey" type="CT_BooleanDmlC">
     * ```
     */
    showLegendKey?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showVal" type="CT_BooleanDmlC">
     * ```
     */
    showVal?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showCatName" type="CT_BooleanDmlC">
     * ```
     */
    showCatName?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showSerName" type="CT_BooleanDmlC">
     * ```
     */
    showSerName?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showPercent" type="CT_BooleanDmlC">
     * ```
     */
    showPercent?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showBubbleSize" type="CT_BooleanDmlC">
     * ```
     */
    showBubbleSize?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="separator" type="xsd:string">
     * ```
     */
    separator?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showLeaderLines" type="CT_BooleanDmlC">
     * ```
     */
    showLeaderLines?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="leaderLines" type="CT_ChartLines">
     * ```
     */
    leaderLines?: ICTChartLines;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDefaultFalse aliasName=deleteKeyword aliasName="deleteKeyword" maxOccurs="1" minOccurs="1" name="delete" type="CT_BooleanDefaultFalse">
     * ```
     */
    deleteKeyword?: ICTBooleanDefaultFalse;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DLbls name="CT_DLbls">
 * ```
 */
export interface ICTDLblsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbl maxOccurs="unbounded" minOccurs="0" name="dLbl" type="CT_DLbl">
     * ```
     */
    dLbl?: ICTDLblDelta[];
    dLblOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumFmtDmlC maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumFmtDmlC">
     * ```
     */
    numFmt?: ICTNumFmtDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLblPos maxOccurs="1" minOccurs="0" name="dLblPos" type="CT_DLblPos">
     * ```
     */
    dLblPos?: ICTDLblPosDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showLegendKey" type="CT_BooleanDmlC">
     * ```
     */
    showLegendKey?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showVal" type="CT_BooleanDmlC">
     * ```
     */
    showVal?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showCatName" type="CT_BooleanDmlC">
     * ```
     */
    showCatName?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showSerName" type="CT_BooleanDmlC">
     * ```
     */
    showSerName?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showPercent" type="CT_BooleanDmlC">
     * ```
     */
    showPercent?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showBubbleSize" type="CT_BooleanDmlC">
     * ```
     */
    showBubbleSize?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="separator" type="xsd:string">
     * ```
     */
    separator?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showLeaderLines" type="CT_BooleanDmlC">
     * ```
     */
    showLeaderLines?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="leaderLines" type="CT_ChartLines">
     * ```
     */
    leaderLines?: ICTChartLinesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDefaultFalse aliasName=deleteKeyword aliasName="deleteKeyword" maxOccurs="1" minOccurs="1" name="delete" type="CT_BooleanDefaultFalse">
     * ```
     */
    deleteKeyword?: ICTBooleanDefaultFalseDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ChartLines name="CT_ChartLines">
 * ```
 */
export interface ICTChartLines {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ChartLines name="CT_ChartLines">
 * ```
 */
export interface ICTChartLinesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_GapAmount name="CT_GapAmount">
 * ```
 */
export interface ICTGapAmount {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_GapAmountUShort" default="150" name="val" type="ST_GapAmount">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_GapAmount name="CT_GapAmount">
 * ```
 */
export interface ICTGapAmountDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_GapAmountUShort" default="150" name="val" type="ST_GapAmount">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LineSer name="CT_LineSer">
 * ```
 */
export interface ICTLineSer {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="order" type="CT_UnsignedInt">
     * ```
     */
    order?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SerTx maxOccurs="1" minOccurs="0" name="tx" type="CT_SerTx">
     * ```
     */
    tx?: ICTSerTx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_MarkerDmlC maxOccurs="1" minOccurs="0" name="marker" type="CT_MarkerDmlC">
     * ```
     */
    marker?: ICTMarkerDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DPt maxOccurs="unbounded" minOccurs="0" name="dPt" type="CT_DPt">
     * ```
     */
    dPt?: ICTDPt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Trendline maxOccurs="unbounded" minOccurs="0" name="trendline" type="CT_Trendline">
     * ```
     */
    trendline?: ICTTrendline[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ErrBars maxOccurs="1" minOccurs="0" name="errBars" type="CT_ErrBars">
     * ```
     */
    errBars?: ICTErrBars;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxDataSource maxOccurs="1" minOccurs="0" name="cat" type="CT_AxDataSource">
     * ```
     */
    cat?: ICTAxDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="val" type="CT_NumDataSource">
     * ```
     */
    val?: ICTNumDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="smooth" type="CT_BooleanDmlC">
     * ```
     */
    smooth?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="listIndex" skipIO="true" type="CT_UnsignedInt">
     * ```
     */
    listIndex?: ICTUnsignedInt;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LineSer name="CT_LineSer">
 * ```
 */
export interface ICTLineSerDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="order" type="CT_UnsignedInt">
     * ```
     */
    order?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SerTx maxOccurs="1" minOccurs="0" name="tx" type="CT_SerTx">
     * ```
     */
    tx?: ICTSerTxDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_MarkerDmlC maxOccurs="1" minOccurs="0" name="marker" type="CT_MarkerDmlC">
     * ```
     */
    marker?: ICTMarkerDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DPt maxOccurs="unbounded" minOccurs="0" name="dPt" type="CT_DPt">
     * ```
     */
    dPt?: ICTDPtDelta[];
    dPtOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Trendline maxOccurs="unbounded" minOccurs="0" name="trendline" type="CT_Trendline">
     * ```
     */
    trendline?: ICTTrendlineDelta[];
    trendlineOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ErrBars maxOccurs="1" minOccurs="0" name="errBars" type="CT_ErrBars">
     * ```
     */
    errBars?: ICTErrBarsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxDataSource maxOccurs="1" minOccurs="0" name="cat" type="CT_AxDataSource">
     * ```
     */
    cat?: ICTAxDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="val" type="CT_NumDataSource">
     * ```
     */
    val?: ICTNumDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="smooth" type="CT_BooleanDmlC">
     * ```
     */
    smooth?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="listIndex" skipIO="true" type="CT_UnsignedInt">
     * ```
     */
    listIndex?: ICTUnsignedIntDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_UpDownBars name="CT_UpDownBars">
 * ```
 */
export interface ICTUpDownBars {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_GapAmount maxOccurs="1" minOccurs="0" name="gapWidth" type="CT_GapAmount">
     * ```
     */
    gapWidth?: ICTGapAmount;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UpDownBar maxOccurs="1" minOccurs="0" name="upBars" type="CT_UpDownBar">
     * ```
     */
    upBars?: ICTUpDownBar;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UpDownBar maxOccurs="1" minOccurs="0" name="downBars" type="CT_UpDownBar">
     * ```
     */
    downBars?: ICTUpDownBar;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_UpDownBars name="CT_UpDownBars">
 * ```
 */
export interface ICTUpDownBarsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_GapAmount maxOccurs="1" minOccurs="0" name="gapWidth" type="CT_GapAmount">
     * ```
     */
    gapWidth?: ICTGapAmountDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UpDownBar maxOccurs="1" minOccurs="0" name="upBars" type="CT_UpDownBar">
     * ```
     */
    upBars?: ICTUpDownBarDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UpDownBar maxOccurs="1" minOccurs="0" name="downBars" type="CT_UpDownBar">
     * ```
     */
    downBars?: ICTUpDownBarDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_RadarStyle name="CT_RadarStyle">
 * ```
 */
export interface ICTRadarStyle {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_RadarStyle default="standard" name="val" type="ST_RadarStyle">
     * ```
     */
    val?: STRadarStyle;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_RadarStyle name="CT_RadarStyle">
 * ```
 */
export interface ICTRadarStyleDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_RadarStyle default="standard" name="val" type="ST_RadarStyle">
     * ```
     */
    val?: STRadarStyle | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_RadarSer name="CT_RadarSer">
 * ```
 */
export interface ICTRadarSer {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="order" type="CT_UnsignedInt">
     * ```
     */
    order?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SerTx maxOccurs="1" minOccurs="0" name="tx" type="CT_SerTx">
     * ```
     */
    tx?: ICTSerTx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_MarkerDmlC maxOccurs="1" minOccurs="0" name="marker" type="CT_MarkerDmlC">
     * ```
     */
    marker?: ICTMarkerDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DPt maxOccurs="unbounded" minOccurs="0" name="dPt" type="CT_DPt">
     * ```
     */
    dPt?: ICTDPt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxDataSource maxOccurs="1" minOccurs="0" name="cat" type="CT_AxDataSource">
     * ```
     */
    cat?: ICTAxDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="val" type="CT_NumDataSource">
     * ```
     */
    val?: ICTNumDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="listIndex" skipIO="true" type="CT_UnsignedInt">
     * ```
     */
    listIndex?: ICTUnsignedInt;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_RadarSer name="CT_RadarSer">
 * ```
 */
export interface ICTRadarSerDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="order" type="CT_UnsignedInt">
     * ```
     */
    order?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SerTx maxOccurs="1" minOccurs="0" name="tx" type="CT_SerTx">
     * ```
     */
    tx?: ICTSerTxDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_MarkerDmlC maxOccurs="1" minOccurs="0" name="marker" type="CT_MarkerDmlC">
     * ```
     */
    marker?: ICTMarkerDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DPt maxOccurs="unbounded" minOccurs="0" name="dPt" type="CT_DPt">
     * ```
     */
    dPt?: ICTDPtDelta[];
    dPtOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxDataSource maxOccurs="1" minOccurs="0" name="cat" type="CT_AxDataSource">
     * ```
     */
    cat?: ICTAxDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="val" type="CT_NumDataSource">
     * ```
     */
    val?: ICTNumDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="listIndex" skipIO="true" type="CT_UnsignedInt">
     * ```
     */
    listIndex?: ICTUnsignedIntDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ScatterStyle name="CT_ScatterStyle">
 * ```
 */
export interface ICTScatterStyle {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_ScatterStyle default="marker" name="val" type="ST_ScatterStyle">
     * ```
     */
    val?: STScatterStyle;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ScatterStyle name="CT_ScatterStyle">
 * ```
 */
export interface ICTScatterStyleDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_ScatterStyle default="marker" name="val" type="ST_ScatterStyle">
     * ```
     */
    val?: STScatterStyle | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ScatterSer name="CT_ScatterSer">
 * ```
 */
export interface ICTScatterSer {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="order" type="CT_UnsignedInt">
     * ```
     */
    order?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SerTx maxOccurs="1" minOccurs="0" name="tx" type="CT_SerTx">
     * ```
     */
    tx?: ICTSerTx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_MarkerDmlC maxOccurs="1" minOccurs="0" name="marker" type="CT_MarkerDmlC">
     * ```
     */
    marker?: ICTMarkerDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DPt maxOccurs="unbounded" minOccurs="0" name="dPt" type="CT_DPt">
     * ```
     */
    dPt?: ICTDPt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Trendline maxOccurs="unbounded" minOccurs="0" name="trendline" type="CT_Trendline">
     * ```
     */
    trendline?: ICTTrendline[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ErrBars maxOccurs="2" minOccurs="0" name="errBars" type="CT_ErrBars">
     * ```
     */
    errBars?: ICTErrBars[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxDataSource maxOccurs="1" minOccurs="0" name="xVal" type="CT_AxDataSource">
     * ```
     */
    xVal?: ICTAxDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="yVal" type="CT_NumDataSource">
     * ```
     */
    yVal?: ICTNumDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="smooth" type="CT_BooleanDmlC">
     * ```
     */
    smooth?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="listIndex" skipIO="true" type="CT_UnsignedInt">
     * ```
     */
    listIndex?: ICTUnsignedInt;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ScatterSer name="CT_ScatterSer">
 * ```
 */
export interface ICTScatterSerDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="order" type="CT_UnsignedInt">
     * ```
     */
    order?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SerTx maxOccurs="1" minOccurs="0" name="tx" type="CT_SerTx">
     * ```
     */
    tx?: ICTSerTxDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_MarkerDmlC maxOccurs="1" minOccurs="0" name="marker" type="CT_MarkerDmlC">
     * ```
     */
    marker?: ICTMarkerDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DPt maxOccurs="unbounded" minOccurs="0" name="dPt" type="CT_DPt">
     * ```
     */
    dPt?: ICTDPtDelta[];
    dPtOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Trendline maxOccurs="unbounded" minOccurs="0" name="trendline" type="CT_Trendline">
     * ```
     */
    trendline?: ICTTrendlineDelta[];
    trendlineOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ErrBars maxOccurs="2" minOccurs="0" name="errBars" type="CT_ErrBars">
     * ```
     */
    errBars?: ICTErrBarsDelta[];
    errBarsOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxDataSource maxOccurs="1" minOccurs="0" name="xVal" type="CT_AxDataSource">
     * ```
     */
    xVal?: ICTAxDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="yVal" type="CT_NumDataSource">
     * ```
     */
    yVal?: ICTNumDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="smooth" type="CT_BooleanDmlC">
     * ```
     */
    smooth?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="listIndex" skipIO="true" type="CT_UnsignedInt">
     * ```
     */
    listIndex?: ICTUnsignedIntDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PieSer name="CT_PieSer">
 * ```
 */
export interface ICTPieSer {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="order" type="CT_UnsignedInt">
     * ```
     */
    order?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SerTx maxOccurs="1" minOccurs="0" name="tx" type="CT_SerTx">
     * ```
     */
    tx?: ICTSerTx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="explosion" type="CT_UnsignedInt">
     * ```
     */
    explosion?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DPt maxOccurs="unbounded" minOccurs="0" name="dPt" type="CT_DPt">
     * ```
     */
    dPt?: ICTDPt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxDataSource maxOccurs="1" minOccurs="0" name="cat" type="CT_AxDataSource">
     * ```
     */
    cat?: ICTAxDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="val" type="CT_NumDataSource">
     * ```
     */
    val?: ICTNumDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="listIndex" skipIO="true" type="CT_UnsignedInt">
     * ```
     */
    listIndex?: ICTUnsignedInt;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PieSer name="CT_PieSer">
 * ```
 */
export interface ICTPieSerDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="order" type="CT_UnsignedInt">
     * ```
     */
    order?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SerTx maxOccurs="1" minOccurs="0" name="tx" type="CT_SerTx">
     * ```
     */
    tx?: ICTSerTxDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="explosion" type="CT_UnsignedInt">
     * ```
     */
    explosion?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DPt maxOccurs="unbounded" minOccurs="0" name="dPt" type="CT_DPt">
     * ```
     */
    dPt?: ICTDPtDelta[];
    dPtOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxDataSource maxOccurs="1" minOccurs="0" name="cat" type="CT_AxDataSource">
     * ```
     */
    cat?: ICTAxDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="val" type="CT_NumDataSource">
     * ```
     */
    val?: ICTNumDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="listIndex" skipIO="true" type="CT_UnsignedInt">
     * ```
     */
    listIndex?: ICTUnsignedIntDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_FirstSliceAng name="CT_FirstSliceAng">
 * ```
 */
export interface ICTFirstSliceAng {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="val" type="ST_FirstSliceAng">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_FirstSliceAng name="CT_FirstSliceAng">
 * ```
 */
export interface ICTFirstSliceAngDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="val" type="ST_FirstSliceAng">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_HoleSize name="CT_HoleSize">
 * ```
 */
export interface ICTHoleSize {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_HoleSizeUByte" default="10" name="val" type="ST_HoleSize">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_HoleSize name="CT_HoleSize">
 * ```
 */
export interface ICTHoleSizeDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_HoleSizeUByte" default="10" name="val" type="ST_HoleSize">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BarDir name="CT_BarDir">
 * ```
 */
export interface ICTBarDir {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_BarDir default="col" name="val" type="ST_BarDir">
     * ```
     */
    val?: STBarDir;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BarDir name="CT_BarDir">
 * ```
 */
export interface ICTBarDirDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_BarDir default="col" name="val" type="ST_BarDir">
     * ```
     */
    val?: STBarDir | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BarGrouping name="CT_BarGrouping">
 * ```
 */
export interface ICTBarGrouping {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_BarGrouping default="clustered" name="val" type="ST_BarGrouping">
     * ```
     */
    val?: STBarGrouping;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BarGrouping name="CT_BarGrouping">
 * ```
 */
export interface ICTBarGroupingDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_BarGrouping default="clustered" name="val" type="ST_BarGrouping">
     * ```
     */
    val?: STBarGrouping | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BarSer name="CT_BarSer">
 * ```
 */
export interface ICTBarSer {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="order" type="CT_UnsignedInt">
     * ```
     */
    order?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SerTx maxOccurs="1" minOccurs="0" name="tx" type="CT_SerTx">
     * ```
     */
    tx?: ICTSerTx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="invertIfNegative" type="CT_BooleanDmlC">
     * ```
     */
    invertIfNegative?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PictureOptions maxOccurs="1" minOccurs="0" name="pictureOptions" type="CT_PictureOptions">
     * ```
     */
    pictureOptions?: ICTPictureOptions;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DPt maxOccurs="unbounded" minOccurs="0" name="dPt" type="CT_DPt">
     * ```
     */
    dPt?: ICTDPt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Trendline maxOccurs="unbounded" minOccurs="0" name="trendline" type="CT_Trendline">
     * ```
     */
    trendline?: ICTTrendline[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ErrBars maxOccurs="1" minOccurs="0" name="errBars" type="CT_ErrBars">
     * ```
     */
    errBars?: ICTErrBars;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxDataSource maxOccurs="1" minOccurs="0" name="cat" type="CT_AxDataSource">
     * ```
     */
    cat?: ICTAxDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="val" type="CT_NumDataSource">
     * ```
     */
    val?: ICTNumDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ShapeDmlC maxOccurs="1" minOccurs="0" name="shape" type="CT_ShapeDmlC">
     * ```
     */
    shape?: ICTShapeDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="listIndex" skipIO="true" type="CT_UnsignedInt">
     * ```
     */
    listIndex?: ICTUnsignedInt;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BarSer name="CT_BarSer">
 * ```
 */
export interface ICTBarSerDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="order" type="CT_UnsignedInt">
     * ```
     */
    order?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SerTx maxOccurs="1" minOccurs="0" name="tx" type="CT_SerTx">
     * ```
     */
    tx?: ICTSerTxDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="invertIfNegative" type="CT_BooleanDmlC">
     * ```
     */
    invertIfNegative?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PictureOptions maxOccurs="1" minOccurs="0" name="pictureOptions" type="CT_PictureOptions">
     * ```
     */
    pictureOptions?: ICTPictureOptionsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DPt maxOccurs="unbounded" minOccurs="0" name="dPt" type="CT_DPt">
     * ```
     */
    dPt?: ICTDPtDelta[];
    dPtOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Trendline maxOccurs="unbounded" minOccurs="0" name="trendline" type="CT_Trendline">
     * ```
     */
    trendline?: ICTTrendlineDelta[];
    trendlineOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ErrBars maxOccurs="1" minOccurs="0" name="errBars" type="CT_ErrBars">
     * ```
     */
    errBars?: ICTErrBarsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxDataSource maxOccurs="1" minOccurs="0" name="cat" type="CT_AxDataSource">
     * ```
     */
    cat?: ICTAxDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="val" type="CT_NumDataSource">
     * ```
     */
    val?: ICTNumDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ShapeDmlC maxOccurs="1" minOccurs="0" name="shape" type="CT_ShapeDmlC">
     * ```
     */
    shape?: ICTShapeDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="listIndex" skipIO="true" type="CT_UnsignedInt">
     * ```
     */
    listIndex?: ICTUnsignedIntDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Overlap name="CT_Overlap">
 * ```
 */
export interface ICTOverlap {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_OverlapByte" default="0" name="val" type="ST_Overlap">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Overlap name="CT_Overlap">
 * ```
 */
export interface ICTOverlapDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_OverlapByte" default="0" name="val" type="ST_Overlap">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ShapeDmlC name="CT_ShapeDmlC">
 * ```
 */
export interface ICTShapeDmlC {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_Shape default="box" name="val" type="ST_Shape">
     * ```
     */
    val?: STShape;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ShapeDmlC name="CT_ShapeDmlC">
 * ```
 */
export interface ICTShapeDmlCDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_Shape default="box" name="val" type="ST_Shape">
     * ```
     */
    val?: STShape | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_OfPieType name="CT_OfPieType">
 * ```
 */
export interface ICTOfPieType {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_OfPieType default="pie" name="val" type="ST_OfPieType">
     * ```
     */
    val?: STOfPieType;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_OfPieType name="CT_OfPieType">
 * ```
 */
export interface ICTOfPieTypeDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_OfPieType default="pie" name="val" type="ST_OfPieType">
     * ```
     */
    val?: STOfPieType | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_SplitType name="CT_SplitType">
 * ```
 */
export interface ICTSplitType {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_SplitType default="auto" name="val" type="ST_SplitType">
     * ```
     */
    val?: STSplitType;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_SplitType name="CT_SplitType">
 * ```
 */
export interface ICTSplitTypeDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_SplitType default="auto" name="val" type="ST_SplitType">
     * ```
     */
    val?: STSplitType | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Double name="CT_Double">
 * ```
 */
export interface ICTDouble {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="val" type="xsd:double" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Double name="CT_Double">
 * ```
 */
export interface ICTDoubleDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="val" type="xsd:double" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_CustSplit name="CT_CustSplit">
 * ```
 */
export interface ICTCustSplit {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="unbounded" minOccurs="0" name="secondPiePt" type="CT_UnsignedInt">
     * ```
     */
    secondPiePt?: ICTUnsignedInt[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_CustSplit name="CT_CustSplit">
 * ```
 */
export interface ICTCustSplitDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="unbounded" minOccurs="0" name="secondPiePt" type="CT_UnsignedInt">
     * ```
     */
    secondPiePt?: ICTUnsignedIntDelta[];
    secondPiePtOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_SecondPieSize name="CT_SecondPieSize">
 * ```
 */
export interface ICTSecondPieSize {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_SecondPieSizeUShort" default="75" name="val" type="ST_SecondPieSize">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_SecondPieSize name="CT_SecondPieSize">
 * ```
 */
export interface ICTSecondPieSizeDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_SecondPieSizeUShort" default="75" name="val" type="ST_SecondPieSize">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_SurfaceSer name="CT_SurfaceSer">
 * ```
 */
export interface ICTSurfaceSer {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="order" type="CT_UnsignedInt">
     * ```
     */
    order?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SerTx maxOccurs="1" minOccurs="0" name="tx" type="CT_SerTx">
     * ```
     */
    tx?: ICTSerTx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxDataSource maxOccurs="1" minOccurs="0" name="cat" type="CT_AxDataSource">
     * ```
     */
    cat?: ICTAxDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="val" type="CT_NumDataSource">
     * ```
     */
    val?: ICTNumDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="listIndex" skipIO="true" type="CT_UnsignedInt">
     * ```
     */
    listIndex?: ICTUnsignedInt;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_SurfaceSer name="CT_SurfaceSer">
 * ```
 */
export interface ICTSurfaceSerDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="order" type="CT_UnsignedInt">
     * ```
     */
    order?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SerTx maxOccurs="1" minOccurs="0" name="tx" type="CT_SerTx">
     * ```
     */
    tx?: ICTSerTxDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxDataSource maxOccurs="1" minOccurs="0" name="cat" type="CT_AxDataSource">
     * ```
     */
    cat?: ICTAxDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="val" type="CT_NumDataSource">
     * ```
     */
    val?: ICTNumDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="listIndex" skipIO="true" type="CT_UnsignedInt">
     * ```
     */
    listIndex?: ICTUnsignedIntDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BandFmts name="CT_BandFmts">
 * ```
 */
export interface ICTBandFmts {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BandFmt maxOccurs="unbounded" minOccurs="0" name="bandFmt" type="CT_BandFmt">
     * ```
     */
    bandFmt?: ICTBandFmt[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BandFmts name="CT_BandFmts">
 * ```
 */
export interface ICTBandFmtsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BandFmt maxOccurs="unbounded" minOccurs="0" name="bandFmt" type="CT_BandFmt">
     * ```
     */
    bandFmt?: ICTBandFmtDelta[];
    bandFmtOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BubbleSer name="CT_BubbleSer">
 * ```
 */
export interface ICTBubbleSer {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="order" type="CT_UnsignedInt">
     * ```
     */
    order?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SerTx maxOccurs="1" minOccurs="0" name="tx" type="CT_SerTx">
     * ```
     */
    tx?: ICTSerTx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="invertIfNegative" type="CT_BooleanDmlC">
     * ```
     */
    invertIfNegative?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DPt maxOccurs="unbounded" minOccurs="0" name="dPt" type="CT_DPt">
     * ```
     */
    dPt?: ICTDPt[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLbls;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Trendline maxOccurs="unbounded" minOccurs="0" name="trendline" type="CT_Trendline">
     * ```
     */
    trendline?: ICTTrendline[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ErrBars maxOccurs="2" minOccurs="0" name="errBars" type="CT_ErrBars">
     * ```
     */
    errBars?: ICTErrBars[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxDataSource maxOccurs="1" minOccurs="0" name="xVal" type="CT_AxDataSource">
     * ```
     */
    xVal?: ICTAxDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="yVal" type="CT_NumDataSource">
     * ```
     */
    yVal?: ICTNumDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="bubbleSize" type="CT_NumDataSource">
     * ```
     */
    bubbleSize?: ICTNumDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="bubble3D" type="CT_BooleanDmlC">
     * ```
     */
    bubble3D?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="listIndex" skipIO="true" type="CT_UnsignedInt">
     * ```
     */
    listIndex?: ICTUnsignedInt;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BubbleSer name="CT_BubbleSer">
 * ```
 */
export interface ICTBubbleSerDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="order" type="CT_UnsignedInt">
     * ```
     */
    order?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SerTx maxOccurs="1" minOccurs="0" name="tx" type="CT_SerTx">
     * ```
     */
    tx?: ICTSerTxDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="invertIfNegative" type="CT_BooleanDmlC">
     * ```
     */
    invertIfNegative?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DPt maxOccurs="unbounded" minOccurs="0" name="dPt" type="CT_DPt">
     * ```
     */
    dPt?: ICTDPtDelta[];
    dPtOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbls maxOccurs="1" minOccurs="0" name="dLbls" type="CT_DLbls">
     * ```
     */
    dLbls?: ICTDLblsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Trendline maxOccurs="unbounded" minOccurs="0" name="trendline" type="CT_Trendline">
     * ```
     */
    trendline?: ICTTrendlineDelta[];
    trendlineOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ErrBars maxOccurs="2" minOccurs="0" name="errBars" type="CT_ErrBars">
     * ```
     */
    errBars?: ICTErrBarsDelta[];
    errBarsOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxDataSource maxOccurs="1" minOccurs="0" name="xVal" type="CT_AxDataSource">
     * ```
     */
    xVal?: ICTAxDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="yVal" type="CT_NumDataSource">
     * ```
     */
    yVal?: ICTNumDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="bubbleSize" type="CT_NumDataSource">
     * ```
     */
    bubbleSize?: ICTNumDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="bubble3D" type="CT_BooleanDmlC">
     * ```
     */
    bubble3D?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="listIndex" skipIO="true" type="CT_UnsignedInt">
     * ```
     */
    listIndex?: ICTUnsignedIntDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BubbleScale name="CT_BubbleScale">
 * ```
 */
export interface ICTBubbleScale {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:uint32 convType="ST_BubbleScaleUInt" default="100" name="val" type="ST_BubbleScale">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BubbleScale name="CT_BubbleScale">
 * ```
 */
export interface ICTBubbleScaleDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:uint32 convType="ST_BubbleScaleUInt" default="100" name="val" type="ST_BubbleScale">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_SizeRepresents name="CT_SizeRepresents">
 * ```
 */
export interface ICTSizeRepresents {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_SizeRepresents default="area" name="val" type="ST_SizeRepresents">
     * ```
     */
    val?: STSizeRepresents;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_SizeRepresents name="CT_SizeRepresents">
 * ```
 */
export interface ICTSizeRepresentsDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_SizeRepresents default="area" name="val" type="ST_SizeRepresents">
     * ```
     */
    val?: STSizeRepresents | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ValAx name="CT_ValAx">
 * ```
 */
export interface ICTValAx {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Scaling maxOccurs="1" minOccurs="1" name="scaling" type="CT_Scaling">
     * ```
     */
    scaling?: ICTScaling;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC aliasName=deleteKeyword aliasName="deleteKeyword" maxOccurs="1" minOccurs="0" name="delete" type="CT_BooleanDmlC">
     * ```
     */
    deleteKeyword?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxPos maxOccurs="1" minOccurs="1" name="axPos" type="CT_AxPos">
     * ```
     */
    axPos?: ICTAxPos;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="majorGridlines" type="CT_ChartLines">
     * ```
     */
    majorGridlines?: ICTChartLines;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="minorGridlines" type="CT_ChartLines">
     * ```
     */
    minorGridlines?: ICTChartLines;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Title maxOccurs="1" minOccurs="0" name="title" type="CT_Title">
     * ```
     */
    title?: ICTTitle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumFmtDmlC maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumFmtDmlC">
     * ```
     */
    numFmt?: ICTNumFmtDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickMark maxOccurs="1" minOccurs="0" name="majorTickMark" type="CT_TickMark">
     * ```
     */
    majorTickMark?: ICTTickMark;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickMark maxOccurs="1" minOccurs="0" name="minorTickMark" type="CT_TickMark">
     * ```
     */
    minorTickMark?: ICTTickMark;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickLblPos maxOccurs="1" minOccurs="0" name="tickLblPos" type="CT_TickLblPos">
     * ```
     */
    tickLblPos?: ICTTickLblPos;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="crossAx" type="CT_UnsignedInt">
     * ```
     */
    crossAx?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Crosses maxOccurs="1" minOccurs="1" name="crosses" type="CT_Crosses">
     * ```
     */
    crosses?: ICTCrosses;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="1" name="crossesAt" type="CT_Double">
     * ```
     */
    crossesAt?: ICTDouble;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_CrossBetween maxOccurs="1" minOccurs="0" name="crossBetween" type="CT_CrossBetween">
     * ```
     */
    crossBetween?: ICTCrossBetween;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxisUnit maxOccurs="1" minOccurs="0" name="majorUnit" type="CT_AxisUnit">
     * ```
     */
    majorUnit?: ICTAxisUnit;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxisUnit maxOccurs="1" minOccurs="0" name="minorUnit" type="CT_AxisUnit">
     * ```
     */
    minorUnit?: ICTAxisUnit;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DispUnits maxOccurs="1" minOccurs="0" name="dispUnits" type="CT_DispUnits">
     * ```
     */
    dispUnits?: ICTDispUnits;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ValAx name="CT_ValAx">
 * ```
 */
export interface ICTValAxDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Scaling maxOccurs="1" minOccurs="1" name="scaling" type="CT_Scaling">
     * ```
     */
    scaling?: ICTScalingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC aliasName=deleteKeyword aliasName="deleteKeyword" maxOccurs="1" minOccurs="0" name="delete" type="CT_BooleanDmlC">
     * ```
     */
    deleteKeyword?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxPos maxOccurs="1" minOccurs="1" name="axPos" type="CT_AxPos">
     * ```
     */
    axPos?: ICTAxPosDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="majorGridlines" type="CT_ChartLines">
     * ```
     */
    majorGridlines?: ICTChartLinesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="minorGridlines" type="CT_ChartLines">
     * ```
     */
    minorGridlines?: ICTChartLinesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Title maxOccurs="1" minOccurs="0" name="title" type="CT_Title">
     * ```
     */
    title?: ICTTitleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumFmtDmlC maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumFmtDmlC">
     * ```
     */
    numFmt?: ICTNumFmtDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickMark maxOccurs="1" minOccurs="0" name="majorTickMark" type="CT_TickMark">
     * ```
     */
    majorTickMark?: ICTTickMarkDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickMark maxOccurs="1" minOccurs="0" name="minorTickMark" type="CT_TickMark">
     * ```
     */
    minorTickMark?: ICTTickMarkDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickLblPos maxOccurs="1" minOccurs="0" name="tickLblPos" type="CT_TickLblPos">
     * ```
     */
    tickLblPos?: ICTTickLblPosDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="crossAx" type="CT_UnsignedInt">
     * ```
     */
    crossAx?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Crosses maxOccurs="1" minOccurs="1" name="crosses" type="CT_Crosses">
     * ```
     */
    crosses?: ICTCrossesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="1" name="crossesAt" type="CT_Double">
     * ```
     */
    crossesAt?: ICTDoubleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_CrossBetween maxOccurs="1" minOccurs="0" name="crossBetween" type="CT_CrossBetween">
     * ```
     */
    crossBetween?: ICTCrossBetweenDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxisUnit maxOccurs="1" minOccurs="0" name="majorUnit" type="CT_AxisUnit">
     * ```
     */
    majorUnit?: ICTAxisUnitDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxisUnit maxOccurs="1" minOccurs="0" name="minorUnit" type="CT_AxisUnit">
     * ```
     */
    minorUnit?: ICTAxisUnitDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DispUnits maxOccurs="1" minOccurs="0" name="dispUnits" type="CT_DispUnits">
     * ```
     */
    dispUnits?: ICTDispUnitsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_CatAx name="CT_CatAx">
 * ```
 */
export interface ICTCatAx {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Scaling maxOccurs="1" minOccurs="1" name="scaling" type="CT_Scaling">
     * ```
     */
    scaling?: ICTScaling;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC aliasName=deleteKeyword aliasName="deleteKeyword" maxOccurs="1" minOccurs="0" name="delete" type="CT_BooleanDmlC">
     * ```
     */
    deleteKeyword?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxPos maxOccurs="1" minOccurs="1" name="axPos" type="CT_AxPos">
     * ```
     */
    axPos?: ICTAxPos;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="majorGridlines" type="CT_ChartLines">
     * ```
     */
    majorGridlines?: ICTChartLines;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="minorGridlines" type="CT_ChartLines">
     * ```
     */
    minorGridlines?: ICTChartLines;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Title maxOccurs="1" minOccurs="0" name="title" type="CT_Title">
     * ```
     */
    title?: ICTTitle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumFmtDmlC maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumFmtDmlC">
     * ```
     */
    numFmt?: ICTNumFmtDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickMark maxOccurs="1" minOccurs="0" name="majorTickMark" type="CT_TickMark">
     * ```
     */
    majorTickMark?: ICTTickMark;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickMark maxOccurs="1" minOccurs="0" name="minorTickMark" type="CT_TickMark">
     * ```
     */
    minorTickMark?: ICTTickMark;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickLblPos maxOccurs="1" minOccurs="0" name="tickLblPos" type="CT_TickLblPos">
     * ```
     */
    tickLblPos?: ICTTickLblPos;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="crossAx" type="CT_UnsignedInt">
     * ```
     */
    crossAx?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Crosses maxOccurs="1" minOccurs="1" name="crosses" type="CT_Crosses">
     * ```
     */
    crosses?: ICTCrosses;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="1" name="crossesAt" type="CT_Double">
     * ```
     */
    crossesAt?: ICTDouble;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC aliasName=autoKeyword aliasName="autoKeyword" maxOccurs="1" minOccurs="0" name="auto" type="CT_BooleanDmlC">
     * ```
     */
    autoKeyword?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LblAlgn maxOccurs="1" minOccurs="0" name="lblAlgn" type="CT_LblAlgn">
     * ```
     */
    lblAlgn?: ICTLblAlgn;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LblOffset maxOccurs="1" minOccurs="0" name="lblOffset" type="CT_LblOffset">
     * ```
     */
    lblOffset?: ICTLblOffset;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Skip maxOccurs="1" minOccurs="0" name="tickLblSkip" type="CT_Skip">
     * ```
     */
    tickLblSkip?: ICTSkip;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Skip maxOccurs="1" minOccurs="0" name="tickMarkSkip" type="CT_Skip">
     * ```
     */
    tickMarkSkip?: ICTSkip;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="noMultiLvlLbl" type="CT_BooleanDmlC">
     * ```
     */
    noMultiLvlLbl?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_CatAx name="CT_CatAx">
 * ```
 */
export interface ICTCatAxDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Scaling maxOccurs="1" minOccurs="1" name="scaling" type="CT_Scaling">
     * ```
     */
    scaling?: ICTScalingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC aliasName=deleteKeyword aliasName="deleteKeyword" maxOccurs="1" minOccurs="0" name="delete" type="CT_BooleanDmlC">
     * ```
     */
    deleteKeyword?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxPos maxOccurs="1" minOccurs="1" name="axPos" type="CT_AxPos">
     * ```
     */
    axPos?: ICTAxPosDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="majorGridlines" type="CT_ChartLines">
     * ```
     */
    majorGridlines?: ICTChartLinesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="minorGridlines" type="CT_ChartLines">
     * ```
     */
    minorGridlines?: ICTChartLinesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Title maxOccurs="1" minOccurs="0" name="title" type="CT_Title">
     * ```
     */
    title?: ICTTitleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumFmtDmlC maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumFmtDmlC">
     * ```
     */
    numFmt?: ICTNumFmtDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickMark maxOccurs="1" minOccurs="0" name="majorTickMark" type="CT_TickMark">
     * ```
     */
    majorTickMark?: ICTTickMarkDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickMark maxOccurs="1" minOccurs="0" name="minorTickMark" type="CT_TickMark">
     * ```
     */
    minorTickMark?: ICTTickMarkDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickLblPos maxOccurs="1" minOccurs="0" name="tickLblPos" type="CT_TickLblPos">
     * ```
     */
    tickLblPos?: ICTTickLblPosDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="crossAx" type="CT_UnsignedInt">
     * ```
     */
    crossAx?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Crosses maxOccurs="1" minOccurs="1" name="crosses" type="CT_Crosses">
     * ```
     */
    crosses?: ICTCrossesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="1" name="crossesAt" type="CT_Double">
     * ```
     */
    crossesAt?: ICTDoubleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC aliasName=autoKeyword aliasName="autoKeyword" maxOccurs="1" minOccurs="0" name="auto" type="CT_BooleanDmlC">
     * ```
     */
    autoKeyword?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LblAlgn maxOccurs="1" minOccurs="0" name="lblAlgn" type="CT_LblAlgn">
     * ```
     */
    lblAlgn?: ICTLblAlgnDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LblOffset maxOccurs="1" minOccurs="0" name="lblOffset" type="CT_LblOffset">
     * ```
     */
    lblOffset?: ICTLblOffsetDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Skip maxOccurs="1" minOccurs="0" name="tickLblSkip" type="CT_Skip">
     * ```
     */
    tickLblSkip?: ICTSkipDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Skip maxOccurs="1" minOccurs="0" name="tickMarkSkip" type="CT_Skip">
     * ```
     */
    tickMarkSkip?: ICTSkipDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="noMultiLvlLbl" type="CT_BooleanDmlC">
     * ```
     */
    noMultiLvlLbl?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DateAx name="CT_DateAx">
 * ```
 */
export interface ICTDateAx {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Scaling maxOccurs="1" minOccurs="1" name="scaling" type="CT_Scaling">
     * ```
     */
    scaling?: ICTScaling;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC aliasName=deleteKeyword aliasName="deleteKeyword" maxOccurs="1" minOccurs="0" name="delete" type="CT_BooleanDmlC">
     * ```
     */
    deleteKeyword?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxPos maxOccurs="1" minOccurs="1" name="axPos" type="CT_AxPos">
     * ```
     */
    axPos?: ICTAxPos;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="majorGridlines" type="CT_ChartLines">
     * ```
     */
    majorGridlines?: ICTChartLines;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="minorGridlines" type="CT_ChartLines">
     * ```
     */
    minorGridlines?: ICTChartLines;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Title maxOccurs="1" minOccurs="0" name="title" type="CT_Title">
     * ```
     */
    title?: ICTTitle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumFmtDmlC maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumFmtDmlC">
     * ```
     */
    numFmt?: ICTNumFmtDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickMark maxOccurs="1" minOccurs="0" name="majorTickMark" type="CT_TickMark">
     * ```
     */
    majorTickMark?: ICTTickMark;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickMark maxOccurs="1" minOccurs="0" name="minorTickMark" type="CT_TickMark">
     * ```
     */
    minorTickMark?: ICTTickMark;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickLblPos maxOccurs="1" minOccurs="0" name="tickLblPos" type="CT_TickLblPos">
     * ```
     */
    tickLblPos?: ICTTickLblPos;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="crossAx" type="CT_UnsignedInt">
     * ```
     */
    crossAx?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Crosses maxOccurs="1" minOccurs="1" name="crosses" type="CT_Crosses">
     * ```
     */
    crosses?: ICTCrosses;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="1" name="crossesAt" type="CT_Double">
     * ```
     */
    crossesAt?: ICTDouble;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC aliasName=autoKeyword aliasName="autoKeyword" maxOccurs="1" minOccurs="0" name="auto" type="CT_BooleanDmlC">
     * ```
     */
    autoKeyword?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LblOffset maxOccurs="1" minOccurs="0" name="lblOffset" type="CT_LblOffset">
     * ```
     */
    lblOffset?: ICTLblOffset;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TimeUnit maxOccurs="1" minOccurs="0" name="baseTimeUnit" type="CT_TimeUnit">
     * ```
     */
    baseTimeUnit?: ICTTimeUnit;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxisUnit maxOccurs="1" minOccurs="0" name="majorUnit" type="CT_AxisUnit">
     * ```
     */
    majorUnit?: ICTAxisUnit;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TimeUnit maxOccurs="1" minOccurs="0" name="majorTimeUnit" type="CT_TimeUnit">
     * ```
     */
    majorTimeUnit?: ICTTimeUnit;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxisUnit maxOccurs="1" minOccurs="0" name="minorUnit" type="CT_AxisUnit">
     * ```
     */
    minorUnit?: ICTAxisUnit;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TimeUnit maxOccurs="1" minOccurs="0" name="minorTimeUnit" type="CT_TimeUnit">
     * ```
     */
    minorTimeUnit?: ICTTimeUnit;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DateAx name="CT_DateAx">
 * ```
 */
export interface ICTDateAxDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Scaling maxOccurs="1" minOccurs="1" name="scaling" type="CT_Scaling">
     * ```
     */
    scaling?: ICTScalingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC aliasName=deleteKeyword aliasName="deleteKeyword" maxOccurs="1" minOccurs="0" name="delete" type="CT_BooleanDmlC">
     * ```
     */
    deleteKeyword?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxPos maxOccurs="1" minOccurs="1" name="axPos" type="CT_AxPos">
     * ```
     */
    axPos?: ICTAxPosDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="majorGridlines" type="CT_ChartLines">
     * ```
     */
    majorGridlines?: ICTChartLinesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="minorGridlines" type="CT_ChartLines">
     * ```
     */
    minorGridlines?: ICTChartLinesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Title maxOccurs="1" minOccurs="0" name="title" type="CT_Title">
     * ```
     */
    title?: ICTTitleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumFmtDmlC maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumFmtDmlC">
     * ```
     */
    numFmt?: ICTNumFmtDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickMark maxOccurs="1" minOccurs="0" name="majorTickMark" type="CT_TickMark">
     * ```
     */
    majorTickMark?: ICTTickMarkDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickMark maxOccurs="1" minOccurs="0" name="minorTickMark" type="CT_TickMark">
     * ```
     */
    minorTickMark?: ICTTickMarkDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickLblPos maxOccurs="1" minOccurs="0" name="tickLblPos" type="CT_TickLblPos">
     * ```
     */
    tickLblPos?: ICTTickLblPosDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="crossAx" type="CT_UnsignedInt">
     * ```
     */
    crossAx?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Crosses maxOccurs="1" minOccurs="1" name="crosses" type="CT_Crosses">
     * ```
     */
    crosses?: ICTCrossesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="1" name="crossesAt" type="CT_Double">
     * ```
     */
    crossesAt?: ICTDoubleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC aliasName=autoKeyword aliasName="autoKeyword" maxOccurs="1" minOccurs="0" name="auto" type="CT_BooleanDmlC">
     * ```
     */
    autoKeyword?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LblOffset maxOccurs="1" minOccurs="0" name="lblOffset" type="CT_LblOffset">
     * ```
     */
    lblOffset?: ICTLblOffsetDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TimeUnit maxOccurs="1" minOccurs="0" name="baseTimeUnit" type="CT_TimeUnit">
     * ```
     */
    baseTimeUnit?: ICTTimeUnitDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxisUnit maxOccurs="1" minOccurs="0" name="majorUnit" type="CT_AxisUnit">
     * ```
     */
    majorUnit?: ICTAxisUnitDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TimeUnit maxOccurs="1" minOccurs="0" name="majorTimeUnit" type="CT_TimeUnit">
     * ```
     */
    majorTimeUnit?: ICTTimeUnitDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxisUnit maxOccurs="1" minOccurs="0" name="minorUnit" type="CT_AxisUnit">
     * ```
     */
    minorUnit?: ICTAxisUnitDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TimeUnit maxOccurs="1" minOccurs="0" name="minorTimeUnit" type="CT_TimeUnit">
     * ```
     */
    minorTimeUnit?: ICTTimeUnitDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_SerAx name="CT_SerAx">
 * ```
 */
export interface ICTSerAx {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Scaling maxOccurs="1" minOccurs="1" name="scaling" type="CT_Scaling">
     * ```
     */
    scaling?: ICTScaling;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC aliasName=deleteKeyword aliasName="deleteKeyword" maxOccurs="1" minOccurs="0" name="delete" type="CT_BooleanDmlC">
     * ```
     */
    deleteKeyword?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxPos maxOccurs="1" minOccurs="1" name="axPos" type="CT_AxPos">
     * ```
     */
    axPos?: ICTAxPos;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="majorGridlines" type="CT_ChartLines">
     * ```
     */
    majorGridlines?: ICTChartLines;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="minorGridlines" type="CT_ChartLines">
     * ```
     */
    minorGridlines?: ICTChartLines;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Title maxOccurs="1" minOccurs="0" name="title" type="CT_Title">
     * ```
     */
    title?: ICTTitle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumFmtDmlC maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumFmtDmlC">
     * ```
     */
    numFmt?: ICTNumFmtDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickMark maxOccurs="1" minOccurs="0" name="majorTickMark" type="CT_TickMark">
     * ```
     */
    majorTickMark?: ICTTickMark;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickMark maxOccurs="1" minOccurs="0" name="minorTickMark" type="CT_TickMark">
     * ```
     */
    minorTickMark?: ICTTickMark;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickLblPos maxOccurs="1" minOccurs="0" name="tickLblPos" type="CT_TickLblPos">
     * ```
     */
    tickLblPos?: ICTTickLblPos;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="crossAx" type="CT_UnsignedInt">
     * ```
     */
    crossAx?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Crosses maxOccurs="1" minOccurs="1" name="crosses" type="CT_Crosses">
     * ```
     */
    crosses?: ICTCrosses;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="1" name="crossesAt" type="CT_Double">
     * ```
     */
    crossesAt?: ICTDouble;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Skip maxOccurs="1" minOccurs="0" name="tickLblSkip" type="CT_Skip">
     * ```
     */
    tickLblSkip?: ICTSkip;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Skip maxOccurs="1" minOccurs="0" name="tickMarkSkip" type="CT_Skip">
     * ```
     */
    tickMarkSkip?: ICTSkip;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_SerAx name="CT_SerAx">
 * ```
 */
export interface ICTSerAxDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="axId" type="CT_UnsignedInt">
     * ```
     */
    axId?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Scaling maxOccurs="1" minOccurs="1" name="scaling" type="CT_Scaling">
     * ```
     */
    scaling?: ICTScalingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC aliasName=deleteKeyword aliasName="deleteKeyword" maxOccurs="1" minOccurs="0" name="delete" type="CT_BooleanDmlC">
     * ```
     */
    deleteKeyword?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxPos maxOccurs="1" minOccurs="1" name="axPos" type="CT_AxPos">
     * ```
     */
    axPos?: ICTAxPosDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="majorGridlines" type="CT_ChartLines">
     * ```
     */
    majorGridlines?: ICTChartLinesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartLines maxOccurs="1" minOccurs="0" name="minorGridlines" type="CT_ChartLines">
     * ```
     */
    minorGridlines?: ICTChartLinesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Title maxOccurs="1" minOccurs="0" name="title" type="CT_Title">
     * ```
     */
    title?: ICTTitleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumFmtDmlC maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumFmtDmlC">
     * ```
     */
    numFmt?: ICTNumFmtDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickMark maxOccurs="1" minOccurs="0" name="majorTickMark" type="CT_TickMark">
     * ```
     */
    majorTickMark?: ICTTickMarkDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickMark maxOccurs="1" minOccurs="0" name="minorTickMark" type="CT_TickMark">
     * ```
     */
    minorTickMark?: ICTTickMarkDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TickLblPos maxOccurs="1" minOccurs="0" name="tickLblPos" type="CT_TickLblPos">
     * ```
     */
    tickLblPos?: ICTTickLblPosDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="crossAx" type="CT_UnsignedInt">
     * ```
     */
    crossAx?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Crosses maxOccurs="1" minOccurs="1" name="crosses" type="CT_Crosses">
     * ```
     */
    crosses?: ICTCrossesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="1" name="crossesAt" type="CT_Double">
     * ```
     */
    crossesAt?: ICTDoubleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Skip maxOccurs="1" minOccurs="0" name="tickLblSkip" type="CT_Skip">
     * ```
     */
    tickLblSkip?: ICTSkipDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Skip maxOccurs="1" minOccurs="0" name="tickMarkSkip" type="CT_Skip">
     * ```
     */
    tickMarkSkip?: ICTSkipDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GeomGuideList name="CT_GeomGuideList">
 * ```
 */
export interface ICTGeomGuideList {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GeomGuide maxOccurs="unbounded" minOccurs="0" name="gd" type="CT_GeomGuide">
     * ```
     */
    gd?: ICTGeomGuide[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GeomGuideList name="CT_GeomGuideList">
 * ```
 */
export interface ICTGeomGuideListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GeomGuide maxOccurs="unbounded" minOccurs="0" name="gd" type="CT_GeomGuide">
     * ```
     */
    gd?: ICTGeomGuideDelta[];
    gdOp?: IArrayOp;
}

export enum STTextShapeType {
    PBDEFAULT = 0,
    textNoShape = 1,
    textPlain = 2,
    textStop = 3,
    textTriangle = 4,
    textTriangleInverted = 5,
    textChevron = 6,
    textChevronInverted = 7,
    textRingInside = 8,
    textRingOutside = 9,
    textArchUp = 10,
    textArchDown = 11,
    textCircle = 12,
    textButton = 13,
    textArchUpPour = 14,
    textArchDownPour = 15,
    textCirclePour = 16,
    textButtonPour = 17,
    textCurveUp = 18,
    textCurveDown = 19,
    textCanUp = 20,
    textCanDown = 21,
    textWave1 = 22,
    textWave2 = 23,
    textDoubleWave1 = 24,
    textWave4 = 25,
    textInflate = 26,
    textDeflate = 27,
    textInflateBottom = 28,
    textDeflateBottom = 29,
    textInflateTop = 30,
    textDeflateTop = 31,
    textDeflateInflate = 32,
    textDeflateInflateDeflate = 33,
    textFadeRight = 34,
    textFadeLeft = 35,
    textFadeUp = 36,
    textFadeDown = 37,
    textSlantUp = 38,
    textSlantDown = 39,
    textCascadeUp = 40,
    textCascadeDown = 41,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Camera name="CT_Camera">
 * ```
 */
export interface ICTCamera {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SphereCoords maxOccurs="1" minOccurs="0" name="rot" type="CT_SphereCoords">
     * ```
     */
    rot?: ICTSphereCoords;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PresetCameraType name="prst" type="ST_PresetCameraType" use="required">
     * ```
     */
    prst?: STPresetCameraType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="fov" type="ST_FOVAngle" use="optional">
     * ```
     */
    fov?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositivePercentageDecimal" default="100" name="zoom" type="ST_PositivePercentageDmlA" use="optional">
     * ```
     */
    zoom?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Camera name="CT_Camera">
 * ```
 */
export interface ICTCameraDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SphereCoords maxOccurs="1" minOccurs="0" name="rot" type="CT_SphereCoords">
     * ```
     */
    rot?: ICTSphereCoordsDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PresetCameraType name="prst" type="ST_PresetCameraType" use="required">
     * ```
     */
    prst?: STPresetCameraType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="fov" type="ST_FOVAngle" use="optional">
     * ```
     */
    fov?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositivePercentageDecimal" default="100" name="zoom" type="ST_PositivePercentageDmlA" use="optional">
     * ```
     */
    zoom?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Backdrop name="CT_Backdrop">
 * ```
 */
export interface ICTBackdrop {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point3D maxOccurs="1" minOccurs="1" name="anchor" type="CT_Point3D">
     * ```
     */
    anchor?: ICTPoint3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Vector3D maxOccurs="1" minOccurs="1" name="norm" type="CT_Vector3D">
     * ```
     */
    norm?: ICTVector3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Vector3D maxOccurs="1" minOccurs="1" name="up" type="CT_Vector3D">
     * ```
     */
    up?: ICTVector3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Backdrop name="CT_Backdrop">
 * ```
 */
export interface ICTBackdropDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point3D maxOccurs="1" minOccurs="1" name="anchor" type="CT_Point3D">
     * ```
     */
    anchor?: ICTPoint3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Vector3D maxOccurs="1" minOccurs="1" name="norm" type="CT_Vector3D">
     * ```
     */
    norm?: ICTVector3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Vector3D maxOccurs="1" minOccurs="1" name="up" type="CT_Vector3D">
     * ```
     */
    up?: ICTVector3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Pt name="CT_Pt">
 * ```
 */
export interface ICTPt {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ElemPropSet maxOccurs="1" minOccurs="0" name="prSet" type="CT_ElemPropSet">
     * ```
     */
    prSet?: ICTElemPropSet;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="t" type="a:CT_TextBody">
     * ```
     */
    t?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="modelId" type="ST_ModelId" use="required">
     * ```
     */
    modelId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_PtType default="node" name="type" type="ST_PtType" use="optional">
     * ```
     */
    type?: STPtType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" default="0" name="cxnId" type="ST_ModelId" use="optional">
     * ```
     */
    cxnId?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Pt name="CT_Pt">
 * ```
 */
export interface ICTPtDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ElemPropSet maxOccurs="1" minOccurs="0" name="prSet" type="CT_ElemPropSet">
     * ```
     */
    prSet?: ICTElemPropSetDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="t" type="a:CT_TextBody">
     * ```
     */
    t?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="modelId" type="ST_ModelId" use="required">
     * ```
     */
    modelId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_PtType default="node" name="type" type="ST_PtType" use="optional">
     * ```
     */
    type?: STPtType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" default="0" name="cxnId" type="ST_ModelId" use="optional">
     * ```
     */
    cxnId?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Cxn name="CT_Cxn">
 * ```
 */
export interface ICTCxn {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="modelId" type="ST_ModelId" use="required">
     * ```
     */
    modelId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_CxnType default="parOf" name="type" type="ST_CxnType" use="optional">
     * ```
     */
    type?: STCxnType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="srcId" type="ST_ModelId" use="required">
     * ```
     */
    srcId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="destId" type="ST_ModelId" use="required">
     * ```
     */
    destId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="srcOrd" type="xsd:unsignedInt" use="required">
     * ```
     */
    srcOrd?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="destOrd" type="xsd:unsignedInt" use="required">
     * ```
     */
    destOrd?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" default="0" name="parTransId" type="ST_ModelId" use="optional">
     * ```
     */
    parTransId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" default="0" name="sibTransId" type="ST_ModelId" use="optional">
     * ```
     */
    sibTransId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="presId" type="xsd:string" use="optional">
     * ```
     */
    presId?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Cxn name="CT_Cxn">
 * ```
 */
export interface ICTCxnDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="modelId" type="ST_ModelId" use="required">
     * ```
     */
    modelId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_CxnType default="parOf" name="type" type="ST_CxnType" use="optional">
     * ```
     */
    type?: STCxnType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="srcId" type="ST_ModelId" use="required">
     * ```
     */
    srcId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="destId" type="ST_ModelId" use="required">
     * ```
     */
    destId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="srcOrd" type="xsd:unsignedInt" use="required">
     * ```
     */
    srcOrd?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="destOrd" type="xsd:unsignedInt" use="required">
     * ```
     */
    destOrd?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" default="0" name="parTransId" type="ST_ModelId" use="optional">
     * ```
     */
    parTransId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" default="0" name="sibTransId" type="ST_ModelId" use="optional">
     * ```
     */
    sibTransId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="presId" type="xsd:string" use="optional">
     * ```
     */
    presId?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdsp2008:CT_GroupShapeNonVisualMsDsp name="CT_GroupShapeNonVisualMsDsp">
 * ```
 */
export interface ICTGroupShapeNonVisualMsDsp {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingProps maxOccurs="1" minOccurs="1" name="cNvPr" type="a:CT_NonVisualDrawingProps">
     * ```
     */
    cNvPr?: ICTNonVisualDrawingProps;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualGroupDrawingShapeProps maxOccurs="1" minOccurs="1" name="cNvGrpSpPr" type="a:CT_NonVisualGroupDrawingShapeProps">
     * ```
     */
    cNvGrpSpPr?: ICTNonVisualGroupDrawingShapeProps;
}

/**
 * ```xml
 * <xsd:complexType name=msdsp2008:CT_GroupShapeNonVisualMsDsp name="CT_GroupShapeNonVisualMsDsp">
 * ```
 */
export interface ICTGroupShapeNonVisualMsDspDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingProps maxOccurs="1" minOccurs="1" name="cNvPr" type="a:CT_NonVisualDrawingProps">
     * ```
     */
    cNvPr?: ICTNonVisualDrawingPropsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualGroupDrawingShapeProps maxOccurs="1" minOccurs="1" name="cNvGrpSpPr" type="a:CT_NonVisualGroupDrawingShapeProps">
     * ```
     */
    cNvGrpSpPr?: ICTNonVisualGroupDrawingShapePropsDelta | null;
}

/**
 * ```xml
 * <xsd:group name=msdsp2008:EG_GroupShapeMsDsp name="EG_GroupShapeMsDsp">
 * ```
 */
export interface IEGGroupShapeMsDsp {
    /**
     * ```xml
     * <xsd:element typeRedirected=msdsp2008:CT_ShapeMsDsp name="sp" type="CT_ShapeMsDsp">
     * ```
     */
    sp?: ICTShapeMsDsp;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdsp2008:CT_GroupShapeMsDsp name="grpSp" type="CT_GroupShapeMsDsp">
     * ```
     */
    grpSp?: ICTGroupShapeMsDsp;
}

/**
 * ```xml
 * <xsd:group name=msdsp2008:EG_GroupShapeMsDsp name="EG_GroupShapeMsDsp">
 * ```
 */
export interface IEGGroupShapeMsDspDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msdsp2008:CT_ShapeMsDsp name="sp" type="CT_ShapeMsDsp">
     * ```
     */
    sp?: ICTShapeMsDspDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdsp2008:CT_GroupShapeMsDsp name="grpSp" type="CT_GroupShapeMsDsp">
     * ```
     */
    grpSp?: ICTGroupShapeMsDspDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Category name="CT_Category">
 * ```
 */
export interface ICTCategory {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="type" type="xsd:anyURI" use="required">
     * ```
     */
    type?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="pri" type="xsd:unsignedInt" use="required">
     * ```
     */
    pri?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Category name="CT_Category">
 * ```
 */
export interface ICTCategoryDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="type" type="xsd:anyURI" use="required">
     * ```
     */
    type?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="pri" type="xsd:unsignedInt" use="required">
     * ```
     */
    pri?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Algorithm name="CT_Algorithm">
 * ```
 */
export interface ICTAlgorithm {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ParameterDmlDgm maxOccurs="unbounded" minOccurs="0" name="param" type="CT_ParameterDmlDgm">
     * ```
     */
    param?: ICTParameterDmlDgm[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_AlgorithmType name="type" type="ST_AlgorithmType" use="required">
     * ```
     */
    type?: STAlgorithmType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="0" name="rev" type="xsd:unsignedInt" use="optional">
     * ```
     */
    rev?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Algorithm name="CT_Algorithm">
 * ```
 */
export interface ICTAlgorithmDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ParameterDmlDgm maxOccurs="unbounded" minOccurs="0" name="param" type="CT_ParameterDmlDgm">
     * ```
     */
    param?: ICTParameterDmlDgmDelta[];
    paramOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_AlgorithmType name="type" type="ST_AlgorithmType" use="required">
     * ```
     */
    type?: STAlgorithmType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="0" name="rev" type="xsd:unsignedInt" use="optional">
     * ```
     */
    rev?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_ShapeDmlDgm name="CT_ShapeDmlDgm">
 * ```
 */
export interface ICTShapeDmlDgm {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_AdjLst maxOccurs="1" minOccurs="0" name="adjLst" type="CT_AdjLst">
     * ```
     */
    adjLst?: ICTAdjLst;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double default="0" name="rot" type="xsd:double" use="optional">
     * ```
     */
    rot?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:blip default="" name="blip" ref="r:blip" type="ST_RelationshipId" use="optional">
     * ```
     */
    blip?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="zOrderOff" type="xsd:int" use="optional">
     * ```
     */
    zOrderOff?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="hideGeom" type="xsd:boolean" use="optional">
     * ```
     */
    hideGeom?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="lkTxEntry" type="xsd:boolean" use="optional">
     * ```
     */
    lkTxEntry?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="blipPhldr" type="xsd:boolean" use="optional">
     * ```
     */
    blipPhldr?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_LayoutShapeType default="none" name="type" type="ST_LayoutShapeType" use="optional">
     * ```
     */
    type?: STLayoutShapeType;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_ShapeDmlDgm name="CT_ShapeDmlDgm">
 * ```
 */
export interface ICTShapeDmlDgmDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_AdjLst maxOccurs="1" minOccurs="0" name="adjLst" type="CT_AdjLst">
     * ```
     */
    adjLst?: ICTAdjLstDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double default="0" name="rot" type="xsd:double" use="optional">
     * ```
     */
    rot?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:blip default="" name="blip" ref="r:blip" type="ST_RelationshipId" use="optional">
     * ```
     */
    blip?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="zOrderOff" type="xsd:int" use="optional">
     * ```
     */
    zOrderOff?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="hideGeom" type="xsd:boolean" use="optional">
     * ```
     */
    hideGeom?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="lkTxEntry" type="xsd:boolean" use="optional">
     * ```
     */
    lkTxEntry?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="blipPhldr" type="xsd:boolean" use="optional">
     * ```
     */
    blipPhldr?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_LayoutShapeType default="none" name="type" type="ST_LayoutShapeType" use="optional">
     * ```
     */
    type?: STLayoutShapeType | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_PresentationOf name="CT_PresentationOf">
 * ```
 */
export interface ICTPresentationOf {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="none" name="axis" type="ST_AxisTypes" use="optional">
     * ```
     */
    axis?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="all" name="ptType" type="ST_ElementTypes" use="optional">
     * ```
     */
    ptType?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="true" name="hideLastTrans" type="ST_Booleans" use="optional">
     * ```
     */
    hideLastTrans?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="1" name="st" type="ST_Ints" use="optional">
     * ```
     */
    st?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="0" name="cnt" type="ST_UnsignedInts" use="optional">
     * ```
     */
    cnt?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="1" name="step" type="ST_Ints" use="optional">
     * ```
     */
    step?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_PresentationOf name="CT_PresentationOf">
 * ```
 */
export interface ICTPresentationOfDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="none" name="axis" type="ST_AxisTypes" use="optional">
     * ```
     */
    axis?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="all" name="ptType" type="ST_ElementTypes" use="optional">
     * ```
     */
    ptType?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="true" name="hideLastTrans" type="ST_Booleans" use="optional">
     * ```
     */
    hideLastTrans?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="1" name="st" type="ST_Ints" use="optional">
     * ```
     */
    st?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="0" name="cnt" type="ST_UnsignedInts" use="optional">
     * ```
     */
    cnt?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="1" name="step" type="ST_Ints" use="optional">
     * ```
     */
    step?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Constraints name="CT_Constraints">
 * ```
 */
export interface ICTConstraints {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Constraint maxOccurs="unbounded" minOccurs="0" name="constr" type="CT_Constraint">
     * ```
     */
    constr?: ICTConstraint[];
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Constraints name="CT_Constraints">
 * ```
 */
export interface ICTConstraintsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Constraint maxOccurs="unbounded" minOccurs="0" name="constr" type="CT_Constraint">
     * ```
     */
    constr?: ICTConstraintDelta[];
    constrOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_RulesDmlDgm name="CT_RulesDmlDgm">
 * ```
 */
export interface ICTRulesDmlDgm {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_NumericRule maxOccurs="unbounded" minOccurs="0" name="rule" type="CT_NumericRule">
     * ```
     */
    rule?: ICTNumericRule[];
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_RulesDmlDgm name="CT_RulesDmlDgm">
 * ```
 */
export interface ICTRulesDmlDgmDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_NumericRule maxOccurs="unbounded" minOccurs="0" name="rule" type="CT_NumericRule">
     * ```
     */
    rule?: ICTNumericRuleDelta[];
    ruleOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_LayoutVariablePropertySet name="CT_LayoutVariablePropertySet">
 * ```
 */
export interface ICTLayoutVariablePropertySet {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_OrgChart maxOccurs="1" minOccurs="0" name="orgChart" type="CT_OrgChart">
     * ```
     */
    orgChart?: ICTOrgChart;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ChildMax maxOccurs="1" minOccurs="0" name="chMax" type="CT_ChildMax">
     * ```
     */
    chMax?: ICTChildMax;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ChildPref maxOccurs="1" minOccurs="0" name="chPref" type="CT_ChildPref">
     * ```
     */
    chPref?: ICTChildPref;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_BulletEnabled maxOccurs="1" minOccurs="0" name="bulletEnabled" type="CT_BulletEnabled">
     * ```
     */
    bulletEnabled?: ICTBulletEnabled;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Direction maxOccurs="1" minOccurs="0" name="dir" type="CT_Direction">
     * ```
     */
    dir?: ICTDirection;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_HierBranchStyle maxOccurs="1" minOccurs="0" name="hierBranch" type="CT_HierBranchStyle">
     * ```
     */
    hierBranch?: ICTHierBranchStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_AnimOne maxOccurs="1" minOccurs="0" name="animOne" type="CT_AnimOne">
     * ```
     */
    animOne?: ICTAnimOne;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_AnimLvl maxOccurs="1" minOccurs="0" name="animLvl" type="CT_AnimLvl">
     * ```
     */
    animLvl?: ICTAnimLvl;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ResizeHandles maxOccurs="1" minOccurs="0" name="resizeHandles" type="CT_ResizeHandles">
     * ```
     */
    resizeHandles?: ICTResizeHandles;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_LayoutVariablePropertySet name="CT_LayoutVariablePropertySet">
 * ```
 */
export interface ICTLayoutVariablePropertySetDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_OrgChart maxOccurs="1" minOccurs="0" name="orgChart" type="CT_OrgChart">
     * ```
     */
    orgChart?: ICTOrgChartDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ChildMax maxOccurs="1" minOccurs="0" name="chMax" type="CT_ChildMax">
     * ```
     */
    chMax?: ICTChildMaxDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ChildPref maxOccurs="1" minOccurs="0" name="chPref" type="CT_ChildPref">
     * ```
     */
    chPref?: ICTChildPrefDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_BulletEnabled maxOccurs="1" minOccurs="0" name="bulletEnabled" type="CT_BulletEnabled">
     * ```
     */
    bulletEnabled?: ICTBulletEnabledDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Direction maxOccurs="1" minOccurs="0" name="dir" type="CT_Direction">
     * ```
     */
    dir?: ICTDirectionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_HierBranchStyle maxOccurs="1" minOccurs="0" name="hierBranch" type="CT_HierBranchStyle">
     * ```
     */
    hierBranch?: ICTHierBranchStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_AnimOne maxOccurs="1" minOccurs="0" name="animOne" type="CT_AnimOne">
     * ```
     */
    animOne?: ICTAnimOneDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_AnimLvl maxOccurs="1" minOccurs="0" name="animLvl" type="CT_AnimLvl">
     * ```
     */
    animLvl?: ICTAnimLvlDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ResizeHandles maxOccurs="1" minOccurs="0" name="resizeHandles" type="CT_ResizeHandles">
     * ```
     */
    resizeHandles?: ICTResizeHandlesDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_ForEach name="CT_ForEach">
 * ```
 */
export interface ICTForEach {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Algorithm maxOccurs="1" minOccurs="0" name="alg" type="CT_Algorithm">
     * ```
     */
    alg?: ICTAlgorithm[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ShapeDmlDgm maxOccurs="1" minOccurs="0" name="shape" type="CT_ShapeDmlDgm">
     * ```
     */
    shape?: ICTShapeDmlDgm[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_PresentationOf maxOccurs="1" minOccurs="0" name="presOf" type="CT_PresentationOf">
     * ```
     */
    presOf?: ICTPresentationOf[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Constraints maxOccurs="1" minOccurs="0" name="constrLst" type="CT_Constraints">
     * ```
     */
    constrLst?: ICTConstraints[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_RulesDmlDgm maxOccurs="1" minOccurs="0" name="ruleLst" type="CT_RulesDmlDgm">
     * ```
     */
    ruleLst?: ICTRulesDmlDgm[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ForEach name="forEach" type="CT_ForEach">
     * ```
     */
    forEach?: ICTForEach[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_LayoutNode name="layoutNode" type="CT_LayoutNode">
     * ```
     */
    layoutNode?: ICTLayoutNode[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Choose name="choose" type="CT_Choose">
     * ```
     */
    choose?: ICTChoose[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="ref" type="xsd:string" use="optional">
     * ```
     */
    ref?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="none" name="axis" type="ST_AxisTypes" use="optional">
     * ```
     */
    axis?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="all" name="ptType" type="ST_ElementTypes" use="optional">
     * ```
     */
    ptType?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="true" name="hideLastTrans" type="ST_Booleans" use="optional">
     * ```
     */
    hideLastTrans?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="1" name="st" type="ST_Ints" use="optional">
     * ```
     */
    st?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="0" name="cnt" type="ST_UnsignedInts" use="optional">
     * ```
     */
    cnt?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="1" name="step" type="ST_Ints" use="optional">
     * ```
     */
    step?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_ForEach name="CT_ForEach">
 * ```
 */
export interface ICTForEachDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Algorithm maxOccurs="1" minOccurs="0" name="alg" type="CT_Algorithm">
     * ```
     */
    alg?: ICTAlgorithmDelta[];
    algOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ShapeDmlDgm maxOccurs="1" minOccurs="0" name="shape" type="CT_ShapeDmlDgm">
     * ```
     */
    shape?: ICTShapeDmlDgmDelta[];
    shapeOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_PresentationOf maxOccurs="1" minOccurs="0" name="presOf" type="CT_PresentationOf">
     * ```
     */
    presOf?: ICTPresentationOfDelta[];
    presOfOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Constraints maxOccurs="1" minOccurs="0" name="constrLst" type="CT_Constraints">
     * ```
     */
    constrLst?: ICTConstraintsDelta[];
    constrLstOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_RulesDmlDgm maxOccurs="1" minOccurs="0" name="ruleLst" type="CT_RulesDmlDgm">
     * ```
     */
    ruleLst?: ICTRulesDmlDgmDelta[];
    ruleLstOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ForEach name="forEach" type="CT_ForEach">
     * ```
     */
    forEach?: ICTForEachDelta[];
    forEachOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_LayoutNode name="layoutNode" type="CT_LayoutNode">
     * ```
     */
    layoutNode?: ICTLayoutNodeDelta[];
    layoutNodeOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Choose name="choose" type="CT_Choose">
     * ```
     */
    choose?: ICTChooseDelta[];
    chooseOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta[];
    extLstOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="ref" type="xsd:string" use="optional">
     * ```
     */
    ref?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="none" name="axis" type="ST_AxisTypes" use="optional">
     * ```
     */
    axis?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="all" name="ptType" type="ST_ElementTypes" use="optional">
     * ```
     */
    ptType?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="true" name="hideLastTrans" type="ST_Booleans" use="optional">
     * ```
     */
    hideLastTrans?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="1" name="st" type="ST_Ints" use="optional">
     * ```
     */
    st?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="0" name="cnt" type="ST_UnsignedInts" use="optional">
     * ```
     */
    cnt?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="1" name="step" type="ST_Ints" use="optional">
     * ```
     */
    step?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Choose name="CT_Choose">
 * ```
 */
export interface ICTChoose {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_When aliasName=ifKeyword aliasName="ifKeyword" maxOccurs="unbounded" name="if" type="CT_When">
     * ```
     */
    ifKeyword?: ICTWhen[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Otherwise aliasName=elseKeyword aliasName="elseKeyword" minOccurs="0" name="else" type="CT_Otherwise">
     * ```
     */
    elseKeyword?: ICTOtherwise;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Choose name="CT_Choose">
 * ```
 */
export interface ICTChooseDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_When aliasName=ifKeyword aliasName="ifKeyword" maxOccurs="unbounded" name="if" type="CT_When">
     * ```
     */
    ifKeyword?: ICTWhenDelta[];
    ifKeywordOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Otherwise aliasName=elseKeyword aliasName="elseKeyword" minOccurs="0" name="else" type="CT_Otherwise">
     * ```
     */
    elseKeyword?: ICTOtherwiseDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string | null;
}

export enum STChildOrderType {
    PBDEFAULT = 0,
    b = 1,
    t = 2,
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_SDCategory name="CT_SDCategory">
 * ```
 */
export interface ICTSDCategory {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="type" type="xsd:anyURI" use="required">
     * ```
     */
    type?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="pri" type="xsd:unsignedInt" use="required">
     * ```
     */
    pri?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_SDCategory name="CT_SDCategory">
 * ```
 */
export interface ICTSDCategoryDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="type" type="xsd:anyURI" use="required">
     * ```
     */
    type?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="pri" type="xsd:unsignedInt" use="required">
     * ```
     */
    pri?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_TextProps name="CT_TextProps">
 * ```
 */
export interface ICTTextProps {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_Text3D ref=dmlmain:EG_Text3D maxOccurs="1" minOccurs="0" name="EG_Text3D" ref="a:EG_Text3D">
     * ref dmlmain:EG_Text3D
     * ```
     */
    egText3D?: IEGText3D;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_TextProps name="CT_TextProps">
 * ```
 */
export interface ICTTextPropsDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_Text3D ref=dmlmain:EG_Text3D maxOccurs="1" minOccurs="0" name="EG_Text3D" ref="a:EG_Text3D">
     * ref dmlmain:EG_Text3D
     * ```
     */
    egText3D?: IEGText3DDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_CTCategory name="CT_CTCategory">
 * ```
 */
export interface ICTCTCategory {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="type" type="xsd:anyURI" use="required">
     * ```
     */
    type?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="pri" type="xsd:unsignedInt" use="required">
     * ```
     */
    pri?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_CTCategory name="CT_CTCategory">
 * ```
 */
export interface ICTCTCategoryDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="type" type="xsd:anyURI" use="required">
     * ```
     */
    type?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="pri" type="xsd:unsignedInt" use="required">
     * ```
     */
    pri?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_ColorsDmlDgm name="CT_ColorsDmlDgm">
 * ```
 */
export interface ICTColorsDmlDgm {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="unbounded" minOccurs="0" name="EG_ColorChoice" ref="a:EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoice[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ClrAppMethod default="span" name="meth" type="ST_ClrAppMethod" use="optional">
     * ```
     */
    meth?: STClrAppMethod;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_HueDir default="cw" name="hueDir" type="ST_HueDir" use="optional">
     * ```
     */
    hueDir?: STHueDir;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_ColorsDmlDgm name="CT_ColorsDmlDgm">
 * ```
 */
export interface ICTColorsDmlDgmDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="unbounded" minOccurs="0" name="EG_ColorChoice" ref="a:EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoiceDelta[];
    egColorChoiceOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ClrAppMethod default="span" name="meth" type="ST_ClrAppMethod" use="optional">
     * ```
     */
    meth?: STClrAppMethod | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_HueDir default="cw" name="hueDir" type="ST_HueDir" use="optional">
     * ```
     */
    hueDir?: STHueDir | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GroupLocking name="CT_GroupLocking">
 * ```
 */
export interface ICTGroupLocking {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noGrp" type="xsd:boolean" use="optional">
     * ```
     */
    noGrp?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noUngrp" type="xsd:boolean" use="optional">
     * ```
     */
    noUngrp?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noSelect" type="xsd:boolean" use="optional">
     * ```
     */
    noSelect?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noRot" type="xsd:boolean" use="optional">
     * ```
     */
    noRot?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeAspect" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeAspect?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noMove" type="xsd:boolean" use="optional">
     * ```
     */
    noMove?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noResize" type="xsd:boolean" use="optional">
     * ```
     */
    noResize?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GroupLocking name="CT_GroupLocking">
 * ```
 */
export interface ICTGroupLockingDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noGrp" type="xsd:boolean" use="optional">
     * ```
     */
    noGrp?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noUngrp" type="xsd:boolean" use="optional">
     * ```
     */
    noUngrp?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noSelect" type="xsd:boolean" use="optional">
     * ```
     */
    noSelect?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noRot" type="xsd:boolean" use="optional">
     * ```
     */
    noRot?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeAspect" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeAspect?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noMove" type="xsd:boolean" use="optional">
     * ```
     */
    noMove?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noResize" type="xsd:boolean" use="optional">
     * ```
     */
    noResize?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ShapeNonVisualPml name="CT_ShapeNonVisualPml">
 * ```
 */
export interface ICTShapeNonVisualPml {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingProps maxOccurs="1" minOccurs="1" name="cNvPr" type="a:CT_NonVisualDrawingProps">
     * ```
     */
    cNvPr?: ICTNonVisualDrawingProps;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingShapeProps maxOccurs="1" minOccurs="1" name="cNvSpPr" type="a:CT_NonVisualDrawingShapeProps">
     * ```
     */
    cNvSpPr?: ICTNonVisualDrawingShapeProps;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ApplicationNonVisualDrawingProps maxOccurs="1" minOccurs="1" name="nvPr" type="CT_ApplicationNonVisualDrawingProps">
     * ```
     */
    nvPr?: ICTApplicationNonVisualDrawingProps;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ShapeNonVisualPml name="CT_ShapeNonVisualPml">
 * ```
 */
export interface ICTShapeNonVisualPmlDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingProps maxOccurs="1" minOccurs="1" name="cNvPr" type="a:CT_NonVisualDrawingProps">
     * ```
     */
    cNvPr?: ICTNonVisualDrawingPropsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingShapeProps maxOccurs="1" minOccurs="1" name="cNvSpPr" type="a:CT_NonVisualDrawingShapeProps">
     * ```
     */
    cNvSpPr?: ICTNonVisualDrawingShapePropsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ApplicationNonVisualDrawingProps maxOccurs="1" minOccurs="1" name="nvPr" type="CT_ApplicationNonVisualDrawingProps">
     * ```
     */
    nvPr?: ICTApplicationNonVisualDrawingPropsDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_GraphicalObjectFrameNonVisualPml name="CT_GraphicalObjectFrameNonVisualPml">
 * ```
 */
export interface ICTGraphicalObjectFrameNonVisualPml {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingProps maxOccurs="1" minOccurs="1" name="cNvPr" type="a:CT_NonVisualDrawingProps">
     * ```
     */
    cNvPr?: ICTNonVisualDrawingProps;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualGraphicFrameProperties maxOccurs="1" minOccurs="1" name="cNvGraphicFramePr" type="a:CT_NonVisualGraphicFrameProperties">
     * ```
     */
    cNvGraphicFramePr?: ICTNonVisualGraphicFrameProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ApplicationNonVisualDrawingProps maxOccurs="1" minOccurs="1" name="nvPr" type="CT_ApplicationNonVisualDrawingProps">
     * ```
     */
    nvPr?: ICTApplicationNonVisualDrawingProps;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_GraphicalObjectFrameNonVisualPml name="CT_GraphicalObjectFrameNonVisualPml">
 * ```
 */
export interface ICTGraphicalObjectFrameNonVisualPmlDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingProps maxOccurs="1" minOccurs="1" name="cNvPr" type="a:CT_NonVisualDrawingProps">
     * ```
     */
    cNvPr?: ICTNonVisualDrawingPropsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualGraphicFrameProperties maxOccurs="1" minOccurs="1" name="cNvGraphicFramePr" type="a:CT_NonVisualGraphicFrameProperties">
     * ```
     */
    cNvGraphicFramePr?: ICTNonVisualGraphicFramePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ApplicationNonVisualDrawingProps maxOccurs="1" minOccurs="1" name="nvPr" type="CT_ApplicationNonVisualDrawingProps">
     * ```
     */
    nvPr?: ICTApplicationNonVisualDrawingPropsDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ConnectorNonVisualPml name="CT_ConnectorNonVisualPml">
 * ```
 */
export interface ICTConnectorNonVisualPml {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingProps maxOccurs="1" minOccurs="1" name="cNvPr" type="a:CT_NonVisualDrawingProps">
     * ```
     */
    cNvPr?: ICTNonVisualDrawingProps;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualConnectorProperties maxOccurs="1" minOccurs="1" name="cNvCxnSpPr" type="a:CT_NonVisualConnectorProperties">
     * ```
     */
    cNvCxnSpPr?: ICTNonVisualConnectorProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ApplicationNonVisualDrawingProps maxOccurs="1" minOccurs="1" name="nvPr" type="CT_ApplicationNonVisualDrawingProps">
     * ```
     */
    nvPr?: ICTApplicationNonVisualDrawingProps;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ConnectorNonVisualPml name="CT_ConnectorNonVisualPml">
 * ```
 */
export interface ICTConnectorNonVisualPmlDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingProps maxOccurs="1" minOccurs="1" name="cNvPr" type="a:CT_NonVisualDrawingProps">
     * ```
     */
    cNvPr?: ICTNonVisualDrawingPropsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualConnectorProperties maxOccurs="1" minOccurs="1" name="cNvCxnSpPr" type="a:CT_NonVisualConnectorProperties">
     * ```
     */
    cNvCxnSpPr?: ICTNonVisualConnectorPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ApplicationNonVisualDrawingProps maxOccurs="1" minOccurs="1" name="nvPr" type="CT_ApplicationNonVisualDrawingProps">
     * ```
     */
    nvPr?: ICTApplicationNonVisualDrawingPropsDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_PictureNonVisualPml name="CT_PictureNonVisualPml">
 * ```
 */
export interface ICTPictureNonVisualPml {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingProps maxOccurs="1" minOccurs="1" name="cNvPr" type="a:CT_NonVisualDrawingProps">
     * ```
     */
    cNvPr?: ICTNonVisualDrawingProps;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualPictureProperties maxOccurs="1" minOccurs="1" name="cNvPicPr" type="a:CT_NonVisualPictureProperties">
     * ```
     */
    cNvPicPr?: ICTNonVisualPictureProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ApplicationNonVisualDrawingProps maxOccurs="1" minOccurs="1" name="nvPr" type="CT_ApplicationNonVisualDrawingProps">
     * ```
     */
    nvPr?: ICTApplicationNonVisualDrawingProps;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_PictureNonVisualPml name="CT_PictureNonVisualPml">
 * ```
 */
export interface ICTPictureNonVisualPmlDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingProps maxOccurs="1" minOccurs="1" name="cNvPr" type="a:CT_NonVisualDrawingProps">
     * ```
     */
    cNvPr?: ICTNonVisualDrawingPropsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualPictureProperties maxOccurs="1" minOccurs="1" name="cNvPicPr" type="a:CT_NonVisualPictureProperties">
     * ```
     */
    cNvPicPr?: ICTNonVisualPicturePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ApplicationNonVisualDrawingProps maxOccurs="1" minOccurs="1" name="nvPr" type="CT_ApplicationNonVisualDrawingProps">
     * ```
     */
    nvPr?: ICTApplicationNonVisualDrawingPropsDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTemplate name="CT_TLTemplate">
 * ```
 */
export interface ICTTLTemplate {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TimeNodeList maxOccurs="1" minOccurs="1" name="tnLst" type="CT_TimeNodeList">
     * ```
     */
    tnLst?: ICTTimeNodeList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="0" name="lvl" type="xsd:unsignedInt" use="optional">
     * ```
     */
    lvl?: number;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTemplate name="CT_TLTemplate">
 * ```
 */
export interface ICTTLTemplateDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TimeNodeList maxOccurs="1" minOccurs="1" name="tnLst" type="CT_TimeNodeList">
     * ```
     */
    tnLst?: ICTTimeNodeListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="0" name="lvl" type="xsd:unsignedInt" use="optional">
     * ```
     */
    lvl?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AnimationDgmBuildProperties name="CT_AnimationDgmBuildProperties">
 * ```
 */
export interface ICTAnimationDgmBuildProperties {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="rev" type="xsd:boolean" use="optional">
     * ```
     */
    rev?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_AnimationDgmBuildType default="allAtOnce" name="bld" type="ST_AnimationDgmBuildType" use="optional">
     * ```
     */
    bld?: STAnimationDgmBuildType;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AnimationDgmBuildProperties name="CT_AnimationDgmBuildProperties">
 * ```
 */
export interface ICTAnimationDgmBuildPropertiesDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="rev" type="xsd:boolean" use="optional">
     * ```
     */
    rev?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_AnimationDgmBuildType default="allAtOnce" name="bld" type="ST_AnimationDgmBuildType" use="optional">
     * ```
     */
    bld?: STAnimationDgmBuildType | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AnimationChartBuildProperties name="CT_AnimationChartBuildProperties">
 * ```
 */
export interface ICTAnimationChartBuildProperties {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="animBg" type="xsd:boolean" use="optional">
     * ```
     */
    animBg?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_AnimationChartBuildType default="allAtOnce" name="bld" type="ST_AnimationChartBuildType" use="optional">
     * ```
     */
    bld?: STAnimationChartBuildType;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AnimationChartBuildProperties name="CT_AnimationChartBuildProperties">
 * ```
 */
export interface ICTAnimationChartBuildPropertiesDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="animBg" type="xsd:boolean" use="optional">
     * ```
     */
    animBg?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_AnimationChartBuildType default="allAtOnce" name="bld" type="ST_AnimationChartBuildType" use="optional">
     * ```
     */
    bld?: STAnimationChartBuildType | null;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_MediaTrim name="CT_MediaTrim">
 * ```
 */
export interface ICTMediaTrim {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="0" name="st" type="p14:ST_UniversalTimeOffset" use="optional">
     * ```
     */
    st?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="0" name="end" type="p14:ST_UniversalTimeOffset" use="optional">
     * ```
     */
    end?: string;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_MediaTrim name="CT_MediaTrim">
 * ```
 */
export interface ICTMediaTrimDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="0" name="st" type="p14:ST_UniversalTimeOffset" use="optional">
     * ```
     */
    st?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="0" name="end" type="p14:ST_UniversalTimeOffset" use="optional">
     * ```
     */
    end?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_MediaFade name="CT_MediaFade">
 * ```
 */
export interface ICTMediaFade {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="0" name="in" type="p14:ST_UniversalTimeOffset" use="optional">
     * ```
     */
    in?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="0" name="out" type="p14:ST_UniversalTimeOffset" use="optional">
     * ```
     */
    out?: string;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_MediaFade name="CT_MediaFade">
 * ```
 */
export interface ICTMediaFadeDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="0" name="in" type="p14:ST_UniversalTimeOffset" use="optional">
     * ```
     */
    in?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="0" name="out" type="p14:ST_UniversalTimeOffset" use="optional">
     * ```
     */
    out?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_MediaBookmarkList name="CT_MediaBookmarkList">
 * ```
 */
export interface ICTMediaBookmarkList {
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_MediaBookmark maxOccurs="unbounded" minOccurs="0" name="bmk" type="CT_MediaBookmark">
     * ```
     */
    bmk?: ICTMediaBookmark[];
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_MediaBookmarkList name="CT_MediaBookmarkList">
 * ```
 */
export interface ICTMediaBookmarkListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_MediaBookmark maxOccurs="unbounded" minOccurs="0" name="bmk" type="CT_MediaBookmark">
     * ```
     */
    bmk?: ICTMediaBookmarkDelta[];
    bmkOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_Section name="CT_Section">
 * ```
 */
export interface ICTSection {
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_SectionSlideIdList maxOccurs="1" minOccurs="1" name="sldIdLst" type="CT_SectionSlideIdList">
     * ```
     */
    sldIdLst?: ICTSectionSlideIdList;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="p:CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string">
     * ```
     */
    name?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="s:ST_Guid">
     * ```
     */
    id?: string;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_Section name="CT_Section">
 * ```
 */
export interface ICTSectionDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_SectionSlideIdList maxOccurs="1" minOccurs="1" name="sldIdLst" type="CT_SectionSlideIdList">
     * ```
     */
    sldIdLst?: ICTSectionSlideIdListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="p:CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string">
     * ```
     */
    name?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="s:ST_Guid">
     * ```
     */
    id?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2012:CT_ParentCommentIdentifier name="CT_ParentCommentIdentifier">
 * ```
 */
export interface ICTParentCommentIdentifier {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="authorId" type="xsd:unsignedInt">
     * ```
     */
    authorId?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt">
     * ```
     */
    idx?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2012:CT_ParentCommentIdentifier name="CT_ParentCommentIdentifier">
 * ```
 */
export interface ICTParentCommentIdentifierDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="authorId" type="xsd:unsignedInt">
     * ```
     */
    authorId?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt">
     * ```
     */
    idx?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ShowInfoBrowse name="CT_ShowInfoBrowse">
 * ```
 */
export interface ICTShowInfoBrowse {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="showScrollbar" type="xsd:boolean" use="optional">
     * ```
     */
    showScrollbar?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ShowInfoBrowse name="CT_ShowInfoBrowse">
 * ```
 */
export interface ICTShowInfoBrowseDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="showScrollbar" type="xsd:boolean" use="optional">
     * ```
     */
    showScrollbar?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ShowInfoKiosk name="CT_ShowInfoKiosk">
 * ```
 */
export interface ICTShowInfoKiosk {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="300000" name="restart" type="xsd:unsignedInt" use="optional">
     * ```
     */
    restart?: number;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_ShowInfoKiosk name="CT_ShowInfoKiosk">
 * ```
 */
export interface ICTShowInfoKioskDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="300000" name="restart" type="xsd:unsignedInt" use="optional">
     * ```
     */
    restart?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdsp2008:CT_DataModelExtBlock name="CT_DataModelExtBlock">
 * ```
 */
export interface ICTDataModelExtBlock {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="relId" type="xsd:string">
     * ```
     */
    relId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="minVer" type="xsd:anyURI">
     * ```
     */
    minVer?: string;
}

/**
 * ```xml
 * <xsd:complexType name=msdsp2008:CT_DataModelExtBlock name="CT_DataModelExtBlock">
 * ```
 */
export interface ICTDataModelExtBlockDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="relId" type="xsd:string">
     * ```
     */
    relId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="minVer" type="xsd:anyURI">
     * ```
     */
    minVer?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_Photo name="CT_Photo">
 * ```
 */
export interface ICTPhoto {
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureLayer maxOccurs="1" minOccurs="1" name="imgLayer" type="CT_PictureLayer">
     * ```
     */
    imgLayer?: ICTPictureLayer;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_Photo name="CT_Photo">
 * ```
 */
export interface ICTPhotoDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureLayer maxOccurs="1" minOccurs="1" name="imgLayer" type="CT_PictureLayer">
     * ```
     */
    imgLayer?: ICTPictureLayerDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msask2018:CT_LineSketchStyleProperties name="CT_LineSketchStyleProperties">
 * ```
 */
export interface ICTLineSketchStyleProperties {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_Geometry ref=dmlmain:EG_Geometry maxOccurs="1" minOccurs="0" name="EG_Geometry" ref="a:EG_Geometry">
     * ref dmlmain:EG_Geometry
     * ```
     */
    egGeometry?: IEGGeometry;
    /**
     * ```xml
     * <xsd:element typeRedirected=msask2018:CT_LineSketchTypeProperties maxOccurs="1" minOccurs="0" name="type" type="CT_LineSketchTypeProperties">
     * ```
     */
    type?: ICTLineSketchTypeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:uint32 maxOccurs="1" minOccurs="0" name="seed" type="ST_LineSketchSeed">
     * ```
     */
    seed?: number;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="sd" type="ST_LineSketchSeed" use="optional">
     * ```
     */
    sd?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msask2018:CT_LineSketchStyleProperties name="CT_LineSketchStyleProperties">
 * ```
 */
export interface ICTLineSketchStylePropertiesDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_Geometry ref=dmlmain:EG_Geometry maxOccurs="1" minOccurs="0" name="EG_Geometry" ref="a:EG_Geometry">
     * ref dmlmain:EG_Geometry
     * ```
     */
    egGeometry?: IEGGeometryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msask2018:CT_LineSketchTypeProperties maxOccurs="1" minOccurs="0" name="type" type="CT_LineSketchTypeProperties">
     * ```
     */
    type?: ICTLineSketchTypePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:uint32 maxOccurs="1" minOccurs="0" name="seed" type="ST_LineSketchSeed">
     * ```
     */
    seed?: number | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="sd" type="ST_LineSketchSeed" use="optional">
     * ```
     */
    sd?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msasvg2016:CT_SVGBlip name="CT_SVGBlip">
 * ```
 */
export interface ICTSVGBlip {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:embed default="" name="embed" ref="r:embed" type="ST_RelationshipId" use="optional">
     * ```
     */
    embed?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:link default="" name="link" ref="r:link" type="ST_RelationshipId" use="optional">
     * ```
     */
    link?: string;
}

/**
 * ```xml
 * <xsd:complexType name=msasvg2016:CT_SVGBlip name="CT_SVGBlip">
 * ```
 */
export interface ICTSVGBlipDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:embed default="" name="embed" ref="r:embed" type="ST_RelationshipId" use="optional">
     * ```
     */
    embed?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:link default="" name="link" ref="r:link" type="ST_RelationshipId" use="optional">
     * ```
     */
    link?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=msahyp2018:CT_HyperlinkColor name="CT_HyperlinkColor">
 * ```
 */
export interface ICTHyperlinkColor {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=msahyp2018:ST_HyperlinkColor name="val" type="ST_HyperlinkColor" use="required">
     * ```
     */
    val?: STHyperlinkColor;
}

/**
 * ```xml
 * <xsd:complexType name=msahyp2018:CT_HyperlinkColor name="CT_HyperlinkColor">
 * ```
 */
export interface ICTHyperlinkColorDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=msahyp2018:ST_HyperlinkColor name="val" type="ST_HyperlinkColor" use="required">
     * ```
     */
    val?: STHyperlinkColor | null;
}

/**
 * ```xml
 * <xsd:complexType name=msthm2012:CT_ThemeFamily name="CT_ThemeFamily">
 * ```
 */
export interface ICTThemeFamily {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string" use="required">
     * ```
     */
    name?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="s:ST_Guid" use="required">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="vid" type="s:ST_Guid" use="required">
     * ```
     */
    vid?: string;
}

/**
 * ```xml
 * <xsd:complexType name=msthm2012:CT_ThemeFamily name="CT_ThemeFamily">
 * ```
 */
export interface ICTThemeFamilyDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string" use="required">
     * ```
     */
    name?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="s:ST_Guid" use="required">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="vid" type="s:ST_Guid" use="required">
     * ```
     */
    vid?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2014:CT_Identifier name="CT_Identifier">
 * ```
 */
export interface ICTIdentifier {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="val" type="xsd:unsignedInt" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2014:CT_Identifier name="CT_Identifier">
 * ```
 */
export interface ICTIdentifierDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="val" type="xsd:unsignedInt" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_FillProperties name="CT_FillProperties">
 * ```
 */
export interface ICTFillProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_FillProperties name="CT_FillProperties">
 * ```
 */
export interface ICTFillPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_EffectProperties name="CT_EffectProperties">
 * ```
 */
export interface ICTEffectProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectList maxOccurs="1" minOccurs="1" name="effectLst" type="CT_EffectList">
     * ```
     */
    effectLst?: ICTEffectList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="effectDag" type="CT_EffectContainer">
     * ```
     */
    effectDag?: ICTEffectContainer;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_EffectProperties name="CT_EffectProperties">
 * ```
 */
export interface ICTEffectPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectList maxOccurs="1" minOccurs="1" name="effectLst" type="CT_EffectList">
     * ```
     */
    effectLst?: ICTEffectListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="effectDag" type="CT_EffectContainer">
     * ```
     */
    effectDag?: ICTEffectContainerDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableStyleTextStyle name="CT_TableStyleTextStyle">
 * ```
 */
export interface ICTTableStyleTextStyle {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FontCollection maxOccurs="1" minOccurs="1" name="font" type="CT_FontCollection">
     * ```
     */
    font?: ICTFontCollection;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FontReferenceDmlA maxOccurs="1" minOccurs="1" name="fontRef" type="CT_FontReferenceDmlA">
     * ```
     */
    fontRef?: ICTFontReferenceDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ScRgbColor maxOccurs="1" minOccurs="1" name="scrgbClr" type="CT_ScRgbColor">
     * ```
     */
    scrgbClr?: ICTScRgbColor;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SRgbColor maxOccurs="1" minOccurs="1" name="srgbClr" type="CT_SRgbColor">
     * ```
     */
    srgbClr?: ICTSRgbColor;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HslColor maxOccurs="1" minOccurs="1" name="hslClr" type="CT_HslColor">
     * ```
     */
    hslClr?: ICTHslColor;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SystemColor maxOccurs="1" minOccurs="1" name="sysClr" type="CT_SystemColor">
     * ```
     */
    sysClr?: ICTSystemColor;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SchemeColor maxOccurs="1" minOccurs="1" name="schemeClr" type="CT_SchemeColor">
     * ```
     */
    schemeClr?: ICTSchemeColor;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PresetColor maxOccurs="1" minOccurs="1" name="prstClr" type="CT_PresetColor">
     * ```
     */
    prstClr?: ICTPresetColor;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_OnOffStyleType default="def" name="b" type="ST_OnOffStyleType" use="optional">
     * ```
     */
    b?: STOnOffStyleType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_OnOffStyleType default="def" name="i" type="ST_OnOffStyleType" use="optional">
     * ```
     */
    i?: STOnOffStyleType;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableStyleTextStyle name="CT_TableStyleTextStyle">
 * ```
 */
export interface ICTTableStyleTextStyleDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FontCollection maxOccurs="1" minOccurs="1" name="font" type="CT_FontCollection">
     * ```
     */
    font?: ICTFontCollectionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FontReferenceDmlA maxOccurs="1" minOccurs="1" name="fontRef" type="CT_FontReferenceDmlA">
     * ```
     */
    fontRef?: ICTFontReferenceDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ScRgbColor maxOccurs="1" minOccurs="1" name="scrgbClr" type="CT_ScRgbColor">
     * ```
     */
    scrgbClr?: ICTScRgbColorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SRgbColor maxOccurs="1" minOccurs="1" name="srgbClr" type="CT_SRgbColor">
     * ```
     */
    srgbClr?: ICTSRgbColorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_HslColor maxOccurs="1" minOccurs="1" name="hslClr" type="CT_HslColor">
     * ```
     */
    hslClr?: ICTHslColorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SystemColor maxOccurs="1" minOccurs="1" name="sysClr" type="CT_SystemColor">
     * ```
     */
    sysClr?: ICTSystemColorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SchemeColor maxOccurs="1" minOccurs="1" name="schemeClr" type="CT_SchemeColor">
     * ```
     */
    schemeClr?: ICTSchemeColorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PresetColor maxOccurs="1" minOccurs="1" name="prstClr" type="CT_PresetColor">
     * ```
     */
    prstClr?: ICTPresetColorDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_OnOffStyleType default="def" name="b" type="ST_OnOffStyleType" use="optional">
     * ```
     */
    b?: STOnOffStyleType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_OnOffStyleType default="def" name="i" type="ST_OnOffStyleType" use="optional">
     * ```
     */
    i?: STOnOffStyleType | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableStyleCellStyle name="CT_TableStyleCellStyle">
 * ```
 */
export interface ICTTableStyleCellStyle {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableCellBorderStyle maxOccurs="1" minOccurs="0" name="tcBdr" type="CT_TableCellBorderStyle">
     * ```
     */
    tcBdr?: ICTTableCellBorderStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FillProperties maxOccurs="1" minOccurs="1" name="fill" type="CT_FillProperties">
     * ```
     */
    fill?: ICTFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StyleMatrixReference maxOccurs="1" minOccurs="1" name="fillRef" type="CT_StyleMatrixReference">
     * ```
     */
    fillRef?: ICTStyleMatrixReference;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Cell3D maxOccurs="1" minOccurs="0" name="cell3D" type="CT_Cell3D">
     * ```
     */
    cell3D?: ICTCell3D;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableStyleCellStyle name="CT_TableStyleCellStyle">
 * ```
 */
export interface ICTTableStyleCellStyleDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableCellBorderStyle maxOccurs="1" minOccurs="0" name="tcBdr" type="CT_TableCellBorderStyle">
     * ```
     */
    tcBdr?: ICTTableCellBorderStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FillProperties maxOccurs="1" minOccurs="1" name="fill" type="CT_FillProperties">
     * ```
     */
    fill?: ICTFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StyleMatrixReference maxOccurs="1" minOccurs="1" name="fillRef" type="CT_StyleMatrixReference">
     * ```
     */
    fillRef?: ICTStyleMatrixReferenceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Cell3D maxOccurs="1" minOccurs="0" name="cell3D" type="CT_Cell3D">
     * ```
     */
    cell3D?: ICTCell3DDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_GuideList name="CT_GuideList">
 * ```
 */
export interface ICTGuideList {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_Guide maxOccurs="unbounded" minOccurs="0" name="guide" type="CT_Guide">
     * ```
     */
    guide?: ICTGuide[];
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_GuideList name="CT_GuideList">
 * ```
 */
export interface ICTGuideListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_Guide maxOccurs="unbounded" minOccurs="0" name="guide" type="CT_Guide">
     * ```
     */
    guide?: ICTGuideDelta[];
    guideOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Scale2D name="CT_Scale2D">
 * ```
 */
export interface ICTScale2D {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Ratio maxOccurs="1" minOccurs="1" name="sx" type="CT_Ratio">
     * ```
     */
    sx?: ICTRatio;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Ratio maxOccurs="1" minOccurs="1" name="sy" type="CT_Ratio">
     * ```
     */
    sy?: ICTRatio;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Scale2D name="CT_Scale2D">
 * ```
 */
export interface ICTScale2DDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Ratio maxOccurs="1" minOccurs="1" name="sx" type="CT_Ratio">
     * ```
     */
    sx?: ICTRatioDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Ratio maxOccurs="1" minOccurs="1" name="sy" type="CT_Ratio">
     * ```
     */
    sy?: ICTRatioDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_OutlineViewSlideEntry name="CT_OutlineViewSlideEntry">
 * ```
 */
export interface ICTOutlineViewSlideEntry {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="collapse" type="xsd:boolean" use="optional">
     * ```
     */
    collapse?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_OutlineViewSlideEntry name="CT_OutlineViewSlideEntry">
 * ```
 */
export interface ICTOutlineViewSlideEntryDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="collapse" type="xsd:boolean" use="optional">
     * ```
     */
    collapse?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AdjustHandleList name="CT_AdjustHandleList">
 * ```
 */
export interface ICTAdjustHandleList {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_XYAdjustHandle maxOccurs="1" minOccurs="1" name="ahXY" type="CT_XYAdjustHandle">
     * ```
     */
    ahXy?: ICTXYAdjustHandle[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PolarAdjustHandle maxOccurs="1" minOccurs="1" name="ahPolar" type="CT_PolarAdjustHandle">
     * ```
     */
    ahPolar?: ICTPolarAdjustHandle[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AdjustHandleList name="CT_AdjustHandleList">
 * ```
 */
export interface ICTAdjustHandleListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_XYAdjustHandle maxOccurs="1" minOccurs="1" name="ahXY" type="CT_XYAdjustHandle">
     * ```
     */
    ahXy?: ICTXYAdjustHandleDelta[];
    ahXyOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PolarAdjustHandle maxOccurs="1" minOccurs="1" name="ahPolar" type="CT_PolarAdjustHandle">
     * ```
     */
    ahPolar?: ICTPolarAdjustHandleDelta[];
    ahPolarOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ConnectionSiteList name="CT_ConnectionSiteList">
 * ```
 */
export interface ICTConnectionSiteList {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ConnectionSite maxOccurs="unbounded" minOccurs="0" name="cxn" type="CT_ConnectionSite">
     * ```
     */
    cxn?: ICTConnectionSite[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ConnectionSiteList name="CT_ConnectionSiteList">
 * ```
 */
export interface ICTConnectionSiteListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ConnectionSite maxOccurs="unbounded" minOccurs="0" name="cxn" type="CT_ConnectionSite">
     * ```
     */
    cxn?: ICTConnectionSiteDelta[];
    cxnOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GeomRect name="CT_GeomRect">
 * ```
 */
export interface ICTGeomRect {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="l" type="ST_AdjCoordinate" use="required">
     * ```
     */
    l?: ICTAdjCoordinate;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="t" type="ST_AdjCoordinate" use="required">
     * ```
     */
    t?: ICTAdjCoordinate;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="r" type="ST_AdjCoordinate" use="required">
     * ```
     */
    r?: ICTAdjCoordinate;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="b" type="ST_AdjCoordinate" use="required">
     * ```
     */
    b?: ICTAdjCoordinate;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GeomRect name="CT_GeomRect">
 * ```
 */
export interface ICTGeomRectDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="l" type="ST_AdjCoordinate" use="required">
     * ```
     */
    l?: ICTAdjCoordinateDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="t" type="ST_AdjCoordinate" use="required">
     * ```
     */
    t?: ICTAdjCoordinateDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="r" type="ST_AdjCoordinate" use="required">
     * ```
     */
    r?: ICTAdjCoordinateDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="b" type="ST_AdjCoordinate" use="required">
     * ```
     */
    b?: ICTAdjCoordinateDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Path2DList name="CT_Path2DList">
 * ```
 */
export interface ICTPath2DList {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Path2D maxOccurs="unbounded" minOccurs="0" name="path" type="CT_Path2D">
     * ```
     */
    path?: ICTPath2D[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Path2DList name="CT_Path2DList">
 * ```
 */
export interface ICTPath2DListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Path2D maxOccurs="unbounded" minOccurs="0" name="path" type="CT_Path2D">
     * ```
     */
    path?: ICTPath2DDelta[];
    pathOp?: IArrayOp;
}

export enum STShapeType {
    PBDEFAULT = 0,
    line = 1,
    lineInv = 2,
    triangle = 3,
    rtTriangle = 4,
    rect = 5,
    diamond = 6,
    parallelogram = 7,
    trapezoid = 8,
    nonIsoscelesTrapezoid = 9,
    pentagon = 10,
    hexagon = 11,
    heptagon = 12,
    octagon = 13,
    decagon = 14,
    dodecagon = 15,
    star4 = 16,
    star5 = 17,
    star6 = 18,
    star7 = 19,
    star8 = 20,
    star10 = 21,
    star12 = 22,
    star16 = 23,
    star24 = 24,
    star32 = 25,
    roundRect = 26,
    round1Rect = 27,
    round2SameRect = 28,
    round2DiagRect = 29,
    snipRoundRect = 30,
    snip1Rect = 31,
    snip2SameRect = 32,
    snip2DiagRect = 33,
    plaque = 34,
    ellipse = 35,
    teardrop = 36,
    homePlate = 37,
    chevron = 38,
    pieWedge = 39,
    pie = 40,
    blockArc = 41,
    donut = 42,
    noSmoking = 43,
    rightArrow = 44,
    leftArrow = 45,
    upArrow = 46,
    downArrow = 47,
    stripedRightArrow = 48,
    notchedRightArrow = 49,
    bentUpArrow = 50,
    leftRightArrow = 51,
    upDownArrow = 52,
    leftUpArrow = 53,
    leftRightUpArrow = 54,
    quadArrow = 55,
    leftArrowCallout = 56,
    rightArrowCallout = 57,
    upArrowCallout = 58,
    downArrowCallout = 59,
    leftRightArrowCallout = 60,
    upDownArrowCallout = 61,
    quadArrowCallout = 62,
    bentArrow = 63,
    uturnArrow = 64,
    circularArrow = 65,
    leftCircularArrow = 66,
    leftRightCircularArrow = 67,
    curvedRightArrow = 68,
    curvedLeftArrow = 69,
    curvedUpArrow = 70,
    curvedDownArrow = 71,
    swooshArrow = 72,
    cube = 73,
    can = 74,
    lightningBolt = 75,
    heart = 76,
    sun = 77,
    moon = 78,
    smileyFace = 79,
    irregularSeal1 = 80,
    irregularSeal2 = 81,
    foldedCorner = 82,
    bevel = 83,
    frame = 84,
    halfFrame = 85,
    corner = 86,
    diagStripe = 87,
    chord = 88,
    arc = 89,
    leftBracket = 90,
    rightBracket = 91,
    leftBrace = 92,
    rightBrace = 93,
    bracketPair = 94,
    bracePair = 95,
    straightConnector1 = 96,
    bentConnector2 = 97,
    bentConnector3 = 98,
    bentConnector4 = 99,
    bentConnector5 = 100,
    curvedConnector2 = 101,
    curvedConnector3 = 102,
    curvedConnector4 = 103,
    curvedConnector5 = 104,
    callout1 = 105,
    callout2 = 106,
    callout3 = 107,
    accentCallout1 = 108,
    accentCallout2 = 109,
    accentCallout3 = 110,
    borderCallout1 = 111,
    borderCallout2 = 112,
    borderCallout3 = 113,
    accentBorderCallout1 = 114,
    accentBorderCallout2 = 115,
    accentBorderCallout3 = 116,
    wedgeRectCallout = 117,
    wedgeRoundRectCallout = 118,
    wedgeEllipseCallout = 119,
    cloudCallout = 120,
    cloud = 121,
    ribbon = 122,
    ribbon2 = 123,
    ellipseRibbon = 124,
    ellipseRibbon2 = 125,
    leftRightRibbon = 126,
    verticalScroll = 127,
    horizontalScroll = 128,
    wave = 129,
    doubleWave = 130,
    plus = 131,
    flowChartProcess = 132,
    flowChartDecision = 133,
    flowChartInputOutput = 134,
    flowChartPredefinedProcess = 135,
    flowChartInternalStorage = 136,
    flowChartDocument = 137,
    flowChartMultidocument = 138,
    flowChartTerminator = 139,
    flowChartPreparation = 140,
    flowChartManualInput = 141,
    flowChartManualOperation = 142,
    flowChartConnector = 143,
    flowChartPunchedCard = 144,
    flowChartPunchedTape = 145,
    flowChartSummingJunction = 146,
    flowChartOr = 147,
    flowChartCollate = 148,
    flowChartSort = 149,
    flowChartExtract = 150,
    flowChartMerge = 151,
    flowChartOfflineStorage = 152,
    flowChartOnlineStorage = 153,
    flowChartMagneticTape = 154,
    flowChartMagneticDisk = 155,
    flowChartMagneticDrum = 156,
    flowChartDisplay = 157,
    flowChartDelay = 158,
    flowChartAlternateProcess = 159,
    flowChartOffpageConnector = 160,
    actionButtonBlank = 161,
    actionButtonHome = 162,
    actionButtonHelp = 163,
    actionButtonInformation = 164,
    actionButtonForwardNext = 165,
    actionButtonBackPrevious = 166,
    actionButtonEnd = 167,
    actionButtonBeginning = 168,
    actionButtonReturn = 169,
    actionButtonDocument = 170,
    actionButtonSound = 171,
    actionButtonMovie = 172,
    gear6 = 173,
    gear9 = 174,
    funnel = 175,
    mathPlus = 176,
    mathMinus = 177,
    mathMultiply = 178,
    mathDivide = 179,
    mathEqual = 180,
    mathNotEqual = 181,
    cornerTabs = 182,
    squareTabs = 183,
    plaqueTabs = 184,
    chartX = 185,
    chartStar = 186,
    chartPlus = 187,
}

export enum STPlaceholderType {
    PBDEFAULT = 0,
    title = 1,
    body = 2,
    ctrTitle = 3,
    subTitle = 4,
    dt = 5,
    sldNum = 6,
    ftr = 7,
    hdr = 8,
    obj = 9,
    chart = 10,
    tbl = 11,
    clipArt = 12,
    dgm = 13,
    media = 14,
    sldImg = 15,
    pic = 16,
}

export enum STPlaceholderSize {
    PBDEFAULT = 0,
    full = 1,
    half = 2,
    quarter = 3,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AudioCD name="CT_AudioCD">
 * ```
 */
export interface ICTAudioCD {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AudioCDTime maxOccurs="1" minOccurs="1" name="st" type="CT_AudioCDTime">
     * ```
     */
    st?: ICTAudioCDTime;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AudioCDTime maxOccurs="1" minOccurs="1" name="end" type="CT_AudioCDTime">
     * ```
     */
    end?: ICTAudioCDTime;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AudioCD name="CT_AudioCD">
 * ```
 */
export interface ICTAudioCDDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AudioCDTime maxOccurs="1" minOccurs="1" name="st" type="CT_AudioCDTime">
     * ```
     */
    st?: ICTAudioCDTimeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AudioCDTime maxOccurs="1" minOccurs="1" name="end" type="CT_AudioCDTime">
     * ```
     */
    end?: ICTAudioCDTimeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AudioFile name="CT_AudioFile">
 * ```
 */
export interface ICTAudioFile {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:link name="link" ref="r:link" type="ST_RelationshipId" use="required">
     * ```
     */
    link?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="contentType" type="xsd:string" use="optional">
     * ```
     */
    contentType?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AudioFile name="CT_AudioFile">
 * ```
 */
export interface ICTAudioFileDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:link name="link" ref="r:link" type="ST_RelationshipId" use="required">
     * ```
     */
    link?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="contentType" type="xsd:string" use="optional">
     * ```
     */
    contentType?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_VideoFile name="CT_VideoFile">
 * ```
 */
export interface ICTVideoFile {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:link name="link" ref="r:link" type="ST_RelationshipId" use="required">
     * ```
     */
    link?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="contentType" type="xsd:string" use="optional">
     * ```
     */
    contentType?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_VideoFile name="CT_VideoFile">
 * ```
 */
export interface ICTVideoFileDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:link name="link" ref="r:link" type="ST_RelationshipId" use="required">
     * ```
     */
    link?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="contentType" type="xsd:string" use="optional">
     * ```
     */
    contentType?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_QuickTimeFile name="CT_QuickTimeFile">
 * ```
 */
export interface ICTQuickTimeFile {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:link name="link" ref="r:link" type="ST_RelationshipId" use="required">
     * ```
     */
    link?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_QuickTimeFile name="CT_QuickTimeFile">
 * ```
 */
export interface ICTQuickTimeFileDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:link name="link" ref="r:link" type="ST_RelationshipId" use="required">
     * ```
     */
    link?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ConnectionDmlA name="CT_ConnectionDmlA">
 * ```
 */
export interface ICTConnectionDmlA {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="ST_DrawingElementId" use="required">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt" use="required">
     * ```
     */
    idx?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ConnectionDmlA name="CT_ConnectionDmlA">
 * ```
 */
export interface ICTConnectionDmlADelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="ST_DrawingElementId" use="required">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt" use="required">
     * ```
     */
    idx?: number | null;
}

export enum STAttachmentClassify {
    PBDEFAULT = 0,
    video = 1,
    audio = 2,
    doc = 3,
    image = 4,
}

export enum STAttachmentShowType {
    PBDEFAULT = 0,
    simpleText = 1,
    text = 2,
    card = 3,
    none = 4,
    preview = 5,
}

/** 附件封面信息 */
export interface IPosterInfo {
    /** 附件里封面的图片地址 */
    url?: string;
}

export enum STAddonMode {
    PBDEFAULT = 0,
    normal = 1,
    iframe = 2,
}

/** 插件详细信息, 插件组件需要的信息, UI层不直接使用 */
export interface IAddonDocElement {
    /** 插件类型 */
    type?: string;
    /** 插件实例 ID */
    addonId?: string;
    /** 随机字符串，插件组件定位插件用 */
    anchorId?: string;
    /** iframe插件的内嵌网页地址 */
    embedUrl?: string;
    /** 插件自定义信息 */
    extraData?: string;
    /** 插件元素的高 */
    height?: number;
    /** 插件元素的宽x */
    width?: number;
    /** 插件元素是否锁定横纵比 */
    lockAspectRatio?: boolean;
    /** iframe 插件内视频或图片的地址 */
    originUrl?: string;
    /** 插件组件生成的插件 ID */
    packageId?: string;
    /** 图片插件的图片地址 */
    src?: string;
    /** 图片插件的图片类型 */
    sourceType?: string;
}

export enum STFontCollectionIndex {
    PBDEFAULT = 0,
    major = 1,
    minor = 2,
    none = 3,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GraphicalObjectData name="CT_GraphicalObjectData">
 * ```
 */
export interface ICTGraphicalObjectData {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlpicture:CT_PictureDmlPct ref=dmlpicture:pic name="pic" ref="dpct:pic" type="CT_PictureDmlPct">
     * ```
     */
    pic?: ICTPictureDmlPct;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_RelIds ref=dmldiagram:relIds name="relIds" ref="dgm:relIds" type="CT_RelIds">
     * ```
     */
    relIds?: ICTRelIds;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_RelId ref=dmlchart:chart name="chart" ref="c:chart" type="CT_RelId">
     * ```
     */
    chart?: ICTRelId;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="uri" type="xsd:token" use="required">
     * ```
     */
    uri?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=tencentdocumentdml:CT_DiagramsGroup name="dgmEx" skipIO="true" type="tddml:CT_DiagramsGroup">
     * ```
     */
    dgmEx?: ICTDiagramsGroup;
    /**
     * ```xml
     * <xsd:element typeRedirected=tencentdocumentdml:CT_ChartGroup name="chartEx" skipIO="true" type="tddml:CT_ChartGroup">
     * ```
     */
    chartEx?: ICTChartGroup;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_RelIdMsChartEx ref=mschartex:chart aliasName=chartSame aliasName="chartSame" name="chart" ref="cx:chart" type="CT_RelIdMsChartEx">
     * ```
     */
    chartSame?: ICTRelIdMsChartEx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableDmlA name="tbl" type="CT_TableDmlA">
     * ```
     */
    tbl?: ICTTableDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OleObjectPml ref=pml:oleObj name="oleObj" ref="p:oleObj" type="CT_OleObjectPml">
     * ```
     */
    oleObj?: ICTOleObjectPml;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_Model3D ref=msam3d2017:model3d name="model3d" ref="am3d:model3d" type="CT_Model3D">
     * ```
     */
    model3d?: ICTModel3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=tencentdocumentcommon:CT_ChartDataTd name="chartTd" skipIO="true" type="tdc:CT_ChartDataTd">
     * ```
     */
    chartTd?: ICTChartDataTd;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GraphicalObjectData name="CT_GraphicalObjectData">
 * ```
 */
export interface ICTGraphicalObjectDataDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlpicture:CT_PictureDmlPct ref=dmlpicture:pic name="pic" ref="dpct:pic" type="CT_PictureDmlPct">
     * ```
     */
    pic?: ICTPictureDmlPctDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_RelIds ref=dmldiagram:relIds name="relIds" ref="dgm:relIds" type="CT_RelIds">
     * ```
     */
    relIds?: ICTRelIdsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_RelId ref=dmlchart:chart name="chart" ref="c:chart" type="CT_RelId">
     * ```
     */
    chart?: ICTRelIdDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="uri" type="xsd:token" use="required">
     * ```
     */
    uri?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=tencentdocumentdml:CT_DiagramsGroup name="dgmEx" skipIO="true" type="tddml:CT_DiagramsGroup">
     * ```
     */
    dgmEx?: ICTDiagramsGroupDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=tencentdocumentdml:CT_ChartGroup name="chartEx" skipIO="true" type="tddml:CT_ChartGroup">
     * ```
     */
    chartEx?: ICTChartGroupDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_RelIdMsChartEx ref=mschartex:chart aliasName=chartSame aliasName="chartSame" name="chart" ref="cx:chart" type="CT_RelIdMsChartEx">
     * ```
     */
    chartSame?: ICTRelIdMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableDmlA name="tbl" type="CT_TableDmlA">
     * ```
     */
    tbl?: ICTTableDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_OleObjectPml ref=pml:oleObj name="oleObj" ref="p:oleObj" type="CT_OleObjectPml">
     * ```
     */
    oleObj?: ICTOleObjectPmlDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_Model3D ref=msam3d2017:model3d name="model3d" ref="am3d:model3d" type="CT_Model3D">
     * ```
     */
    model3d?: ICTModel3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=tencentdocumentcommon:CT_ChartDataTd name="chartTd" skipIO="true" type="tdc:CT_ChartDataTd">
     * ```
     */
    chartTd?: ICTChartDataTdDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Definitions name="CT_Definitions">
 * ```
 */
export interface ICTDefinitions {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Brush name="brush" type="CT_Brush">
     * ```
     */
    brush?: ICTBrush[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Canvas name="canvas" type="CT_Canvas">
     * ```
     */
    canvas?: ICTCanvas[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_CanvasTransform name="canvasTransform" type="CT_CanvasTransform">
     * ```
     */
    canvasTransform?: ICTCanvasTransform[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Context name="context" type="CT_Context">
     * ```
     */
    context?: ICTContext[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_InkSource name="inkSource" type="CT_InkSource">
     * ```
     */
    inkSource?: ICTInkSource[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Mapping name="mapping" type="CT_Mapping">
     * ```
     */
    mapping?: ICTMapping[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Timestamp name="timestamp" type="CT_Timestamp">
     * ```
     */
    timestamp?: ICTTimestamp[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Trace name="trace" type="CT_Trace">
     * ```
     */
    trace?: ICTTrace[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceFormat name="traceFormat" type="CT_TraceFormat">
     * ```
     */
    traceFormat?: ICTTraceFormat[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceGroup name="traceGroup" type="CT_TraceGroup">
     * ```
     */
    traceGroup?: ICTTraceGroup[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceView name="traceView" type="CT_TraceView">
     * ```
     */
    traceView?: ICTTraceView[];
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Definitions name="CT_Definitions">
 * ```
 */
export interface ICTDefinitionsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Brush name="brush" type="CT_Brush">
     * ```
     */
    brush?: ICTBrushDelta[];
    brushOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Canvas name="canvas" type="CT_Canvas">
     * ```
     */
    canvas?: ICTCanvasDelta[];
    canvasOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_CanvasTransform name="canvasTransform" type="CT_CanvasTransform">
     * ```
     */
    canvasTransform?: ICTCanvasTransformDelta[];
    canvasTransformOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Context name="context" type="CT_Context">
     * ```
     */
    context?: ICTContextDelta[];
    contextOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_InkSource name="inkSource" type="CT_InkSource">
     * ```
     */
    inkSource?: ICTInkSourceDelta[];
    inkSourceOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Mapping name="mapping" type="CT_Mapping">
     * ```
     */
    mapping?: ICTMappingDelta[];
    mappingOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Timestamp name="timestamp" type="CT_Timestamp">
     * ```
     */
    timestamp?: ICTTimestampDelta[];
    timestampOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Trace name="trace" type="CT_Trace">
     * ```
     */
    trace?: ICTTraceDelta[];
    traceOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceFormat name="traceFormat" type="CT_TraceFormat">
     * ```
     */
    traceFormat?: ICTTraceFormatDelta[];
    traceFormatOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceGroup name="traceGroup" type="CT_TraceGroup">
     * ```
     */
    traceGroup?: ICTTraceGroupDelta[];
    traceGroupOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceView name="traceView" type="CT_TraceView">
     * ```
     */
    traceView?: ICTTraceViewDelta[];
    traceViewOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Context name="CT_Context">
 * ```
 */
export interface ICTContext {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Canvas minOccurs="0" name="canvas" type="CT_Canvas">
     * ```
     */
    canvas?: ICTCanvas;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_CanvasTransform minOccurs="0" name="canvasTransform" type="CT_CanvasTransform">
     * ```
     */
    canvasTransform?: ICTCanvasTransform;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceFormat minOccurs="0" name="traceFormat" type="CT_TraceFormat">
     * ```
     */
    traceFormat?: ICTTraceFormat;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_InkSource minOccurs="0" name="inkSource" type="CT_InkSource">
     * ```
     */
    inkSource?: ICTInkSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Brush minOccurs="0" name="brush" type="CT_Brush">
     * ```
     */
    brush?: ICTBrush;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Timestamp minOccurs="0" name="timestamp" type="CT_Timestamp">
     * ```
     */
    timestamp?: ICTTimestamp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="contextRef" type="xsd:anyURI">
     * ```
     */
    contextRef?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="canvasRef" type="xsd:anyURI">
     * ```
     */
    canvasRef?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="canvasTransformRef" type="xsd:anyURI">
     * ```
     */
    canvasTransformRef?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="traceFormatRef" type="xsd:anyURI">
     * ```
     */
    traceFormatRef?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="inkSourceRef" type="xsd:anyURI">
     * ```
     */
    inkSourceRef?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="brushRef" type="xsd:anyURI">
     * ```
     */
    brushRef?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="timestampRef" type="xsd:anyURI">
     * ```
     */
    timestampRef?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Context name="CT_Context">
 * ```
 */
export interface ICTContextDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Canvas minOccurs="0" name="canvas" type="CT_Canvas">
     * ```
     */
    canvas?: ICTCanvasDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_CanvasTransform minOccurs="0" name="canvasTransform" type="CT_CanvasTransform">
     * ```
     */
    canvasTransform?: ICTCanvasTransformDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceFormat minOccurs="0" name="traceFormat" type="CT_TraceFormat">
     * ```
     */
    traceFormat?: ICTTraceFormatDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_InkSource minOccurs="0" name="inkSource" type="CT_InkSource">
     * ```
     */
    inkSource?: ICTInkSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Brush minOccurs="0" name="brush" type="CT_Brush">
     * ```
     */
    brush?: ICTBrushDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Timestamp minOccurs="0" name="timestamp" type="CT_Timestamp">
     * ```
     */
    timestamp?: ICTTimestampDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="contextRef" type="xsd:anyURI">
     * ```
     */
    contextRef?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="canvasRef" type="xsd:anyURI">
     * ```
     */
    canvasRef?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="canvasTransformRef" type="xsd:anyURI">
     * ```
     */
    canvasTransformRef?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="traceFormatRef" type="xsd:anyURI">
     * ```
     */
    traceFormatRef?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="inkSourceRef" type="xsd:anyURI">
     * ```
     */
    inkSourceRef?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="brushRef" type="xsd:anyURI">
     * ```
     */
    brushRef?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="timestampRef" type="xsd:anyURI">
     * ```
     */
    timestampRef?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Trace name="CT_Trace">
 * ```
 */
export interface ICTTrace {
    /**
     * ```xml
     * <xsd:extension base=xsd:string base="ST_TraceData">
     * ```
     */
    characterData?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=inkml:ST_TraceType default="penDown" name="type" type="ST_TraceType" use="optional">
     * ```
     */
    type?: STTraceType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=inkml:ST_Continuation default="begin" name="continuation" type="ST_Continuation" use="optional">
     * ```
     */
    continuation?: STContinuation;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="priorRef" type="xsd:anyURI" use="optional">
     * ```
     */
    priorRef?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="contextRef" type="xsd:anyURI">
     * ```
     */
    contextRef?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="brushRef" type="xsd:anyURI">
     * ```
     */
    brushRef?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="duration" type="xsd:integer">
     * ```
     */
    duration?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="timeOffset" type="xsd:integer">
     * ```
     */
    timeOffset?: number;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Trace name="CT_Trace">
 * ```
 */
export interface ICTTraceDelta {
    /**
     * ```xml
     * <xsd:extension base=xsd:string base="ST_TraceData">
     * ```
     */
    characterData?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=inkml:ST_TraceType default="penDown" name="type" type="ST_TraceType" use="optional">
     * ```
     */
    type?: STTraceType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=inkml:ST_Continuation default="begin" name="continuation" type="ST_Continuation" use="optional">
     * ```
     */
    continuation?: STContinuation | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="priorRef" type="xsd:anyURI" use="optional">
     * ```
     */
    priorRef?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="contextRef" type="xsd:anyURI">
     * ```
     */
    contextRef?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="brushRef" type="xsd:anyURI">
     * ```
     */
    brushRef?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="duration" type="xsd:integer">
     * ```
     */
    duration?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="timeOffset" type="xsd:integer">
     * ```
     */
    timeOffset?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_TraceGroup name="CT_TraceGroup">
 * ```
 */
export interface ICTTraceGroup {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Trace minOccurs="0" name="trace" type="CT_Trace">
     * ```
     */
    trace?: ICTTrace[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceGroup minOccurs="0" name="traceGroup" type="CT_TraceGroup">
     * ```
     */
    traceGroup?: ICTTraceGroup[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceView minOccurs="0" name="traceView" type="CT_TraceView">
     * ```
     */
    traceView?: ICTTraceView[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Annotation minOccurs="0" name="annotation" type="CT_Annotation">
     * ```
     */
    annotation?: ICTAnnotation[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_AnnotationXML minOccurs="0" name="annotationXML" type="CT_AnnotationXML">
     * ```
     */
    annotationXml?: ICTAnnotationXML[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="contextRef" type="xsd:anyURI">
     * ```
     */
    contextRef?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="brushRef" type="xsd:anyURI">
     * ```
     */
    brushRef?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_TraceGroup name="CT_TraceGroup">
 * ```
 */
export interface ICTTraceGroupDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Trace minOccurs="0" name="trace" type="CT_Trace">
     * ```
     */
    trace?: ICTTraceDelta[];
    traceOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceGroup minOccurs="0" name="traceGroup" type="CT_TraceGroup">
     * ```
     */
    traceGroup?: ICTTraceGroupDelta[];
    traceGroupOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceView minOccurs="0" name="traceView" type="CT_TraceView">
     * ```
     */
    traceView?: ICTTraceViewDelta[];
    traceViewOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Annotation minOccurs="0" name="annotation" type="CT_Annotation">
     * ```
     */
    annotation?: ICTAnnotationDelta[];
    annotationOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_AnnotationXML minOccurs="0" name="annotationXML" type="CT_AnnotationXML">
     * ```
     */
    annotationXml?: ICTAnnotationXMLDelta[];
    annotationXmlOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="contextRef" type="xsd:anyURI">
     * ```
     */
    contextRef?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="brushRef" type="xsd:anyURI">
     * ```
     */
    brushRef?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_TraceView name="CT_TraceView">
 * ```
 */
export interface ICTTraceView {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="traceDataRef" type="xsd:anyURI">
     * ```
     */
    traceDataRef?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="from" type="ST_IntegerList">
     * ```
     */
    from?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="to" type="ST_IntegerList">
     * ```
     */
    to?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_TraceView name="CT_TraceView">
 * ```
 */
export interface ICTTraceViewDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="traceDataRef" type="xsd:anyURI">
     * ```
     */
    traceDataRef?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="from" type="ST_IntegerList">
     * ```
     */
    from?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="to" type="ST_IntegerList">
     * ```
     */
    to?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Annotation name="CT_Annotation">
 * ```
 */
export interface ICTAnnotation {
    /**
     * ```xml
     * <xsd:extension base=xsd:string base="xsd:string">
     * ```
     */
    characterData?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="type" type="xsd:string">
     * ```
     */
    type?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="encoding" type="xsd:string">
     * ```
     */
    encoding?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Annotation name="CT_Annotation">
 * ```
 */
export interface ICTAnnotationDelta {
    /**
     * ```xml
     * <xsd:extension base=xsd:string base="xsd:string">
     * ```
     */
    characterData?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="type" type="xsd:string">
     * ```
     */
    type?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="encoding" type="xsd:string">
     * ```
     */
    encoding?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_AnnotationXML name="CT_AnnotationXML">
 * ```
 */
export interface ICTAnnotationXML {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="type" type="xsd:string">
     * ```
     */
    type?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="encoding" type="xsd:string">
     * ```
     */
    encoding?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="href" type="xsd:anyURI" use="optional">
     * ```
     */
    href?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_AnnotationXML name="CT_AnnotationXML">
 * ```
 */
export interface ICTAnnotationXMLDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="type" type="xsd:string">
     * ```
     */
    type?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="encoding" type="xsd:string">
     * ```
     */
    encoding?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="href" type="xsd:anyURI" use="optional">
     * ```
     */
    href?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_OleObjectEmbed name="CT_OleObjectEmbed">
 * ```
 */
export interface ICTOleObjectEmbed {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_OleObjectFollowColorScheme default="none" name="followColorScheme" type="ST_OleObjectFollowColorScheme" use="optional">
     * ```
     */
    followColorScheme?: STOleObjectFollowColorScheme;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_OleObjectEmbed name="CT_OleObjectEmbed">
 * ```
 */
export interface ICTOleObjectEmbedDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_OleObjectFollowColorScheme default="none" name="followColorScheme" type="ST_OleObjectFollowColorScheme" use="optional">
     * ```
     */
    followColorScheme?: STOleObjectFollowColorScheme | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_OleObjectLink name="CT_OleObjectLink">
 * ```
 */
export interface ICTOleObjectLink {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="updateAutomatic" type="xsd:boolean" use="optional">
     * ```
     */
    updateAutomatic?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_OleObjectLink name="CT_OleObjectLink">
 * ```
 */
export interface ICTOleObjectLinkDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_ExtensionListPml maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListPml">
     * ```
     */
    extLst?: ICTExtensionListPmlDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="updateAutomatic" type="xsd:boolean" use="optional">
     * ```
     */
    updateAutomatic?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_Model3DCamera name="CT_Model3DCamera">
 * ```
 */
export interface ICTModel3DCamera {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point3D maxOccurs="1" minOccurs="1" name="pos" type="a:CT_Point3D">
     * ```
     */
    pos?: ICTPoint3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Vector3D maxOccurs="1" minOccurs="1" name="up" type="a:CT_Vector3D">
     * ```
     */
    up?: ICTVector3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point3D maxOccurs="1" minOccurs="1" name="lookAt" type="a:CT_Point3D">
     * ```
     */
    lookAt?: ICTPoint3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_OrthographicProjection name="orthographic" type="CT_OrthographicProjection">
     * ```
     */
    orthographic?: ICTOrthographicProjection;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_PerspectiveProjection name="perspective" type="CT_PerspectiveProjection">
     * ```
     */
    perspective?: ICTPerspectiveProjection;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_Model3DCamera name="CT_Model3DCamera">
 * ```
 */
export interface ICTModel3DCameraDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point3D maxOccurs="1" minOccurs="1" name="pos" type="a:CT_Point3D">
     * ```
     */
    pos?: ICTPoint3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Vector3D maxOccurs="1" minOccurs="1" name="up" type="a:CT_Vector3D">
     * ```
     */
    up?: ICTVector3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point3D maxOccurs="1" minOccurs="1" name="lookAt" type="a:CT_Point3D">
     * ```
     */
    lookAt?: ICTPoint3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_OrthographicProjection name="orthographic" type="CT_OrthographicProjection">
     * ```
     */
    orthographic?: ICTOrthographicProjectionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_PerspectiveProjection name="perspective" type="CT_PerspectiveProjection">
     * ```
     */
    perspective?: ICTPerspectiveProjectionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_Model3DTransform name="CT_Model3DTransform">
 * ```
 */
export interface ICTModel3DTransform {
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_PositiveRatio maxOccurs="1" minOccurs="0" name="meterPerModelUnit" type="CT_PositiveRatio">
     * ```
     */
    meterPerModelUnit?: ICTPositiveRatio;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Vector3D maxOccurs="1" minOccurs="0" name="preTrans" type="a:CT_Vector3D">
     * ```
     */
    preTrans?: ICTVector3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_Scale3D maxOccurs="1" minOccurs="0" name="scale" type="CT_Scale3D">
     * ```
     */
    scale?: ICTScale3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_Rotate3D maxOccurs="1" minOccurs="0" name="rot" type="CT_Rotate3D">
     * ```
     */
    rot?: ICTRotate3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Vector3D maxOccurs="1" minOccurs="0" name="postTrans" type="a:CT_Vector3D">
     * ```
     */
    postTrans?: ICTVector3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_Model3DTransform name="CT_Model3DTransform">
 * ```
 */
export interface ICTModel3DTransformDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_PositiveRatio maxOccurs="1" minOccurs="0" name="meterPerModelUnit" type="CT_PositiveRatio">
     * ```
     */
    meterPerModelUnit?: ICTPositiveRatioDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Vector3D maxOccurs="1" minOccurs="0" name="preTrans" type="a:CT_Vector3D">
     * ```
     */
    preTrans?: ICTVector3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_Scale3D maxOccurs="1" minOccurs="0" name="scale" type="CT_Scale3D">
     * ```
     */
    scale?: ICTScale3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_Rotate3D maxOccurs="1" minOccurs="0" name="rot" type="CT_Rotate3D">
     * ```
     */
    rot?: ICTRotate3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Vector3D maxOccurs="1" minOccurs="0" name="postTrans" type="a:CT_Vector3D">
     * ```
     */
    postTrans?: ICTVector3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msa1611:CT_PictureAttributionSourceURL name="CT_PictureAttributionSourceURL">
 * ```
 */
export interface ICTPictureAttributionSourceURL {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string;
}

/**
 * ```xml
 * <xsd:complexType name=msa1611:CT_PictureAttributionSourceURL name="CT_PictureAttributionSourceURL">
 * ```
 */
export interface ICTPictureAttributionSourceURLDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_Model3DRaster name="CT_Model3DRaster">
 * ```
 */
export interface ICTModel3DRaster {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Blip maxOccurs="1" minOccurs="0" name="blip" type="a:CT_Blip">
     * ```
     */
    blip?: ICTBlip;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="rName" type="xsd:string" use="required">
     * ```
     */
    rName?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="rVer" type="xsd:string" use="required">
     * ```
     */
    rVer?: string;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_Model3DRaster name="CT_Model3DRaster">
 * ```
 */
export interface ICTModel3DRasterDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Blip maxOccurs="1" minOccurs="0" name="blip" type="a:CT_Blip">
     * ```
     */
    blip?: ICTBlipDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="rName" type="xsd:string" use="required">
     * ```
     */
    rName?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="rVer" type="xsd:string" use="required">
     * ```
     */
    rVer?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_ObjectViewport name="CT_ObjectViewport">
 * ```
 */
export interface ICTObjectViewport {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="viewportSz" type="a:ST_PositiveCoordinate" use="required">
     * ```
     */
    viewportSz?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_ObjectViewport name="CT_ObjectViewport">
 * ```
 */
export interface ICTObjectViewportDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="viewportSz" type="a:ST_PositiveCoordinate" use="required">
     * ```
     */
    viewportSz?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_WindowViewport name="CT_WindowViewport">
 * ```
 */
export interface ICTWindowViewport {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_WindowViewport name="CT_WindowViewport">
 * ```
 */
export interface ICTWindowViewportDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_AmbientLight name="CT_AmbientLight">
 * ```
 */
export interface ICTAmbientLight {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="clr" type="a:CT_ColorDmlA">
     * ```
     */
    clr?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_PositiveRatio maxOccurs="1" minOccurs="1" name="illuminance" type="CT_PositiveRatio">
     * ```
     */
    illuminance?: ICTPositiveRatio;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="enabled" type="xsd:boolean" use="optional">
     * ```
     */
    enabled?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_AmbientLight name="CT_AmbientLight">
 * ```
 */
export interface ICTAmbientLightDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="clr" type="a:CT_ColorDmlA">
     * ```
     */
    clr?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_PositiveRatio maxOccurs="1" minOccurs="1" name="illuminance" type="CT_PositiveRatio">
     * ```
     */
    illuminance?: ICTPositiveRatioDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="enabled" type="xsd:boolean" use="optional">
     * ```
     */
    enabled?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_PointLight name="CT_PointLight">
 * ```
 */
export interface ICTPointLight {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="clr" type="a:CT_ColorDmlA">
     * ```
     */
    clr?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_PositiveRatio maxOccurs="1" minOccurs="1" name="intensity" type="CT_PositiveRatio">
     * ```
     */
    intensity?: ICTPositiveRatio;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point3D maxOccurs="1" minOccurs="1" name="pos" type="a:CT_Point3D">
     * ```
     */
    pos?: ICTPoint3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="enabled" type="xsd:boolean" use="optional">
     * ```
     */
    enabled?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="rad" type="a:ST_PositiveCoordinate" use="required">
     * ```
     */
    rad?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_PointLight name="CT_PointLight">
 * ```
 */
export interface ICTPointLightDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="clr" type="a:CT_ColorDmlA">
     * ```
     */
    clr?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_PositiveRatio maxOccurs="1" minOccurs="1" name="intensity" type="CT_PositiveRatio">
     * ```
     */
    intensity?: ICTPositiveRatioDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point3D maxOccurs="1" minOccurs="1" name="pos" type="a:CT_Point3D">
     * ```
     */
    pos?: ICTPoint3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="enabled" type="xsd:boolean" use="optional">
     * ```
     */
    enabled?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="rad" type="a:ST_PositiveCoordinate" use="required">
     * ```
     */
    rad?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_SpotLight name="CT_SpotLight">
 * ```
 */
export interface ICTSpotLight {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="clr" type="a:CT_ColorDmlA">
     * ```
     */
    clr?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_PositiveRatio maxOccurs="1" minOccurs="1" name="intensity" type="CT_PositiveRatio">
     * ```
     */
    intensity?: ICTPositiveRatio;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point3D maxOccurs="1" minOccurs="1" name="pos" type="a:CT_Point3D">
     * ```
     */
    pos?: ICTPoint3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point3D maxOccurs="1" minOccurs="1" name="lookAt" type="a:CT_Point3D">
     * ```
     */
    lookAt?: ICTPoint3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="enabled" type="xsd:boolean" use="optional">
     * ```
     */
    enabled?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="rad" type="a:ST_PositiveCoordinate" use="required">
     * ```
     */
    rad?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="spotAng" type="a:ST_FOVAngle" use="required">
     * ```
     */
    spotAng?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_SpotLight name="CT_SpotLight">
 * ```
 */
export interface ICTSpotLightDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="clr" type="a:CT_ColorDmlA">
     * ```
     */
    clr?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_PositiveRatio maxOccurs="1" minOccurs="1" name="intensity" type="CT_PositiveRatio">
     * ```
     */
    intensity?: ICTPositiveRatioDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point3D maxOccurs="1" minOccurs="1" name="pos" type="a:CT_Point3D">
     * ```
     */
    pos?: ICTPoint3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point3D maxOccurs="1" minOccurs="1" name="lookAt" type="a:CT_Point3D">
     * ```
     */
    lookAt?: ICTPoint3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="enabled" type="xsd:boolean" use="optional">
     * ```
     */
    enabled?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="rad" type="a:ST_PositiveCoordinate" use="required">
     * ```
     */
    rad?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="spotAng" type="a:ST_FOVAngle" use="required">
     * ```
     */
    spotAng?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_DirectionalLight name="CT_DirectionalLight">
 * ```
 */
export interface ICTDirectionalLight {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="clr" type="a:CT_ColorDmlA">
     * ```
     */
    clr?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_PositiveRatio maxOccurs="1" minOccurs="1" name="illuminance" type="CT_PositiveRatio">
     * ```
     */
    illuminance?: ICTPositiveRatio;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point3D maxOccurs="1" minOccurs="1" name="pos" type="a:CT_Point3D">
     * ```
     */
    pos?: ICTPoint3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point3D maxOccurs="1" minOccurs="1" name="lookAt" type="a:CT_Point3D">
     * ```
     */
    lookAt?: ICTPoint3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="enabled" type="xsd:boolean" use="optional">
     * ```
     */
    enabled?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="angularRad" type="ST_AngularRadius" use="required">
     * ```
     */
    angularRad?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_DirectionalLight name="CT_DirectionalLight">
 * ```
 */
export interface ICTDirectionalLightDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="clr" type="a:CT_ColorDmlA">
     * ```
     */
    clr?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_PositiveRatio maxOccurs="1" minOccurs="1" name="illuminance" type="CT_PositiveRatio">
     * ```
     */
    illuminance?: ICTPositiveRatioDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point3D maxOccurs="1" minOccurs="1" name="pos" type="a:CT_Point3D">
     * ```
     */
    pos?: ICTPoint3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Point3D maxOccurs="1" minOccurs="1" name="lookAt" type="a:CT_Point3D">
     * ```
     */
    lookAt?: ICTPoint3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="enabled" type="xsd:boolean" use="optional">
     * ```
     */
    enabled?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="angularRad" type="ST_AngularRadius" use="required">
     * ```
     */
    angularRad?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_UnknownLight name="CT_UnknownLight">
 * ```
 */
export interface ICTUnknownLight {}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PresetLineDashProperties name="CT_PresetLineDashProperties">
 * ```
 */
export interface ICTPresetLineDashProperties {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PresetLineDashVal name="val" type="ST_PresetLineDashVal" use="optional">
     * ```
     */
    val?: STPresetLineDashVal;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PresetLineDashProperties name="CT_PresetLineDashProperties">
 * ```
 */
export interface ICTPresetLineDashPropertiesDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PresetLineDashVal name="val" type="ST_PresetLineDashVal" use="optional">
     * ```
     */
    val?: STPresetLineDashVal | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_DashStopList name="CT_DashStopList">
 * ```
 */
export interface ICTDashStopList {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_DashStop maxOccurs="unbounded" minOccurs="0" name="ds" type="CT_DashStop">
     * ```
     */
    ds?: ICTDashStop[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_DashStopList name="CT_DashStopList">
 * ```
 */
export interface ICTDashStopListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_DashStop maxOccurs="unbounded" minOccurs="0" name="ds" type="CT_DashStop">
     * ```
     */
    ds?: ICTDashStopDelta[];
    dsOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_LineJoinMiterProperties name="CT_LineJoinMiterProperties">
 * ```
 */
export interface ICTLineJoinMiterProperties {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositivePercentageDecimal" default="100" name="lim" type="ST_PositivePercentageDmlA" use="optional">
     * ```
     */
    lim?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_LineJoinMiterProperties name="CT_LineJoinMiterProperties">
 * ```
 */
export interface ICTLineJoinMiterPropertiesDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositivePercentageDecimal" default="100" name="lim" type="ST_PositivePercentageDmlA" use="optional">
     * ```
     */
    lim?: number | null;
}

export enum STLineEndType {
    PBDEFAULT = 0,
    none = 1,
    triangle = 2,
    stealth = 3,
    diamond = 4,
    oval = 5,
    arrow = 6,
}

export enum STLineEndWidth {
    PBDEFAULT = 0,
    sm = 1,
    med = 2,
    lg = 3,
}

export enum STLineEndLength {
    PBDEFAULT = 0,
    sm = 1,
    med = 2,
    lg = 3,
}

export enum STBevelPresetType {
    PBDEFAULT = 0,
    relaxedInset = 1,
    circle = 2,
    slope = 3,
    cross = 4,
    angle = 5,
    softRound = 6,
    convex = 7,
    coolSlant = 8,
    divot = 9,
    riblet = 10,
    hardEdge = 11,
    artDeco = 12,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_SphereCoords name="CT_SphereCoords">
 * ```
 */
export interface ICTSphereCoords {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="lat" type="ST_PositiveFixedAngle" use="required">
     * ```
     */
    lat?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="lon" type="ST_PositiveFixedAngle" use="required">
     * ```
     */
    lon?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="rev" type="ST_PositiveFixedAngle" use="required">
     * ```
     */
    rev?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_SphereCoords name="CT_SphereCoords">
 * ```
 */
export interface ICTSphereCoordsDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="lat" type="ST_PositiveFixedAngle" use="required">
     * ```
     */
    lat?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="lon" type="ST_PositiveFixedAngle" use="required">
     * ```
     */
    lon?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="rev" type="ST_PositiveFixedAngle" use="required">
     * ```
     */
    rev?: number | null;
}

export enum STLightRigType {
    PBDEFAULT = 0,
    legacyFlat1 = 1,
    legacyFlat2 = 2,
    legacyFlat3 = 3,
    legacyFlat4 = 4,
    legacyNormal1 = 5,
    legacyNormal2 = 6,
    legacyNormal3 = 7,
    legacyNormal4 = 8,
    legacyHarsh1 = 9,
    legacyHarsh2 = 10,
    legacyHarsh3 = 11,
    legacyHarsh4 = 12,
    threePt = 13,
    balanced = 14,
    soft = 15,
    harsh = 16,
    flood = 17,
    contrasting = 18,
    morning = 19,
    sunrise = 20,
    sunset = 21,
    chilly = 22,
    freezing = 23,
    flat = 24,
    twoPt = 25,
    glow = 26,
    brightRoom = 27,
}

export enum STLightRigDirection {
    PBDEFAULT = 0,
    tl = 1,
    t = 2,
    tr = 3,
    l = 4,
    r = 5,
    bl = 6,
    b = 7,
    br = 8,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GradientStopDmlA name="CT_GradientStopDmlA">
 * ```
 */
export interface ICTGradientStopDmlA {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoice;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositiveFixedPercentageDecimal" default="100000" name="pos" type="ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    pos?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GradientStopDmlA name="CT_GradientStopDmlA">
 * ```
 */
export interface ICTGradientStopDmlADelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoiceDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositiveFixedPercentageDecimal" default="100000" name="pos" type="ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    pos?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_LinearShadeProperties name="CT_LinearShadeProperties">
 * ```
 */
export interface ICTLinearShadeProperties {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="ang" type="ST_PositiveFixedAngle" use="optional">
     * ```
     */
    ang?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="scaled" type="xsd:boolean" use="optional">
     * ```
     */
    scaled?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_LinearShadeProperties name="CT_LinearShadeProperties">
 * ```
 */
export interface ICTLinearShadePropertiesDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="ang" type="ST_PositiveFixedAngle" use="optional">
     * ```
     */
    ang?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="scaled" type="xsd:boolean" use="optional">
     * ```
     */
    scaled?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PathShadeProperties name="CT_PathShadeProperties">
 * ```
 */
export interface ICTPathShadeProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_RelativeRect maxOccurs="1" minOccurs="0" name="fillToRect" type="CT_RelativeRect">
     * ```
     */
    fillToRect?: ICTRelativeRect;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PathShadeType name="path" type="ST_PathShadeType" use="optional">
     * ```
     */
    path?: STPathShadeType;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PathShadeProperties name="CT_PathShadeProperties">
 * ```
 */
export interface ICTPathShadePropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_RelativeRect maxOccurs="1" minOccurs="0" name="fillToRect" type="CT_RelativeRect">
     * ```
     */
    fillToRect?: ICTRelativeRectDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PathShadeType name="path" type="ST_PathShadeType" use="optional">
     * ```
     */
    path?: STPathShadeType | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AlphaBiLevelEffect name="CT_AlphaBiLevelEffect">
 * ```
 */
export interface ICTAlphaBiLevelEffect {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositiveFixedPercentageDecimal" name="thresh" type="ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    thresh?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AlphaBiLevelEffect name="CT_AlphaBiLevelEffect">
 * ```
 */
export interface ICTAlphaBiLevelEffectDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositiveFixedPercentageDecimal" name="thresh" type="ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    thresh?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AlphaInverseEffect name="CT_AlphaInverseEffect">
 * ```
 */
export interface ICTAlphaInverseEffect {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoice;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AlphaInverseEffect name="CT_AlphaInverseEffect">
 * ```
 */
export interface ICTAlphaInverseEffectDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoiceDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AlphaModulateEffect name="CT_AlphaModulateEffect">
 * ```
 */
export interface ICTAlphaModulateEffect {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="cont" type="CT_EffectContainer">
     * ```
     */
    cont?: ICTEffectContainer;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AlphaModulateEffect name="CT_AlphaModulateEffect">
 * ```
 */
export interface ICTAlphaModulateEffectDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="cont" type="CT_EffectContainer">
     * ```
     */
    cont?: ICTEffectContainerDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AlphaModulateFixedEffect name="CT_AlphaModulateFixedEffect">
 * ```
 */
export interface ICTAlphaModulateFixedEffect {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositivePercentageDecimal" default="100" name="amt" type="ST_PositivePercentageDmlA" use="optional">
     * ```
     */
    amt?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AlphaModulateFixedEffect name="CT_AlphaModulateFixedEffect">
 * ```
 */
export interface ICTAlphaModulateFixedEffectDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositivePercentageDecimal" default="100" name="amt" type="ST_PositivePercentageDmlA" use="optional">
     * ```
     */
    amt?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AlphaReplaceEffect name="CT_AlphaReplaceEffect">
 * ```
 */
export interface ICTAlphaReplaceEffect {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositiveFixedPercentageDecimal" name="a" type="ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    a?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AlphaReplaceEffect name="CT_AlphaReplaceEffect">
 * ```
 */
export interface ICTAlphaReplaceEffectDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositiveFixedPercentageDecimal" name="a" type="ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    a?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_BiLevelEffect name="CT_BiLevelEffect">
 * ```
 */
export interface ICTBiLevelEffect {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositiveFixedPercentageDecimal" name="thresh" type="ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    thresh?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_BiLevelEffect name="CT_BiLevelEffect">
 * ```
 */
export interface ICTBiLevelEffectDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositiveFixedPercentageDecimal" name="thresh" type="ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    thresh?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ColorChangeEffect name="CT_ColorChangeEffect">
 * ```
 */
export interface ICTColorChangeEffect {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="clrFrom" type="CT_ColorDmlA">
     * ```
     */
    clrFrom?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="clrTo" type="CT_ColorDmlA">
     * ```
     */
    clrTo?: ICTColorDmlA;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="useA" type="xsd:boolean" use="optional">
     * ```
     */
    useA?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ColorChangeEffect name="CT_ColorChangeEffect">
 * ```
 */
export interface ICTColorChangeEffectDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="clrFrom" type="CT_ColorDmlA">
     * ```
     */
    clrFrom?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorDmlA maxOccurs="1" minOccurs="1" name="clrTo" type="CT_ColorDmlA">
     * ```
     */
    clrTo?: ICTColorDmlADelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="useA" type="xsd:boolean" use="optional">
     * ```
     */
    useA?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ColorReplaceEffect name="CT_ColorReplaceEffect">
 * ```
 */
export interface ICTColorReplaceEffect {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoice;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ColorReplaceEffect name="CT_ColorReplaceEffect">
 * ```
 */
export interface ICTColorReplaceEffectDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoiceDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_DuotoneEffect name="CT_DuotoneEffect">
 * ```
 */
export interface ICTDuotoneEffect {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="2" minOccurs="2" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoice[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_DuotoneEffect name="CT_DuotoneEffect">
 * ```
 */
export interface ICTDuotoneEffectDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="2" minOccurs="2" name="EG_ColorChoice" ref="EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoiceDelta[];
    egColorChoiceOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_HSLEffect name="CT_HSLEffect">
 * ```
 */
export interface ICTHSLEffect {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="hue" type="ST_PositiveFixedAngle" use="optional">
     * ```
     */
    hue?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_FixedPercentageDecimal" default="0" name="sat" type="ST_FixedPercentageDmlA" use="optional">
     * ```
     */
    sat?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_FixedPercentageDecimal" default="0" name="lum" type="ST_FixedPercentageDmlA" use="optional">
     * ```
     */
    lum?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_HSLEffect name="CT_HSLEffect">
 * ```
 */
export interface ICTHSLEffectDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="hue" type="ST_PositiveFixedAngle" use="optional">
     * ```
     */
    hue?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_FixedPercentageDecimal" default="0" name="sat" type="ST_FixedPercentageDmlA" use="optional">
     * ```
     */
    sat?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_FixedPercentageDecimal" default="0" name="lum" type="ST_FixedPercentageDmlA" use="optional">
     * ```
     */
    lum?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_LuminanceEffect name="CT_LuminanceEffect">
 * ```
 */
export interface ICTLuminanceEffect {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_FixedPercentageDecimal" default="0" name="bright" type="ST_FixedPercentageDmlA" use="optional">
     * ```
     */
    bright?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_FixedPercentageDecimal" default="0" name="contrast" type="ST_FixedPercentageDmlA" use="optional">
     * ```
     */
    contrast?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_LuminanceEffect name="CT_LuminanceEffect">
 * ```
 */
export interface ICTLuminanceEffectDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_FixedPercentageDecimal" default="0" name="bright" type="ST_FixedPercentageDmlA" use="optional">
     * ```
     */
    bright?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_FixedPercentageDecimal" default="0" name="contrast" type="ST_FixedPercentageDmlA" use="optional">
     * ```
     */
    contrast?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TintEffect name="CT_TintEffect">
 * ```
 */
export interface ICTTintEffect {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="hue" type="ST_PositiveFixedAngle" use="optional">
     * ```
     */
    hue?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_FixedPercentageDecimal" default="0" name="amt" type="ST_FixedPercentageDmlA" use="optional">
     * ```
     */
    amt?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TintEffect name="CT_TintEffect">
 * ```
 */
export interface ICTTintEffectDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="hue" type="ST_PositiveFixedAngle" use="optional">
     * ```
     */
    hue?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_FixedPercentageDecimal" default="0" name="amt" type="ST_FixedPercentageDmlA" use="optional">
     * ```
     */
    amt?: number | null;
}

export enum STBlipCompression {
    PBDEFAULT = 0,
    email = 1,
    screen = 2,
    print = 3,
    hqprint = 4,
    none = 5,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TileInfoProperties name="CT_TileInfoProperties">
 * ```
 */
export interface ICTTileInfoProperties {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="tx" type="ST_Coordinate" use="optional">
     * ```
     */
    tx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="ty" type="ST_Coordinate" use="optional">
     * ```
     */
    ty?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" name="sx" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    sx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" name="sy" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    sy?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TileFlipMode default="none" name="flip" type="ST_TileFlipMode" use="optional">
     * ```
     */
    flip?: STTileFlipMode;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_RectAlignment name="algn" type="ST_RectAlignment" use="optional">
     * ```
     */
    algn?: STRectAlignment;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TileInfoProperties name="CT_TileInfoProperties">
 * ```
 */
export interface ICTTileInfoPropertiesDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="tx" type="ST_Coordinate" use="optional">
     * ```
     */
    tx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="ty" type="ST_Coordinate" use="optional">
     * ```
     */
    ty?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" name="sx" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    sx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" name="sy" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    sy?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_TileFlipMode default="none" name="flip" type="ST_TileFlipMode" use="optional">
     * ```
     */
    flip?: STTileFlipMode | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_RectAlignment name="algn" type="ST_RectAlignment" use="optional">
     * ```
     */
    algn?: STRectAlignment | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_StretchInfoProperties name="CT_StretchInfoProperties">
 * ```
 */
export interface ICTStretchInfoProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_RelativeRect maxOccurs="1" minOccurs="0" name="fillRect" type="CT_RelativeRect">
     * ```
     */
    fillRect?: ICTRelativeRect;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_StretchInfoProperties name="CT_StretchInfoProperties">
 * ```
 */
export interface ICTStretchInfoPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_RelativeRect maxOccurs="1" minOccurs="0" name="fillRect" type="CT_RelativeRect">
     * ```
     */
    fillRect?: ICTRelativeRectDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTimeCondition name="CT_TLTimeCondition">
 * ```
 */
export interface ICTTLTimeCondition {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeTargetElement name="tgtEl" type="CT_TLTimeTargetElement">
     * ```
     */
    tgtEl?: ICTTLTimeTargetElement;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTriggerTimeNodeID name="tn" type="CT_TLTriggerTimeNodeID">
     * ```
     */
    tn?: ICTTLTriggerTimeNodeID;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTriggerRuntimeNode name="rtn" type="CT_TLTriggerRuntimeNode">
     * ```
     */
    rtn?: ICTTLTriggerRuntimeNode;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLTriggerEvent name="evt" type="ST_TLTriggerEvent" use="optional">
     * ```
     */
    evt?: STTLTriggerEvent;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="delay" type="ST_TLTime" use="optional">
     * ```
     */
    delay?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTimeCondition name="CT_TLTimeCondition">
 * ```
 */
export interface ICTTLTimeConditionDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTimeTargetElement name="tgtEl" type="CT_TLTimeTargetElement">
     * ```
     */
    tgtEl?: ICTTLTimeTargetElementDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTriggerTimeNodeID name="tn" type="CT_TLTriggerTimeNodeID">
     * ```
     */
    tn?: ICTTLTriggerTimeNodeIDDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTriggerRuntimeNode name="rtn" type="CT_TLTriggerRuntimeNode">
     * ```
     */
    rtn?: ICTTLTriggerRuntimeNodeDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLTriggerEvent name="evt" type="ST_TLTriggerEvent" use="optional">
     * ```
     */
    evt?: STTLTriggerEvent | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="delay" type="ST_TLTime" use="optional">
     * ```
     */
    delay?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLIterateData name="CT_TLIterateData">
 * ```
 */
export interface ICTTLIterateData {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLIterateIntervalTime name="tmAbs" type="CT_TLIterateIntervalTime">
     * ```
     */
    tmAbs?: ICTTLIterateIntervalTime;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLIterateIntervalPercentage name="tmPct" type="CT_TLIterateIntervalPercentage">
     * ```
     */
    tmPct?: ICTTLIterateIntervalPercentage;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_IterateType default="el" name="type" type="ST_IterateType" use="optional">
     * ```
     */
    type?: STIterateType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="backwards" type="xsd:boolean" use="optional">
     * ```
     */
    backwards?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLIterateData name="CT_TLIterateData">
 * ```
 */
export interface ICTTLIterateDataDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLIterateIntervalTime name="tmAbs" type="CT_TLIterateIntervalTime">
     * ```
     */
    tmAbs?: ICTTLIterateIntervalTimeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLIterateIntervalPercentage name="tmPct" type="CT_TLIterateIntervalPercentage">
     * ```
     */
    tmPct?: ICTTLIterateIntervalPercentageDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_IterateType default="el" name="type" type="ST_IterateType" use="optional">
     * ```
     */
    type?: STIterateType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="backwards" type="xsd:boolean" use="optional">
     * ```
     */
    backwards?: boolean | null;
}

export enum STTLTimeNodePresetClassType {
    PBDEFAULT = 0,
    entr = 1,
    exit = 2,
    emph = 3,
    path = 4,
    verb = 5,
    mediacall = 6,
}

export enum STTLTimeNodeRestartType {
    PBDEFAULT = 0,
    always = 1,
    whenNotActive = 2,
    never = 3,
}

export enum STTLTimeNodeFillType {
    PBDEFAULT = 0,
    remove = 1,
    freeze = 2,
    hold = 3,
    transition = 4,
}

export enum STTLTimeNodeSyncType {
    PBDEFAULT = 0,
    canSlip = 1,
    locked = 2,
}

export enum STTLTimeNodeMasterRelation {
    PBDEFAULT = 0,
    sameClick = 1,
    lastClick = 2,
    nextClick = 3,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTimeTargetElement name="CT_TLTimeTargetElement">
 * ```
 */
export interface ICTTLTimeTargetElement {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="sldTgt" type="CT_EmptyPml">
     * ```
     */
    sldTgt?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EmbeddedWAVAudioFile name="sndTgt" type="a:CT_EmbeddedWAVAudioFile">
     * ```
     */
    sndTgt?: ICTEmbeddedWAVAudioFile;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLShapeTargetElement name="spTgt" type="CT_TLShapeTargetElement">
     * ```
     */
    spTgt?: ICTTLShapeTargetElement;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLSubShapeId name="inkTgt" type="CT_TLSubShapeId">
     * ```
     */
    inkTgt?: ICTTLSubShapeId;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTimeTargetElement name="CT_TLTimeTargetElement">
 * ```
 */
export interface ICTTLTimeTargetElementDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="sldTgt" type="CT_EmptyPml">
     * ```
     */
    sldTgt?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EmbeddedWAVAudioFile name="sndTgt" type="a:CT_EmbeddedWAVAudioFile">
     * ```
     */
    sndTgt?: ICTEmbeddedWAVAudioFileDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLShapeTargetElement name="spTgt" type="CT_TLShapeTargetElement">
     * ```
     */
    spTgt?: ICTTLShapeTargetElementDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLSubShapeId name="inkTgt" type="CT_TLSubShapeId">
     * ```
     */
    inkTgt?: ICTTLSubShapeIdDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLBehaviorAttributeNameList name="CT_TLBehaviorAttributeNameList">
 * ```
 */
export interface ICTTLBehaviorAttributeNameList {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="unbounded" minOccurs="1" name="attrName" type="xsd:string">
     * ```
     */
    attrName?: string[];
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLBehaviorAttributeNameList name="CT_TLBehaviorAttributeNameList">
 * ```
 */
export interface ICTTLBehaviorAttributeNameListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="unbounded" minOccurs="1" name="attrName" type="xsd:string">
     * ```
     */
    attrName?: string[];
    attrNameOp?: IArrayOp;
}

export enum STTLBehaviorAdditiveType {
    PBDEFAULT = 0,
    base = 1,
    sum = 2,
    repl = 3,
    mult = 4,
    none = 5,
}

export enum STTLBehaviorAccumulateType {
    PBDEFAULT = 0,
    none = 1,
    always = 2,
}

export enum STTLBehaviorTransformType {
    PBDEFAULT = 0,
    pt = 1,
    img = 2,
}

export enum STTLBehaviorOverrideType {
    PBDEFAULT = 0,
    normal = 1,
    childStyle = 2,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTimeAnimateValue name="CT_TLTimeAnimateValue">
 * ```
 */
export interface ICTTLTimeAnimateValue {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimVariant maxOccurs="1" minOccurs="0" name="val" type="CT_TLAnimVariant">
     * ```
     */
    val?: ICTTLAnimVariant;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="indefinite" name="tm" type="ST_TLTimeAnimateValueTime" use="optional">
     * ```
     */
    tm?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="fmla" type="xsd:string" use="optional">
     * ```
     */
    fmla?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTimeAnimateValue name="CT_TLTimeAnimateValue">
 * ```
 */
export interface ICTTLTimeAnimateValueDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLAnimVariant maxOccurs="1" minOccurs="0" name="val" type="CT_TLAnimVariant">
     * ```
     */
    val?: ICTTLAnimVariantDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="indefinite" name="tm" type="ST_TLTimeAnimateValueTime" use="optional">
     * ```
     */
    tm?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="fmla" type="xsd:string" use="optional">
     * ```
     */
    fmla?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLByRgbColorTransform name="CT_TLByRgbColorTransform">
 * ```
 */
export interface ICTTLByRgbColorTransform {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_FixedPercentageDecimal" name="r" type="a:ST_FixedPercentageDmlA" use="required">
     * ```
     */
    r?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_FixedPercentageDecimal" name="g" type="a:ST_FixedPercentageDmlA" use="required">
     * ```
     */
    g?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_FixedPercentageDecimal" name="b" type="a:ST_FixedPercentageDmlA" use="required">
     * ```
     */
    b?: number;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLByRgbColorTransform name="CT_TLByRgbColorTransform">
 * ```
 */
export interface ICTTLByRgbColorTransformDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_FixedPercentageDecimal" name="r" type="a:ST_FixedPercentageDmlA" use="required">
     * ```
     */
    r?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_FixedPercentageDecimal" name="g" type="a:ST_FixedPercentageDmlA" use="required">
     * ```
     */
    g?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_FixedPercentageDecimal" name="b" type="a:ST_FixedPercentageDmlA" use="required">
     * ```
     */
    b?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLByHslColorTransform name="CT_TLByHslColorTransform">
 * ```
 */
export interface ICTTLByHslColorTransform {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="h" type="a:ST_AngleDmlA" use="required">
     * ```
     */
    h?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_FixedPercentageDecimal" name="s" type="a:ST_FixedPercentageDmlA" use="required">
     * ```
     */
    s?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_FixedPercentageDecimal" name="l" type="a:ST_FixedPercentageDmlA" use="required">
     * ```
     */
    l?: number;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLByHslColorTransform name="CT_TLByHslColorTransform">
 * ```
 */
export interface ICTTLByHslColorTransformDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="h" type="a:ST_AngleDmlA" use="required">
     * ```
     */
    h?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_FixedPercentageDecimal" name="s" type="a:ST_FixedPercentageDmlA" use="required">
     * ```
     */
    s?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_FixedPercentageDecimal" name="l" type="a:ST_FixedPercentageDmlA" use="required">
     * ```
     */
    l?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimVariantBooleanVal name="CT_TLAnimVariantBooleanVal">
 * ```
 */
export interface ICTTLAnimVariantBooleanVal {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="val" type="xsd:boolean" use="required">
     * ```
     */
    val?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimVariantBooleanVal name="CT_TLAnimVariantBooleanVal">
 * ```
 */
export interface ICTTLAnimVariantBooleanValDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="val" type="xsd:boolean" use="required">
     * ```
     */
    val?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimVariantIntegerVal name="CT_TLAnimVariantIntegerVal">
 * ```
 */
export interface ICTTLAnimVariantIntegerVal {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="val" type="xsd:int" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimVariantIntegerVal name="CT_TLAnimVariantIntegerVal">
 * ```
 */
export interface ICTTLAnimVariantIntegerValDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="val" type="xsd:int" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimVariantFloatVal name="CT_TLAnimVariantFloatVal">
 * ```
 */
export interface ICTTLAnimVariantFloatVal {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="val" type="xsd:float" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimVariantFloatVal name="CT_TLAnimVariantFloatVal">
 * ```
 */
export interface ICTTLAnimVariantFloatValDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="val" type="xsd:float" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimVariantStringVal name="CT_TLAnimVariantStringVal">
 * ```
 */
export interface ICTTLAnimVariantStringVal {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="xsd:string" use="required">
     * ```
     */
    val?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLAnimVariantStringVal name="CT_TLAnimVariantStringVal">
 * ```
 */
export interface ICTTLAnimVariantStringValDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="xsd:string" use="required">
     * ```
     */
    val?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_SupplementalFont name="CT_SupplementalFont">
 * ```
 */
export interface ICTSupplementalFont {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="script" type="xsd:string" use="required">
     * ```
     */
    script?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="typeface" type="ST_TextTypeface" use="required">
     * ```
     */
    typeface?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_SupplementalFont name="CT_SupplementalFont">
 * ```
 */
export interface ICTSupplementalFontDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="script" type="xsd:string" use="required">
     * ```
     */
    script?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="typeface" type="ST_TextTypeface" use="required">
     * ```
     */
    typeface?: string | null;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_FillProperties name="EG_FillProperties">
 * ```
 */
export interface IEGFillProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_FillProperties name="EG_FillProperties">
 * ```
 */
export interface IEGFillPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_EffectStyleItem name="CT_EffectStyleItem">
 * ```
 */
export interface ICTEffectStyleItem {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectList maxOccurs="1" minOccurs="1" name="effectLst" type="CT_EffectList">
     * ```
     */
    effectLst?: ICTEffectList;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="effectDag" type="CT_EffectContainer">
     * ```
     */
    effectDag?: ICTEffectContainer;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Scene3D maxOccurs="1" minOccurs="0" name="scene3d" type="CT_Scene3D">
     * ```
     */
    scene3d?: ICTScene3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Shape3D maxOccurs="1" minOccurs="0" name="sp3d" type="CT_Shape3D">
     * ```
     */
    sp3d?: ICTShape3D;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_EffectStyleItem name="CT_EffectStyleItem">
 * ```
 */
export interface ICTEffectStyleItemDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectList maxOccurs="1" minOccurs="1" name="effectLst" type="CT_EffectList">
     * ```
     */
    effectLst?: ICTEffectListDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="effectDag" type="CT_EffectContainer">
     * ```
     */
    effectDag?: ICTEffectContainerDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Scene3D maxOccurs="1" minOccurs="0" name="scene3d" type="CT_Scene3D">
     * ```
     */
    scene3d?: ICTScene3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Shape3D maxOccurs="1" minOccurs="0" name="sp3d" type="CT_Shape3D">
     * ```
     */
    sp3d?: ICTShape3DDelta | null;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_ColorTransform name="EG_ColorTransform">
 * ```
 */
export interface IEGColorTransform {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositiveFixedPercentage maxOccurs="1" minOccurs="1" name="tint" type="CT_PositiveFixedPercentage">
     * ```
     */
    tint?: ICTPositiveFixedPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositiveFixedPercentage maxOccurs="1" minOccurs="1" name="shade" type="CT_PositiveFixedPercentage">
     * ```
     */
    shade?: ICTPositiveFixedPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ComplementTransform convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="comp" type="CT_ComplementTransform">
     * ```
     */
    comp?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_InverseTransform convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="inv" type="CT_InverseTransform">
     * ```
     */
    inv?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GrayscaleTransform convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="gray" type="CT_GrayscaleTransform">
     * ```
     */
    gray?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositiveFixedPercentage maxOccurs="1" minOccurs="1" name="alpha" type="CT_PositiveFixedPercentage">
     * ```
     */
    alpha?: ICTPositiveFixedPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FixedPercentage maxOccurs="1" minOccurs="1" name="alphaOff" type="CT_FixedPercentage">
     * ```
     */
    alphaOff?: ICTFixedPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositivePercentage maxOccurs="1" minOccurs="1" name="alphaMod" type="CT_PositivePercentage">
     * ```
     */
    alphaMod?: ICTPositivePercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositiveFixedAngle maxOccurs="1" minOccurs="1" name="hue" type="CT_PositiveFixedAngle">
     * ```
     */
    hue?: ICTPositiveFixedAngle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Angle maxOccurs="1" minOccurs="1" name="hueOff" type="CT_Angle">
     * ```
     */
    hueOff?: ICTAngle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositivePercentage maxOccurs="1" minOccurs="1" name="hueMod" type="CT_PositivePercentage">
     * ```
     */
    hueMod?: ICTPositivePercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="sat" type="CT_Percentage">
     * ```
     */
    sat?: ICTPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="satOff" type="CT_Percentage">
     * ```
     */
    satOff?: ICTPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="satMod" type="CT_Percentage">
     * ```
     */
    satMod?: ICTPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="lum" type="CT_Percentage">
     * ```
     */
    lum?: ICTPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="lumOff" type="CT_Percentage">
     * ```
     */
    lumOff?: ICTPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="lumMod" type="CT_Percentage">
     * ```
     */
    lumMod?: ICTPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="red" type="CT_Percentage">
     * ```
     */
    red?: ICTPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="redOff" type="CT_Percentage">
     * ```
     */
    redOff?: ICTPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="redMod" type="CT_Percentage">
     * ```
     */
    redMod?: ICTPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="green" type="CT_Percentage">
     * ```
     */
    green?: ICTPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="greenOff" type="CT_Percentage">
     * ```
     */
    greenOff?: ICTPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="greenMod" type="CT_Percentage">
     * ```
     */
    greenMod?: ICTPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="blue" type="CT_Percentage">
     * ```
     */
    blue?: ICTPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="blueOff" type="CT_Percentage">
     * ```
     */
    blueOff?: ICTPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="blueMod" type="CT_Percentage">
     * ```
     */
    blueMod?: ICTPercentage;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GammaTransform convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="gamma" type="CT_GammaTransform">
     * ```
     */
    gamma?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_InverseGammaTransform convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="invGamma" type="CT_InverseGammaTransform">
     * ```
     */
    invGamma?: boolean;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_ColorTransform name="EG_ColorTransform">
 * ```
 */
export interface IEGColorTransformDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositiveFixedPercentage maxOccurs="1" minOccurs="1" name="tint" type="CT_PositiveFixedPercentage">
     * ```
     */
    tint?: ICTPositiveFixedPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositiveFixedPercentage maxOccurs="1" minOccurs="1" name="shade" type="CT_PositiveFixedPercentage">
     * ```
     */
    shade?: ICTPositiveFixedPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ComplementTransform convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="comp" type="CT_ComplementTransform">
     * ```
     */
    comp?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_InverseTransform convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="inv" type="CT_InverseTransform">
     * ```
     */
    inv?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GrayscaleTransform convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="gray" type="CT_GrayscaleTransform">
     * ```
     */
    gray?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositiveFixedPercentage maxOccurs="1" minOccurs="1" name="alpha" type="CT_PositiveFixedPercentage">
     * ```
     */
    alpha?: ICTPositiveFixedPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FixedPercentage maxOccurs="1" minOccurs="1" name="alphaOff" type="CT_FixedPercentage">
     * ```
     */
    alphaOff?: ICTFixedPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositivePercentage maxOccurs="1" minOccurs="1" name="alphaMod" type="CT_PositivePercentage">
     * ```
     */
    alphaMod?: ICTPositivePercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositiveFixedAngle maxOccurs="1" minOccurs="1" name="hue" type="CT_PositiveFixedAngle">
     * ```
     */
    hue?: ICTPositiveFixedAngleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Angle maxOccurs="1" minOccurs="1" name="hueOff" type="CT_Angle">
     * ```
     */
    hueOff?: ICTAngleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PositivePercentage maxOccurs="1" minOccurs="1" name="hueMod" type="CT_PositivePercentage">
     * ```
     */
    hueMod?: ICTPositivePercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="sat" type="CT_Percentage">
     * ```
     */
    sat?: ICTPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="satOff" type="CT_Percentage">
     * ```
     */
    satOff?: ICTPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="satMod" type="CT_Percentage">
     * ```
     */
    satMod?: ICTPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="lum" type="CT_Percentage">
     * ```
     */
    lum?: ICTPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="lumOff" type="CT_Percentage">
     * ```
     */
    lumOff?: ICTPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="lumMod" type="CT_Percentage">
     * ```
     */
    lumMod?: ICTPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="red" type="CT_Percentage">
     * ```
     */
    red?: ICTPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="redOff" type="CT_Percentage">
     * ```
     */
    redOff?: ICTPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="redMod" type="CT_Percentage">
     * ```
     */
    redMod?: ICTPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="green" type="CT_Percentage">
     * ```
     */
    green?: ICTPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="greenOff" type="CT_Percentage">
     * ```
     */
    greenOff?: ICTPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="greenMod" type="CT_Percentage">
     * ```
     */
    greenMod?: ICTPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="blue" type="CT_Percentage">
     * ```
     */
    blue?: ICTPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="blueOff" type="CT_Percentage">
     * ```
     */
    blueOff?: ICTPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Percentage maxOccurs="1" minOccurs="1" name="blueMod" type="CT_Percentage">
     * ```
     */
    blueMod?: ICTPercentageDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GammaTransform convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="gamma" type="CT_GammaTransform">
     * ```
     */
    gamma?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_InverseGammaTransform convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="invGamma" type="CT_InverseGammaTransform">
     * ```
     */
    invGamma?: boolean | null;
}

export enum STSystemColorVal {
    PBDEFAULT = 0,
    scrollBar = 1,
    background = 2,
    activeCaption = 3,
    inactiveCaption = 4,
    menu = 5,
    window = 6,
    windowFrame = 7,
    menuText = 8,
    windowText = 9,
    captionText = 10,
    activeBorder = 11,
    inactiveBorder = 12,
    appWorkspace = 13,
    highlight = 14,
    highlightText = 15,
    btnFace = 16,
    btnShadow = 17,
    grayText = 18,
    btnText = 19,
    inactiveCaptionText = 20,
    btnHighlight = 21,
    '3dDkShadow' = 22,
    '3dLight' = 23,
    infoText = 24,
    infoBk = 25,
    hotLight = 26,
    gradientActiveCaption = 27,
    gradientInactiveCaption = 28,
    menuHighlight = 29,
    menuBar = 30,
}

export enum STSchemeColorVal {
    PBDEFAULT = 0,
    bg1 = 1,
    tx1 = 2,
    bg2 = 3,
    tx2 = 4,
    accent1 = 5,
    accent2 = 6,
    accent3 = 7,
    accent4 = 8,
    accent5 = 9,
    accent6 = 10,
    hlink = 11,
    folHlink = 12,
    phClr = 13,
    dk1 = 14,
    lt1 = 15,
    dk2 = 16,
    lt2 = 17,
}

export enum STPresetColorVal {
    PBDEFAULT = 0,
    aliceBlue = 1,
    antiqueWhite = 2,
    aqua = 3,
    aquamarine = 4,
    azure = 5,
    beige = 6,
    bisque = 7,
    black = 8,
    blanchedAlmond = 9,
    blue = 10,
    blueViolet = 11,
    brown = 12,
    burlyWood = 13,
    cadetBlue = 14,
    chartreuse = 15,
    chocolate = 16,
    coral = 17,
    cornflowerBlue = 18,
    cornsilk = 19,
    crimson = 20,
    cyan = 21,
    darkBlue = 22,
    darkCyan = 23,
    darkGoldenrod = 24,
    darkGray = 25,
    darkGrey = 26,
    darkGreen = 27,
    darkKhaki = 28,
    darkMagenta = 29,
    darkOliveGreen = 30,
    darkOrange = 31,
    darkOrchid = 32,
    darkRed = 33,
    darkSalmon = 34,
    darkSeaGreen = 35,
    darkSlateBlue = 36,
    darkSlateGray = 37,
    darkSlateGrey = 38,
    darkTurquoise = 39,
    darkViolet = 40,
    dkBlue = 41,
    dkCyan = 42,
    dkGoldenrod = 43,
    dkGray = 44,
    dkGrey = 45,
    dkGreen = 46,
    dkKhaki = 47,
    dkMagenta = 48,
    dkOliveGreen = 49,
    dkOrange = 50,
    dkOrchid = 51,
    dkRed = 52,
    dkSalmon = 53,
    dkSeaGreen = 54,
    dkSlateBlue = 55,
    dkSlateGray = 56,
    dkSlateGrey = 57,
    dkTurquoise = 58,
    dkViolet = 59,
    deepPink = 60,
    deepSkyBlue = 61,
    dimGray = 62,
    dimGrey = 63,
    dodgerBlue = 64,
    firebrick = 65,
    floralWhite = 66,
    forestGreen = 67,
    fuchsia = 68,
    gainsboro = 69,
    ghostWhite = 70,
    gold = 71,
    goldenrod = 72,
    gray = 73,
    grey = 74,
    green = 75,
    greenYellow = 76,
    honeydew = 77,
    hotPink = 78,
    indianRed = 79,
    indigo = 80,
    ivory = 81,
    khaki = 82,
    lavender = 83,
    lavenderBlush = 84,
    lawnGreen = 85,
    lemonChiffon = 86,
    lightBlue = 87,
    lightCoral = 88,
    lightCyan = 89,
    lightGoldenrodYellow = 90,
    lightGray = 91,
    lightGrey = 92,
    lightGreen = 93,
    lightPink = 94,
    lightSalmon = 95,
    lightSeaGreen = 96,
    lightSkyBlue = 97,
    lightSlateGray = 98,
    lightSlateGrey = 99,
    lightSteelBlue = 100,
    lightYellow = 101,
    ltBlue = 102,
    ltCoral = 103,
    ltCyan = 104,
    ltGoldenrodYellow = 105,
    ltGray = 106,
    ltGrey = 107,
    ltGreen = 108,
    ltPink = 109,
    ltSalmon = 110,
    ltSeaGreen = 111,
    ltSkyBlue = 112,
    ltSlateGray = 113,
    ltSlateGrey = 114,
    ltSteelBlue = 115,
    ltYellow = 116,
    lime = 117,
    limeGreen = 118,
    linen = 119,
    magenta = 120,
    maroon = 121,
    medAquamarine = 122,
    medBlue = 123,
    medOrchid = 124,
    medPurple = 125,
    medSeaGreen = 126,
    medSlateBlue = 127,
    medSpringGreen = 128,
    medTurquoise = 129,
    medVioletRed = 130,
    mediumAquamarine = 131,
    mediumBlue = 132,
    mediumOrchid = 133,
    mediumPurple = 134,
    mediumSeaGreen = 135,
    mediumSlateBlue = 136,
    mediumSpringGreen = 137,
    mediumTurquoise = 138,
    mediumVioletRed = 139,
    midnightBlue = 140,
    mintCream = 141,
    mistyRose = 142,
    moccasin = 143,
    navajoWhite = 144,
    navy = 145,
    oldLace = 146,
    olive = 147,
    oliveDrab = 148,
    orange = 149,
    orangeRed = 150,
    orchid = 151,
    paleGoldenrod = 152,
    paleGreen = 153,
    paleTurquoise = 154,
    paleVioletRed = 155,
    papayaWhip = 156,
    peachPuff = 157,
    peru = 158,
    pink = 159,
    plum = 160,
    powderBlue = 161,
    purple = 162,
    red = 163,
    rosyBrown = 164,
    royalBlue = 165,
    saddleBrown = 166,
    salmon = 167,
    sandyBrown = 168,
    seaGreen = 169,
    seaShell = 170,
    sienna = 171,
    silver = 172,
    skyBlue = 173,
    slateBlue = 174,
    slateGray = 175,
    slateGrey = 176,
    snow = 177,
    springGreen = 178,
    steelBlue = 179,
    tan = 180,
    teal = 181,
    thistle = 182,
    tomato = 183,
    turquoise = 184,
    violet = 185,
    wheat = 186,
    white = 187,
    whiteSmoke = 188,
    yellow = 189,
    yellowGreen = 190,
}

export enum STTextTabAlignType {
    PBDEFAULT = 0,
    l = 1,
    ctr = 2,
    r = 3,
    dec = 4,
}

export enum STBlendMode {
    PBDEFAULT = 0,
    over = 1,
    mult = 2,
    screen = 3,
    darken = 4,
    lighten = 5,
}

export enum STRectAlignment {
    PBDEFAULT = 0,
    tl = 1,
    t = 2,
    tr = 3,
    l = 4,
    ctr = 5,
    r = 6,
    bl = 7,
    b = 8,
    br = 9,
}

export enum STPresetShadowVal {
    PBDEFAULT = 0,
    shdw1 = 1,
    shdw2 = 2,
    shdw3 = 3,
    shdw4 = 4,
    shdw5 = 5,
    shdw6 = 6,
    shdw7 = 7,
    shdw8 = 8,
    shdw9 = 9,
    shdw10 = 10,
    shdw11 = 11,
    shdw12 = 12,
    shdw13 = 13,
    shdw14 = 14,
    shdw15 = 15,
    shdw16 = 16,
    shdw17 = 17,
    shdw18 = 18,
    shdw19 = 19,
    shdw20 = 20,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_EffectReference name="CT_EffectReference">
 * ```
 */
export interface ICTEffectReference {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="ref" type="xsd:token" use="required">
     * ```
     */
    ref?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_EffectReference name="CT_EffectReference">
 * ```
 */
export interface ICTEffectReferenceDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="ref" type="xsd:token" use="required">
     * ```
     */
    ref?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AlphaOutsetEffect name="CT_AlphaOutsetEffect">
 * ```
 */
export interface ICTAlphaOutsetEffect {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="0" name="rad" type="ST_Coordinate" use="optional">
     * ```
     */
    rad?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AlphaOutsetEffect name="CT_AlphaOutsetEffect">
 * ```
 */
export interface ICTAlphaOutsetEffectDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="0" name="rad" type="ST_Coordinate" use="optional">
     * ```
     */
    rad?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_BlendEffect name="CT_BlendEffect">
 * ```
 */
export interface ICTBlendEffect {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="cont" type="CT_EffectContainer">
     * ```
     */
    cont?: ICTEffectContainer;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_BlendMode name="blend" type="ST_BlendMode" use="required">
     * ```
     */
    blend?: STBlendMode;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_BlendEffect name="CT_BlendEffect">
 * ```
 */
export interface ICTBlendEffectDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_EffectContainer maxOccurs="1" minOccurs="1" name="cont" type="CT_EffectContainer">
     * ```
     */
    cont?: ICTEffectContainerDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_BlendMode name="blend" type="ST_BlendMode" use="required">
     * ```
     */
    blend?: STBlendMode | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_FillEffect name="CT_FillEffect">
 * ```
 */
export interface ICTFillEffect {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_FillEffect name="CT_FillEffect">
 * ```
 */
export interface ICTFillEffectDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="1" name="solidFill" type="CT_SolidColorFillProperties">
     * ```
     */
    solidFill?: ICTSolidColorFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GradientFillProperties maxOccurs="1" minOccurs="1" name="gradFill" type="CT_GradientFillProperties">
     * ```
     */
    gradFill?: ICTGradientFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PatternFillProperties maxOccurs="1" minOccurs="1" name="pattFill" type="CT_PatternFillProperties">
     * ```
     */
    pattFill?: ICTPatternFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GroupFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="grpFill" type="CT_GroupFillProperties">
     * ```
     */
    grpFill?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NoFillProperties convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="noFill" type="CT_NoFillProperties">
     * ```
     */
    noFill?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_RelativeOffsetEffect name="CT_RelativeOffsetEffect">
 * ```
 */
export interface ICTRelativeOffsetEffect {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="0" name="tx" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    tx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="0" name="ty" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    ty?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_RelativeOffsetEffect name="CT_RelativeOffsetEffect">
 * ```
 */
export interface ICTRelativeOffsetEffectDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="0" name="tx" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    tx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="0" name="ty" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    ty?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TransformEffect name="CT_TransformEffect">
 * ```
 */
export interface ICTTransformEffect {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="100" name="sx" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    sx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="100" name="sy" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    sy?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="kx" type="ST_FixedAngle" use="optional">
     * ```
     */
    kx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="ky" type="ST_FixedAngle" use="optional">
     * ```
     */
    ky?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="0" name="tx" type="ST_Coordinate" use="optional">
     * ```
     */
    tx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="0" name="ty" type="ST_Coordinate" use="optional">
     * ```
     */
    ty?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TransformEffect name="CT_TransformEffect">
 * ```
 */
export interface ICTTransformEffectDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="100" name="sx" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    sx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" default="100" name="sy" type="ST_PercentageDmlA" use="optional">
     * ```
     */
    sy?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="kx" type="ST_FixedAngle" use="optional">
     * ```
     */
    kx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="ky" type="ST_FixedAngle" use="optional">
     * ```
     */
    ky?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="0" name="tx" type="ST_Coordinate" use="optional">
     * ```
     */
    tx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="0" name="ty" type="ST_Coordinate" use="optional">
     * ```
     */
    ty?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Layout name="CT_Layout">
 * ```
 */
export interface ICTLayout {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ManualLayout maxOccurs="1" minOccurs="0" name="manualLayout" type="CT_ManualLayout">
     * ```
     */
    manualLayout?: ICTManualLayout;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Layout name="CT_Layout">
 * ```
 */
export interface ICTLayoutDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ManualLayout maxOccurs="1" minOccurs="0" name="manualLayout" type="CT_ManualLayout">
     * ```
     */
    manualLayout?: ICTManualLayoutDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DTable name="CT_DTable">
 * ```
 */
export interface ICTDTable {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showHorzBorder" type="CT_BooleanDmlC">
     * ```
     */
    showHorzBorder?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showVertBorder" type="CT_BooleanDmlC">
     * ```
     */
    showVertBorder?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showOutline" type="CT_BooleanDmlC">
     * ```
     */
    showOutline?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showKeys" type="CT_BooleanDmlC">
     * ```
     */
    showKeys?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DTable name="CT_DTable">
 * ```
 */
export interface ICTDTableDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showHorzBorder" type="CT_BooleanDmlC">
     * ```
     */
    showHorzBorder?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showVertBorder" type="CT_BooleanDmlC">
     * ```
     */
    showVertBorder?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showOutline" type="CT_BooleanDmlC">
     * ```
     */
    showOutline?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showKeys" type="CT_BooleanDmlC">
     * ```
     */
    showKeys?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Tx name="CT_Tx">
 * ```
 */
export interface ICTTx {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StrRef maxOccurs="1" minOccurs="1" name="strRef" type="CT_StrRef">
     * ```
     */
    strRef?: ICTStrRef;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="1" name="rich" type="a:CT_TextBody">
     * ```
     */
    rich?: ICTTextBody;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Tx name="CT_Tx">
 * ```
 */
export interface ICTTxDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StrRef maxOccurs="1" minOccurs="1" name="strRef" type="CT_StrRef">
     * ```
     */
    strRef?: ICTStrRefDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="1" name="rich" type="a:CT_TextBody">
     * ```
     */
    rich?: ICTTextBodyDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LegendPos name="CT_LegendPos">
 * ```
 */
export interface ICTLegendPos {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_LegendPos default="r" name="val" type="ST_LegendPos">
     * ```
     */
    val?: STLegendPos;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LegendPos name="CT_LegendPos">
 * ```
 */
export interface ICTLegendPosDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_LegendPos default="r" name="val" type="ST_LegendPos">
     * ```
     */
    val?: STLegendPos | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LegendEntry name="CT_LegendEntry">
 * ```
 */
export interface ICTLegendEntry {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC aliasName=deleteKeyword aliasName="deleteKeyword" maxOccurs="1" minOccurs="1" name="delete" type="CT_BooleanDmlC">
     * ```
     */
    deleteKeyword?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LegendEntry name="CT_LegendEntry">
 * ```
 */
export interface ICTLegendEntryDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC aliasName=deleteKeyword aliasName="deleteKeyword" maxOccurs="1" minOccurs="1" name="delete" type="CT_BooleanDmlC">
     * ```
     */
    deleteKeyword?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PivotFmt name="CT_PivotFmt">
 * ```
 */
export interface ICTPivotFmt {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_MarkerDmlC maxOccurs="1" minOccurs="0" name="marker" type="CT_MarkerDmlC">
     * ```
     */
    marker?: ICTMarkerDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbl maxOccurs="1" minOccurs="0" name="dLbl" type="CT_DLbl">
     * ```
     */
    dLbl?: ICTDLbl;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PivotFmt name="CT_PivotFmt">
 * ```
 */
export interface ICTPivotFmtDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_MarkerDmlC maxOccurs="1" minOccurs="0" name="marker" type="CT_MarkerDmlC">
     * ```
     */
    marker?: ICTMarkerDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLbl maxOccurs="1" minOccurs="0" name="dLbl" type="CT_DLbl">
     * ```
     */
    dLbl?: ICTDLblDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_RotX name="CT_RotX">
 * ```
 */
export interface ICTRotX {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="val" type="ST_RotX">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_RotX name="CT_RotX">
 * ```
 */
export interface ICTRotXDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="val" type="ST_RotX">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_HPercent name="CT_HPercent">
 * ```
 */
export interface ICTHPercent {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_HPercentUShort" default="100" name="val" type="ST_HPercent">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_HPercent name="CT_HPercent">
 * ```
 */
export interface ICTHPercentDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_HPercentUShort" default="100" name="val" type="ST_HPercent">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_RotY name="CT_RotY">
 * ```
 */
export interface ICTRotY {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="val" type="ST_RotY">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_RotY name="CT_RotY">
 * ```
 */
export interface ICTRotYDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="val" type="ST_RotY">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DepthPercent name="CT_DepthPercent">
 * ```
 */
export interface ICTDepthPercent {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_DepthPercentUShort" default="100" name="val" type="ST_DepthPercent">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DepthPercent name="CT_DepthPercent">
 * ```
 */
export interface ICTDepthPercentDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_DepthPercentUShort" default="100" name="val" type="ST_DepthPercent">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Perspective name="CT_Perspective">
 * ```
 */
export interface ICTPerspective {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="30" name="val" type="ST_Perspective">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Perspective name="CT_Perspective">
 * ```
 */
export interface ICTPerspectiveDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="30" name="val" type="ST_Perspective">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Thickness name="CT_Thickness">
 * ```
 */
export interface ICTThickness {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="td:ST_Integer" name="val" type="ST_Thickness" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Thickness name="CT_Thickness">
 * ```
 */
export interface ICTThicknessDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="td:ST_Integer" name="val" type="ST_Thickness" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PictureOptions name="CT_PictureOptions">
 * ```
 */
export interface ICTPictureOptions {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="applyToFront" type="CT_BooleanDmlC">
     * ```
     */
    applyToFront?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="applyToSides" type="CT_BooleanDmlC">
     * ```
     */
    applyToSides?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="applyToEnd" type="CT_BooleanDmlC">
     * ```
     */
    applyToEnd?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PictureFormat maxOccurs="1" minOccurs="0" name="pictureFormat" type="CT_PictureFormat">
     * ```
     */
    pictureFormat?: ICTPictureFormat;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PictureStackUnit maxOccurs="1" minOccurs="0" name="pictureStackUnit" type="CT_PictureStackUnit">
     * ```
     */
    pictureStackUnit?: ICTPictureStackUnit;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PictureOptions name="CT_PictureOptions">
 * ```
 */
export interface ICTPictureOptionsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="applyToFront" type="CT_BooleanDmlC">
     * ```
     */
    applyToFront?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="applyToSides" type="CT_BooleanDmlC">
     * ```
     */
    applyToSides?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="applyToEnd" type="CT_BooleanDmlC">
     * ```
     */
    applyToEnd?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PictureFormat maxOccurs="1" minOccurs="0" name="pictureFormat" type="CT_PictureFormat">
     * ```
     */
    pictureFormat?: ICTPictureFormatDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PictureStackUnit maxOccurs="1" minOccurs="0" name="pictureStackUnit" type="CT_PictureStackUnit">
     * ```
     */
    pictureStackUnit?: ICTPictureStackUnitDelta | null;
}

export enum STDispBlanksAs {
    PBDEFAULT = 0,
    span = 1,
    gap = 2,
    zero = 3,
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_TextRun name="EG_TextRun">
 * ```
 */
export interface IEGTextRun {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_RegularTextRun name="r" type="CT_RegularTextRun">
     * ```
     */
    r?: ICTRegularTextRun;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextLineBreak name="br" type="CT_TextLineBreak">
     * ```
     */
    br?: ICTTextLineBreak;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFieldDmlA name="fld" type="CT_TextFieldDmlA">
     * ```
     */
    fld?: ICTTextFieldDmlA;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_TextRun name="EG_TextRun">
 * ```
 */
export interface IEGTextRunDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_RegularTextRun name="r" type="CT_RegularTextRun">
     * ```
     */
    r?: ICTRegularTextRunDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextLineBreak name="br" type="CT_TextLineBreak">
     * ```
     */
    br?: ICTTextLineBreakDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextFieldDmlA name="fld" type="CT_TextFieldDmlA">
     * ```
     */
    fld?: ICTTextFieldDmlADelta | null;
}

export enum STPageSetupOrientation {
    PBDEFAULT = 0,
    default = 1,
    portrait = 2,
    landscape = 3,
}

/**
 * ```xml
 * <xsd:group name=dmlchart:EG_ExtensionDmlC name="EG_ExtensionDmlC">
 * ```
 */
export interface IEGExtensionDmlC {
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_FullRef ref=msc2012:fullRef name="fullRef" ref="c15:fullRef" type="CT_FullRef">
     * ```
     */
    fullRef?: ICTFullRef;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_FilteredAreaSer ref=msc2012:filteredAreaSeries name="filteredAreaSeries" ref="c15:filteredAreaSeries" type="CT_FilteredAreaSer">
     * ```
     */
    filteredAreaSeries?: ICTFilteredAreaSer;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_FilteredBarSer ref=msc2012:filteredBarSeries name="filteredBarSeries" ref="c15:filteredBarSeries" type="CT_FilteredBarSer">
     * ```
     */
    filteredBarSeries?: ICTFilteredBarSer;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_FilteredBubbleSer ref=msc2012:filteredBubbleSeries name="filteredBubbleSeries" ref="c15:filteredBubbleSeries" type="CT_FilteredBubbleSer">
     * ```
     */
    filteredBubbleSeries?: ICTFilteredBubbleSer;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_FilteredLineSer ref=msc2012:filteredLineSeries name="filteredLineSeries" ref="c15:filteredLineSeries" type="CT_FilteredLineSer">
     * ```
     */
    filteredLineSeries?: ICTFilteredLineSer;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_FilteredPieSer ref=msc2012:filteredPieSeries name="filteredPieSeries" ref="c15:filteredPieSeries" type="CT_FilteredPieSer">
     * ```
     */
    filteredPieSeries?: ICTFilteredPieSer;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_FilteredRadarSer ref=msc2012:filteredRadarSeries name="filteredRadarSeries" ref="c15:filteredRadarSeries" type="CT_FilteredRadarSer">
     * ```
     */
    filteredRadarSeries?: ICTFilteredRadarSer;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_FilteredScatterSer ref=msc2012:filteredScatterSeries name="filteredScatterSeries" ref="c15:filteredScatterSeries" type="CT_FilteredScatterSer">
     * ```
     */
    filteredScatterSeries?: ICTFilteredScatterSer;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_FilteredSurfaceSer ref=msc2012:filteredSurfaceSeries name="filteredSurfaceSeries" ref="c15:filteredSurfaceSeries" type="CT_FilteredSurfaceSer">
     * ```
     */
    filteredSurfaceSeries?: ICTFilteredSurfaceSer;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_SeriesDataLabelsRange ref=msc2012:datalabelsRange name="datalabelsRange" ref="c15:datalabelsRange" type="CT_SeriesDataLabelsRange">
     * ```
     */
    datalabelsRange?: ICTSeriesDataLabelsRange;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Layout ref=msc2012:layout name="layout" ref="c15:layout" type="c:CT_Layout">
     * ```
     */
    layout?: ICTLayout;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_DataLabelFieldTable ref=msc2012:dlblFieldTable name="dlblFieldTable" ref="c15:dlblFieldTable" type="CT_DataLabelFieldTable">
     * ```
     */
    dlblFieldTable?: ICTDataLabelFieldTable;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC ref=msc2012:showDataLabelsRange name="showDataLabelsRange" ref="c15:showDataLabelsRange" type="c:CT_BooleanDmlC">
     * ```
     */
    showDataLabelsRange?: ICTBooleanDmlC;
}

/**
 * ```xml
 * <xsd:group name=dmlchart:EG_ExtensionDmlC name="EG_ExtensionDmlC">
 * ```
 */
export interface IEGExtensionDmlCDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_FullRef ref=msc2012:fullRef name="fullRef" ref="c15:fullRef" type="CT_FullRef">
     * ```
     */
    fullRef?: ICTFullRefDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_FilteredAreaSer ref=msc2012:filteredAreaSeries name="filteredAreaSeries" ref="c15:filteredAreaSeries" type="CT_FilteredAreaSer">
     * ```
     */
    filteredAreaSeries?: ICTFilteredAreaSerDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_FilteredBarSer ref=msc2012:filteredBarSeries name="filteredBarSeries" ref="c15:filteredBarSeries" type="CT_FilteredBarSer">
     * ```
     */
    filteredBarSeries?: ICTFilteredBarSerDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_FilteredBubbleSer ref=msc2012:filteredBubbleSeries name="filteredBubbleSeries" ref="c15:filteredBubbleSeries" type="CT_FilteredBubbleSer">
     * ```
     */
    filteredBubbleSeries?: ICTFilteredBubbleSerDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_FilteredLineSer ref=msc2012:filteredLineSeries name="filteredLineSeries" ref="c15:filteredLineSeries" type="CT_FilteredLineSer">
     * ```
     */
    filteredLineSeries?: ICTFilteredLineSerDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_FilteredPieSer ref=msc2012:filteredPieSeries name="filteredPieSeries" ref="c15:filteredPieSeries" type="CT_FilteredPieSer">
     * ```
     */
    filteredPieSeries?: ICTFilteredPieSerDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_FilteredRadarSer ref=msc2012:filteredRadarSeries name="filteredRadarSeries" ref="c15:filteredRadarSeries" type="CT_FilteredRadarSer">
     * ```
     */
    filteredRadarSeries?: ICTFilteredRadarSerDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_FilteredScatterSer ref=msc2012:filteredScatterSeries name="filteredScatterSeries" ref="c15:filteredScatterSeries" type="CT_FilteredScatterSer">
     * ```
     */
    filteredScatterSeries?: ICTFilteredScatterSerDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_FilteredSurfaceSer ref=msc2012:filteredSurfaceSeries name="filteredSurfaceSeries" ref="c15:filteredSurfaceSeries" type="CT_FilteredSurfaceSer">
     * ```
     */
    filteredSurfaceSeries?: ICTFilteredSurfaceSerDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_SeriesDataLabelsRange ref=msc2012:datalabelsRange name="datalabelsRange" ref="c15:datalabelsRange" type="CT_SeriesDataLabelsRange">
     * ```
     */
    datalabelsRange?: ICTSeriesDataLabelsRangeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Layout ref=msc2012:layout name="layout" ref="c15:layout" type="c:CT_Layout">
     * ```
     */
    layout?: ICTLayoutDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_DataLabelFieldTable ref=msc2012:dlblFieldTable name="dlblFieldTable" ref="c15:dlblFieldTable" type="CT_DataLabelFieldTable">
     * ```
     */
    dlblFieldTable?: ICTDataLabelFieldTableDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC ref=msc2012:showDataLabelsRange name="showDataLabelsRange" ref="c15:showDataLabelsRange" type="c:CT_BooleanDmlC">
     * ```
     */
    showDataLabelsRange?: ICTBooleanDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mscs2012:CT_StyleReference name="CT_StyleReference">
 * ```
 */
export interface ICTStyleReference {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="a:EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoice;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleColor maxOccurs="1" minOccurs="0" name="styleClr" type="CT_StyleColor">
     * ```
     */
    styleClr?: ICTStyleColor;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="a:ST_StyleMatrixColumnIndex" use="required">
     * ```
     */
    idx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="mods" type="ST_StyleReferenceModifierList" use="optional">
     * ```
     */
    mods?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mscs2012:CT_StyleReference name="CT_StyleReference">
 * ```
 */
export interface ICTStyleReferenceDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="a:EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoiceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleColor maxOccurs="1" minOccurs="0" name="styleClr" type="CT_StyleColor">
     * ```
     */
    styleClr?: ICTStyleColorDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="a:ST_StyleMatrixColumnIndex" use="required">
     * ```
     */
    idx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="mods" type="ST_StyleReferenceModifierList" use="optional">
     * ```
     */
    mods?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=mscs2012:CT_FontReferenceMsCs name="CT_FontReferenceMsCs">
 * ```
 */
export interface ICTFontReferenceMsCs {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="a:EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoice;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleColor maxOccurs="1" minOccurs="0" name="styleClr" type="CT_StyleColor">
     * ```
     */
    styleClr?: ICTStyleColor;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_FontCollectionIndex name="idx" type="a:ST_FontCollectionIndex" use="required">
     * ```
     */
    idx?: STFontCollectionIndex;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="mods" type="ST_StyleReferenceModifierList" use="optional">
     * ```
     */
    mods?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mscs2012:CT_FontReferenceMsCs name="CT_FontReferenceMsCs">
 * ```
 */
export interface ICTFontReferenceMsCsDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorChoice ref=dmlmain:EG_ColorChoice maxOccurs="1" minOccurs="0" name="EG_ColorChoice" ref="a:EG_ColorChoice">
     * ref dmlmain:EG_ColorChoice
     * ```
     */
    egColorChoice?: IEGColorChoiceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_StyleColor maxOccurs="1" minOccurs="0" name="styleClr" type="CT_StyleColor">
     * ```
     */
    styleClr?: ICTStyleColorDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_FontCollectionIndex name="idx" type="a:ST_FontCollectionIndex" use="required">
     * ```
     */
    idx?: STFontCollectionIndex | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="mods" type="ST_StyleReferenceModifierList" use="optional">
     * ```
     */
    mods?: string | null;
}

export enum STMarkerStyleMsCs {
    PBDEFAULT = 0,
    circle = 1,
    dash = 2,
    diamond = 3,
    dot = 4,
    plus = 5,
    square = 6,
    star = 7,
    triangle = 8,
    x = 9,
}

export enum STGrouping {
    PBDEFAULT = 0,
    percentStacked = 1,
    standard = 2,
    stacked = 3,
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_SerTx name="CT_SerTx">
 * ```
 */
export interface ICTSerTx {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StrRef maxOccurs="1" minOccurs="1" name="strRef" type="CT_StrRef">
     * ```
     */
    strRef?: ICTStrRef;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="v" type="s:ST_Xstring">
     * ```
     */
    v?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_SerTx name="CT_SerTx">
 * ```
 */
export interface ICTSerTxDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StrRef maxOccurs="1" minOccurs="1" name="strRef" type="CT_StrRef">
     * ```
     */
    strRef?: ICTStrRefDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="v" type="s:ST_Xstring">
     * ```
     */
    v?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DPt name="CT_DPt">
 * ```
 */
export interface ICTDPt {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="invertIfNegative" type="CT_BooleanDmlC">
     * ```
     */
    invertIfNegative?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_MarkerDmlC maxOccurs="1" minOccurs="0" name="marker" type="CT_MarkerDmlC">
     * ```
     */
    marker?: ICTMarkerDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="bubble3D" type="CT_BooleanDmlC">
     * ```
     */
    bubble3D?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="explosion" type="CT_UnsignedInt">
     * ```
     */
    explosion?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PictureOptions maxOccurs="1" minOccurs="0" name="pictureOptions" type="CT_PictureOptions">
     * ```
     */
    pictureOptions?: ICTPictureOptions;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DPt name="CT_DPt">
 * ```
 */
export interface ICTDPtDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="invertIfNegative" type="CT_BooleanDmlC">
     * ```
     */
    invertIfNegative?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_MarkerDmlC maxOccurs="1" minOccurs="0" name="marker" type="CT_MarkerDmlC">
     * ```
     */
    marker?: ICTMarkerDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="bubble3D" type="CT_BooleanDmlC">
     * ```
     */
    bubble3D?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="explosion" type="CT_UnsignedInt">
     * ```
     */
    explosion?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PictureOptions maxOccurs="1" minOccurs="0" name="pictureOptions" type="CT_PictureOptions">
     * ```
     */
    pictureOptions?: ICTPictureOptionsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Trendline name="CT_Trendline">
 * ```
 */
export interface ICTTrendline {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="name" type="xsd:string">
     * ```
     */
    name?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TrendlineType maxOccurs="1" minOccurs="1" name="trendlineType" type="CT_TrendlineType">
     * ```
     */
    trendlineType?: ICTTrendlineType;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Order maxOccurs="1" minOccurs="0" name="order" type="CT_Order">
     * ```
     */
    order?: ICTOrder;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Period maxOccurs="1" minOccurs="0" name="period" type="CT_Period">
     * ```
     */
    period?: ICTPeriod;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="forward" type="CT_Double">
     * ```
     */
    forward?: ICTDouble;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="backward" type="CT_Double">
     * ```
     */
    backward?: ICTDouble;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="intercept" type="CT_Double">
     * ```
     */
    intercept?: ICTDouble;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="dispRSqr" type="CT_BooleanDmlC">
     * ```
     */
    dispRSqr?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="dispEq" type="CT_BooleanDmlC">
     * ```
     */
    dispEq?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TrendlineLbl maxOccurs="1" minOccurs="0" name="trendlineLbl" type="CT_TrendlineLbl">
     * ```
     */
    trendlineLbl?: ICTTrendlineLbl;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Trendline name="CT_Trendline">
 * ```
 */
export interface ICTTrendlineDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="name" type="xsd:string">
     * ```
     */
    name?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TrendlineType maxOccurs="1" minOccurs="1" name="trendlineType" type="CT_TrendlineType">
     * ```
     */
    trendlineType?: ICTTrendlineTypeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Order maxOccurs="1" minOccurs="0" name="order" type="CT_Order">
     * ```
     */
    order?: ICTOrderDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Period maxOccurs="1" minOccurs="0" name="period" type="CT_Period">
     * ```
     */
    period?: ICTPeriodDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="forward" type="CT_Double">
     * ```
     */
    forward?: ICTDoubleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="backward" type="CT_Double">
     * ```
     */
    backward?: ICTDoubleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="intercept" type="CT_Double">
     * ```
     */
    intercept?: ICTDoubleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="dispRSqr" type="CT_BooleanDmlC">
     * ```
     */
    dispRSqr?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="dispEq" type="CT_BooleanDmlC">
     * ```
     */
    dispEq?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TrendlineLbl maxOccurs="1" minOccurs="0" name="trendlineLbl" type="CT_TrendlineLbl">
     * ```
     */
    trendlineLbl?: ICTTrendlineLblDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ErrBars name="CT_ErrBars">
 * ```
 */
export interface ICTErrBars {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ErrDir maxOccurs="1" minOccurs="0" name="errDir" type="CT_ErrDir">
     * ```
     */
    errDir?: ICTErrDir;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ErrBarType maxOccurs="1" minOccurs="1" name="errBarType" type="CT_ErrBarType">
     * ```
     */
    errBarType?: ICTErrBarType;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ErrValType maxOccurs="1" minOccurs="1" name="errValType" type="CT_ErrValType">
     * ```
     */
    errValType?: ICTErrValType;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="noEndCap" type="CT_BooleanDmlC">
     * ```
     */
    noEndCap?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="plus" type="CT_NumDataSource">
     * ```
     */
    plus?: ICTNumDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="minus" type="CT_NumDataSource">
     * ```
     */
    minus?: ICTNumDataSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="val" type="CT_Double">
     * ```
     */
    val?: ICTDouble;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ErrBars name="CT_ErrBars">
 * ```
 */
export interface ICTErrBarsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ErrDir maxOccurs="1" minOccurs="0" name="errDir" type="CT_ErrDir">
     * ```
     */
    errDir?: ICTErrDirDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ErrBarType maxOccurs="1" minOccurs="1" name="errBarType" type="CT_ErrBarType">
     * ```
     */
    errBarType?: ICTErrBarTypeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ErrValType maxOccurs="1" minOccurs="1" name="errValType" type="CT_ErrValType">
     * ```
     */
    errValType?: ICTErrValTypeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="noEndCap" type="CT_BooleanDmlC">
     * ```
     */
    noEndCap?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="plus" type="CT_NumDataSource">
     * ```
     */
    plus?: ICTNumDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumDataSource maxOccurs="1" minOccurs="0" name="minus" type="CT_NumDataSource">
     * ```
     */
    minus?: ICTNumDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="val" type="CT_Double">
     * ```
     */
    val?: ICTDoubleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_AxDataSource name="CT_AxDataSource">
 * ```
 */
export interface ICTAxDataSource {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_MultiLvlStrRef maxOccurs="1" minOccurs="1" name="multiLvlStrRef" type="CT_MultiLvlStrRef">
     * ```
     */
    multiLvlStrRef?: ICTMultiLvlStrRef;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumRef maxOccurs="1" minOccurs="1" name="numRef" type="CT_NumRef">
     * ```
     */
    numRef?: ICTNumRef;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumData maxOccurs="1" minOccurs="1" name="numLit" type="CT_NumData">
     * ```
     */
    numLit?: ICTNumData;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StrRef maxOccurs="1" minOccurs="1" name="strRef" type="CT_StrRef">
     * ```
     */
    strRef?: ICTStrRef;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StrData maxOccurs="1" minOccurs="1" name="strLit" type="CT_StrData">
     * ```
     */
    strLit?: ICTStrData;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_AxDataSource name="CT_AxDataSource">
 * ```
 */
export interface ICTAxDataSourceDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_MultiLvlStrRef maxOccurs="1" minOccurs="1" name="multiLvlStrRef" type="CT_MultiLvlStrRef">
     * ```
     */
    multiLvlStrRef?: ICTMultiLvlStrRefDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumRef maxOccurs="1" minOccurs="1" name="numRef" type="CT_NumRef">
     * ```
     */
    numRef?: ICTNumRefDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumData maxOccurs="1" minOccurs="1" name="numLit" type="CT_NumData">
     * ```
     */
    numLit?: ICTNumDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StrRef maxOccurs="1" minOccurs="1" name="strRef" type="CT_StrRef">
     * ```
     */
    strRef?: ICTStrRefDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StrData maxOccurs="1" minOccurs="1" name="strLit" type="CT_StrData">
     * ```
     */
    strLit?: ICTStrDataDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_NumDataSource name="CT_NumDataSource">
 * ```
 */
export interface ICTNumDataSource {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumRef maxOccurs="1" minOccurs="1" name="numRef" type="CT_NumRef">
     * ```
     */
    numRef?: ICTNumRef;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumData maxOccurs="1" minOccurs="1" name="numLit" type="CT_NumData">
     * ```
     */
    numLit?: ICTNumData;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_NumDataSource name="CT_NumDataSource">
 * ```
 */
export interface ICTNumDataSourceDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumRef maxOccurs="1" minOccurs="1" name="numRef" type="CT_NumRef">
     * ```
     */
    numRef?: ICTNumRefDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumData maxOccurs="1" minOccurs="1" name="numLit" type="CT_NumData">
     * ```
     */
    numLit?: ICTNumDataDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DLbl name="CT_DLbl">
 * ```
 */
export interface ICTDLbl {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC aliasName=deleteKeyword aliasName="deleteKeyword" maxOccurs="1" minOccurs="1" name="delete" type="CT_BooleanDmlC">
     * ```
     */
    deleteKeyword?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Layout maxOccurs="1" minOccurs="0" name="layout" type="CT_Layout">
     * ```
     */
    layout?: ICTLayout;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Tx maxOccurs="1" minOccurs="0" name="tx" type="CT_Tx">
     * ```
     */
    tx?: ICTTx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumFmtDmlC maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumFmtDmlC">
     * ```
     */
    numFmt?: ICTNumFmtDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLblPos maxOccurs="1" minOccurs="0" name="dLblPos" type="CT_DLblPos">
     * ```
     */
    dLblPos?: ICTDLblPos;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showLegendKey" type="CT_BooleanDmlC">
     * ```
     */
    showLegendKey?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showVal" type="CT_BooleanDmlC">
     * ```
     */
    showVal?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showCatName" type="CT_BooleanDmlC">
     * ```
     */
    showCatName?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showSerName" type="CT_BooleanDmlC">
     * ```
     */
    showSerName?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showPercent" type="CT_BooleanDmlC">
     * ```
     */
    showPercent?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showBubbleSize" type="CT_BooleanDmlC">
     * ```
     */
    showBubbleSize?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="separator" type="xsd:string">
     * ```
     */
    separator?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DLbl name="CT_DLbl">
 * ```
 */
export interface ICTDLblDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC aliasName=deleteKeyword aliasName="deleteKeyword" maxOccurs="1" minOccurs="1" name="delete" type="CT_BooleanDmlC">
     * ```
     */
    deleteKeyword?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Layout maxOccurs="1" minOccurs="0" name="layout" type="CT_Layout">
     * ```
     */
    layout?: ICTLayoutDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Tx maxOccurs="1" minOccurs="0" name="tx" type="CT_Tx">
     * ```
     */
    tx?: ICTTxDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumFmtDmlC maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumFmtDmlC">
     * ```
     */
    numFmt?: ICTNumFmtDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DLblPos maxOccurs="1" minOccurs="0" name="dLblPos" type="CT_DLblPos">
     * ```
     */
    dLblPos?: ICTDLblPosDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showLegendKey" type="CT_BooleanDmlC">
     * ```
     */
    showLegendKey?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showVal" type="CT_BooleanDmlC">
     * ```
     */
    showVal?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showCatName" type="CT_BooleanDmlC">
     * ```
     */
    showCatName?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showSerName" type="CT_BooleanDmlC">
     * ```
     */
    showSerName?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showPercent" type="CT_BooleanDmlC">
     * ```
     */
    showPercent?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showBubbleSize" type="CT_BooleanDmlC">
     * ```
     */
    showBubbleSize?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="separator" type="xsd:string">
     * ```
     */
    separator?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_NumFmtDmlC name="CT_NumFmtDmlC">
 * ```
 */
export interface ICTNumFmtDmlC {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="formatCode" type="s:ST_Xstring" use="required">
     * ```
     */
    formatCode?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="sourceLinked" type="xsd:boolean">
     * ```
     */
    sourceLinked?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_NumFmtDmlC name="CT_NumFmtDmlC">
 * ```
 */
export interface ICTNumFmtDmlCDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="formatCode" type="s:ST_Xstring" use="required">
     * ```
     */
    formatCode?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="sourceLinked" type="xsd:boolean">
     * ```
     */
    sourceLinked?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DLblPos name="CT_DLblPos">
 * ```
 */
export interface ICTDLblPos {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_DLblPos name="val" type="ST_DLblPos" use="required">
     * ```
     */
    val?: STDLblPos;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DLblPos name="CT_DLblPos">
 * ```
 */
export interface ICTDLblPosDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_DLblPos name="val" type="ST_DLblPos" use="required">
     * ```
     */
    val?: STDLblPos | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BooleanDefaultFalse name="CT_BooleanDefaultFalse">
 * ```
 */
export interface ICTBooleanDefaultFalse {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" defaultValue="false" name="val" type="xsd:boolean" use="optional">
     * ```
     */
    val?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BooleanDefaultFalse name="CT_BooleanDefaultFalse">
 * ```
 */
export interface ICTBooleanDefaultFalseDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" defaultValue="false" name="val" type="xsd:boolean" use="optional">
     * ```
     */
    val?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_MarkerDmlC name="CT_MarkerDmlC">
 * ```
 */
export interface ICTMarkerDmlC {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_MarkerStyle maxOccurs="1" minOccurs="0" name="symbol" type="CT_MarkerStyle">
     * ```
     */
    symbol?: ICTMarkerStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_MarkerSize maxOccurs="1" minOccurs="0" name="size" type="CT_MarkerSize">
     * ```
     */
    size?: ICTMarkerSize;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_MarkerDmlC name="CT_MarkerDmlC">
 * ```
 */
export interface ICTMarkerDmlCDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_MarkerStyle maxOccurs="1" minOccurs="0" name="symbol" type="CT_MarkerStyle">
     * ```
     */
    symbol?: ICTMarkerStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_MarkerSize maxOccurs="1" minOccurs="0" name="size" type="CT_MarkerSize">
     * ```
     */
    size?: ICTMarkerSizeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_UpDownBar name="CT_UpDownBar">
 * ```
 */
export interface ICTUpDownBar {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_UpDownBar name="CT_UpDownBar">
 * ```
 */
export interface ICTUpDownBarDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
}

export enum STRadarStyle {
    PBDEFAULT = 0,
    standard = 1,
    marker = 2,
    filled = 3,
}

export enum STScatterStyle {
    PBDEFAULT = 0,
    none = 1,
    line = 2,
    lineMarker = 3,
    marker = 4,
    smooth = 5,
    smoothMarker = 6,
}

export enum STBarDir {
    PBDEFAULT = 0,
    bar = 1,
    col = 2,
}

export enum STBarGrouping {
    PBDEFAULT = 0,
    percentStacked = 1,
    clustered = 2,
    standard = 3,
    stacked = 4,
}

export enum STShape {
    PBDEFAULT = 0,
    cone = 1,
    coneToMax = 2,
    box = 3,
    cylinder = 4,
    pyramid = 5,
    pyramidToMax = 6,
}

export enum STOfPieType {
    PBDEFAULT = 0,
    pie = 1,
    bar = 2,
}

export enum STSplitType {
    PBDEFAULT = 0,
    auto = 1,
    cust = 2,
    percent = 3,
    pos = 4,
    val = 5,
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BandFmt name="CT_BandFmt">
 * ```
 */
export interface ICTBandFmt {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BandFmt name="CT_BandFmt">
 * ```
 */
export interface ICTBandFmtDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="1" name="idx" type="CT_UnsignedInt">
     * ```
     */
    idx?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
}

export enum STSizeRepresents {
    PBDEFAULT = 0,
    area = 1,
    w = 2,
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Scaling name="CT_Scaling">
 * ```
 */
export interface ICTScaling {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LogBase maxOccurs="1" minOccurs="0" name="logBase" type="CT_LogBase">
     * ```
     */
    logBase?: ICTLogBase;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Orientation maxOccurs="1" minOccurs="0" name="orientation" type="CT_Orientation">
     * ```
     */
    orientation?: ICTOrientation;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="max" type="CT_Double">
     * ```
     */
    max?: ICTDouble;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="min" type="CT_Double">
     * ```
     */
    min?: ICTDouble;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Scaling name="CT_Scaling">
 * ```
 */
export interface ICTScalingDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LogBase maxOccurs="1" minOccurs="0" name="logBase" type="CT_LogBase">
     * ```
     */
    logBase?: ICTLogBaseDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Orientation maxOccurs="1" minOccurs="0" name="orientation" type="CT_Orientation">
     * ```
     */
    orientation?: ICTOrientationDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="max" type="CT_Double">
     * ```
     */
    max?: ICTDoubleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="min" type="CT_Double">
     * ```
     */
    min?: ICTDoubleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_AxPos name="CT_AxPos">
 * ```
 */
export interface ICTAxPos {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_AxPos name="val" type="ST_AxPos" use="required">
     * ```
     */
    val?: STAxPos;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_AxPos name="CT_AxPos">
 * ```
 */
export interface ICTAxPosDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_AxPos name="val" type="ST_AxPos" use="required">
     * ```
     */
    val?: STAxPos | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_TickMark name="CT_TickMark">
 * ```
 */
export interface ICTTickMark {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_TickMark default="cross" name="val" type="ST_TickMark">
     * ```
     */
    val?: STTickMark;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_TickMark name="CT_TickMark">
 * ```
 */
export interface ICTTickMarkDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_TickMark default="cross" name="val" type="ST_TickMark">
     * ```
     */
    val?: STTickMark | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_TickLblPos name="CT_TickLblPos">
 * ```
 */
export interface ICTTickLblPos {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_TickLblPos default="nextTo" name="val" type="ST_TickLblPos">
     * ```
     */
    val?: STTickLblPos;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_TickLblPos name="CT_TickLblPos">
 * ```
 */
export interface ICTTickLblPosDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_TickLblPos default="nextTo" name="val" type="ST_TickLblPos">
     * ```
     */
    val?: STTickLblPos | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Crosses name="CT_Crosses">
 * ```
 */
export interface ICTCrosses {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_Crosses name="val" type="ST_Crosses" use="required">
     * ```
     */
    val?: STCrosses;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Crosses name="CT_Crosses">
 * ```
 */
export interface ICTCrossesDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_Crosses name="val" type="ST_Crosses" use="required">
     * ```
     */
    val?: STCrosses | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_CrossBetween name="CT_CrossBetween">
 * ```
 */
export interface ICTCrossBetween {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_CrossBetween name="val" type="ST_CrossBetween" use="required">
     * ```
     */
    val?: STCrossBetween;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_CrossBetween name="CT_CrossBetween">
 * ```
 */
export interface ICTCrossBetweenDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_CrossBetween name="val" type="ST_CrossBetween" use="required">
     * ```
     */
    val?: STCrossBetween | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_AxisUnit name="CT_AxisUnit">
 * ```
 */
export interface ICTAxisUnit {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="val" type="ST_AxisUnit" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_AxisUnit name="CT_AxisUnit">
 * ```
 */
export interface ICTAxisUnitDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="val" type="ST_AxisUnit" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DispUnits name="CT_DispUnits">
 * ```
 */
export interface ICTDispUnits {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="1" name="custUnit" type="CT_Double">
     * ```
     */
    custUnit?: ICTDouble;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BuiltInUnit maxOccurs="1" minOccurs="1" name="builtInUnit" type="CT_BuiltInUnit">
     * ```
     */
    builtInUnit?: ICTBuiltInUnit;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DispUnitsLbl maxOccurs="1" minOccurs="0" name="dispUnitsLbl" type="CT_DispUnitsLbl">
     * ```
     */
    dispUnitsLbl?: ICTDispUnitsLbl;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DispUnits name="CT_DispUnits">
 * ```
 */
export interface ICTDispUnitsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="1" name="custUnit" type="CT_Double">
     * ```
     */
    custUnit?: ICTDoubleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BuiltInUnit maxOccurs="1" minOccurs="1" name="builtInUnit" type="CT_BuiltInUnit">
     * ```
     */
    builtInUnit?: ICTBuiltInUnitDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DispUnitsLbl maxOccurs="1" minOccurs="0" name="dispUnitsLbl" type="CT_DispUnitsLbl">
     * ```
     */
    dispUnitsLbl?: ICTDispUnitsLblDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LblAlgn name="CT_LblAlgn">
 * ```
 */
export interface ICTLblAlgn {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_LblAlgn name="val" type="ST_LblAlgn" use="required">
     * ```
     */
    val?: STLblAlgn;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LblAlgn name="CT_LblAlgn">
 * ```
 */
export interface ICTLblAlgnDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_LblAlgn name="val" type="ST_LblAlgn" use="required">
     * ```
     */
    val?: STLblAlgn | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LblOffset name="CT_LblOffset">
 * ```
 */
export interface ICTLblOffset {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_LblOffsetUShort" default="100" name="val" type="ST_LblOffset">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LblOffset name="CT_LblOffset">
 * ```
 */
export interface ICTLblOffsetDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_LblOffsetUShort" default="100" name="val" type="ST_LblOffset">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Skip name="CT_Skip">
 * ```
 */
export interface ICTSkip {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="val" type="ST_Skip" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Skip name="CT_Skip">
 * ```
 */
export interface ICTSkipDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="val" type="ST_Skip" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_TimeUnit name="CT_TimeUnit">
 * ```
 */
export interface ICTTimeUnit {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_TimeUnit default="days" name="val" type="ST_TimeUnit">
     * ```
     */
    val?: STTimeUnit;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_TimeUnit name="CT_TimeUnit">
 * ```
 */
export interface ICTTimeUnitDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_TimeUnit default="days" name="val" type="ST_TimeUnit">
     * ```
     */
    val?: STTimeUnit | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GeomGuide name="CT_GeomGuide">
 * ```
 */
export interface ICTGeomGuide {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="ST_GeomGuideName" use="required">
     * ```
     */
    name?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="fmla" type="ST_GeomGuideFormula" use="required">
     * ```
     */
    fmla?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GeomGuide name="CT_GeomGuide">
 * ```
 */
export interface ICTGeomGuideDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="ST_GeomGuideName" use="required">
     * ```
     */
    name?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="fmla" type="ST_GeomGuideFormula" use="required">
     * ```
     */
    fmla?: string | null;
}

export enum STPresetCameraType {
    PBDEFAULT = 0,
    legacyObliqueTopLeft = 1,
    legacyObliqueTop = 2,
    legacyObliqueTopRight = 3,
    legacyObliqueLeft = 4,
    legacyObliqueFront = 5,
    legacyObliqueRight = 6,
    legacyObliqueBottomLeft = 7,
    legacyObliqueBottom = 8,
    legacyObliqueBottomRight = 9,
    legacyPerspectiveTopLeft = 10,
    legacyPerspectiveTop = 11,
    legacyPerspectiveTopRight = 12,
    legacyPerspectiveLeft = 13,
    legacyPerspectiveFront = 14,
    legacyPerspectiveRight = 15,
    legacyPerspectiveBottomLeft = 16,
    legacyPerspectiveBottom = 17,
    legacyPerspectiveBottomRight = 18,
    orthographicFront = 19,
    isometricTopUp = 20,
    isometricTopDown = 21,
    isometricBottomUp = 22,
    isometricBottomDown = 23,
    isometricLeftUp = 24,
    isometricLeftDown = 25,
    isometricRightUp = 26,
    isometricRightDown = 27,
    isometricOffAxis1Left = 28,
    isometricOffAxis1Right = 29,
    isometricOffAxis1Top = 30,
    isometricOffAxis2Left = 31,
    isometricOffAxis2Right = 32,
    isometricOffAxis2Top = 33,
    isometricOffAxis3Left = 34,
    isometricOffAxis3Right = 35,
    isometricOffAxis3Bottom = 36,
    isometricOffAxis4Left = 37,
    isometricOffAxis4Right = 38,
    isometricOffAxis4Bottom = 39,
    obliqueTopLeft = 40,
    obliqueTop = 41,
    obliqueTopRight = 42,
    obliqueLeft = 43,
    obliqueRight = 44,
    obliqueBottomLeft = 45,
    obliqueBottom = 46,
    obliqueBottomRight = 47,
    perspectiveFront = 48,
    perspectiveLeft = 49,
    perspectiveRight = 50,
    perspectiveAbove = 51,
    perspectiveBelow = 52,
    perspectiveAboveLeftFacing = 53,
    perspectiveAboveRightFacing = 54,
    perspectiveContrastingLeftFacing = 55,
    perspectiveContrastingRightFacing = 56,
    perspectiveHeroicLeftFacing = 57,
    perspectiveHeroicRightFacing = 58,
    perspectiveHeroicExtremeLeftFacing = 59,
    perspectiveHeroicExtremeRightFacing = 60,
    perspectiveRelaxed = 61,
    perspectiveRelaxedModerately = 62,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Point3D name="CT_Point3D">
 * ```
 */
export interface ICTPoint3D {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="x" type="ST_Coordinate" use="required">
     * ```
     */
    x?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="y" type="ST_Coordinate" use="required">
     * ```
     */
    y?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="z" type="ST_Coordinate" use="required">
     * ```
     */
    z?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Point3D name="CT_Point3D">
 * ```
 */
export interface ICTPoint3DDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="x" type="ST_Coordinate" use="required">
     * ```
     */
    x?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="y" type="ST_Coordinate" use="required">
     * ```
     */
    y?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="z" type="ST_Coordinate" use="required">
     * ```
     */
    z?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Vector3D name="CT_Vector3D">
 * ```
 */
export interface ICTVector3D {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="dx" type="ST_Coordinate" use="required">
     * ```
     */
    dx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="dy" type="ST_Coordinate" use="required">
     * ```
     */
    dy?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="dz" type="ST_Coordinate" use="required">
     * ```
     */
    dz?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Vector3D name="CT_Vector3D">
 * ```
 */
export interface ICTVector3DDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="dx" type="ST_Coordinate" use="required">
     * ```
     */
    dx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="dy" type="ST_Coordinate" use="required">
     * ```
     */
    dy?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="dz" type="ST_Coordinate" use="required">
     * ```
     */
    dz?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_ElemPropSet name="CT_ElemPropSet">
 * ```
 */
export interface ICTElemPropSet {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_LayoutVariablePropertySet maxOccurs="1" minOccurs="0" name="presLayoutVars" type="CT_LayoutVariablePropertySet">
     * ```
     */
    presLayoutVars?: ICTLayoutVariablePropertySet;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeStyle maxOccurs="1" minOccurs="0" name="style" type="a:CT_ShapeStyle">
     * ```
     */
    style?: ICTShapeStyle;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="presAssocID" type="ST_ModelId" use="optional">
     * ```
     */
    presAssocId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="presName" type="xsd:string" use="optional">
     * ```
     */
    presName?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="presStyleLbl" type="xsd:string" use="optional">
     * ```
     */
    presStyleLbl?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="presStyleIdx" type="xsd:int" use="optional">
     * ```
     */
    presStyleIdx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="presStyleCnt" type="xsd:int" use="optional">
     * ```
     */
    presStyleCnt?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="loTypeId" type="xsd:string" use="optional">
     * ```
     */
    loTypeId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="loCatId" type="xsd:string" use="optional">
     * ```
     */
    loCatId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="qsTypeId" type="xsd:string" use="optional">
     * ```
     */
    qsTypeId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="qsCatId" type="xsd:string" use="optional">
     * ```
     */
    qsCatId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="csTypeId" type="xsd:string" use="optional">
     * ```
     */
    csTypeId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="csCatId" type="xsd:string" use="optional">
     * ```
     */
    csCatId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="coherent3DOff" type="xsd:boolean" use="optional">
     * ```
     */
    coherent3DOff?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="phldrT" type="xsd:string" use="optional">
     * ```
     */
    phldrT?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="phldr" type="xsd:boolean" use="optional">
     * ```
     */
    phldr?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="custAng" type="xsd:int" use="optional">
     * ```
     */
    custAng?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="custFlipVert" type="xsd:boolean" use="optional">
     * ```
     */
    custFlipVert?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="custFlipHor" type="xsd:boolean" use="optional">
     * ```
     */
    custFlipHor?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="custSzX" type="xsd:int" use="optional">
     * ```
     */
    custSzX?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="custSzY" type="xsd:int" use="optional">
     * ```
     */
    custSzY?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="td:ST_Integer" name="custScaleX" type="ST_PrSetCustVal" use="optional">
     * ```
     */
    custScaleX?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="td:ST_Integer" name="custScaleY" type="ST_PrSetCustVal" use="optional">
     * ```
     */
    custScaleY?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="custT" type="xsd:boolean" use="optional">
     * ```
     */
    custT?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="td:ST_Integer" name="custLinFactX" type="ST_PrSetCustVal" use="optional">
     * ```
     */
    custLinFactX?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="td:ST_Integer" name="custLinFactY" type="ST_PrSetCustVal" use="optional">
     * ```
     */
    custLinFactY?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="td:ST_Integer" name="custLinFactNeighborX" type="ST_PrSetCustVal" use="optional">
     * ```
     */
    custLinFactNeighborX?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="td:ST_Integer" name="custLinFactNeighborY" type="ST_PrSetCustVal" use="optional">
     * ```
     */
    custLinFactNeighborY?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="td:ST_Integer" name="custRadScaleRad" type="ST_PrSetCustVal" use="optional">
     * ```
     */
    custRadScaleRad?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="td:ST_Integer" name="custRadScaleInc" type="ST_PrSetCustVal" use="optional">
     * ```
     */
    custRadScaleInc?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_ElemPropSet name="CT_ElemPropSet">
 * ```
 */
export interface ICTElemPropSetDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_LayoutVariablePropertySet maxOccurs="1" minOccurs="0" name="presLayoutVars" type="CT_LayoutVariablePropertySet">
     * ```
     */
    presLayoutVars?: ICTLayoutVariablePropertySetDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeStyle maxOccurs="1" minOccurs="0" name="style" type="a:CT_ShapeStyle">
     * ```
     */
    style?: ICTShapeStyleDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="presAssocID" type="ST_ModelId" use="optional">
     * ```
     */
    presAssocId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="presName" type="xsd:string" use="optional">
     * ```
     */
    presName?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="presStyleLbl" type="xsd:string" use="optional">
     * ```
     */
    presStyleLbl?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="presStyleIdx" type="xsd:int" use="optional">
     * ```
     */
    presStyleIdx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="presStyleCnt" type="xsd:int" use="optional">
     * ```
     */
    presStyleCnt?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="loTypeId" type="xsd:string" use="optional">
     * ```
     */
    loTypeId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="loCatId" type="xsd:string" use="optional">
     * ```
     */
    loCatId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="qsTypeId" type="xsd:string" use="optional">
     * ```
     */
    qsTypeId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="qsCatId" type="xsd:string" use="optional">
     * ```
     */
    qsCatId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="csTypeId" type="xsd:string" use="optional">
     * ```
     */
    csTypeId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="csCatId" type="xsd:string" use="optional">
     * ```
     */
    csCatId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="coherent3DOff" type="xsd:boolean" use="optional">
     * ```
     */
    coherent3DOff?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="phldrT" type="xsd:string" use="optional">
     * ```
     */
    phldrT?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="phldr" type="xsd:boolean" use="optional">
     * ```
     */
    phldr?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="custAng" type="xsd:int" use="optional">
     * ```
     */
    custAng?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="custFlipVert" type="xsd:boolean" use="optional">
     * ```
     */
    custFlipVert?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="custFlipHor" type="xsd:boolean" use="optional">
     * ```
     */
    custFlipHor?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="custSzX" type="xsd:int" use="optional">
     * ```
     */
    custSzX?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="custSzY" type="xsd:int" use="optional">
     * ```
     */
    custSzY?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="td:ST_Integer" name="custScaleX" type="ST_PrSetCustVal" use="optional">
     * ```
     */
    custScaleX?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="td:ST_Integer" name="custScaleY" type="ST_PrSetCustVal" use="optional">
     * ```
     */
    custScaleY?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="custT" type="xsd:boolean" use="optional">
     * ```
     */
    custT?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="td:ST_Integer" name="custLinFactX" type="ST_PrSetCustVal" use="optional">
     * ```
     */
    custLinFactX?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="td:ST_Integer" name="custLinFactY" type="ST_PrSetCustVal" use="optional">
     * ```
     */
    custLinFactY?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="td:ST_Integer" name="custLinFactNeighborX" type="ST_PrSetCustVal" use="optional">
     * ```
     */
    custLinFactNeighborX?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="td:ST_Integer" name="custLinFactNeighborY" type="ST_PrSetCustVal" use="optional">
     * ```
     */
    custLinFactNeighborY?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="td:ST_Integer" name="custRadScaleRad" type="ST_PrSetCustVal" use="optional">
     * ```
     */
    custRadScaleRad?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="td:ST_Integer" name="custRadScaleInc" type="ST_PrSetCustVal" use="optional">
     * ```
     */
    custRadScaleInc?: number | null;
}

export enum STPtType {
    PBDEFAULT = 0,
    node = 1,
    asst = 2,
    doc = 3,
    pres = 4,
    parTrans = 5,
    sibTrans = 6,
}

export enum STCxnType {
    PBDEFAULT = 0,
    parOf = 1,
    presOf = 2,
    presParOf = 3,
    unknownRelationship = 4,
}

/**
 * ```xml
 * <xsd:complexType name=msdsp2008:CT_ShapeMsDsp name="CT_ShapeMsDsp">
 * ```
 */
export interface ICTShapeMsDsp {
    /**
     * ```xml
     * <xsd:element typeRedirected=msdsp2008:CT_ShapeNonVisualMsDsp maxOccurs="1" minOccurs="1" name="nvSpPr" type="CT_ShapeNonVisualMsDsp">
     * ```
     */
    nvSpPr?: ICTShapeNonVisualMsDsp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="1" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeStyle maxOccurs="1" minOccurs="0" name="style" type="a:CT_ShapeStyle">
     * ```
     */
    style?: ICTShapeStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txBody" type="a:CT_TextBody">
     * ```
     */
    txBody?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Transform2D maxOccurs="1" minOccurs="0" name="txXfrm" type="a:CT_Transform2D">
     * ```
     */
    txXfrm?: ICTTransform2D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="modelId" type="dgm:ST_ModelId" use="required">
     * ```
     */
    modelId?: string;
}

/**
 * ```xml
 * <xsd:complexType name=msdsp2008:CT_ShapeMsDsp name="CT_ShapeMsDsp">
 * ```
 */
export interface ICTShapeMsDspDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msdsp2008:CT_ShapeNonVisualMsDsp maxOccurs="1" minOccurs="1" name="nvSpPr" type="CT_ShapeNonVisualMsDsp">
     * ```
     */
    nvSpPr?: ICTShapeNonVisualMsDspDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="1" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeStyle maxOccurs="1" minOccurs="0" name="style" type="a:CT_ShapeStyle">
     * ```
     */
    style?: ICTShapeStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txBody" type="a:CT_TextBody">
     * ```
     */
    txBody?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Transform2D maxOccurs="1" minOccurs="0" name="txXfrm" type="a:CT_Transform2D">
     * ```
     */
    txXfrm?: ICTTransform2DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="modelId" type="dgm:ST_ModelId" use="required">
     * ```
     */
    modelId?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_ParameterDmlDgm name="CT_ParameterDmlDgm">
 * ```
 */
export interface ICTParameterDmlDgm {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ParameterId name="type" type="ST_ParameterId" use="required">
     * ```
     */
    type?: STParameterId;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_String" name="val" type="ST_ParameterVal" use="required">
     * ```
     */
    val?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_ParameterDmlDgm name="CT_ParameterDmlDgm">
 * ```
 */
export interface ICTParameterDmlDgmDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ParameterId name="type" type="ST_ParameterId" use="required">
     * ```
     */
    type?: STParameterId | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_String" name="val" type="ST_ParameterVal" use="required">
     * ```
     */
    val?: string | null;
}

export enum STAlgorithmType {
    PBDEFAULT = 0,
    composite = 1,
    conn = 2,
    cycle = 3,
    hierChild = 4,
    hierRoot = 5,
    pyra = 6,
    lin = 7,
    sp = 8,
    tx = 9,
    snake = 10,
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_AdjLst name="CT_AdjLst">
 * ```
 */
export interface ICTAdjLst {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Adj maxOccurs="unbounded" minOccurs="0" name="adj" type="CT_Adj">
     * ```
     */
    adj?: ICTAdj[];
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_AdjLst name="CT_AdjLst">
 * ```
 */
export interface ICTAdjLstDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Adj maxOccurs="unbounded" minOccurs="0" name="adj" type="CT_Adj">
     * ```
     */
    adj?: ICTAdjDelta[];
    adjOp?: IArrayOp;
}

export enum STLayoutShapeType {
    PBDEFAULT = 0,
    line = 1,
    lineInv = 2,
    triangle = 3,
    rtTriangle = 4,
    rect = 5,
    diamond = 6,
    parallelogram = 7,
    trapezoid = 8,
    nonIsoscelesTrapezoid = 9,
    pentagon = 10,
    hexagon = 11,
    heptagon = 12,
    octagon = 13,
    decagon = 14,
    dodecagon = 15,
    star4 = 16,
    star5 = 17,
    star6 = 18,
    star7 = 19,
    star8 = 20,
    star10 = 21,
    star12 = 22,
    star16 = 23,
    star24 = 24,
    star32 = 25,
    roundRect = 26,
    round1Rect = 27,
    round2SameRect = 28,
    round2DiagRect = 29,
    snipRoundRect = 30,
    snip1Rect = 31,
    snip2SameRect = 32,
    snip2DiagRect = 33,
    plaque = 34,
    ellipse = 35,
    teardrop = 36,
    homePlate = 37,
    chevron = 38,
    pieWedge = 39,
    pie = 40,
    blockArc = 41,
    donut = 42,
    noSmoking = 43,
    rightArrow = 44,
    leftArrow = 45,
    upArrow = 46,
    downArrow = 47,
    stripedRightArrow = 48,
    notchedRightArrow = 49,
    bentUpArrow = 50,
    leftRightArrow = 51,
    upDownArrow = 52,
    leftUpArrow = 53,
    leftRightUpArrow = 54,
    quadArrow = 55,
    leftArrowCallout = 56,
    rightArrowCallout = 57,
    upArrowCallout = 58,
    downArrowCallout = 59,
    leftRightArrowCallout = 60,
    upDownArrowCallout = 61,
    quadArrowCallout = 62,
    bentArrow = 63,
    uturnArrow = 64,
    circularArrow = 65,
    leftCircularArrow = 66,
    leftRightCircularArrow = 67,
    curvedRightArrow = 68,
    curvedLeftArrow = 69,
    curvedUpArrow = 70,
    curvedDownArrow = 71,
    swooshArrow = 72,
    cube = 73,
    can = 74,
    lightningBolt = 75,
    heart = 76,
    sun = 77,
    moon = 78,
    smileyFace = 79,
    irregularSeal1 = 80,
    irregularSeal2 = 81,
    foldedCorner = 82,
    bevel = 83,
    frame = 84,
    halfFrame = 85,
    corner = 86,
    diagStripe = 87,
    chord = 88,
    arc = 89,
    leftBracket = 90,
    rightBracket = 91,
    leftBrace = 92,
    rightBrace = 93,
    bracketPair = 94,
    bracePair = 95,
    straightConnector1 = 96,
    bentConnector2 = 97,
    bentConnector3 = 98,
    bentConnector4 = 99,
    bentConnector5 = 100,
    curvedConnector2 = 101,
    curvedConnector3 = 102,
    curvedConnector4 = 103,
    curvedConnector5 = 104,
    callout1 = 105,
    callout2 = 106,
    callout3 = 107,
    accentCallout1 = 108,
    accentCallout2 = 109,
    accentCallout3 = 110,
    borderCallout1 = 111,
    borderCallout2 = 112,
    borderCallout3 = 113,
    accentBorderCallout1 = 114,
    accentBorderCallout2 = 115,
    accentBorderCallout3 = 116,
    wedgeRectCallout = 117,
    wedgeRoundRectCallout = 118,
    wedgeEllipseCallout = 119,
    cloudCallout = 120,
    cloud = 121,
    ribbon = 122,
    ribbon2 = 123,
    ellipseRibbon = 124,
    ellipseRibbon2 = 125,
    leftRightRibbon = 126,
    verticalScroll = 127,
    horizontalScroll = 128,
    wave = 129,
    doubleWave = 130,
    plus = 131,
    flowChartProcess = 132,
    flowChartDecision = 133,
    flowChartInputOutput = 134,
    flowChartPredefinedProcess = 135,
    flowChartInternalStorage = 136,
    flowChartDocument = 137,
    flowChartMultidocument = 138,
    flowChartTerminator = 139,
    flowChartPreparation = 140,
    flowChartManualInput = 141,
    flowChartManualOperation = 142,
    flowChartConnector = 143,
    flowChartPunchedCard = 144,
    flowChartPunchedTape = 145,
    flowChartSummingJunction = 146,
    flowChartOr = 147,
    flowChartCollate = 148,
    flowChartSort = 149,
    flowChartExtract = 150,
    flowChartMerge = 151,
    flowChartOfflineStorage = 152,
    flowChartOnlineStorage = 153,
    flowChartMagneticTape = 154,
    flowChartMagneticDisk = 155,
    flowChartMagneticDrum = 156,
    flowChartDisplay = 157,
    flowChartDelay = 158,
    flowChartAlternateProcess = 159,
    flowChartOffpageConnector = 160,
    actionButtonBlank = 161,
    actionButtonHome = 162,
    actionButtonHelp = 163,
    actionButtonInformation = 164,
    actionButtonForwardNext = 165,
    actionButtonBackPrevious = 166,
    actionButtonEnd = 167,
    actionButtonBeginning = 168,
    actionButtonReturn = 169,
    actionButtonDocument = 170,
    actionButtonSound = 171,
    actionButtonMovie = 172,
    gear6 = 173,
    gear9 = 174,
    funnel = 175,
    mathPlus = 176,
    mathMinus = 177,
    mathMultiply = 178,
    mathDivide = 179,
    mathEqual = 180,
    mathNotEqual = 181,
    cornerTabs = 182,
    squareTabs = 183,
    plaqueTabs = 184,
    chartX = 185,
    chartStar = 186,
    chartPlus = 187,
    none = 188,
    conn = 189,
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Constraint name="CT_Constraint">
 * ```
 */
export interface ICTConstraint {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ConstraintType name="type" type="ST_ConstraintType" use="required">
     * ```
     */
    type?: STConstraintType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ConstraintRelationship aliasName=forKeyword aliasName="forKeyword" default="self" name="for" type="ST_ConstraintRelationship" use="optional">
     * ```
     */
    forKeyword?: STConstraintRelationship;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="forName" type="xsd:string" use="optional">
     * ```
     */
    forName?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ElementType default="all" name="ptType" type="ST_ElementType" use="optional">
     * ```
     */
    ptType?: STElementType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ConstraintType default="none" name="refType" type="ST_ConstraintType" use="optional">
     * ```
     */
    refType?: STConstraintType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ConstraintRelationship default="self" name="refFor" type="ST_ConstraintRelationship" use="optional">
     * ```
     */
    refFor?: STConstraintRelationship;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="refForName" type="xsd:string" use="optional">
     * ```
     */
    refForName?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ElementType default="all" name="refPtType" type="ST_ElementType" use="optional">
     * ```
     */
    refPtType?: STElementType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_BoolOperator default="none" name="op" type="ST_BoolOperator" use="optional">
     * ```
     */
    op?: STBoolOperator;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double default="0" name="val" type="xsd:double" use="optional">
     * ```
     */
    val?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double default="1" name="fact" type="xsd:double" use="optional">
     * ```
     */
    fact?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Constraint name="CT_Constraint">
 * ```
 */
export interface ICTConstraintDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ConstraintType name="type" type="ST_ConstraintType" use="required">
     * ```
     */
    type?: STConstraintType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ConstraintRelationship aliasName=forKeyword aliasName="forKeyword" default="self" name="for" type="ST_ConstraintRelationship" use="optional">
     * ```
     */
    forKeyword?: STConstraintRelationship | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="forName" type="xsd:string" use="optional">
     * ```
     */
    forName?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ElementType default="all" name="ptType" type="ST_ElementType" use="optional">
     * ```
     */
    ptType?: STElementType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ConstraintType default="none" name="refType" type="ST_ConstraintType" use="optional">
     * ```
     */
    refType?: STConstraintType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ConstraintRelationship default="self" name="refFor" type="ST_ConstraintRelationship" use="optional">
     * ```
     */
    refFor?: STConstraintRelationship | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="refForName" type="xsd:string" use="optional">
     * ```
     */
    refForName?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ElementType default="all" name="refPtType" type="ST_ElementType" use="optional">
     * ```
     */
    refPtType?: STElementType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_BoolOperator default="none" name="op" type="ST_BoolOperator" use="optional">
     * ```
     */
    op?: STBoolOperator | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double default="0" name="val" type="xsd:double" use="optional">
     * ```
     */
    val?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double default="1" name="fact" type="xsd:double" use="optional">
     * ```
     */
    fact?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_NumericRule name="CT_NumericRule">
 * ```
 */
export interface ICTNumericRule {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ConstraintType name="type" type="ST_ConstraintType" use="required">
     * ```
     */
    type?: STConstraintType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ConstraintRelationship aliasName=forKeyword aliasName="forKeyword" default="self" name="for" type="ST_ConstraintRelationship" use="optional">
     * ```
     */
    forKeyword?: STConstraintRelationship;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="forName" type="xsd:string" use="optional">
     * ```
     */
    forName?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ElementType default="all" name="ptType" type="ST_ElementType" use="optional">
     * ```
     */
    ptType?: STElementType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double default="NaN" name="val" type="xsd:double" use="optional">
     * ```
     */
    val?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double default="NaN" name="fact" type="xsd:double" use="optional">
     * ```
     */
    fact?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double default="NaN" name="max" type="xsd:double" use="optional">
     * ```
     */
    max?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_NumericRule name="CT_NumericRule">
 * ```
 */
export interface ICTNumericRuleDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ConstraintType name="type" type="ST_ConstraintType" use="required">
     * ```
     */
    type?: STConstraintType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ConstraintRelationship aliasName=forKeyword aliasName="forKeyword" default="self" name="for" type="ST_ConstraintRelationship" use="optional">
     * ```
     */
    forKeyword?: STConstraintRelationship | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="forName" type="xsd:string" use="optional">
     * ```
     */
    forName?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ElementType default="all" name="ptType" type="ST_ElementType" use="optional">
     * ```
     */
    ptType?: STElementType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double default="NaN" name="val" type="xsd:double" use="optional">
     * ```
     */
    val?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double default="NaN" name="fact" type="xsd:double" use="optional">
     * ```
     */
    fact?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double default="NaN" name="max" type="xsd:double" use="optional">
     * ```
     */
    max?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_OrgChart name="CT_OrgChart">
 * ```
 */
export interface ICTOrgChart {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="val" type="xsd:boolean" use="optional">
     * ```
     */
    val?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_OrgChart name="CT_OrgChart">
 * ```
 */
export interface ICTOrgChartDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="val" type="xsd:boolean" use="optional">
     * ```
     */
    val?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_ChildMax name="CT_ChildMax">
 * ```
 */
export interface ICTChildMax {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="-1" name="val" type="ST_NodeCount" use="optional">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_ChildMax name="CT_ChildMax">
 * ```
 */
export interface ICTChildMaxDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="-1" name="val" type="ST_NodeCount" use="optional">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_ChildPref name="CT_ChildPref">
 * ```
 */
export interface ICTChildPref {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="-1" name="val" type="ST_NodeCount" use="optional">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_ChildPref name="CT_ChildPref">
 * ```
 */
export interface ICTChildPrefDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="-1" name="val" type="ST_NodeCount" use="optional">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_BulletEnabled name="CT_BulletEnabled">
 * ```
 */
export interface ICTBulletEnabled {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="val" type="xsd:boolean" use="optional">
     * ```
     */
    val?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_BulletEnabled name="CT_BulletEnabled">
 * ```
 */
export interface ICTBulletEnabledDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="val" type="xsd:boolean" use="optional">
     * ```
     */
    val?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Direction name="CT_Direction">
 * ```
 */
export interface ICTDirection {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_DirectionDmlDgm default="norm" name="val" type="ST_DirectionDmlDgm" use="optional">
     * ```
     */
    val?: STDirectionDmlDgm;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Direction name="CT_Direction">
 * ```
 */
export interface ICTDirectionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_DirectionDmlDgm default="norm" name="val" type="ST_DirectionDmlDgm" use="optional">
     * ```
     */
    val?: STDirectionDmlDgm | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_HierBranchStyle name="CT_HierBranchStyle">
 * ```
 */
export interface ICTHierBranchStyle {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_HierBranchStyle default="std" name="val" type="ST_HierBranchStyle" use="optional">
     * ```
     */
    val?: STHierBranchStyle;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_HierBranchStyle name="CT_HierBranchStyle">
 * ```
 */
export interface ICTHierBranchStyleDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_HierBranchStyle default="std" name="val" type="ST_HierBranchStyle" use="optional">
     * ```
     */
    val?: STHierBranchStyle | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_AnimOne name="CT_AnimOne">
 * ```
 */
export interface ICTAnimOne {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_AnimOneStr default="one" name="val" type="ST_AnimOneStr" use="optional">
     * ```
     */
    val?: STAnimOneStr;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_AnimOne name="CT_AnimOne">
 * ```
 */
export interface ICTAnimOneDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_AnimOneStr default="one" name="val" type="ST_AnimOneStr" use="optional">
     * ```
     */
    val?: STAnimOneStr | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_AnimLvl name="CT_AnimLvl">
 * ```
 */
export interface ICTAnimLvl {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_AnimLvlStr default="none" name="val" type="ST_AnimLvlStr" use="optional">
     * ```
     */
    val?: STAnimLvlStr;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_AnimLvl name="CT_AnimLvl">
 * ```
 */
export interface ICTAnimLvlDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_AnimLvlStr default="none" name="val" type="ST_AnimLvlStr" use="optional">
     * ```
     */
    val?: STAnimLvlStr | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_ResizeHandles name="CT_ResizeHandles">
 * ```
 */
export interface ICTResizeHandles {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ResizeHandlesStr default="rel" name="val" type="ST_ResizeHandlesStr" use="optional">
     * ```
     */
    val?: STResizeHandlesStr;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_ResizeHandles name="CT_ResizeHandles">
 * ```
 */
export interface ICTResizeHandlesDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_ResizeHandlesStr default="rel" name="val" type="ST_ResizeHandlesStr" use="optional">
     * ```
     */
    val?: STResizeHandlesStr | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_When name="CT_When">
 * ```
 */
export interface ICTWhen {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Algorithm maxOccurs="1" minOccurs="0" name="alg" type="CT_Algorithm">
     * ```
     */
    alg?: ICTAlgorithm[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ShapeDmlDgm maxOccurs="1" minOccurs="0" name="shape" type="CT_ShapeDmlDgm">
     * ```
     */
    shape?: ICTShapeDmlDgm[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_PresentationOf maxOccurs="1" minOccurs="0" name="presOf" type="CT_PresentationOf">
     * ```
     */
    presOf?: ICTPresentationOf[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Constraints maxOccurs="1" minOccurs="0" name="constrLst" type="CT_Constraints">
     * ```
     */
    constrLst?: ICTConstraints[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_RulesDmlDgm maxOccurs="1" minOccurs="0" name="ruleLst" type="CT_RulesDmlDgm">
     * ```
     */
    ruleLst?: ICTRulesDmlDgm[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ForEach name="forEach" type="CT_ForEach">
     * ```
     */
    forEach?: ICTForEach[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_LayoutNode name="layoutNode" type="CT_LayoutNode">
     * ```
     */
    layoutNode?: ICTLayoutNode[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Choose name="choose" type="CT_Choose">
     * ```
     */
    choose?: ICTChoose[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="none" name="axis" type="ST_AxisTypes" use="optional">
     * ```
     */
    axis?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="all" name="ptType" type="ST_ElementTypes" use="optional">
     * ```
     */
    ptType?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="true" name="hideLastTrans" type="ST_Booleans" use="optional">
     * ```
     */
    hideLastTrans?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="1" name="st" type="ST_Ints" use="optional">
     * ```
     */
    st?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="0" name="cnt" type="ST_UnsignedInts" use="optional">
     * ```
     */
    cnt?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="1" name="step" type="ST_Ints" use="optional">
     * ```
     */
    step?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_FunctionType name="func" type="ST_FunctionType" use="required">
     * ```
     */
    func?: STFunctionType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_FunctionOperator name="op" type="ST_FunctionOperator" use="required">
     * ```
     */
    op?: STFunctionOperator;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_String" name="val" type="ST_FunctionValue" use="required">
     * ```
     */
    val?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_FunctionArgument default="none" name="arg" type="ST_FunctionArgument" use="optional">
     * ```
     */
    arg?: STFunctionArgument;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_When name="CT_When">
 * ```
 */
export interface ICTWhenDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Algorithm maxOccurs="1" minOccurs="0" name="alg" type="CT_Algorithm">
     * ```
     */
    alg?: ICTAlgorithmDelta[];
    algOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ShapeDmlDgm maxOccurs="1" minOccurs="0" name="shape" type="CT_ShapeDmlDgm">
     * ```
     */
    shape?: ICTShapeDmlDgmDelta[];
    shapeOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_PresentationOf maxOccurs="1" minOccurs="0" name="presOf" type="CT_PresentationOf">
     * ```
     */
    presOf?: ICTPresentationOfDelta[];
    presOfOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Constraints maxOccurs="1" minOccurs="0" name="constrLst" type="CT_Constraints">
     * ```
     */
    constrLst?: ICTConstraintsDelta[];
    constrLstOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_RulesDmlDgm maxOccurs="1" minOccurs="0" name="ruleLst" type="CT_RulesDmlDgm">
     * ```
     */
    ruleLst?: ICTRulesDmlDgmDelta[];
    ruleLstOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ForEach name="forEach" type="CT_ForEach">
     * ```
     */
    forEach?: ICTForEachDelta[];
    forEachOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_LayoutNode name="layoutNode" type="CT_LayoutNode">
     * ```
     */
    layoutNode?: ICTLayoutNodeDelta[];
    layoutNodeOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Choose name="choose" type="CT_Choose">
     * ```
     */
    choose?: ICTChooseDelta[];
    chooseOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta[];
    extLstOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="none" name="axis" type="ST_AxisTypes" use="optional">
     * ```
     */
    axis?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="all" name="ptType" type="ST_ElementTypes" use="optional">
     * ```
     */
    ptType?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="true" name="hideLastTrans" type="ST_Booleans" use="optional">
     * ```
     */
    hideLastTrans?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="1" name="st" type="ST_Ints" use="optional">
     * ```
     */
    st?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="0" name="cnt" type="ST_UnsignedInts" use="optional">
     * ```
     */
    cnt?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="1" name="step" type="ST_Ints" use="optional">
     * ```
     */
    step?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_FunctionType name="func" type="ST_FunctionType" use="required">
     * ```
     */
    func?: STFunctionType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_FunctionOperator name="op" type="ST_FunctionOperator" use="required">
     * ```
     */
    op?: STFunctionOperator | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_String" name="val" type="ST_FunctionValue" use="required">
     * ```
     */
    val?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmldiagram:ST_FunctionArgument default="none" name="arg" type="ST_FunctionArgument" use="optional">
     * ```
     */
    arg?: STFunctionArgument | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Otherwise name="CT_Otherwise">
 * ```
 */
export interface ICTOtherwise {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Algorithm maxOccurs="1" minOccurs="0" name="alg" type="CT_Algorithm">
     * ```
     */
    alg?: ICTAlgorithm[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ShapeDmlDgm maxOccurs="1" minOccurs="0" name="shape" type="CT_ShapeDmlDgm">
     * ```
     */
    shape?: ICTShapeDmlDgm[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_PresentationOf maxOccurs="1" minOccurs="0" name="presOf" type="CT_PresentationOf">
     * ```
     */
    presOf?: ICTPresentationOf[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Constraints maxOccurs="1" minOccurs="0" name="constrLst" type="CT_Constraints">
     * ```
     */
    constrLst?: ICTConstraints[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_RulesDmlDgm maxOccurs="1" minOccurs="0" name="ruleLst" type="CT_RulesDmlDgm">
     * ```
     */
    ruleLst?: ICTRulesDmlDgm[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ForEach name="forEach" type="CT_ForEach">
     * ```
     */
    forEach?: ICTForEach[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_LayoutNode name="layoutNode" type="CT_LayoutNode">
     * ```
     */
    layoutNode?: ICTLayoutNode[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Choose name="choose" type="CT_Choose">
     * ```
     */
    choose?: ICTChoose[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Otherwise name="CT_Otherwise">
 * ```
 */
export interface ICTOtherwiseDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Algorithm maxOccurs="1" minOccurs="0" name="alg" type="CT_Algorithm">
     * ```
     */
    alg?: ICTAlgorithmDelta[];
    algOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ShapeDmlDgm maxOccurs="1" minOccurs="0" name="shape" type="CT_ShapeDmlDgm">
     * ```
     */
    shape?: ICTShapeDmlDgmDelta[];
    shapeOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_PresentationOf maxOccurs="1" minOccurs="0" name="presOf" type="CT_PresentationOf">
     * ```
     */
    presOf?: ICTPresentationOfDelta[];
    presOfOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Constraints maxOccurs="1" minOccurs="0" name="constrLst" type="CT_Constraints">
     * ```
     */
    constrLst?: ICTConstraintsDelta[];
    constrLstOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_RulesDmlDgm maxOccurs="1" minOccurs="0" name="ruleLst" type="CT_RulesDmlDgm">
     * ```
     */
    ruleLst?: ICTRulesDmlDgmDelta[];
    ruleLstOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ForEach name="forEach" type="CT_ForEach">
     * ```
     */
    forEach?: ICTForEachDelta[];
    forEachOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_LayoutNode name="layoutNode" type="CT_LayoutNode">
     * ```
     */
    layoutNode?: ICTLayoutNodeDelta[];
    layoutNodeOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_Choose name="choose" type="CT_Choose">
     * ```
     */
    choose?: ICTChooseDelta[];
    chooseOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta[];
    extLstOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string | null;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_Text3D name="EG_Text3D">
 * ```
 */
export interface IEGText3D {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Shape3D maxOccurs="1" minOccurs="1" name="sp3d" type="CT_Shape3D">
     * ```
     */
    sp3d?: ICTShape3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FlatText maxOccurs="1" minOccurs="1" name="flatTx" type="CT_FlatText">
     * ```
     */
    flatTx?: ICTFlatText;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_Text3D name="EG_Text3D">
 * ```
 */
export interface IEGText3DDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Shape3D maxOccurs="1" minOccurs="1" name="sp3d" type="CT_Shape3D">
     * ```
     */
    sp3d?: ICTShape3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_FlatText maxOccurs="1" minOccurs="1" name="flatTx" type="CT_FlatText">
     * ```
     */
    flatTx?: ICTFlatTextDelta | null;
}

export enum STClrAppMethod {
    PBDEFAULT = 0,
    span = 1,
    cycle = 2,
    repeat = 3,
}

export enum STHueDir {
    PBDEFAULT = 0,
    cw = 1,
    ccw = 2,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_NonVisualDrawingShapeProps name="CT_NonVisualDrawingShapeProps">
 * ```
 */
export interface ICTNonVisualDrawingShapeProps {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeLocking maxOccurs="1" minOccurs="0" name="spLocks" type="CT_ShapeLocking">
     * ```
     */
    spLocks?: ICTShapeLocking;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="txBox" type="xsd:boolean" use="optional">
     * ```
     */
    txBox?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_NonVisualDrawingShapeProps name="CT_NonVisualDrawingShapeProps">
 * ```
 */
export interface ICTNonVisualDrawingShapePropsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeLocking maxOccurs="1" minOccurs="0" name="spLocks" type="CT_ShapeLocking">
     * ```
     */
    spLocks?: ICTShapeLockingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="txBox" type="xsd:boolean" use="optional">
     * ```
     */
    txBox?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_NonVisualGraphicFrameProperties name="CT_NonVisualGraphicFrameProperties">
 * ```
 */
export interface ICTNonVisualGraphicFrameProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GraphicalObjectFrameLocking maxOccurs="1" minOccurs="0" name="graphicFrameLocks" type="CT_GraphicalObjectFrameLocking">
     * ```
     */
    graphicFrameLocks?: ICTGraphicalObjectFrameLocking;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_NonVisualGraphicFrameProperties name="CT_NonVisualGraphicFrameProperties">
 * ```
 */
export interface ICTNonVisualGraphicFramePropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_GraphicalObjectFrameLocking maxOccurs="1" minOccurs="0" name="graphicFrameLocks" type="CT_GraphicalObjectFrameLocking">
     * ```
     */
    graphicFrameLocks?: ICTGraphicalObjectFrameLockingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_NonVisualConnectorProperties name="CT_NonVisualConnectorProperties">
 * ```
 */
export interface ICTNonVisualConnectorProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ConnectorLocking maxOccurs="1" minOccurs="0" name="cxnSpLocks" type="CT_ConnectorLocking">
     * ```
     */
    cxnSpLocks?: ICTConnectorLocking;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ConnectionDmlA maxOccurs="1" minOccurs="0" name="stCxn" type="CT_ConnectionDmlA">
     * ```
     */
    stCxn?: ICTConnectionDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ConnectionDmlA maxOccurs="1" minOccurs="0" name="endCxn" type="CT_ConnectionDmlA">
     * ```
     */
    endCxn?: ICTConnectionDmlA;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_NonVisualConnectorProperties name="CT_NonVisualConnectorProperties">
 * ```
 */
export interface ICTNonVisualConnectorPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ConnectorLocking maxOccurs="1" minOccurs="0" name="cxnSpLocks" type="CT_ConnectorLocking">
     * ```
     */
    cxnSpLocks?: ICTConnectorLockingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ConnectionDmlA maxOccurs="1" minOccurs="0" name="stCxn" type="CT_ConnectionDmlA">
     * ```
     */
    stCxn?: ICTConnectionDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ConnectionDmlA maxOccurs="1" minOccurs="0" name="endCxn" type="CT_ConnectionDmlA">
     * ```
     */
    endCxn?: ICTConnectionDmlADelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_NonVisualPictureProperties name="CT_NonVisualPictureProperties">
 * ```
 */
export interface ICTNonVisualPictureProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PictureLocking maxOccurs="1" minOccurs="0" name="picLocks" type="CT_PictureLocking">
     * ```
     */
    picLocks?: ICTPictureLocking;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="preferRelativeResize" type="xsd:boolean" use="optional">
     * ```
     */
    preferRelativeResize?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_NonVisualPictureProperties name="CT_NonVisualPictureProperties">
 * ```
 */
export interface ICTNonVisualPicturePropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PictureLocking maxOccurs="1" minOccurs="0" name="picLocks" type="CT_PictureLocking">
     * ```
     */
    picLocks?: ICTPictureLockingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="preferRelativeResize" type="xsd:boolean" use="optional">
     * ```
     */
    preferRelativeResize?: boolean | null;
}

export enum STAnimationDgmBuildType {
    PBDEFAULT = 0,
    allAtOnce = 1,
    one = 2,
    lvlOne = 3,
    lvlAtOnce = 4,
}

export enum STAnimationChartBuildType {
    PBDEFAULT = 0,
    allAtOnce = 1,
    series = 2,
    category = 3,
    seriesEl = 4,
    categoryEl = 5,
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_MediaBookmark name="CT_MediaBookmark">
 * ```
 */
export interface ICTMediaBookmark {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string">
     * ```
     */
    name?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="time" type="p14:ST_UniversalTimeOffset">
     * ```
     */
    time?: string;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_MediaBookmark name="CT_MediaBookmark">
 * ```
 */
export interface ICTMediaBookmarkDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string">
     * ```
     */
    name?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="time" type="p14:ST_UniversalTimeOffset">
     * ```
     */
    time?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_SectionSlideIdList name="CT_SectionSlideIdList">
 * ```
 */
export interface ICTSectionSlideIdList {
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_SectionSlideIdListEntry maxOccurs="unbounded" minOccurs="0" name="sldId" type="CT_SectionSlideIdListEntry">
     * ```
     */
    sldId?: ICTSectionSlideIdListEntry[];
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_SectionSlideIdList name="CT_SectionSlideIdList">
 * ```
 */
export interface ICTSectionSlideIdListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msppt2010:CT_SectionSlideIdListEntry maxOccurs="unbounded" minOccurs="0" name="sldId" type="CT_SectionSlideIdListEntry">
     * ```
     */
    sldId?: ICTSectionSlideIdListEntryDelta[];
    sldIdOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureLayer name="CT_PictureLayer">
 * ```
 */
export interface ICTPictureLayer {
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffect maxOccurs="unbounded" minOccurs="0" name="imgEffect" type="CT_PictureEffect">
     * ```
     */
    imgEffect?: ICTPictureEffect[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:embed default="" name="embed" ref="r:embed" type="ST_RelationshipId" use="optional">
     * ```
     */
    embed?: string;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureLayer name="CT_PictureLayer">
 * ```
 */
export interface ICTPictureLayerDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffect maxOccurs="unbounded" minOccurs="0" name="imgEffect" type="CT_PictureEffect">
     * ```
     */
    imgEffect?: ICTPictureEffectDelta[];
    imgEffectOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:embed default="" name="embed" ref="r:embed" type="ST_RelationshipId" use="optional">
     * ```
     */
    embed?: string | null;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_Geometry name="EG_Geometry">
 * ```
 */
export interface IEGGeometry {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_CustomGeometry2D maxOccurs="1" minOccurs="1" name="custGeom" type="CT_CustomGeometry2D">
     * ```
     */
    custGeom?: ICTCustomGeometry2D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PresetGeometry2D maxOccurs="1" minOccurs="1" name="prstGeom" type="CT_PresetGeometry2D">
     * ```
     */
    prstGeom?: ICTPresetGeometry2D;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_Geometry name="EG_Geometry">
 * ```
 */
export interface IEGGeometryDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_CustomGeometry2D maxOccurs="1" minOccurs="1" name="custGeom" type="CT_CustomGeometry2D">
     * ```
     */
    custGeom?: ICTCustomGeometry2DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_PresetGeometry2D maxOccurs="1" minOccurs="1" name="prstGeom" type="CT_PresetGeometry2D">
     * ```
     */
    prstGeom?: ICTPresetGeometry2DDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msask2018:CT_LineSketchTypeProperties name="CT_LineSketchTypeProperties">
 * ```
 */
export interface ICTLineSketchTypeProperties {
    /**
     * ```xml
     * <xsd:group typeRedirected=msask2018:EG_LineSketchType ref=msask2018:EG_LineSketchType maxOccurs="1" minOccurs="0" name="EG_LineSketchType" ref="EG_LineSketchType">
     * ref msask2018:EG_LineSketchType
     * ```
     */
    egLineSketchType?: IEGLineSketchType;
}

/**
 * ```xml
 * <xsd:complexType name=msask2018:CT_LineSketchTypeProperties name="CT_LineSketchTypeProperties">
 * ```
 */
export interface ICTLineSketchTypePropertiesDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=msask2018:EG_LineSketchType ref=msask2018:EG_LineSketchType maxOccurs="1" minOccurs="0" name="EG_LineSketchType" ref="EG_LineSketchType">
     * ref msask2018:EG_LineSketchType
     * ```
     */
    egLineSketchType?: IEGLineSketchTypeDelta | null;
}

export enum STHyperlinkColor {
    PBDEFAULT = 0,
    hlink = 1,
    tx = 2,
}

export enum STOnOffStyleType {
    PBDEFAULT = 0,
    on = 1,
    off = 2,
    def = 3,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableCellBorderStyle name="CT_TableCellBorderStyle">
 * ```
 */
export interface ICTTableCellBorderStyle {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ThemeableLineStyle maxOccurs="1" minOccurs="0" name="left" type="CT_ThemeableLineStyle">
     * ```
     */
    left?: ICTThemeableLineStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ThemeableLineStyle maxOccurs="1" minOccurs="0" name="right" type="CT_ThemeableLineStyle">
     * ```
     */
    right?: ICTThemeableLineStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ThemeableLineStyle maxOccurs="1" minOccurs="0" name="top" type="CT_ThemeableLineStyle">
     * ```
     */
    top?: ICTThemeableLineStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ThemeableLineStyle maxOccurs="1" minOccurs="0" name="bottom" type="CT_ThemeableLineStyle">
     * ```
     */
    bottom?: ICTThemeableLineStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ThemeableLineStyle maxOccurs="1" minOccurs="0" name="insideH" type="CT_ThemeableLineStyle">
     * ```
     */
    insideH?: ICTThemeableLineStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ThemeableLineStyle maxOccurs="1" minOccurs="0" name="insideV" type="CT_ThemeableLineStyle">
     * ```
     */
    insideV?: ICTThemeableLineStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ThemeableLineStyle maxOccurs="1" minOccurs="0" name="tl2br" type="CT_ThemeableLineStyle">
     * ```
     */
    tl2br?: ICTThemeableLineStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ThemeableLineStyle maxOccurs="1" minOccurs="0" name="tr2bl" type="CT_ThemeableLineStyle">
     * ```
     */
    tr2bl?: ICTThemeableLineStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableCellBorderStyle name="CT_TableCellBorderStyle">
 * ```
 */
export interface ICTTableCellBorderStyleDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ThemeableLineStyle maxOccurs="1" minOccurs="0" name="left" type="CT_ThemeableLineStyle">
     * ```
     */
    left?: ICTThemeableLineStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ThemeableLineStyle maxOccurs="1" minOccurs="0" name="right" type="CT_ThemeableLineStyle">
     * ```
     */
    right?: ICTThemeableLineStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ThemeableLineStyle maxOccurs="1" minOccurs="0" name="top" type="CT_ThemeableLineStyle">
     * ```
     */
    top?: ICTThemeableLineStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ThemeableLineStyle maxOccurs="1" minOccurs="0" name="bottom" type="CT_ThemeableLineStyle">
     * ```
     */
    bottom?: ICTThemeableLineStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ThemeableLineStyle maxOccurs="1" minOccurs="0" name="insideH" type="CT_ThemeableLineStyle">
     * ```
     */
    insideH?: ICTThemeableLineStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ThemeableLineStyle maxOccurs="1" minOccurs="0" name="insideV" type="CT_ThemeableLineStyle">
     * ```
     */
    insideV?: ICTThemeableLineStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ThemeableLineStyle maxOccurs="1" minOccurs="0" name="tl2br" type="CT_ThemeableLineStyle">
     * ```
     */
    tl2br?: ICTThemeableLineStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ThemeableLineStyle maxOccurs="1" minOccurs="0" name="tr2bl" type="CT_ThemeableLineStyle">
     * ```
     */
    tr2bl?: ICTThemeableLineStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_Guide name="CT_Guide">
 * ```
 */
export interface ICTGuide {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_DirectionPml default="vert" name="orient" type="ST_DirectionPml" use="optional">
     * ```
     */
    orient?: STDirectionPml;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="0" name="pos" type="a:ST_Coordinate32" use="optional">
     * ```
     */
    pos?: number;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_Guide name="CT_Guide">
 * ```
 */
export interface ICTGuideDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_DirectionPml default="vert" name="orient" type="ST_DirectionPml" use="optional">
     * ```
     */
    orient?: STDirectionPml | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" default="0" name="pos" type="a:ST_Coordinate32" use="optional">
     * ```
     */
    pos?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Ratio name="CT_Ratio">
 * ```
 */
export interface ICTRatio {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="n" type="xsd:long" use="required">
     * ```
     */
    n?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="d" type="xsd:long" use="required">
     * ```
     */
    d?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Ratio name="CT_Ratio">
 * ```
 */
export interface ICTRatioDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="n" type="xsd:long" use="required">
     * ```
     */
    n?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="d" type="xsd:long" use="required">
     * ```
     */
    d?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_XYAdjustHandle name="CT_XYAdjustHandle">
 * ```
 */
export interface ICTXYAdjustHandle {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AdjPoint2D maxOccurs="1" minOccurs="1" name="pos" type="CT_AdjPoint2D">
     * ```
     */
    pos?: ICTAdjPoint2D;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="gdRefX" type="ST_GeomGuideName" use="optional">
     * ```
     */
    gdRefX?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="minX" type="ST_AdjCoordinate" use="optional">
     * ```
     */
    minX?: ICTAdjCoordinate;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="maxX" type="ST_AdjCoordinate" use="optional">
     * ```
     */
    maxX?: ICTAdjCoordinate;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="gdRefY" type="ST_GeomGuideName" use="optional">
     * ```
     */
    gdRefY?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="minY" type="ST_AdjCoordinate" use="optional">
     * ```
     */
    minY?: ICTAdjCoordinate;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="maxY" type="ST_AdjCoordinate" use="optional">
     * ```
     */
    maxY?: ICTAdjCoordinate;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_XYAdjustHandle name="CT_XYAdjustHandle">
 * ```
 */
export interface ICTXYAdjustHandleDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AdjPoint2D maxOccurs="1" minOccurs="1" name="pos" type="CT_AdjPoint2D">
     * ```
     */
    pos?: ICTAdjPoint2DDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="gdRefX" type="ST_GeomGuideName" use="optional">
     * ```
     */
    gdRefX?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="minX" type="ST_AdjCoordinate" use="optional">
     * ```
     */
    minX?: ICTAdjCoordinateDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="maxX" type="ST_AdjCoordinate" use="optional">
     * ```
     */
    maxX?: ICTAdjCoordinateDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="gdRefY" type="ST_GeomGuideName" use="optional">
     * ```
     */
    gdRefY?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="minY" type="ST_AdjCoordinate" use="optional">
     * ```
     */
    minY?: ICTAdjCoordinateDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="maxY" type="ST_AdjCoordinate" use="optional">
     * ```
     */
    maxY?: ICTAdjCoordinateDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PolarAdjustHandle name="CT_PolarAdjustHandle">
 * ```
 */
export interface ICTPolarAdjustHandle {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AdjPoint2D maxOccurs="1" minOccurs="1" name="pos" type="CT_AdjPoint2D">
     * ```
     */
    pos?: ICTAdjPoint2D;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="gdRefR" type="ST_GeomGuideName" use="optional">
     * ```
     */
    gdRefR?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="minR" type="ST_AdjCoordinate" use="optional">
     * ```
     */
    minR?: ICTAdjCoordinate;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="maxR" type="ST_AdjCoordinate" use="optional">
     * ```
     */
    maxR?: ICTAdjCoordinate;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="gdRefAng" type="ST_GeomGuideName" use="optional">
     * ```
     */
    gdRefAng?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjAngle convType="CT_AdjAngle" name="minAng" type="ST_AdjAngle" use="optional">
     * ```
     */
    minAng?: ICTAdjAngle;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjAngle convType="CT_AdjAngle" name="maxAng" type="ST_AdjAngle" use="optional">
     * ```
     */
    maxAng?: ICTAdjAngle;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PolarAdjustHandle name="CT_PolarAdjustHandle">
 * ```
 */
export interface ICTPolarAdjustHandleDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AdjPoint2D maxOccurs="1" minOccurs="1" name="pos" type="CT_AdjPoint2D">
     * ```
     */
    pos?: ICTAdjPoint2DDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="gdRefR" type="ST_GeomGuideName" use="optional">
     * ```
     */
    gdRefR?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="minR" type="ST_AdjCoordinate" use="optional">
     * ```
     */
    minR?: ICTAdjCoordinateDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="maxR" type="ST_AdjCoordinate" use="optional">
     * ```
     */
    maxR?: ICTAdjCoordinateDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="gdRefAng" type="ST_GeomGuideName" use="optional">
     * ```
     */
    gdRefAng?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjAngle convType="CT_AdjAngle" name="minAng" type="ST_AdjAngle" use="optional">
     * ```
     */
    minAng?: ICTAdjAngleDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjAngle convType="CT_AdjAngle" name="maxAng" type="ST_AdjAngle" use="optional">
     * ```
     */
    maxAng?: ICTAdjAngleDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ConnectionSite name="CT_ConnectionSite">
 * ```
 */
export interface ICTConnectionSite {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AdjPoint2D maxOccurs="1" minOccurs="1" name="pos" type="CT_AdjPoint2D">
     * ```
     */
    pos?: ICTAdjPoint2D;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjAngle convType="CT_AdjAngle" name="ang" type="ST_AdjAngle" use="required">
     * ```
     */
    ang?: ICTAdjAngle;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ConnectionSite name="CT_ConnectionSite">
 * ```
 */
export interface ICTConnectionSiteDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AdjPoint2D maxOccurs="1" minOccurs="1" name="pos" type="CT_AdjPoint2D">
     * ```
     */
    pos?: ICTAdjPoint2DDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjAngle convType="CT_AdjAngle" name="ang" type="ST_AdjAngle" use="required">
     * ```
     */
    ang?: ICTAdjAngleDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AdjCoordinate name="CT_AdjCoordinate">
 * ```
 */
export interface ICTAdjCoordinate {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="coordinate" type="ST_Coordinate">
     * ```
     */
    coordinate?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="geomGuideName" type="ST_GeomGuideName">
     * ```
     */
    geomGuideName?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AdjCoordinate name="CT_AdjCoordinate">
 * ```
 */
export interface ICTAdjCoordinateDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="coordinate" type="ST_Coordinate">
     * ```
     */
    coordinate?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="geomGuideName" type="ST_GeomGuideName">
     * ```
     */
    geomGuideName?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Path2D name="CT_Path2D">
 * ```
 */
export interface ICTPath2D {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_Path2D ref=dmlmain:EG_Path2D maxOccurs="unbounded" minOccurs="0" name="EG_Path2D" ref="EG_Path2D">
     * ref dmlmain:EG_Path2D
     * ```
     */
    egPath2D?: IEGPath2D[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="w" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    w?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="h" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    h?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PathFillMode default="norm" name="fill" type="ST_PathFillMode" use="optional">
     * ```
     */
    fill?: STPathFillMode;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="stroke" type="xsd:boolean" use="optional">
     * ```
     */
    stroke?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="extrusionOk" type="xsd:boolean" use="optional">
     * ```
     */
    extrusionOk?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="pathString" skipIO="true" type="xsd:string" use="optional">
     * ```
     */
    pathString?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Path2D name="CT_Path2D">
 * ```
 */
export interface ICTPath2DDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_Path2D ref=dmlmain:EG_Path2D maxOccurs="unbounded" minOccurs="0" name="EG_Path2D" ref="EG_Path2D">
     * ref dmlmain:EG_Path2D
     * ```
     */
    egPath2D?: IEGPath2DDelta[];
    egPath2DOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="w" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    w?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="h" type="ST_PositiveCoordinate" use="optional">
     * ```
     */
    h?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_PathFillMode default="norm" name="fill" type="ST_PathFillMode" use="optional">
     * ```
     */
    fill?: STPathFillMode | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="stroke" type="xsd:boolean" use="optional">
     * ```
     */
    stroke?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="extrusionOk" type="xsd:boolean" use="optional">
     * ```
     */
    extrusionOk?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="" name="pathString" skipIO="true" type="xsd:string" use="optional">
     * ```
     */
    pathString?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AudioCDTime name="CT_AudioCDTime">
 * ```
 */
export interface ICTAudioCDTime {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="track" type="xsd:unsignedByte" use="required">
     * ```
     */
    track?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="0" name="time" type="xsd:unsignedInt" use="optional">
     * ```
     */
    time?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AudioCDTime name="CT_AudioCDTime">
 * ```
 */
export interface ICTAudioCDTimeDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="track" type="xsd:unsignedByte" use="required">
     * ```
     */
    track?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="0" name="time" type="xsd:unsignedInt" use="optional">
     * ```
     */
    time?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlpicture:CT_PictureDmlPct name="CT_PictureDmlPct">
 * ```
 */
export interface ICTPictureDmlPct {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlpicture:CT_PictureNonVisualDmlPct maxOccurs="1" minOccurs="1" name="nvPicPr" type="CT_PictureNonVisualDmlPct">
     * ```
     */
    nvPicPr?: ICTPictureNonVisualDmlPct;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="a:CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="1" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean ref=tencentdocumentword:isNotAutoFitLayout name="isNotAutoFitLayout" ref="td:isNotAutoFitLayout" type="xsd:boolean">
     * ```
     */
    isNotAutoFitLayout?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlpicture:CT_PictureDmlPct name="CT_PictureDmlPct">
 * ```
 */
export interface ICTPictureDmlPctDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlpicture:CT_PictureNonVisualDmlPct maxOccurs="1" minOccurs="1" name="nvPicPr" type="CT_PictureNonVisualDmlPct">
     * ```
     */
    nvPicPr?: ICTPictureNonVisualDmlPctDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_BlipFillProperties maxOccurs="1" minOccurs="1" name="blipFill" type="a:CT_BlipFillProperties">
     * ```
     */
    blipFill?: ICTBlipFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="1" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean ref=tencentdocumentword:isNotAutoFitLayout name="isNotAutoFitLayout" ref="td:isNotAutoFitLayout" type="xsd:boolean">
     * ```
     */
    isNotAutoFitLayout?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_RelIds name="CT_RelIds">
 * ```
 */
export interface ICTRelIds {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:dm default="" name="dm" ref="r:dm" type="ST_RelationshipId" use="required">
     * ```
     */
    dm?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:lo default="" name="lo" ref="r:lo" type="ST_RelationshipId" use="required">
     * ```
     */
    lo?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:qs default="" name="qs" ref="r:qs" type="ST_RelationshipId" use="required">
     * ```
     */
    qs?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:cs default="" name="cs" ref="r:cs" type="ST_RelationshipId" use="required">
     * ```
     */
    cs?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_RelIds name="CT_RelIds">
 * ```
 */
export interface ICTRelIdsDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:dm default="" name="dm" ref="r:dm" type="ST_RelationshipId" use="required">
     * ```
     */
    dm?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:lo default="" name="lo" ref="r:lo" type="ST_RelationshipId" use="required">
     * ```
     */
    lo?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:qs default="" name="qs" ref="r:qs" type="ST_RelationshipId" use="required">
     * ```
     */
    qs?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:cs default="" name="cs" ref="r:cs" type="ST_RelationshipId" use="required">
     * ```
     */
    cs?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=tencentdocumentdml:CT_DiagramsGroup name="CT_DiagramsGroup">
 * ```
 */
export interface ICTDiagramsGroup {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ColorTransform maxOccurs="1" minOccurs="0" name="color" type="dgm:CT_ColorTransform">
     * ```
     */
    color?: ICTColorTransform;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_DataModel maxOccurs="1" minOccurs="0" name="data" type="dgm:CT_DataModel">
     * ```
     */
    data?: ICTDataModel;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdsp2008:CT_DrawingMsDsp maxOccurs="1" minOccurs="0" name="drawing" type="dsp:CT_DrawingMsDsp">
     * ```
     */
    drawing?: ICTDrawingMsDsp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_DiagramDefinition maxOccurs="1" minOccurs="0" name="layout" type="dgm:CT_DiagramDefinition">
     * ```
     */
    layout?: ICTDiagramDefinition;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_StyleDefinition maxOccurs="1" minOccurs="0" name="quickStyle" type="dgm:CT_StyleDefinition">
     * ```
     */
    quickStyle?: ICTStyleDefinition;
}

/**
 * ```xml
 * <xsd:complexType name=tencentdocumentdml:CT_DiagramsGroup name="CT_DiagramsGroup">
 * ```
 */
export interface ICTDiagramsGroupDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_ColorTransform maxOccurs="1" minOccurs="0" name="color" type="dgm:CT_ColorTransform">
     * ```
     */
    color?: ICTColorTransformDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_DataModel maxOccurs="1" minOccurs="0" name="data" type="dgm:CT_DataModel">
     * ```
     */
    data?: ICTDataModelDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdsp2008:CT_DrawingMsDsp maxOccurs="1" minOccurs="0" name="drawing" type="dsp:CT_DrawingMsDsp">
     * ```
     */
    drawing?: ICTDrawingMsDspDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_DiagramDefinition maxOccurs="1" minOccurs="0" name="layout" type="dgm:CT_DiagramDefinition">
     * ```
     */
    layout?: ICTDiagramDefinitionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmldiagram:CT_StyleDefinition maxOccurs="1" minOccurs="0" name="quickStyle" type="dgm:CT_StyleDefinition">
     * ```
     */
    quickStyle?: ICTStyleDefinitionDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=tencentdocumentdml:CT_ChartGroup name="CT_ChartGroup">
 * ```
 */
export interface ICTChartGroup {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartSpace maxOccurs="1" minOccurs="0" name="chartSpace" type="c:CT_ChartSpace">
     * ```
     */
    chartSpace?: ICTChartSpace;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ChartSpaceMsChartEx maxOccurs="1" minOccurs="0" name="chartSpaceEx" type="cex:CT_ChartSpaceMsChartEx">
     * ```
     */
    chartSpaceEx?: ICTChartSpaceMsChartEx;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_ColorStyle maxOccurs="1" minOccurs="0" name="colorStyle" type="cs:CT_ColorStyle">
     * ```
     */
    colorStyle?: ICTColorStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_ChartStyle maxOccurs="1" minOccurs="0" name="chartStyle" type="cs:CT_ChartStyle">
     * ```
     */
    chartStyle?: ICTChartStyle;
}

/**
 * ```xml
 * <xsd:complexType name=tencentdocumentdml:CT_ChartGroup name="CT_ChartGroup">
 * ```
 */
export interface ICTChartGroupDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartSpace maxOccurs="1" minOccurs="0" name="chartSpace" type="c:CT_ChartSpace">
     * ```
     */
    chartSpace?: ICTChartSpaceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ChartSpaceMsChartEx maxOccurs="1" minOccurs="0" name="chartSpaceEx" type="cex:CT_ChartSpaceMsChartEx">
     * ```
     */
    chartSpaceEx?: ICTChartSpaceMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_ColorStyle maxOccurs="1" minOccurs="0" name="colorStyle" type="cs:CT_ColorStyle">
     * ```
     */
    colorStyle?: ICTColorStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_ChartStyle maxOccurs="1" minOccurs="0" name="chartStyle" type="cs:CT_ChartStyle">
     * ```
     */
    chartStyle?: ICTChartStyleDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_RelIdMsChartEx name="CT_RelIdMsChartEx">
 * ```
 */
export interface ICTRelIdMsChartEx {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_RelIdMsChartEx name="CT_RelIdMsChartEx">
 * ```
 */
export interface ICTRelIdMsChartExDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableDmlA name="CT_TableDmlA">
 * ```
 */
export interface ICTTableDmlA {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableProperties maxOccurs="1" minOccurs="0" name="tblPr" type="CT_TableProperties">
     * ```
     */
    tblPr?: ICTTableProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableGrid maxOccurs="1" minOccurs="1" name="tblGrid" type="CT_TableGrid">
     * ```
     */
    tblGrid?: ICTTableGrid;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableRow maxOccurs="unbounded" minOccurs="0" name="tr" type="CT_TableRow">
     * ```
     */
    tr?: ICTTableRow[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableDmlA name="CT_TableDmlA">
 * ```
 */
export interface ICTTableDmlADelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableProperties maxOccurs="1" minOccurs="0" name="tblPr" type="CT_TableProperties">
     * ```
     */
    tblPr?: ICTTablePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableGrid maxOccurs="1" minOccurs="1" name="tblGrid" type="CT_TableGrid">
     * ```
     */
    tblGrid?: ICTTableGridDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableRow maxOccurs="unbounded" minOccurs="0" name="tr" type="CT_TableRow">
     * ```
     */
    tr?: ICTTableRowDelta[];
    trOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=tencentdocumentcommon:CT_ChartDataTd name="CT_ChartDataTd">
 * ```
 */
export interface ICTChartDataTd {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartSpace maxOccurs="1" minOccurs="0" name="chart" type="c:CT_ChartSpace">
     * ```
     */
    chart?: ICTChartSpace;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ChartSpaceMsChartEx maxOccurs="1" minOccurs="0" name="msChartEx" type="cex:CT_ChartSpaceMsChartEx">
     * ```
     */
    msChartEx?: ICTChartSpaceMsChartEx;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="unbounded" minOccurs="1" name="range" type="xsd:string">
     * ```
     */
    range?: string[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=tencentdocumentcommon:ST_ChartType name="chartType" type="ST_ChartType" use="required">
     * ```
     */
    chartType?: STChartType;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_ChartStyle maxOccurs="1" minOccurs="0" name="style" type="cs:CT_ChartStyle">
     * ```
     */
    style?: ICTChartStyle;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_ColorStyle maxOccurs="1" minOccurs="0" name="color" type="cs:CT_ColorStyle">
     * ```
     */
    color?: ICTColorStyle;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="shouldUseColAsSeries" type="xsd:boolean">
     * ```
     */
    shouldUseColAsSeries?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=tencentdocumentcommon:CT_TotalSeriesList maxOccurs="1" minOccurs="0" name="totalSeriesList" type="CT_TotalSeriesList">
     * ```
     */
    totalSeriesList?: ICTTotalSeriesList;
}

/**
 * ```xml
 * <xsd:complexType name=tencentdocumentcommon:CT_ChartDataTd name="CT_ChartDataTd">
 * ```
 */
export interface ICTChartDataTdDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ChartSpace maxOccurs="1" minOccurs="0" name="chart" type="c:CT_ChartSpace">
     * ```
     */
    chart?: ICTChartSpaceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ChartSpaceMsChartEx maxOccurs="1" minOccurs="0" name="msChartEx" type="cex:CT_ChartSpaceMsChartEx">
     * ```
     */
    msChartEx?: ICTChartSpaceMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="unbounded" minOccurs="1" name="range" type="xsd:string">
     * ```
     */
    range?: string[];
    rangeOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=tencentdocumentcommon:ST_ChartType name="chartType" type="ST_ChartType" use="required">
     * ```
     */
    chartType?: STChartType | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_ChartStyle maxOccurs="1" minOccurs="0" name="style" type="cs:CT_ChartStyle">
     * ```
     */
    style?: ICTChartStyleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mscs2012:CT_ColorStyle maxOccurs="1" minOccurs="0" name="color" type="cs:CT_ColorStyle">
     * ```
     */
    color?: ICTColorStyleDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="shouldUseColAsSeries" type="xsd:boolean">
     * ```
     */
    shouldUseColAsSeries?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=tencentdocumentcommon:CT_TotalSeriesList maxOccurs="1" minOccurs="0" name="totalSeriesList" type="CT_TotalSeriesList">
     * ```
     */
    totalSeriesList?: ICTTotalSeriesListDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Brush name="CT_Brush">
 * ```
 */
export interface ICTBrush {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_BrushProperty maxOccurs="unbounded" minOccurs="0" name="brushProperty" type="CT_BrushProperty">
     * ```
     */
    brushProperty?: ICTBrushProperty[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Annotation maxOccurs="unbounded" minOccurs="0" name="annotation" type="CT_Annotation">
     * ```
     */
    annotation?: ICTAnnotation[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_AnnotationXML maxOccurs="unbounded" minOccurs="0" name="annotationXML" type="CT_AnnotationXML">
     * ```
     */
    annotationXml?: ICTAnnotationXML[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string" use="optional">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="brushRef" type="xsd:anyURI">
     * ```
     */
    brushRef?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Brush name="CT_Brush">
 * ```
 */
export interface ICTBrushDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_BrushProperty maxOccurs="unbounded" minOccurs="0" name="brushProperty" type="CT_BrushProperty">
     * ```
     */
    brushProperty?: ICTBrushPropertyDelta[];
    brushPropertyOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Annotation maxOccurs="unbounded" minOccurs="0" name="annotation" type="CT_Annotation">
     * ```
     */
    annotation?: ICTAnnotationDelta[];
    annotationOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_AnnotationXML maxOccurs="unbounded" minOccurs="0" name="annotationXML" type="CT_AnnotationXML">
     * ```
     */
    annotationXml?: ICTAnnotationXMLDelta[];
    annotationXmlOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string" use="optional">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="brushRef" type="xsd:anyURI">
     * ```
     */
    brushRef?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Canvas name="CT_Canvas">
 * ```
 */
export interface ICTCanvas {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceFormat minOccurs="0" name="traceFormat" type="CT_TraceFormat">
     * ```
     */
    traceFormat?: ICTTraceFormat;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="traceFormatRef" type="xsd:anyURI">
     * ```
     */
    traceFormatRef?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Canvas name="CT_Canvas">
 * ```
 */
export interface ICTCanvasDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceFormat minOccurs="0" name="traceFormat" type="CT_TraceFormat">
     * ```
     */
    traceFormat?: ICTTraceFormatDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="traceFormatRef" type="xsd:anyURI">
     * ```
     */
    traceFormatRef?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_CanvasTransform name="CT_CanvasTransform">
 * ```
 */
export interface ICTCanvasTransform {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Mapping maxOccurs="2" minOccurs="1" name="mapping" type="CT_Mapping">
     * ```
     */
    mapping?: ICTMapping[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="invertible" type="xsd:boolean" use="optional">
     * ```
     */
    invertible?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_CanvasTransform name="CT_CanvasTransform">
 * ```
 */
export interface ICTCanvasTransformDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Mapping maxOccurs="2" minOccurs="1" name="mapping" type="CT_Mapping">
     * ```
     */
    mapping?: ICTMappingDelta[];
    mappingOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="invertible" type="xsd:boolean" use="optional">
     * ```
     */
    invertible?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_InkSource name="CT_InkSource">
 * ```
 */
export interface ICTInkSource {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceFormat name="traceFormat" type="CT_TraceFormat">
     * ```
     */
    traceFormat?: ICTTraceFormat;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_SampleRate minOccurs="0" name="sampleRate" type="CT_SampleRate">
     * ```
     */
    sampleRate?: ICTSampleRate;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Latency minOccurs="0" name="latency" type="CT_Latency">
     * ```
     */
    latency?: ICTLatency;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_ActiveArea minOccurs="0" name="activeArea" type="CT_ActiveArea">
     * ```
     */
    activeArea?: ICTActiveArea;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_SourceProperty maxOccurs="unbounded" minOccurs="0" name="sourceProperty" type="CT_SourceProperty">
     * ```
     */
    sourceProperty?: ICTSourceProperty[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_ChannelProperties minOccurs="0" name="channelProperties" type="CT_ChannelProperties">
     * ```
     */
    channelProperties?: ICTChannelProperties;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string" use="required">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="manufacturer" type="xsd:string">
     * ```
     */
    manufacturer?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="model" type="xsd:string">
     * ```
     */
    model?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="serialNo" type="xsd:string">
     * ```
     */
    serialNo?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="specificationRef" type="xsd:anyURI">
     * ```
     */
    specificationRef?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="description" type="xsd:string">
     * ```
     */
    description?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_InkSource name="CT_InkSource">
 * ```
 */
export interface ICTInkSourceDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TraceFormat name="traceFormat" type="CT_TraceFormat">
     * ```
     */
    traceFormat?: ICTTraceFormatDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_SampleRate minOccurs="0" name="sampleRate" type="CT_SampleRate">
     * ```
     */
    sampleRate?: ICTSampleRateDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Latency minOccurs="0" name="latency" type="CT_Latency">
     * ```
     */
    latency?: ICTLatencyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_ActiveArea minOccurs="0" name="activeArea" type="CT_ActiveArea">
     * ```
     */
    activeArea?: ICTActiveAreaDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_SourceProperty maxOccurs="unbounded" minOccurs="0" name="sourceProperty" type="CT_SourceProperty">
     * ```
     */
    sourceProperty?: ICTSourcePropertyDelta[];
    sourcePropertyOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_ChannelProperties minOccurs="0" name="channelProperties" type="CT_ChannelProperties">
     * ```
     */
    channelProperties?: ICTChannelPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string" use="required">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="manufacturer" type="xsd:string">
     * ```
     */
    manufacturer?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="model" type="xsd:string">
     * ```
     */
    model?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="serialNo" type="xsd:string">
     * ```
     */
    serialNo?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="specificationRef" type="xsd:anyURI">
     * ```
     */
    specificationRef?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="description" type="xsd:string">
     * ```
     */
    description?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Mapping mixed="true" name="CT_Mapping">
 * ```
 */
export interface ICTMapping {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Bind maxOccurs="unbounded" minOccurs="0" name="bind" type="CT_Bind">
     * ```
     */
    bind?: ICTBind[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TableInkML name="table" type="CT_TableInkML">
     * ```
     */
    table?: ICTTableInkML;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Affine name="affine" type="CT_Affine">
     * ```
     */
    affine?: ICTAffine;
    /**
     * ```xml
     * <xsd:element typeRedirected=mathml:CT_EmptyMathML ref=mathml:math name="math" ref="mathml:math" type="CT_EmptyMathML">
     * ```
     */
    math?: ICTEmptyMathML;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Mapping maxOccurs="unbounded" minOccurs="0" name="mapping" type="CT_Mapping">
     * ```
     */
    mapping?: ICTMapping[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=inkml:ST_MappingType default="unknown" name="type" type="ST_MappingType">
     * ```
     */
    type?: STMappingType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="mappingRef" type="xsd:anyURI">
     * ```
     */
    mappingRef?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Mapping mixed="true" name="CT_Mapping">
 * ```
 */
export interface ICTMappingDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Bind maxOccurs="unbounded" minOccurs="0" name="bind" type="CT_Bind">
     * ```
     */
    bind?: ICTBindDelta[];
    bindOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_TableInkML name="table" type="CT_TableInkML">
     * ```
     */
    table?: ICTTableInkMLDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Affine name="affine" type="CT_Affine">
     * ```
     */
    affine?: ICTAffineDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mathml:CT_EmptyMathML ref=mathml:math name="math" ref="mathml:math" type="CT_EmptyMathML">
     * ```
     */
    math?: ICTEmptyMathML | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Mapping maxOccurs="unbounded" minOccurs="0" name="mapping" type="CT_Mapping">
     * ```
     */
    mapping?: ICTMappingDelta[];
    mappingOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=inkml:ST_MappingType default="unknown" name="type" type="ST_MappingType">
     * ```
     */
    type?: STMappingType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="mappingRef" type="xsd:anyURI">
     * ```
     */
    mappingRef?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Timestamp name="CT_Timestamp">
 * ```
 */
export interface ICTTimestamp {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string" use="required">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="time" type="xsd:unsignedLong">
     * ```
     */
    time?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="timestampRef" type="xsd:anyURI">
     * ```
     */
    timestampRef?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="timeString" type="xsd:dateTime">
     * ```
     */
    timeString?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="timeOffset" type="xsd:unsignedLong" use="optional">
     * ```
     */
    timeOffset?: number;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Timestamp name="CT_Timestamp">
 * ```
 */
export interface ICTTimestampDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string" use="required">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="time" type="xsd:unsignedLong">
     * ```
     */
    time?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="timestampRef" type="xsd:anyURI">
     * ```
     */
    timestampRef?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="timeString" type="xsd:dateTime">
     * ```
     */
    timeString?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="timeOffset" type="xsd:unsignedLong" use="optional">
     * ```
     */
    timeOffset?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_TraceFormat name="CT_TraceFormat">
 * ```
 */
export interface ICTTraceFormat {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Channel maxOccurs="unbounded" minOccurs="0" name="channel" type="CT_Channel">
     * ```
     */
    channel?: ICTChannel[];
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_IntermittentChannels minOccurs="0" name="intermittentChannels" type="CT_IntermittentChannels">
     * ```
     */
    intermittentChannels?: ICTIntermittentChannels;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string">
     * ```
     */
    id?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_TraceFormat name="CT_TraceFormat">
 * ```
 */
export interface ICTTraceFormatDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Channel maxOccurs="unbounded" minOccurs="0" name="channel" type="CT_Channel">
     * ```
     */
    channel?: ICTChannelDelta[];
    channelOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_IntermittentChannels minOccurs="0" name="intermittentChannels" type="CT_IntermittentChannels">
     * ```
     */
    intermittentChannels?: ICTIntermittentChannelsDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string">
     * ```
     */
    id?: string | null;
}

export enum STTraceType {
    PBDEFAULT = 0,
    penDown = 1,
    penUp = 2,
    indeterminate = 3,
}

export enum STContinuation {
    PBDEFAULT = 0,
    begin = 1,
    end = 2,
    middle = 3,
}

export enum STOleObjectFollowColorScheme {
    PBDEFAULT = 0,
    none = 1,
    full = 2,
    textAndBackground = 3,
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_OrthographicProjection name="CT_OrthographicProjection">
 * ```
 */
export interface ICTOrthographicProjection {
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_PositiveRatio maxOccurs="1" minOccurs="1" name="sz" type="CT_PositiveRatio">
     * ```
     */
    sz?: ICTPositiveRatio;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_OrthographicProjection name="CT_OrthographicProjection">
 * ```
 */
export interface ICTOrthographicProjectionDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msam3d2017:CT_PositiveRatio maxOccurs="1" minOccurs="1" name="sz" type="CT_PositiveRatio">
     * ```
     */
    sz?: ICTPositiveRatioDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_PerspectiveProjection name="CT_PerspectiveProjection">
 * ```
 */
export interface ICTPerspectiveProjection {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="fov" type="a:ST_FOVAngle" use="required">
     * ```
     */
    fov?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_PerspectiveProjection name="CT_PerspectiveProjection">
 * ```
 */
export interface ICTPerspectiveProjectionDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="a:CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="fov" type="a:ST_FOVAngle" use="required">
     * ```
     */
    fov?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_PositiveRatio name="CT_PositiveRatio">
 * ```
 */
export interface ICTPositiveRatio {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="n" type="xsd:unsignedLong" use="required">
     * ```
     */
    n?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="d" type="xsd:unsignedLong" use="required">
     * ```
     */
    d?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_PositiveRatio name="CT_PositiveRatio">
 * ```
 */
export interface ICTPositiveRatioDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="n" type="xsd:unsignedLong" use="required">
     * ```
     */
    n?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="d" type="xsd:unsignedLong" use="required">
     * ```
     */
    d?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_Scale3D name="CT_Scale3D">
 * ```
 */
export interface ICTScale3D {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Ratio maxOccurs="1" minOccurs="1" name="sx" type="a:CT_Ratio">
     * ```
     */
    sx?: ICTRatio;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Ratio maxOccurs="1" minOccurs="1" name="sy" type="a:CT_Ratio">
     * ```
     */
    sy?: ICTRatio;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Ratio maxOccurs="1" minOccurs="1" name="sz" type="a:CT_Ratio">
     * ```
     */
    sz?: ICTRatio;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_Scale3D name="CT_Scale3D">
 * ```
 */
export interface ICTScale3DDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Ratio maxOccurs="1" minOccurs="1" name="sx" type="a:CT_Ratio">
     * ```
     */
    sx?: ICTRatioDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Ratio maxOccurs="1" minOccurs="1" name="sy" type="a:CT_Ratio">
     * ```
     */
    sy?: ICTRatioDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Ratio maxOccurs="1" minOccurs="1" name="sz" type="a:CT_Ratio">
     * ```
     */
    sz?: ICTRatioDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_Rotate3D name="CT_Rotate3D">
 * ```
 */
export interface ICTRotate3D {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="ax" type="a:ST_AngleDmlA" use="optional">
     * ```
     */
    ax?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="ay" type="a:ST_AngleDmlA" use="optional">
     * ```
     */
    ay?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="az" type="a:ST_AngleDmlA" use="optional">
     * ```
     */
    az?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msam3d2017:CT_Rotate3D name="CT_Rotate3D">
 * ```
 */
export interface ICTRotate3DDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="ax" type="a:ST_AngleDmlA" use="optional">
     * ```
     */
    ax?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="ay" type="a:ST_AngleDmlA" use="optional">
     * ```
     */
    ay?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="az" type="a:ST_AngleDmlA" use="optional">
     * ```
     */
    az?: number | null;
}

export enum STPresetLineDashVal {
    PBDEFAULT = 0,
    solid = 1,
    dot = 2,
    dash = 3,
    lgDash = 4,
    dashDot = 5,
    lgDashDot = 6,
    lgDashDotDot = 7,
    sysDash = 8,
    sysDot = 9,
    sysDashDot = 10,
    sysDashDotDot = 11,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_DashStop name="CT_DashStop">
 * ```
 */
export interface ICTDashStop {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositivePercentageDecimal" default="100" name="d" type="ST_PositivePercentageDmlA" use="required">
     * ```
     */
    d?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositivePercentageDecimal" default="0" name="sp" type="ST_PositivePercentageDmlA" use="required">
     * ```
     */
    sp?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_DashStop name="CT_DashStop">
 * ```
 */
export interface ICTDashStopDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositivePercentageDecimal" default="100" name="d" type="ST_PositivePercentageDmlA" use="required">
     * ```
     */
    d?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositivePercentageDecimal" default="0" name="sp" type="ST_PositivePercentageDmlA" use="required">
     * ```
     */
    sp?: number | null;
}

export enum STPathShadeType {
    PBDEFAULT = 0,
    shape = 1,
    circle = 2,
    rect = 3,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTriggerTimeNodeID name="CT_TLTriggerTimeNodeID">
 * ```
 */
export interface ICTTLTriggerTimeNodeID {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="val" type="ST_TLTimeNodeID" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTriggerTimeNodeID name="CT_TLTriggerTimeNodeID">
 * ```
 */
export interface ICTTLTriggerTimeNodeIDDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="val" type="ST_TLTimeNodeID" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTriggerRuntimeNode name="CT_TLTriggerRuntimeNode">
 * ```
 */
export interface ICTTLTriggerRuntimeNode {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLTriggerRuntimeNode name="val" type="ST_TLTriggerRuntimeNode" use="required">
     * ```
     */
    val?: STTLTriggerRuntimeNode;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTriggerRuntimeNode name="CT_TLTriggerRuntimeNode">
 * ```
 */
export interface ICTTLTriggerRuntimeNodeDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLTriggerRuntimeNode name="val" type="ST_TLTriggerRuntimeNode" use="required">
     * ```
     */
    val?: STTLTriggerRuntimeNode | null;
}

export enum STTLTriggerEvent {
    PBDEFAULT = 0,
    onBegin = 1,
    onEnd = 2,
    begin = 3,
    end = 4,
    onClick = 5,
    onDblClick = 6,
    onMouseOver = 7,
    onMouseOut = 8,
    onNext = 9,
    onPrev = 10,
    onStopAudio = 11,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLIterateIntervalTime name="CT_TLIterateIntervalTime">
 * ```
 */
export interface ICTTLIterateIntervalTime {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="ST_TLTime" use="required">
     * ```
     */
    val?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLIterateIntervalTime name="CT_TLIterateIntervalTime">
 * ```
 */
export interface ICTTLIterateIntervalTimeDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="ST_TLTime" use="required">
     * ```
     */
    val?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLIterateIntervalPercentage name="CT_TLIterateIntervalPercentage">
 * ```
 */
export interface ICTTLIterateIntervalPercentage {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositivePercentageDecimal" name="val" type="a:ST_PositivePercentageDmlA" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLIterateIntervalPercentage name="CT_TLIterateIntervalPercentage">
 * ```
 */
export interface ICTTLIterateIntervalPercentageDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositivePercentageDecimal" name="val" type="a:ST_PositivePercentageDmlA" use="required">
     * ```
     */
    val?: number | null;
}

export enum STIterateType {
    PBDEFAULT = 0,
    el = 1,
    wd = 2,
    lt = 3,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLShapeTargetElement name="CT_TLShapeTargetElement">
 * ```
 */
export interface ICTTLShapeTargetElement {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="bg" type="CT_EmptyPml">
     * ```
     */
    bg?: boolean;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLSubShapeId name="subSp" type="CT_TLSubShapeId">
     * ```
     */
    subSp?: ICTTLSubShapeId;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLOleChartTargetElement name="oleChartEl" type="CT_TLOleChartTargetElement">
     * ```
     */
    oleChartEl?: ICTTLOleChartTargetElement;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTextTargetElement name="txEl" type="CT_TLTextTargetElement">
     * ```
     */
    txEl?: ICTTLTextTargetElement;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AnimationElementChoice name="graphicEl" type="a:CT_AnimationElementChoice">
     * ```
     */
    graphicEl?: ICTAnimationElementChoice;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="spid" type="a:ST_DrawingElementId" use="required">
     * ```
     */
    spid?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLShapeTargetElement name="CT_TLShapeTargetElement">
 * ```
 */
export interface ICTTLShapeTargetElementDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_EmptyPml convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" name="bg" type="CT_EmptyPml">
     * ```
     */
    bg?: boolean | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLSubShapeId name="subSp" type="CT_TLSubShapeId">
     * ```
     */
    subSp?: ICTTLSubShapeIdDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLOleChartTargetElement name="oleChartEl" type="CT_TLOleChartTargetElement">
     * ```
     */
    oleChartEl?: ICTTLOleChartTargetElementDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_TLTextTargetElement name="txEl" type="CT_TLTextTargetElement">
     * ```
     */
    txEl?: ICTTLTextTargetElementDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AnimationElementChoice name="graphicEl" type="a:CT_AnimationElementChoice">
     * ```
     */
    graphicEl?: ICTAnimationElementChoiceDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="spid" type="a:ST_DrawingElementId" use="required">
     * ```
     */
    spid?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLSubShapeId name="CT_TLSubShapeId">
 * ```
 */
export interface ICTTLSubShapeId {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="spid" type="a:ST_ShapeID" use="required">
     * ```
     */
    spid?: string;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLSubShapeId name="CT_TLSubShapeId">
 * ```
 */
export interface ICTTLSubShapeIdDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="spid" type="a:ST_ShapeID" use="required">
     * ```
     */
    spid?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PositiveFixedPercentage name="CT_PositiveFixedPercentage">
 * ```
 */
export interface ICTPositiveFixedPercentage {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositiveFixedPercentageDecimal" name="val" type="ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PositiveFixedPercentage name="CT_PositiveFixedPercentage">
 * ```
 */
export interface ICTPositiveFixedPercentageDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositiveFixedPercentageDecimal" name="val" type="ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_FixedPercentage name="CT_FixedPercentage">
 * ```
 */
export interface ICTFixedPercentage {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_FixedPercentageDecimal" name="val" type="ST_FixedPercentageDmlA" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_FixedPercentage name="CT_FixedPercentage">
 * ```
 */
export interface ICTFixedPercentageDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_FixedPercentageDecimal" name="val" type="ST_FixedPercentageDmlA" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PositivePercentage name="CT_PositivePercentage">
 * ```
 */
export interface ICTPositivePercentage {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositivePercentageDecimal" name="val" type="ST_PositivePercentageDmlA" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PositivePercentage name="CT_PositivePercentage">
 * ```
 */
export interface ICTPositivePercentageDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PositivePercentageDecimal" name="val" type="ST_PositivePercentageDmlA" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PositiveFixedAngle name="CT_PositiveFixedAngle">
 * ```
 */
export interface ICTPositiveFixedAngle {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="val" type="ST_PositiveFixedAngle" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PositiveFixedAngle name="CT_PositiveFixedAngle">
 * ```
 */
export interface ICTPositiveFixedAngleDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="val" type="ST_PositiveFixedAngle" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Angle name="CT_Angle">
 * ```
 */
export interface ICTAngle {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="val" type="ST_AngleDmlA" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Angle name="CT_Angle">
 * ```
 */
export interface ICTAngleDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="val" type="ST_AngleDmlA" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Percentage name="CT_Percentage">
 * ```
 */
export interface ICTPercentage {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" name="val" type="ST_PercentageDmlA" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Percentage name="CT_Percentage">
 * ```
 */
export interface ICTPercentageDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="ST_PercentageDecimal" name="val" type="ST_PercentageDmlA" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ManualLayout name="CT_ManualLayout">
 * ```
 */
export interface ICTManualLayout {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LayoutTarget maxOccurs="1" minOccurs="0" name="layoutTarget" type="CT_LayoutTarget">
     * ```
     */
    layoutTarget?: ICTLayoutTarget;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LayoutMode maxOccurs="1" minOccurs="0" name="xMode" type="CT_LayoutMode">
     * ```
     */
    xMode?: ICTLayoutMode;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LayoutMode maxOccurs="1" minOccurs="0" name="yMode" type="CT_LayoutMode">
     * ```
     */
    yMode?: ICTLayoutMode;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LayoutMode maxOccurs="1" minOccurs="0" name="wMode" type="CT_LayoutMode">
     * ```
     */
    wMode?: ICTLayoutMode;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LayoutMode maxOccurs="1" minOccurs="0" name="hMode" type="CT_LayoutMode">
     * ```
     */
    hMode?: ICTLayoutMode;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="x" type="CT_Double">
     * ```
     */
    x?: ICTDouble;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="y" type="CT_Double">
     * ```
     */
    y?: ICTDouble;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="w" type="CT_Double">
     * ```
     */
    w?: ICTDouble;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="h" type="CT_Double">
     * ```
     */
    h?: ICTDouble;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ManualLayout name="CT_ManualLayout">
 * ```
 */
export interface ICTManualLayoutDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LayoutTarget maxOccurs="1" minOccurs="0" name="layoutTarget" type="CT_LayoutTarget">
     * ```
     */
    layoutTarget?: ICTLayoutTargetDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LayoutMode maxOccurs="1" minOccurs="0" name="xMode" type="CT_LayoutMode">
     * ```
     */
    xMode?: ICTLayoutModeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LayoutMode maxOccurs="1" minOccurs="0" name="yMode" type="CT_LayoutMode">
     * ```
     */
    yMode?: ICTLayoutModeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LayoutMode maxOccurs="1" minOccurs="0" name="wMode" type="CT_LayoutMode">
     * ```
     */
    wMode?: ICTLayoutModeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LayoutMode maxOccurs="1" minOccurs="0" name="hMode" type="CT_LayoutMode">
     * ```
     */
    hMode?: ICTLayoutModeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="x" type="CT_Double">
     * ```
     */
    x?: ICTDoubleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="y" type="CT_Double">
     * ```
     */
    y?: ICTDoubleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="w" type="CT_Double">
     * ```
     */
    w?: ICTDoubleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Double maxOccurs="1" minOccurs="0" name="h" type="CT_Double">
     * ```
     */
    h?: ICTDoubleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_StrRef name="CT_StrRef">
 * ```
 */
export interface ICTStrRef {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="f" type="xsd:string">
     * ```
     */
    f?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StrData maxOccurs="1" minOccurs="0" name="strCache" type="CT_StrData">
     * ```
     */
    strCache?: ICTStrData;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_StrRef name="CT_StrRef">
 * ```
 */
export interface ICTStrRefDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="f" type="xsd:string">
     * ```
     */
    f?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StrData maxOccurs="1" minOccurs="0" name="strCache" type="CT_StrData">
     * ```
     */
    strCache?: ICTStrDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

export enum STLegendPos {
    PBDEFAULT = 0,
    b = 1,
    tr = 2,
    l = 3,
    r = 4,
    t = 5,
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PictureFormat name="CT_PictureFormat">
 * ```
 */
export interface ICTPictureFormat {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_PictureFormat name="val" type="ST_PictureFormat" use="required">
     * ```
     */
    val?: STPictureFormat;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PictureFormat name="CT_PictureFormat">
 * ```
 */
export interface ICTPictureFormatDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_PictureFormat name="val" type="ST_PictureFormat" use="required">
     * ```
     */
    val?: STPictureFormat | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PictureStackUnit name="CT_PictureStackUnit">
 * ```
 */
export interface ICTPictureStackUnit {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="val" type="ST_PictureStackUnit" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PictureStackUnit name="CT_PictureStackUnit">
 * ```
 */
export interface ICTPictureStackUnitDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="val" type="ST_PictureStackUnit" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_RegularTextRun name="CT_RegularTextRun">
 * ```
 */
export interface ICTRegularTextRun {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextCharacterProperties maxOccurs="1" minOccurs="0" name="rPr" type="CT_TextCharacterProperties">
     * ```
     */
    rPr?: ICTTextCharacterProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="t" type="xsd:string">
     * ```
     */
    t?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_RegularTextRun name="CT_RegularTextRun">
 * ```
 */
export interface ICTRegularTextRunDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextCharacterProperties maxOccurs="1" minOccurs="0" name="rPr" type="CT_TextCharacterProperties">
     * ```
     */
    rPr?: ICTTextCharacterPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="t" type="xsd:string">
     * ```
     */
    t?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextLineBreak name="CT_TextLineBreak">
 * ```
 */
export interface ICTTextLineBreak {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextCharacterProperties maxOccurs="1" minOccurs="0" name="rPr" type="CT_TextCharacterProperties">
     * ```
     */
    rPr?: ICTTextCharacterProperties;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TextLineBreak name="CT_TextLineBreak">
 * ```
 */
export interface ICTTextLineBreakDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextCharacterProperties maxOccurs="1" minOccurs="0" name="rPr" type="CT_TextCharacterProperties">
     * ```
     */
    rPr?: ICTTextCharacterPropertiesDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_FullRef name="CT_FullRef">
 * ```
 */
export interface ICTFullRef {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="sqref" type="xsd:string">
     * ```
     */
    sqref?: string;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_FullRef name="CT_FullRef">
 * ```
 */
export interface ICTFullRefDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="sqref" type="xsd:string">
     * ```
     */
    sqref?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_FilteredAreaSer name="CT_FilteredAreaSer">
 * ```
 */
export interface ICTFilteredAreaSer {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AreaSer maxOccurs="1" minOccurs="1" name="ser" type="c:CT_AreaSer">
     * ```
     */
    ser?: ICTAreaSer;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_FilteredAreaSer name="CT_FilteredAreaSer">
 * ```
 */
export interface ICTFilteredAreaSerDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AreaSer maxOccurs="1" minOccurs="1" name="ser" type="c:CT_AreaSer">
     * ```
     */
    ser?: ICTAreaSerDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_FilteredBarSer name="CT_FilteredBarSer">
 * ```
 */
export interface ICTFilteredBarSer {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BarSer maxOccurs="1" minOccurs="1" name="ser" type="c:CT_BarSer">
     * ```
     */
    ser?: ICTBarSer;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_FilteredBarSer name="CT_FilteredBarSer">
 * ```
 */
export interface ICTFilteredBarSerDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BarSer maxOccurs="1" minOccurs="1" name="ser" type="c:CT_BarSer">
     * ```
     */
    ser?: ICTBarSerDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_FilteredBubbleSer name="CT_FilteredBubbleSer">
 * ```
 */
export interface ICTFilteredBubbleSer {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BubbleSer maxOccurs="1" minOccurs="1" name="ser" type="c:CT_BubbleSer">
     * ```
     */
    ser?: ICTBubbleSer;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_FilteredBubbleSer name="CT_FilteredBubbleSer">
 * ```
 */
export interface ICTFilteredBubbleSerDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BubbleSer maxOccurs="1" minOccurs="1" name="ser" type="c:CT_BubbleSer">
     * ```
     */
    ser?: ICTBubbleSerDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_FilteredLineSer name="CT_FilteredLineSer">
 * ```
 */
export interface ICTFilteredLineSer {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LineSer maxOccurs="1" minOccurs="1" name="ser" type="c:CT_LineSer">
     * ```
     */
    ser?: ICTLineSer;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_FilteredLineSer name="CT_FilteredLineSer">
 * ```
 */
export interface ICTFilteredLineSerDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LineSer maxOccurs="1" minOccurs="1" name="ser" type="c:CT_LineSer">
     * ```
     */
    ser?: ICTLineSerDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_FilteredPieSer name="CT_FilteredPieSer">
 * ```
 */
export interface ICTFilteredPieSer {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PieSer maxOccurs="1" minOccurs="1" name="ser" type="c:CT_PieSer">
     * ```
     */
    ser?: ICTPieSer;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_FilteredPieSer name="CT_FilteredPieSer">
 * ```
 */
export interface ICTFilteredPieSerDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PieSer maxOccurs="1" minOccurs="1" name="ser" type="c:CT_PieSer">
     * ```
     */
    ser?: ICTPieSerDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_FilteredRadarSer name="CT_FilteredRadarSer">
 * ```
 */
export interface ICTFilteredRadarSer {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_RadarSer maxOccurs="1" minOccurs="1" name="ser" type="c:CT_RadarSer">
     * ```
     */
    ser?: ICTRadarSer;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_FilteredRadarSer name="CT_FilteredRadarSer">
 * ```
 */
export interface ICTFilteredRadarSerDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_RadarSer maxOccurs="1" minOccurs="1" name="ser" type="c:CT_RadarSer">
     * ```
     */
    ser?: ICTRadarSerDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_FilteredScatterSer name="CT_FilteredScatterSer">
 * ```
 */
export interface ICTFilteredScatterSer {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ScatterSer maxOccurs="1" minOccurs="1" name="ser" type="c:CT_ScatterSer">
     * ```
     */
    ser?: ICTScatterSer;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_FilteredScatterSer name="CT_FilteredScatterSer">
 * ```
 */
export interface ICTFilteredScatterSerDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ScatterSer maxOccurs="1" minOccurs="1" name="ser" type="c:CT_ScatterSer">
     * ```
     */
    ser?: ICTScatterSerDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_FilteredSurfaceSer name="CT_FilteredSurfaceSer">
 * ```
 */
export interface ICTFilteredSurfaceSer {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SurfaceSer maxOccurs="1" minOccurs="1" name="ser" type="c:CT_SurfaceSer">
     * ```
     */
    ser?: ICTSurfaceSer;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_FilteredSurfaceSer name="CT_FilteredSurfaceSer">
 * ```
 */
export interface ICTFilteredSurfaceSerDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_SurfaceSer maxOccurs="1" minOccurs="1" name="ser" type="c:CT_SurfaceSer">
     * ```
     */
    ser?: ICTSurfaceSerDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_SeriesDataLabelsRange name="CT_SeriesDataLabelsRange">
 * ```
 */
export interface ICTSeriesDataLabelsRange {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="f" type="xsd:string">
     * ```
     */
    f?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StrData maxOccurs="1" minOccurs="0" name="dlblRangeCache" type="c:CT_StrData">
     * ```
     */
    dlblRangeCache?: ICTStrData;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_SeriesDataLabelsRange name="CT_SeriesDataLabelsRange">
 * ```
 */
export interface ICTSeriesDataLabelsRangeDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="f" type="xsd:string">
     * ```
     */
    f?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StrData maxOccurs="1" minOccurs="0" name="dlblRangeCache" type="c:CT_StrData">
     * ```
     */
    dlblRangeCache?: ICTStrDataDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_DataLabelFieldTable name="CT_DataLabelFieldTable">
 * ```
 */
export interface ICTDataLabelFieldTable {
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_DataLabelFieldTableEntry maxOccurs="unbounded" minOccurs="0" name="dlblFTEntry" type="CT_DataLabelFieldTableEntry">
     * ```
     */
    dlblFtEntry?: ICTDataLabelFieldTableEntry[];
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_DataLabelFieldTable name="CT_DataLabelFieldTable">
 * ```
 */
export interface ICTDataLabelFieldTableDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2012:CT_DataLabelFieldTableEntry maxOccurs="unbounded" minOccurs="0" name="dlblFTEntry" type="CT_DataLabelFieldTableEntry">
     * ```
     */
    dlblFtEntry?: ICTDataLabelFieldTableEntryDelta[];
    dlblFtEntryOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=mscs2012:CT_StyleColor name="CT_StyleColor">
 * ```
 */
export interface ICTStyleColor {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorTransform ref=dmlmain:EG_ColorTransform maxOccurs="unbounded" minOccurs="0" name="EG_ColorTransform" ref="a:EG_ColorTransform">
     * ref dmlmain:EG_ColorTransform
     * ```
     */
    egColorTransform?: IEGColorTransform[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="ST_StyleColorVal">
     * ```
     */
    val?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mscs2012:CT_StyleColor name="CT_StyleColor">
 * ```
 */
export interface ICTStyleColorDelta {
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlmain:EG_ColorTransform ref=dmlmain:EG_ColorTransform maxOccurs="unbounded" minOccurs="0" name="EG_ColorTransform" ref="a:EG_ColorTransform">
     * ref dmlmain:EG_ColorTransform
     * ```
     */
    egColorTransform?: IEGColorTransformDelta[];
    egColorTransformOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="val" type="ST_StyleColorVal">
     * ```
     */
    val?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_TrendlineType name="CT_TrendlineType">
 * ```
 */
export interface ICTTrendlineType {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_TrendlineType default="linear" name="val" type="ST_TrendlineType">
     * ```
     */
    val?: STTrendlineType;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_TrendlineType name="CT_TrendlineType">
 * ```
 */
export interface ICTTrendlineTypeDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_TrendlineType default="linear" name="val" type="ST_TrendlineType">
     * ```
     */
    val?: STTrendlineType | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Order name="CT_Order">
 * ```
 */
export interface ICTOrder {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="2" name="val" type="ST_Order">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Order name="CT_Order">
 * ```
 */
export interface ICTOrderDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="2" name="val" type="ST_Order">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Period name="CT_Period">
 * ```
 */
export interface ICTPeriod {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="2" name="val" type="ST_Period">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Period name="CT_Period">
 * ```
 */
export interface ICTPeriodDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="2" name="val" type="ST_Period">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_TrendlineLbl name="CT_TrendlineLbl">
 * ```
 */
export interface ICTTrendlineLbl {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Layout maxOccurs="1" minOccurs="0" name="layout" type="CT_Layout">
     * ```
     */
    layout?: ICTLayout;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Tx maxOccurs="1" minOccurs="0" name="tx" type="CT_Tx">
     * ```
     */
    tx?: ICTTx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumFmtDmlC maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumFmtDmlC">
     * ```
     */
    numFmt?: ICTNumFmtDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_TrendlineLbl name="CT_TrendlineLbl">
 * ```
 */
export interface ICTTrendlineLblDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Layout maxOccurs="1" minOccurs="0" name="layout" type="CT_Layout">
     * ```
     */
    layout?: ICTLayoutDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Tx maxOccurs="1" minOccurs="0" name="tx" type="CT_Tx">
     * ```
     */
    tx?: ICTTxDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumFmtDmlC maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumFmtDmlC">
     * ```
     */
    numFmt?: ICTNumFmtDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ErrDir name="CT_ErrDir">
 * ```
 */
export interface ICTErrDir {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_ErrDir name="val" type="ST_ErrDir" use="required">
     * ```
     */
    val?: STErrDir;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ErrDir name="CT_ErrDir">
 * ```
 */
export interface ICTErrDirDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_ErrDir name="val" type="ST_ErrDir" use="required">
     * ```
     */
    val?: STErrDir | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ErrBarType name="CT_ErrBarType">
 * ```
 */
export interface ICTErrBarType {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_ErrBarType default="both" name="val" type="ST_ErrBarType">
     * ```
     */
    val?: STErrBarType;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ErrBarType name="CT_ErrBarType">
 * ```
 */
export interface ICTErrBarTypeDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_ErrBarType default="both" name="val" type="ST_ErrBarType">
     * ```
     */
    val?: STErrBarType | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ErrValType name="CT_ErrValType">
 * ```
 */
export interface ICTErrValType {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_ErrValType default="fixedVal" name="val" type="ST_ErrValType">
     * ```
     */
    val?: STErrValType;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ErrValType name="CT_ErrValType">
 * ```
 */
export interface ICTErrValTypeDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_ErrValType default="fixedVal" name="val" type="ST_ErrValType">
     * ```
     */
    val?: STErrValType | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_MultiLvlStrRef name="CT_MultiLvlStrRef">
 * ```
 */
export interface ICTMultiLvlStrRef {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="f" type="xsd:string">
     * ```
     */
    f?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_MultiLvlStrData maxOccurs="1" minOccurs="0" name="multiLvlStrCache" type="CT_MultiLvlStrData">
     * ```
     */
    multiLvlStrCache?: ICTMultiLvlStrData;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_MultiLvlStrRef name="CT_MultiLvlStrRef">
 * ```
 */
export interface ICTMultiLvlStrRefDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="f" type="xsd:string">
     * ```
     */
    f?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_MultiLvlStrData maxOccurs="1" minOccurs="0" name="multiLvlStrCache" type="CT_MultiLvlStrData">
     * ```
     */
    multiLvlStrCache?: ICTMultiLvlStrDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_NumRef name="CT_NumRef">
 * ```
 */
export interface ICTNumRef {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="f" type="xsd:string">
     * ```
     */
    f?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumData maxOccurs="1" minOccurs="0" name="numCache" type="CT_NumData">
     * ```
     */
    numCache?: ICTNumData;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_NumRef name="CT_NumRef">
 * ```
 */
export interface ICTNumRefDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="f" type="xsd:string">
     * ```
     */
    f?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumData maxOccurs="1" minOccurs="0" name="numCache" type="CT_NumData">
     * ```
     */
    numCache?: ICTNumDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_NumData name="CT_NumData">
 * ```
 */
export interface ICTNumData {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="formatCode" type="s:ST_Xstring">
     * ```
     */
    formatCode?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="ptCount" type="CT_UnsignedInt">
     * ```
     */
    ptCount?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumVal maxOccurs="unbounded" minOccurs="0" name="pt" type="CT_NumVal">
     * ```
     */
    pt?: ICTNumVal[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_NumData name="CT_NumData">
 * ```
 */
export interface ICTNumDataDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="formatCode" type="s:ST_Xstring">
     * ```
     */
    formatCode?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="ptCount" type="CT_UnsignedInt">
     * ```
     */
    ptCount?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_NumVal maxOccurs="unbounded" minOccurs="0" name="pt" type="CT_NumVal">
     * ```
     */
    pt?: ICTNumValDelta[];
    ptOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_StrData name="CT_StrData">
 * ```
 */
export interface ICTStrData {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="ptCount" type="CT_UnsignedInt">
     * ```
     */
    ptCount?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StrVal maxOccurs="unbounded" minOccurs="0" name="pt" type="CT_StrVal">
     * ```
     */
    pt?: ICTStrVal[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_StrData name="CT_StrData">
 * ```
 */
export interface ICTStrDataDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="ptCount" type="CT_UnsignedInt">
     * ```
     */
    ptCount?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StrVal maxOccurs="unbounded" minOccurs="0" name="pt" type="CT_StrVal">
     * ```
     */
    pt?: ICTStrValDelta[];
    ptOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

export enum STDLblPos {
    PBDEFAULT = 0,
    bestFit = 1,
    b = 2,
    ctr = 3,
    inBase = 4,
    inEnd = 5,
    l = 6,
    outEnd = 7,
    r = 8,
    t = 9,
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_MarkerStyle name="CT_MarkerStyle">
 * ```
 */
export interface ICTMarkerStyle {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_MarkerStyleDmlC name="val" type="ST_MarkerStyleDmlC" use="required">
     * ```
     */
    val?: STMarkerStyleDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_MarkerStyle name="CT_MarkerStyle">
 * ```
 */
export interface ICTMarkerStyleDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_MarkerStyleDmlC name="val" type="ST_MarkerStyleDmlC" use="required">
     * ```
     */
    val?: STMarkerStyleDmlC | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_MarkerSize name="CT_MarkerSize">
 * ```
 */
export interface ICTMarkerSize {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="5" name="val" type="ST_MarkerSizeDmlC">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_MarkerSize name="CT_MarkerSize">
 * ```
 */
export interface ICTMarkerSizeDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="5" name="val" type="ST_MarkerSizeDmlC">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LogBase name="CT_LogBase">
 * ```
 */
export interface ICTLogBase {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="val" type="ST_LogBase" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LogBase name="CT_LogBase">
 * ```
 */
export interface ICTLogBaseDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="val" type="ST_LogBase" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Orientation name="CT_Orientation">
 * ```
 */
export interface ICTOrientation {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_OrientationDmlC default="minMax" name="val" type="ST_OrientationDmlC">
     * ```
     */
    val?: STOrientationDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Orientation name="CT_Orientation">
 * ```
 */
export interface ICTOrientationDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_OrientationDmlC default="minMax" name="val" type="ST_OrientationDmlC">
     * ```
     */
    val?: STOrientationDmlC | null;
}

export enum STAxPos {
    PBDEFAULT = 0,
    b = 1,
    l = 2,
    r = 3,
    t = 4,
}

export enum STTickMark {
    PBDEFAULT = 0,
    cross = 1,
    in = 2,
    none = 3,
    out = 4,
}

export enum STTickLblPos {
    PBDEFAULT = 0,
    high = 1,
    low = 2,
    nextTo = 3,
    none = 4,
}

export enum STCrosses {
    PBDEFAULT = 0,
    autoZero = 1,
    max = 2,
    min = 3,
}

export enum STCrossBetween {
    PBDEFAULT = 0,
    between = 1,
    midCat = 2,
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BuiltInUnit name="CT_BuiltInUnit">
 * ```
 */
export interface ICTBuiltInUnit {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_BuiltInUnit default="thousands" name="val" type="ST_BuiltInUnit">
     * ```
     */
    val?: STBuiltInUnit;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_BuiltInUnit name="CT_BuiltInUnit">
 * ```
 */
export interface ICTBuiltInUnitDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_BuiltInUnit default="thousands" name="val" type="ST_BuiltInUnit">
     * ```
     */
    val?: STBuiltInUnit | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DispUnitsLbl name="CT_DispUnitsLbl">
 * ```
 */
export interface ICTDispUnitsLbl {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Layout maxOccurs="1" minOccurs="0" name="layout" type="CT_Layout">
     * ```
     */
    layout?: ICTLayout;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Tx maxOccurs="1" minOccurs="0" name="tx" type="CT_Tx">
     * ```
     */
    tx?: ICTTx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_DispUnitsLbl name="CT_DispUnitsLbl">
 * ```
 */
export interface ICTDispUnitsLblDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Layout maxOccurs="1" minOccurs="0" name="layout" type="CT_Layout">
     * ```
     */
    layout?: ICTLayoutDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Tx maxOccurs="1" minOccurs="0" name="tx" type="CT_Tx">
     * ```
     */
    tx?: ICTTxDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
}

export enum STLblAlgn {
    PBDEFAULT = 0,
    ctr = 1,
    l = 2,
    r = 3,
}

export enum STTimeUnit {
    PBDEFAULT = 0,
    days = 1,
    months = 2,
    years = 3,
}

/**
 * ```xml
 * <xsd:complexType name=msdsp2008:CT_ShapeNonVisualMsDsp name="CT_ShapeNonVisualMsDsp">
 * ```
 */
export interface ICTShapeNonVisualMsDsp {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingProps maxOccurs="1" minOccurs="1" name="cNvPr" type="a:CT_NonVisualDrawingProps">
     * ```
     */
    cNvPr?: ICTNonVisualDrawingProps;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingShapeProps maxOccurs="1" minOccurs="1" name="cNvSpPr" type="a:CT_NonVisualDrawingShapeProps">
     * ```
     */
    cNvSpPr?: ICTNonVisualDrawingShapeProps;
}

/**
 * ```xml
 * <xsd:complexType name=msdsp2008:CT_ShapeNonVisualMsDsp name="CT_ShapeNonVisualMsDsp">
 * ```
 */
export interface ICTShapeNonVisualMsDspDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingProps maxOccurs="1" minOccurs="1" name="cNvPr" type="a:CT_NonVisualDrawingProps">
     * ```
     */
    cNvPr?: ICTNonVisualDrawingPropsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingShapeProps maxOccurs="1" minOccurs="1" name="cNvSpPr" type="a:CT_NonVisualDrawingShapeProps">
     * ```
     */
    cNvSpPr?: ICTNonVisualDrawingShapePropsDelta | null;
}

export enum STParameterId {
    PBDEFAULT = 0,
    horzAlign = 1,
    vertAlign = 2,
    chDir = 3,
    chAlign = 4,
    secChAlign = 5,
    linDir = 6,
    secLinDir = 7,
    stElem = 8,
    bendPt = 9,
    connRout = 10,
    begSty = 11,
    endSty = 12,
    dim = 13,
    rotPath = 14,
    ctrShpMap = 15,
    nodeHorzAlign = 16,
    nodeVertAlign = 17,
    fallback = 18,
    txDir = 19,
    pyraAcctPos = 20,
    pyraAcctTxMar = 21,
    txBlDir = 22,
    txAnchorHorz = 23,
    txAnchorVert = 24,
    txAnchorHorzCh = 25,
    txAnchorVertCh = 26,
    parTxLTRAlign = 27,
    parTxRTLAlign = 28,
    shpTxLTRAlignCh = 29,
    shpTxRTLAlignCh = 30,
    autoTxRot = 31,
    grDir = 32,
    flowDir = 33,
    contDir = 34,
    bkpt = 35,
    off = 36,
    hierAlign = 37,
    bkPtFixedVal = 38,
    stBulletLvl = 39,
    stAng = 40,
    spanAng = 41,
    ar = 42,
    lnSpPar = 43,
    lnSpAfParP = 44,
    lnSpCh = 45,
    lnSpAfChP = 46,
    rtShortDist = 47,
    alignTx = 48,
    pyraLvlNode = 49,
    pyraAcctBkgdNode = 50,
    pyraAcctTxNode = 51,
    srcNode = 52,
    dstNode = 53,
    begPts = 54,
    endPts = 55,
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Adj name="CT_Adj">
 * ```
 */
export interface ICTAdj {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="ST_Index1" use="required">
     * ```
     */
    idx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="val" type="xsd:double" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmldiagram:CT_Adj name="CT_Adj">
 * ```
 */
export interface ICTAdjDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="ST_Index1" use="required">
     * ```
     */
    idx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="val" type="xsd:double" use="required">
     * ```
     */
    val?: number | null;
}

export enum STConstraintType {
    PBDEFAULT = 0,
    none = 1,
    alignOff = 2,
    begMarg = 3,
    bendDist = 4,
    begPad = 5,
    b = 6,
    bMarg = 7,
    bOff = 8,
    ctrX = 9,
    ctrXOff = 10,
    ctrY = 11,
    ctrYOff = 12,
    connDist = 13,
    diam = 14,
    endMarg = 15,
    endPad = 16,
    h = 17,
    hArH = 18,
    hOff = 19,
    l = 20,
    lMarg = 21,
    lOff = 22,
    r = 23,
    rMarg = 24,
    rOff = 25,
    primFontSz = 26,
    pyraAcctRatio = 27,
    secFontSz = 28,
    sibSp = 29,
    secSibSp = 30,
    sp = 31,
    stemThick = 32,
    t = 33,
    tMarg = 34,
    tOff = 35,
    userA = 36,
    userB = 37,
    userC = 38,
    userD = 39,
    userE = 40,
    userF = 41,
    userG = 42,
    userH = 43,
    userI = 44,
    userJ = 45,
    userK = 46,
    userL = 47,
    userM = 48,
    userN = 49,
    userO = 50,
    userP = 51,
    userQ = 52,
    userR = 53,
    userS = 54,
    userT = 55,
    userU = 56,
    userV = 57,
    userW = 58,
    userX = 59,
    userY = 60,
    userZ = 61,
    w = 62,
    wArH = 63,
    wOff = 64,
}

export enum STConstraintRelationship {
    PBDEFAULT = 0,
    self = 1,
    ch = 2,
    des = 3,
}

export enum STElementType {
    PBDEFAULT = 0,
    all = 1,
    doc = 2,
    node = 3,
    norm = 4,
    nonNorm = 5,
    asst = 6,
    nonAsst = 7,
    parTrans = 8,
    pres = 9,
    sibTrans = 10,
}

export enum STBoolOperator {
    PBDEFAULT = 0,
    none = 1,
    equ = 2,
    gte = 3,
    lte = 4,
}

export enum STDirectionDmlDgm {
    PBDEFAULT = 0,
    norm = 1,
    rev = 2,
}

export enum STHierBranchStyle {
    PBDEFAULT = 0,
    l = 1,
    r = 2,
    hang = 3,
    std = 4,
    init = 5,
}

export enum STAnimOneStr {
    PBDEFAULT = 0,
    none = 1,
    one = 2,
    branch = 3,
}

export enum STAnimLvlStr {
    PBDEFAULT = 0,
    none = 1,
    lvl = 2,
    ctr = 3,
}

export enum STResizeHandlesStr {
    PBDEFAULT = 0,
    exact = 1,
    rel = 2,
}

export enum STFunctionType {
    PBDEFAULT = 0,
    cnt = 1,
    pos = 2,
    revPos = 3,
    posEven = 4,
    posOdd = 5,
    var = 6,
    depth = 7,
    maxDepth = 8,
}

export enum STFunctionOperator {
    PBDEFAULT = 0,
    equ = 1,
    neq = 2,
    gt = 3,
    lt = 4,
    gte = 5,
    lte = 6,
}

export enum STFunctionArgument {
    PBDEFAULT = 0,
    none = 1,
    orgChart = 2,
    chMax = 3,
    chPref = 4,
    bulEnabled = 5,
    dir = 6,
    hierBranch = 7,
    animOne = 8,
    animLvl = 9,
    resizeHandles = 10,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ShapeLocking name="CT_ShapeLocking">
 * ```
 */
export interface ICTShapeLocking {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noGrp" type="xsd:boolean" use="optional">
     * ```
     */
    noGrp?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noSelect" type="xsd:boolean" use="optional">
     * ```
     */
    noSelect?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noRot" type="xsd:boolean" use="optional">
     * ```
     */
    noRot?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeAspect" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeAspect?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noMove" type="xsd:boolean" use="optional">
     * ```
     */
    noMove?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noResize" type="xsd:boolean" use="optional">
     * ```
     */
    noResize?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noEditPoints" type="xsd:boolean" use="optional">
     * ```
     */
    noEditPoints?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noAdjustHandles" type="xsd:boolean" use="optional">
     * ```
     */
    noAdjustHandles?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeArrowheads" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeArrowheads?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeShapeType" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeShapeType?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noTextEdit" type="xsd:boolean" use="optional">
     * ```
     */
    noTextEdit?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ShapeLocking name="CT_ShapeLocking">
 * ```
 */
export interface ICTShapeLockingDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noGrp" type="xsd:boolean" use="optional">
     * ```
     */
    noGrp?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noSelect" type="xsd:boolean" use="optional">
     * ```
     */
    noSelect?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noRot" type="xsd:boolean" use="optional">
     * ```
     */
    noRot?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeAspect" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeAspect?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noMove" type="xsd:boolean" use="optional">
     * ```
     */
    noMove?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noResize" type="xsd:boolean" use="optional">
     * ```
     */
    noResize?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noEditPoints" type="xsd:boolean" use="optional">
     * ```
     */
    noEditPoints?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noAdjustHandles" type="xsd:boolean" use="optional">
     * ```
     */
    noAdjustHandles?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeArrowheads" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeArrowheads?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeShapeType" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeShapeType?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noTextEdit" type="xsd:boolean" use="optional">
     * ```
     */
    noTextEdit?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GraphicalObjectFrameLocking name="CT_GraphicalObjectFrameLocking">
 * ```
 */
export interface ICTGraphicalObjectFrameLocking {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noGrp" type="xsd:boolean" use="optional">
     * ```
     */
    noGrp?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noDrilldown" type="xsd:boolean" use="optional">
     * ```
     */
    noDrilldown?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noSelect" type="xsd:boolean" use="optional">
     * ```
     */
    noSelect?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeAspect" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeAspect?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noMove" type="xsd:boolean" use="optional">
     * ```
     */
    noMove?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noResize" type="xsd:boolean" use="optional">
     * ```
     */
    noResize?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_GraphicalObjectFrameLocking name="CT_GraphicalObjectFrameLocking">
 * ```
 */
export interface ICTGraphicalObjectFrameLockingDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noGrp" type="xsd:boolean" use="optional">
     * ```
     */
    noGrp?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noDrilldown" type="xsd:boolean" use="optional">
     * ```
     */
    noDrilldown?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noSelect" type="xsd:boolean" use="optional">
     * ```
     */
    noSelect?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeAspect" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeAspect?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noMove" type="xsd:boolean" use="optional">
     * ```
     */
    noMove?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noResize" type="xsd:boolean" use="optional">
     * ```
     */
    noResize?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ConnectorLocking name="CT_ConnectorLocking">
 * ```
 */
export interface ICTConnectorLocking {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noGrp" type="xsd:boolean" use="optional">
     * ```
     */
    noGrp?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noSelect" type="xsd:boolean" use="optional">
     * ```
     */
    noSelect?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noRot" type="xsd:boolean" use="optional">
     * ```
     */
    noRot?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeAspect" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeAspect?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noMove" type="xsd:boolean" use="optional">
     * ```
     */
    noMove?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noResize" type="xsd:boolean" use="optional">
     * ```
     */
    noResize?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noEditPoints" type="xsd:boolean" use="optional">
     * ```
     */
    noEditPoints?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noAdjustHandles" type="xsd:boolean" use="optional">
     * ```
     */
    noAdjustHandles?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeArrowheads" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeArrowheads?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeShapeType" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeShapeType?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ConnectorLocking name="CT_ConnectorLocking">
 * ```
 */
export interface ICTConnectorLockingDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noGrp" type="xsd:boolean" use="optional">
     * ```
     */
    noGrp?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noSelect" type="xsd:boolean" use="optional">
     * ```
     */
    noSelect?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noRot" type="xsd:boolean" use="optional">
     * ```
     */
    noRot?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeAspect" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeAspect?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noMove" type="xsd:boolean" use="optional">
     * ```
     */
    noMove?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noResize" type="xsd:boolean" use="optional">
     * ```
     */
    noResize?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noEditPoints" type="xsd:boolean" use="optional">
     * ```
     */
    noEditPoints?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noAdjustHandles" type="xsd:boolean" use="optional">
     * ```
     */
    noAdjustHandles?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeArrowheads" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeArrowheads?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeShapeType" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeShapeType?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PictureLocking name="CT_PictureLocking">
 * ```
 */
export interface ICTPictureLocking {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noGrp" type="xsd:boolean" use="optional">
     * ```
     */
    noGrp?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noSelect" type="xsd:boolean" use="optional">
     * ```
     */
    noSelect?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noRot" type="xsd:boolean" use="optional">
     * ```
     */
    noRot?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeAspect" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeAspect?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noMove" type="xsd:boolean" use="optional">
     * ```
     */
    noMove?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noResize" type="xsd:boolean" use="optional">
     * ```
     */
    noResize?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noEditPoints" type="xsd:boolean" use="optional">
     * ```
     */
    noEditPoints?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noAdjustHandles" type="xsd:boolean" use="optional">
     * ```
     */
    noAdjustHandles?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeArrowheads" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeArrowheads?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeShapeType" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeShapeType?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noCrop" type="xsd:boolean" use="optional">
     * ```
     */
    noCrop?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_PictureLocking name="CT_PictureLocking">
 * ```
 */
export interface ICTPictureLockingDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noGrp" type="xsd:boolean" use="optional">
     * ```
     */
    noGrp?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noSelect" type="xsd:boolean" use="optional">
     * ```
     */
    noSelect?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noRot" type="xsd:boolean" use="optional">
     * ```
     */
    noRot?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeAspect" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeAspect?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noMove" type="xsd:boolean" use="optional">
     * ```
     */
    noMove?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noResize" type="xsd:boolean" use="optional">
     * ```
     */
    noResize?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noEditPoints" type="xsd:boolean" use="optional">
     * ```
     */
    noEditPoints?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noAdjustHandles" type="xsd:boolean" use="optional">
     * ```
     */
    noAdjustHandles?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeArrowheads" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeArrowheads?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noChangeShapeType" type="xsd:boolean" use="optional">
     * ```
     */
    noChangeShapeType?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="noCrop" type="xsd:boolean" use="optional">
     * ```
     */
    noCrop?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_SectionSlideIdListEntry name="CT_SectionSlideIdListEntry">
 * ```
 */
export interface ICTSectionSlideIdListEntry {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="p:ST_SlideId" use="required">
     * ```
     */
    id?: string;
}

/**
 * ```xml
 * <xsd:complexType name=msppt2010:CT_SectionSlideIdListEntry name="CT_SectionSlideIdListEntry">
 * ```
 */
export interface ICTSectionSlideIdListEntryDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="p:ST_SlideId" use="required">
     * ```
     */
    id?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffect name="CT_PictureEffect">
 * ```
 */
export interface ICTPictureEffect {
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectBlur maxOccurs="1" minOccurs="1" name="artisticBlur" type="CT_PictureEffectBlur">
     * ```
     */
    artisticBlur?: ICTPictureEffectBlur;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectCement maxOccurs="1" minOccurs="1" name="artisticCement" type="CT_PictureEffectCement">
     * ```
     */
    artisticCement?: ICTPictureEffectCement;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectChalkSketch maxOccurs="1" minOccurs="1" name="artisticChalkSketch" type="CT_PictureEffectChalkSketch">
     * ```
     */
    artisticChalkSketch?: ICTPictureEffectChalkSketch;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectCrisscrossEtching maxOccurs="1" minOccurs="1" name="artisticCrisscrossEtching" type="CT_PictureEffectCrisscrossEtching">
     * ```
     */
    artisticCrisscrossEtching?: ICTPictureEffectCrisscrossEtching;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectCutout maxOccurs="1" minOccurs="1" name="artisticCutout" type="CT_PictureEffectCutout">
     * ```
     */
    artisticCutout?: ICTPictureEffectCutout;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectFilmGrain maxOccurs="1" minOccurs="1" name="artisticFilmGrain" type="CT_PictureEffectFilmGrain">
     * ```
     */
    artisticFilmGrain?: ICTPictureEffectFilmGrain;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectGlass maxOccurs="1" minOccurs="1" name="artisticGlass" type="CT_PictureEffectGlass">
     * ```
     */
    artisticGlass?: ICTPictureEffectGlass;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectGlowDiffused maxOccurs="1" minOccurs="1" name="artisticGlowDiffused" type="CT_PictureEffectGlowDiffused">
     * ```
     */
    artisticGlowDiffused?: ICTPictureEffectGlowDiffused;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectGlowEdges maxOccurs="1" minOccurs="1" name="artisticGlowEdges" type="CT_PictureEffectGlowEdges">
     * ```
     */
    artisticGlowEdges?: ICTPictureEffectGlowEdges;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectLightScreen maxOccurs="1" minOccurs="1" name="artisticLightScreen" type="CT_PictureEffectLightScreen">
     * ```
     */
    artisticLightScreen?: ICTPictureEffectLightScreen;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectLineDrawing maxOccurs="1" minOccurs="1" name="artisticLineDrawing" type="CT_PictureEffectLineDrawing">
     * ```
     */
    artisticLineDrawing?: ICTPictureEffectLineDrawing;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectMarker maxOccurs="1" minOccurs="1" name="artisticMarker" type="CT_PictureEffectMarker">
     * ```
     */
    artisticMarker?: ICTPictureEffectMarker;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectMosiaicBubbles maxOccurs="1" minOccurs="1" name="artisticMosiaicBubbles" type="CT_PictureEffectMosiaicBubbles">
     * ```
     */
    artisticMosiaicBubbles?: ICTPictureEffectMosiaicBubbles;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectPaintStrokes maxOccurs="1" minOccurs="1" name="artisticPaintStrokes" type="CT_PictureEffectPaintStrokes">
     * ```
     */
    artisticPaintStrokes?: ICTPictureEffectPaintStrokes;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectPaintBrush maxOccurs="1" minOccurs="1" name="artisticPaintBrush" type="CT_PictureEffectPaintBrush">
     * ```
     */
    artisticPaintBrush?: ICTPictureEffectPaintBrush;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectPastelsSmooth maxOccurs="1" minOccurs="1" name="artisticPastelsSmooth" type="CT_PictureEffectPastelsSmooth">
     * ```
     */
    artisticPastelsSmooth?: ICTPictureEffectPastelsSmooth;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectPencilGrayscale maxOccurs="1" minOccurs="1" name="artisticPencilGrayscale" type="CT_PictureEffectPencilGrayscale">
     * ```
     */
    artisticPencilGrayscale?: ICTPictureEffectPencilGrayscale;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectPencilSketch maxOccurs="1" minOccurs="1" name="artisticPencilSketch" type="CT_PictureEffectPencilSketch">
     * ```
     */
    artisticPencilSketch?: ICTPictureEffectPencilSketch;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectPhotocopy maxOccurs="1" minOccurs="1" name="artisticPhotocopy" type="CT_PictureEffectPhotocopy">
     * ```
     */
    artisticPhotocopy?: ICTPictureEffectPhotocopy;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectPlasticWrap maxOccurs="1" minOccurs="1" name="artisticPlasticWrap" type="CT_PictureEffectPlasticWrap">
     * ```
     */
    artisticPlasticWrap?: ICTPictureEffectPlasticWrap;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectTexturizer maxOccurs="1" minOccurs="1" name="artisticTexturizer" type="CT_PictureEffectTexturizer">
     * ```
     */
    artisticTexturizer?: ICTPictureEffectTexturizer;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectWatercolorSponge maxOccurs="1" minOccurs="1" name="artisticWatercolorSponge" type="CT_PictureEffectWatercolorSponge">
     * ```
     */
    artisticWatercolorSponge?: ICTPictureEffectWatercolorSponge;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectBackgroundRemoval maxOccurs="1" minOccurs="1" name="backgroundRemoval" type="CT_PictureEffectBackgroundRemoval">
     * ```
     */
    backgroundRemoval?: ICTPictureEffectBackgroundRemoval;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectBrightnessContrast maxOccurs="1" minOccurs="1" name="brightnessContrast" type="CT_PictureEffectBrightnessContrast">
     * ```
     */
    brightnessContrast?: ICTPictureEffectBrightnessContrast;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectColorTemperature maxOccurs="1" minOccurs="1" name="colorTemperature" type="CT_PictureEffectColorTemperature">
     * ```
     */
    colorTemperature?: ICTPictureEffectColorTemperature;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectSaturation maxOccurs="1" minOccurs="1" name="saturation" type="CT_PictureEffectSaturation">
     * ```
     */
    saturation?: ICTPictureEffectSaturation;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectSharpenSoften maxOccurs="1" minOccurs="1" name="sharpenSoften" type="CT_PictureEffectSharpenSoften">
     * ```
     */
    sharpenSoften?: ICTPictureEffectSharpenSoften;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="visible" type="xsd:boolean" use="optional">
     * ```
     */
    visible?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffect name="CT_PictureEffect">
 * ```
 */
export interface ICTPictureEffectDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectBlur maxOccurs="1" minOccurs="1" name="artisticBlur" type="CT_PictureEffectBlur">
     * ```
     */
    artisticBlur?: ICTPictureEffectBlurDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectCement maxOccurs="1" minOccurs="1" name="artisticCement" type="CT_PictureEffectCement">
     * ```
     */
    artisticCement?: ICTPictureEffectCementDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectChalkSketch maxOccurs="1" minOccurs="1" name="artisticChalkSketch" type="CT_PictureEffectChalkSketch">
     * ```
     */
    artisticChalkSketch?: ICTPictureEffectChalkSketchDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectCrisscrossEtching maxOccurs="1" minOccurs="1" name="artisticCrisscrossEtching" type="CT_PictureEffectCrisscrossEtching">
     * ```
     */
    artisticCrisscrossEtching?: ICTPictureEffectCrisscrossEtchingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectCutout maxOccurs="1" minOccurs="1" name="artisticCutout" type="CT_PictureEffectCutout">
     * ```
     */
    artisticCutout?: ICTPictureEffectCutoutDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectFilmGrain maxOccurs="1" minOccurs="1" name="artisticFilmGrain" type="CT_PictureEffectFilmGrain">
     * ```
     */
    artisticFilmGrain?: ICTPictureEffectFilmGrainDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectGlass maxOccurs="1" minOccurs="1" name="artisticGlass" type="CT_PictureEffectGlass">
     * ```
     */
    artisticGlass?: ICTPictureEffectGlassDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectGlowDiffused maxOccurs="1" minOccurs="1" name="artisticGlowDiffused" type="CT_PictureEffectGlowDiffused">
     * ```
     */
    artisticGlowDiffused?: ICTPictureEffectGlowDiffusedDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectGlowEdges maxOccurs="1" minOccurs="1" name="artisticGlowEdges" type="CT_PictureEffectGlowEdges">
     * ```
     */
    artisticGlowEdges?: ICTPictureEffectGlowEdgesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectLightScreen maxOccurs="1" minOccurs="1" name="artisticLightScreen" type="CT_PictureEffectLightScreen">
     * ```
     */
    artisticLightScreen?: ICTPictureEffectLightScreenDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectLineDrawing maxOccurs="1" minOccurs="1" name="artisticLineDrawing" type="CT_PictureEffectLineDrawing">
     * ```
     */
    artisticLineDrawing?: ICTPictureEffectLineDrawingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectMarker maxOccurs="1" minOccurs="1" name="artisticMarker" type="CT_PictureEffectMarker">
     * ```
     */
    artisticMarker?: ICTPictureEffectMarkerDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectMosiaicBubbles maxOccurs="1" minOccurs="1" name="artisticMosiaicBubbles" type="CT_PictureEffectMosiaicBubbles">
     * ```
     */
    artisticMosiaicBubbles?: ICTPictureEffectMosiaicBubblesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectPaintStrokes maxOccurs="1" minOccurs="1" name="artisticPaintStrokes" type="CT_PictureEffectPaintStrokes">
     * ```
     */
    artisticPaintStrokes?: ICTPictureEffectPaintStrokesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectPaintBrush maxOccurs="1" minOccurs="1" name="artisticPaintBrush" type="CT_PictureEffectPaintBrush">
     * ```
     */
    artisticPaintBrush?: ICTPictureEffectPaintBrushDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectPastelsSmooth maxOccurs="1" minOccurs="1" name="artisticPastelsSmooth" type="CT_PictureEffectPastelsSmooth">
     * ```
     */
    artisticPastelsSmooth?: ICTPictureEffectPastelsSmoothDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectPencilGrayscale maxOccurs="1" minOccurs="1" name="artisticPencilGrayscale" type="CT_PictureEffectPencilGrayscale">
     * ```
     */
    artisticPencilGrayscale?: ICTPictureEffectPencilGrayscaleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectPencilSketch maxOccurs="1" minOccurs="1" name="artisticPencilSketch" type="CT_PictureEffectPencilSketch">
     * ```
     */
    artisticPencilSketch?: ICTPictureEffectPencilSketchDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectPhotocopy maxOccurs="1" minOccurs="1" name="artisticPhotocopy" type="CT_PictureEffectPhotocopy">
     * ```
     */
    artisticPhotocopy?: ICTPictureEffectPhotocopyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectPlasticWrap maxOccurs="1" minOccurs="1" name="artisticPlasticWrap" type="CT_PictureEffectPlasticWrap">
     * ```
     */
    artisticPlasticWrap?: ICTPictureEffectPlasticWrapDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectTexturizer maxOccurs="1" minOccurs="1" name="artisticTexturizer" type="CT_PictureEffectTexturizer">
     * ```
     */
    artisticTexturizer?: ICTPictureEffectTexturizerDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectWatercolorSponge maxOccurs="1" minOccurs="1" name="artisticWatercolorSponge" type="CT_PictureEffectWatercolorSponge">
     * ```
     */
    artisticWatercolorSponge?: ICTPictureEffectWatercolorSpongeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectBackgroundRemoval maxOccurs="1" minOccurs="1" name="backgroundRemoval" type="CT_PictureEffectBackgroundRemoval">
     * ```
     */
    backgroundRemoval?: ICTPictureEffectBackgroundRemovalDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectBrightnessContrast maxOccurs="1" minOccurs="1" name="brightnessContrast" type="CT_PictureEffectBrightnessContrast">
     * ```
     */
    brightnessContrast?: ICTPictureEffectBrightnessContrastDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectColorTemperature maxOccurs="1" minOccurs="1" name="colorTemperature" type="CT_PictureEffectColorTemperature">
     * ```
     */
    colorTemperature?: ICTPictureEffectColorTemperatureDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectSaturation maxOccurs="1" minOccurs="1" name="saturation" type="CT_PictureEffectSaturation">
     * ```
     */
    saturation?: ICTPictureEffectSaturationDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectSharpenSoften maxOccurs="1" minOccurs="1" name="sharpenSoften" type="CT_PictureEffectSharpenSoften">
     * ```
     */
    sharpenSoften?: ICTPictureEffectSharpenSoftenDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="visible" type="xsd:boolean" use="optional">
     * ```
     */
    visible?: boolean | null;
}

/**
 * ```xml
 * <xsd:group name=msask2018:EG_LineSketchType name="EG_LineSketchType">
 * ```
 */
export interface IEGLineSketchType {
    /**
     * ```xml
     * <xsd:element typeRedirected=msask2018:CT_EmptyMsAsk name="lineSketchNone" type="CT_EmptyMsAsk">
     * ```
     */
    lineSketchNone?: ICTEmptyMsAsk;
    /**
     * ```xml
     * <xsd:element typeRedirected=msask2018:CT_EmptyMsAsk name="lineSketchCurved" type="CT_EmptyMsAsk">
     * ```
     */
    lineSketchCurved?: ICTEmptyMsAsk;
    /**
     * ```xml
     * <xsd:element typeRedirected=msask2018:CT_EmptyMsAsk name="lineSketchFreehand" type="CT_EmptyMsAsk">
     * ```
     */
    lineSketchFreehand?: ICTEmptyMsAsk;
    /**
     * ```xml
     * <xsd:element typeRedirected=msask2018:CT_EmptyMsAsk name="lineSketchScribble" type="CT_EmptyMsAsk">
     * ```
     */
    lineSketchScribble?: ICTEmptyMsAsk;
}

/**
 * ```xml
 * <xsd:group name=msask2018:EG_LineSketchType name="EG_LineSketchType">
 * ```
 */
export interface IEGLineSketchTypeDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msask2018:CT_EmptyMsAsk name="lineSketchNone" type="CT_EmptyMsAsk">
     * ```
     */
    lineSketchNone?: ICTEmptyMsAsk | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msask2018:CT_EmptyMsAsk name="lineSketchCurved" type="CT_EmptyMsAsk">
     * ```
     */
    lineSketchCurved?: ICTEmptyMsAsk | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msask2018:CT_EmptyMsAsk name="lineSketchFreehand" type="CT_EmptyMsAsk">
     * ```
     */
    lineSketchFreehand?: ICTEmptyMsAsk | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msask2018:CT_EmptyMsAsk name="lineSketchScribble" type="CT_EmptyMsAsk">
     * ```
     */
    lineSketchScribble?: ICTEmptyMsAsk | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ThemeableLineStyle name="CT_ThemeableLineStyle">
 * ```
 */
export interface ICTThemeableLineStyle {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="1" name="ln" type="CT_LineProperties">
     * ```
     */
    ln?: ICTLineProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StyleMatrixReference maxOccurs="1" minOccurs="1" name="lnRef" type="CT_StyleMatrixReference">
     * ```
     */
    lnRef?: ICTStyleMatrixReference;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_ThemeableLineStyle name="CT_ThemeableLineStyle">
 * ```
 */
export interface ICTThemeableLineStyleDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_LineProperties maxOccurs="1" minOccurs="1" name="ln" type="CT_LineProperties">
     * ```
     */
    ln?: ICTLinePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_StyleMatrixReference maxOccurs="1" minOccurs="1" name="lnRef" type="CT_StyleMatrixReference">
     * ```
     */
    lnRef?: ICTStyleMatrixReferenceDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AdjPoint2D name="CT_AdjPoint2D">
 * ```
 */
export interface ICTAdjPoint2D {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="x" type="ST_AdjCoordinate" use="required">
     * ```
     */
    x?: ICTAdjCoordinate;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="y" type="ST_AdjCoordinate" use="required">
     * ```
     */
    y?: ICTAdjCoordinate;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AdjPoint2D name="CT_AdjPoint2D">
 * ```
 */
export interface ICTAdjPoint2DDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="x" type="ST_AdjCoordinate" use="required">
     * ```
     */
    x?: ICTAdjCoordinateDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="y" type="ST_AdjCoordinate" use="required">
     * ```
     */
    y?: ICTAdjCoordinateDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AdjAngle name="CT_AdjAngle">
 * ```
 */
export interface ICTAdjAngle {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="angle" type="ST_AngleDmlA">
     * ```
     */
    angle?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="geomGuideName" type="ST_GeomGuideName">
     * ```
     */
    geomGuideName?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AdjAngle name="CT_AdjAngle">
 * ```
 */
export interface ICTAdjAngleDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="angle" type="ST_AngleDmlA">
     * ```
     */
    angle?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="geomGuideName" type="ST_GeomGuideName">
     * ```
     */
    geomGuideName?: string | null;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_Path2D name="EG_Path2D">
 * ```
 */
export interface IEGPath2D {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Path2DMoveTo maxOccurs="1" minOccurs="1" name="moveTo" type="CT_Path2DMoveTo">
     * ```
     */
    moveTo?: ICTPath2DMoveTo;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Path2DLineTo maxOccurs="1" minOccurs="1" name="lnTo" type="CT_Path2DLineTo">
     * ```
     */
    lnTo?: ICTPath2DLineTo;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Path2DArcTo maxOccurs="1" minOccurs="1" name="arcTo" type="CT_Path2DArcTo">
     * ```
     */
    arcTo?: ICTPath2DArcTo;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Path2DQuadBezierTo maxOccurs="1" minOccurs="1" name="quadBezTo" type="CT_Path2DQuadBezierTo">
     * ```
     */
    quadBezTo?: ICTPath2DQuadBezierTo;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Path2DCubicBezierTo maxOccurs="1" minOccurs="1" name="cubicBezTo" type="CT_Path2DCubicBezierTo">
     * ```
     */
    cubicBezTo?: ICTPath2DCubicBezierTo;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Path2DClose convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="close" type="CT_Path2DClose">
     * ```
     */
    close?: boolean;
}

/**
 * ```xml
 * <xsd:group name=dmlmain:EG_Path2D name="EG_Path2D">
 * ```
 */
export interface IEGPath2DDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Path2DMoveTo maxOccurs="1" minOccurs="1" name="moveTo" type="CT_Path2DMoveTo">
     * ```
     */
    moveTo?: ICTPath2DMoveToDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Path2DLineTo maxOccurs="1" minOccurs="1" name="lnTo" type="CT_Path2DLineTo">
     * ```
     */
    lnTo?: ICTPath2DLineToDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Path2DArcTo maxOccurs="1" minOccurs="1" name="arcTo" type="CT_Path2DArcTo">
     * ```
     */
    arcTo?: ICTPath2DArcToDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Path2DQuadBezierTo maxOccurs="1" minOccurs="1" name="quadBezTo" type="CT_Path2DQuadBezierTo">
     * ```
     */
    quadBezTo?: ICTPath2DQuadBezierToDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Path2DCubicBezierTo maxOccurs="1" minOccurs="1" name="cubicBezTo" type="CT_Path2DCubicBezierTo">
     * ```
     */
    cubicBezTo?: ICTPath2DCubicBezierToDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_Path2DClose convTypeRedirected=xsd:boolean convType="td:ST_EmptyCTBoolean" maxOccurs="1" minOccurs="1" name="close" type="CT_Path2DClose">
     * ```
     */
    close?: boolean | null;
}

export enum STPathFillMode {
    PBDEFAULT = 0,
    none = 1,
    norm = 2,
    lighten = 3,
    lightenLess = 4,
    darken = 5,
    darkenLess = 6,
}

/**
 * ```xml
 * <xsd:complexType name=dmlpicture:CT_PictureNonVisualDmlPct name="CT_PictureNonVisualDmlPct">
 * ```
 */
export interface ICTPictureNonVisualDmlPct {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingProps maxOccurs="1" minOccurs="1" name="cNvPr" type="a:CT_NonVisualDrawingProps">
     * ```
     */
    cNvPr?: ICTNonVisualDrawingProps;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualPictureProperties maxOccurs="1" minOccurs="1" name="cNvPicPr" type="a:CT_NonVisualPictureProperties">
     * ```
     */
    cNvPicPr?: ICTNonVisualPictureProperties;
}

/**
 * ```xml
 * <xsd:complexType name=dmlpicture:CT_PictureNonVisualDmlPct name="CT_PictureNonVisualDmlPct">
 * ```
 */
export interface ICTPictureNonVisualDmlPctDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualDrawingProps maxOccurs="1" minOccurs="1" name="cNvPr" type="a:CT_NonVisualDrawingProps">
     * ```
     */
    cNvPr?: ICTNonVisualDrawingPropsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_NonVisualPictureProperties maxOccurs="1" minOccurs="1" name="cNvPicPr" type="a:CT_NonVisualPictureProperties">
     * ```
     */
    cNvPicPr?: ICTNonVisualPicturePropertiesDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ChartSpace name="CT_ChartSpace">
 * ```
 */
export interface ICTChartSpace {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="date1904" type="CT_BooleanDmlC">
     * ```
     */
    date1904?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TextLanguageID maxOccurs="1" minOccurs="0" name="lang" type="CT_TextLanguageID">
     * ```
     */
    lang?: ICTTextLanguageID;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="roundedCorners" type="CT_BooleanDmlC">
     * ```
     */
    roundedCorners?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StyleDmlC maxOccurs="1" minOccurs="0" name="style" type="CT_StyleDmlC">
     * ```
     */
    style?: ICTStyleDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorMapping maxOccurs="1" minOccurs="0" name="clrMapOvr" type="a:CT_ColorMapping">
     * ```
     */
    clrMapOvr?: ICTColorMapping;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PivotSource maxOccurs="1" minOccurs="0" name="pivotSource" type="CT_PivotSource">
     * ```
     */
    pivotSource?: ICTPivotSource;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Protection maxOccurs="1" minOccurs="0" name="protection" type="CT_Protection">
     * ```
     */
    protection?: ICTProtection;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Chart maxOccurs="1" minOccurs="1" name="chart" type="CT_Chart">
     * ```
     */
    chart?: ICTChart;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExternalData maxOccurs="1" minOccurs="0" name="externalData" type="CT_ExternalData">
     * ```
     */
    externalData?: ICTExternalData;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PrintSettings maxOccurs="1" minOccurs="0" name="printSettings" type="CT_PrintSettings">
     * ```
     */
    printSettings?: ICTPrintSettings;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_RelId maxOccurs="1" minOccurs="0" name="userShapes" type="CT_RelId">
     * ```
     */
    userShapes?: ICTRelId;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:chartId name="chartId" ref="td:chartId" type="s:ST_String">
     * ```
     */
    chartId?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2007:CT_StyleMsC2007 ref=msc2007:style aliasName=c14Style aliasName="c14Style" maxOccurs="1" minOccurs="0" name="style" ref="c14:style" type="CT_StyleMsC2007">
     * ```
     */
    c14Style?: ICTStyleMsC2007;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_ChartSpace name="CT_ChartSpace">
 * ```
 */
export interface ICTChartSpaceDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="date1904" type="CT_BooleanDmlC">
     * ```
     */
    date1904?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_TextLanguageID maxOccurs="1" minOccurs="0" name="lang" type="CT_TextLanguageID">
     * ```
     */
    lang?: ICTTextLanguageIDDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="roundedCorners" type="CT_BooleanDmlC">
     * ```
     */
    roundedCorners?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StyleDmlC maxOccurs="1" minOccurs="0" name="style" type="CT_StyleDmlC">
     * ```
     */
    style?: ICTStyleDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorMapping maxOccurs="1" minOccurs="0" name="clrMapOvr" type="a:CT_ColorMapping">
     * ```
     */
    clrMapOvr?: ICTColorMappingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PivotSource maxOccurs="1" minOccurs="0" name="pivotSource" type="CT_PivotSource">
     * ```
     */
    pivotSource?: ICTPivotSourceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Protection maxOccurs="1" minOccurs="0" name="protection" type="CT_Protection">
     * ```
     */
    protection?: ICTProtectionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Chart maxOccurs="1" minOccurs="1" name="chart" type="CT_Chart">
     * ```
     */
    chart?: ICTChartDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExternalData maxOccurs="1" minOccurs="0" name="externalData" type="CT_ExternalData">
     * ```
     */
    externalData?: ICTExternalDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PrintSettings maxOccurs="1" minOccurs="0" name="printSettings" type="CT_PrintSettings">
     * ```
     */
    printSettings?: ICTPrintSettingsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_RelId maxOccurs="1" minOccurs="0" name="userShapes" type="CT_RelId">
     * ```
     */
    userShapes?: ICTRelIdDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:chartId name="chartId" ref="td:chartId" type="s:ST_String">
     * ```
     */
    chartId?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=msc2007:CT_StyleMsC2007 ref=msc2007:style aliasName=c14Style aliasName="c14Style" maxOccurs="1" minOccurs="0" name="style" ref="c14:style" type="CT_StyleMsC2007">
     * ```
     */
    c14Style?: ICTStyleMsC2007Delta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ChartSpaceMsChartEx name="CT_ChartSpaceMsChartEx">
 * ```
 */
export interface ICTChartSpaceMsChartEx {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ChartData maxOccurs="1" minOccurs="1" name="chartData" type="CT_ChartData">
     * ```
     */
    chartData?: ICTChartData;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ChartMsChartEx maxOccurs="1" minOccurs="1" name="chart" type="CT_ChartMsChartEx">
     * ```
     */
    chart?: ICTChartMsChartEx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorMapping maxOccurs="1" minOccurs="0" name="clrMapOvr" type="a:CT_ColorMapping">
     * ```
     */
    clrMapOvr?: ICTColorMapping;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_FormatOverrides maxOccurs="1" minOccurs="0" name="fmtOvrs" type="CT_FormatOverrides">
     * ```
     */
    fmtOvrs?: ICTFormatOverrides;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_PrintSettingsMsChartEx maxOccurs="1" minOccurs="0" name="printSettings" type="CT_PrintSettingsMsChartEx">
     * ```
     */
    printSettings?: ICTPrintSettingsMsChartEx;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:chartId name="chartId" ref="td:chartId" type="s:ST_String">
     * ```
     */
    chartId?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ChartSpaceMsChartEx name="CT_ChartSpaceMsChartEx">
 * ```
 */
export interface ICTChartSpaceMsChartExDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ChartData maxOccurs="1" minOccurs="1" name="chartData" type="CT_ChartData">
     * ```
     */
    chartData?: ICTChartDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ChartMsChartEx maxOccurs="1" minOccurs="1" name="chart" type="CT_ChartMsChartEx">
     * ```
     */
    chart?: ICTChartMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ColorMapping maxOccurs="1" minOccurs="0" name="clrMapOvr" type="a:CT_ColorMapping">
     * ```
     */
    clrMapOvr?: ICTColorMappingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_FormatOverrides maxOccurs="1" minOccurs="0" name="fmtOvrs" type="CT_FormatOverrides">
     * ```
     */
    fmtOvrs?: ICTFormatOverridesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_PrintSettingsMsChartEx maxOccurs="1" minOccurs="0" name="printSettings" type="CT_PrintSettingsMsChartEx">
     * ```
     */
    printSettings?: ICTPrintSettingsMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=tencentdocumentword:chartId name="chartId" ref="td:chartId" type="s:ST_String">
     * ```
     */
    chartId?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableGrid name="CT_TableGrid">
 * ```
 */
export interface ICTTableGrid {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableCol maxOccurs="unbounded" minOccurs="0" name="gridCol" type="CT_TableCol">
     * ```
     */
    gridCol?: ICTTableCol[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableGrid name="CT_TableGrid">
 * ```
 */
export interface ICTTableGridDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableCol maxOccurs="unbounded" minOccurs="0" name="gridCol" type="CT_TableCol">
     * ```
     */
    gridCol?: ICTTableColDelta[];
    gridColOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableRow name="CT_TableRow">
 * ```
 */
export interface ICTTableRow {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableCell maxOccurs="unbounded" minOccurs="0" name="tc" type="CT_TableCell">
     * ```
     */
    tc?: ICTTableCell[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="h" type="ST_Coordinate" use="required">
     * ```
     */
    h?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableRow name="CT_TableRow">
 * ```
 */
export interface ICTTableRowDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableCell maxOccurs="unbounded" minOccurs="0" name="tc" type="CT_TableCell">
     * ```
     */
    tc?: ICTTableCellDelta[];
    tcOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="h" type="ST_Coordinate" use="required">
     * ```
     */
    h?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=tencentdocumentcommon:CT_TotalSeriesList name="CT_TotalSeriesList">
 * ```
 */
export interface ICTTotalSeriesList {
    /**
     * ```xml
     * <xsd:element typeRedirected=tencentdocumentcommon:CT_TotalSeriesItem maxOccurs="unbounded" minOccurs="0" name="totalSeriesItem" type="CT_TotalSeriesItem">
     * ```
     */
    totalSeriesItem?: ICTTotalSeriesItem[];
}

/**
 * ```xml
 * <xsd:complexType name=tencentdocumentcommon:CT_TotalSeriesList name="CT_TotalSeriesList">
 * ```
 */
export interface ICTTotalSeriesListDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=tencentdocumentcommon:CT_TotalSeriesItem maxOccurs="unbounded" minOccurs="0" name="totalSeriesItem" type="CT_TotalSeriesItem">
     * ```
     */
    totalSeriesItem?: ICTTotalSeriesItemDelta[];
    totalSeriesItemOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_BrushProperty name="CT_BrushProperty">
 * ```
 */
export interface ICTBrushProperty {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="ST_BrushPropertyName" use="required">
     * ```
     */
    name?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="value" type="ST_BrushPropertyValue" use="required">
     * ```
     */
    value?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="units" type="ST_Units" use="optional">
     * ```
     */
    units?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_BrushProperty name="CT_BrushProperty">
 * ```
 */
export interface ICTBrushPropertyDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="ST_BrushPropertyName" use="required">
     * ```
     */
    name?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="value" type="ST_BrushPropertyValue" use="required">
     * ```
     */
    value?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="units" type="ST_Units" use="optional">
     * ```
     */
    units?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_SampleRate name="CT_SampleRate">
 * ```
 */
export interface ICTSampleRate {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="uniform" type="xsd:boolean" use="optional">
     * ```
     */
    uniform?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="value" type="xsd:decimal" use="required">
     * ```
     */
    value?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_SampleRate name="CT_SampleRate">
 * ```
 */
export interface ICTSampleRateDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="uniform" type="xsd:boolean" use="optional">
     * ```
     */
    uniform?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="value" type="xsd:decimal" use="required">
     * ```
     */
    value?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Latency name="CT_Latency">
 * ```
 */
export interface ICTLatency {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="value" type="xsd:decimal" use="required">
     * ```
     */
    value?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Latency name="CT_Latency">
 * ```
 */
export interface ICTLatencyDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="value" type="xsd:decimal" use="required">
     * ```
     */
    value?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_ActiveArea name="CT_ActiveArea">
 * ```
 */
export interface ICTActiveArea {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="size" type="xsd:string">
     * ```
     */
    size?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="height" type="xsd:decimal">
     * ```
     */
    height?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="width" type="xsd:decimal">
     * ```
     */
    width?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="units" type="ST_Units">
     * ```
     */
    units?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_ActiveArea name="CT_ActiveArea">
 * ```
 */
export interface ICTActiveAreaDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="size" type="xsd:string">
     * ```
     */
    size?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="height" type="xsd:decimal">
     * ```
     */
    height?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="width" type="xsd:decimal">
     * ```
     */
    width?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="units" type="ST_Units">
     * ```
     */
    units?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_SourceProperty name="CT_SourceProperty">
 * ```
 */
export interface ICTSourceProperty {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string" use="required">
     * ```
     */
    name?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="value" type="xsd:decimal" use="required">
     * ```
     */
    value?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="units" type="ST_Units">
     * ```
     */
    units?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_SourceProperty name="CT_SourceProperty">
 * ```
 */
export interface ICTSourcePropertyDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string" use="required">
     * ```
     */
    name?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="value" type="xsd:decimal" use="required">
     * ```
     */
    value?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="units" type="ST_Units">
     * ```
     */
    units?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_ChannelProperties name="CT_ChannelProperties">
 * ```
 */
export interface ICTChannelProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_ChannelProperty maxOccurs="unbounded" minOccurs="0" name="channelProperty" type="CT_ChannelProperty">
     * ```
     */
    channelProperty?: ICTChannelProperty[];
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_ChannelProperties name="CT_ChannelProperties">
 * ```
 */
export interface ICTChannelPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_ChannelProperty maxOccurs="unbounded" minOccurs="0" name="channelProperty" type="CT_ChannelProperty">
     * ```
     */
    channelProperty?: ICTChannelPropertyDelta[];
    channelPropertyOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Bind name="CT_Bind">
 * ```
 */
export interface ICTBind {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="source" type="xsd:string" use="optional">
     * ```
     */
    source?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="target" type="xsd:string" use="optional">
     * ```
     */
    target?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="column" type="xsd:string" use="optional">
     * ```
     */
    column?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="variable" type="xsd:string" use="optional">
     * ```
     */
    variable?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Bind name="CT_Bind">
 * ```
 */
export interface ICTBindDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="source" type="xsd:string" use="optional">
     * ```
     */
    source?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="target" type="xsd:string" use="optional">
     * ```
     */
    target?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="column" type="xsd:string" use="optional">
     * ```
     */
    column?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="variable" type="xsd:string" use="optional">
     * ```
     */
    variable?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_TableInkML name="CT_TableInkML">
 * ```
 */
export interface ICTTableInkML {
    /**
     * ```xml
     * <xsd:extension base=xsd:string base="ST_DecimalTableOrBooleanTable">
     * ```
     */
    characterData?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string" use="optional">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=inkml:ST_Apply default="absolute" name="apply" type="ST_Apply" use="optional">
     * ```
     */
    apply?: STApply;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=inkml:ST_Interpolation default="linear" name="interpolation" type="ST_Interpolation" use="optional">
     * ```
     */
    interpolation?: STInterpolation;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_TableInkML name="CT_TableInkML">
 * ```
 */
export interface ICTTableInkMLDelta {
    /**
     * ```xml
     * <xsd:extension base=xsd:string base="ST_DecimalTableOrBooleanTable">
     * ```
     */
    characterData?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string" use="optional">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=inkml:ST_Apply default="absolute" name="apply" type="ST_Apply" use="optional">
     * ```
     */
    apply?: STApply | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=inkml:ST_Interpolation default="linear" name="interpolation" type="ST_Interpolation" use="optional">
     * ```
     */
    interpolation?: STInterpolation | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Affine name="CT_Affine">
 * ```
 */
export interface ICTAffine {
    /**
     * ```xml
     * <xsd:extension base=xsd:string base="ST_DecimalTable">
     * ```
     */
    characterData?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string" use="optional">
     * ```
     */
    id?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Affine name="CT_Affine">
 * ```
 */
export interface ICTAffineDelta {
    /**
     * ```xml
     * <xsd:extension base=xsd:string base="ST_DecimalTable">
     * ```
     */
    characterData?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string" use="optional">
     * ```
     */
    id?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=mathml:CT_EmptyMathML name="CT_EmptyMathML">
 * ```
 */
export interface ICTEmptyMathML {}

export enum STMappingType {
    PBDEFAULT = 0,
    identity = 1,
    product = 2,
    table = 3,
    affine = 4,
    mathml = 5,
    unknown = 6,
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Channel name="CT_Channel">
 * ```
 */
export interface ICTChannel {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Mapping minOccurs="0" name="mapping" type="CT_Mapping">
     * ```
     */
    mapping?: ICTMapping;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="ST_ChannelName" use="required">
     * ```
     */
    name?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=inkml:ST_TypeInkML default="decimal" name="type" type="ST_TypeInkML" use="optional">
     * ```
     */
    type?: STTypeInkML;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string aliasName=defaultKeyword aliasName="defaultKeyword" default="0" name="default" type="ST_Default" use="optional">
     * ```
     */
    defaultKeyword?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="min" type="xsd:decimal">
     * ```
     */
    min?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="max" type="xsd:decimal">
     * ```
     */
    max?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=inkml:ST_OrientationInkML default="+ve" name="orientation" type="ST_OrientationInkML" use="optional">
     * ```
     */
    orientation?: STOrientationInkML;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="respectTo" type="xsd:anyURI" use="optional">
     * ```
     */
    respectTo?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="units" type="ST_Units">
     * ```
     */
    units?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_Channel name="CT_Channel">
 * ```
 */
export interface ICTChannelDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Mapping minOccurs="0" name="mapping" type="CT_Mapping">
     * ```
     */
    mapping?: ICTMappingDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=xml:id convType="s:ST_IdString" name="id" ref="xml:id" type="xsd:string">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="ST_ChannelName" use="required">
     * ```
     */
    name?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=inkml:ST_TypeInkML default="decimal" name="type" type="ST_TypeInkML" use="optional">
     * ```
     */
    type?: STTypeInkML | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string aliasName=defaultKeyword aliasName="defaultKeyword" default="0" name="default" type="ST_Default" use="optional">
     * ```
     */
    defaultKeyword?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="min" type="xsd:decimal">
     * ```
     */
    min?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="max" type="xsd:decimal">
     * ```
     */
    max?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=inkml:ST_OrientationInkML default="+ve" name="orientation" type="ST_OrientationInkML" use="optional">
     * ```
     */
    orientation?: STOrientationInkML | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="respectTo" type="xsd:anyURI" use="optional">
     * ```
     */
    respectTo?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="units" type="ST_Units">
     * ```
     */
    units?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_IntermittentChannels name="CT_IntermittentChannels">
 * ```
 */
export interface ICTIntermittentChannels {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Channel maxOccurs="unbounded" name="channel" type="CT_Channel">
     * ```
     */
    channel?: ICTChannel[];
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_IntermittentChannels name="CT_IntermittentChannels">
 * ```
 */
export interface ICTIntermittentChannelsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=inkml:CT_Channel maxOccurs="unbounded" name="channel" type="CT_Channel">
     * ```
     */
    channel?: ICTChannelDelta[];
    channelOp?: IArrayOp;
}

export enum STTLTriggerRuntimeNode {
    PBDEFAULT = 0,
    first = 1,
    last = 2,
    all = 3,
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLOleChartTargetElement name="CT_TLOleChartTargetElement">
 * ```
 */
export interface ICTTLOleChartTargetElement {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLChartSubelementType name="type" type="ST_TLChartSubelementType" use="required">
     * ```
     */
    type?: STTLChartSubelementType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="0" name="lvl" type="xsd:unsignedInt" use="optional">
     * ```
     */
    lvl?: number;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLOleChartTargetElement name="CT_TLOleChartTargetElement">
 * ```
 */
export interface ICTTLOleChartTargetElementDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=pml:ST_TLChartSubelementType name="type" type="ST_TLChartSubelementType" use="required">
     * ```
     */
    type?: STTLChartSubelementType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="0" name="lvl" type="xsd:unsignedInt" use="optional">
     * ```
     */
    lvl?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTextTargetElement name="CT_TLTextTargetElement">
 * ```
 */
export interface ICTTLTextTargetElement {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_IndexRange name="charRg" type="CT_IndexRange">
     * ```
     */
    charRg?: ICTIndexRange;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_IndexRange name="pRg" type="CT_IndexRange">
     * ```
     */
    pRg?: ICTIndexRange;
}

/**
 * ```xml
 * <xsd:complexType name=pml:CT_TLTextTargetElement name="CT_TLTextTargetElement">
 * ```
 */
export interface ICTTLTextTargetElementDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_IndexRange name="charRg" type="CT_IndexRange">
     * ```
     */
    charRg?: ICTIndexRangeDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=pml:CT_IndexRange name="pRg" type="CT_IndexRange">
     * ```
     */
    pRg?: ICTIndexRangeDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AnimationElementChoice name="CT_AnimationElementChoice">
 * ```
 */
export interface ICTAnimationElementChoice {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AnimationDgmElement name="dgm" type="CT_AnimationDgmElement">
     * ```
     */
    dgm?: ICTAnimationDgmElement;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AnimationChartElement name="chart" type="CT_AnimationChartElement">
     * ```
     */
    chart?: ICTAnimationChartElement;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AnimationElementChoice name="CT_AnimationElementChoice">
 * ```
 */
export interface ICTAnimationElementChoiceDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AnimationDgmElement name="dgm" type="CT_AnimationDgmElement">
     * ```
     */
    dgm?: ICTAnimationDgmElementDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AnimationChartElement name="chart" type="CT_AnimationChartElement">
     * ```
     */
    chart?: ICTAnimationChartElementDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LayoutTarget name="CT_LayoutTarget">
 * ```
 */
export interface ICTLayoutTarget {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_LayoutTarget default="outer" name="val" type="ST_LayoutTarget">
     * ```
     */
    val?: STLayoutTarget;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LayoutTarget name="CT_LayoutTarget">
 * ```
 */
export interface ICTLayoutTargetDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_LayoutTarget default="outer" name="val" type="ST_LayoutTarget">
     * ```
     */
    val?: STLayoutTarget | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LayoutMode name="CT_LayoutMode">
 * ```
 */
export interface ICTLayoutMode {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_LayoutMode default="factor" name="val" type="ST_LayoutMode">
     * ```
     */
    val?: STLayoutMode;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LayoutMode name="CT_LayoutMode">
 * ```
 */
export interface ICTLayoutModeDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlchart:ST_LayoutMode default="factor" name="val" type="ST_LayoutMode">
     * ```
     */
    val?: STLayoutMode | null;
}

export enum STPictureFormat {
    PBDEFAULT = 0,
    stretch = 1,
    stack = 2,
    stackScale = 3,
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_DataLabelFieldTableEntry name="CT_DataLabelFieldTableEntry">
 * ```
 */
export interface ICTDataLabelFieldTableEntry {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="txfldGUID" type="xsd:string">
     * ```
     */
    txfldGuid?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="f" type="xsd:string">
     * ```
     */
    f?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StrData maxOccurs="1" minOccurs="0" name="dlblFieldTableCache" type="c:CT_StrData">
     * ```
     */
    dlblFieldTableCache?: ICTStrData;
}

/**
 * ```xml
 * <xsd:complexType name=msc2012:CT_DataLabelFieldTableEntry name="CT_DataLabelFieldTableEntry">
 * ```
 */
export interface ICTDataLabelFieldTableEntryDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="txfldGUID" type="xsd:string">
     * ```
     */
    txfldGuid?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="f" type="xsd:string">
     * ```
     */
    f?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StrData maxOccurs="1" minOccurs="0" name="dlblFieldTableCache" type="c:CT_StrData">
     * ```
     */
    dlblFieldTableCache?: ICTStrDataDelta | null;
}

export enum STTrendlineType {
    PBDEFAULT = 0,
    exp = 1,
    linear = 2,
    log = 3,
    movingAvg = 4,
    poly = 5,
    power = 6,
}

export enum STErrDir {
    PBDEFAULT = 0,
    x = 1,
    y = 2,
}

export enum STErrBarType {
    PBDEFAULT = 0,
    both = 1,
    minus = 2,
    plus = 3,
}

export enum STErrValType {
    PBDEFAULT = 0,
    cust = 1,
    fixedVal = 2,
    percentage = 3,
    stdDev = 4,
    stdErr = 5,
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_MultiLvlStrData name="CT_MultiLvlStrData">
 * ```
 */
export interface ICTMultiLvlStrData {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="ptCount" type="CT_UnsignedInt">
     * ```
     */
    ptCount?: ICTUnsignedInt;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LvlDmlC maxOccurs="unbounded" minOccurs="0" name="lvl" type="CT_LvlDmlC">
     * ```
     */
    lvl?: ICTLvlDmlC[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_MultiLvlStrData name="CT_MultiLvlStrData">
 * ```
 */
export interface ICTMultiLvlStrDataDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_UnsignedInt maxOccurs="1" minOccurs="0" name="ptCount" type="CT_UnsignedInt">
     * ```
     */
    ptCount?: ICTUnsignedIntDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_LvlDmlC maxOccurs="unbounded" minOccurs="0" name="lvl" type="CT_LvlDmlC">
     * ```
     */
    lvl?: ICTLvlDmlCDelta[];
    lvlOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_NumVal name="CT_NumVal">
 * ```
 */
export interface ICTNumVal {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="v" type="s:ST_Xstring">
     * ```
     */
    v?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt" use="required">
     * ```
     */
    idx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="formatCode" type="s:ST_Xstring" use="optional">
     * ```
     */
    formatCode?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_NumVal name="CT_NumVal">
 * ```
 */
export interface ICTNumValDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="v" type="s:ST_Xstring">
     * ```
     */
    v?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt" use="required">
     * ```
     */
    idx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="formatCode" type="s:ST_Xstring" use="optional">
     * ```
     */
    formatCode?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_StrVal name="CT_StrVal">
 * ```
 */
export interface ICTStrVal {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="v" type="s:ST_Xstring">
     * ```
     */
    v?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt" use="required">
     * ```
     */
    idx?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_StrVal name="CT_StrVal">
 * ```
 */
export interface ICTStrValDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="1" name="v" type="s:ST_Xstring">
     * ```
     */
    v?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt" use="required">
     * ```
     */
    idx?: number | null;
}

export enum STMarkerStyleDmlC {
    PBDEFAULT = 0,
    circle = 1,
    dash = 2,
    diamond = 3,
    dot = 4,
    none = 5,
    picture = 6,
    plus = 7,
    square = 8,
    star = 9,
    triangle = 10,
    x = 11,
    auto = 12,
}

export enum STOrientationDmlC {
    PBDEFAULT = 0,
    maxMin = 1,
    minMax = 2,
}

export enum STBuiltInUnit {
    PBDEFAULT = 0,
    hundreds = 1,
    thousands = 2,
    tenThousands = 3,
    hundredThousands = 4,
    millions = 5,
    tenMillions = 6,
    hundredMillions = 7,
    billions = 8,
    trillions = 9,
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectBlur name="CT_PictureEffectBlur">
 * ```
 */
export interface ICTPictureEffectBlur {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="10" name="radius" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    radius?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectBlur name="CT_PictureEffectBlur">
 * ```
 */
export interface ICTPictureEffectBlurDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="10" name="radius" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    radius?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectCement name="CT_PictureEffectCement">
 * ```
 */
export interface ICTPictureEffectCement {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="24" name="crackSpacing" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    crackSpacing?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectCement name="CT_PictureEffectCement">
 * ```
 */
export interface ICTPictureEffectCementDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="24" name="crackSpacing" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    crackSpacing?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectChalkSketch name="CT_PictureEffectChalkSketch">
 * ```
 */
export interface ICTPictureEffectChalkSketch {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="pressure" type="ST_ArtisticEffectParam4" use="optional">
     * ```
     */
    pressure?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectChalkSketch name="CT_PictureEffectChalkSketch">
 * ```
 */
export interface ICTPictureEffectChalkSketchDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="pressure" type="ST_ArtisticEffectParam4" use="optional">
     * ```
     */
    pressure?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectCrisscrossEtching name="CT_PictureEffectCrisscrossEtching">
 * ```
 */
export interface ICTPictureEffectCrisscrossEtching {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="75" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="30" name="pressure" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    pressure?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectCrisscrossEtching name="CT_PictureEffectCrisscrossEtching">
 * ```
 */
export interface ICTPictureEffectCrisscrossEtchingDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="75" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="30" name="pressure" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    pressure?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectCutout name="CT_PictureEffectCutout">
 * ```
 */
export interface ICTPictureEffectCutout {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="2" name="numberOfShades" type="ST_ArtisticEffectParam6" use="optional">
     * ```
     */
    numberOfShades?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectCutout name="CT_PictureEffectCutout">
 * ```
 */
export interface ICTPictureEffectCutoutDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="2" name="numberOfShades" type="ST_ArtisticEffectParam6" use="optional">
     * ```
     */
    numberOfShades?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectFilmGrain name="CT_PictureEffectFilmGrain">
 * ```
 */
export interface ICTPictureEffectFilmGrain {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="40" name="grainSize" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    grainSize?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectFilmGrain name="CT_PictureEffectFilmGrain">
 * ```
 */
export interface ICTPictureEffectFilmGrainDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="40" name="grainSize" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    grainSize?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectGlass name="CT_PictureEffectGlass">
 * ```
 */
export interface ICTPictureEffectGlass {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="34" name="scaling" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    scaling?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectGlass name="CT_PictureEffectGlass">
 * ```
 */
export interface ICTPictureEffectGlassDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="34" name="scaling" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    scaling?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectGlowDiffused name="CT_PictureEffectGlowDiffused">
 * ```
 */
export interface ICTPictureEffectGlowDiffused {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="5" name="intensity" type="ST_ArtisticEffectParam10" use="optional">
     * ```
     */
    intensity?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectGlowDiffused name="CT_PictureEffectGlowDiffused">
 * ```
 */
export interface ICTPictureEffectGlowDiffusedDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="5" name="intensity" type="ST_ArtisticEffectParam10" use="optional">
     * ```
     */
    intensity?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectGlowEdges name="CT_PictureEffectGlowEdges">
 * ```
 */
export interface ICTPictureEffectGlowEdges {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="15" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="3" name="smoothness" type="ST_ArtisticEffectParam10" use="optional">
     * ```
     */
    smoothness?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectGlowEdges name="CT_PictureEffectGlowEdges">
 * ```
 */
export interface ICTPictureEffectGlowEdgesDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="15" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="3" name="smoothness" type="ST_ArtisticEffectParam10" use="optional">
     * ```
     */
    smoothness?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectLightScreen name="CT_PictureEffectLightScreen">
 * ```
 */
export interface ICTPictureEffectLightScreen {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="4" name="gridSize" type="ST_ArtisticEffectParam10" use="optional">
     * ```
     */
    gridSize?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectLightScreen name="CT_PictureEffectLightScreen">
 * ```
 */
export interface ICTPictureEffectLightScreenDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="4" name="gridSize" type="ST_ArtisticEffectParam10" use="optional">
     * ```
     */
    gridSize?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectLineDrawing name="CT_PictureEffectLineDrawing">
 * ```
 */
export interface ICTPictureEffectLineDrawing {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="25" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="pencilSize" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    pencilSize?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectLineDrawing name="CT_PictureEffectLineDrawing">
 * ```
 */
export interface ICTPictureEffectLineDrawingDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="25" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="0" name="pencilSize" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    pencilSize?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectMarker name="CT_PictureEffectMarker">
 * ```
 */
export interface ICTPictureEffectMarker {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="97" name="size" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    size?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectMarker name="CT_PictureEffectMarker">
 * ```
 */
export interface ICTPictureEffectMarkerDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="97" name="size" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    size?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectMosiaicBubbles name="CT_PictureEffectMosiaicBubbles">
 * ```
 */
export interface ICTPictureEffectMosiaicBubbles {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="14" name="pressure" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    pressure?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectMosiaicBubbles name="CT_PictureEffectMosiaicBubbles">
 * ```
 */
export interface ICTPictureEffectMosiaicBubblesDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="14" name="pressure" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    pressure?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectPaintStrokes name="CT_PictureEffectPaintStrokes">
 * ```
 */
export interface ICTPictureEffectPaintStrokes {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="5" name="intensity" type="ST_ArtisticEffectParam10" use="optional">
     * ```
     */
    intensity?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectPaintStrokes name="CT_PictureEffectPaintStrokes">
 * ```
 */
export interface ICTPictureEffectPaintStrokesDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="5" name="intensity" type="ST_ArtisticEffectParam10" use="optional">
     * ```
     */
    intensity?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectPaintBrush name="CT_PictureEffectPaintBrush">
 * ```
 */
export interface ICTPictureEffectPaintBrush {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="2" name="brushSize" type="ST_ArtisticEffectParam10" use="optional">
     * ```
     */
    brushSize?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectPaintBrush name="CT_PictureEffectPaintBrush">
 * ```
 */
export interface ICTPictureEffectPaintBrushDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="2" name="brushSize" type="ST_ArtisticEffectParam10" use="optional">
     * ```
     */
    brushSize?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectPastelsSmooth name="CT_PictureEffectPastelsSmooth">
 * ```
 */
export interface ICTPictureEffectPastelsSmooth {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="34" name="scaling" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    scaling?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectPastelsSmooth name="CT_PictureEffectPastelsSmooth">
 * ```
 */
export interface ICTPictureEffectPastelsSmoothDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="34" name="scaling" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    scaling?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectPencilGrayscale name="CT_PictureEffectPencilGrayscale">
 * ```
 */
export interface ICTPictureEffectPencilGrayscale {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="27" name="pencilSize" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    pencilSize?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectPencilGrayscale name="CT_PictureEffectPencilGrayscale">
 * ```
 */
export interface ICTPictureEffectPencilGrayscaleDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="27" name="pencilSize" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    pencilSize?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectPencilSketch name="CT_PictureEffectPencilSketch">
 * ```
 */
export interface ICTPictureEffectPencilSketch {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="22" name="pressure" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    pressure?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectPencilSketch name="CT_PictureEffectPencilSketch">
 * ```
 */
export interface ICTPictureEffectPencilSketchDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="22" name="pressure" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    pressure?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectPhotocopy name="CT_PictureEffectPhotocopy">
 * ```
 */
export interface ICTPictureEffectPhotocopy {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="30" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="3" name="detail" type="ST_ArtisticEffectParam10" use="optional">
     * ```
     */
    detail?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectPhotocopy name="CT_PictureEffectPhotocopy">
 * ```
 */
export interface ICTPictureEffectPhotocopyDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="30" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="3" name="detail" type="ST_ArtisticEffectParam10" use="optional">
     * ```
     */
    detail?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectPlasticWrap name="CT_PictureEffectPlasticWrap">
 * ```
 */
export interface ICTPictureEffectPlasticWrap {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="5" name="smoothness" type="ST_ArtisticEffectParam10" use="optional">
     * ```
     */
    smoothness?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectPlasticWrap name="CT_PictureEffectPlasticWrap">
 * ```
 */
export interface ICTPictureEffectPlasticWrapDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="5" name="smoothness" type="ST_ArtisticEffectParam10" use="optional">
     * ```
     */
    smoothness?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectTexturizer name="CT_PictureEffectTexturizer">
 * ```
 */
export interface ICTPictureEffectTexturizer {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="34" name="scaling" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    scaling?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectTexturizer name="CT_PictureEffectTexturizer">
 * ```
 */
export interface ICTPictureEffectTexturizerDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="34" name="scaling" type="ST_ArtisticEffectParam100" use="optional">
     * ```
     */
    scaling?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectWatercolorSponge name="CT_PictureEffectWatercolorSponge">
 * ```
 */
export interface ICTPictureEffectWatercolorSponge {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="2" name="brushSize" type="ST_ArtisticEffectParam10" use="optional">
     * ```
     */
    brushSize?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectWatercolorSponge name="CT_PictureEffectWatercolorSponge">
 * ```
 */
export interface ICTPictureEffectWatercolorSpongeDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" default="0" name="trans" type="a:ST_PositiveFixedPercentageDmlA" use="optional">
     * ```
     */
    trans?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="2" name="brushSize" type="ST_ArtisticEffectParam10" use="optional">
     * ```
     */
    brushSize?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectBackgroundRemoval name="CT_PictureEffectBackgroundRemoval">
 * ```
 */
export interface ICTPictureEffectBackgroundRemoval {
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectBackgroundRemovalForegroundMark maxOccurs="unbounded" minOccurs="0" name="foregroundMark" type="CT_PictureEffectBackgroundRemovalForegroundMark">
     * ```
     */
    foregroundMark?: ICTPictureEffectBackgroundRemovalForegroundMark[];
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectBackgroundRemovalBackgroundMark maxOccurs="unbounded" minOccurs="0" name="backgroundMark" type="CT_PictureEffectBackgroundRemovalBackgroundMark">
     * ```
     */
    backgroundMark?: ICTPictureEffectBackgroundRemovalBackgroundMark[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="t" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    t?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="b" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    b?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="l" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    l?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="r" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    r?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectBackgroundRemoval name="CT_PictureEffectBackgroundRemoval">
 * ```
 */
export interface ICTPictureEffectBackgroundRemovalDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectBackgroundRemovalForegroundMark maxOccurs="unbounded" minOccurs="0" name="foregroundMark" type="CT_PictureEffectBackgroundRemovalForegroundMark">
     * ```
     */
    foregroundMark?: ICTPictureEffectBackgroundRemovalForegroundMarkDelta[];
    foregroundMarkOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=msdm2010:CT_PictureEffectBackgroundRemovalBackgroundMark maxOccurs="unbounded" minOccurs="0" name="backgroundMark" type="CT_PictureEffectBackgroundRemovalBackgroundMark">
     * ```
     */
    backgroundMark?: ICTPictureEffectBackgroundRemovalBackgroundMarkDelta[];
    backgroundMarkOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="t" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    t?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="b" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    b?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="l" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    l?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="r" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    r?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectBrightnessContrast name="CT_PictureEffectBrightnessContrast">
 * ```
 */
export interface ICTPictureEffectBrightnessContrast {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_FixedPercentageDecimal" default="0" name="bright" type="a:ST_FixedPercentageDmlA" use="optional">
     * ```
     */
    bright?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_FixedPercentageDecimal" default="0" name="contrast" type="a:ST_FixedPercentageDmlA" use="optional">
     * ```
     */
    contrast?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectBrightnessContrast name="CT_PictureEffectBrightnessContrast">
 * ```
 */
export interface ICTPictureEffectBrightnessContrastDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_FixedPercentageDecimal" default="0" name="bright" type="a:ST_FixedPercentageDmlA" use="optional">
     * ```
     */
    bright?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_FixedPercentageDecimal" default="0" name="contrast" type="a:ST_FixedPercentageDmlA" use="optional">
     * ```
     */
    contrast?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectColorTemperature name="CT_PictureEffectColorTemperature">
 * ```
 */
export interface ICTPictureEffectColorTemperature {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="6500" name="colorTemp" type="ST_ColorTemperature" use="optional">
     * ```
     */
    colorTemp?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectColorTemperature name="CT_PictureEffectColorTemperature">
 * ```
 */
export interface ICTPictureEffectColorTemperatureDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="6500" name="colorTemp" type="ST_ColorTemperature" use="optional">
     * ```
     */
    colorTemp?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectSaturation name="CT_PictureEffectSaturation">
 * ```
 */
export interface ICTPictureEffectSaturation {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="100" name="sat" type="ST_SaturationAmount" use="optional">
     * ```
     */
    sat?: string;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectSaturation name="CT_PictureEffectSaturation">
 * ```
 */
export interface ICTPictureEffectSaturationDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string default="100" name="sat" type="ST_SaturationAmount" use="optional">
     * ```
     */
    sat?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectSharpenSoften name="CT_PictureEffectSharpenSoften">
 * ```
 */
export interface ICTPictureEffectSharpenSoften {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_FixedPercentageDecimal" default="0" name="amount" type="a:ST_FixedPercentageDmlA" use="optional">
     * ```
     */
    amount?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectSharpenSoften name="CT_PictureEffectSharpenSoften">
 * ```
 */
export interface ICTPictureEffectSharpenSoftenDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_FixedPercentageDecimal" default="0" name="amount" type="a:ST_FixedPercentageDmlA" use="optional">
     * ```
     */
    amount?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msask2018:CT_EmptyMsAsk name="CT_EmptyMsAsk">
 * ```
 */
export interface ICTEmptyMsAsk {}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Path2DMoveTo name="CT_Path2DMoveTo">
 * ```
 */
export interface ICTPath2DMoveTo {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AdjPoint2D maxOccurs="1" minOccurs="1" name="pt" type="CT_AdjPoint2D">
     * ```
     */
    pt?: ICTAdjPoint2D;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Path2DMoveTo name="CT_Path2DMoveTo">
 * ```
 */
export interface ICTPath2DMoveToDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AdjPoint2D maxOccurs="1" minOccurs="1" name="pt" type="CT_AdjPoint2D">
     * ```
     */
    pt?: ICTAdjPoint2DDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Path2DLineTo name="CT_Path2DLineTo">
 * ```
 */
export interface ICTPath2DLineTo {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AdjPoint2D maxOccurs="1" minOccurs="1" name="pt" type="CT_AdjPoint2D">
     * ```
     */
    pt?: ICTAdjPoint2D;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Path2DLineTo name="CT_Path2DLineTo">
 * ```
 */
export interface ICTPath2DLineToDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AdjPoint2D maxOccurs="1" minOccurs="1" name="pt" type="CT_AdjPoint2D">
     * ```
     */
    pt?: ICTAdjPoint2DDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Path2DArcTo name="CT_Path2DArcTo">
 * ```
 */
export interface ICTPath2DArcTo {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="wR" type="ST_AdjCoordinate" use="required">
     * ```
     */
    wR?: ICTAdjCoordinate;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="hR" type="ST_AdjCoordinate" use="required">
     * ```
     */
    hR?: ICTAdjCoordinate;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjAngle convType="CT_AdjAngle" name="stAng" type="ST_AdjAngle" use="required">
     * ```
     */
    stAng?: ICTAdjAngle;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjAngle convType="CT_AdjAngle" name="swAng" type="ST_AdjAngle" use="required">
     * ```
     */
    swAng?: ICTAdjAngle;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Path2DArcTo name="CT_Path2DArcTo">
 * ```
 */
export interface ICTPath2DArcToDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="wR" type="ST_AdjCoordinate" use="required">
     * ```
     */
    wR?: ICTAdjCoordinateDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjCoordinate convType="CT_AdjCoordinate" name="hR" type="ST_AdjCoordinate" use="required">
     * ```
     */
    hR?: ICTAdjCoordinateDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjAngle convType="CT_AdjAngle" name="stAng" type="ST_AdjAngle" use="required">
     * ```
     */
    stAng?: ICTAdjAngleDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=dmlmain:CT_AdjAngle convType="CT_AdjAngle" name="swAng" type="ST_AdjAngle" use="required">
     * ```
     */
    swAng?: ICTAdjAngleDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Path2DQuadBezierTo name="CT_Path2DQuadBezierTo">
 * ```
 */
export interface ICTPath2DQuadBezierTo {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AdjPoint2D maxOccurs="2" minOccurs="2" name="pt" type="CT_AdjPoint2D">
     * ```
     */
    pt?: ICTAdjPoint2D[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Path2DQuadBezierTo name="CT_Path2DQuadBezierTo">
 * ```
 */
export interface ICTPath2DQuadBezierToDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AdjPoint2D maxOccurs="2" minOccurs="2" name="pt" type="CT_AdjPoint2D">
     * ```
     */
    pt?: ICTAdjPoint2DDelta[];
    ptOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Path2DCubicBezierTo name="CT_Path2DCubicBezierTo">
 * ```
 */
export interface ICTPath2DCubicBezierTo {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AdjPoint2D maxOccurs="3" minOccurs="3" name="pt" type="CT_AdjPoint2D">
     * ```
     */
    pt?: ICTAdjPoint2D[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_Path2DCubicBezierTo name="CT_Path2DCubicBezierTo">
 * ```
 */
export interface ICTPath2DCubicBezierToDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_AdjPoint2D maxOccurs="3" minOccurs="3" name="pt" type="CT_AdjPoint2D">
     * ```
     */
    pt?: ICTAdjPoint2DDelta[];
    ptOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Chart name="CT_Chart">
 * ```
 */
export interface ICTChart {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Title maxOccurs="1" minOccurs="0" name="title" type="CT_Title">
     * ```
     */
    title?: ICTTitle;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="autoTitleDeleted" type="CT_BooleanDmlC">
     * ```
     */
    autoTitleDeleted?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PivotFmts maxOccurs="1" minOccurs="0" name="pivotFmts" type="CT_PivotFmts">
     * ```
     */
    pivotFmts?: ICTPivotFmts;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_View3D maxOccurs="1" minOccurs="0" name="view3D" type="CT_View3D">
     * ```
     */
    view3D?: ICTView3D;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Surface maxOccurs="1" minOccurs="0" name="floor" type="CT_Surface">
     * ```
     */
    floor?: ICTSurface;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Surface maxOccurs="1" minOccurs="0" name="sideWall" type="CT_Surface">
     * ```
     */
    sideWall?: ICTSurface;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Surface maxOccurs="1" minOccurs="0" name="backWall" type="CT_Surface">
     * ```
     */
    backWall?: ICTSurface;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PlotArea maxOccurs="1" minOccurs="1" name="plotArea" type="CT_PlotArea">
     * ```
     */
    plotArea?: ICTPlotArea;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Legend maxOccurs="1" minOccurs="0" name="legend" type="CT_Legend">
     * ```
     */
    legend?: ICTLegend;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="plotVisOnly" type="CT_BooleanDmlC">
     * ```
     */
    plotVisOnly?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DispBlanksAs maxOccurs="1" minOccurs="0" name="dispBlanksAs" type="CT_DispBlanksAs">
     * ```
     */
    dispBlanksAs?: ICTDispBlanksAs;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showDLblsOverMax" type="CT_BooleanDmlC">
     * ```
     */
    showDLblsOverMax?: ICTBooleanDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_Chart name="CT_Chart">
 * ```
 */
export interface ICTChartDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Title maxOccurs="1" minOccurs="0" name="title" type="CT_Title">
     * ```
     */
    title?: ICTTitleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="autoTitleDeleted" type="CT_BooleanDmlC">
     * ```
     */
    autoTitleDeleted?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PivotFmts maxOccurs="1" minOccurs="0" name="pivotFmts" type="CT_PivotFmts">
     * ```
     */
    pivotFmts?: ICTPivotFmtsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_View3D maxOccurs="1" minOccurs="0" name="view3D" type="CT_View3D">
     * ```
     */
    view3D?: ICTView3DDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Surface maxOccurs="1" minOccurs="0" name="floor" type="CT_Surface">
     * ```
     */
    floor?: ICTSurfaceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Surface maxOccurs="1" minOccurs="0" name="sideWall" type="CT_Surface">
     * ```
     */
    sideWall?: ICTSurfaceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Surface maxOccurs="1" minOccurs="0" name="backWall" type="CT_Surface">
     * ```
     */
    backWall?: ICTSurfaceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_PlotArea maxOccurs="1" minOccurs="1" name="plotArea" type="CT_PlotArea">
     * ```
     */
    plotArea?: ICTPlotAreaDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Legend maxOccurs="1" minOccurs="0" name="legend" type="CT_Legend">
     * ```
     */
    legend?: ICTLegendDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="plotVisOnly" type="CT_BooleanDmlC">
     * ```
     */
    plotVisOnly?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DispBlanksAs maxOccurs="1" minOccurs="0" name="dispBlanksAs" type="CT_DispBlanksAs">
     * ```
     */
    dispBlanksAs?: ICTDispBlanksAsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_BooleanDmlC maxOccurs="1" minOccurs="0" name="showDLblsOverMax" type="CT_BooleanDmlC">
     * ```
     */
    showDLblsOverMax?: ICTBooleanDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ChartData name="CT_ChartData">
 * ```
 */
export interface ICTChartData {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExternalDataMsChartEx maxOccurs="1" minOccurs="0" name="externalData" type="CT_ExternalDataMsChartEx">
     * ```
     */
    externalData?: ICTExternalDataMsChartEx;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Data maxOccurs="unbounded" minOccurs="1" name="data" type="CT_Data">
     * ```
     */
    data?: ICTData[];
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ChartData name="CT_ChartData">
 * ```
 */
export interface ICTChartDataDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExternalDataMsChartEx maxOccurs="1" minOccurs="0" name="externalData" type="CT_ExternalDataMsChartEx">
     * ```
     */
    externalData?: ICTExternalDataMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Data maxOccurs="unbounded" minOccurs="1" name="data" type="CT_Data">
     * ```
     */
    data?: ICTDataDelta[];
    dataOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ChartMsChartEx name="CT_ChartMsChartEx">
 * ```
 */
export interface ICTChartMsChartEx {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ChartTitle maxOccurs="1" minOccurs="0" name="title" type="CT_ChartTitle">
     * ```
     */
    title?: ICTChartTitle;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_PlotAreaMsChartEx maxOccurs="1" minOccurs="1" name="plotArea" type="CT_PlotAreaMsChartEx">
     * ```
     */
    plotArea?: ICTPlotAreaMsChartEx;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_LegendMsChartEx maxOccurs="1" minOccurs="0" name="legend" type="CT_LegendMsChartEx">
     * ```
     */
    legend?: ICTLegendMsChartEx;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ChartMsChartEx name="CT_ChartMsChartEx">
 * ```
 */
export interface ICTChartMsChartExDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ChartTitle maxOccurs="1" minOccurs="0" name="title" type="CT_ChartTitle">
     * ```
     */
    title?: ICTChartTitleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_PlotAreaMsChartEx maxOccurs="1" minOccurs="1" name="plotArea" type="CT_PlotAreaMsChartEx">
     * ```
     */
    plotArea?: ICTPlotAreaMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_LegendMsChartEx maxOccurs="1" minOccurs="0" name="legend" type="CT_LegendMsChartEx">
     * ```
     */
    legend?: ICTLegendMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_FormatOverrides name="CT_FormatOverrides">
 * ```
 */
export interface ICTFormatOverrides {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_FormatOverride maxOccurs="unbounded" minOccurs="0" name="fmtOvr" type="CT_FormatOverride">
     * ```
     */
    fmtOvr?: ICTFormatOverride[];
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_FormatOverrides name="CT_FormatOverrides">
 * ```
 */
export interface ICTFormatOverridesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_FormatOverride maxOccurs="unbounded" minOccurs="0" name="fmtOvr" type="CT_FormatOverride">
     * ```
     */
    fmtOvr?: ICTFormatOverrideDelta[];
    fmtOvrOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_PrintSettingsMsChartEx name="CT_PrintSettingsMsChartEx">
 * ```
 */
export interface ICTPrintSettingsMsChartEx {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_HeaderFooterMsChartEx maxOccurs="1" minOccurs="0" name="headerFooter" type="CT_HeaderFooterMsChartEx">
     * ```
     */
    headerFooter?: ICTHeaderFooterMsChartEx;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_PageMarginsMsChartEx maxOccurs="1" minOccurs="0" name="pageMargins" type="CT_PageMarginsMsChartEx">
     * ```
     */
    pageMargins?: ICTPageMarginsMsChartEx;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_PageSetupMsChartEx maxOccurs="1" minOccurs="0" name="pageSetup" type="CT_PageSetupMsChartEx">
     * ```
     */
    pageSetup?: ICTPageSetupMsChartEx;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_PrintSettingsMsChartEx name="CT_PrintSettingsMsChartEx">
 * ```
 */
export interface ICTPrintSettingsMsChartExDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_HeaderFooterMsChartEx maxOccurs="1" minOccurs="0" name="headerFooter" type="CT_HeaderFooterMsChartEx">
     * ```
     */
    headerFooter?: ICTHeaderFooterMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_PageMarginsMsChartEx maxOccurs="1" minOccurs="0" name="pageMargins" type="CT_PageMarginsMsChartEx">
     * ```
     */
    pageMargins?: ICTPageMarginsMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_PageSetupMsChartEx maxOccurs="1" minOccurs="0" name="pageSetup" type="CT_PageSetupMsChartEx">
     * ```
     */
    pageSetup?: ICTPageSetupMsChartExDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ExtensionListMsChartEx name="CT_ExtensionListMsChartEx">
 * ```
 */
export interface ICTExtensionListMsChartEx {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionMsChartEx maxOccurs="unbounded" minOccurs="0" name="ext" type="CT_ExtensionMsChartEx">
     * ```
     */
    ext?: ICTExtensionMsChartEx[];
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ExtensionListMsChartEx name="CT_ExtensionListMsChartEx">
 * ```
 */
export interface ICTExtensionListMsChartExDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionMsChartEx maxOccurs="unbounded" minOccurs="0" name="ext" type="CT_ExtensionMsChartEx">
     * ```
     */
    ext?: ICTExtensionMsChartExDelta[];
    extOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableCol name="CT_TableCol">
 * ```
 */
export interface ICTTableCol {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="w" type="ST_Coordinate" use="required">
     * ```
     */
    w?: number;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableCol name="CT_TableCol">
 * ```
 */
export interface ICTTableColDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:double convType="td:ST_CoordinatePrecise" name="w" type="ST_Coordinate" use="required">
     * ```
     */
    w?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableCell name="CT_TableCell">
 * ```
 */
export interface ICTTableCell {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txBody" type="CT_TextBody">
     * ```
     */
    txBody?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableCellProperties maxOccurs="1" minOccurs="0" name="tcPr" type="CT_TableCellProperties">
     * ```
     */
    tcPr?: ICTTableCellProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionList;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="1" name="rowSpan" type="xsd:int" use="optional">
     * ```
     */
    rowSpan?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="1" name="gridSpan" type="xsd:int" use="optional">
     * ```
     */
    gridSpan?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="hMerge" type="xsd:boolean" use="optional">
     * ```
     */
    hMerge?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="vMerge" type="xsd:boolean" use="optional">
     * ```
     */
    vMerge?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="xsd:string" use="optional">
     * ```
     */
    id?: string;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_TableCell name="CT_TableCell">
 * ```
 */
export interface ICTTableCellDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txBody" type="CT_TextBody">
     * ```
     */
    txBody?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TableCellProperties maxOccurs="1" minOccurs="0" name="tcPr" type="CT_TableCellProperties">
     * ```
     */
    tcPr?: ICTTableCellPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_OfficeArtExtensionList maxOccurs="1" minOccurs="0" name="extLst" type="CT_OfficeArtExtensionList">
     * ```
     */
    extLst?: ICTOfficeArtExtensionListDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="1" name="rowSpan" type="xsd:int" use="optional">
     * ```
     */
    rowSpan?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="1" name="gridSpan" type="xsd:int" use="optional">
     * ```
     */
    gridSpan?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="hMerge" type="xsd:boolean" use="optional">
     * ```
     */
    hMerge?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="vMerge" type="xsd:boolean" use="optional">
     * ```
     */
    vMerge?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="xsd:string" use="optional">
     * ```
     */
    id?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=tencentdocumentcommon:CT_TotalSeriesItem name="CT_TotalSeriesItem">
 * ```
 */
export interface ICTTotalSeriesItem {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="legendRangeString" type="xsd:string">
     * ```
     */
    legendRangeString?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="valueRangeString" type="xsd:string">
     * ```
     */
    valueRangeString?: string;
}

/**
 * ```xml
 * <xsd:complexType name=tencentdocumentcommon:CT_TotalSeriesItem name="CT_TotalSeriesItem">
 * ```
 */
export interface ICTTotalSeriesItemDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="legendRangeString" type="xsd:string">
     * ```
     */
    legendRangeString?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="valueRangeString" type="xsd:string">
     * ```
     */
    valueRangeString?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_ChannelProperty name="CT_ChannelProperty">
 * ```
 */
export interface ICTChannelProperty {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="channel" type="ST_ChannelName" use="required">
     * ```
     */
    channel?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="ST_ChannelPropertyName" use="required">
     * ```
     */
    name?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="value" type="xsd:decimal" use="required">
     * ```
     */
    value?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="units" type="ST_Units">
     * ```
     */
    units?: string;
}

/**
 * ```xml
 * <xsd:complexType name=inkml:CT_ChannelProperty name="CT_ChannelProperty">
 * ```
 */
export interface ICTChannelPropertyDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="channel" type="ST_ChannelName" use="required">
     * ```
     */
    channel?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="ST_ChannelPropertyName" use="required">
     * ```
     */
    name?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="value" type="xsd:decimal" use="required">
     * ```
     */
    value?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="units" type="ST_Units">
     * ```
     */
    units?: string | null;
}

export enum STApply {
    PBDEFAULT = 0,
    absolute = 1,
    relative = 2,
}

export enum STInterpolation {
    PBDEFAULT = 0,
    floor = 1,
    middle = 2,
    ceiling = 3,
    linear = 4,
    cubic = 5,
}

export enum STTypeInkML {
    PBDEFAULT = 0,
    integer = 1,
    decimal = 2,
    double = 3,
    boolean = 4,
}

export enum STOrientationInkML {
    PBDEFAULT = 0,
    POSITIVE_VE = 1,
    NEGATIVE_VE = 2,
}

export enum STTLChartSubelementType {
    PBDEFAULT = 0,
    gridLegend = 1,
    series = 2,
    category = 3,
    ptInSeries = 4,
    ptInCategory = 5,
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AnimationDgmElement name="CT_AnimationDgmElement">
 * ```
 */
export interface ICTAnimationDgmElement {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" default="{00000000-0000-0000-0000-000000000000}" name="id" type="s:ST_Guid" use="optional">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_DgmBuildStep default="sp" name="bldStep" type="ST_DgmBuildStep" use="optional">
     * ```
     */
    bldStep?: STDgmBuildStep;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AnimationDgmElement name="CT_AnimationDgmElement">
 * ```
 */
export interface ICTAnimationDgmElementDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:string convType="s:ST_IdString" default="{00000000-0000-0000-0000-000000000000}" name="id" type="s:ST_Guid" use="optional">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_DgmBuildStep default="sp" name="bldStep" type="ST_DgmBuildStep" use="optional">
     * ```
     */
    bldStep?: STDgmBuildStep | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AnimationChartElement name="CT_AnimationChartElement">
 * ```
 */
export interface ICTAnimationChartElement {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="-1" name="seriesIdx" type="xsd:int" use="optional">
     * ```
     */
    seriesIdx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="-1" name="categoryIdx" type="xsd:int" use="optional">
     * ```
     */
    categoryIdx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ChartBuildStep name="bldStep" type="ST_ChartBuildStep" use="required">
     * ```
     */
    bldStep?: STChartBuildStep;
}

/**
 * ```xml
 * <xsd:complexType name=dmlmain:CT_AnimationChartElement name="CT_AnimationChartElement">
 * ```
 */
export interface ICTAnimationChartElementDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="-1" name="seriesIdx" type="xsd:int" use="optional">
     * ```
     */
    seriesIdx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="-1" name="categoryIdx" type="xsd:int" use="optional">
     * ```
     */
    categoryIdx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=dmlmain:ST_ChartBuildStep name="bldStep" type="ST_ChartBuildStep" use="required">
     * ```
     */
    bldStep?: STChartBuildStep | null;
}

export enum STLayoutTarget {
    PBDEFAULT = 0,
    inner = 1,
    outer = 2,
}

export enum STLayoutMode {
    PBDEFAULT = 0,
    edge = 1,
    factor = 2,
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LvlDmlC name="CT_LvlDmlC">
 * ```
 */
export interface ICTLvlDmlC {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StrVal maxOccurs="unbounded" minOccurs="0" name="pt" type="CT_StrVal">
     * ```
     */
    pt?: ICTStrVal[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_LvlDmlC name="CT_LvlDmlC">
 * ```
 */
export interface ICTLvlDmlCDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_StrVal maxOccurs="unbounded" minOccurs="0" name="pt" type="CT_StrVal">
     * ```
     */
    pt?: ICTStrValDelta[];
    ptOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectBackgroundRemovalForegroundMark name="CT_PictureEffectBackgroundRemovalForegroundMark">
 * ```
 */
export interface ICTPictureEffectBackgroundRemovalForegroundMark {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="x1" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    x1?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="y1" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    y1?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="x2" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    x2?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="y2" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    y2?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectBackgroundRemovalForegroundMark name="CT_PictureEffectBackgroundRemovalForegroundMark">
 * ```
 */
export interface ICTPictureEffectBackgroundRemovalForegroundMarkDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="x1" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    x1?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="y1" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    y1?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="x2" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    x2?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="y2" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    y2?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectBackgroundRemovalBackgroundMark name="CT_PictureEffectBackgroundRemovalBackgroundMark">
 * ```
 */
export interface ICTPictureEffectBackgroundRemovalBackgroundMark {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="x1" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    x1?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="y1" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    y1?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="x2" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    x2?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="y2" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    y2?: number;
}

/**
 * ```xml
 * <xsd:complexType name=msdm2010:CT_PictureEffectBackgroundRemovalBackgroundMark name="CT_PictureEffectBackgroundRemovalBackgroundMark">
 * ```
 */
export interface ICTPictureEffectBackgroundRemovalBackgroundMarkDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="x1" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    x1?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="y1" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    y1?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="x2" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    x2?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string convTypeRedirected=xsd:integer convType="a:ST_PositiveFixedPercentageDecimal" name="y2" type="a:ST_PositiveFixedPercentageDmlA" use="required">
     * ```
     */
    y2?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PlotArea name="CT_PlotArea">
 * ```
 */
export interface ICTPlotArea {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Layout maxOccurs="1" minOccurs="0" name="layout" type="CT_Layout">
     * ```
     */
    layout?: ICTLayout;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DTable maxOccurs="1" minOccurs="0" name="dTable" type="CT_DTable">
     * ```
     */
    dTable?: ICTDTable;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlC;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxDataSource maxOccurs="1" minOccurs="0" name="cat" skipIO="true" type="CT_AxDataSource">
     * ```
     */
    cat?: ICTAxDataSource;
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlchart:EG_Plot ref=dmlchart:EG_Plot maxOccurs="unbounded" minOccurs="1" name="EG_Plot" ref="EG_Plot">
     * ref dmlchart:EG_Plot
     * ```
     */
    egPlot?: IEGPlot[];
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlchart:EG_Axis ref=dmlchart:EG_Axis maxOccurs="unbounded" minOccurs="0" name="EG_Axis" ref="EG_Axis">
     * ref dmlchart:EG_Axis
     * ```
     */
    egAxis?: IEGAxis[];
}

/**
 * ```xml
 * <xsd:complexType name=dmlchart:CT_PlotArea name="CT_PlotArea">
 * ```
 */
export interface ICTPlotAreaDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_Layout maxOccurs="1" minOccurs="0" name="layout" type="CT_Layout">
     * ```
     */
    layout?: ICTLayoutDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_DTable maxOccurs="1" minOccurs="0" name="dTable" type="CT_DTable">
     * ```
     */
    dTable?: ICTDTableDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_ExtensionListDmlC maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListDmlC">
     * ```
     */
    extLst?: ICTExtensionListDmlCDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlchart:CT_AxDataSource maxOccurs="1" minOccurs="0" name="cat" skipIO="true" type="CT_AxDataSource">
     * ```
     */
    cat?: ICTAxDataSourceDelta | null;
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlchart:EG_Plot ref=dmlchart:EG_Plot maxOccurs="unbounded" minOccurs="1" name="EG_Plot" ref="EG_Plot">
     * ref dmlchart:EG_Plot
     * ```
     */
    egPlot?: IEGPlotDelta[];
    egPlotOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:group typeRedirected=dmlchart:EG_Axis ref=dmlchart:EG_Axis maxOccurs="unbounded" minOccurs="0" name="EG_Axis" ref="EG_Axis">
     * ref dmlchart:EG_Axis
     * ```
     */
    egAxis?: IEGAxisDelta[];
    egAxisOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ExternalDataMsChartEx name="CT_ExternalDataMsChartEx">
 * ```
 */
export interface ICTExternalDataMsChartEx {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="autoUpdate" type="xsd:boolean" use="optional">
     * ```
     */
    autoUpdate?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ExternalDataMsChartEx name="CT_ExternalDataMsChartEx">
 * ```
 */
export interface ICTExternalDataMsChartExDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string ref=sharedrelationshipreference:id convType="s:ST_IdString" name="id" ref="r:id" type="ST_RelationshipId" use="required">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="autoUpdate" type="xsd:boolean" use="optional">
     * ```
     */
    autoUpdate?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Data name="CT_Data">
 * ```
 */
export interface ICTData {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_NumericDimension maxOccurs="1" minOccurs="1" name="numDim" type="CT_NumericDimension">
     * ```
     */
    numDim?: ICTNumericDimension[];
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_StringDimension maxOccurs="1" minOccurs="1" name="strDim" type="CT_StringDimension">
     * ```
     */
    strDim?: ICTStringDimension[];
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="ST_DataId" use="required">
     * ```
     */
    id?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Data name="CT_Data">
 * ```
 */
export interface ICTDataDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_NumericDimension maxOccurs="1" minOccurs="1" name="numDim" type="CT_NumericDimension">
     * ```
     */
    numDim?: ICTNumericDimensionDelta[];
    numDimOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_StringDimension maxOccurs="1" minOccurs="1" name="strDim" type="CT_StringDimension">
     * ```
     */
    strDim?: ICTStringDimensionDelta[];
    strDimOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="ST_DataId" use="required">
     * ```
     */
    id?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ChartTitle name="CT_ChartTitle">
 * ```
 */
export interface ICTChartTitle {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_TextMsChartEx maxOccurs="1" minOccurs="0" name="tx" type="CT_TextMsChartEx">
     * ```
     */
    tx?: ICTTextMsChartEx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_SidePos default="t" name="pos" type="ST_SidePos" use="optional">
     * ```
     */
    pos?: STSidePos;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_PosAlign default="ctr" name="align" type="ST_PosAlign" use="optional">
     * ```
     */
    align?: STPosAlign;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="0" name="overlay" type="xsd:boolean" use="optional">
     * ```
     */
    overlay?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ChartTitle name="CT_ChartTitle">
 * ```
 */
export interface ICTChartTitleDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_TextMsChartEx maxOccurs="1" minOccurs="0" name="tx" type="CT_TextMsChartEx">
     * ```
     */
    tx?: ICTTextMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_SidePos default="t" name="pos" type="ST_SidePos" use="optional">
     * ```
     */
    pos?: STSidePos | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_PosAlign default="ctr" name="align" type="ST_PosAlign" use="optional">
     * ```
     */
    align?: STPosAlign | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="0" name="overlay" type="xsd:boolean" use="optional">
     * ```
     */
    overlay?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_PlotAreaMsChartEx name="CT_PlotAreaMsChartEx">
 * ```
 */
export interface ICTPlotAreaMsChartEx {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_PlotAreaRegion maxOccurs="1" minOccurs="1" name="plotAreaRegion" type="CT_PlotAreaRegion">
     * ```
     */
    plotAreaRegion?: ICTPlotAreaRegion;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Axis maxOccurs="unbounded" minOccurs="0" name="axis" type="CT_Axis">
     * ```
     */
    axis?: ICTAxis[];
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_PlotAreaMsChartEx name="CT_PlotAreaMsChartEx">
 * ```
 */
export interface ICTPlotAreaMsChartExDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_PlotAreaRegion maxOccurs="1" minOccurs="1" name="plotAreaRegion" type="CT_PlotAreaRegion">
     * ```
     */
    plotAreaRegion?: ICTPlotAreaRegionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Axis maxOccurs="unbounded" minOccurs="0" name="axis" type="CT_Axis">
     * ```
     */
    axis?: ICTAxisDelta[];
    axisOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_LegendMsChartEx name="CT_LegendMsChartEx">
 * ```
 */
export interface ICTLegendMsChartEx {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_SidePos default="r" name="pos" type="ST_SidePos" use="optional">
     * ```
     */
    pos?: STSidePos;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_PosAlign default="ctr" name="align" type="ST_PosAlign" use="optional">
     * ```
     */
    align?: STPosAlign;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="0" name="overlay" type="xsd:boolean" use="optional">
     * ```
     */
    overlay?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_LegendMsChartEx name="CT_LegendMsChartEx">
 * ```
 */
export interface ICTLegendMsChartExDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_SidePos default="r" name="pos" type="ST_SidePos" use="optional">
     * ```
     */
    pos?: STSidePos | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_PosAlign default="ctr" name="align" type="ST_PosAlign" use="optional">
     * ```
     */
    align?: STPosAlign | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="0" name="overlay" type="xsd:boolean" use="optional">
     * ```
     */
    overlay?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_FormatOverride name="CT_FormatOverride">
 * ```
 */
export interface ICTFormatOverride {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt" use="required">
     * ```
     */
    idx?: number;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_FormatOverride name="CT_FormatOverride">
 * ```
 */
export interface ICTFormatOverrideDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt" use="required">
     * ```
     */
    idx?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_HeaderFooterMsChartEx name="CT_HeaderFooterMsChartEx">
 * ```
 */
export interface ICTHeaderFooterMsChartEx {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="oddHeader" type="xsd:string">
     * ```
     */
    oddHeader?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="oddFooter" type="xsd:string">
     * ```
     */
    oddFooter?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="evenHeader" type="xsd:string">
     * ```
     */
    evenHeader?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="evenFooter" type="xsd:string">
     * ```
     */
    evenFooter?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="firstHeader" type="xsd:string">
     * ```
     */
    firstHeader?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="firstFooter" type="xsd:string">
     * ```
     */
    firstFooter?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="alignWithMargins" type="xsd:boolean" use="optional">
     * ```
     */
    alignWithMargins?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="differentOddEven" type="xsd:boolean" use="optional">
     * ```
     */
    differentOddEven?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="differentFirst" type="xsd:boolean" use="optional">
     * ```
     */
    differentFirst?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_HeaderFooterMsChartEx name="CT_HeaderFooterMsChartEx">
 * ```
 */
export interface ICTHeaderFooterMsChartExDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="oddHeader" type="xsd:string">
     * ```
     */
    oddHeader?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="oddFooter" type="xsd:string">
     * ```
     */
    oddFooter?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="evenHeader" type="xsd:string">
     * ```
     */
    evenHeader?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="evenFooter" type="xsd:string">
     * ```
     */
    evenFooter?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="firstHeader" type="xsd:string">
     * ```
     */
    firstHeader?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="firstFooter" type="xsd:string">
     * ```
     */
    firstFooter?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="true" name="alignWithMargins" type="xsd:boolean" use="optional">
     * ```
     */
    alignWithMargins?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="differentOddEven" type="xsd:boolean" use="optional">
     * ```
     */
    differentOddEven?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="differentFirst" type="xsd:boolean" use="optional">
     * ```
     */
    differentFirst?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_PageMarginsMsChartEx name="CT_PageMarginsMsChartEx">
 * ```
 */
export interface ICTPageMarginsMsChartEx {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="l" type="xsd:double" use="required">
     * ```
     */
    l?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="r" type="xsd:double" use="required">
     * ```
     */
    r?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="t" type="xsd:double" use="required">
     * ```
     */
    t?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="b" type="xsd:double" use="required">
     * ```
     */
    b?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="header" type="xsd:double" use="required">
     * ```
     */
    header?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="footer" type="xsd:double" use="required">
     * ```
     */
    footer?: number;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_PageMarginsMsChartEx name="CT_PageMarginsMsChartEx">
 * ```
 */
export interface ICTPageMarginsMsChartExDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="l" type="xsd:double" use="required">
     * ```
     */
    l?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="r" type="xsd:double" use="required">
     * ```
     */
    r?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="t" type="xsd:double" use="required">
     * ```
     */
    t?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="b" type="xsd:double" use="required">
     * ```
     */
    b?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="header" type="xsd:double" use="required">
     * ```
     */
    header?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="footer" type="xsd:double" use="required">
     * ```
     */
    footer?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_PageSetupMsChartEx name="CT_PageSetupMsChartEx">
 * ```
 */
export interface ICTPageSetupMsChartEx {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="1" name="paperSize" type="xsd:unsignedInt" use="optional">
     * ```
     */
    paperSize?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="1" name="firstPageNumber" type="xsd:unsignedInt" use="optional">
     * ```
     */
    firstPageNumber?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_PageOrientationMsChartEx default="default" name="orientation" type="ST_PageOrientationMsChartEx" use="optional">
     * ```
     */
    orientation?: STPageOrientationMsChartEx;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="blackAndWhite" type="xsd:boolean" use="optional">
     * ```
     */
    blackAndWhite?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="draft" type="xsd:boolean" use="optional">
     * ```
     */
    draft?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="useFirstPageNumber" type="xsd:boolean" use="optional">
     * ```
     */
    useFirstPageNumber?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="600" name="horizontalDpi" type="xsd:int" use="optional">
     * ```
     */
    horizontalDpi?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="600" name="verticalDpi" type="xsd:int" use="optional">
     * ```
     */
    verticalDpi?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="1" name="copies" type="xsd:unsignedInt" use="optional">
     * ```
     */
    copies?: number;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_PageSetupMsChartEx name="CT_PageSetupMsChartEx">
 * ```
 */
export interface ICTPageSetupMsChartExDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="1" name="paperSize" type="xsd:unsignedInt" use="optional">
     * ```
     */
    paperSize?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="1" name="firstPageNumber" type="xsd:unsignedInt" use="optional">
     * ```
     */
    firstPageNumber?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_PageOrientationMsChartEx default="default" name="orientation" type="ST_PageOrientationMsChartEx" use="optional">
     * ```
     */
    orientation?: STPageOrientationMsChartEx | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="blackAndWhite" type="xsd:boolean" use="optional">
     * ```
     */
    blackAndWhite?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="draft" type="xsd:boolean" use="optional">
     * ```
     */
    draft?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="false" name="useFirstPageNumber" type="xsd:boolean" use="optional">
     * ```
     */
    useFirstPageNumber?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="600" name="horizontalDpi" type="xsd:int" use="optional">
     * ```
     */
    horizontalDpi?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="600" name="verticalDpi" type="xsd:int" use="optional">
     * ```
     */
    verticalDpi?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 default="1" name="copies" type="xsd:unsignedInt" use="optional">
     * ```
     */
    copies?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ExtensionMsChartEx name="CT_ExtensionMsChartEx">
 * ```
 */
export interface ICTExtensionMsChartEx {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="uri" type="xsd:token">
     * ```
     */
    uri?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ExtensionMsChartEx name="CT_ExtensionMsChartEx">
 * ```
 */
export interface ICTExtensionMsChartExDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="uri" type="xsd:token">
     * ```
     */
    uri?: string | null;
}

export enum STDgmBuildStep {
    PBDEFAULT = 0,
    sp = 1,
    bg = 2,
}

export enum STChartBuildStep {
    PBDEFAULT = 0,
    category = 1,
    ptInCategory = 2,
    series = 3,
    ptInSeries = 4,
    allPts = 5,
    gridLegend = 6,
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_NumericDimension name="CT_NumericDimension">
 * ```
 */
export interface ICTNumericDimension {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Formula maxOccurs="1" minOccurs="1" name="f" type="CT_Formula">
     * ```
     */
    f?: ICTFormula;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Formula maxOccurs="1" minOccurs="0" name="nf" type="CT_Formula">
     * ```
     */
    nf?: ICTFormula;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_NumericLevel maxOccurs="unbounded" minOccurs="0" name="lvl" type="CT_NumericLevel">
     * ```
     */
    lvl?: ICTNumericLevel[];
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_NumericLevel aliasName=lvlSame aliasName="lvlSame" maxOccurs="unbounded" minOccurs="1" name="lvl" type="CT_NumericLevel">
     * ```
     */
    lvlSame?: ICTNumericLevel[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_NumericDimensionType name="type" type="ST_NumericDimensionType" use="required">
     * ```
     */
    type?: STNumericDimensionType;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_NumericDimension name="CT_NumericDimension">
 * ```
 */
export interface ICTNumericDimensionDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Formula maxOccurs="1" minOccurs="1" name="f" type="CT_Formula">
     * ```
     */
    f?: ICTFormulaDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Formula maxOccurs="1" minOccurs="0" name="nf" type="CT_Formula">
     * ```
     */
    nf?: ICTFormulaDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_NumericLevel maxOccurs="unbounded" minOccurs="0" name="lvl" type="CT_NumericLevel">
     * ```
     */
    lvl?: ICTNumericLevelDelta[];
    lvlOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_NumericLevel aliasName=lvlSame aliasName="lvlSame" maxOccurs="unbounded" minOccurs="1" name="lvl" type="CT_NumericLevel">
     * ```
     */
    lvlSame?: ICTNumericLevelDelta[];
    lvlSameOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_NumericDimensionType name="type" type="ST_NumericDimensionType" use="required">
     * ```
     */
    type?: STNumericDimensionType | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_StringDimension name="CT_StringDimension">
 * ```
 */
export interface ICTStringDimension {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Formula maxOccurs="1" minOccurs="1" name="f" type="CT_Formula">
     * ```
     */
    f?: ICTFormula;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Formula maxOccurs="1" minOccurs="0" name="nf" type="CT_Formula">
     * ```
     */
    nf?: ICTFormula;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_StringLevel maxOccurs="unbounded" minOccurs="0" name="lvl" type="CT_StringLevel">
     * ```
     */
    lvl?: ICTStringLevel[];
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_StringLevel aliasName=lvlSame aliasName="lvlSame" maxOccurs="unbounded" minOccurs="1" name="lvl" type="CT_StringLevel">
     * ```
     */
    lvlSame?: ICTStringLevel[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_StringDimensionType name="type" type="ST_StringDimensionType" use="required">
     * ```
     */
    type?: STStringDimensionType;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_StringDimension name="CT_StringDimension">
 * ```
 */
export interface ICTStringDimensionDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Formula maxOccurs="1" minOccurs="1" name="f" type="CT_Formula">
     * ```
     */
    f?: ICTFormulaDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Formula maxOccurs="1" minOccurs="0" name="nf" type="CT_Formula">
     * ```
     */
    nf?: ICTFormulaDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_StringLevel maxOccurs="unbounded" minOccurs="0" name="lvl" type="CT_StringLevel">
     * ```
     */
    lvl?: ICTStringLevelDelta[];
    lvlOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_StringLevel aliasName=lvlSame aliasName="lvlSame" maxOccurs="unbounded" minOccurs="1" name="lvl" type="CT_StringLevel">
     * ```
     */
    lvlSame?: ICTStringLevelDelta[];
    lvlSameOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_StringDimensionType name="type" type="ST_StringDimensionType" use="required">
     * ```
     */
    type?: STStringDimensionType | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_TextMsChartEx name="CT_TextMsChartEx">
 * ```
 */
export interface ICTTextMsChartEx {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_TextData maxOccurs="1" minOccurs="1" name="txData" type="CT_TextData">
     * ```
     */
    txData?: ICTTextData;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="1" name="rich" type="a:CT_TextBody">
     * ```
     */
    rich?: ICTTextBody;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_TextMsChartEx name="CT_TextMsChartEx">
 * ```
 */
export interface ICTTextMsChartExDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_TextData maxOccurs="1" minOccurs="1" name="txData" type="CT_TextData">
     * ```
     */
    txData?: ICTTextDataDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="1" name="rich" type="a:CT_TextBody">
     * ```
     */
    rich?: ICTTextBodyDelta | null;
}

export enum STSidePos {
    PBDEFAULT = 0,
    l = 1,
    t = 2,
    r = 3,
    b = 4,
}

export enum STPosAlign {
    PBDEFAULT = 0,
    min = 1,
    ctr = 2,
    max = 3,
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_PlotAreaRegion name="CT_PlotAreaRegion">
 * ```
 */
export interface ICTPlotAreaRegion {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_PlotSurface maxOccurs="1" minOccurs="0" name="plotSurface" type="CT_PlotSurface">
     * ```
     */
    plotSurface?: ICTPlotSurface;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Series maxOccurs="unbounded" minOccurs="0" name="series" type="CT_Series">
     * ```
     */
    series?: ICTSeries[];
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_PlotAreaRegion name="CT_PlotAreaRegion">
 * ```
 */
export interface ICTPlotAreaRegionDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_PlotSurface maxOccurs="1" minOccurs="0" name="plotSurface" type="CT_PlotSurface">
     * ```
     */
    plotSurface?: ICTPlotSurfaceDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Series maxOccurs="unbounded" minOccurs="0" name="series" type="CT_Series">
     * ```
     */
    series?: ICTSeriesDelta[];
    seriesOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Axis name="CT_Axis">
 * ```
 */
export interface ICTAxis {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_CategoryAxisScaling name="catScaling" type="CT_CategoryAxisScaling">
     * ```
     */
    catScaling?: ICTCategoryAxisScaling;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ValueAxisScaling name="valScaling" type="CT_ValueAxisScaling">
     * ```
     */
    valScaling?: ICTValueAxisScaling;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_AxisTitle maxOccurs="1" minOccurs="0" name="title" type="CT_AxisTitle">
     * ```
     */
    title?: ICTAxisTitle;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_AxisUnits maxOccurs="1" minOccurs="0" name="units" type="CT_AxisUnits">
     * ```
     */
    units?: ICTAxisUnits;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Gridlines maxOccurs="1" minOccurs="0" name="majorGridlines" type="CT_Gridlines">
     * ```
     */
    majorGridlines?: ICTGridlines;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Gridlines maxOccurs="1" minOccurs="0" name="minorGridlines" type="CT_Gridlines">
     * ```
     */
    minorGridlines?: ICTGridlines;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_TickMarks maxOccurs="1" minOccurs="0" name="majorTickMarks" type="CT_TickMarks">
     * ```
     */
    majorTickMarks?: ICTTickMarks;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_TickMarks maxOccurs="1" minOccurs="0" name="minorTickMarks" type="CT_TickMarks">
     * ```
     */
    minorTickMarks?: ICTTickMarks;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_TickLabels maxOccurs="1" minOccurs="0" name="tickLabels" type="CT_TickLabels">
     * ```
     */
    tickLabels?: ICTTickLabels;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_NumberFormat maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumberFormat">
     * ```
     */
    numFmt?: ICTNumberFormat;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="ST_AxisId" use="required">
     * ```
     */
    id?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="0" name="hidden" type="xsd:boolean" use="optional">
     * ```
     */
    hidden?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Axis name="CT_Axis">
 * ```
 */
export interface ICTAxisDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_CategoryAxisScaling name="catScaling" type="CT_CategoryAxisScaling">
     * ```
     */
    catScaling?: ICTCategoryAxisScalingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ValueAxisScaling name="valScaling" type="CT_ValueAxisScaling">
     * ```
     */
    valScaling?: ICTValueAxisScalingDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_AxisTitle maxOccurs="1" minOccurs="0" name="title" type="CT_AxisTitle">
     * ```
     */
    title?: ICTAxisTitleDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_AxisUnits maxOccurs="1" minOccurs="0" name="units" type="CT_AxisUnits">
     * ```
     */
    units?: ICTAxisUnitsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Gridlines maxOccurs="1" minOccurs="0" name="majorGridlines" type="CT_Gridlines">
     * ```
     */
    majorGridlines?: ICTGridlinesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Gridlines maxOccurs="1" minOccurs="0" name="minorGridlines" type="CT_Gridlines">
     * ```
     */
    minorGridlines?: ICTGridlinesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_TickMarks maxOccurs="1" minOccurs="0" name="majorTickMarks" type="CT_TickMarks">
     * ```
     */
    majorTickMarks?: ICTTickMarksDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_TickMarks maxOccurs="1" minOccurs="0" name="minorTickMarks" type="CT_TickMarks">
     * ```
     */
    minorTickMarks?: ICTTickMarksDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_TickLabels maxOccurs="1" minOccurs="0" name="tickLabels" type="CT_TickLabels">
     * ```
     */
    tickLabels?: ICTTickLabelsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_NumberFormat maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumberFormat">
     * ```
     */
    numFmt?: ICTNumberFormatDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 convTypeRedirected=xsd:string convType="s:ST_IdString" name="id" type="ST_AxisId" use="required">
     * ```
     */
    id?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="0" name="hidden" type="xsd:boolean" use="optional">
     * ```
     */
    hidden?: boolean | null;
}

export enum STPageOrientationMsChartEx {
    PBDEFAULT = 0,
    default = 1,
    portrait = 2,
    landscape = 3,
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Formula name="CT_Formula">
 * ```
 */
export interface ICTFormula {
    /**
     * ```xml
     * <xsd:extension base=xsd:string base="xsd:string">
     * ```
     */
    characterData?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_FormulaDirection default="col" name="dir" type="ST_FormulaDirection" use="optional">
     * ```
     */
    dir?: STFormulaDirection;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Formula name="CT_Formula">
 * ```
 */
export interface ICTFormulaDelta {
    /**
     * ```xml
     * <xsd:extension base=xsd:string base="xsd:string">
     * ```
     */
    characterData?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_FormulaDirection default="col" name="dir" type="ST_FormulaDirection" use="optional">
     * ```
     */
    dir?: STFormulaDirection | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_NumericLevel name="CT_NumericLevel">
 * ```
 */
export interface ICTNumericLevel {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_NumericValue maxOccurs="unbounded" minOccurs="0" name="pt" type="CT_NumericValue">
     * ```
     */
    pt?: ICTNumericValue[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="ptCount" type="xsd:unsignedInt" use="required">
     * ```
     */
    ptCount?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="formatCode" type="xsd:string" use="optional">
     * ```
     */
    formatCode?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_NumericLevel name="CT_NumericLevel">
 * ```
 */
export interface ICTNumericLevelDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_NumericValue maxOccurs="unbounded" minOccurs="0" name="pt" type="CT_NumericValue">
     * ```
     */
    pt?: ICTNumericValueDelta[];
    ptOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="ptCount" type="xsd:unsignedInt" use="required">
     * ```
     */
    ptCount?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="formatCode" type="xsd:string" use="optional">
     * ```
     */
    formatCode?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string | null;
}

export enum STNumericDimensionType {
    PBDEFAULT = 0,
    val = 1,
    x = 2,
    y = 3,
    size = 4,
    colorVal = 5,
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_StringLevel name="CT_StringLevel">
 * ```
 */
export interface ICTStringLevel {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_StringValue maxOccurs="unbounded" minOccurs="0" name="pt" type="CT_StringValue">
     * ```
     */
    pt?: ICTStringValue[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="ptCount" type="xsd:unsignedInt" use="required">
     * ```
     */
    ptCount?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_StringLevel name="CT_StringLevel">
 * ```
 */
export interface ICTStringLevelDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_StringValue maxOccurs="unbounded" minOccurs="0" name="pt" type="CT_StringValue">
     * ```
     */
    pt?: ICTStringValueDelta[];
    ptOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="ptCount" type="xsd:unsignedInt" use="required">
     * ```
     */
    ptCount?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="name" type="xsd:string" use="optional">
     * ```
     */
    name?: string | null;
}

export enum STStringDimensionType {
    PBDEFAULT = 0,
    cat = 1,
    colorStr = 2,
    entityId = 3,
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_TextData name="CT_TextData">
 * ```
 */
export interface ICTTextData {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Formula maxOccurs="1" minOccurs="1" name="f" type="CT_Formula">
     * ```
     */
    f?: ICTFormula;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="v" type="xsd:string">
     * ```
     */
    v?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string aliasName=vSame aliasName="vSame" maxOccurs="1" minOccurs="1" name="v" type="xsd:string">
     * ```
     */
    vSame?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_TextData name="CT_TextData">
 * ```
 */
export interface ICTTextDataDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Formula maxOccurs="1" minOccurs="1" name="f" type="CT_Formula">
     * ```
     */
    f?: ICTFormulaDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="v" type="xsd:string">
     * ```
     */
    v?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string aliasName=vSame aliasName="vSame" maxOccurs="1" minOccurs="1" name="v" type="xsd:string">
     * ```
     */
    vSame?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_PlotSurface name="CT_PlotSurface">
 * ```
 */
export interface ICTPlotSurface {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_PlotSurface name="CT_PlotSurface">
 * ```
 */
export interface ICTPlotSurfaceDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Series name="CT_Series">
 * ```
 */
export interface ICTSeries {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_TextMsChartEx maxOccurs="1" minOccurs="0" name="tx" type="CT_TextMsChartEx">
     * ```
     */
    tx?: ICTTextMsChartEx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ValueColors maxOccurs="1" minOccurs="0" name="valueColors" type="CT_ValueColors">
     * ```
     */
    valueColors?: ICTValueColors;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ValueColorPositions maxOccurs="1" minOccurs="0" name="valueColorPositions" type="CT_ValueColorPositions">
     * ```
     */
    valueColorPositions?: ICTValueColorPositions;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_DataPoint maxOccurs="unbounded" minOccurs="0" name="dataPt" type="CT_DataPoint">
     * ```
     */
    dataPt?: ICTDataPoint[];
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_DataLabels maxOccurs="1" minOccurs="0" name="dataLabels" type="CT_DataLabels">
     * ```
     */
    dataLabels?: ICTDataLabels;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_DataId maxOccurs="1" minOccurs="0" name="dataId" type="CT_DataId">
     * ```
     */
    dataId?: ICTDataId;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_SeriesLayoutProperties maxOccurs="1" minOccurs="0" name="layoutPr" type="CT_SeriesLayoutProperties">
     * ```
     */
    layoutPr?: ICTSeriesLayoutProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:uint32 maxOccurs="unbounded" minOccurs="0" name="axisId" type="ST_AxisId">
     * ```
     */
    axisId?: number[];
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_SeriesLayout name="layoutId" type="ST_SeriesLayout" use="required">
     * ```
     */
    layoutId?: STSeriesLayout;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="0" name="hidden" type="xsd:boolean" use="optional">
     * ```
     */
    hidden?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="ownerIdx" type="xsd:unsignedInt" use="optional">
     * ```
     */
    ownerIdx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="uniqueId" type="xsd:string" use="optional">
     * ```
     */
    uniqueId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="formatIdx" type="xsd:unsignedInt" use="optional">
     * ```
     */
    formatIdx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="listIndex" skipIO="true" type="xsd:unsignedInt" use="optional">
     * ```
     */
    listIndex?: number;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Series name="CT_Series">
 * ```
 */
export interface ICTSeriesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_TextMsChartEx maxOccurs="1" minOccurs="0" name="tx" type="CT_TextMsChartEx">
     * ```
     */
    tx?: ICTTextMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ValueColors maxOccurs="1" minOccurs="0" name="valueColors" type="CT_ValueColors">
     * ```
     */
    valueColors?: ICTValueColorsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ValueColorPositions maxOccurs="1" minOccurs="0" name="valueColorPositions" type="CT_ValueColorPositions">
     * ```
     */
    valueColorPositions?: ICTValueColorPositionsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_DataPoint maxOccurs="unbounded" minOccurs="0" name="dataPt" type="CT_DataPoint">
     * ```
     */
    dataPt?: ICTDataPointDelta[];
    dataPtOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_DataLabels maxOccurs="1" minOccurs="0" name="dataLabels" type="CT_DataLabels">
     * ```
     */
    dataLabels?: ICTDataLabelsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_DataId maxOccurs="1" minOccurs="0" name="dataId" type="CT_DataId">
     * ```
     */
    dataId?: ICTDataIdDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_SeriesLayoutProperties maxOccurs="1" minOccurs="0" name="layoutPr" type="CT_SeriesLayoutProperties">
     * ```
     */
    layoutPr?: ICTSeriesLayoutPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:uint32 maxOccurs="unbounded" minOccurs="0" name="axisId" type="ST_AxisId">
     * ```
     */
    axisId?: number[];
    axisIdOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_SeriesLayout name="layoutId" type="ST_SeriesLayout" use="required">
     * ```
     */
    layoutId?: STSeriesLayout | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean default="0" name="hidden" type="xsd:boolean" use="optional">
     * ```
     */
    hidden?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="ownerIdx" type="xsd:unsignedInt" use="optional">
     * ```
     */
    ownerIdx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="uniqueId" type="xsd:string" use="optional">
     * ```
     */
    uniqueId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="formatIdx" type="xsd:unsignedInt" use="optional">
     * ```
     */
    formatIdx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="listIndex" skipIO="true" type="xsd:unsignedInt" use="optional">
     * ```
     */
    listIndex?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_CategoryAxisScaling name="CT_CategoryAxisScaling">
 * ```
 */
export interface ICTCategoryAxisScaling {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="gapWidth" type="ST_GapWidthRatio" use="optional">
     * ```
     */
    gapWidth?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_CategoryAxisScaling name="CT_CategoryAxisScaling">
 * ```
 */
export interface ICTCategoryAxisScalingDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="gapWidth" type="ST_GapWidthRatio" use="optional">
     * ```
     */
    gapWidth?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ValueAxisScaling name="CT_ValueAxisScaling">
 * ```
 */
export interface ICTValueAxisScaling {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="max" type="ST_DoubleOrAutomatic" use="optional">
     * ```
     */
    max?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="min" type="ST_DoubleOrAutomatic" use="optional">
     * ```
     */
    min?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="majorUnit" type="ST_ValueAxisUnit" use="optional">
     * ```
     */
    majorUnit?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="minorUnit" type="ST_ValueAxisUnit" use="optional">
     * ```
     */
    minorUnit?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ValueAxisScaling name="CT_ValueAxisScaling">
 * ```
 */
export interface ICTValueAxisScalingDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="max" type="ST_DoubleOrAutomatic" use="optional">
     * ```
     */
    max?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="min" type="ST_DoubleOrAutomatic" use="optional">
     * ```
     */
    min?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="majorUnit" type="ST_ValueAxisUnit" use="optional">
     * ```
     */
    majorUnit?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="minorUnit" type="ST_ValueAxisUnit" use="optional">
     * ```
     */
    minorUnit?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_AxisTitle name="CT_AxisTitle">
 * ```
 */
export interface ICTAxisTitle {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_TextMsChartEx maxOccurs="1" minOccurs="0" name="tx" type="CT_TextMsChartEx">
     * ```
     */
    tx?: ICTTextMsChartEx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_AxisTitle name="CT_AxisTitle">
 * ```
 */
export interface ICTAxisTitleDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_TextMsChartEx maxOccurs="1" minOccurs="0" name="tx" type="CT_TextMsChartEx">
     * ```
     */
    tx?: ICTTextMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_AxisUnits name="CT_AxisUnits">
 * ```
 */
export interface ICTAxisUnits {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_AxisUnitsLabel maxOccurs="1" minOccurs="0" name="unitsLabel" type="CT_AxisUnitsLabel">
     * ```
     */
    unitsLabel?: ICTAxisUnitsLabel;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_AxisUnitMsChartEx name="unit" type="ST_AxisUnitMsChartEx" use="optional">
     * ```
     */
    unit?: STAxisUnitMsChartEx;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_AxisUnits name="CT_AxisUnits">
 * ```
 */
export interface ICTAxisUnitsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_AxisUnitsLabel maxOccurs="1" minOccurs="0" name="unitsLabel" type="CT_AxisUnitsLabel">
     * ```
     */
    unitsLabel?: ICTAxisUnitsLabelDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_AxisUnitMsChartEx name="unit" type="ST_AxisUnitMsChartEx" use="optional">
     * ```
     */
    unit?: STAxisUnitMsChartEx | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Gridlines name="CT_Gridlines">
 * ```
 */
export interface ICTGridlines {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Gridlines name="CT_Gridlines">
 * ```
 */
export interface ICTGridlinesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_TickMarks name="CT_TickMarks">
 * ```
 */
export interface ICTTickMarks {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_TickMarksType name="type" type="ST_TickMarksType" use="optional">
     * ```
     */
    type?: STTickMarksType;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_TickMarks name="CT_TickMarks">
 * ```
 */
export interface ICTTickMarksDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_TickMarksType name="type" type="ST_TickMarksType" use="optional">
     * ```
     */
    type?: STTickMarksType | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_TickLabels name="CT_TickLabels">
 * ```
 */
export interface ICTTickLabels {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_TickLabels name="CT_TickLabels">
 * ```
 */
export interface ICTTickLabelsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_NumberFormat name="CT_NumberFormat">
 * ```
 */
export interface ICTNumberFormat {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="formatCode" type="xsd:string" use="required">
     * ```
     */
    formatCode?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="sourceLinked" type="xsd:boolean" use="optional">
     * ```
     */
    sourceLinked?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_NumberFormat name="CT_NumberFormat">
 * ```
 */
export interface ICTNumberFormatDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="formatCode" type="xsd:string" use="required">
     * ```
     */
    formatCode?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="sourceLinked" type="xsd:boolean" use="optional">
     * ```
     */
    sourceLinked?: boolean | null;
}

export enum STFormulaDirection {
    PBDEFAULT = 0,
    col = 1,
    row = 2,
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_NumericValue name="CT_NumericValue">
 * ```
 */
export interface ICTNumericValue {
    /**
     * ```xml
     * <xsd:extension base=xsd:double base="xsd:double">
     * ```
     */
    characterData?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt" use="required">
     * ```
     */
    idx?: number;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_NumericValue name="CT_NumericValue">
 * ```
 */
export interface ICTNumericValueDelta {
    /**
     * ```xml
     * <xsd:extension base=xsd:double base="xsd:double">
     * ```
     */
    characterData?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt" use="required">
     * ```
     */
    idx?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_StringValue name="CT_StringValue">
 * ```
 */
export interface ICTStringValue {
    /**
     * ```xml
     * <xsd:extension base=xsd:string base="xsd:string">
     * ```
     */
    characterData?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt" use="required">
     * ```
     */
    idx?: number;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_StringValue name="CT_StringValue">
 * ```
 */
export interface ICTStringValueDelta {
    /**
     * ```xml
     * <xsd:extension base=xsd:string base="xsd:string">
     * ```
     */
    characterData?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt" use="required">
     * ```
     */
    idx?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ValueColors name="CT_ValueColors">
 * ```
 */
export interface ICTValueColors {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="0" name="minColor" type="a:CT_SolidColorFillProperties">
     * ```
     */
    minColor?: ICTSolidColorFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="0" name="midColor" type="a:CT_SolidColorFillProperties">
     * ```
     */
    midColor?: ICTSolidColorFillProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="0" name="maxColor" type="a:CT_SolidColorFillProperties">
     * ```
     */
    maxColor?: ICTSolidColorFillProperties;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ValueColors name="CT_ValueColors">
 * ```
 */
export interface ICTValueColorsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="0" name="minColor" type="a:CT_SolidColorFillProperties">
     * ```
     */
    minColor?: ICTSolidColorFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="0" name="midColor" type="a:CT_SolidColorFillProperties">
     * ```
     */
    midColor?: ICTSolidColorFillPropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_SolidColorFillProperties maxOccurs="1" minOccurs="0" name="maxColor" type="a:CT_SolidColorFillProperties">
     * ```
     */
    maxColor?: ICTSolidColorFillPropertiesDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ValueColorPositions name="CT_ValueColorPositions">
 * ```
 */
export interface ICTValueColorPositions {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ValueColorEndPosition maxOccurs="1" minOccurs="0" name="min" type="CT_ValueColorEndPosition">
     * ```
     */
    min?: ICTValueColorEndPosition;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ValueColorMiddlePosition maxOccurs="1" minOccurs="0" name="mid" type="CT_ValueColorMiddlePosition">
     * ```
     */
    mid?: ICTValueColorMiddlePosition;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ValueColorEndPosition maxOccurs="1" minOccurs="0" name="max" type="CT_ValueColorEndPosition">
     * ```
     */
    max?: ICTValueColorEndPosition;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="2" name="count" type="ST_ValueColorPositionCount" use="optional">
     * ```
     */
    count?: number;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ValueColorPositions name="CT_ValueColorPositions">
 * ```
 */
export interface ICTValueColorPositionsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ValueColorEndPosition maxOccurs="1" minOccurs="0" name="min" type="CT_ValueColorEndPosition">
     * ```
     */
    min?: ICTValueColorEndPositionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ValueColorMiddlePosition maxOccurs="1" minOccurs="0" name="mid" type="CT_ValueColorMiddlePosition">
     * ```
     */
    mid?: ICTValueColorMiddlePositionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ValueColorEndPosition maxOccurs="1" minOccurs="0" name="max" type="CT_ValueColorEndPosition">
     * ```
     */
    max?: ICTValueColorEndPositionDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer default="2" name="count" type="ST_ValueColorPositionCount" use="optional">
     * ```
     */
    count?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_DataPoint name="CT_DataPoint">
 * ```
 */
export interface ICTDataPoint {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt" use="required">
     * ```
     */
    idx?: number;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_DataPoint name="CT_DataPoint">
 * ```
 */
export interface ICTDataPointDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt" use="required">
     * ```
     */
    idx?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_DataLabels name="CT_DataLabels">
 * ```
 */
export interface ICTDataLabels {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_NumberFormat maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumberFormat">
     * ```
     */
    numFmt?: ICTNumberFormat;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_DataLabelVisibilities maxOccurs="1" minOccurs="0" name="visibility" type="CT_DataLabelVisibilities">
     * ```
     */
    visibility?: ICTDataLabelVisibilities;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="separator" type="xsd:string">
     * ```
     */
    separator?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_DataLabel maxOccurs="unbounded" minOccurs="0" name="dataLabel" type="CT_DataLabel">
     * ```
     */
    dataLabel?: ICTDataLabel[];
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_DataLabelHidden maxOccurs="unbounded" minOccurs="0" name="dataLabelHidden" type="CT_DataLabelHidden">
     * ```
     */
    dataLabelHidden?: ICTDataLabelHidden[];
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_DataLabelPos name="pos" type="ST_DataLabelPos" use="optional">
     * ```
     */
    pos?: STDataLabelPos;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_DataLabels name="CT_DataLabels">
 * ```
 */
export interface ICTDataLabelsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_NumberFormat maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumberFormat">
     * ```
     */
    numFmt?: ICTNumberFormatDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_DataLabelVisibilities maxOccurs="1" minOccurs="0" name="visibility" type="CT_DataLabelVisibilities">
     * ```
     */
    visibility?: ICTDataLabelVisibilitiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="separator" type="xsd:string">
     * ```
     */
    separator?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_DataLabel maxOccurs="unbounded" minOccurs="0" name="dataLabel" type="CT_DataLabel">
     * ```
     */
    dataLabel?: ICTDataLabelDelta[];
    dataLabelOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_DataLabelHidden maxOccurs="unbounded" minOccurs="0" name="dataLabelHidden" type="CT_DataLabelHidden">
     * ```
     */
    dataLabelHidden?: ICTDataLabelHiddenDelta[];
    dataLabelHiddenOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_DataLabelPos name="pos" type="ST_DataLabelPos" use="optional">
     * ```
     */
    pos?: STDataLabelPos | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_DataId name="CT_DataId">
 * ```
 */
export interface ICTDataId {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="val" type="ST_DataId" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_DataId name="CT_DataId">
 * ```
 */
export interface ICTDataIdDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="val" type="ST_DataId" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_SeriesLayoutProperties name="CT_SeriesLayoutProperties">
 * ```
 */
export interface ICTSeriesLayoutProperties {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ParentLabelLayout maxOccurs="1" minOccurs="0" name="parentLabelLayout" type="CT_ParentLabelLayout">
     * ```
     */
    parentLabelLayout?: ICTParentLabelLayout;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_RegionLabelLayout maxOccurs="1" minOccurs="0" name="regionLabelLayout" type="CT_RegionLabelLayout">
     * ```
     */
    regionLabelLayout?: ICTRegionLabelLayout;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_SeriesElementVisibilities maxOccurs="1" minOccurs="0" name="visibility" type="CT_SeriesElementVisibilities">
     * ```
     */
    visibility?: ICTSeriesElementVisibilities;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Aggregation maxOccurs="1" minOccurs="0" name="aggregation" type="CT_Aggregation">
     * ```
     */
    aggregation?: ICTAggregation;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Binning maxOccurs="1" minOccurs="0" name="binning" type="CT_Binning">
     * ```
     */
    binning?: ICTBinning;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Geography maxOccurs="1" minOccurs="0" name="geography" type="CT_Geography">
     * ```
     */
    geography?: ICTGeography;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Statistics maxOccurs="1" minOccurs="0" name="statistics" type="CT_Statistics">
     * ```
     */
    statistics?: ICTStatistics;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Subtotals maxOccurs="1" minOccurs="0" name="subtotals" type="CT_Subtotals">
     * ```
     */
    subtotals?: ICTSubtotals;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_SeriesLayoutProperties name="CT_SeriesLayoutProperties">
 * ```
 */
export interface ICTSeriesLayoutPropertiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ParentLabelLayout maxOccurs="1" minOccurs="0" name="parentLabelLayout" type="CT_ParentLabelLayout">
     * ```
     */
    parentLabelLayout?: ICTParentLabelLayoutDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_RegionLabelLayout maxOccurs="1" minOccurs="0" name="regionLabelLayout" type="CT_RegionLabelLayout">
     * ```
     */
    regionLabelLayout?: ICTRegionLabelLayoutDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_SeriesElementVisibilities maxOccurs="1" minOccurs="0" name="visibility" type="CT_SeriesElementVisibilities">
     * ```
     */
    visibility?: ICTSeriesElementVisibilitiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Aggregation maxOccurs="1" minOccurs="0" name="aggregation" type="CT_Aggregation">
     * ```
     */
    aggregation?: ICTAggregation | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Binning maxOccurs="1" minOccurs="0" name="binning" type="CT_Binning">
     * ```
     */
    binning?: ICTBinningDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Geography maxOccurs="1" minOccurs="0" name="geography" type="CT_Geography">
     * ```
     */
    geography?: ICTGeographyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Statistics maxOccurs="1" minOccurs="0" name="statistics" type="CT_Statistics">
     * ```
     */
    statistics?: ICTStatisticsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Subtotals maxOccurs="1" minOccurs="0" name="subtotals" type="CT_Subtotals">
     * ```
     */
    subtotals?: ICTSubtotalsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
}

export enum STSeriesLayout {
    PBDEFAULT = 0,
    boxWhisker = 1,
    clusteredColumn = 2,
    funnel = 3,
    paretoLine = 4,
    regionMap = 5,
    sunburst = 6,
    treemap = 7,
    waterfall = 8,
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_AxisUnitsLabel name="CT_AxisUnitsLabel">
 * ```
 */
export interface ICTAxisUnitsLabel {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_TextMsChartEx maxOccurs="1" minOccurs="0" name="tx" type="CT_TextMsChartEx">
     * ```
     */
    tx?: ICTTextMsChartEx;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_AxisUnitsLabel name="CT_AxisUnitsLabel">
 * ```
 */
export interface ICTAxisUnitsLabelDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_TextMsChartEx maxOccurs="1" minOccurs="0" name="tx" type="CT_TextMsChartEx">
     * ```
     */
    tx?: ICTTextMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
}

export enum STAxisUnitMsChartEx {
    PBDEFAULT = 0,
    hundreds = 1,
    thousands = 2,
    tenThousands = 3,
    hundredThousands = 4,
    millions = 5,
    tenMillions = 6,
    hundredMillions = 7,
    billions = 8,
    trillions = 9,
    percentage = 10,
}

export enum STTickMarksType {
    PBDEFAULT = 0,
    in = 1,
    out = 2,
    cross = 3,
    none = 4,
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ValueColorEndPosition name="CT_ValueColorEndPosition">
 * ```
 */
export interface ICTValueColorEndPosition {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtremeValueColorPosition name="extremeValue" type="CT_ExtremeValueColorPosition">
     * ```
     */
    extremeValue?: ICTExtremeValueColorPosition;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_NumberColorPosition name="number" type="CT_NumberColorPosition">
     * ```
     */
    number?: ICTNumberColorPosition;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_PercentageColorPosition name="percent" type="CT_PercentageColorPosition">
     * ```
     */
    percent?: ICTPercentageColorPosition;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ValueColorEndPosition name="CT_ValueColorEndPosition">
 * ```
 */
export interface ICTValueColorEndPositionDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtremeValueColorPosition name="extremeValue" type="CT_ExtremeValueColorPosition">
     * ```
     */
    extremeValue?: ICTExtremeValueColorPosition | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_NumberColorPosition name="number" type="CT_NumberColorPosition">
     * ```
     */
    number?: ICTNumberColorPositionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_PercentageColorPosition name="percent" type="CT_PercentageColorPosition">
     * ```
     */
    percent?: ICTPercentageColorPositionDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ValueColorMiddlePosition name="CT_ValueColorMiddlePosition">
 * ```
 */
export interface ICTValueColorMiddlePosition {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_NumberColorPosition name="number" type="CT_NumberColorPosition">
     * ```
     */
    number?: ICTNumberColorPosition;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_PercentageColorPosition name="percent" type="CT_PercentageColorPosition">
     * ```
     */
    percent?: ICTPercentageColorPosition;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ValueColorMiddlePosition name="CT_ValueColorMiddlePosition">
 * ```
 */
export interface ICTValueColorMiddlePositionDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_NumberColorPosition name="number" type="CT_NumberColorPosition">
     * ```
     */
    number?: ICTNumberColorPositionDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_PercentageColorPosition name="percent" type="CT_PercentageColorPosition">
     * ```
     */
    percent?: ICTPercentageColorPositionDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_DataLabelVisibilities name="CT_DataLabelVisibilities">
 * ```
 */
export interface ICTDataLabelVisibilities {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="seriesName" type="xsd:boolean" use="optional">
     * ```
     */
    seriesName?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="categoryName" type="xsd:boolean" use="optional">
     * ```
     */
    categoryName?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="value" type="xsd:boolean" use="optional">
     * ```
     */
    value?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_DataLabelVisibilities name="CT_DataLabelVisibilities">
 * ```
 */
export interface ICTDataLabelVisibilitiesDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="seriesName" type="xsd:boolean" use="optional">
     * ```
     */
    seriesName?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="categoryName" type="xsd:boolean" use="optional">
     * ```
     */
    categoryName?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="value" type="xsd:boolean" use="optional">
     * ```
     */
    value?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_DataLabel name="CT_DataLabel">
 * ```
 */
export interface ICTDataLabel {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_NumberFormat maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumberFormat">
     * ```
     */
    numFmt?: ICTNumberFormat;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapeProperties;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBody;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_DataLabelVisibilities maxOccurs="1" minOccurs="0" name="visibility" type="CT_DataLabelVisibilities">
     * ```
     */
    visibility?: ICTDataLabelVisibilities;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="separator" type="xsd:string">
     * ```
     */
    separator?: string;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartEx;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt" use="required">
     * ```
     */
    idx?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_DataLabelPos name="pos" type="ST_DataLabelPos" use="optional">
     * ```
     */
    pos?: STDataLabelPos;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_DataLabel name="CT_DataLabel">
 * ```
 */
export interface ICTDataLabelDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_NumberFormat maxOccurs="1" minOccurs="0" name="numFmt" type="CT_NumberFormat">
     * ```
     */
    numFmt?: ICTNumberFormatDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_ShapeProperties maxOccurs="1" minOccurs="0" name="spPr" type="a:CT_ShapeProperties">
     * ```
     */
    spPr?: ICTShapePropertiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=dmlmain:CT_TextBody maxOccurs="1" minOccurs="0" name="txPr" type="a:CT_TextBody">
     * ```
     */
    txPr?: ICTTextBodyDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_DataLabelVisibilities maxOccurs="1" minOccurs="0" name="visibility" type="CT_DataLabelVisibilities">
     * ```
     */
    visibility?: ICTDataLabelVisibilitiesDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="separator" type="xsd:string">
     * ```
     */
    separator?: string | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_ExtensionListMsChartEx maxOccurs="1" minOccurs="0" name="extLst" type="CT_ExtensionListMsChartEx">
     * ```
     */
    extLst?: ICTExtensionListMsChartExDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt" use="required">
     * ```
     */
    idx?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_DataLabelPos name="pos" type="ST_DataLabelPos" use="optional">
     * ```
     */
    pos?: STDataLabelPos | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_DataLabelHidden name="CT_DataLabelHidden">
 * ```
 */
export interface ICTDataLabelHidden {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt" use="required">
     * ```
     */
    idx?: number;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_DataLabelHidden name="CT_DataLabelHidden">
 * ```
 */
export interface ICTDataLabelHiddenDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="idx" type="xsd:unsignedInt" use="required">
     * ```
     */
    idx?: number | null;
}

export enum STDataLabelPos {
    PBDEFAULT = 0,
    bestFit = 1,
    b = 2,
    ctr = 3,
    inBase = 4,
    inEnd = 5,
    l = 6,
    outEnd = 7,
    r = 8,
    t = 9,
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ParentLabelLayout name="CT_ParentLabelLayout">
 * ```
 */
export interface ICTParentLabelLayout {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_ParentLabelLayout name="val" type="ST_ParentLabelLayout" use="required">
     * ```
     */
    val?: STParentLabelLayout;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ParentLabelLayout name="CT_ParentLabelLayout">
 * ```
 */
export interface ICTParentLabelLayoutDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_ParentLabelLayout name="val" type="ST_ParentLabelLayout" use="required">
     * ```
     */
    val?: STParentLabelLayout | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_RegionLabelLayout name="CT_RegionLabelLayout">
 * ```
 */
export interface ICTRegionLabelLayout {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_RegionLabelLayout name="val" type="ST_RegionLabelLayout" use="required">
     * ```
     */
    val?: STRegionLabelLayout;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_RegionLabelLayout name="CT_RegionLabelLayout">
 * ```
 */
export interface ICTRegionLabelLayoutDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_RegionLabelLayout name="val" type="ST_RegionLabelLayout" use="required">
     * ```
     */
    val?: STRegionLabelLayout | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_SeriesElementVisibilities name="CT_SeriesElementVisibilities">
 * ```
 */
export interface ICTSeriesElementVisibilities {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="connectorLines" type="xsd:boolean" use="optional">
     * ```
     */
    connectorLines?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="meanLine" type="xsd:boolean" use="optional">
     * ```
     */
    meanLine?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="meanMarker" type="xsd:boolean" use="optional">
     * ```
     */
    meanMarker?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="nonoutliers" type="xsd:boolean" use="optional">
     * ```
     */
    nonoutliers?: boolean;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="outliers" type="xsd:boolean" use="optional">
     * ```
     */
    outliers?: boolean;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_SeriesElementVisibilities name="CT_SeriesElementVisibilities">
 * ```
 */
export interface ICTSeriesElementVisibilitiesDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="connectorLines" type="xsd:boolean" use="optional">
     * ```
     */
    connectorLines?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="meanLine" type="xsd:boolean" use="optional">
     * ```
     */
    meanLine?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="meanMarker" type="xsd:boolean" use="optional">
     * ```
     */
    meanMarker?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="nonoutliers" type="xsd:boolean" use="optional">
     * ```
     */
    nonoutliers?: boolean | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:boolean name="outliers" type="xsd:boolean" use="optional">
     * ```
     */
    outliers?: boolean | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Aggregation name="CT_Aggregation">
 * ```
 */
export interface ICTAggregation {}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Binning name="CT_Binning">
 * ```
 */
export interface ICTBinning {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:double name="binSize" type="xsd:double">
     * ```
     */
    binSize?: number;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:uint32 name="binCount" type="xsd:unsignedInt">
     * ```
     */
    binCount?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_IntervalClosedSide name="intervalClosed" type="ST_IntervalClosedSide" use="optional">
     * ```
     */
    intervalClosed?: STIntervalClosedSide;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="underflow" type="ST_DoubleOrAutomatic" use="optional">
     * ```
     */
    underflow?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="overflow" type="ST_DoubleOrAutomatic" use="optional">
     * ```
     */
    overflow?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Binning name="CT_Binning">
 * ```
 */
export interface ICTBinningDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:double name="binSize" type="xsd:double">
     * ```
     */
    binSize?: number | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:uint32 name="binCount" type="xsd:unsignedInt">
     * ```
     */
    binCount?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_IntervalClosedSide name="intervalClosed" type="ST_IntervalClosedSide" use="optional">
     * ```
     */
    intervalClosed?: STIntervalClosedSide | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="underflow" type="ST_DoubleOrAutomatic" use="optional">
     * ```
     */
    underflow?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="overflow" type="ST_DoubleOrAutomatic" use="optional">
     * ```
     */
    overflow?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Geography name="CT_Geography">
 * ```
 */
export interface ICTGeography {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoCache maxOccurs="1" minOccurs="0" name="geoCache" type="CT_GeoCache">
     * ```
     */
    geoCache?: ICTGeoCache;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_GeoProjectionType name="projectionType" type="ST_GeoProjectionType" use="optional">
     * ```
     */
    projectionType?: STGeoProjectionType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_GeoMappingLevel name="viewedRegionType" type="ST_GeoMappingLevel" use="optional">
     * ```
     */
    viewedRegionType?: STGeoMappingLevel;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="cultureLanguage" type="xsd:language" use="required">
     * ```
     */
    cultureLanguage?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="cultureRegion" type="xsd:string" use="required">
     * ```
     */
    cultureRegion?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="attribution" type="xsd:string" use="required">
     * ```
     */
    attribution?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Geography name="CT_Geography">
 * ```
 */
export interface ICTGeographyDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoCache maxOccurs="1" minOccurs="0" name="geoCache" type="CT_GeoCache">
     * ```
     */
    geoCache?: ICTGeoCacheDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_GeoProjectionType name="projectionType" type="ST_GeoProjectionType" use="optional">
     * ```
     */
    projectionType?: STGeoProjectionType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_GeoMappingLevel name="viewedRegionType" type="ST_GeoMappingLevel" use="optional">
     * ```
     */
    viewedRegionType?: STGeoMappingLevel | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="cultureLanguage" type="xsd:language" use="required">
     * ```
     */
    cultureLanguage?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="cultureRegion" type="xsd:string" use="required">
     * ```
     */
    cultureRegion?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="attribution" type="xsd:string" use="required">
     * ```
     */
    attribution?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Statistics name="CT_Statistics">
 * ```
 */
export interface ICTStatistics {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_QuartileMethod name="quartileMethod" type="ST_QuartileMethod" use="optional">
     * ```
     */
    quartileMethod?: STQuartileMethod;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Statistics name="CT_Statistics">
 * ```
 */
export interface ICTStatisticsDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_QuartileMethod name="quartileMethod" type="ST_QuartileMethod" use="optional">
     * ```
     */
    quartileMethod?: STQuartileMethod | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Subtotals name="CT_Subtotals">
 * ```
 */
export interface ICTSubtotals {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_SubtotalIndex maxOccurs="unbounded" minOccurs="0" name="idx" type="CT_SubtotalIndex">
     * ```
     */
    idx?: ICTSubtotalIndex[];
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Subtotals name="CT_Subtotals">
 * ```
 */
export interface ICTSubtotalsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_SubtotalIndex maxOccurs="unbounded" minOccurs="0" name="idx" type="CT_SubtotalIndex">
     * ```
     */
    idx?: ICTSubtotalIndexDelta[];
    idxOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_ExtremeValueColorPosition name="CT_ExtremeValueColorPosition">
 * ```
 */
export interface ICTExtremeValueColorPosition {}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_NumberColorPosition name="CT_NumberColorPosition">
 * ```
 */
export interface ICTNumberColorPosition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="val" type="xsd:double" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_NumberColorPosition name="CT_NumberColorPosition">
 * ```
 */
export interface ICTNumberColorPositionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="val" type="xsd:double" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_PercentageColorPosition name="CT_PercentageColorPosition">
 * ```
 */
export interface ICTPercentageColorPosition {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="val" type="xsd:double" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_PercentageColorPosition name="CT_PercentageColorPosition">
 * ```
 */
export interface ICTPercentageColorPositionDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="val" type="xsd:double" use="required">
     * ```
     */
    val?: number | null;
}

export enum STParentLabelLayout {
    PBDEFAULT = 0,
    none = 1,
    banner = 2,
    overlapping = 3,
}

export enum STRegionLabelLayout {
    PBDEFAULT = 0,
    none = 1,
    bestFitOnly = 2,
    showAll = 3,
}

export enum STIntervalClosedSide {
    PBDEFAULT = 0,
    l = 1,
    r = 2,
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoCache name="CT_GeoCache">
 * ```
 */
export interface ICTGeoCache {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="binary" type="xsd:base64Binary">
     * ```
     */
    binary?: string[];
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Clear maxOccurs="1" minOccurs="0" name="clear" type="CT_Clear">
     * ```
     */
    clear?: ICTClear[];
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="provider" type="xsd:string" use="required">
     * ```
     */
    provider?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoCache name="CT_GeoCache">
 * ```
 */
export interface ICTGeoCacheDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="1" minOccurs="0" name="binary" type="xsd:base64Binary">
     * ```
     */
    binary?: string[];
    binaryOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Clear maxOccurs="1" minOccurs="0" name="clear" type="CT_Clear">
     * ```
     */
    clear?: ICTClearDelta[];
    clearOp?: IArrayOp;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="provider" type="xsd:string" use="required">
     * ```
     */
    provider?: string | null;
}

export enum STGeoProjectionType {
    PBDEFAULT = 0,
    mercator = 1,
    miller = 2,
    robinson = 3,
    albers = 4,
}

export enum STGeoMappingLevel {
    PBDEFAULT = 0,
    dataOnly = 1,
    postalCode = 2,
    county = 3,
    state = 4,
    countryRegion = 5,
    countryRegionList = 6,
    world = 7,
}

export enum STQuartileMethod {
    PBDEFAULT = 0,
    inclusive = 1,
    exclusive = 2,
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_SubtotalIndex name="CT_SubtotalIndex">
 * ```
 */
export interface ICTSubtotalIndex {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="val" type="xsd:unsignedInt" use="required">
     * ```
     */
    val?: number;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_SubtotalIndex name="CT_SubtotalIndex">
 * ```
 */
export interface ICTSubtotalIndexDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:uint32 name="val" type="xsd:unsignedInt" use="required">
     * ```
     */
    val?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Clear name="CT_Clear">
 * ```
 */
export interface ICTClear {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoLocationQueryResults maxOccurs="1" minOccurs="0" name="geoLocationQueryResults" type="CT_GeoLocationQueryResults">
     * ```
     */
    geoLocationQueryResults?: ICTGeoLocationQueryResults;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoDataEntityQueryResults maxOccurs="1" minOccurs="0" name="geoDataEntityQueryResults" type="CT_GeoDataEntityQueryResults">
     * ```
     */
    geoDataEntityQueryResults?: ICTGeoDataEntityQueryResults;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoDataPointToEntityQueryResults maxOccurs="1" minOccurs="0" name="geoDataPointToEntityQueryResults" type="CT_GeoDataPointToEntityQueryResults">
     * ```
     */
    geoDataPointToEntityQueryResults?: ICTGeoDataPointToEntityQueryResults;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoChildEntitiesQueryResults maxOccurs="1" minOccurs="0" name="geoChildEntitiesQueryResults" type="CT_GeoChildEntitiesQueryResults">
     * ```
     */
    geoChildEntitiesQueryResults?: ICTGeoChildEntitiesQueryResults;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoParentEntitiesQueryResults maxOccurs="1" minOccurs="0" name="geoParentEntitiesQueryResults" type="CT_GeoParentEntitiesQueryResults">
     * ```
     */
    geoParentEntitiesQueryResults?: ICTGeoParentEntitiesQueryResults;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Clear name="CT_Clear">
 * ```
 */
export interface ICTClearDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoLocationQueryResults maxOccurs="1" minOccurs="0" name="geoLocationQueryResults" type="CT_GeoLocationQueryResults">
     * ```
     */
    geoLocationQueryResults?: ICTGeoLocationQueryResultsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoDataEntityQueryResults maxOccurs="1" minOccurs="0" name="geoDataEntityQueryResults" type="CT_GeoDataEntityQueryResults">
     * ```
     */
    geoDataEntityQueryResults?: ICTGeoDataEntityQueryResultsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoDataPointToEntityQueryResults maxOccurs="1" minOccurs="0" name="geoDataPointToEntityQueryResults" type="CT_GeoDataPointToEntityQueryResults">
     * ```
     */
    geoDataPointToEntityQueryResults?: ICTGeoDataPointToEntityQueryResultsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoChildEntitiesQueryResults maxOccurs="1" minOccurs="0" name="geoChildEntitiesQueryResults" type="CT_GeoChildEntitiesQueryResults">
     * ```
     */
    geoChildEntitiesQueryResults?: ICTGeoChildEntitiesQueryResultsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoParentEntitiesQueryResults maxOccurs="1" minOccurs="0" name="geoParentEntitiesQueryResults" type="CT_GeoParentEntitiesQueryResults">
     * ```
     */
    geoParentEntitiesQueryResults?: ICTGeoParentEntitiesQueryResultsDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoLocationQueryResults name="CT_GeoLocationQueryResults">
 * ```
 */
export interface ICTGeoLocationQueryResults {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoLocationQueryResult maxOccurs="unbounded" minOccurs="0" name="geoLocationQueryResult" type="CT_GeoLocationQueryResult">
     * ```
     */
    geoLocationQueryResult?: ICTGeoLocationQueryResult[];
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoLocationQueryResults name="CT_GeoLocationQueryResults">
 * ```
 */
export interface ICTGeoLocationQueryResultsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoLocationQueryResult maxOccurs="unbounded" minOccurs="0" name="geoLocationQueryResult" type="CT_GeoLocationQueryResult">
     * ```
     */
    geoLocationQueryResult?: ICTGeoLocationQueryResultDelta[];
    geoLocationQueryResultOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoDataEntityQueryResults name="CT_GeoDataEntityQueryResults">
 * ```
 */
export interface ICTGeoDataEntityQueryResults {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoDataEntityQueryResult maxOccurs="unbounded" minOccurs="0" name="geoDataEntityQueryResult" type="CT_GeoDataEntityQueryResult">
     * ```
     */
    geoDataEntityQueryResult?: ICTGeoDataEntityQueryResult[];
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoDataEntityQueryResults name="CT_GeoDataEntityQueryResults">
 * ```
 */
export interface ICTGeoDataEntityQueryResultsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoDataEntityQueryResult maxOccurs="unbounded" minOccurs="0" name="geoDataEntityQueryResult" type="CT_GeoDataEntityQueryResult">
     * ```
     */
    geoDataEntityQueryResult?: ICTGeoDataEntityQueryResultDelta[];
    geoDataEntityQueryResultOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoDataPointToEntityQueryResults name="CT_GeoDataPointToEntityQueryResults">
 * ```
 */
export interface ICTGeoDataPointToEntityQueryResults {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoDataPointToEntityQueryResult maxOccurs="unbounded" minOccurs="0" name="geoDataPointToEntityQueryResult" type="CT_GeoDataPointToEntityQueryResult">
     * ```
     */
    geoDataPointToEntityQueryResult?: ICTGeoDataPointToEntityQueryResult[];
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoDataPointToEntityQueryResults name="CT_GeoDataPointToEntityQueryResults">
 * ```
 */
export interface ICTGeoDataPointToEntityQueryResultsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoDataPointToEntityQueryResult maxOccurs="unbounded" minOccurs="0" name="geoDataPointToEntityQueryResult" type="CT_GeoDataPointToEntityQueryResult">
     * ```
     */
    geoDataPointToEntityQueryResult?: ICTGeoDataPointToEntityQueryResultDelta[];
    geoDataPointToEntityQueryResultOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoChildEntitiesQueryResults name="CT_GeoChildEntitiesQueryResults">
 * ```
 */
export interface ICTGeoChildEntitiesQueryResults {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoChildEntitiesQueryResult maxOccurs="unbounded" minOccurs="0" name="geoChildEntitiesQueryResult" type="CT_GeoChildEntitiesQueryResult">
     * ```
     */
    geoChildEntitiesQueryResult?: ICTGeoChildEntitiesQueryResult[];
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoChildEntitiesQueryResults name="CT_GeoChildEntitiesQueryResults">
 * ```
 */
export interface ICTGeoChildEntitiesQueryResultsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoChildEntitiesQueryResult maxOccurs="unbounded" minOccurs="0" name="geoChildEntitiesQueryResult" type="CT_GeoChildEntitiesQueryResult">
     * ```
     */
    geoChildEntitiesQueryResult?: ICTGeoChildEntitiesQueryResultDelta[];
    geoChildEntitiesQueryResultOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoParentEntitiesQueryResults name="CT_GeoParentEntitiesQueryResults">
 * ```
 */
export interface ICTGeoParentEntitiesQueryResults {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoParentEntitiesQueryResult maxOccurs="unbounded" minOccurs="0" name="geoParentEntitiesQueryResult" type="CT_GeoParentEntitiesQueryResult">
     * ```
     */
    geoParentEntitiesQueryResult?: ICTGeoParentEntitiesQueryResult[];
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoParentEntitiesQueryResults name="CT_GeoParentEntitiesQueryResults">
 * ```
 */
export interface ICTGeoParentEntitiesQueryResultsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoParentEntitiesQueryResult maxOccurs="unbounded" minOccurs="0" name="geoParentEntitiesQueryResult" type="CT_GeoParentEntitiesQueryResult">
     * ```
     */
    geoParentEntitiesQueryResult?: ICTGeoParentEntitiesQueryResultDelta[];
    geoParentEntitiesQueryResultOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoLocationQueryResult name="CT_GeoLocationQueryResult">
 * ```
 */
export interface ICTGeoLocationQueryResult {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoLocationQuery maxOccurs="1" minOccurs="0" name="geoLocationQuery" type="CT_GeoLocationQuery">
     * ```
     */
    geoLocationQuery?: ICTGeoLocationQuery;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoLocations maxOccurs="1" minOccurs="0" name="geoLocations" type="CT_GeoLocations">
     * ```
     */
    geoLocations?: ICTGeoLocations;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoLocationQueryResult name="CT_GeoLocationQueryResult">
 * ```
 */
export interface ICTGeoLocationQueryResultDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoLocationQuery maxOccurs="1" minOccurs="0" name="geoLocationQuery" type="CT_GeoLocationQuery">
     * ```
     */
    geoLocationQuery?: ICTGeoLocationQueryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoLocations maxOccurs="1" minOccurs="0" name="geoLocations" type="CT_GeoLocations">
     * ```
     */
    geoLocations?: ICTGeoLocationsDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoDataEntityQueryResult name="CT_GeoDataEntityQueryResult">
 * ```
 */
export interface ICTGeoDataEntityQueryResult {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoDataEntityQuery maxOccurs="1" minOccurs="0" name="geoDataEntityQuery" type="CT_GeoDataEntityQuery">
     * ```
     */
    geoDataEntityQuery?: ICTGeoDataEntityQuery;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoData maxOccurs="1" minOccurs="0" name="geoData" type="CT_GeoData">
     * ```
     */
    geoData?: ICTGeoData;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoDataEntityQueryResult name="CT_GeoDataEntityQueryResult">
 * ```
 */
export interface ICTGeoDataEntityQueryResultDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoDataEntityQuery maxOccurs="1" minOccurs="0" name="geoDataEntityQuery" type="CT_GeoDataEntityQuery">
     * ```
     */
    geoDataEntityQuery?: ICTGeoDataEntityQueryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoData maxOccurs="1" minOccurs="0" name="geoData" type="CT_GeoData">
     * ```
     */
    geoData?: ICTGeoDataDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoDataPointToEntityQueryResult name="CT_GeoDataPointToEntityQueryResult">
 * ```
 */
export interface ICTGeoDataPointToEntityQueryResult {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoDataPointQuery maxOccurs="1" minOccurs="0" name="geoDataPointQuery" type="CT_GeoDataPointQuery">
     * ```
     */
    geoDataPointQuery?: ICTGeoDataPointQuery;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoDataPointToEntityQuery maxOccurs="1" minOccurs="0" name="geoDataPointToEntityQuery" type="CT_GeoDataPointToEntityQuery">
     * ```
     */
    geoDataPointToEntityQuery?: ICTGeoDataPointToEntityQuery;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoDataPointToEntityQueryResult name="CT_GeoDataPointToEntityQueryResult">
 * ```
 */
export interface ICTGeoDataPointToEntityQueryResultDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoDataPointQuery maxOccurs="1" minOccurs="0" name="geoDataPointQuery" type="CT_GeoDataPointQuery">
     * ```
     */
    geoDataPointQuery?: ICTGeoDataPointQueryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoDataPointToEntityQuery maxOccurs="1" minOccurs="0" name="geoDataPointToEntityQuery" type="CT_GeoDataPointToEntityQuery">
     * ```
     */
    geoDataPointToEntityQuery?: ICTGeoDataPointToEntityQueryDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoChildEntitiesQueryResult name="CT_GeoChildEntitiesQueryResult">
 * ```
 */
export interface ICTGeoChildEntitiesQueryResult {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoChildEntitiesQuery maxOccurs="1" minOccurs="0" name="geoChildEntitiesQuery" type="CT_GeoChildEntitiesQuery">
     * ```
     */
    geoChildEntitiesQuery?: ICTGeoChildEntitiesQuery;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoChildEntities maxOccurs="1" minOccurs="0" name="geoChildEntities" type="CT_GeoChildEntities">
     * ```
     */
    geoChildEntities?: ICTGeoChildEntities;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoChildEntitiesQueryResult name="CT_GeoChildEntitiesQueryResult">
 * ```
 */
export interface ICTGeoChildEntitiesQueryResultDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoChildEntitiesQuery maxOccurs="1" minOccurs="0" name="geoChildEntitiesQuery" type="CT_GeoChildEntitiesQuery">
     * ```
     */
    geoChildEntitiesQuery?: ICTGeoChildEntitiesQueryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoChildEntities maxOccurs="1" minOccurs="0" name="geoChildEntities" type="CT_GeoChildEntities">
     * ```
     */
    geoChildEntities?: ICTGeoChildEntitiesDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoParentEntitiesQueryResult name="CT_GeoParentEntitiesQueryResult">
 * ```
 */
export interface ICTGeoParentEntitiesQueryResult {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoParentEntitiesQuery maxOccurs="1" minOccurs="1" name="geoParentEntitiesQuery" type="CT_GeoParentEntitiesQuery">
     * ```
     */
    geoParentEntitiesQuery?: ICTGeoParentEntitiesQuery;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoEntity maxOccurs="1" minOccurs="0" name="geoEntity" type="CT_GeoEntity">
     * ```
     */
    geoEntity?: ICTGeoEntity;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoParentEntity maxOccurs="1" minOccurs="0" name="geoParentEntity" type="CT_GeoParentEntity">
     * ```
     */
    geoParentEntity?: ICTGeoParentEntity;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoParentEntitiesQueryResult name="CT_GeoParentEntitiesQueryResult">
 * ```
 */
export interface ICTGeoParentEntitiesQueryResultDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoParentEntitiesQuery maxOccurs="1" minOccurs="1" name="geoParentEntitiesQuery" type="CT_GeoParentEntitiesQuery">
     * ```
     */
    geoParentEntitiesQuery?: ICTGeoParentEntitiesQueryDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoEntity maxOccurs="1" minOccurs="0" name="geoEntity" type="CT_GeoEntity">
     * ```
     */
    geoEntity?: ICTGeoEntityDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoParentEntity maxOccurs="1" minOccurs="0" name="geoParentEntity" type="CT_GeoParentEntity">
     * ```
     */
    geoParentEntity?: ICTGeoParentEntityDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoLocationQuery name="CT_GeoLocationQuery">
 * ```
 */
export interface ICTGeoLocationQuery {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="countryRegion" type="xsd:string" use="optional">
     * ```
     */
    countryRegion?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="adminDistrict1" type="xsd:string" use="optional">
     * ```
     */
    adminDistrict1?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="adminDistrict2" type="xsd:string" use="optional">
     * ```
     */
    adminDistrict2?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="postalCode" type="xsd:string" use="optional">
     * ```
     */
    postalCode?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_EntityType name="entityType" type="ST_EntityType" use="required">
     * ```
     */
    entityType?: STEntityType;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoLocationQuery name="CT_GeoLocationQuery">
 * ```
 */
export interface ICTGeoLocationQueryDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="countryRegion" type="xsd:string" use="optional">
     * ```
     */
    countryRegion?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="adminDistrict1" type="xsd:string" use="optional">
     * ```
     */
    adminDistrict1?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="adminDistrict2" type="xsd:string" use="optional">
     * ```
     */
    adminDistrict2?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="postalCode" type="xsd:string" use="optional">
     * ```
     */
    postalCode?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_EntityType name="entityType" type="ST_EntityType" use="required">
     * ```
     */
    entityType?: STEntityType | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoLocations name="CT_GeoLocations">
 * ```
 */
export interface ICTGeoLocations {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoLocation maxOccurs="1" minOccurs="0" name="geoLocation" type="CT_GeoLocation">
     * ```
     */
    geoLocation?: ICTGeoLocation;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoLocations name="CT_GeoLocations">
 * ```
 */
export interface ICTGeoLocationsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoLocation maxOccurs="1" minOccurs="0" name="geoLocation" type="CT_GeoLocation">
     * ```
     */
    geoLocation?: ICTGeoLocationDelta | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoDataEntityQuery name="CT_GeoDataEntityQuery">
 * ```
 */
export interface ICTGeoDataEntityQuery {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_EntityType name="entityType" type="ST_EntityType" use="required">
     * ```
     */
    entityType?: STEntityType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityId" type="xsd:string" use="required">
     * ```
     */
    entityId?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoDataEntityQuery name="CT_GeoDataEntityQuery">
 * ```
 */
export interface ICTGeoDataEntityQueryDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_EntityType name="entityType" type="ST_EntityType" use="required">
     * ```
     */
    entityType?: STEntityType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityId" type="xsd:string" use="required">
     * ```
     */
    entityId?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoData name="CT_GeoData">
 * ```
 */
export interface ICTGeoData {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoPolygons maxOccurs="1" minOccurs="0" name="geoPolygons" type="CT_GeoPolygons">
     * ```
     */
    geoPolygons?: ICTGeoPolygons;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Copyrights maxOccurs="1" minOccurs="0" name="copyrights" type="CT_Copyrights">
     * ```
     */
    copyrights?: ICTCopyrights;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityName" type="xsd:string" use="required">
     * ```
     */
    entityName?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityId" type="xsd:string" use="required">
     * ```
     */
    entityId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="east" type="xsd:double" use="required">
     * ```
     */
    east?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="west" type="xsd:double" use="required">
     * ```
     */
    west?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="north" type="xsd:double" use="required">
     * ```
     */
    north?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="south" type="xsd:double" use="required">
     * ```
     */
    south?: number;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoData name="CT_GeoData">
 * ```
 */
export interface ICTGeoDataDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoPolygons maxOccurs="1" minOccurs="0" name="geoPolygons" type="CT_GeoPolygons">
     * ```
     */
    geoPolygons?: ICTGeoPolygonsDelta | null;
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Copyrights maxOccurs="1" minOccurs="0" name="copyrights" type="CT_Copyrights">
     * ```
     */
    copyrights?: ICTCopyrightsDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityName" type="xsd:string" use="required">
     * ```
     */
    entityName?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityId" type="xsd:string" use="required">
     * ```
     */
    entityId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="east" type="xsd:double" use="required">
     * ```
     */
    east?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="west" type="xsd:double" use="required">
     * ```
     */
    west?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="north" type="xsd:double" use="required">
     * ```
     */
    north?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="south" type="xsd:double" use="required">
     * ```
     */
    south?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoDataPointQuery name="CT_GeoDataPointQuery">
 * ```
 */
export interface ICTGeoDataPointQuery {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_EntityType name="entityType" type="ST_EntityType" use="required">
     * ```
     */
    entityType?: STEntityType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="latitude" type="xsd:double" use="required">
     * ```
     */
    latitude?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="longitude" type="xsd:double" use="required">
     * ```
     */
    longitude?: number;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoDataPointQuery name="CT_GeoDataPointQuery">
 * ```
 */
export interface ICTGeoDataPointQueryDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_EntityType name="entityType" type="ST_EntityType" use="required">
     * ```
     */
    entityType?: STEntityType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="latitude" type="xsd:double" use="required">
     * ```
     */
    latitude?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="longitude" type="xsd:double" use="required">
     * ```
     */
    longitude?: number | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoDataPointToEntityQuery name="CT_GeoDataPointToEntityQuery">
 * ```
 */
export interface ICTGeoDataPointToEntityQuery {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_EntityType name="entityType" type="ST_EntityType" use="required">
     * ```
     */
    entityType?: STEntityType;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityId" type="xsd:string" use="required">
     * ```
     */
    entityId?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoDataPointToEntityQuery name="CT_GeoDataPointToEntityQuery">
 * ```
 */
export interface ICTGeoDataPointToEntityQueryDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_EntityType name="entityType" type="ST_EntityType" use="required">
     * ```
     */
    entityType?: STEntityType | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityId" type="xsd:string" use="required">
     * ```
     */
    entityId?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoChildEntitiesQuery name="CT_GeoChildEntitiesQuery">
 * ```
 */
export interface ICTGeoChildEntitiesQuery {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoChildTypes maxOccurs="1" minOccurs="0" name="geoChildTypes" type="CT_GeoChildTypes">
     * ```
     */
    geoChildTypes?: ICTGeoChildTypes;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityId" type="xsd:string" use="required">
     * ```
     */
    entityId?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoChildEntitiesQuery name="CT_GeoChildEntitiesQuery">
 * ```
 */
export interface ICTGeoChildEntitiesQueryDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoChildTypes maxOccurs="1" minOccurs="0" name="geoChildTypes" type="CT_GeoChildTypes">
     * ```
     */
    geoChildTypes?: ICTGeoChildTypesDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityId" type="xsd:string" use="required">
     * ```
     */
    entityId?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoChildEntities name="CT_GeoChildEntities">
 * ```
 */
export interface ICTGeoChildEntities {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoHierarchyEntity maxOccurs="unbounded" minOccurs="0" name="geoHierarchyEntity" type="CT_GeoHierarchyEntity">
     * ```
     */
    geoHierarchyEntity?: ICTGeoHierarchyEntity[];
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoChildEntities name="CT_GeoChildEntities">
 * ```
 */
export interface ICTGeoChildEntitiesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoHierarchyEntity maxOccurs="unbounded" minOccurs="0" name="geoHierarchyEntity" type="CT_GeoHierarchyEntity">
     * ```
     */
    geoHierarchyEntity?: ICTGeoHierarchyEntityDelta[];
    geoHierarchyEntityOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoParentEntitiesQuery name="CT_GeoParentEntitiesQuery">
 * ```
 */
export interface ICTGeoParentEntitiesQuery {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityId" type="xsd:string" use="required">
     * ```
     */
    entityId?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoParentEntitiesQuery name="CT_GeoParentEntitiesQuery">
 * ```
 */
export interface ICTGeoParentEntitiesQueryDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityId" type="xsd:string" use="required">
     * ```
     */
    entityId?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoEntity name="CT_GeoEntity">
 * ```
 */
export interface ICTGeoEntity {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityName" type="xsd:string" use="required">
     * ```
     */
    entityName?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_EntityType name="entityType" type="ST_EntityType" use="required">
     * ```
     */
    entityType?: STEntityType;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoEntity name="CT_GeoEntity">
 * ```
 */
export interface ICTGeoEntityDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityName" type="xsd:string" use="required">
     * ```
     */
    entityName?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_EntityType name="entityType" type="ST_EntityType" use="required">
     * ```
     */
    entityType?: STEntityType | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoParentEntity name="CT_GeoParentEntity">
 * ```
 */
export interface ICTGeoParentEntity {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityId" type="xsd:string" use="required">
     * ```
     */
    entityId?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoParentEntity name="CT_GeoParentEntity">
 * ```
 */
export interface ICTGeoParentEntityDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityId" type="xsd:string" use="required">
     * ```
     */
    entityId?: string | null;
}

export enum STEntityType {
    PBDEFAULT = 0,
    Address = 1,
    AdminDistrict = 2,
    AdminDistrict2 = 3,
    AdminDistrict3 = 4,
    Continent = 5,
    CountryRegion = 6,
    Locality = 7,
    Ocean = 8,
    Planet = 9,
    PostalCode = 10,
    Region = 11,
    Unsupported = 12,
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoLocation name="CT_GeoLocation">
 * ```
 */
export interface ICTGeoLocation {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Address maxOccurs="1" minOccurs="0" name="address" type="CT_Address">
     * ```
     */
    address?: ICTAddress;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="latitude" type="xsd:double" use="optional">
     * ```
     */
    latitude?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="longitude" type="xsd:double" use="optional">
     * ```
     */
    longitude?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityName" type="xsd:string" use="required">
     * ```
     */
    entityName?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_EntityType name="entityType" type="ST_EntityType" use="required">
     * ```
     */
    entityType?: STEntityType;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoLocation name="CT_GeoLocation">
 * ```
 */
export interface ICTGeoLocationDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_Address maxOccurs="1" minOccurs="0" name="address" type="CT_Address">
     * ```
     */
    address?: ICTAddressDelta | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="latitude" type="xsd:double" use="optional">
     * ```
     */
    latitude?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:double name="longitude" type="xsd:double" use="optional">
     * ```
     */
    longitude?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityName" type="xsd:string" use="required">
     * ```
     */
    entityName?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_EntityType name="entityType" type="ST_EntityType" use="required">
     * ```
     */
    entityType?: STEntityType | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoPolygons name="CT_GeoPolygons">
 * ```
 */
export interface ICTGeoPolygons {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoPolygon maxOccurs="unbounded" minOccurs="0" name="geoPolygon" type="CT_GeoPolygon">
     * ```
     */
    geoPolygon?: ICTGeoPolygon[];
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoPolygons name="CT_GeoPolygons">
 * ```
 */
export interface ICTGeoPolygonsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:CT_GeoPolygon maxOccurs="unbounded" minOccurs="0" name="geoPolygon" type="CT_GeoPolygon">
     * ```
     */
    geoPolygon?: ICTGeoPolygonDelta[];
    geoPolygonOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Copyrights name="CT_Copyrights">
 * ```
 */
export interface ICTCopyrights {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="unbounded" minOccurs="0" name="copyright" type="xsd:string">
     * ```
     */
    copyright?: string[];
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Copyrights name="CT_Copyrights">
 * ```
 */
export interface ICTCopyrightsDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=xsd:string maxOccurs="unbounded" minOccurs="0" name="copyright" type="xsd:string">
     * ```
     */
    copyright?: string[];
    copyrightOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoChildTypes name="CT_GeoChildTypes">
 * ```
 */
export interface ICTGeoChildTypes {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:ST_EntityType maxOccurs="unbounded" minOccurs="0" name="entityType" type="ST_EntityType">
     * ```
     */
    entityType?: STEntityType[];
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoChildTypes name="CT_GeoChildTypes">
 * ```
 */
export interface ICTGeoChildTypesDelta {
    /**
     * ```xml
     * <xsd:element typeRedirected=mschartex:ST_EntityType maxOccurs="unbounded" minOccurs="0" name="entityType" type="ST_EntityType">
     * ```
     */
    entityType?: STEntityType[];
    entityTypeOp?: IArrayOp;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoHierarchyEntity name="CT_GeoHierarchyEntity">
 * ```
 */
export interface ICTGeoHierarchyEntity {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityName" type="xsd:string" use="required">
     * ```
     */
    entityName?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityId" type="xsd:string" use="required">
     * ```
     */
    entityId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_EntityType name="entityType" type="ST_EntityType" use="required">
     * ```
     */
    entityType?: STEntityType;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoHierarchyEntity name="CT_GeoHierarchyEntity">
 * ```
 */
export interface ICTGeoHierarchyEntityDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityName" type="xsd:string" use="required">
     * ```
     */
    entityName?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="entityId" type="xsd:string" use="required">
     * ```
     */
    entityId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=mschartex:ST_EntityType name="entityType" type="ST_EntityType" use="required">
     * ```
     */
    entityType?: STEntityType | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Address name="CT_Address">
 * ```
 */
export interface ICTAddress {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="address1" type="xsd:string" use="optional">
     * ```
     */
    address1?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="countryRegion" type="xsd:string" use="optional">
     * ```
     */
    countryRegion?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="adminDistrict1" type="xsd:string" use="optional">
     * ```
     */
    adminDistrict1?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="adminDistrict2" type="xsd:string" use="optional">
     * ```
     */
    adminDistrict2?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="postalCode" type="xsd:string" use="optional">
     * ```
     */
    postalCode?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="locality" type="xsd:string" use="optional">
     * ```
     */
    locality?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="isoCountryCode" type="xsd:string" use="optional">
     * ```
     */
    isoCountryCode?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_Address name="CT_Address">
 * ```
 */
export interface ICTAddressDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="address1" type="xsd:string" use="optional">
     * ```
     */
    address1?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="countryRegion" type="xsd:string" use="optional">
     * ```
     */
    countryRegion?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="adminDistrict1" type="xsd:string" use="optional">
     * ```
     */
    adminDistrict1?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="adminDistrict2" type="xsd:string" use="optional">
     * ```
     */
    adminDistrict2?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="postalCode" type="xsd:string" use="optional">
     * ```
     */
    postalCode?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="locality" type="xsd:string" use="optional">
     * ```
     */
    locality?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="isoCountryCode" type="xsd:string" use="optional">
     * ```
     */
    isoCountryCode?: string | null;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoPolygon name="CT_GeoPolygon">
 * ```
 */
export interface ICTGeoPolygon {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="polygonId" type="xsd:string" use="required">
     * ```
     */
    polygonId?: string;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="numPoints" type="xsd:integer" use="required">
     * ```
     */
    numPoints?: number;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="pcaRings" type="xsd:string" use="required">
     * ```
     */
    pcaRings?: string;
}

/**
 * ```xml
 * <xsd:complexType name=mschartex:CT_GeoPolygon name="CT_GeoPolygon">
 * ```
 */
export interface ICTGeoPolygonDelta {
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="polygonId" type="xsd:string" use="required">
     * ```
     */
    polygonId?: string | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:integer name="numPoints" type="xsd:integer" use="required">
     * ```
     */
    numPoints?: number | null;
    /**
     * ```xml
     * <xsd:attribute typeRedirected=xsd:string name="pcaRings" type="xsd:string" use="required">
     * ```
     */
    pcaRings?: string | null;
}
